import React, {
  createContext,
  forwardRef,
  memo,
  startTransition,
  useCallback,
  useContext,
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import styles from "./AeternaInvicta.module.scss";
import { getMonsterImageUrl } from "./monsterImages";
import { getMatchTopographyStyle } from "./matchTopography";
import { LORE_ENTRIES } from "./loreEntries";
import { RULEBOOK_SECTIONS } from "./rulebookEntries";
import { SUPPORT_INFO_ENTRIES } from "./supportEntries";
import { getWebSocketUrl as resolveWebSocketUrl } from "../../utils/webSocketUrl";
import {
  canShowAds,
  getNativePlatform,
  isNativeApp,
  showRewardedAd,
} from "../../utils/nativeApp";
import {
  clearSession,
  loadSession,
  saveAccountSession,
  saveGuestSession,
} from "../../utils/session";
import useGamepadNavigation from "./useGamepadNavigation";
import {
  buildDeckProfile,
  canSuggestCards,
  filterSuggestedCards,
  EQUIPMENT_ITEM_TYPES,
  canUpgradeEquipmentCard,
} from "./deckSuggest";
import { analyzeDeck, formatAverage } from "./deckAnalyze";
import {
  CHARGE_KINDS,
  chargeKindClearsAtTurnEnd,
  getChargeCount,
  getChargeKindKeywordEntries,
  getChargeKindKeywordId,
  getChargeKindLabel,
} from "./chargeKinds";
import {
  applyRecommendationToDraft,
  buildRecommendationKey,
  catalogFingerprint,
  getDeckRecommendBlocker,
  ownershipFingerprint,
  recommendDeckChanges,
  validateRecommendedDeck,
} from "./deckRecommend";
import AiFoilLayers from "./foil/AiFoilLayers";
import AiHoloPreview from "./foil/AiHoloPreview";
import AiLazyImage, { warmImages } from "./foil/AiLazyImage";
import {
  assetVersionedUrl,
  isKnownAsset,
  preloadImageUrls,
  setAssetVersions,
} from "./assetPreloader";
import {
  prewarmFoilAssets,
  useDeferredFoilReveal,
} from "./foil/aiFoilPrewarm";
import {
  ALL_FOIL_EFFECT_POOL,
  foilEffectLabel,
  getFoilArtFilterClass as resolveFoilArtFilterClass,
  getFoilStampIconFile,
  isBorderlessFoil,
  isTraditionalFoil,
  resolveFoilEffectId,
  resolveFoilStampId,
  sortFoilEffects,
} from "./foil/aiShine";
import AdminCardEditor from "./AdminCardEditor";
import AdminPlayerBots from "./AdminPlayerBots";
import {
  GRAPHICS_QUALITY_OPTIONS,
  applyGraphicsTierToDocument,
  capFoilQualityForGraphics,
  clearLearnedGraphicsTier,
  loadGraphicsQuality,
  lowerGraphicsTier,
  normalizeGraphicsQuality,
  resolveAutoGraphicsTier,
  saveGraphicsQuality,
  saveLearnedGraphicsTier,
  watchFrameRate,
} from "./graphicsQuality";
import {
  playMatchSfxList,
  playCardHoverSfx,
  playSfx,
  skipToNextTrack,
  setMusicContext,
  setMusicEnabled as setAudioMusicEnabled,
  setSoundEnabled as setAudioSoundEnabled,
  setMusicVolume as setAudioMusicVolume,
  setSoundVolume as setAudioSoundVolume,
  getMusicVolume as getAudioMusicVolume,
  getSoundVolume as getAudioSoundVolume,
  stopMenuMusic,
  syncAudioPrefsFromSettings,
} from "./aeternaAudio";
import { resolveAeternaAssetPath } from "./assetBase";
import {
  DEFAULT_LANGUAGE,
  LANGUAGE_OPTIONS,
  createTranslator,
  loadStoredLanguage,
  localizeServerMessageText,
  normalizeLanguage,
  saveStoredLanguage,
  setActiveLanguage,
  t,
} from "./i18n";

function formatAdventureDamageType(damageType) {
  const type = damageType || "physical";
  const key = `adventure.damageType.${type}`;
  const label = t(key);
  return label === key ? type : label;
}

function formatAdventureMonsterAttack(attack) {
  if (!attack) return "";
  const parts = [];
  const damage = Math.max(0, Math.floor(Number(attack.damage) || 0));
  if (damage > 0) {
    parts.push(
      t("adventure.attackDamage", {
        damage,
        type: formatAdventureDamageType(attack.damageType),
      }),
    );
  }
  const heal = Math.max(0, Math.floor(Number(attack.heal) || 0));
  if (heal > 0) {
    const targetLabel =
      attack.healTarget === "allies"
        ? t("adventure.healTargetAllies")
        : attack.healTarget === "ally"
          ? t("adventure.healTargetAlly")
          : t("adventure.healTargetSelf");
    parts.push(t("adventure.attackHeal", { heal, target: targetLabel }));
  }
  const statusParts = [];
  if ((attack.bleed || 0) > 0) statusParts.push(t("match.view.statusBleed", { amount: attack.bleed }));
  if ((attack.poison || 0) > 0) statusParts.push(t("match.view.statusPoison", { amount: attack.poison }));
  if ((attack.burn || 0) > 0) statusParts.push(t("match.view.statusBurn", { amount: attack.burn }));
  if ((attack.chill || 0) > 0) statusParts.push(t("match.view.statusChill", { amount: attack.chill }));
  if ((attack.shock || 0) > 0) statusParts.push(t("match.view.statusShock", { amount: attack.shock }));
  if (statusParts.length) parts.push(statusParts.join(", "));
  return parts.join(" · ");
}

function formatAdventureMonsterAttackLine(attack) {
  const summary = formatAdventureMonsterAttack(attack);
  if (!summary) return attack?.name || t("adventure.attack");
  return `${attack.name}: ${summary}`;
}

function formatAdventureMonsterAttackStrip(attack) {
  const summary = formatAdventureMonsterAttack(attack);
  if (!summary) return attack?.name || t("adventure.attack");
  return `${attack.name} ${summary}`;
}

function formatAdventureMonsterIntention(enemy) {
  if (!enemy || enemy.defeated) return "";
  const attack = enemy.intendedAttack || null;
  const turns = Math.max(0, Math.floor(Number(enemy.turnsUntilAct) || 0));
  const summary = attack
    ? formatAdventureMonsterAttackStrip(attack)
    : t("adventure.unknown");
  if (turns <= 1) return t("adventure.intentNext", { summary });
  return t("adventure.intentIn", { turns, summary });
}

const ADVENTURE_NODE_TYPES = new Set(["start", "combat", "shop", "reward", "story"]);

function adventureNodeLabel(node) {
  if (!node) return "";
  if (node.revealed && ADVENTURE_NODE_TYPES.has(node.type)) {
    return t(`adventure.nodeType.${node.type}`);
  }
  return node.label || "";
}

function getAdventureMapNodeTypeClass(type) {
  switch (type) {
    case "start":
      return styles.adventureMapNodeCamp;
    case "combat":
      return styles.adventureMapNodeCombat;
    case "shop":
      return styles.adventureMapNodeShop;
    case "reward":
      return styles.adventureMapNodeTreasure;
    case "story":
      return styles.adventureMapNodeStory;
    default:
      return styles.adventureMapNodeUnknown;
  }
}

const WS_PATH = "/aeterna-invicta";
const MAX_COPIES = 2;
const MIN_DECK = 10;
const DECK_SLOTS = 10;
const VOLUME_STEP_PERCENT = 10;
const CUSTOMIZE_PURCHASE_CONFIRM_MS = 3000;
// Settings menu order: critical settings first, destructive ones last.
const SETTINGS_SECTIONS = [
  { id: "account", items: ["account"] },
  {
    id: "general",
    items: ["language", "music", "musicVolume", "sound", "soundVolume"],
  },
  {
    id: "gameplay",
    items: [
      "quickUse",
      "hints",
      "matchGuides",
      "cardNames",
      "toasts",
      "toastSpeed",
    ],
  },
  {
    id: "display",
    items: ["graphics", "foil", "fullscreen", "theme", "workshop"],
  },
  { id: "controls", items: ["handDisplay", "leftHanded", "controller"] },
  { id: "social", items: ["chat", "blocked"] },
  { id: "danger", danger: true, items: ["adventure", "deleteAccount"] },
];
const SETTINGS_ACCOUNT_ONLY_ITEMS = new Set(["chat", "deleteAccount"]);
const MAX_DECK_PAIRS = 100;
const MAX_DECK_POWER = 10;
const DEFAULT_ASPECT_ID = "as_traveler";
const MATCH_HAND_SLOTS = 4;
const HAND_DRAWER_STORAGE_KEY = "aeternaInvicta.handDrawer";
const SOCIAL_PLAYERS_STORAGE_KEY = "aeternaInvicta.socialPlayersCollapsed";
// Space kept above hand cards for their Invoke badges.
const HAND_BADGE_SPACE_PX = 20;
const MATCH_BOARD_MIN_COL_PX = 180;
const HAND_EDGE_TUCK_PX = 24;
const NEW_DECK_OPTION = "__new_deck__";
const RANDOM_QUEUE_DECK_OPTION = "__random__";
const DODGE_MITIGATION_PERCENT = 25;
const VITALS_FLOATER_ANIM_MS = 3000;
const VITALS_FLOATER_CLEANUP_MS = 3100;
const VITALS_FLOATER_STAGGER_MS = 150;
const VITALS_HIT_FLASH_MS = 520;
const COMBAT_PROJECTILE_ANIM_MS = 720;
const COMBAT_PROJECTILE_CLEANUP_MS = 780;
const COMBAT_PROJECTILE_STAGGER_MS = 380;
const COMBAT_SOURCE_ANIM_MS = 1200;
const COMBAT_SOURCE_FIRE_AT_MS = 820;
const COMBAT_SOURCE_SETTLE_MS = 1280;
const COMBAT_MELEE_ANIM_MS = 2800;
const COMBAT_MELEE_HIT_AT_MS = 1320;
const COMBAT_MELEE_SETTLE_MS = 2900;
const COMBAT_ECHO_GHOST_DELAY_MS = 600;
const COMBAT_HIT_FLASH_MS = 420;
const COMBAT_IMPACT_SFX = new Set(["physicalAttack", "magicAttack"]);
const CARD_PLAY_ANIM_MS = 720;
const CARD_PLAY_SLAM_AT_MS = 430;
const CARD_PLAY_CLEANUP_MS = 1450;
const CARD_PLAY_GHOST_SETTLE_MS = 48;
const CARD_PLAY_DUST_COUNT = 38;
const CARD_PLAY_DUST_COUNT_BY_GRAPHICS_TIER = {
  high: CARD_PLAY_DUST_COUNT,
  medium: 16,
  low: 0,
};
const CARD_PLAY_DUST_MS = 980;
const GAME_VERSION = "0.69";
const PATCH_NOTES = [
  {
    version: "0.69",
    sections: [
      {
        title: "New Cards: Tier 2 Equipment",
        items: [
          "26 new Tier 2 equipment cards join the base set, each a stronger take on an existing equipment type: Swordsman's Scimitar, Greataxe of the Warrior, Guardian's Flanged Mace, Assassin's Skean, Angry Goblin's Beat Stick, Runeseeker's Wand, Artificer's Staff, Marksman's Recurve Bow, Aggressive Arbalest, General's Trident, Guardsman's Pavise, Paladin's Icon, Sellsword's Sallet, Crown of Avarice, Circlet of Mending, Wolf Cowl, Gladiator's Brigandine, Garrison Hauberk, Astrologer's Robes, Cloak of Shadow, Heavy Bracers, Adroit Boots, Garrison Greaves, Librarian's Slippers, Bruiser's Sabatons, and Brooch of Lucidity.",
          "Tier 2 equipment costs 0 Power.",
        ],
      },
      {
        title: "Equipment Tiers",
        items: [
          "Equipment now has a Tier, shown with tier icons next to its name. All earlier equipment is Tier 1.",
          "Tier 2 equipment is played as an upgrade onto an equipped Tier 1 piece of the same type: Mail onto Mail, Sword onto Sword, Necklace onto Necklace. Without a matching piece equipped, a Tier 2 card can only be Invoked.",
          "The upgraded Tier 1 piece stays under the Tier 2 card and is discarded with it. Nothing carries over, and the new piece can't be expended until your next turn. Upgrading uses your equipment play for the turn.",
          "Examining a Tier 2 card shows what it upgrades, and equipped upgrades show what they upgraded from.",
        ],
      },
      {
        title: "New Mechanics",
        items: [
          "Delayed resources: gained now, usable from the start of your next turn. They appear faded with a small clock icon. Heavy Bracers, Astrologer's Robes, Garrison Hauberk, Wolf Cowl, Cloak of Shadow, and Brooch of Lucidity also grant 1 Delayed resource when expended for resources, and Circlet of Mending can grant 1 Persistent and 1 Delayed Wisdom.",
          "Shadow: a buff stack that turns the next hit of 2 or more damage into 1 damage. Shadow is used before Dodge and expires when your opponent ends their turn. Cloak of Shadow grants Shadow.",
          "Block: if you took physical damage last turn, that equipment's Physical Armor effects grant extra armor equal to its Block value. Guardsman's Pavise has Block 1.",
          "Hone, Fletch, and some expend effects can now also cost Durability, as on Swordsman's Scimitar, Marksman's Recurve Bow, and Greataxe of the Warrior.",
        ],
      },
      {
        title: "Card Changes",
        items: [
          "Summon Imp now costs 3 Wisdom at Levels 1 and 2, 4 at Level 3, and 5 at Level 4 (was 4, 4, 5, 6), matching Summon Golem and Summon Wisp.",
          "Every equipment card now has an item type, such as Sword, Polearm, Mail, Gloves, Necklace, or Artifact.",
          "Engulfed Torch is now a one-handed Club weapon (was an off-hand).",
          "Pillar of Earth is now a Hammer. Spears, Pikes, Halberds, and Glaives are now Polearms, and Tomes, Orbs, the Brilliant Prism, and the Kaleidoscopic Lens are now Artifacts.",
          "Essences can now go in linked slots, but they only take effect from the host slot.",
        ],
      },
      {
        title: "New Decks",
        items: [
          "Three new Library decks feature Tier 2 equipment: Garrison Blade (Strength), Shadow Marksman (Dexterity), and Starlit Runeseeker (Wisdom). New players still start with the Strength, Dexterity, and Wisdom decks.",
        ],
      },
      {
        title: "New Foil",
        items: [
          "Rising Star: every few seconds a star streaks across the card from the top right, leaving a lingering trail of sparkles. Available as Base, Borderless, and Frame variants.",
        ],
      },
      {
        title: "Cards & Deck Building",
        items: [
          "Summon Golem, Summon Wisp, and Summon Imp show their summon cost on one line, with the Minion's health and its own Expend action in a separate box below.",
          "Regeneration has a new icon in card text and in your status row.",
          "Invoke effects are shown with an Invoke icon.",
          "Ranged physical attacks, like bows, crossbows, and throwables, now fire arrows across the board.",
          "New Equipment Type and Tier filters, plus sorting by Tier, in the Collection, deck builder, and Workshop. Equipment types now have their own filter instead of sharing the Type filter.",
          "Deck Suggest and Recommend now account for Tier upgrades: they pair Tier 2 cards with a matching Tier 1 piece and explain upgrade picks.",
        ],
      },
    ],
  },
  {
    version: "0.68",
    sections: [
      {
        title: "Performance",
        items: [
          "New Graphics Quality setting (Auto, High, Medium, Low). Lower levels reduce background motion, animated foil, glows, and blur to keep matches smooth on slower devices. Auto picks a level for your device and steps down if a match starts to stutter. The setting is saved per device.",
          "Match timers and combat animations update without redrawing the whole board, so matches run smoother.",
          "Card images load more efficiently, and fewer stay loaded at once.",
          "Fixed music tracks sometimes overlapping when switching screens quickly.",
        ],
      },
      {
        title: "New Content",
        items: [
          "39 new intermediate sample decks in the Library, including Dark Covenant, Bloodthorn, Lone Blade, Packmaster, Triune Tempest, Kaleidoscope, Emberglass, and Tideglass.",
          "Resetting decks from the Library now only restores sample decks whose cards you own. A sample deck is included once all of its cards are unlocked.",
        ],
      },
      {
        title: "Match Guides",
        items: [
          "New Match Guides glow playable cards, usable equipment and abilities, and End Turn, with a collapsible helper box that explains what you can do next.",
          "Guides are set to Auto by default, showing for guest accounts and your first 10 games. Turn them on or off anytime in Settings, or from the guide box itself.",
        ],
      },
      {
        title: "Cards & Rules",
        items: [
          "Poison Burst now costs 1 Dexterity at every level (was 2 to 4). It consumes all Poison on your opponent and deals 1 poison damage that grows with level, plus 1 per Poison stack consumed.",
          "Iron Overload now costs 1 Strength at every level (was 2 to 4). It consumes your Physical Armor and deals 1 physical damage that grows with level, plus 1 per armor consumed.",
          "Strike, Slash, Executioner's Hook, and Storm Pike invokes now deal 1 damage (was 2).",
          "Engulfed Torch's invoke now deals 1 fire damage instead of healing 1.",
          "Summoner's Staff's invoke now grants 1 Wisdom instead of dealing 2 magical damage.",
          "Sol's Katana and Plated Cuirass no longer have an Invoke.",
          "WIS at Start & Empower Magic Armor Gear now raises both the chosen equipment's passive Magic Armor and the Magic Armor it grants when expended by 1.",
          "Empower Physical Armor Card's equipment option now targets equipment whose expend grants Physical Armor and raises that expended armor by 1. Passive armor is unchanged.",
          "Empowered equipment shows its Trait armor bonus on the card.",
        ],
      },
      {
        title: "Rewards",
        items: [
          "Winning a Classic match now unlocks 5 cards (up from 3) and 1 foil reward (down from 2), or 3 foil rewards once your collection is complete (up from 2). Losses no longer unlock cards.",
          "Each achievement now rewards 5 currency (up from 1), and completing an achievement category rewards 25 (up from 10).",
        ],
      },
      {
        title: "Interface",
        items: [
          "New Left-Handed Mode in Settings moves the End Turn and Emote buttons to the left and your hand to the right during matches.",
          "Traditional foil cards use clean attribute-colored frames: red for Strength, green for Dexterity, blue for Wisdom, white for Neutral, and bronze for multi-attribute cards. Menu cards also have a refreshed frame.",
          "Ranged elemental abilities now fly across the board as projectiles.",
          "Resource counters pulse when resources are converted, such as turning Neutral into an attribute.",
          "Cards your opponent plays land on the board showing exactly how they appear there.",
          "Fire, Cold, and Lightning Resistance have their own badge styles.",
          "Costs paid with Durability show Durability icons, and Use Durability has its own keyword tooltip.",
          "Trait descriptions show damage, armor, and healing icons.",
          "Power now has a keyword tooltip explaining the deck Power limit.",
          "If equipment still needs an Identity, a Choose Identity button appears and End Turn waits until you pick one.",
          "Export and Close stay pinned to the bottom while scrolling a Library sample deck.",
          "Titles and long text scale and wrap better on small screens.",
          "\"VS Human\" is now \"VS Player\" in the match lobby and queue.",
          "Much more of the game is translated into Japanese and Vietnamese.",
        ],
      },
      {
        title: "Community & Privacy",
        items: [
          "Report a player from their profile in Social, Ranking, chat, or the player preview during a match. Choose a category and add optional details. Reports are private.",
          "Block a player to hide their chat messages and match emotes and stop them from challenging you. Manage blocked players in Settings.",
          "Support has a new Privacy and Data Safety section, and you can delete your account from Settings.",
          "Support now includes Open Source Acknowledgements.",
        ],
      },
    ],
  },
  {
    version: "0.67",
    sections: [
      {
        title: "New Content",
        items: [
          "Six new elemental active decks: Infernal Pact (Fireball and Summon Imp) for Mage and Cinder Hunt (Scorching Shot) for Scout; Glacial Rite (Ice Storm and Frostbite) for Aegis and Avalanche (Frozen Slam) for Brawler; Thunderguard (Conductive Strike) for Mage and Galvanic Snare (Shockwave) for Scout.",
        ],
      },
      {
        title: "Card Exam",
        items: [
          "Examine is redesigned: the card art sits beside its details, type tags show as chips, Power, Attribute, Slot, and Resources show as stat tiles, and Expend, Passive, Triggers, Invoke, and Level Progression each get their own section.",
        ],
      },
      {
        title: "Collection",
        items: [
          "Collection filters start from Card Type (Aspect, Equipment, Ability), Attribute, and Cost, which are always visible.",
          "Filters that don't apply are hidden: Slot appears only for Equipment, Kind (Active, Augment, Essence) only for Abilities, and Cost is hidden for Aspects. Type, effect, and sort options follow the selected card type.",
          "Holo unlocked is now a toggle, and a single Cards toggle cycles between All, Unlocked, and Locked. Holo filters and Holo count sorting appear once you have unlocked a holo.",
          "Collection shows three cards per row on phones, and filter controls use a smaller font.",
          "Collection is paged: 9 cards per page by default, with a dropdown for 9, 18, 27, 36, or All. Arrows above and below the cards move between pages.",
          "New Sort: Power option in the Collection and the deck builder card picker lists the highest Power cards first.",
        ],
      },
      {
        title: "Card Text",
        items: [
          "Abilities that cost health show Health icons before the arrow.",
          "Equipment that damages you as part of its expend shows that damage as a cost before the arrow. Vest of Immolation reads 1 fire damage → Burn 1, and Bloodpact Ring reads 2 physical damage → Regeneration 1.",
          "Phoenix Mantle's expend reads: consume up to 1 self Burn to heal 1 per stack.",
        ],
      },
      {
        title: "Cards & Rules",
        items: [
          "Fire Strike, Fireball, Ice Shot, Ice Storm, Conductive Strike, and Lightning Bolt now cost 1 primary resource and let you choose: deal 1 elemental damage, pay 1 Neutral to apply 1 Burn, Chill, or Shock, or pay 2 Neutral for both.",
          "Scorching Shot, Frozen Slam, and Storm Lance now cost 2 primary resources and let you choose a heavy mode (pay 1 more primary and 3 Neutral for big elemental damage plus 2 ailment stacks) or a quick mode (pay 1 Neutral for 1 physical or magical damage plus an elemental hit). Both modes grow with ability level.",
          "Added Fire, Cold, and Lightning Damage augment any attack or magic active that deals damage. Added Burn, Chill, and Shock also augment an active linked with Added Fire, Cold, or Lightning Damage respectively, even without the matching element tag.",
          "Augments and Essences now scale with ability level, granting a bigger bonus at each level. Essences at levels 3 and 4 make their equipment's expend actions cost more.",
          "Might, Finesse, and Insight no longer add cost. They raise the linked active's level by 1 to 3, and at levels 3 and 4 also reduce its primary cost by 1 or 2.",
          "Shadow Step's Dodge grows with level.",
          "Attack and magic abilities now carry Physical or Magical type tags matching the damage they deal.",
          "Elemental Volley's invoke now grants 1 Dexterity.",
          "Grip Wraps: pay 1 Dexterity and expend to give the next slotted physical melee active +1 physical damage. Its Encore passive moved to Encore Boots, which now enable Encore for 3 Dexterity.",
          "Longbow's Fletch is now an expend choice instead of a passive, and Longbow no longer enables Encore.",
          "Twinblade and Spellguard are now Power 6.",
          "Shield affinity bonuses (such as Shield Bash) now count shields on your board as well as equipped ones.",
          "Charges now come in kinds: Bolt (Hand Crossbow), Fletch (Longbow), Arcane (Charge Wand), Earth (Pillar of Earth), Nature (Druid's Staff), Bulwark (Plated Cuirass), and Focus (Focus Charm). Each card only gains and spends its own kind, and equipment shows a separate counter per kind.",
          "Channel Gloves, Iron-Sage Gauntlets, and Spellthief Gloves now add Arcane Charges only, so they charge Charge Wand but no longer load Hand Crossbow or charge Pillar of Earth or Druid's Staff.",
          "Only Earth Charges clear at end of turn. Other charge kinds stay until spent.",
          "Quiver of Iron Bolts' Distant Connection now only boosts Crossbow expend damage. Abilities, including Machinist's Crossbow's Inherent Pierce, no longer use it up.",
          "Inherent abilities (such as Pierce on Machinist's Crossbow) can now be activated: tap the equipment and choose the ability from its action menu, or use its button in the action bar.",
          "Sprint Boots and Trickster Mitts no longer have an Invoke.",
          "Sigil of the Covenant now expends for 1 Wisdom or for 1 Magic Armor.",
          "Labradorite Amulet no longer locks out actives that don't match its Identity. Instead, it can only be expended for its Identity's resource while an active of that same attribute is slotted in its column.",
        ],
      },
      {
        title: "Server Messages",
        items: [
          "Server announcements and maintenance warnings now appear as a longer-lasting toast at the top of the screen. Click the toast to see the full message list.",
          "A circular ! icon in the top bar appears while there are recent server messages and glows when one is unread. Click it to review recent messages and any active maintenance countdown.",
          "Admin can send server-wide messages (with ready-made templates) and start a maintenance countdown that automatically warns players at 1 hour, 30, 15, 5, 4, 3, 2, and 1 minute(s) remaining.",
        ],
      },
    ],
  },
  {
    version: "0.66",
    sections: [
      {
        title: "Play & Matchmaking",
        items: [
          "The Play menu is redesigned: pick VS CPU or VS Human, then Play Against CPU or Enter Queue vs Human. Selecting VS Human shows how many players are currently in queue.",
          "Queue vs Human pairs real players. A queue overlay shows your wait time, the live player count, and a Cancel Queue button; matches start 3 seconds after pairing.",
          "Draft, Adventure, and Tutorials are grouped into a single Modes section with picture cards.",
        ],
      },
      {
        title: "New Content",
        items: [
          "New Lore page in the navigation bar, starting with The Grand Tournament and Magic.",
          "New XP & Leveling tutorials: Earn Your Keep, The Long Game, and So Many Choices teach Quests, once-per-match quests, and leveling up into a Trait.",
          "Six new intermediate starter decks: Emberblade (fire) and Ashen Oath (Burn) for Fighter, Rime Volley (cold) and Winter's Grasp (Chill) for Scout, Stormbreak (lightning) and Static Dominion (Shock) for Mage.",
        ],
      },
      {
        title: "Deck Builder",
        items: [
          "New Analyze button opens a deck breakdown: power, attributes, types, slot and cost spreads, and average damage, healing, armor, ailment, draw, and resource output per card.",
        ],
      },
      {
        title: "Match UI",
        items: [
          "Tap a hand or board card to bring it into focus at the center of the screen with its actions below. Cards you cannot act on open the same view with Examine.",
          "View either player's discard pile from their portrait preview (most recent first).",
          "The turn indicator now sits between the boards and reads \"Your turn · Round N\" or \"<Name>'s turn · Round N\".",
          "Durability costs show as the Durability icon before the arrow on cards and action buttons. Strength, Dexterity, and Wisdom in card text show as their resource icons.",
          "Empty equipment slots highlight on hover and show \"Equip here\" when the selected card fits.",
          "Tokens have a dashed border, never show foils, and read \"Remove Permanently\" instead of returning to the deck.",
          "Physical and magic attack sounds now play when the attack animation lands.",
          "Fixed the view shaking when an ability in the rightmost slot attacked, and the Quests panel no longer runs off screen or behind the hand.",
          "Post-match advertisement unlock cards only appear when you still have cards or foils left to unlock.",
        ],
      },
      {
        title: "Cards & Rules",
        items: [
          "Added Fire, Cold, and Lightning Damage augments now deal their bonus as a separate hit of that element, mitigated by resistance instead of the ability's own damage type.",
          "War Band's slotted Strength bonus is now true physical damage: it adds to physical abilities, and non-physical abilities such as Fire Strike deal it as a separate physical hit.",
          "War Axe's slotted Melee cost reduction now applies correctly to abilities with augment costs.",
          "Identity costs show their full cost in card text. Emerald Charm now reads Cleanse Poison 1.",
          "Equipment damage previews include Ninjutsu throwable bonuses (for example Kunai with Next Throwable Output).",
        ],
      },
      {
        title: "CPU",
        items: [
          "Ninjutsu CPUs prioritize turn-start throwable token traits, value Melee Physical Critical Strike more against high Physical Armor, and no longer waste equipment expends that cannot pay out.",
        ],
      },
      {
        title: "Mobile App",
        items: [
          "The top match bar (Show Navbar, Game Log, Forfeit) now sits below the camera cutout and status bar on phones.",
          "The Android and iOS apps use the Aeterna Invicta icon and launch splash screen.",
        ],
      },
    ],
  },
  {
    version: "0.65",
    sections: [
      {
        title: "Currency & Marketplace",
        items: [
          "Classic queue matches award Currency (10 on victory; 1 on a completed long loss). Spend it in the Marketplace and Customize screens.",
          "Marketplace rotates every 6 hours (Central): locked cards for 5 Currency and missing foil variants for 10 Currency, with sold items marked clearly after purchase.",
          "Customize unlocks for boards and name colors can be bought with Currency (100 each) when you do not already own them.",
        ],
      },
      {
        title: "Draft",
        items: [
          "Draft Mode costs 10 Currency to enter, or is free once per day after 4:00 PM Central.",
          "Draft runs pay Currency by performance, up to 500 for a full 10-game clear with all three lives remaining.",
          "Strong draft clears award exclusive Draft Wizard stamp foils (1–3) on owned cards. Stamp foils are draft-only and do not appear in standard foil unlocks.",
        ],
      },
      {
        title: "Minions & Auras",
        items: [
          "Summon Golem, Summon Wisp, and Summon Imp scale with ability level: summon cost, Minion health, and expend output (heal/regen, physical damage, or fire damage and Burn) rise through Levels 1–4.",
          "Increased Minion Health and Increased Minion Power use level-scaled bonuses and costs instead of flat values. Increased Aura Power likewise scales by level.",
          "Gigantic Aura, Behemoth's Aura, Keen Eye, and Invoker's Boon scale with ability level (health, physical damage, ranged Critical Hit bonus damage, and Wisdom cost reduction).",
          "Invoker's Boon cost reduction is once per source per turn: after a discounted Wisdom action resolves, that source returns to its base cost for the rest of the turn.",
          "Summoner's Staff Power is now 6.",
        ],
      },
      {
        title: "Match",
        items: [
          "Send emoji emotes during matches; they float across the board with a short cooldown. Settings include an option to hide incoming emotes.",
          "PvP matches use clocks: 60s mulligan, then a 90s turn bank with +15s per completed turn. Three turn timeouts forfeit the match. Workshop, Tutorial, Adventure, and CPU matches stay untimed.",
        ],
      },
      {
        title: "Customize & Social",
        items: [
          "Pick a name color in Customize, including Rainbow, Metallic, and Gilded holo variants. Colors show on your name in matches and chat.",
          "Chat is opt-in once your account is eligible. Mute other players from chat, and open Privacy Policy and Delete Account pages from Account settings.",
          "Submit support tickets from in-game Help.",
        ],
      },
      {
        title: "Rewards & Account",
        items: [
          "Classic post-match unlocks are retuned: 3 cards and 2 foils on win (was 5 cards / 1 foil); 1 card on a qualifying loss.",
          "After classic wins or long losses, an optional advertisement reward can grant extra unlocks (+2 cards and +1 foil on win; +2 cards on a qualifying loss).",
          "Login sessions persist more reliably and expire cleanly when you sign out or the session ends.",
          "Tutorials are reorganized into clearer category groups.",
        ],
      },
    ],
  },
  {
    version: "0.64",
    sections: [
      {
        title: "Match UI",
        items: [
          "Playing equipment or abilities flies a full card face from your hand (or the opponent's) into the board slot, holds briefly, then slams home with a grey dust burst.",
          "Traditional card text on the board, deck editor, and collection resizes to fit dense effect lines instead of clipping mid-sentence.",
          "Card exams no longer repeat a keyword's full rules text when that keyword is already listed as a linked Passive (for example Armor Breaker on Mace).",
        ],
      },
      {
        title: "Equipment",
        items: [
          "Broken gear (0 Durability) can still be expended for resources, but not for other expend effects or buffs until repaired.",
          "Dual-attribute Identity armor is reworked: body pieces pay Identity for armor/Dodge; gloves restore Durability, add Combo, or load weapon Charges; boots cut next weapon or ability costs or Refresh the weapon; helms Restock for Identity + Neutral.",
          "Halberd expend is a flat 2 physical damage for 1 Durability (Durability 2).",
          "Convert Resource spends only the listed non-persistent resource (Neutral costs are not paid with Strength, Dexterity, or Wisdom).",
        ],
      },
      {
        title: "Adventure",
        items: [
          "Monsters telegraph attack timing and intended strikes more clearly.",
          "Encounter enemy counts and tier ranges are tightened so fights stay more readable.",
        ],
      },
      {
        title: "CPU",
        items: [
          "CPU plays better around Level Up Traits, ability levels, XP quests, lethal lines, and the new Identity gear actions.",
        ],
      },
      {
        title: "Account & Decks",
        items: [
          "Account settings support changing your password and permanently deleting your account (with username confirmation).",
          "Deck editor Power readout uses a clearer icon layout for Aspect, Equipment, Ability, and total Power.",
        ],
      },
      {
        title: "Art & Audio",
        items: [
          "Updated Bastion Helm card art.",
          "New match track: Before the Shield Wall.",
          "Five new menu tracks: Copper and Candlewick, Inside the Merchant's Vault, Ledger of Hidden Things, Silver for Secrets, and Trade at Twilight.",
        ],
      },
    ],
  },
  {
    version: "0.63",
    sections: [
      {
        title: "Traveler",
        items: [
          "Traveler’s Aspect base effect is once per turn: the next equipment or ability effect that costs at least 1 Neutral costs 1 less Neutral (minimum 0). Turn-start free Neutral is removed.",
          "Traveler uses a free-choice Trait tree (pick any option each Level Up, not branch-locked): Level 2 turn-start STR / DEX / WIS; Level 3 turn-start Physical Armor / Dodge / Magic Armor; Level 4 once-per-turn STR / DEX / WIS cost −1 (minimum 0); Level 5 +15 Health.",
          "Library: Traveler starter leans Neutral gear and Neutral-cost actives (Smite, Volley, Block, Ward) so the Aspect discount fires every turn; Prism Charm and Cleave / Precise Shot splash into primary attributes for Trait picks. Vitality keeps Traveler with a Neutral heal/armor package; Socket Charge moves to Mage.",
        ],
      },
      {
        title: "Match UI",
        items: [
          "Hand card action menus grow to fit long Invoke labels instead of clipping text at a fixed width.",
        ],
      },
    ],
  },
  {
    version: "0.62",
    sections: [
      {
        title: "Match UI",
        items: [
          "Quests under your XP bar lists the once-per-turn and once-per-match XP actions, with live checkmarks as you complete them. Match quests include filling all three board columns, equipping every gear slot type once (weapon, trinket, body, hands, feet, head), and activating a Level 2 Ability (including an Active with a matching Level 2+ Augment).",
        ],
      },
      {
        title: "Traits",
        items: [
          "Level Up Trait selection is now a connected tree: your Aspect base effect is the root node, with line-linked circle choices for Levels 2–5.",
          "Tap a node to open a Trait helper popup (click outside to close). Current choices glow yellow and require Confirm to lock in. Previously chosen Traits stay marked on the tree.",
          "Tree node shorthand shows turn-start resources and bonuses with attribute icons (for example +1 STR/turn).",
          "Aegis uses a branched Level Perk tree (shared Level 2 and 5, then A/B paths for 3–4) with Magic Armor cost discounts, gear empowerment, and Magical Armor retaliation.",
          "Arcanist uses a fully branched Level Perk tree (A/B from Level 2–5): Wisdom/health and ability level-ups on path A; magical-damage Invoke → +2 WIS, stacked magical damage charges, magical cost discounts, and discard recycle on path B.",
          "Brawler uses a connected horizontal Trait tree: allocate any node linked to your Aspect root or a previously chosen Trait (you can branch both ways). Nodes stack melee physical damage charges; path A leans health, path B leans armor, Invoke, unexpend, and free melee actions.",
          "Library: Slugfest (Brawler) packs melee weapons and Strength actives to burn stacked melee damage charges, with Heavy Blow / Greatsword / Spiked Shield as free and unexpend payoffs plus melee hand density for Invoke.",
          "Aspect card exams show the same connected Trait tree, with the Aspect base effect as the root node instead of a separate Effect line.",
          "Damage numbers and the health orb update when attack card animations connect with the target Aspect, not as soon as the action resolves.",
        ],
      },
      {
        title: "Ninjutsu",
        items: [
          "Ninjutsu now grants 1 Dodge 25% stack at the start of each turn.",
          "Throwable Tokens (Shuriken, Kunai, Poison Dart) are free to equip for Ninjutsu and leave play when spent.",
          "Ninjutsu uses a radial Trait hub: eight unique nodes around the Aspect base effect. Pick any unchosen node each Level Up (Levels 2–5).",
          "Trait options cover throwable Echo, throwable output and token generation, turn-start tokens with health or Dodge, ranged Ability Level Ups, Neutral from physical one-handed or ability hits, and melee physical Critical Strike.",
          "Library: Ninjutsu starter deck leans throwable weapons, ranged actives, and Dexterity tools built around the token loop.",
        ],
      },
      {
        title: "Art & Audio",
        items: [
          "Reworked Aspect art for Cleric, Fighter, Mage, Monochrome, Ninjutsu, and Scout.",
          "Four new menu tracks: Beneath the Tavern Floor, Twelve Copper Pieces, Curios at the Crossroads, and Silver for Ancient Lore.",
        ],
      },
    ],
  },
  {
    version: "0.61",
    sections: [
      {
        title: "Customize",
        items: [
          "Customize now picks your in-match player board (Flat unlocked by default; Elegant, Ice, Nature, and Volcanic unlock from foil rewards) instead of portrait and background.",
          "Queue and CPU match intros show each player's Aspect card instead of portrait and background.",
        ],
      },
      {
        title: "Match UI",
        items: [
          "Each seat's equipment board shows that player's selected board art behind their cards. You see both your board and your opponent's.",
          "YOU / OPP exams lead with the player's Aspect card.",
        ],
      },
      {
        title: "Unlocks",
        items: [
          "Locked boards share the foil unlock pool on classic wins (1 foil reward, or 3 when the full card collection is unlocked).",
          "Admin can unlock all player boards for a target account.",
        ],
      },
    ],
  },
  {
    version: "0.60",
    sections: [
      {
        title: "Core Rework",
        items: [
          "Aeterna Invicta has been rebuilt around a tighter dual-deck duel: equip into Weapon, Armor, and Trinket, then slot and activate Abilities from those columns.",
          "Starting life, hand size, and the equipment catalog are retuned for the new three-slot board. Off-hand and body gear still play as Armor; weapons play as Weapon; trinkets play as Trinket.",
          "Matches climb through player levels with XP. Leveling raises your max and current health, and opens Trait choices that shape the rest of the fight.",
        ],
      },
      {
        title: "Traits",
        items: [
          "On Level Up, a Level Up banner plays, then you choose a Trait for that level (2–5). You must pick before taking other actions or ending your turn.",
          "If you level up on the opponent's turn, your Trait choice appears on your next turn after you draw.",
          "All Aspects use the same Level Perk tree keyed to the Aspect attribute (Strength, Dexterity, Wisdom, or Neutral). Neutral defense options grant Physical Armor.",
          "Level Perks: start-of-turn resources with armor/dodge or health (2); permanently level up a slotted Ability (or Skip / no abilities for +3 XP) with armor/dodge or health (3); start-of-turn resources with persistent resources or a one-time cost cut (4); then once-per-turn matching equipment or ability output +2 (5).",
          "View Board / Choose Trait lets you peek at the board during selection. Selected Traits are listed in the Trait modal and on YOU / OPP portrait exams, by level. Level 3 Ability Level Ups highlight slotted Abilities matching your Aspect's primary attribute for you to pick, with an optional Skip for +3 XP.",
        ],
      },
      {
        title: "Hand & Equipment",
        items: [
          "Invoke: once per turn, discard a card from hand to resolve its Invoke effect. Free hand discard is no longer available.",
          "Equipment can carry Durability and Restock. Durability tracks wear on items that spend it; Restock draws extra cards on your next turn.",
          "Ability columns stay tied to Weapon, Armor, and Trinket. Host and augment slots follow the equipment in that column.",
          "Equipped equipment can be discarded even after it has been expended this turn.",
        ],
      },
      {
        title: "Match UI",
        items: [
          "XP gauge shows your current level and progress toward the next Trait. Quests under the gauge tracks once-per-turn and once-per-match XP goals.",
          "Portrait exams (YOU / OPP) show Aspect text plus Traits broken out by Level 2, 3, and 4.",
          "Hand hint control sits under the hand. Remaining Actions and resource fly-in feedback from recent builds remain.",
        ],
      },
    ],
  },
  {
    version: "0.55i",
    sections: [
      {
        title: "Tutorials",
        items: [
          "After confirming a tutorial summary, you return to the Tutorials list instead of the main menu.",
        ],
      },
      {
        title: "Match HUD",
        items: [
          "Remaining Actions strip on the action bar (Equip, Ability, Expend Gear, Activate Abl), with right-aligned labeling to keep the row on one line.",
          "Resources label on the bottom action bar. Resource icons stay centered between the left and right buttons, switch to half-size after 7, and wrap to a new row after 14 so side buttons are not pushed around.",
        ],
      },
      {
        title: "Resource Gain Feedback",
        items: [
          "Gaining resources plays a fly-in: icons pop from the played-card toast, spin down into the Resources bar, then pop into place when they arrive.",
          "Action-bar resource icons stay hidden until that fly-in finishes, so new gains no longer flash in early.",
        ],
      },
      {
        title: "Hand & Card Exam",
        items: [
          "Click a hand card for Equip / Slot / Examine / Invoke. Clicking elsewhere closes the menu.",
          "Card exam shows the art preview centered with the card name underneath.",
          "Board exam linked ability slots no longer crush vertically, and the exam scrollbar sits farther from the content.",
        ],
      },
    ],
  },
  {
    version: "0.55h",
    sections: [
      {
        title: "Aspects",
        items: [
          "Disciple of Pain now deals 2 additional damage while at 25% or less life (was 3).",
          "Way of the Agnostic tiers now come from exclusive-Neutral equipment and Neutral active abilities (up to 6). Tier 1 grants 2 XP once, Tier 2 grants 1 Restock once, Tier 3/4/6 grant turn-start Physical Armor / Magic Armor / Neutral, and Tier 5 grants +5 max health while held. Tier 7 and Neutral cost discounts are removed.",
        ],
      },
      {
        title: "Holo & Foils",
        items: [
          "Variants are now a layout plus a foil: Base, Borderless, and Frame, each with Rainbow, Grayscale, Galaxy, Beam, Diagonal Ray, Horizon, Ripple, Inverse Holo, Alternate Color, 3D, and Topography. Unlocks appear as names like Borderless Rainbow.",
          "Base layout keeps the description box clear: foil effects apply to the card image only.",
          "New foil: Topography — faint golden contour lines overlaid on the art. Every card draws its own contour map from its card id, and the lines brighten under the pointer.",
        ],
      },
    ],
  },
  {
    version: "0.55g",
    sections: [
      {
        title: "Board UI",
        items: [
          "Linked augments grey out after their matching active ability has been activated for the turn.",
          "Equipped board cards show more of their art with a lighter dark overlay.",
        ],
      },
    ],
  },
  {
    version: "0.55f",
    sections: [
      {
        title: "Armor",
        items: [
          "Physical and Magic Armor still reduce matching damage by their current stacks, then that hit removes 1 stack of the matching armor. Repeated smaller attacks can chip armor down instead of leaving a large pool untouched.",
          "Damage that ignores armor, and damage that does not match the armor type, does not remove armor stacks.",
        ],
      },
    ],
  },
  {
    version: "0.55e",
    sections: [
      {
        title: "Increased Aura Power",
        items: [
          "New Strength Augment: Increased Aura Power. Links to Aura Actives, multiplies their activation cost by 1.5x, and multiplies numerical Aura outputs by 1.25x (rounded down). Snapshotted when the Aura is activated.",
          "Behemoth's Aura physical damage bonus increased from 3 to 4 (5 with Increased Aura Power).",
          "Library decks Gigantic and Behemoth now include Increased Aura Power.",
        ],
      },
    ],
  },
  {
    version: "0.55e",
    sections: [
      {
        title: "Equipment Summoning Sickness",
        items: [
          "Newly equipped equipment cannot be expended until your next turn. Slotted abilities can still be activated immediately.",
        ],
      },
    ],
  },
  {
    version: "0.55d",
    sections: [
      {
        title: "Max Health Passives",
        items: [
          "Effects that add max health (Gigantic Aura, Jelly Armor, and similar) also raise current health by the same amount when granted.",
          "When those effects end, current health and max health both drop by the granted amount (this can defeat you).",
        ],
      },
    ],
  },
  {
    version: "0.55c",
    sections: [
      {
        title: "Aura Abilities",
        items: [
          "New Active subtype: Aura. Pay the activate cost to keep an Aura on the board; it replaces the host equipment like a Minion and suppresses that equipment's own effects while active.",
          "Examine an Aura to see its text plus the host equipment and other slotted abilities. Deactivate from the exam or board actions; the activation cost is not refunded.",
          "Gigantic Aura (Strength 4): +25 max and current health while active. When the Aura ends, max health and current health both drop by 25 (this can defeat you).",
          "Behemoth's Aura (Strength 4): +4 physical damage to activated equipment actions and Active abilities that already deal at least 1 physical damage.",
          "Keen Eye (Dexterity 4): your ranged damage that already deals at least 1 damage becomes Critical Hits (ignore armor).",
          "Invoker's Boon (Wisdom 6): Wisdom equipment actions and Wisdom Actives that cost Wisdom cost 1 less, to a minimum of 1.",
          "Library decks: Gigantic, Behemoth, Keen Eye, and Invoker, each built around one Aura.",
        ],
      },
    ],
  },
  {
    version: "0.55b",
    sections: [
      {
        title: "Essence Abilities",
        items: [
          "New Ability kind: Essence. Slot into equipment ability slots like Actives and Augments. Deck copy limit remains 2 of the same name.",
          "Two same-named Essence cards on one item only apply once.",
          "Inherent keyword: on Essence cards, Inherent means the host equipment's own effects. Essences enhance damage, armor, and similar effects on the equipment they are slotted into.",
          "Blacksmith's Essence: +3 inherent physical damage. Armorsmith's Essence: +1 inherent physical armor (passive and expend). Arcanist's Essence: +3 inherent magical damage. Warder's Essence: +1 inherent magic armor (passive and expend).",
          "Library starters: Forged Essence (Blacksmith's + Armorsmith's with Ironclad) and Arcane Ward (Arcanist's + Warder's with Aegis).",
          "Library: Arcane Barrage (Arcanist) stacks magical weapons, Magic Missile/Nova/Surge/Cataclysm, and Arcanist's Essence.",
          "Collection exam: unlocked layout and foil variants appear in a Variant dropdown beside the card art; the preview and full-size art view follow the selected variant.",
        ],
      },
    ],
  },
  {
    version: "0.55a",
    sections: [
      {
        title: "Cards",
        items: [
          "New equipment for Minion decks: Summoner's Staff (2H Wisdom staff, 4 linked slots, expend for 2 Wisdom) reduces slotted Minion active and augment costs by 1; primary attribute costs are reduced before Neutral.",
          "Packmaster's Cuirass (Dexterity body, 4 linked slots, expend for 2 Dexterity) reduces slotted Minion active costs by 1.",
        ],
      },
      {
        title: "Ninjutsu",
        items: [
          "Equipping a throwable token (Shuriken, Kunai, or Poison Dart) no longer uses your one equipment play per turn. You can equip a token and another piece of equipment in the same turn.",
        ],
      },
      {
        title: "UI",
        items: [
          "Gallery, Collection, and deck picker type filters now list the full catalog of card types, including Minion, Poison, Healing, and weapon subtypes.",
          "Sort by Type added to catalog and deck picker filters.",
        ],
      },
    ],
  },
  {
    version: "0.55",
    sections: [
      {
        title: "Minions",
        items: [
          "New Minion actives: Summon Golem (Strength), Summon Wisp (Dexterity), and Summon Imp (Wisdom). Activate to summon; while the Minion lives you cannot re-summon that ability.",
          "While a Minion is active, expend it once per turn for its action (Golem: physical damage; Wisp: heal or Regeneration; Imp: fire damage and Burn).",
          "Incoming damage hits your oldest Minions first, then you. Unsummon from the ability exam, board actions, or your status Minion chip. Discarding or unslotting the Summon ability also removes its Minion.",
          "New Minion augments: Increased Minion Health (+6 HP, +2 Strength cost) and Increased Minion Power (1.5x expend output, +2 Wisdom cost). Augments linked after a Minion is already summoned apply on the next summon, not to the living one.",
          "Expend buttons show augmented damage. Other damage augments that match a Minion active (such as Increased Physical Damage on Golem) work the same way when linked before summon.",
        ],
      },
      {
        title: "Match UI",
        items: [
          "Game Log damage lines name the target (player or specific Minion), including when damage is split or a Minion is destroyed.",
          "Top status bar no longer shows current resources for either seat; HP, status badges, and deck counts remain.",
          "Workshop: tap the Aspect slot on You or Opponent to swap Aspects.",
        ],
      },
    ],
  },
  {
    version: "0.54",
    sections: [
      {
        title: "Match HUD",
        items: [
          "Classic duels show an occupied-slot peek strip for the other seat above your board (Aspect and filled equipment only). Tap a chip to examine; use View Opp for the full board.",
          "While viewing the opponent board, the strip shows your own occupied slots.",
          "On wide landscape screens (desktop, tablet, foldables), Classic duels use a split layout: your board and hand on the left, opponent board on the right.",
          "Match status names now include Aspect, resources, status badges, and deck counts. Opening a portrait also shows that seat summary.",
        ],
      },
    ],
  },
  {
    version: "0.53",
    sections: [
      {
        title: "Adventure Mode Co-Op",
        items: [
          "Party up with up to 4 players from the Adventure hub. Invite others who are online and in the Adventure Mode menu.",
          "The inviting player is Party Leader. The party follows the leader's map; members can vote on available paths, and everyone confirms before an event begins.",
          "Story effects apply to all party members. Shop, Treasure, and post-combat card offers are instanced per player (overlap is still possible by chance). Everyone confirms before returning to the map.",
          "Co-op combat: all players act on the same turn. When everyone ends their turn, enemies attack every living party member.",
          "Enemy health scales with party size after rolling (2 players 150%, 3 players 200%, 4 players 250%).",
          "The party wins or loses together. Only the Party Leader can forfeit.",
          "In combat, View cycles your board, each ally board, and the enemy board. Damaging cards still target enemies.",
          "Adventure combat status bar shows only your own name and health.",
        ],
      },
    ],
  },
  {
    version: "0.53",
    sections: [
      {
        title: "Cards",
        items: [
          "Way of the Agnostic is now an Aspect. Exclusive-Neutral equipment and Neutral abilities deal bonus damage equal to your exclusive-Neutral equipment count; Strength, Dexterity, Wisdom, and hybrid sources deal no damage.",
          "Decks that still list the old Way of the Agnostic ability are invalid and are dropped on load. The Agnostic starter deck uses the Aspect and replaces those ability slots with Smite.",
        ],
      },
    ],
  },
  {
    version: "0.52",
    sections: [
      {
        title: "Aspects",
        items: [
          "Aspects are a new deck card type—your class for the match. Every deck must include exactly one Aspect alongside Equipment and Ability.",
          "Aspects apply a global passive for the duel (for example Traveler reduces the next Neutral cost by 1 once per turn).",
          "New Aspects: Brawler, Arcanist, Pyromancer, Cryomancer, Stormcaller, Toxicologist, Ironclad, Aegis, Duelist, Executioner, Fighter, Scout, Mage, Path of Focus, Disciple of Pain, Elementalist, Master of Skills, and Ninjutsu.",
          "Path of Focus: choose Strength, Dexterity, or Wisdom at the start of each turn; you cannot gain Neutral.",
          "Deck Builder, Library, Draft, Adventure, starters, and share/import all support Aspect selection.",
          "Draft opens with an Aspect pick (three distinct options), then continues through Equipment and Ability rounds.",
        ],
      },
      {
        title: "UI",
        items: [
          "Your body board shows your chosen Aspect card in a dedicated slot (replacing the in-board portrait). Click it to examine the Aspect.",
          "Click your name or your opponent's name in the match status bar to preview that player's portrait and background; click anywhere to close.",
          "Aspects use a dedicated card frame.",
        ],
      },
    ],
  },
  {
    version: "0.51",
    sections: [
      {
        title: "Cards",
        items: [
          "32 new hybrid equipment cards across every slot: Strength/Dexterity, Strength/Wisdom, Dexterity/Wisdom, and Strength/Dexterity/Wisdom.",
          "Hybrid resource OR cards show attribute icons separated by / and let you expend for one chosen attribute.",
          "Only body hybrid equipment can grant multiple attributes in a single expend (AND); all other hybrid slots are choose-one.",
          "Hybrid equipment does not stack both Physical Armor and Magic Armor as passives; those are temporary armor expend choices instead.",
          "Hybrid equipment uses dedicated multi-attribute card frames (Strength/Dexterity, Strength/Wisdom, Dexterity/Wisdom, and Strength/Dexterity/Wisdom).",
        ],
      },
      {
        title: "UI",
        items: [
          "Attribute filters in deck editor and workshop match any of a card's attributes.",
        ],
      },
    ],
  },
  {
    version: "0.50",
    sections: [
      {
        title: "Cards",
        items: [
          "New equipment: Vest of Immolation (body, Wisdom): 5 slots (3 linked, 2 unlinked); expend for 2 Wisdom or deal 4 fire damage and apply 2 Burn to yourself.",
        ],
      },
      {
        title: "Rules",
        items: [
          "Apply Ailment effects grant listed stacks (Burn, Chill, Shock, and other stack types) to Self or Opponent.",
        ],
      },
    ],
  },
  {
    version: "0.50",
    sections: [
      {
        title: "Holo & Foils",
        items: [
          "New foil: Diagonal Ray — diagonal rainbow sunpillar bands that shift with pointer tilt.",
          "New foil: Horizon — straight horizontal holo beams in the same family as Beam.",
          "New foil: 3D — base art stays normal; red, blue, and green offset copies sit at 50% opacity under the frame with a light metallic sheen and spotlight glare.",
        ],
      },
    ],
  },
  {
    version: "0.49",
    sections: [
      {
        title: "Cards",
        items: [
          "New ability: Chip Shot (active, Neutral): attack/ranged/physical; costs 1 Neutral; On Slot deals 1 physical damage; activate for 2 physical damage.",
        ],
      },
      {
        title: "Rules",
        items: [
          "Abilities can carry On Slot effects that resolve when that ability is slotted (including when moved into a new slot).",
        ],
      },
    ],
  },
  {
    version: "0.48",
    sections: [
      {
        title: "Cards",
        items: [
          "New equipment: Druid's Staff (two-handed, Wisdom): 3 linked slots; on equip gains 9 Nature Charges; expend for 2 Neutral or spend Nature Charges (3 each) to allocate Strength, Dexterity, and/or Wisdom.",
        ],
      },
      {
        title: "Rules",
        items: [
          "Nature Charges are local equipment counters that persist until spent (they do not clear at end of turn). Charge labels can name specialized counters on the card badge.",
        ],
      },
    ],
  },
  {
    version: "0.47",
    sections: [
      {
        title: "Cards",
        items: [
          "New equipment: Skillmaster's Ring (trinket, Neutral): no slots; on equip draw up to 1 ability if hand has room; expend for 1 Neutral.",
          "New equipment: Tactician's Ring (trinket, Neutral): no slots; on equip draw up to 1 equipment if hand has room; expend for 1 Neutral.",
        ],
      },
      {
        title: "Rules",
        items: [
          "Draw effects pull cards from the matching deck into hand up to the listed amount, stopping early if the hand is full or the deck is empty.",
        ],
      },
    ],
  },
  {
    version: "0.46",
    sections: [
      {
        title: "Cards",
        items: [
          "New equipment: Sol's Katana (one-handed, Dexterity): 1 unlinked slot; expend for 1 Dexterity or 1 physical damage (10 while it is your only weapon and Offhand is empty).",
        ],
      },
      {
        title: "Rules",
        items: [
          "Lone Weapon: if this is the only weapon in your equipment deck, it starts in your hand (including after Mulligan) and uses its Lone Weapon damage value when expended.",
        ],
      },
    ],
  },
  {
    version: "0.45",
    sections: [
      {
        title: "Deck Builder",
        items: [
          "Choose/Replace slot picker includes Suggest Cards: narrows options using your current Equipment and Ability decks (attributes, types, missing slots, cost curve, active vs augment balance). Requires at least one card already in either deck; suggestions tighten as the decks fill. Equipment suggestions prioritize entirely missing body slots first so decks can reach one of each slot.",
        ],
      },
    ],
  },
  {
    version: "0.44",
    sections: [
      {
        title: "Ranking",
        items: [
          "New Ranking navbar view: browse all players with CPU and human win/loss/tie records, sort by CPU or human win ratio, and page through 10 players at a time.",
          "Player names in Ranking open the same social examine options as Social.",
        ],
      },
      {
        title: "Community",
        items: [
          "New Community navbar view with a link to join the Discord server.",
        ],
      },
    ],
  },
  {
    version: "0.43",
    sections: [
      {
        title: "Library & Glossary",
        items: [
          "New Library navbar view: browse starter decks as square buttons, open a deck to examine its equipment and ability cards, and export/copy the deck JSON.",
          "New Glossary navbar view: searchable list of keywords, terms, and mechanics with descriptions and related cards you can examine.",
        ],
      },
      {
        title: "Queue",
        items: [
          "Classic deck selector includes Random Deck: when selected, a random valid deck from your account is chosen when a match starts.",
        ],
      },
      {
        title: "Cards",
        items: [
          "New equipment: Executioner's Hook, Kaleidoscopic Lens, Phoenix Mantle.",
          "New abilities: Shadow Step, Second Wind, Essence Siphon.",
          "Convert Stacks can heal (consume your own stacks for healing). Damage effects can heal for a fraction of actual damage dealt.",
        ],
      },
    ],
  },
  {
    version: "0.41e",
    sections: [
      {
        title: "Customize",
        items: [
          "New Customize navbar view: pick a portrait (Knight, Rogue, Witch, Wizard) and a background (Castle, Caves, Forest, Library, Sea).",
          "Live preview at the top of Customize shows your current portrait layered over your chosen background.",
          "Choices are saved to your profile and shown to opponents in queue matches.",
        ],
      },
      {
        title: "Match UI",
        items: [
          "Classic and Draft queue matches show a VS intro with each player's username, portrait, and background before the game begins. CPU opponents use a random portrait and background.",
          "Your portrait and background appear on the equipment board during matches. Switch to the opponent view to see theirs.",
          "Match status bar vitals now show your username and health on the left and your opponent's on the right.",
          "Armor and resistance badges no longer repeat their labels (for example, MAG ARMOR 1 instead of MAGICAL ARMOR MAG ARMOR 1).",
        ],
      },
    ],
  },
  {
    version: "0.41d",
    sections: [
      {
        title: "Match UI",
        items: [
          "Played equipment with local charges or counters shows a small square badge at the bottom-right of the card, styled like equipment quick actions.",
          "Tap the badge to open a helper popup with the counter name and amount (for example, Charges: 4). Tap again or press B to dismiss.",
          "Equipment card exams no longer repeat ON EQUIP or passive lines when structured card data already shows them.",
        ],
      },
      {
        title: "Cards",
        items: [
          "Plague Gloves and Snake Fang Necklace now increase Poison on slotted active poison abilities only.",
          "Updated art for Balanced Cudgel, Venommancer's Gloves, and Nova.",
        ],
      },
    ],
  },
  {
    version: "0.41c",
    sections: [
      {
        title: "Controls",
        items: [
          "Gamepad support: D-pad or left stick moves through options with a yellow selection outline. In Settings, D-pad highlights setting names; A opens that setting's choices, then A picks a value. B backs out. Select/Start switches between the navbar and the current view. Works with Bluetooth controllers and handhelds such as Retroid Pocket Nova and AYN Thor.",
        ],
      },
    ],
  },
  {
    version: "0.41b",
    sections: [
      {
        title: "Cards",
        items: [
          "New equipment: Eagle Shield, Viper Buckler, Machinist's Crossbow (Inherent Pierce), Poignard, Plague Gloves, Sanguine Greaves, Reliquary Pendant, Frostladen Cloak, Sigil of the Covenant.",
          "New abilities: Venom Strike, Venom Antidote.",
          "Inherent Abilities: equipment can grant a fixed ability (e.g. Pierce) that activates like a slotted active; item slot augments apply to it as if linked. Cannot be moved or unslotted.",
          "Temporary Bleed Resistance reduces incoming bleed stacks and clears at the end of the opponent's turn.",
        ],
      },
    ],
  },
  {
    version: "0.41a",
    sections: [
      {
        title: "Holo & Foils",
        items: [
          "Random holo unlocks only on victory: CPU (traditional or draft), random Human queue matches, or defeating a Boss in Adventure.",
          "No random holo from losses, Social Challenge matches, or non-boss Adventure wins/forfeits.",
          "Victory summary reliably shows the unlocked holo card after a winning blow.",
        ],
      },
    ],
  },
  {
    version: "0.41",
    sections: [
      {
        title: "Holo & Foils",
        items: [
          "Card hologram variants: Holo, Inverse Holo, Metallic, Grayscale, Alternate Color, Reverse Holo, Galaxy, and Diagonal.",
          "Galaxy foil uses cosmos star layers, a wandering white circular sheen, and rainbow reflection limited to that sheen.",
          "Alternate Color keeps the hue-shifted art and uses a metallic-style silver foil instead of a rainbow overlay.",
          "Pointer-following white glare removed from most foils so sheens stay cleaner; Galaxy keeps its circular glow.",
          "Settings: Foil Effects Maximum, Minimum (static), or Off.",
          "Collection and deck builder support picking unlocked foil variants per card.",
          "Match and adventure rewards can unlock a random holo variant. Admin can grant cards or holos per player.",
          "CPU opponents have a 10% chance per card to show a random foil for that match only.",
          "Hand, board, collection, and exam art (including expanded art) show foil effects.",
        ],
      },
      {
        title: "Collection",
        items: [
          "Full playsets of every card are available. Gallery removed in favor of Collection foil browsing.",
        ],
      },
    ],
  },
  {
    version: "0.40a",
    sections: [
      {
        title: "Match UI",
        items: [
          "Heal quick actions on equipment show the heal icon in the square action button.",
          "Clicking a slotted ability name in an equipment exam opens that ability's info exam.",
          "Floating vitals text appears on player and opponent names when damage, healing, armor, or status riders apply.",
          "Hybrid resource payment only prompts when more than one valid way to pay exists; otherwise the cost is taken automatically.",
          "Expend confirmation text stays on one line and no longer covers the action button.",
        ],
      },
      {
        title: "Settings",
        items: [
          "Hints (on by default): ? buttons near equipment and ability hands open contextual helper text.",
          "Card Names (on by default): show or hide card names in your hand and on slotted abilities on the board.",
        ],
      },
      {
        title: "UI",
        items: [
          "Forfeit and Show Navbar / Hide Navbar use compact text links in the match status bar.",
        ],
      },
    ],
  },
  {
    version: "0.40",
    sections: [
      {
        title: "Match UI",
        items: [
          "Traditional board layout: square quick-action buttons on played equipment for expend effects, refresh, and charges.",
          "Tap resource icons on equipment to expend for resources. Tap a quick action once to confirm, then again to expend: Tap again to expend: {effect}.",
          "Damage quick actions show the damage icon. Armor quick actions show the armor icon.",
          "Click a slotted ability on your board for a compact action popup: Activate or Discard.",
          "Hand and equipment exam popups use labeled rows (Attribute, Type, Activation Cost, Effect, and more). Type keywords open tooltips.",
          "Status badges on the board and action bar open keyword tooltips when clicked.",
          "Viewing the opponent board tints the seat panel with a darker orange background.",
          "Closing a normal CPU or human match summary returns to the main menu, even when a draft or adventure save exists.",
        ],
      },
      {
        title: "UI",
        items: [
          "Settings descriptions updated. Match log renamed to Game Log. Forfeit and disconnect messaging improved.",
          "Tutorial overlay opacity adjusted. Scroll hints and stacked action-bar text polish.",
          "Empty hand placeholders and EQUIPMENT / ABILITIES hand labels.",
          "Asset preload progress bar shows on the splash screen only, not on reconnect.",
        ],
      },
      {
        title: "Cards",
        items: [
          "Scholar's Sandals (feet, Wisdom): 1 slot; choose 1 temporary magic armor or Persist 1 Wisdom.",
          "Wayfarer Sabatons (feet, Neutral): 1 slot; choose 1 Neutral or Heal 2.",
          "Warden Greaves (feet, Strength): 1 slot; choose 1 temporary physical armor or 1 Strength; passive Physical Armor 1.",
          "Runic Gladius (one-handed magic, Wisdom): 2 linked slots; choose 2 magical damage or 1 Wisdom; passive +1 magical damage to slotted magic actives.",
          "Balanced Cudgel (one-handed melee, Neutral): 3 slots; 5 physical damage; passive Filled Socket Damage 1.",
          "Storm Pike (two-handed lightning magic, Wisdom): 2+2 linked slots; choose 5 lightning + Shock 1 or 1 Wisdom.",
          "Gain Thorns (Strength active): 1 Strength for 10% melee Thorns until end of turn.",
        ],
      },
    ],
  },
  {
    version: "0.36",
    sections: [
      {
        title: "Adventure Mode",
        items: [
          "Play Adventure Mode from the menu. Start a new run or continue a saved run.",
          "Roguelike map with branching paths: Combat, Shop, Treasure, and Story nodes. Revealed nodes show encounter previews.",
          "Journeyman's Pack: starter adventure deck and inventory separate from PvP decks.",
          "Hub tabs: Adventure Deck, Inventory (max 50 cards), and Map.",
          "Edit the adventure deck from inventory. Save anytime; travel requires a full valid 10 equipment + 10 ability deck with max 2 copies per card.",
          "Combat uses the dual-deck duel engine against a group of monsters. Only the player mulligans at combat start.",
          "Select enemies to target damaging attacks and Hex. The board view flips to enemies automatically.",
          "Equipment and ability exam overlays close when a damaging action needs an enemy target.",
          "Combat Summary: gold earned, defeated enemies, optional card rewards. Forfeit counts as defeat.",
          "Shops sell equipment and abilities. Story events grant persistent run effects such as melee damage bonuses, shop discounts, and extra treasure choices.",
          "Run effects clear after their encounter type (combat, shop, or treasure).",
        ],
      },
      {
        title: "Monsters",
        items: [
          "28 monsters with art, each rated difficulty 1–10 (hidden from the player).",
          "Encounters are built by tier: Common (5–10 total difficulty), Uncommon (11–15), Rare (16–25), and Very Rare (25 with a boss).",
          "Bosses: Lich, Dragon Whelp, Warlord, and Abomination.",
          "Monster attacks can deal damage, heal self or allies, and apply ailment riders.",
        ],
      },
      {
        title: "Adventure Combat Rules",
        items: [
          "Bleed, Poison, and Burn: monster takes 1 damage at end of enemy round, then loses 1 stack.",
          "Chill and Shock: monster deals 1 less outgoing damage per attack, then loses 1 stack at end of enemy round.",
          "Wither: at start of enemy round, monster loses current and max health equal to stacks; loses 1 stack at end without restoring health.",
          "Hex: reduces monster outgoing damage (50% at level 1, +5% per level, max 90%); clears at end of enemy round.",
          "Equipment exam text shows adventure-specific effect descriptions during combat.",
          "Multi-hit damage sweeps living enemies in order. Cancel a pending adventure attack without spending resources.",
        ],
      },
      {
        title: "Workshop",
        items: [
          "Leaving Workshop or switching to another screen ends the Workshop session.",
          "Reconnect restores your last hub screen, or your current live game when one is active.",
        ],
      },
      {
        title: "UI",
        items: [
          "Welcome splash with Enter Game. New players see a Tutorials shortcut.",
          "Assets preload in the background with a bottom progress bar.",
        ],
      },
    ],
  },
  {
    version: "0.33",
    sections: [
      {
        title: "Social",
        items: [
          "Spectate Game from a player's Social profile while they are In Game or In CPU Game (only if you are not in a match). Hands stay hidden as ? cards; forfeit leaves spectator mode.",
        ],
      },
    ],
  },
  {
    version: "0.32a",
    sections: [
      {
        title: "UI",
        items: [
          "Match summary includes a Log button to review the full Combat Log.",
          "Deck Builder can Delete a saved deck (must keep at least one). Confirm before delete.",
          "Queue requires a valid active deck.",
        ],
      },
    ],
  },
  {
    version: "0.32",
    sections: [
      {
        title: "Rules",
        items: [
          "Slotted Attribute Resource: grant 1 Strength, Dexterity, or Wisdom matching the slotted ability attribute; Neutral if empty, Neutral-attributed, or mixed.",
        ],
      },
      {
        title: "Cards",
        items: [
          "Captain's Tricorne (head, Dexterity): 2 linked slots; Persist 1 Dexterity.",
          "Venommancer's Gloves (hands, Dexterity): 2 linked slots; expend for 1 Dexterity or Poison 1.",
          "Lamellar Boots (feet, Dexterity): no slots; expend for Dodge; Refresh for 2 Dexterity.",
          "Corsair Coat (body, Dexterity): 4 linked slots; expend for 1 Dexterity or 2 physical + 2 magic armor.",
          "Mnemonic Diadem (head, Wisdom): 1 unlinked slot; Persist 1 Wisdom or 1 temporary magic armor.",
          "Acolyte Robes (body, Wisdom): 3+2 linked slots; choose 2 Wisdom, Heal 4, or Regeneration 2.",
          "Brilliant Prism (offhand, Neutral): 1 slot; Slotted Attribute Resource; Passive Physical Armor 1 and Magic Armor 1.",
        ],
      },
    ],
  },
  {
    version: "0.31",
    sections: [
      {
        title: "Rules",
        items: [
          "Temporary Slotted Ability Level: equipment can raise levels of matching slotted abilities until end of turn.",
        ],
      },
      {
        title: "Cards",
        items: [
          "Forged Mitts (hands, Strength): 2 linked slots; Persist 1 Strength; Passive Physical Armor 1.",
          "Iron Sabatons (feet, Strength): 2 linked slots; expend for 1 Strength or 2 temporary physical armor.",
          "Siege Plate (body, Strength): 2 linked + 2 unlinked; on equip 1 Strength; expend for 2 Strength or 2 temporary physical armor.",
          "Bloodpact Ring (trinket, Strength): expend to take 2 physical and gain Regeneration 2.",
          "War Axe (one-handed melee, Strength): 1 slot; expend for 1 Strength or 6 physical; +1 slotted melee.",
          "Tower Shield (offhand, Strength): 2 linked slots; expend for 1 Strength or 2 temporary physical armor.",
          "War Banner (two-handed, Strength): 2+2 linked + 1 unlinked; expend for 2 Strength or +1 slotted Strength ability level until end of turn.",
        ],
      },
    ],
  },
  {
    version: "0.30",
    sections: [
      {
        title: "Cards",
        items: [
          "Simple Coif (head, Neutral): 2 unlinked slots; expend for 1 temporary physical and magic armor.",
          "Courier Mitts (hands, Neutral): 1 slot; expend for 1 Persist Neutral or Heal 2.",
          "Bear Paws (feet, Neutral): 2 linked slots; expend for Dodge.",
          "Baton (one-handed melee, Neutral): 3 unlinked slots; +1 damage per filled socket; expend for 1 Neutral or 2 physical.",
          "Engulfed Torch (offhand, Neutral): 2 unlinked slots; expend for 1 Neutral or 1 fire + 2 physical as one pre-mitigation hit.",
          "Pike (two-handed melee, Neutral): 2 unlinked + 2+2 linked slots; expend for 2 Neutral or 6 physical Ignore Armor; +2 slotted melee output.",
        ],
      },
    ],
  },
  {
    version: "0.29",
    sections: [
      {
        title: "Rules",
        items: [
          "Charges: some equipment stores local charges. Add Charge pays a cost to gain charges. Effects can deal damage from charges and consume them. Charges clear at end of turn unless noted otherwise.",
        ],
      },
      {
        title: "Cards",
        items: [
          "Pillar of Earth (two-handed magic, Wisdom): 4+2 linked slots, expend for 2 Wisdom or spend all charges for 2 magical damage each, Add Charge for 1 Wisdom.",
        ],
      },
    ],
  },
  {
    version: "0.28",
    sections: [
      {
        title: "Rules",
        items: [
          "Distant Connection: buffs the next damage from a named weapon type (for example Crossbow). Can be paid on expend, or granted passively once per turn.",
          "Refresh: while equipment with Refresh is expended, pay its Refresh cost to unexpend it and use it again this turn.",
        ],
      },
      {
        title: "Cards",
        items: [
          "Quiver of Iron Bolts (offhand, Dexterity): pay 1 DEX and expend for Distant Connection (next Crossbow weapon +1 damage until end of turn).",
          "Stiletto (one-handed melee, Dexterity): 2 linked slots, expend for 1 Dexterity or 3 physical Ignore Armor damage, Refresh for 1 Dexterity.",
        ],
      },
    ],
  },
  {
    version: "0.27",
    sections: [
      {
        title: "Rules",
        items: [
          "Elemental Resistance: fire, cold, and lightning resistance reduce incoming damage of that type by their total amount.",
          "At the start of your turn, matching ailment stacks are reduced by your resistance (Burn for fire, Chill for cold, Shock for lightning) before those ailments resolve.",
        ],
      },
      {
        title: "Cards",
        items: [
          "Ember Helm (head, Strength): 1 ability slot, expend for 1 Strength, passive 1 Fire Resistance.",
          "Frigid Beret (head, Dexterity): 1 ability slot, expend for 1 Dexterity, passive 1 Cold Resistance.",
          "Humming Circlet (head, Wisdom): 1 ability slot, expend for 1 Wisdom, passive 1 Lightning Resistance.",
        ],
      },
    ],
  },
  {
    version: "0.26",
    sections: [
      {
        title: "Rules",
        items: [
          "Ability slot actions: once per turn (shared with slotting from hand, including Nimble Fingers), you may Move a slotted ability to any empty ability slot on your board, or Unslot it back to your ability hand if you have space (max 2).",
          "Moving keeps the ability instance as-is. Unslotting returns the card to hand; slotting it again later creates a fresh instance.",
          "Tutorials block Move and Unslot when those actions would skip the intended step.",
        ],
      },
      {
        title: "UI",
        items: [
          "Move and Unslot appear on slotted abilities in equipment exams, the slotted-ability exam, and the action-bar ability strip.",
          "After choosing Move, empty ability slots highlight so you can pick the destination.",
        ],
      },
    ],
  },
  {
    version: "0.25",
    sections: [
      {
        title: "UI",
        items: [
          "Match status bar shows opponent and player names with current/max health and a red heart icon.",
          "Board and action-bar layouts no longer repeat health; it lives only in the status bar.",
          "In-match equipment and ability exam panels leave the top status bar visible.",
        ],
      },
      {
        title: "Cards",
        items: [
          "Baresteel Aegis now grants 3 physical armor passively.",
          "Heavy Blow, Barrage, and Cataclysm: 3 mono-attribute actives for strength melee, dexterity ranged, and wisdom magic.",
          "Frozen Slam, Scorching Shot, and Storm Lance: 3-cost elemental actives (cold melee, fire ranged, lightning magic).",
          "Prismatic Strike (1 Strength + 1 Neutral) and Elemental Volley (1 Dexterity + 1 Neutral): 3 physical damage plus Burn, Chill, and Shock 1.",
          "Prismatic Spark: 2 Wisdom + 1 Neutral magic active that applies Burn 3, Chill 3, and Shock 3.",
        ],
      },
      {
        title: "Rules",
        items: [
          "Burn, Chill, and Shock can be applied as standalone ability effects without dealing damage.",
        ],
      },
    ],
  },
  {
    version: "0.24b",
    sections: [
      {
        title: "Cards",
        items: [
          "Might: strength augment that links to strength actives; +1 ability level and +1 Strength cost.",
          "Finesse: dexterity augment that links to dexterity actives; +1 ability level and +1 Dexterity cost.",
          "Insight: wisdom augment that links to wisdom actives; +1 ability level and +1 Wisdom cost.",
        ],
      },
      {
        title: "Rules",
        items: [
          "Augments can raise a linked active's ability level (levelAdd), scaling the active's effects and resource cost before other cost increases.",
        ],
      },
    ],
  },
  {
    version: "0.24a",
    sections: [
      {
        title: "Fixes",
        items: [
          "Ability slot link lines and augment linking only connect adjacent slots that are both linked. Unlinked slots no longer join a neighboring linked group (for example Barbed Armor and Adept Robe).",
        ],
      },
      {
        title: "Cards",
        items: [
          "Equipment slot layouts updated for the corrected linking rules, including Ranger Cloak, Chain Mail, Shadow Vest, Arc Staff, and several two-slot pieces.",
          "Angler's Rod and Padded Vest use two separate linked pairs.",
          "Art added for Barbed Armor, Travel Coat, Prism Charm, Scrap Launcher, Blood Magic, and the elemental damage augments.",
        ],
      },
    ],
  },
  {
    version: "0.24",
    sections: [
      {
        title: "Cards",
        items: [
          "Barbed Armor: strength body with 5 slots (4 linked) and Thorns against melee damage.",
          "Travel Coat: neutral body with 4 unlinked slots; expend for 1 temporary physical and magic armor.",
          "Prism Charm: neutral trinket; expend to convert 1 Neutral into Strength, Dexterity, or Wisdom.",
          "Scrap Launcher: dexterity two-handed ranged weapon; expend deals physical damage plus 1 per card in your discard piles.",
          "Added Fire Damage, Added Cold Damage, and Added Lightning Damage: augments that link to any damaging active.",
          "Fire Strike, Fireball, Ice Shot, Ice Storm, Conductive Strike, and Lightning Bolt deal 5 damage and apply 1 ailment.",
        ],
      },
      {
        title: "Rules",
        items: [
          "Thorns reflects a percent of melee damage taken back to the attacker.",
          "Elemental damage augments raise the linked active's attribute cost and add elemental damage plus Burn, Chill, or Shock.",
          "Augments can match damaging actives only (heals and other non-damage effects do not link).",
        ],
      },
    ],
  },
  {
    version: "0.23",
    sections: [
      {
        title: "UI",
        items: [
          "Card faces: centered names, equipment slot icons top-left, resource and cost icons top-right, type tags hidden.",
          "Slot and resource icons float at the card corners. Names, effects, and icons sit above the card frame.",
          "Art-card effect text is white. Card-face shorthand: PHYS. ARM., MAG. ARM., PHYS. DAM., MAG. DAM., STR, DEX, WIS, NEU.",
          "Augment cards hide match/type lines on the face. Effect text sits one line above the bottom frame.",
          "Larger displays scale card fonts, slot icons, and resource icons with card size. Mobile keeps the compact sizes.",
          "Mobile Gallery, Collection, Deck Builder, and slot picker show 2 cards per row.",
          "In-match hand cards use smaller names, slot icons, and resource icons.",
          "Collection owned count is a circle hanging off the bottom-left corner.",
          "Gallery and Collection add search, slot, kind, effect, cost, and sort filters. Collection can filter and sort by owned copies.",
          "Deck editor slot exam shows clickable art in the top right to open full-size art.",
        ],
      },
      {
        title: "Fixes",
        items: [
          "Inspecting equipped gear in a match no longer crashes.",
        ],
      },
    ],
  },
  {
    version: "0.21c",
    sections: [
      {
        title: "Cards",
        items: [
          "Blood Magic: strength sacrifice augment that links to any active ability.",
        ],
      },
      {
        title: "Rules",
        items: [
          "Blood Magic converts the linked active ability's full resource cost to health at 8 HP per resource (including augment cost adds and cost multipliers).",
          "Linked abilities show health cost instead of resources and pay HP on activation.",
        ],
      },
    ],
  },
  {
    version: "0.21b",
    sections: [
      {
        title: "UI",
        items: [
          "Equipment resource-choice expends (such as Labradorite Amulet) use resource icons on action buttons instead of text.",
          "While you have Shock, Gain buttons and played equipment preview which resource icon Shock will remove, wrapped in yellow parentheses.",
          "Shock preview appears on the action strip, equipment exam Actions, and resource grants shown on equipped gear.",
        ],
      },
    ],
  },
  {
    version: "0.21a",
    sections: [
      {
        title: "Cards",
        items: [
          "Pearl Amulet replaces Plain Amulet (2 neutral on expend).",
          "Labradorite Amulet: expend to gain 1 Strength, Dexterity, or Wisdom.",
          "Trickster Mitts: on equip, Nimble Fingers lets you slot one extra ability this turn.",
          "Jelly Armor: +20 max and current health while equipped; lose both when unequipped.",
          "Blacksmith's Ring, Conjurer's Earrings, and Quartz Necklace: attribute trinkets with armor passives.",
        ],
      },
      {
        title: "Rules",
        items: [
          "Bleed: you lose 1 stack at the end of your turn instead of all stacks. Expending equipment still deals 1 damage and removes 1 Bleed.",
        ],
      },
      {
        title: "UI",
        items: [
          "Equipment with multiple resource choices shows each option separated by /.",
        ],
      },
    ],
  },
  {
    version: "0.20b",
    sections: [
      {
        title: "UI",
        items: [
          "Tutorial hint bar spans the match width on mobile so longer steps read in fewer lines.",
          "Workshop has a Hide Controls button that collapses the top bar to a Show Controls pill and frees board space.",
          "Workshop bar buttons, labels, and resource steppers are slightly smaller.",
        ],
      },
    ],
  },
  {
    version: "0.20a",
    sections: [
      {
        title: "Tutorial",
        items: [
          "Connect the Dots: slot Increased Magical Damage into a linked socket with Magic Missile, gain wisdom, then activate for lethal damage.",
          "Tutorial highlights now cover linked augment slots when slotting and every wisdom resource expend before activation.",
          "Connect the Dots blocks wrong sockets, effect expends, discards, and early activation while each step is in progress.",
        ],
      },
      {
        title: "UI",
        items: [
          "Board equipment no longer shows a redundant expended label; strikethrough styling still marks spent gear.",
        ],
      },
    ],
  },
  {
    version: "0.19a",
    sections: [
      {
        title: "Tutorial",
        items: [
          "Arm Yourself!: equip Short Sword from your hand.",
          "Useful Slots: equip Leather Gloves, then slot Pierce into an empty socket.",
          "Tutorial scenarios now block off-path actions with toast guidance, such as End Turn, discard, or the wrong expend while a step is in progress.",
          "Tutorial highlights cover hand cards, Equip and Slot buttons, and empty ability slots on equipped gear.",
          "Tutorial Scenarios screen has a Return to Menu button.",
        ],
      },
    ],
  },
  {
    version: "0.18a",
    sections: [
      {
        title: "Tutorial",
        items: [
          "Tutorial hints now highlight the correct equipment strip and action buttons during play.",
          "Pay to Win step two explains that resources last until end of turn before asking you to activate Pierce.",
          "Completing a tutorial auto-opens the match summary after a short delay.",
        ],
      },
      {
        title: "Match",
        items: [
          "Slotted abilities can be discarded after they have been used this turn.",
        ],
      },
      {
        title: "UI",
        items: [
          "Ready glow on affordable actions now appears in the equipment exam modal as well as the action bar.",
          "Main menu groups Classic, Draft, and Tutorials into separate sections. Tutorials live on the home screen instead of a navbar button.",
          "Deck builder, collection, and slot picker cards lift slightly on hover.",
        ],
      },
    ],
  },
  {
    version: "0.17a",
    sections: [
      {
        title: "UI",
        items: [
          "Empty linked sockets glow gold when Slot would match an augment already on the piece, or when Slot would place an augment next to a matching active.",
          "Hand cards sit closer to the center of the screen.",
        ],
      },
      {
        title: "Social",
        items: [
          "A running Workshop no longer shows In Workshop after you leave that screen.",
        ],
      },
    ],
  },
  {
    version: "0.16a",
    sections: [
      {
        title: "Draft",
        items: [
          "Draft packs and decks can include more than two copies of the same card.",
        ],
      },
      {
        title: "Cards",
        items: [
          "Halberd expend damage is 7.",
          "Dodge shows 50% mitigation.",
        ],
      },
      {
        title: "UI",
        items: [
          "Navbar uses icons. Hover a button for its name.",
          "Deck and draft pickers show owned or pool copies, not copies already in the deck.",
          "Card pickers keep full cards spaced apart instead of stacking names.",
          "Heal toasts say healed health instead of taken damage.",
        ],
      },
      {
        title: "Social",
        items: [
          "Player status shows the current screen, such as Deck Editing or Viewing Collection.",
        ],
      },
    ],
  },
  {
    version: "0.15a",
    sections: [
      {
        title: "Draft",
        items: [
          "Draft Mode from Queue: 24 packs of 3, then a 10/10 deck from your pool.",
          "3 lives and up to 10 games. Queue draft matches vs CPU or vs Human.",
          "Best completed run is shown on your Social profile.",
        ],
      },
    ],
  },
  {
    version: "0.14b",
    sections: [
      {
        title: "UI",
        items: [
          "Card names in the Combat Log are clickable and open that card's exam. Close with Close or by clicking outside.",
        ],
      },
    ],
  },
  {
    version: "0.14a",
    sections: [
      {
        title: "Cards",
        items: [
          "Angler's Rod: Dexterity two-handed weapon. Four slots as two linked pairs. Expend for 2 Dexterity or to shuffle one random discarded card into its deck.",
          "Scrying Orb: Wisdom offhand. Expend for 1 Wisdom or to discard an ability in hand, then draw 1 if the ability deck has cards.",
        ],
      },
      {
        title: "Match",
        items: [
          "From Discard can pull a discarded equipment or ability card and shuffle it into its deck.",
          "Discard and Draw asks you to choose a glowing ability in hand, then discard it and draw 1 if the deck still has cards.",
        ],
      },
    ],
  },
  {
    version: "0.13a",
    sections: [
      {
        title: "Cards",
        items: [
          "Demon Pact now pays 6 health to gain 2 physical armor, 2 magical armor, and deal 4 magical damage.",
        ],
      },
    ],
  },
  {
    version: "0.12a",
    sections: [
      {
        title: "Cards",
        items: [
          "Enhance Attributes: Neutral 0-cost resource active. The host's next expend for resources this turn grants +1 of its attribute, or Neutral if mixed or Neutral. Once per turn. Cannot gain Encore.",
        ],
      },
    ],
  },
  {
    version: "0.11a",
    sections: [
      {
        title: "Workshop",
        items: [
          "Sandbox board for testing cards, resources, and turns without Queue.",
          "Add any card to either board. Max two copies of the same card per side.",
        ],
      },
    ],
  },
  {
    version: "0.10a",
    sections: [
      {
        title: "Cards",
        items: [
          "Poison Burst: Dexterity active. Consume all Poison on the opponent and deal level-scaled poison damage (1.25x / 1.5x / 2x / 2.5x, rounded up) for 2 / 2 / 3 / 4 DEX.",
          "Arc Bolt is now Magic Missile.",
          "Pierce now deals 5 physical damage.",
          "Leather Gloves have two linked ability slots.",
        ],
      },
      {
        title: "Match",
        items: [
          "Convert Stacks can consume all or a set number of a stack type on yourself or the opponent and convert them into another effect. Poison Add applies those stacks first, then Poison Burst consumes them.",
        ],
      },
      {
        title: "UI",
        items: [
          "Deck Builder uses a wider layout with more slots per row on large screens.",
          "Collection and gallery show paid expend costs as payment → effect.",
        ],
      },
    ],
  },
  {
    version: "0.09a",
    sections: [
      {
        title: "Cards",
        items: [
          "Consume From Within: Wisdom active. Apply 3 Wither to the opponent. Types: magic, dark.",
          "Invoke the Darkness: Dark augment for dark actives. Linked ability cost ×2 and output ×2.",
          "Demon Pact now pays 6 health to gain 6 physical armor, 6 magical armor, and deal 6 magical damage.",
        ],
      },
      {
        title: "Match",
        items: [
          "Wither: at the start of your turn, lose current and maximum health equal to your Wither stacks. At the end of your turn, lose 1 stack and restore 1 maximum health. Current health is not restored.",
        ],
      },
      {
        title: "UI",
        items: [
          "Deck Builder can export a shareable JSON copy and import a pasted deck as a new saved deck.",
        ],
      },
    ],
  },
  {
    version: "0.08a",
    sections: [
      {
        title: "Cards",
        items: [
          "Fire Strike and Fireball deal 3 fire damage and apply Burn 1 on a successful hit.",
          "Ice Shot and Ice Storm deal 3 cold damage and apply Chill 1 on a successful hit.",
          "Conductive Strike and Lightning Bolt deal 3 lightning damage and apply Shock 1 on a successful hit.",
        ],
      },
      {
        title: "Match",
        items: [
          "Burn: at the start of your turn, take 1 damage and lose 1 stack.",
          "Chill: active abilities cost +1 Neutral. Using an ability removes 1 stack. 1 stack is also removed at the end of your turn.",
          "Shock: the next resource grant from expended equipment or a resource-granting ability gives 1 less, colored attributes first. 1 stack is also removed at the end of your turn.",
        ],
      },
    ],
  },
  {
    version: "0.07a",
    sections: [
      {
        title: "Cards",
        items: [
          "Masochist's Crown: head equipment. On equip, take 10 physical damage. Expend for 2 Strength. Strength abilities +2 level. Two linked slots.",
          "Demon Pact: Wisdom active. Pay 5 health to gain 5 physical armor, 5 magical armor, and deal 5 magical damage. Types: magical, sacrifice, dark.",
          "Hexed Possessions now costs 2 Wisdom and 1 Neutral. It disables the chosen equipment's expend abilities. Extra levels add Neutral to slotted abilities on that item.",
          "New effect Damage Self. New types Sacrifice and Dark.",
        ],
      },
      {
        title: "Match",
        items: [
          "Health-cost abilities show (health) → effect.",
          "A warning appears if equipping or activating a card would cause you to lose.",
          "Equipment expend buttons show payment → effect.",
          "Successful expends and activations flash the button you used.",
          "Ready glow only appears when you can still pay the cost.",
          "Opponent armor and statuses are shown on the board and action bar views, not in the top status bar.",
        ],
      },
      {
        title: "UI",
        items: [
          "Larger board, hand, and status bars on desktop.",
          "Gallery, Collection, and deck picker can filter by Magical, Sacrifice, and Dark.",
        ],
      },
    ],
  },
  {
    version: "0.06a",
    sections: [
      {
        title: "Cards",
        items: [
          "Melee and Ranged type tags on weapons and matching abilities. Pierce and Flurry have both.",
          "Slotted Melee or Ranged actives gain bonus output on matching equipment. Stacks with existing slotted bonuses.",
          "Short Sword +1 melee. Mace +2 melee. Dagger, Poison Shiv, Club, and Spiked Shield +1 melee. Greatsword and Halberd +3 melee. Spear +2 melee.",
          "Hand Crossbow +1 ranged. Longbow +1 ranged.",
        ],
      },
      {
        title: "UI",
        items: [
          "Gallery, Collection, and deck picker can filter by Melee and Ranged.",
        ],
      },
    ],
  },
  {
    version: "0.05a",
    sections: [
      {
        title: "Cards",
        items: [
          "Way of the Agnostic now deals 2 damage, plus 2 for each exclusive-Neutral equipment.",
          "Hexed Possessions now costs 1 Wisdom.",
          "Increased Vitality is now Increase Health Output.",
          "Added Iron Overload, Aegis Nova, Hexed Possessions, Attack Echo, and Spell Echo.",
          "Added equipment including Bastion Helm, Keen Cap, Focus Gloves, Steadfast Gloves, Encore Boots, Sage Treads, Vital Cowl, Vital Vest, Charge Wand, and Halberd.",
          "Shield Bash now scales with equipped shields.",
          "Elemental damage, Bleed, Poison, Persist resources, and on-equip effects.",
        ],
      },
      {
        title: "Match",
        items: [
          "The first player of Round 1 cannot deal damage. The status row shows *FIRST-TURN PENALTY* during that turn.",
          "Action bar view now shows player and opponent health.",
          "Your own expend and ability toasts are hidden. Opponents still see them.",
          "Attribute icons for Strength, Dexterity, Wisdom, and Neutral.",
          "Activate currently slotted damage abilities in one action.",
        ],
      },
      {
        title: "Queue and Decks",
        items: [
          "Choose your current deck from the Queue screen.",
          "Clone a deck in the deck editor.",
          "Up to 100 saved decks.",
        ],
      },
      {
        title: "Social",
        items: [
          "The player list shows everyone who has logged in.",
          "Recently Online: last seen within one hour. Offline: last seen more than one hour ago.",
          "Chat and challenges.",
        ],
      },
      {
        title: "UI",
        items: [
          "Card artwork updates.",
          "Version number in the header.",
        ],
      },
    ],
  },
];

// Admin-only export of one patch-notes version as Discord-flavored Markdown.
// Discord rejects messages over 2,000 characters, so long notes are split into
// numbered parts at section (then line) boundaries.
const DISCORD_MESSAGE_LIMIT = 2000;

function formatPatchNotesMarkdown(entry) {
  const lines = [`## Aeterna Invicta v${entry.version}`];
  entry.sections.forEach((section) => {
    lines.push("", `### ${section.title}`);
    section.items.forEach((item) => lines.push(`- ${item}`));
  });
  return lines.join("\n");
}

function splitPatchNotesForDiscord(text, limit = DISCORD_MESSAGE_LIMIT) {
  const blocks = text.split(/\n(?=### )/);
  const parts = [];
  let current = "";
  const pushLines = (block) => {
    block.split("\n").forEach((line) => {
      const next = current ? `${current}\n${line}` : line;
      if (next.length > limit && current) {
        parts.push(current);
        current = line.slice(0, limit);
      } else {
        current = next.slice(0, limit);
      }
    });
  };
  blocks.forEach((block) => {
    const next = current ? `${current}\n${block}` : block;
    if (next.length <= limit) {
      current = next;
      return;
    }
    if (current) parts.push(current);
    current = "";
    if (block.length <= limit) current = block;
    else pushLines(block);
  });
  if (current) parts.push(current);
  return parts;
}

function downloadTextFile(fileName, text, type = "text/markdown") {
  const blob = new Blob([text], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
const AETERNA_ASSET_ROOT = resolveAeternaAssetPath("/assets/aeterna-invicta");
const CARD_FRAME_BASE = `${AETERNA_ASSET_ROOT}/cardFrames`;

// Versioned asset URL for a path under the assets root ("cards/eq_x.webp").
function aeternaAssetUrl(relPath, options) {
  return assetVersionedUrl(AETERNA_ASSET_ROOT, relPath, options);
}

function cardFrameAssetUrl(file) {
  return aeternaAssetUrl(`cardFrames/${file}`);
}
const CARD_FRAME_FILES = {
  "strength:equipment": "card_frame_strength_equipment.webp",
  "strength:ability": "card_frame_strength_ability.webp",
  "dexterity:equipment": "card_frame_dexterity_equipment.webp",
  "dexterity:ability": "card_frame_dexterity_ability.webp",
  "wisdom:equipment": "card_frame_wisdom_equipment.webp",
  "wisdom:ability": "card_frame_wisdom_ability.webp",
  "neutral:equipment": "card_frame_neutral_equipment.webp",
  "neutral:ability": "card_frame_neutral_ability.webp",
  "strength+dexterity:equipment": "card_frame_strength_dexterity_equipment.webp",
  "strength+wisdom:equipment": "card_frame_strength_wisdom_equipment.webp",
  "dexterity+wisdom:equipment": "card_frame_wisdom_dexterity_equipment.webp",
  "strength+dexterity+wisdom:equipment":
    "card_frame_strength_dexterity_wisdom_equipment.webp",
  aspect: "card_frame_aspect.webp",
};
const SIMPLE_CARD_FRAME_FILES = {
  strength: "card_frame_simple_red.webp",
  dexterity: "card_frame_simple_green.webp",
  wisdom: "card_frame_simple_blue.webp",
  neutral: "card_frame_simple_white.webp",
  hybrid: "card_frame_simple_bronze.webp",
};
const MATCH_PLAY_MESSAGE_TYPES = new Set([
  "ai:playEquipment",
  "ai:playAbility",
  "ai:moveAbility",
  "ai:unslotAbility",
  "ai:expendEquipment",
  "ai:refreshEquipment",
  "ai:addEquipmentCharge",
  "ai:sharpenEquipment",
  "ai:honeEquipment",
  "ai:fletchEquipment",
  "ai:enableEncoreEquipment",
  "ai:researchEquipment",
  "ai:activateSlottedDamageEquipment",
  "ai:repairEquipment",
  "ai:expendAllForResources",
  "ai:activateCurrentAbilitiesForDamage",
  "ai:repeatLastTurnActions",
  "ai:activateAbility",
  "ai:expendMinion",
  "ai:unsummonMinion",
  "ai:deactivateAura",
  "ai:resolveAbilityTarget",
  "ai:cancelAbilityTarget",
  "ai:resolveAspectChoice",
  "ai:resolveTraitChoice",
  "ai:resolveTraitAbilityLevel",
  "ai:skipTraitAbilityLevel",
  "ai:resolveTraitEquipmentChoice",
  "ai:skipTraitEquipmentChoice",
  "ai:discardEquipment",
  "ai:discardAbility",
  "ai:discardHand",
  "ai:discardHandEquipment",
  "ai:discardHandAbility",
  "ai:invokeHand",
  "ai:endTurn",
  "ai:mulligan",
  "ai:ready",
]);
const ACTION_FLASH_MS = 560;
const READY_END_TURN_LOCK_MS = 2000;

function buildGameLogExportDocument(payload) {
  const {
    match: matchInfo,
    players: exportPlayers,
    summary,
    snapshot,
    timeline,
    ...header
  } = payload;
  const exportedLocally = new Date();
  let timeZone = null;
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    timeZone = null;
  }
  return {
    ...header,
    client: {
      gameVersion: GAME_VERSION,
      platform: getNativePlatform(),
      timeZone,
      exportedAtLocal: exportedLocally.toLocaleString(),
    },
    match: matchInfo,
    players: exportPlayers,
    summary,
    snapshot,
    timeline,
  };
}

function gameLogExportFileName(payload) {
  const matchId = String(payload?.match?.id || "match")
    .replace(/[^a-zA-Z0-9_-]+/g, "_")
    .slice(0, 60);
  const stamp = String(payload?.exportedAt || new Date().toISOString())
    .replace(/\.\d+Z$/, "Z")
    .replace(/:/g, "-");
  return `aeterna-invicta-game-log_${matchId}_${stamp}.json`;
}

async function deliverGameLogExport(payload) {
  if (!payload || typeof payload !== "object") return "failed";
  const text = JSON.stringify(buildGameLogExportDocument(payload), null, 2);
  const fileName = gameLogExportFileName(payload);
  if (isNativeApp()) {
    let shared = false;
    try {
      if (typeof File !== "undefined" && navigator.canShare) {
        const file = new File([text], fileName, { type: "application/json" });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: fileName });
          shared = true;
        }
      }
    } catch {
      shared = false;
    }
    if (shared) return "shared";
    try {
      await navigator.clipboard.writeText(text);
      return "copied";
    } catch {
      return "failed";
    }
  }
  try {
    const blob = new Blob([text], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return "downloaded";
  } catch {
    return "failed";
  }
}

function actionFlashKeyFromPayload(payload) {
  if (!payload) return null;
  if (payload.type === "ai:expendEquipment") {
    if (payload.mode === "resources") {
      return `expend:${payload.instanceId}:resources`;
    }
    return `expend:${payload.instanceId}:effect:${payload.effectIndex ?? "all"}`;
  }
  if (payload.type === "ai:refreshEquipment") {
    return `refresh:${payload.instanceId}`;
  }
  if (payload.type === "ai:addEquipmentCharge") {
    return `charge:${payload.instanceId}`;
  }
  if (payload.type === "ai:sharpenEquipment") {
    return `sharpen:${payload.instanceId}`;
  }
  if (payload.type === "ai:honeEquipment") {
    return `hone:${payload.instanceId}`;
  }
  if (payload.type === "ai:fletchEquipment") {
    return `fletch:${payload.instanceId}`;
  }
  if (payload.type === "ai:enableEncoreEquipment") {
    return `enableEncore:${payload.instanceId}`;
  }
  if (payload.type === "ai:researchEquipment") {
    return `research:${payload.instanceId}`;
  }
  if (payload.type === "ai:activateSlottedDamageEquipment") {
    return `slottedDamage:${payload.instanceId}`;
  }
  if (payload.type === "ai:repairEquipment") {
    return `repair:${payload.instanceId}`;
  }
  if (payload.type === "ai:activateAbility") {
    return `activate:${payload.abilityInstanceId}`;
  }
  if (payload.type === "ai:expendMinion") {
    return `expendMinion:${payload.abilityInstanceId}:${payload.effectIndex ?? "all"}`;
  }
  if (payload.type === "ai:unsummonMinion") {
    return `unsummonMinion:${payload.abilityInstanceId}`;
  }
  if (payload.type === "ai:deactivateAura") {
    return `deactivateAura:${payload.abilityInstanceId}`;
  }
  return null;
}

function padMatchHand(hand, alignEnd = false) {
  const cards = Array.isArray(hand) ? hand : [];
  const count = Math.max(MATCH_HAND_SLOTS, cards.length);
  const empties = Array.from({ length: count - cards.length }, () => null);
  return alignEnd ? empties.concat(cards) : cards.concat(empties);
}

function deckIdsEqual(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
    return false;
  }
  return a.every((id, index) => id === b[index]);
}

function isDraftDeckValid(equipment, ability, aspectId) {
  return (
    filledDeckIds(equipment).length === MIN_DECK &&
    filledDeckIds(ability).length === MIN_DECK &&
    typeof aspectId === "string" &&
    Boolean(aspectId)
  );
}

function getCardPower(card) {
  const value = Number(card?.power);
  if (!Number.isFinite(value) || value < 0) return 0;
  return Math.floor(value);
}

function playerOwnsCard(collection, cardId) {
  if (!cardId) return false;
  return Number(collection?.[cardId]) > 0;
}

function deckUsesOnlyOwnedCards(equipmentIds, abilityIds, aspectId, collection) {
  const ids = [
    ...(equipmentIds || []),
    ...(abilityIds || []),
  ];
  if (aspectId) ids.push(aspectId);
  return ids.every((cardId) => playerOwnsCard(collection, cardId));
}

function sumDeckIdsPower(ids, catalogMap) {
  return (ids || []).reduce((total, cardId) => {
    if (!cardId) return total;
    return total + getCardPower(catalogMap?.[cardId]);
  }, 0);
}

function getDeckPairPowerTotals(equipmentIds, abilityIds, aspectId, catalogMap) {
  const equipmentPower = sumDeckIdsPower(equipmentIds, catalogMap);
  const abilityPower = sumDeckIdsPower(abilityIds, catalogMap);
  const aspectPower = aspectId
    ? getCardPower(catalogMap?.[aspectId])
    : 0;
  return {
    equipmentPower,
    abilityPower,
    aspectPower,
    totalPower: equipmentPower + abilityPower + aspectPower,
  };
}

function isDeckPairWithinPower(equipmentIds, abilityIds, aspectId, catalogMap) {
  return (
    getDeckPairPowerTotals(equipmentIds, abilityIds, aspectId, catalogMap)
      .totalPower <= MAX_DECK_POWER
  );
}

function isClassicDeckValid(equipment, ability, aspectId, catalogMap, collection) {
  if (!isDraftDeckValid(equipment, ability, aspectId)) return false;
  const equipmentIds = filledDeckIds(equipment);
  const abilityIds = filledDeckIds(ability);
  if (!deckUsesOnlyOwnedCards(equipmentIds, abilityIds, aspectId, collection)) {
    return false;
  }
  return isDeckPairWithinPower(
    equipmentIds,
    abilityIds,
    aspectId,
    catalogMap,
  );
}

function deckListWithinCopyLimit(ids) {
  const counts = countIds(ids);
  return Object.values(counts).every((count) => count <= MAX_COPIES);
}

function isAdventureDeckValid(equipment, ability, aspectId) {
  const equipmentIds = filledDeckIds(equipment);
  const abilityIds = filledDeckIds(ability);
  return (
    equipmentIds.length === MIN_DECK &&
    abilityIds.length === MIN_DECK &&
    deckListWithinCopyLimit(equipmentIds) &&
    deckListWithinCopyLimit(abilityIds) &&
    typeof aspectId === "string" &&
    Boolean(aspectId)
  );
}

function buildDeckSharePayload(name, equipmentIds, abilityIds, aspectId) {
  return {
    name: (name || "Deck").trim().slice(0, 40) || "Deck",
    equipmentIds: [...(equipmentIds || [])],
    abilityIds: [...(abilityIds || [])],
    aspectId: aspectId || DEFAULT_ASPECT_ID,
  };
}

function extractDeckShareJson(text) {
  const raw = String(text || "").trim();
  if (!raw) return "";
  const fenced = raw.match(/```(?:json)?\s*([\s\S]*?)```/i);
  return (fenced ? fenced[1] : raw).trim();
}

function normalizeShareIdList(value) {
  if (!Array.isArray(value)) return [];
  return value
    .filter((id) => typeof id === "string")
    .map((id) => id.trim())
    .filter(Boolean);
}

function validateShareCardList(ids, expectedType, label, catalogMap) {
  if (ids.length !== MIN_DECK) {
    return {
      ok: false,
      message: `${label} must be exactly ${MIN_DECK} cards.`,
    };
  }
  const counts = countIds(ids);
  for (const [cardId, count] of Object.entries(counts)) {
    if (count > MAX_COPIES) {
      return {
        ok: false,
        message: `${label}: max ${MAX_COPIES} copies of ${cardId}.`,
      };
    }
    const card = catalogMap?.[cardId];
    if (!card || !cardHasType(card, expectedType)) {
      return { ok: false, message: `${label}: invalid card ${cardId}.` };
    }
  }
  return { ok: true };
}

function parseDeckShareText(text, catalogMap, options = {}) {
  const raw = extractDeckShareJson(text);
  if (!raw) return { ok: false, message: "Paste a deck JSON first." };
  let data;
  try {
    data = JSON.parse(raw);
  } catch {
    return { ok: false, message: "Invalid JSON." };
  }
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return { ok: false, message: "Invalid deck data." };
  }
  const name =
    typeof data.name === "string"
      ? data.name.trim().slice(0, 40) || "Deck"
      : "Deck";
  const equipmentIds = normalizeShareIdList(
    data.equipmentIds || data.equipment,
  );
  const abilityIds = normalizeShareIdList(data.abilityIds || data.ability);
  const equipment = validateShareCardList(
    equipmentIds,
    "equipment",
    "Equipment deck",
    catalogMap,
  );
  if (!equipment.ok) return equipment;
  const ability = validateShareCardList(
    abilityIds,
    "ability",
    "Ability deck",
    catalogMap,
  );
  if (!ability.ok) return ability;
  const aspectId =
    typeof data.aspectId === "string" && data.aspectId.trim()
      ? data.aspectId.trim()
      : DEFAULT_ASPECT_ID;
  const aspectCard = catalogMap?.[aspectId];
  if (!aspectCard || !cardHasType(aspectCard, "aspect")) {
    return { ok: false, message: "Deck must include exactly one Aspect." };
  }
  if (
    options.collection &&
    !deckUsesOnlyOwnedCards(
      equipmentIds,
      abilityIds,
      aspectId,
      options.collection,
    )
  ) {
    return { ok: false, message: "Deck includes locked cards." };
  }
  const power = getDeckPairPowerTotals(
    equipmentIds,
    abilityIds,
    aspectId,
    catalogMap,
  ).totalPower;
  if (power > MAX_DECK_POWER) {
    return {
      ok: false,
      message: `Combined deck Power cannot exceed ${MAX_DECK_POWER} (currently ${power}).`,
    };
  }
  return { ok: true, name, equipmentIds, abilityIds, aspectId };
}

const ATTR_CLASS = {
  strength: styles.attrStrength,
  dexterity: styles.attrDexterity,
  wisdom: styles.attrWisdom,
  neutral: styles.attrNeutral,
};

const ATTR_ICON_URL = {
  strength: `${AETERNA_ASSET_ROOT}/icons/icon_strength.svg`,
  dexterity: `${AETERNA_ASSET_ROOT}/icons/icon_dexterity.svg`,
  wisdom: `${AETERNA_ASSET_ROOT}/icons/icon_wisdom.svg`,
  neutral: `${AETERNA_ASSET_ROOT}/icons/icon_neutral.svg`,
  physicalArmor: `${AETERNA_ASSET_ROOT}/icons/icon_physical_armor.svg`,
};

const DEFAULT_CARD_SET = "base";
const CARD_SET_LABELS = {
  base: "Base",
  gorram_desert: "Gorram Desert",
};
const CARD_SET_ICON_FILES = {
  base: "set_icon_base.svg",
  gorram_desert: "set_icon_gorram_desert.svg",
};
const CARD_SET_DESCRIPTIONS = {
  base: "The core Aeterna Invicta expansion. Starter decks and the standard card pool come from Base.",
  gorram_desert:
    "An upcoming expansion set from the Gorram Desert. Its cards will join the catalog when the set releases.",
};
const CARD_SET_ICON_BASE = `${AETERNA_ASSET_ROOT}/set`;

function normalizeCardSet(raw) {
  const value = String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return value || DEFAULT_CARD_SET;
}

function formatCardSetLabel(setId) {
  const normalized = normalizeCardSet(setId);
  return CARD_SET_LABELS[normalized] || normalized.replace(/_/g, " ");
}

function getCardSetIconUrl(setId) {
  const normalized = normalizeCardSet(setId);
  const file =
    CARD_SET_ICON_FILES[normalized] || CARD_SET_ICON_FILES[DEFAULT_CARD_SET];
  return `${CARD_SET_ICON_BASE}/${file}`;
}

function getCardSetKeywordId(setId) {
  return `set_${normalizeCardSet(setId)}`;
}

function getCardSetKeyword(setId) {
  const normalized = normalizeCardSet(setId);
  return {
    id: getCardSetKeywordId(normalized),
    label: formatCardSetLabel(normalized),
    description:
      CARD_SET_DESCRIPTIONS[normalized] ||
      `${formatCardSetLabel(normalized)} expansion set.`,
  };
}

const DAMAGE_ICON_URL = `${AETERNA_ASSET_ROOT}/icons/icon_damage.svg`;
const ARMOR_ICON_URL = `${AETERNA_ASSET_ROOT}/icons/icon_armor.svg`;
const HEAL_ICON_URL = `${AETERNA_ASSET_ROOT}/icons/icon_heal.svg`;
const DURABILITY_ICON_URL = `${AETERNA_ASSET_ROOT}/icons/icon_durability.svg`;
const PLACEHOLDER_COUNTER_ICON_URL = `${AETERNA_ASSET_ROOT}/icons/icon_placeholder_counter.svg`;
const ABILITY_LEVEL_ICON_URL = `${AETERNA_ASSET_ROOT}/icons/icon_ability_level.svg`;
const EQUIPMENT_TIER_ICON_URL = `${AETERNA_ASSET_ROOT}/icons/icon_equipment_tier.svg`;
const POWER_ICON_URL = `${AETERNA_ASSET_ROOT}/icons/icon_power.svg`;
const EXPEND_ACTIVATE_ICON_URL = `${AETERNA_ASSET_ROOT}/icons/icon_expend_activate.svg`;
const INVOKE_ICON_URL = `${AETERNA_ASSET_ROOT}/icons/icon_invoke.svg`;
const ARROW_ICON_URL = `${AETERNA_ASSET_ROOT}/icons/icon_arrow.svg`;
const LOCKED_ICON_URL = `${AETERNA_ASSET_ROOT}/icons/icon_locked.svg`;
const CURRENCY_ICON_URL = `${AETERNA_ASSET_ROOT}/icons/icon_currency.svg`;
const ELEMENTAL_DAMAGE_ICON_URL = {
  physical: `${AETERNA_ASSET_ROOT}/icons/icon_physical_damage.svg`,
  magical: `${AETERNA_ASSET_ROOT}/icons/icon_magical_damage.svg`,
  fire: `${AETERNA_ASSET_ROOT}/icons/icon_fire_damage.svg`,
  cold: `${AETERNA_ASSET_ROOT}/icons/icon_cold_damage.svg`,
  lightning: `${AETERNA_ASSET_ROOT}/icons/icon_lightning_damage.svg`,
};

const DAMAGE_TYPE_KEYWORD_ID = {
  physical: "physicalDamage",
  magical: "magicalDamage",
  fire: "fire",
  cold: "cold",
  lightning: "lightning",
};

const DAMAGE_TYPE_COUNT_CLASS = {
  physical: "effectDamageTypeCountPhysical",
  magical: "effectDamageTypeCountMagical",
  fire: "effectDamageTypeCountFire",
  cold: "effectDamageTypeCountCold",
  lightning: "effectDamageTypeCountLightning",
};
const STATUS_EFFECT_ICON_URL = {
  bleed: `${AETERNA_ASSET_ROOT}/icons/icon_bleed.svg`,
  burn: `${AETERNA_ASSET_ROOT}/icons/icon_burn.svg`,
  chill: `${AETERNA_ASSET_ROOT}/icons/icon_chill.svg`,
  poison: `${AETERNA_ASSET_ROOT}/icons/icon_poison.svg`,
  restock: `${AETERNA_ASSET_ROOT}/icons/icon_restock.svg`,
  shock: `${AETERNA_ASSET_ROOT}/icons/icon_shock.svg`,
  dodge: `${AETERNA_ASSET_ROOT}/icons/icon_dodge.svg`,
  shadow: `${AETERNA_ASSET_ROOT}/icons/icon_shadow.svg`,
  regeneration: `${AETERNA_ASSET_ROOT}/icons/icon_regeneration.svg`,
  physicalArmor: `${AETERNA_ASSET_ROOT}/icons/icon_physical_armor.svg`,
  magicArmor: `${AETERNA_ASSET_ROOT}/icons/icon_magic_armor.svg`,
  thorns: `${AETERNA_ASSET_ROOT}/icons/icon_thorns.svg`,
  sharpen: `${AETERNA_ASSET_ROOT}/icons/icon_sharpen.svg`,
  hone: `${AETERNA_ASSET_ROOT}/icons/icon_hone.svg`,
  fletch: `${AETERNA_ASSET_ROOT}/icons/icon_fletch.svg`,
  research: `${AETERNA_ASSET_ROOT}/icons/icon_research.svg`,
};
const STATUS_EFFECT_ICON_IDS = Object.keys(STATUS_EFFECT_ICON_URL);
const EQUIPMENT_STATUS_MARK_ICON_IDS = ["sharpen", "hone", "research", "fletch"];
const STATUS_EFFECT_COUNT_CLASS = {
  thorns: "statusEffectIconCountThorns",
  regeneration: "statusEffectIconCountRegeneration",
};

const EQUIPMENT_SLOT_ICON_URL = {
  oneHandedWeapon: `${AETERNA_ASSET_ROOT}/icons/icon_equipment_slot_one_handed.svg`,
  twoHandedWeapon: `${AETERNA_ASSET_ROOT}/icons/icon_equipment_slot_two_handed.svg`,
  offHand: `${AETERNA_ASSET_ROOT}/icons/icon_equipment_slot_offhand.svg`,
  head: `${AETERNA_ASSET_ROOT}/icons/icon_equipment_slot_head.svg`,
  hands: `${AETERNA_ASSET_ROOT}/icons/icon_equipment_slot_hands.svg`,
  body: `${AETERNA_ASSET_ROOT}/icons/icon_equipment_slot_body.svg`,
  feet: `${AETERNA_ASSET_ROOT}/icons/icon_equipment_slot_feet.svg`,
  trinket: `${AETERNA_ASSET_ROOT}/icons/icon_equipment_slot_trinket.svg`,
  weapon: `${AETERNA_ASSET_ROOT}/icons/icon_equipment_slot_one_handed.svg`,
  armor: `${AETERNA_ASSET_ROOT}/icons/icon_equipment_slot_body.svg`,
};

const BOARD_EMPTY_SLOT_ICON_URL = {
  weapon: `${AETERNA_ASSET_ROOT}/matchUI/board_ui_weapon_slot.webp`,
  armor: `${AETERNA_ASSET_ROOT}/matchUI/board_ui_armor_slot.webp`,
  trinket: `${AETERNA_ASSET_ROOT}/matchUI/board_ui_trinket_slot.webp`,
  activeAbility: `${AETERNA_ASSET_ROOT}/matchUI/board_ui_active_ability_slot.webp`,
};

const NAV_ICON_URL = {
  main: `${AETERNA_ASSET_ROOT}/icons/icon_main.svg`,
  decks: `${AETERNA_ASSET_ROOT}/icons/icon_decks.svg`,
  collection: `${AETERNA_ASSET_ROOT}/icons/icon_collection.svg`,
  library: `${AETERNA_ASSET_ROOT}/icons/icon_library.svg`,
  social: `${AETERNA_ASSET_ROOT}/icons/icon_social.svg`,
  ranking: `${AETERNA_ASSET_ROOT}/icons/icon_ranking.svg`,
  settings: `${AETERNA_ASSET_ROOT}/icons/icon_settings.svg`,
  workshop: `${AETERNA_ASSET_ROOT}/icons/icon_workshop.svg`,
  community: `${AETERNA_ASSET_ROOT}/icons/icon_discord.svg`,
  support: `${AETERNA_ASSET_ROOT}/icons/icon_help.svg`,
  customize: `${AETERNA_ASSET_ROOT}/icons/icon_customize.svg`,
  marketplace: `${AETERNA_ASSET_ROOT}/icons/icon_shop.svg`,
  achievements: `${AETERNA_ASSET_ROOT}/icons/icon_dailies.svg`,
};

const DAILY_REROLL_ICON_URL = `${AETERNA_ASSET_ROOT}/icons/icon_reroll.svg`;

function resolveDailyIcon(daily) {
  const variant = String(daily?.variant || "");
  switch (daily?.kind) {
    case "dealDamage":
      return ELEMENTAL_DAMAGE_ICON_URL[variant]
        ? { src: ELEMENTAL_DAMAGE_ICON_URL[variant], mask: false }
        : { src: DAMAGE_ICON_URL, mask: false };
    case "applyAilment":
      return STATUS_EFFECT_ICON_URL[variant]
        ? { src: STATUS_EFFECT_ICON_URL[variant], mask: false }
        : { src: NAV_ICON_URL.achievements, mask: true };
    case "gainResource":
      return ATTR_ICON_URL[variant]
        ? { src: ATTR_ICON_URL[variant], mask: false }
        : { src: NAV_ICON_URL.achievements, mask: true };
    case "playCards": {
      const [group, value] = variant.split(":");
      if (group === "attribute" && ATTR_ICON_URL[value]) {
        return { src: ATTR_ICON_URL[value], mask: false };
      }
      return { src: NAV_ICON_URL.decks, mask: true };
    }
    case "winWithAspect":
      return { src: NAV_ICON_URL.ranking, mask: true };
    case "expendDamage":
    case "expendEffect":
    case "activateAbility":
    case "activateCost1":
    case "activateCost2":
    case "activateCost3":
      return { src: EXPEND_ACTIVATE_ICON_URL, mask: true };
    case "invokeCards":
      return { src: NAV_ICON_URL.collection, mask: true };
    default:
      return { src: NAV_ICON_URL.achievements, mask: true };
  }
}

const MENU_MODE_CARD_URL = {
  classicCpu: `${AETERNA_ASSET_ROOT}/menu/mode_classic_CPU.webp`,
  classicHuman: `${AETERNA_ASSET_ROOT}/menu/mode_classic_human.webp`,
  draft: `${AETERNA_ASSET_ROOT}/menu/mode_draft.webp`,
  adventure: `${AETERNA_ASSET_ROOT}/menu/mode_adventure.webp`,
  tutorial: `${AETERNA_ASSET_ROOT}/menu/mode_tutorial.webp`,
};

const MENU_MODE_CARD_FRAME_URL = `${CARD_FRAME_BASE}/card_frame_simple_bronze.webp`;

const BOARD_UNLOCK_CURRENCY_COST = 200;
const NAME_COLOR_UNLOCK_CURRENCY_COST = 25;
const HOLO_NAME_COLOR_UNLOCK_CURRENCY_COST = 100;
const MARKETPLACE_CARD_CURRENCY_COST = 5;
const MARKETPLACE_FOIL_CURRENCY_COST = 10;
const ACTIVE_DECK_MARKER = "★";

const DISCORD_INVITE_URL = "https://discord.gg/vSMgEbh8R";
const SUPPORT_CONTACT_EMAIL = "aeternainvicta.ccg@gmail.com";

const PRIVACY_POLICY_PAGE_URL = "/privacy.html";
const PRIVACY_POLICY_PUBLIC_URL = "/privacy";
const DELETE_ACCOUNT_PAGE_URL = "/delete-account";
const CHAT_ACCOUNT_AGE_MS = 24 * 60 * 60 * 1000;
const CHAT_MIN_COMPLETED_GAMES = 5;
const MATCH_GUIDES_AUTO_GAMES = 10;
const MATCH_GUIDE_IDLE_MS = 10000;
const MATCH_GUIDE_MINIMIZED_KEY = "aeternaInvicta.matchGuideMinimized";
const PLAYER_REPORT_DETAILS_MAX = 500;
const PLAYER_REPORT_CATEGORIES = [
  { id: "harassment", label: "Harassment, Bullying, or Threats" },
  { id: "hate", label: "Hate Speech or Discrimination" },
  { id: "inappropriate", label: "Sexual or Inappropriate Content" },
  { id: "spam", label: "Spam or Scams" },
  { id: "cheating", label: "Cheating or Exploiting" },
  { id: "username", label: "Offensive Username" },
  { id: "other", label: "Other" },
];


const BOARD_LIST = [
  { id: "flat", label: "Flat", free: true },
  { id: "elegant", label: "Elegant", free: false },
  { id: "ice", label: "Ice", free: false },
  { id: "nature", label: "Nature", free: false },
  { id: "volcanic", label: "Volcanic", free: false },
  { id: "castle", label: "Castle", free: false },
  { id: "factory", label: "Factory", free: false },
  { id: "swamp", label: "Swamp", free: false },
];

const BOARD_BY_ID = Object.fromEntries(
  BOARD_LIST.map((board) => [board.id, board]),
);

const NAME_COLOR_LIST = [
  { id: "garnet", label: "Garnet", hex: "#9e2835", free: false },
  { id: "scarlet", label: "Scarlet", hex: "#e53b44", free: false },
  { id: "amber", label: "Amber", hex: "#fb922b", free: false },
  { id: "gold", label: "Gold", hex: "#ffe762", free: false },
  { id: "verdant", label: "Verdant", hex: "#63c64d", free: false },
  { id: "forest", label: "Forest", hex: "#327345", free: false },
  { id: "abyss", label: "Abyss", hex: "#193d3f", free: false },
  { id: "steel", label: "Steel", hex: "#4f6781", free: false },
  { id: "mist", label: "Mist", hex: "#afbfd2", free: false },
  { id: "white", label: "White", hex: "#ffffff", free: true },
  { id: "cyan", label: "Cyan", hex: "#2ce8f4", free: false },
  { id: "azure", label: "Azure", hex: "#0484d1", free: false },
  { id: "sand", label: "Sand", hex: "#e4a672", free: false },
  { id: "clay", label: "Clay", hex: "#b86f50", free: false },
  { id: "umber", label: "Umber", hex: "#743f39", free: false },
  { id: "plum", label: "Plum", hex: "#3f2832", free: false },
  {
    id: "rainbow_holo",
    label: "Rainbow Holo",
    hex: "#ffffff",
    free: false,
    effect: "rainbow",
  },
  {
    id: "metallic_holo",
    label: "Metallic Holo",
    hex: "#ffffff",
    free: false,
    effect: "metallic",
  },
  {
    id: "gilded_holo",
    label: "Gilded Holo",
    hex: "#ffffff",
    free: false,
    effect: "gilded",
  },
];

const NAME_COLOR_BY_ID = Object.fromEntries(
  NAME_COLOR_LIST.map((color) => [color.id, color]),
);

function localizedOptionLabel(prefix, option) {
  if (!option) return "";
  const key = `${prefix}.${option.id}`;
  const label = t(key);
  return label === key ? option.label : label;
}

const SOCIAL_STATUS_KEYS = {
  Online: "online",
  Offline: "offline",
  "Recently Online": "recentlyOnline",
  "In CPU Game": "inCpuGame",
  "In Game": "inGame",
  Spectating: "spectating",
  "In Draft Queue": "inDraftQueue",
  "In Queue": "inQueue",
  "Viewing Queue": "viewingQueue",
  "Deck Editing": "deckEditing",
  "Viewing Collection": "viewingCollection",
  "Viewing Library": "viewingLibrary",
  "Viewing Achievements": "viewingAchievements",
  "Viewing Glossary": "viewingGlossary",
  "Adjusting Settings": "adjustingSettings",
  "Customizing Board": "customizingBoard",
  "In Marketplace": "inMarketplace",
  "In Social": "inSocial",
  "Viewing Ranking": "viewingRanking",
  "In Workshop": "inWorkshop",
  "Viewing Community": "viewingCommunity",
  "Viewing Support": "viewingSupport",
  "Viewing Tutorials": "viewingTutorials",
  "Viewing Summary": "viewingSummary",
  Drafting: "drafting",
  "Building Draft Deck": "buildingDraftDeck",
  "Viewing Draft Summary": "viewingDraftSummary",
  "Adventure Mode": "adventureMode",
  "Adventure Combat Summary": "adventureCombatSummary",
  "In Admin": "inAdmin",
  "Watching Replay": "watchingReplay",
};

function localizeSocialStatus(status) {
  const id = SOCIAL_STATUS_KEYS[status];
  return id ? localizedOptionLabel("social.status", { id, label: status }) : status;
}

const DEFAULT_NAME_COLOR_ID = "white";
const DEFAULT_NAME_COLOR_HEX = "#ffffff";

const MATCH_INTRO_MS = 2500;
const DEFAULT_BOARD_ID = "flat";

function getGameBoardImageUrl(boardId) {
  const id = normalizeBoardId(boardId);
  return aeternaAssetUrl(`gameBoard/player_board_${id}.webp`);
}

function normalizeBoardId(value) {
  return BOARD_BY_ID[value] ? value : DEFAULT_BOARD_ID;
}

function normalizeUnlockedBoards(value) {
  const unlocked = new Set(
    BOARD_LIST.filter((board) => board.free).map((board) => board.id),
  );
  (Array.isArray(value) ? value : []).forEach((entry) => {
    if (BOARD_BY_ID[entry]) unlocked.add(entry);
  });
  return BOARD_LIST.map((board) => board.id).filter((id) => unlocked.has(id));
}

function normalizeNameColorId(value) {
  return NAME_COLOR_BY_ID[value] ? value : DEFAULT_NAME_COLOR_ID;
}

function normalizeUnlockedNameColors(value) {
  const unlocked = new Set(
    NAME_COLOR_LIST.filter((color) => color.free).map((color) => color.id),
  );
  (Array.isArray(value) ? value : []).forEach((entry) => {
    if (NAME_COLOR_BY_ID[entry]) unlocked.add(entry);
  });
  return NAME_COLOR_LIST.map((color) => color.id).filter((id) =>
    unlocked.has(id),
  );
}

function resolvePlayerNameColorId(playerOrColor, unlockedNameColors) {
  if (typeof playerOrColor === "string") {
    const unlocked = normalizeUnlockedNameColors(unlockedNameColors);
    const preferred = normalizeNameColorId(playerOrColor);
    return unlocked.includes(preferred) ? preferred : DEFAULT_NAME_COLOR_ID;
  }
  const unlocked = normalizeUnlockedNameColors(
    unlockedNameColors || playerOrColor?.unlockedNameColors,
  );
  const preferred = normalizeNameColorId(playerOrColor?.nameColor);
  return unlocked.includes(preferred) ? preferred : DEFAULT_NAME_COLOR_ID;
}

function getNameColorHex(colorId) {
  return (
    NAME_COLOR_BY_ID[normalizeNameColorId(colorId)]?.hex ||
    DEFAULT_NAME_COLOR_HEX
  );
}

function getNameColorEffect(colorId) {
  return NAME_COLOR_BY_ID[normalizeNameColorId(colorId)]?.effect || null;
}

function getNameColorTextClassName(colorId) {
  const effect = getNameColorEffect(colorId);
  if (effect === "rainbow") return styles.nameColorFoilRainbow;
  if (effect === "metallic") return styles.nameColorFoilMetallic;
  if (effect === "gilded") return styles.nameColorFoilGilded;
  return null;
}

function getNameColorSwatchClassName(colorId) {
  const effect = getNameColorEffect(colorId);
  if (effect === "rainbow") {
    return `${styles.nameColorSwatch} ${styles.nameColorSwatchRainbow}`;
  }
  if (effect === "metallic") {
    return `${styles.nameColorSwatch} ${styles.nameColorSwatchMetallic}`;
  }
  if (effect === "gilded") {
    return `${styles.nameColorSwatch} ${styles.nameColorSwatchGilded}`;
  }
  return styles.nameColorSwatch;
}

function getNameColorTextProps(colorId, fallbackHex = null) {
  const id = colorId ? normalizeNameColorId(colorId) : null;
  const effectClass = id ? getNameColorTextClassName(id) : null;
  if (effectClass) {
    return {
      className: effectClass,
      style: undefined,
    };
  }
  return {
    className: null,
    style: {
      color:
        (id ? getNameColorHex(id) : null) ||
        fallbackHex ||
        DEFAULT_NAME_COLOR_HEX,
    },
  };
}

function resolvePlayerBoardId(playerOrBoard, unlockedBoards) {
  if (typeof playerOrBoard === "string" || playerOrBoard == null) {
    const preferred = normalizeBoardId(playerOrBoard);
    const unlocked = normalizeUnlockedBoards(unlockedBoards);
    return unlocked.includes(preferred) ? preferred : DEFAULT_BOARD_ID;
  }
  const unlocked = normalizeUnlockedBoards(
    unlockedBoards || playerOrBoard?.unlockedBoards,
  );
  const preferred = normalizeBoardId(playerOrBoard?.board);
  return unlocked.includes(preferred) ? preferred : DEFAULT_BOARD_ID;
}

function shouldShowQueueMatchIntro(match) {
  if (!match || match.phase === "ended") return false;
  if (match.workshop || match.tutorial || match.adventure || match.spectating) {
    return false;
  }
  if (match.isSocialChallenge) return false;
  return true;
}

// Small UI art folders: every file the server lists here is preloaded, so new
// icons need no code change. Their render URLs are unversioned constants, so
// they are preloaded unversioned too (same cache entry).
const PRELOAD_ASSET_FOLDERS = ["icons/", "matchUI/", "set/"];

function collectStaticPreloadUrls(assetVersions) {
  const urls = new Set([
    ...Object.values(ATTR_ICON_URL),
    DAMAGE_ICON_URL,
    ARMOR_ICON_URL,
    HEAL_ICON_URL,
    DURABILITY_ICON_URL,
    ABILITY_LEVEL_ICON_URL,
    EQUIPMENT_TIER_ICON_URL,
    POWER_ICON_URL,
    EXPEND_ACTIVATE_ICON_URL,
    INVOKE_ICON_URL,
    LOCKED_ICON_URL,
    CURRENCY_ICON_URL,
    ...Object.values(ELEMENTAL_DAMAGE_ICON_URL),
    ...Object.values(STATUS_EFFECT_ICON_URL),
    ...Object.values(EQUIPMENT_SLOT_ICON_URL),
    ...Object.values(BOARD_EMPTY_SLOT_ICON_URL),
    ...Object.values(NAV_ICON_URL),
    ...Object.values(MENU_MODE_CARD_URL),
    ...BOARD_LIST.map((board) => getGameBoardImageUrl(board.id)),
    ...Object.values(CARD_FRAME_FILES).map(cardFrameAssetUrl),
    ...Object.values(SIMPLE_CARD_FRAME_FILES).map(cardFrameAssetUrl),
  ]);
  Object.keys(assetVersions || {}).forEach((relPath) => {
    if (PRELOAD_ASSET_FOLDERS.some((folder) => relPath.startsWith(folder))) {
      urls.add(`${AETERNA_ASSET_ROOT}/${relPath}`);
    }
  });
  return [...urls].filter(Boolean);
}

function collectCardArtPreloadUrls(cards) {
  const urls = new Set();
  (cards || []).forEach((card) => {
    if (!card) return;
    const art = getCardImageUrl(card);
    if (art) urls.add(art);
    const frame = getCardFrameUrl(card);
    if (frame) urls.add(frame);
  });
  return [...urls];
}

function collectDeckCardIds(player) {
  const ids = new Set();
  const pairs = player?.deckPairs || [];
  const active =
    pairs.find((pair) => pair.id === player?.activeDeckPairId) || pairs[0];
  [active, ...pairs].forEach((pair) => {
    if (!pair) return;
    if (pair.aspectId) ids.add(pair.aspectId);
    (pair.equipmentIds || []).forEach((id) => ids.add(id));
    (pair.abilityIds || []).forEach((id) => ids.add(id));
  });
  return [...ids];
}

function collectMatchCardIds(match) {
  const ids = new Set();
  const visit = (value, depth = 0) => {
    if (!value || depth > 5) return;
    if (Array.isArray(value)) {
      value.forEach((entry) => visit(entry, depth + 1));
      return;
    }
    if (typeof value !== "object") return;
    if (typeof value.cardId === "string") ids.add(value.cardId);
    if (typeof value.aspectId === "string") ids.add(value.aspectId);
    Object.values(value).forEach((entry) => {
      if (entry && typeof entry === "object") visit(entry, depth + 1);
    });
  };
  Object.values(match?.seats || {}).forEach((seat) => visit(seat));
  return [...ids];
}

function vitalsFloaterToneFromToken(token) {
  const text = String(token || "").toUpperCase();
  if (/\bXP\b|\bLEVEL\b|\bLV\b/.test(text)) return "xp";
  if (/\bHEAL\b|\bREGENERATION\b/.test(text)) return "heal";
  if (/\bFIRE\b|\bBURN\b/.test(text)) return "fire";
  if (/\bCOLD\b|\bCHILL\b|\bICE\b/.test(text)) return "cold";
  if (/\bLIGHTNING\b|\bSHOCK\b/.test(text)) return "lightning";
  if (/\bPOISON\b/.test(text)) return "poison";
  if (/\bDARK\b|\bWITHER\b/.test(text)) return "dark";
  return "damage";
}

function splitVitalsFloaterSegments(text) {
  const raw = String(text || "").trim();
  if (!raw) return [];
  const pattern =
    /\+\d+\s+XP(?:\s*·\s*LV\s*\d+)?|\d+\s+(?:FIRE|COLD|LIGHTNING|DARK|POISON|PHYSICAL|MAGICAL|MAGIC)\s+DAMAGE|\d+\s+THORNS\s+DAMAGE|\d+\s+OVERTIME\s+DAMAGE|\d+\s+(?:HEAL|BURN|CHILL|SHOCK|POISON|BLEED|WITHER|HEALTH|REGENERATION|DODGE)|\d+\s+(?:PHYSICAL|MAGICAL)\s+ARMOR|\d+\s+BLEED\s+RESISTANCE|\d+\s+DAMAGE/gi;
  const segments = [];
  let lastIndex = 0;
  let match = pattern.exec(raw);
  while (match) {
    if (match.index > lastIndex) {
      const gap = raw.slice(lastIndex, match.index).trim();
      if (gap) {
        segments.push({
          text: gap,
          tone: vitalsFloaterToneFromToken(gap),
        });
      }
    }
    const token = match[0].trim();
    segments.push({
      text: token,
      tone: vitalsFloaterToneFromToken(token),
    });
    lastIndex = match.index + match[0].length;
    match = pattern.exec(raw);
  }
  if (lastIndex < raw.length) {
    const rest = raw.slice(lastIndex).trim();
    if (rest) {
      segments.push({
        text: rest,
        tone: vitalsFloaterToneFromToken(rest),
      });
    }
  }
  if (!segments.length) {
    segments.push({
      text: raw,
      tone: vitalsFloaterToneFromToken(raw),
    });
  }
  return segments;
}

const VITALS_FLOATER_TONE_CLASS = {
  damage: styles.statusVitalsFloaterToneDamage,
  lightning: styles.statusVitalsFloaterToneLightning,
  fire: styles.statusVitalsFloaterToneFire,
  cold: styles.statusVitalsFloaterToneCold,
  dark: styles.statusVitalsFloaterToneDark,
  poison: styles.statusVitalsFloaterTonePoison,
  heal: styles.statusVitalsFloaterToneHeal,
  xp: styles.statusVitalsFloaterToneXp,
};

const COMBAT_PROJECTILE_TONE_CLASS = {
  physical: styles.combatProjectileTonePhysical,
  magical: styles.combatProjectileToneMagical,
  lightning: styles.combatProjectileToneLightning,
  fire: styles.combatProjectileToneFire,
  cold: styles.combatProjectileToneCold,
  dark: styles.combatProjectileToneDark,
  poison: styles.combatProjectileTonePoison,
  heal: styles.combatProjectileToneHeal,
  damage: styles.combatProjectileTonePhysical,
};

const MATCH_EMOTE_OPTIONS = [
  "😀",
  "😆",
  "😅",
  "🫠",
  "😘",
  "😗",
  "😑",
  "😒",
  "🫥",
  "🤯",
  "🥺",
  "🥴",
  "😎",
  "😡",
  "😈",
  "😤",
];
const MATCH_EMOTE_SET = new Set(MATCH_EMOTE_OPTIONS);
const MATCH_EMOTE_COOLDOWN_MS = 50;
const MATCH_EMOTE_ANIM_MS = 2800;
const MATCH_EMOTE_CLEANUP_MS = 3200;

const COMBAT_HIT_FLASH_TONE_CLASS = {
  physical: styles.combatHitFlashTonePhysical,
  magical: styles.combatHitFlashToneMagical,
  lightning: styles.combatHitFlashToneLightning,
  fire: styles.combatHitFlashToneFire,
  cold: styles.combatHitFlashToneCold,
  dark: styles.combatHitFlashToneDark,
  poison: styles.combatHitFlashTonePoison,
  heal: styles.combatHitFlashToneHeal,
  damage: styles.combatHitFlashTonePhysical,
};

function createDisplayListStore() {
  let items = [];
  const listeners = new Set();
  return {
    get: () => items,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    set: (next) => {
      const resolved = typeof next === "function" ? next(items) : next;
      if (resolved === items) return;
      items = resolved;
      listeners.forEach((listener) => listener());
    },
  };
}

function useDisplayListStore() {
  const storeRef = useRef(null);
  if (!storeRef.current) storeRef.current = createDisplayListStore();
  return storeRef.current;
}

function CombatProjectileLayer({ projectileStore, hitFlashStore }) {
  const projectiles = useSyncExternalStore(
    projectileStore.subscribe,
    projectileStore.get,
  );
  const hitFlashes = useSyncExternalStore(
    hitFlashStore.subscribe,
    hitFlashStore.get,
  );
  if (!projectiles.length && !hitFlashes.length) return null;
  return (
    <div className={styles.combatProjectileLayer} aria-hidden="true">
      {projectiles.map((entry) =>
        entry.shape === "arrow" ? (
          <span
            key={entry.key}
            className={`${styles.combatProjectileArrow} ${
              COMBAT_PROJECTILE_TONE_CLASS[entry.tone] ||
              COMBAT_PROJECTILE_TONE_CLASS.physical
            }${entry.echo ? ` ${styles.combatProjectileEcho}` : ""}`}
            style={{
              "--combat-sx": `${entry.sx}px`,
              "--combat-sy": `${entry.sy}px`,
              "--combat-ex": `${entry.ex}px`,
              "--combat-ey": `${entry.ey}px`,
              "--combat-rot": `${
                (Math.atan2(entry.ey - entry.sy, entry.ex - entry.sx) * 180) /
                Math.PI
              }deg`,
              animationDuration: `${COMBAT_PROJECTILE_ANIM_MS}ms`,
              animationDelay: `${entry.nockMs || 0}ms`,
            }}
          >
            <span
              className={styles.combatArrowAim}
              style={{
                animationDuration: `${COMBAT_PROJECTILE_ANIM_MS}ms`,
                animationDelay: `${entry.nockMs || 0}ms`,
              }}
            >
              <span
                className={styles.combatArrowNock}
                style={
                  entry.nockMs > 0
                    ? { animationDuration: `${entry.nockMs}ms` }
                    : { animation: "none" }
                }
              >
                <span
                  className={styles.combatArrowShape}
                  style={maskIconImageStyle(ARROW_ICON_URL)}
                />
              </span>
            </span>
          </span>
        ) : (
          <span
            key={entry.key}
            className={`${styles.combatProjectile} ${
              COMBAT_PROJECTILE_TONE_CLASS[entry.tone] ||
              COMBAT_PROJECTILE_TONE_CLASS.physical
            }${entry.echo ? ` ${styles.combatProjectileEcho}` : ""}`}
            style={{
              "--combat-sx": `${entry.sx}px`,
              "--combat-sy": `${entry.sy}px`,
              "--combat-ex": `${entry.ex}px`,
              "--combat-ey": `${entry.ey}px`,
              animationDuration: `${COMBAT_PROJECTILE_ANIM_MS}ms`,
            }}
          />
        ),
      )}
      {hitFlashes.map((entry) => (
        <span
          key={entry.key}
          className={`${styles.combatHitFlash} ${
            COMBAT_HIT_FLASH_TONE_CLASS[entry.tone] ||
            COMBAT_HIT_FLASH_TONE_CLASS.physical
          }`}
          style={{
            left: entry.left,
            top: entry.top,
            width: entry.width,
            height: entry.height,
          }}
        />
      ))}
    </div>
  );
}

const COMBAT_SOURCE_TONE_CLASS = {
  physical: styles.combatSourceTonePhysical,
  magical: styles.combatSourceToneMagical,
  lightning: styles.combatSourceToneLightning,
  fire: styles.combatSourceToneFire,
  cold: styles.combatSourceToneCold,
  dark: styles.combatSourceToneDark,
  poison: styles.combatSourceTonePoison,
  heal: styles.combatSourceToneHeal,
  damage: styles.combatSourceTonePhysical,
};

// Physical ranged cards (bows, crossbows, throwables, shots) fire an arrow
// aimed at the target instead of the default orb.
function combatProjectileShape(card, entry) {
  if (!card || !entry || entry.tone === "heal") return "orb";
  if (entry.target && entry.target === entry.actor) return "orb";
  return cardHasType(card, "ranged") && cardHasType(card, "physical")
    ? "arrow"
    : "orb";
}

function combatAnchorQuery(key) {
  if (!key) return null;
  return document.querySelector(`[data-combat-anchor="${key}"]`);
}

function resolveCombatProjectileSourceEl(entry) {
  if (!entry) return null;
  const sourceKeys = [];
  if (entry.sourceKind === "ability" && entry.abilityInstanceId) {
    sourceKeys.push(`ability:${entry.abilityInstanceId}`);
  }
  if (entry.equipmentInstanceId) {
    sourceKeys.push(`equipment:${entry.equipmentInstanceId}`);
  }
  if (entry.actor) {
    sourceKeys.push(`aspect:${entry.actor}`);
  }
  if (entry.sourceKind === "aspect" && entry.actor) {
    sourceKeys.unshift(`aspect:${entry.actor}`);
  }
  for (const key of sourceKeys) {
    const el = combatAnchorQuery(key);
    if (el) return el;
  }
  return null;
}

function resolveCombatProjectilePoints(entry, sourceEl = null) {
  if (!entry) return null;
  const fromEl = sourceEl || resolveCombatProjectileSourceEl(entry);
  const toEl = entry.target ? combatAnchorQuery(`aspect:${entry.target}`) : null;
  if (!fromEl || !toEl) return null;
  const from = fromEl.getBoundingClientRect();
  const to = toEl.getBoundingClientRect();
  if (!(from.width > 0 && from.height > 0 && to.width > 0 && to.height > 0)) {
    return null;
  }
  let sx = from.left + from.width / 2;
  let sy = from.top + from.height / 2;
  let ex = to.left + to.width / 2;
  let ey = to.top + to.height / 2;
  if (Math.hypot(ex - sx, ey - sy) < 8) {
    ey = sy - Math.max(36, from.height * 0.55);
  }
  return {
    sx,
    sy,
    ex,
    ey,
    sourceEl: fromEl,
  };
}

function prefersReducedCombatMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function collectMatchBoardPieces(match) {
  const pieces = new Map();
  if (!match?.seats) return pieces;
  Object.entries(match.seats).forEach(([username, seat]) => {
    if (!seat) return;
    Object.entries(seat.board || {}).forEach(([boardSlot, equipment]) => {
      if (!equipment?.instanceId) return;
      pieces.set(equipment.instanceId, {
        username,
        kind: "equipment",
        boardSlot,
        cardId: equipment.cardId || null,
        foilEffect: equipment.foilEffect || null,
        instanceId: equipment.instanceId,
        boardCard: equipment,
      });
    });
    listAbilityBoardSlotKeys(seat).forEach((boardSlot) => {
      getColumnAbilitySlots(seat, boardSlot).forEach((slot) => {
        const ability = slot?.ability;
        if (!ability?.instanceId) return;
        pieces.set(ability.instanceId, {
          username,
          kind: "ability",
          boardSlot,
          slotId: slot.id || null,
          cardId: ability.cardId || null,
          foilEffect: ability.foilEffect || null,
          instanceId: ability.instanceId,
          boardCard: ability,
        });
      });
    });
  });
  return pieces;
}

function buildCardPlayDust(
  centerX,
  centerY,
  halfW,
  halfH,
  count = CARD_PLAY_DUST_COUNT,
) {
  const particles = [];
  for (let i = 0; i < count; i += 1) {
    const edgeBias = Math.random();
    let ox;
    let oy;
    if (edgeBias < 0.72) {
      const side = Math.floor(Math.random() * 4);
      const along = Math.random() * 2 - 1;
      const outward = 0.72 + Math.random() * 0.5;
      if (side === 0) {
        ox = along * halfW;
        oy = -halfH * outward;
      } else if (side === 1) {
        ox = along * halfW;
        oy = halfH * outward;
      } else if (side === 2) {
        ox = -halfW * outward;
        oy = along * halfH;
      } else {
        ox = halfW * outward;
        oy = along * halfH;
      }
    } else {
      const angle = Math.random() * Math.PI * 2;
      const radius = 0.5 + Math.random() * 0.7;
      ox = Math.cos(angle) * halfW * radius;
      oy = Math.sin(angle) * halfH * radius;
    }
    const driftAngle =
      Math.atan2(oy, ox) + (Math.random() - 0.5) * Math.PI * 0.95;
    const dist = 12 + Math.random() * 44;
    const dx = Math.cos(driftAngle) * dist;
    const dy = Math.sin(driftAngle) * dist - (5 + Math.random() * 16);
    const base = 1.2 + Math.random() * 3.4;
    const stretch = 0.35 + Math.random() * 1.1;
    const width = base * (stretch > 1 ? stretch : 1);
    const height = base * (stretch > 1 ? 1 / stretch : stretch + 0.35);
    const grey = 78 + Math.floor(Math.random() * 48);
    const r1 = 28 + Math.random() * 48;
    const r2 = 28 + Math.random() * 48;
    const r3 = 28 + Math.random() * 48;
    const r4 = 28 + Math.random() * 48;
    const r5 = 30 + Math.random() * 50;
    const r6 = 30 + Math.random() * 50;
    const r7 = 30 + Math.random() * 50;
    const r8 = 30 + Math.random() * 50;
    particles.push({
      key: `dust-${i}-${Math.floor(Math.random() * 1e6)}`,
      left: centerX + ox,
      top: centerY + oy,
      dx,
      dy,
      dxEarly: dx * 0.16,
      dyEarly: dy * 0.16,
      width,
      height,
      rotate: Math.random() * 360,
      rotateEnd: (Math.random() - 0.5) * 50,
      radius: `${r1}% ${r2}% ${r3}% ${r4}% / ${r5}% ${r6}% ${r7}% ${r8}%`,
      color: `rgb(${grey}, ${grey}, ${grey})`,
      opacity: 0.22 + Math.random() * 0.38,
      delay: CARD_PLAY_SLAM_AT_MS + Math.floor(Math.random() * 80),
      duration: CARD_PLAY_DUST_MS + Math.floor(Math.random() * 220),
    });
  }
  return particles;
}

function applyCombatSourceWindup(sourceEl, points, tone) {
  if (!sourceEl || !points) return () => {};
  let dx = points.ex - points.sx;
  let dy = points.ey - points.sy;
  let dist = Math.hypot(dx, dy);
  if (!(dist > 8)) {
    dx = 0;
    dy = -28;
    dist = 28;
  }
  const ux = dx / dist;
  const uy = dy / dist;
  const aimRot = Math.max(
    -38,
    Math.min(38, (Math.atan2(dx, -dy) * 180) / Math.PI),
  );
  const backDist = Math.min(28, Math.max(16, dist * 0.1));
  const forwardDist = Math.min(34, Math.max(18, dist * 0.12));
  const toneClass =
    COMBAT_SOURCE_TONE_CLASS[tone] || COMBAT_SOURCE_TONE_CLASS.physical;
  sourceEl.classList.remove(styles.combatSourceAttack, styles.combatSourceMelee);
  Object.values(COMBAT_SOURCE_TONE_CLASS).forEach((cls) => {
    sourceEl.classList.remove(cls);
  });
  void sourceEl.offsetWidth;
  sourceEl.style.setProperty("--combat-aim-rot", `${aimRot}deg`);
  sourceEl.style.setProperty("--combat-back-x", `${(-ux * backDist).toFixed(1)}px`);
  sourceEl.style.setProperty("--combat-back-y", `${(-uy * backDist).toFixed(1)}px`);
  sourceEl.style.setProperty(
    "--combat-forward-x",
    `${(ux * forwardDist).toFixed(1)}px`,
  );
  sourceEl.style.setProperty(
    "--combat-forward-y",
    `${(uy * forwardDist).toFixed(1)}px`,
  );
  sourceEl.style.setProperty("--combat-source-anim-ms", `${COMBAT_SOURCE_ANIM_MS}ms`);
  sourceEl.classList.add(styles.combatSourceAttack, toneClass);
  let settled = false;
  return () => {
    if (settled) return;
    settled = true;
    sourceEl.classList.remove(styles.combatSourceAttack, toneClass);
    sourceEl.style.removeProperty("--combat-aim-rot");
    sourceEl.style.removeProperty("--combat-back-x");
    sourceEl.style.removeProperty("--combat-back-y");
    sourceEl.style.removeProperty("--combat-forward-x");
    sourceEl.style.removeProperty("--combat-forward-y");
    sourceEl.style.removeProperty("--combat-source-anim-ms");
  };
}

function applyCombatSourceMelee(sourceEl, points, tone) {
  if (!sourceEl || !points) return () => {};
  let dx = points.ex - points.sx;
  let dy = points.ey - points.sy;
  let dist = Math.hypot(dx, dy);
  if (!(dist > 8)) {
    dx = 0;
    dy = -80;
    dist = 80;
  }
  const ux = dx / dist;
  const uy = dy / dist;
  const px = -uy;
  const py = ux;
  const slashAmt = Math.min(64, Math.max(36, dist * 0.2));
  const lungeX = dx * 0.72;
  const lungeY = dy * 0.72;
  const slashAx = lungeX - px * slashAmt;
  const slashAy = lungeY - py * slashAmt;
  const slashBx = lungeX + px * slashAmt;
  const slashBy = lungeY + py * slashAmt;
  const aimRot = Math.max(
    -36,
    Math.min(36, (Math.atan2(dx, -dy) * 180) / Math.PI),
  );
  const windupRot = aimRot + 140;
  const followRot = aimRot - 200;
  const toneClass =
    COMBAT_SOURCE_TONE_CLASS[tone] || COMBAT_SOURCE_TONE_CLASS.physical;
  sourceEl.classList.remove(styles.combatSourceAttack, styles.combatSourceMelee);
  Object.values(COMBAT_SOURCE_TONE_CLASS).forEach((cls) => {
    sourceEl.classList.remove(cls);
  });
  void sourceEl.offsetWidth;
  sourceEl.style.setProperty("--combat-aim-rot", `${aimRot}deg`);
  sourceEl.style.setProperty("--combat-windup-rot", `${windupRot}deg`);
  sourceEl.style.setProperty("--combat-follow-rot", `${followRot}deg`);
  sourceEl.style.setProperty("--combat-lunge-x", `${lungeX.toFixed(1)}px`);
  sourceEl.style.setProperty("--combat-lunge-y", `${lungeY.toFixed(1)}px`);
  sourceEl.style.setProperty("--combat-slash-a-x", `${slashAx.toFixed(1)}px`);
  sourceEl.style.setProperty("--combat-slash-a-y", `${slashAy.toFixed(1)}px`);
  sourceEl.style.setProperty("--combat-slash-b-x", `${slashBx.toFixed(1)}px`);
  sourceEl.style.setProperty("--combat-slash-b-y", `${slashBy.toFixed(1)}px`);
  sourceEl.style.setProperty("--combat-source-anim-ms", `${COMBAT_MELEE_ANIM_MS}ms`);
  sourceEl.classList.add(styles.combatSourceMelee, toneClass);
  let settled = false;
  return () => {
    if (settled) return;
    settled = true;
    sourceEl.classList.remove(styles.combatSourceMelee, toneClass);
    sourceEl.style.removeProperty("--combat-aim-rot");
    sourceEl.style.removeProperty("--combat-windup-rot");
    sourceEl.style.removeProperty("--combat-follow-rot");
    sourceEl.style.removeProperty("--combat-lunge-x");
    sourceEl.style.removeProperty("--combat-lunge-y");
    sourceEl.style.removeProperty("--combat-slash-a-x");
    sourceEl.style.removeProperty("--combat-slash-a-y");
    sourceEl.style.removeProperty("--combat-slash-b-x");
    sourceEl.style.removeProperty("--combat-slash-b-y");
    sourceEl.style.removeProperty("--combat-source-anim-ms");
  };
}

function spawnCombatEchoGhost(sourceEl) {
  const parent = sourceEl?.parentElement;
  if (!parent) return null;
  const ghost = sourceEl.cloneNode(true);
  [ghost, ...ghost.querySelectorAll("[data-combat-anchor], [data-gamepad-item], [id]")].forEach(
    (el) => {
      el.removeAttribute("data-combat-anchor");
      el.removeAttribute("data-gamepad-item");
      el.removeAttribute("id");
    },
  );
  ghost.classList.remove(
    styles.combatSourceAttack,
    styles.combatSourceMelee,
    styles.cardPlayPending,
  );
  Object.values(COMBAT_SOURCE_TONE_CLASS).forEach((cls) => {
    ghost.classList.remove(cls);
  });
  ghost.classList.add(styles.combatEchoGhost);
  ghost.setAttribute("aria-hidden", "true");
  ghost.setAttribute("inert", "");
  ghost.tabIndex = -1;
  ghost.style.left = `${sourceEl.offsetLeft}px`;
  ghost.style.top = `${sourceEl.offsetTop}px`;
  ghost.style.width = `${sourceEl.offsetWidth}px`;
  ghost.style.height = `${sourceEl.offsetHeight}px`;
  sourceEl.after(ghost);
  const driftX = sourceEl.offsetLeft - ghost.offsetLeft;
  const driftY = sourceEl.offsetTop - ghost.offsetTop;
  if (driftX || driftY) {
    ghost.style.left = `${sourceEl.offsetLeft + driftX}px`;
    ghost.style.top = `${sourceEl.offsetTop + driftY}px`;
  }
  return ghost;
}

const CARD_PLAY_GHOST_COPIED_STYLES = [
  "paddingTop",
  "paddingRight",
  "paddingBottom",
  "paddingLeft",
  "borderTopLeftRadius",
  "borderTopRightRadius",
  "borderBottomRightRadius",
  "borderBottomLeftRadius",
  "borderTopColor",
  "borderRightColor",
  "borderBottomColor",
  "borderLeftColor",
  "fontSize",
  "lineHeight",
];

function buildCardPlayGhost(sourceEl) {
  if (!sourceEl || typeof window === "undefined") return null;
  const ghost = sourceEl.cloneNode(true);
  [
    ghost,
    ...ghost.querySelectorAll(
      "[data-combat-anchor], [data-gamepad-item], [id], [tabindex], [title]",
    ),
  ].forEach((el) => {
    el.removeAttribute("data-combat-anchor");
    el.removeAttribute("data-gamepad-item");
    el.removeAttribute("id");
    el.removeAttribute("tabindex");
    el.removeAttribute("title");
  });
  ghost.querySelectorAll("img").forEach((img) => {
    img.loading = "eager";
  });
  ghost.classList.remove(
    styles.cardPlayPending,
    styles.combatSourceAttack,
    styles.combatSourceMelee,
    styles.equipBlockSelected,
    styles.equipBlockTarget,
    styles.cardTargetMatch,
    styles.matchGuideGlow,
  );
  Object.values(COMBAT_SOURCE_TONE_CLASS).forEach((cls) => {
    ghost.classList.remove(cls);
  });
  const computed = window.getComputedStyle(sourceEl);
  CARD_PLAY_GHOST_COPIED_STYLES.forEach((prop) => {
    if (computed[prop]) ghost.style[prop] = computed[prop];
  });
  Object.assign(ghost.style, {
    position: "absolute",
    left: "0",
    top: "0",
    width: "100%",
    height: "100%",
    minWidth: "0",
    minHeight: "0",
    maxWidth: "none",
    maxHeight: "none",
    margin: "0",
    boxSizing: "border-box",
    transform: "none",
    opacity: "1",
    visibility: "visible",
    pointerEvents: "none",
  });
  ghost.setAttribute("aria-hidden", "true");
  ghost.setAttribute("inert", "");
  return ghost;
}

function attachCardPlayGhost(containerEl, ghostNode) {
  if (!containerEl || !ghostNode || ghostNode.parentNode === containerEl) return;
  containerEl.replaceChildren(ghostNode);
}

function shakeCombatMeleeTarget(targetEl) {
  if (!targetEl) return () => {};
  targetEl.classList.remove(styles.combatTargetMeleeHit);
  void targetEl.offsetWidth;
  targetEl.classList.add(styles.combatTargetMeleeHit);
  let cleared = false;
  const clear = () => {
    if (cleared) return;
    cleared = true;
    targetEl.classList.remove(styles.combatTargetMeleeHit);
  };
  const timerId = window.setTimeout(clear, COMBAT_HIT_FLASH_MS);
  return () => {
    window.clearTimeout(timerId);
    clear();
  };
}

function isXpVitalsFloaterText(text) {
  return /\bXP\b/i.test(String(text || ""));
}

function parseVitalsFloaterHpDelta(text) {
  const raw = String(text || "").toUpperCase();
  if (!raw || isXpVitalsFloaterText(raw)) return 0;
  let delta = 0;
  for (const match of raw.matchAll(/(\d+)\s+HEAL\b/g)) {
    delta += Math.max(0, Number(match[1]) || 0);
  }
  for (const match of raw.matchAll(/(\d+)\s+HEALTH\b/g)) {
    delta -= Math.max(0, Number(match[1]) || 0);
  }
  let remainder = raw.replace(
    /(\d+)\s+(?:FIRE|COLD|LIGHTNING|DARK|POISON|PHYSICAL|MAGICAL|MAGIC|THORNS|OVERTIME)\s+DAMAGE\b/g,
    (token, amount) => {
      delta -= Math.max(0, Number(amount) || 0);
      return " ";
    },
  );
  for (const match of remainder.matchAll(/(\d+)\s+DAMAGE\b/g)) {
    delta -= Math.max(0, Number(match[1]) || 0);
  }
  return delta;
}

function xpBarPercent(xp, xpToNext) {
  const next = Math.max(1, Number(xpToNext) || 1);
  return Math.max(0, Math.min(100, ((Math.max(0, Number(xp) || 0) / next) * 100)));
}

function xpToNextForLevel(level) {
  const safe = Math.max(1, Math.min(5, Math.floor(Number(level) || 1)));
  if (safe >= 5) return 20;
  if (safe === 1) return 5;
  if (safe === 2) return 10;
  if (safe === 3) return 15;
  return 20;
}

const XP_TURN_QUESTS = [
  {
    id: "equip",
    label: "Equip an Equipment Card",
    xp: 1,
  },
  {
    id: "slotAbility",
    label: "Slot an Ability Card",
    xp: 1,
  },
  {
    id: "resourceExpend",
    label: "Gain at least 1 resource",
    xp: 1,
  },
  {
    id: "combatDamage",
    label: "Deal unblocked damage",
    xp: 1,
  },
  {
    id: "healBuff",
    label: "Gain a support effect such as healing or armor",
    xp: 1,
  },
];

const XP_EQUIP_SLOT_QUEST_STEPS = [
  { id: "weapon", label: "Weapon" },
  { id: "trinket", label: "Trinket" },
  { id: "body", label: "Body" },
  { id: "hands", label: "Hands" },
  { id: "feet", label: "Feet" },
  { id: "head", label: "Head" },
];

const XP_MATCH_QUESTS = [
  {
    id: "fullEquipment",
    label: "Have an equipment equipped in all 3 slots at one time",
    xp: 3,
  },
  {
    id: "fullAbilities",
    label: "Have an ability slotted in all 3 active or main slots at one time",
    xp: 3,
  },
  {
    id: "equipAllSlots",
    label: "Equip each equipment slot at least once",
    xp: 3,
    steps: XP_EQUIP_SLOT_QUEST_STEPS,
    stepsKey: "equipSlots",
  },
  {
    id: "activateLevel2Ability",
    label: "Activate a slotted Level 2 Ability once",
    xp: 3,
  },
  {
    id: "activateAugmentedAbility",
    label: "Activate an Ability with a matching Augment once",
    xp: 3,
  },
];

function BoardXpGauge({
  side,
  level,
  xp,
  xpToNext,
  floaterEntries = [],
  className = "",
}) {
  const safeLevel = Math.max(1, Math.min(5, level ?? 1));
  const safeXp = Math.max(0, xp ?? 0);
  const safeXpToNext = Math.max(
    1,
    xpToNext ?? xpToNextForLevel(safeLevel),
  );
  const targetPct = xpBarPercent(safeXp, safeXpToNext);
  const [displayLevel, setDisplayLevel] = useState(safeLevel);
  const [displayXp, setDisplayXp] = useState(safeXp);
  const [displayXpToNext, setDisplayXpToNext] = useState(safeXpToNext);
  const [displayPct, setDisplayPct] = useState(targetPct);
  const animRef = useRef(null);
  const stateRef = useRef({
    level: safeLevel,
    xp: safeXp,
    xpToNext: safeXpToNext,
    pct: targetPct,
  });

  useEffect(() => {
    const prev = stateRef.current;
    const next = {
      level: safeLevel,
      xp: safeXp,
      xpToNext: safeXpToNext,
      pct: targetPct,
    };
    if (
      prev.level === next.level &&
      prev.xp === next.xp &&
      prev.xpToNext === next.xpToNext
    ) {
      return undefined;
    }

    if (animRef.current) {
      window.cancelAnimationFrame(animRef.current);
      animRef.current = null;
    }

    const segments = [];
    if (next.level > prev.level) {
      let fromLevel = prev.level;
      let fromXp = prev.xp;
      let fromXpToNext = prev.xpToNext;
      while (fromLevel < next.level) {
        segments.push({
          fromPct: xpBarPercent(fromXp, fromXpToNext),
          toPct: 100,
          toLevel: fromLevel,
          toXp: fromXpToNext,
          toXpToNext: fromXpToNext,
          duration: 320,
        });
        fromLevel += 1;
        fromXp = 0;
        fromXpToNext = xpToNextForLevel(fromLevel);
        segments.push({
          fromPct: 0,
          toPct: 0,
          toLevel: fromLevel,
          toXp: 0,
          toXpToNext: fromXpToNext,
          duration: 0,
        });
      }
      segments.push({
        fromPct: 0,
        toPct: next.pct,
        toLevel: next.level,
        toXp: next.xp,
        toXpToNext: next.xpToNext,
        duration: 420,
      });
    } else {
      segments.push({
        fromPct: prev.pct,
        toPct: next.pct,
        toLevel: next.level,
        toXp: next.xp,
        toXpToNext: next.xpToNext,
        duration: 480,
      });
    }

    let segmentIndex = 0;
    let segmentStart = 0;
    const ease = (t) => 1 - (1 - t) ** 3;

    const tick = (now) => {
      if (segmentIndex >= segments.length) {
        stateRef.current = next;
        setDisplayLevel(next.level);
        setDisplayXp(next.xp);
        setDisplayXpToNext(next.xpToNext);
        setDisplayPct(next.pct);
        animRef.current = null;
        return;
      }
      const segment = segments[segmentIndex];
      if (!segmentStart) segmentStart = now;
      if (segment.duration <= 0) {
        setDisplayLevel(segment.toLevel);
        setDisplayXp(segment.toXp);
        setDisplayXpToNext(segment.toXpToNext);
        setDisplayPct(segment.toPct);
        segmentIndex += 1;
        segmentStart = 0;
        animRef.current = window.requestAnimationFrame(tick);
        return;
      }
      const progress = Math.max(
        0,
        Math.min(1, (now - segmentStart) / segment.duration),
      );
      const eased = ease(progress);
      const pct =
        segment.fromPct + (segment.toPct - segment.fromPct) * eased;
      setDisplayPct(pct);
      setDisplayLevel(segment.toLevel);
      setDisplayXpToNext(segment.toXpToNext);
      const startXp = (segment.fromPct / 100) * segment.toXpToNext;
      const endXp =
        segment.toPct >= 100 ? segment.toXpToNext : segment.toXp;
      setDisplayXp(Math.round(startXp + (endXp - startXp) * eased));
      if (progress >= 1) {
        segmentIndex += 1;
        segmentStart = 0;
      }
      animRef.current = window.requestAnimationFrame(tick);
    };

    animRef.current = window.requestAnimationFrame(tick);
    return () => {
      if (animRef.current) {
        window.cancelAnimationFrame(animRef.current);
        animRef.current = null;
      }
      stateRef.current = next;
    };
  }, [safeLevel, safeXp, safeXpToNext, targetPct]);

  return (
    <div
      className={`${styles.boardXpGauge}${className ? ` ${className}` : ""}`}
      aria-label={`Level ${safeLevel}, ${safeXp} of ${safeXpToNext} XP`}
    >
      <span className={styles.boardXpLabel}>LV {displayLevel}</span>
      <span className={styles.boardXpTrackWrap}>
        <span className={styles.boardXpTrack}>
          <span
            className={styles.boardXpFill}
            style={{ width: `${displayPct}%` }}
            aria-hidden="true"
          />
          <span className={styles.boardXpValue}>
            {displayXp}/{displayXpToNext}
          </span>
        </span>
        {floaterEntries.length ? (
          <span className={styles.boardXpFloaterLayer} aria-hidden="true">
            {floaterEntries.map((entry) => (
              <span
                key={entry.key}
                className={`${styles.boardXpFloater} ${
                  side === "opponent"
                    ? styles.boardXpFloaterOpponent
                    : styles.boardXpFloaterPlayer
                }`}
              >
                {renderVitalsFloaterText(entry.text)}
              </span>
            ))}
          </span>
        ) : null}
      </span>
    </div>
  );
}

// Multi-part hits (e.g. magical + lightning damage + shock) stack one part
// per line so long combos stay on screen.
function renderVitalsFloaterText(text) {
  const segments = splitVitalsFloaterSegments(text);
  const stacked = segments.length > 1;
  return segments.map((segment, index) => (
    <span
      key={`${segment.tone}-${index}-${segment.text}`}
      className={`${
        VITALS_FLOATER_TONE_CLASS[segment.tone] ||
        VITALS_FLOATER_TONE_CLASS.damage
      }${stacked ? ` ${styles.vitalsFloaterLine}` : ""}`}
    >
      {!stacked && index > 0 ? " " : ""}
      {segment.text}
    </span>
  ));
}

function maskIconImageStyle(src) {
  if (!src) return undefined;
  return {
    maskImage: `url("${src}")`,
    WebkitMaskImage: `url("${src}")`,
  };
}

function MaskIcon({ src, className, style }) {
  if (!src) return null;
  return (
    <span
      className={className}
      style={{
        ...maskIconImageStyle(src),
        ...style,
      }}
      aria-hidden="true"
    />
  );
}

function ChargeIcon({ className, title = "Charge" }) {
  return (
    <span
      className={`${styles.chargeIcon}${className ? ` ${className}` : ""}`}
      title={title}
      aria-hidden="true"
    />
  );
}

function ChargeCostIcons({ amount, className }) {
  if (amount === "all") {
    return (
      <span
        className={`${styles.chargeCostIcons}${className ? ` ${className}` : ""}`}
        title="All Charges"
      >
        <ChargeIcon />
        <span className={styles.chargeCostAmount}>all</span>
      </span>
    );
  }
  const n = Math.max(0, Math.floor(Number(amount) || 0));
  if (n <= 0) return null;
  if (n >= 5) {
    return (
      <span
        className={`${styles.chargeCostIcons}${className ? ` ${className}` : ""}`}
        title="Charges"
      >
        <ChargeIcon />
        <span className={styles.chargeCostAmount}>{n}</span>
      </span>
    );
  }
  return (
    <span
      className={`${styles.chargeCostIcons}${className ? ` ${className}` : ""}`}
      title="Charges"
    >
      {Array.from({ length: n }, (_, index) => (
        <ChargeIcon key={`charge-cost-${index}`} />
      ))}
    </span>
  );
}

function DurabilityIcon({ className }) {
  return (
    <MaskIcon
      src={DURABILITY_ICON_URL}
      className={`${styles.durabilityIcon}${className ? ` ${className}` : ""}`}
    />
  );
}

function DurabilityCostIcons({
  amount,
  className,
  keywordProps = null,
  restore = false,
}) {
  const tipProps = useKeywordTipProps(keywordProps);
  const n = Math.max(0, Math.floor(Number(amount) || 0));
  if (n <= 0) return null;
  const keyword = getKeywordById(restore ? "durability" : "consumeDurability");
  const title = restore ? `Restore ${n} Durability` : `−${n} Durability`;
  const canOpenKeyword =
    Boolean(keyword) && typeof tipProps?.onKeywordClick === "function";
  const active = canOpenKeyword && tipProps.activeKeywordId === keyword.id;
  const content =
    n >= 5 ? (
      <>
        <DurabilityIcon />
        <span className={styles.chargeCostAmount}>{n}</span>
      </>
    ) : (
      Array.from({ length: n }, (_, index) => (
        <DurabilityIcon key={`durability-cost-${index}`} />
      ))
    );
  const baseClass = `${styles.durabilityCostIcons}${className ? ` ${className}` : ""}`;
  if (!canOpenKeyword) {
    return (
      <span className={baseClass} title={title}>
        {content}
      </span>
    );
  }
  return (
    <button
      type="button"
      className={`${baseClass} ${styles.keywordIconButton}${
        active ? ` ${styles.keywordIconButtonActive}` : ""
      }`}
      title={title}
      aria-label={`${keyword.label} ${n}`}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        tipProps.onKeywordClick(keyword);
      }}
    >
      {content}
    </button>
  );
}

function getEffectConsumeChargeCost(effect) {
  if (!effect) return 0;
  if (
    effect.chargeDamage?.consume === "all" ||
    effect.chargeDamage?.consume === true
  ) {
    return "all";
  }
  if (effect.chargeDamage?.consume) {
    const amount = Math.max(
      0,
      Math.floor(Number(effect.chargeDamage.consume) || 0),
    );
    return amount > 0 ? amount : 0;
  }
  if (effect.consumeCharges === true) return "all";
  if (effect.consumeCharges) {
    return Math.max(1, Math.floor(Number(effect.consumeCharges) || 1));
  }
  return 0;
}

function resolveEquipmentEffectChargeKind(equipment, effect) {
  if (CHARGE_KINDS[effect?.charge]) return effect.charge;
  const passive = (equipment?.passiveEffects || []).find(
    (entry) => entry?.id === "charges" && CHARGE_KINDS[entry.charge],
  );
  return passive?.charge || null;
}

function getEquipmentEffectChargeBlockMessage(equipment, effect) {
  for (const entry of listEffectEntries(effect)) {
    if (!entry) continue;
    const kind = resolveEquipmentEffectChargeKind(equipment, entry);
    const charges = getChargeCount(equipment, kind);
    if (entry.id === "spendChargesForResources") {
      const per = Math.max(1, entry.chargesPerResource || 3);
      if (Math.floor(charges / per) < 1) {
        return `Need more ${getChargeKindLabel(kind)}.`;
      }
    }
    if (entry.consumeCharges) {
      const need =
        entry.consumeCharges === true
          ? 1
          : Math.max(1, Math.floor(Number(entry.consumeCharges) || 1));
      if (charges < need) {
        return `Need ${need} ${getChargeKindLabel(kind, need)}.`;
      }
    }
    if (entry.chargeDamage?.asHits && charges < 1) {
      return `Need ${getChargeKindLabel(kind)}.`;
    }
  }
  return null;
}

function StatusEffectIcon({ effectId, className }) {
  const src = STATUS_EFFECT_ICON_URL[effectId];
  if (!src) return null;
  return (
    <img
      src={src}
      alt=""
      className={`${styles.statusEffectIcon} ${className || ""}`}
      draggable={false}
      aria-hidden="true"
    />
  );
}

function resolveOutputIconBonusIndexes(count, outputDelta) {
  const delta = Math.floor(Number(outputDelta) || 0);
  if (!(delta > 0) || count <= 0) return { delta: 0, bonusStart: count };
  const bonus = Math.min(delta, count);
  return { delta, bonusStart: count - bonus };
}

function ElementalDamageInline({
  damageType,
  amount = null,
  outputDelta = 0,
  keywordProps = null,
}) {
  const tipProps = useKeywordTipProps(keywordProps);
  const src = ELEMENTAL_DAMAGE_ICON_URL[damageType];
  const count =
    amount == null || amount === ""
      ? 1
      : Math.max(0, Math.floor(Number(amount) || 0));
  if (!src || count <= 0) return null;
  const keyword = getKeywordById(
    DAMAGE_TYPE_KEYWORD_ID[damageType] || damageType,
  );
  const canOpenKeyword =
    Boolean(keyword) && typeof tipProps?.onKeywordClick === "function";
  const active = canOpenKeyword && tipProps.activeKeywordId === keyword.id;
  const delta = Math.floor(Number(outputDelta) || 0);
  const typeCountClass =
    styles[DAMAGE_TYPE_COUNT_CLASS[damageType]] || styles.statusEffectIconCount;
  const countClass =
    delta > 0
      ? styles.actionOutputUp
      : delta < 0
        ? styles.actionOutputDown
        : typeCountClass;
  const { bonusStart } = resolveOutputIconBonusIndexes(count, delta);
  const tokens =
    count >= 5
      ? [{ count, key: `${damageType}-stack`, index: 0 }]
      : Array.from({ length: count }, (_, index) => ({
          count: 1,
          key: `${damageType}-${index}`,
          index,
        }));
  const label = keyword?.label || damageType;
  return (
    <span className={styles.statusEffectIconRow}>
      {tokens.map((token) => {
        const counted = token.count > 1;
        const isBonus = !counted && delta > 0 && token.index >= bonusStart;
        const isPenalty = !counted && delta < 0;
        const itemClass = [
          styles.statusEffectIconItem,
          counted ? styles.statusEffectIconItemCounted : "",
          isBonus ? styles.actionOutputIconUp : "",
          isPenalty ? styles.actionOutputIconDown : "",
          canOpenKeyword ? styles.keywordIconButton : "",
          active ? styles.keywordIconButtonActive : "",
        ]
          .filter(Boolean)
          .join(" ");
        const content = (
          <>
            {counted ? (
              <span className={countClass} aria-hidden="true">
                {token.count}
              </span>
            ) : null}
            <img
              src={src}
              alt=""
              className={`${styles.statusEffectIcon} ${styles.effectDamageTypeIcon}`}
              draggable={false}
              aria-hidden="true"
            />
          </>
        );
        if (!canOpenKeyword) {
          return (
            <span
              key={token.key}
              className={itemClass}
              aria-label={`${count} ${label}`}
            >
              {content}
            </span>
          );
        }
        return (
          <button
            key={token.key}
            type="button"
            className={itemClass}
            aria-label={label}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              tipProps.onKeywordClick(keyword);
            }}
          >
            {content}
          </button>
        );
      })}
    </span>
  );
}

function HealInline({ amount, outputDelta = 0, keywordProps = null }) {
  const tipProps = useKeywordTipProps(keywordProps);
  const count = Math.max(0, Math.floor(Number(amount) || 0));
  if (!HEAL_ICON_URL || count <= 0) return null;
  const keyword = getKeywordById("heal");
  const canOpenKeyword =
    Boolean(keyword) && typeof tipProps?.onKeywordClick === "function";
  const active = canOpenKeyword && tipProps.activeKeywordId === keyword.id;
  const delta = Math.floor(Number(outputDelta) || 0);
  const countClass =
    delta > 0
      ? styles.actionOutputUp
      : delta < 0
        ? styles.actionOutputDown
        : styles.effectHealCount;
  const { bonusStart } = resolveOutputIconBonusIndexes(count, delta);
  const tokens =
    count >= 5
      ? [{ count, key: "heal-stack", index: 0 }]
      : Array.from({ length: count }, (_, index) => ({
          count: 1,
          key: `heal-${index}`,
          index,
        }));
  const label = keyword?.label || "Heal";
  return (
    <span className={styles.statusEffectIconRow}>
      {tokens.map((token) => {
        const counted = token.count > 1;
        const isBonus = !counted && delta > 0 && token.index >= bonusStart;
        const isPenalty = !counted && delta < 0;
        const itemClass = [
          styles.statusEffectIconItem,
          counted ? styles.statusEffectIconItemCounted : "",
          isBonus ? styles.actionOutputIconUp : "",
          isPenalty ? styles.actionOutputIconDown : "",
          canOpenKeyword ? styles.keywordIconButton : "",
          active ? styles.keywordIconButtonActive : "",
        ]
          .filter(Boolean)
          .join(" ");
        const content = (
          <>
            {counted ? (
              <span className={countClass} aria-hidden="true">
                {token.count}
              </span>
            ) : null}
            <img
              src={HEAL_ICON_URL}
              alt=""
              className={`${styles.statusEffectIcon} ${styles.effectHealIcon}`}
              draggable={false}
              aria-hidden="true"
            />
          </>
        );
        if (!canOpenKeyword) {
          return (
            <span
              key={token.key}
              className={itemClass}
              aria-label={`${count} ${label}`}
            >
              {content}
            </span>
          );
        }
        return (
          <button
            key={token.key}
            type="button"
            className={itemClass}
            aria-label={label}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              tipProps.onKeywordClick(keyword);
            }}
          >
            {content}
          </button>
        );
      })}
    </span>
  );
}

const STATUS_EFFECT_KEYWORD_ID = {
  bleed: "bleed",
  burn: "burn",
  chill: "chill",
  poison: "poison",
  restock: "restock",
  shock: "shock",
  dodge: "dodgeRoll",
  physicalArmor: "physicalArmor",
  magicArmor: "magicArmor",
  thorns: "thorns",
  sharpen: "sharpen",
  hone: "hone",
  fletch: "fletch",
  research: "research",
};

function StatusEffectIconStack({
  effectId,
  amount,
  className = "",
  keywordProps = null,
  outputDelta = 0,
}) {
  const tipProps = useKeywordTipProps(keywordProps);
  const count = Math.max(0, Math.floor(Number(amount) || 0));
  const src = STATUS_EFFECT_ICON_URL[effectId];
  if (!src || count <= 0) return null;
  const keyword = getKeywordById(STATUS_EFFECT_KEYWORD_ID[effectId] || effectId);
  const canOpenKeyword =
    Boolean(keyword) && typeof tipProps?.onKeywordClick === "function";
  const active = canOpenKeyword && tipProps.activeKeywordId === keyword.id;
  const delta = Math.floor(Number(outputDelta) || 0);
  const effectCountClass = styles[STATUS_EFFECT_COUNT_CLASS[effectId]];
  const countClass =
    delta > 0
      ? styles.actionOutputUp
      : delta < 0
        ? styles.actionOutputDown
        : effectCountClass
          ? `${styles.statusEffectIconCount} ${effectCountClass}`
          : styles.statusEffectIconCount;
  const { bonusStart } = resolveOutputIconBonusIndexes(count, delta);
  const tokens =
    count >= 5
      ? [{ count, key: `${effectId}-stack`, index: 0 }]
      : Array.from({ length: count }, (_, index) => ({
          count: 1,
          key: `${effectId}-${index}`,
          index,
        }));
  const label = keyword?.label || effectId;
  return (
    <span className={`${styles.statusEffectIconRow} ${className || ""}`}>
      {tokens.map((token) => {
        const counted = token.count > 1;
        const isBonus = !counted && delta > 0 && token.index >= bonusStart;
        const isPenalty = !counted && delta < 0;
        const itemClass = [
          styles.statusEffectIconItem,
          counted ? styles.statusEffectIconItemCounted : "",
          isBonus ? styles.actionOutputIconUp : "",
          isPenalty ? styles.actionOutputIconDown : "",
          canOpenKeyword ? styles.keywordIconButton : "",
          active ? styles.keywordIconButtonActive : "",
        ]
          .filter(Boolean)
          .join(" ");
        const content = (
          <>
            {counted ? (
              <span className={countClass} aria-hidden="true">
                {token.count}
              </span>
            ) : null}
            <StatusEffectIcon effectId={effectId} />
          </>
        );
        if (!canOpenKeyword) {
          return (
            <span
              key={token.key}
              className={itemClass}
              aria-label={`${count} ${label}`}
            >
              {content}
            </span>
          );
        }
        return (
          <button
            key={token.key}
            type="button"
            className={itemClass}
            aria-label={label}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              tipProps.onKeywordClick(keyword);
            }}
          >
            {content}
          </button>
        );
      })}
    </span>
  );
}

function NavButtonIcon({ src }) {
  return <MaskIcon src={src} className={styles.navButtonIcon} />;
}

function EquipmentSlotIcon({ slot, className }) {
  const iconUrl = EQUIPMENT_SLOT_ICON_URL[slot];
  if (!iconUrl) return null;
  return (
    <ResourceIconStrip className={styles.cardHeaderIconStrip}>
      <span className={styles.equipmentSlotIconWrap} title={formatSlotLabel(slot)}>
        <MaskIcon
          src={iconUrl}
          className={`${styles.equipmentSlotIcon} ${className || ""}`}
        />
      </span>
    </ResourceIconStrip>
  );
}

function countResourceTokenTotal(entries) {
  return (entries || []).reduce(
    (sum, [, value]) => sum + Math.max(0, Number(value) || 0),
    0,
  );
}

function countResourceGrantChoiceTokens(choices) {
  return (choices || []).reduce(
    (sum, entries) => sum + countResourceTokenTotal(entries),
    0,
  );
}

function CardHeaderResources({
  resourceGrantChoices,
  persistGrants,
  delayedGrants = null,
  abilityCosts,
  abilityHealthCost,
  augmentCostIncrease,
  augmentCostMult,
  compact,
}) {
  const persistEntries = (persistGrants || []).filter(
    ([, value]) => (Number(value) || 0) > 0,
  );
  const delayedEntries = (delayedGrants || []).filter(
    ([, value]) => (Number(value) || 0) > 0,
  );
  const hasResourceGrantDisplay = (resourceGrantChoices || []).some((entries) =>
    (entries || []).some(([, value]) => (Number(value) || 0) > 0),
  );
  const abilityCostEntries = (abilityCosts || []).filter(
    ([, value]) => (Number(value) || 0) > 0,
  );
  const augmentCostEntries = (augmentCostIncrease || []).filter(
    ([, value]) => (Number(value) || 0) > 0,
  );
  const totalTokens =
    countResourceGrantChoiceTokens(resourceGrantChoices) +
    countResourceTokenTotal(persistEntries) +
    countResourceTokenTotal(delayedEntries) +
    countResourceTokenTotal(abilityCostEntries) +
    countResourceTokenTotal(augmentCostEntries) +
    Math.max(0, Number(abilityHealthCost) || 0);
  const useColumnLayout = totalTokens > 1;
  const iconClassName = compact
    ? styles.cardHandCompactIcons
    : styles.cardHeaderResourceIcons;
  const stripClassName = useColumnLayout ? styles.cardHeaderResourceStripColumn : "";

  const strips = [];
  if (hasResourceGrantDisplay) {
    strips.push(
      <ResourceGrantChoiceIconStrip
        key="grant"
        choices={resourceGrantChoices}
        iconClassName={iconClassName}
        stripClassName={stripClassName}
      />,
    );
  }
  if (persistEntries.length > 0) {
    strips.push(
      <ResourceIconStrip key="persist" className={stripClassName}>
        {persistEntries.map(([attribute, value]) => (
          <AttributeIcons
            key={`persist-${attribute}`}
            attribute={attribute}
            count={value}
            persist
          />
        ))}
      </ResourceIconStrip>,
    );
  }
  if (delayedEntries.length > 0) {
    strips.push(
      <ResourceIconStrip key="delayed" className={stripClassName}>
        {delayedEntries.map(([attribute, value]) => (
          <AttributeIcons
            key={`delayed-${attribute}`}
            attribute={attribute}
            count={value}
            delayed
          />
        ))}
      </ResourceIconStrip>,
    );
  }
  if (abilityHealthCost > 0 || abilityCostEntries.length > 0) {
    strips.push(
      <ResourceIconStrip key="ability-cost" className={stripClassName}>
        {abilityHealthCost > 0 ? (
          <span
            className={
              compact
                ? styles.cardHandCompactHealthCost
                : styles.cardHeaderHealthCost
            }
          >
            {abilityHealthCost}
          </span>
        ) : null}
        <AttributeIconList entries={abilityCostEntries} className={iconClassName} />
      </ResourceIconStrip>,
    );
  }
  if (augmentCostEntries.length > 0 || augmentCostMult) {
    strips.push(
      <ResourceIconStrip key="augment-cost" className={stripClassName}>
        {augmentCostEntries.length > 0 ? (
          <span className={styles.cardHandCompactCostAdd}>
            <span
              className={
                compact
                  ? styles.cardHandCompactCostAddSign
                  : styles.cardHeaderCostAddSign
              }
              aria-hidden="true"
            >
              +
            </span>
            <AttributeIconList
              entries={augmentCostEntries}
              className={iconClassName}
            />
          </span>
        ) : null}
        {augmentCostMult ? (
          <span
            className={
              compact
                ? styles.cardHandCompactCostMult
                : styles.cardHeaderCostMult
            }
          >
            ×{augmentCostMult}
          </span>
        ) : null}
      </ResourceIconStrip>,
    );
  }
  if (!strips.length) return null;
  return (
    <div
      className={`${styles.cardHeaderResources} ${
        useColumnLayout ? styles.cardHeaderResourcesColumn : ""
      }`}
    >
      {strips}
    </div>
  );
}

function NavButtonLabel({ children }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const parent = el.parentElement;
    if (!parent) return undefined;
    const fit = () => {
      const style = window.getComputedStyle(parent);
      const pad =
        (parseFloat(style.paddingLeft) || 0) +
        (parseFloat(style.paddingRight) || 0);
      const available = Math.max(8, parent.clientWidth - pad);
      el.style.fontSize = "";
      const base = parseFloat(window.getComputedStyle(el).fontSize) || 11;
      let low = 6;
      let high = base;
      let best = 6;
      for (let i = 0; i < 12; i += 1) {
        const mid = (low + high) / 2;
        el.style.fontSize = `${mid}px`;
        if (el.scrollWidth <= available) {
          best = mid;
          low = mid;
        } else {
          high = mid;
        }
      }
      el.style.fontSize = `${best}px`;
    };
    return attachVisibleResizeFit(parent, fit);
  }, [children]);
  return (
    <span ref={ref} className={styles.navButtonLabel}>
      {children}
    </span>
  );
}

// Small clock beside a resource icon: the resource is Delayed (usable from
// the owner's next turn). Persistent resources use an asterisk instead.
function DelayedClockMark() {
  return (
    <svg
      className={styles.attrIconDelayed}
      viewBox="0 0 16 16"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" strokeWidth="2" />
      <path
        d="M8 4.2V8l2.6 1.8"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ActionBarResourceIcons({
  resources,
  persistentResources,
  delayedResources = null,
  label = null,
  blockRef = null,
  popKeys = null,
  onPopEnd = null,
  convertPulses = null,
  onConvertEnd = null,
  keywordProps = null,
}) {
  const tipProps = useKeywordTipProps(keywordProps);
  const tokens = [];
  ["strength", "dexterity", "wisdom", "neutral"].forEach((attribute) => {
    const normal = Math.max(0, Number(resources?.[attribute]) || 0);
    const persistent = Math.max(
      0,
      Number(persistentResources?.[attribute]) || 0,
    );
    const convertPulse = convertPulses?.[attribute] || null;
    const convertFrom = convertPulse
      ? Math.max(0, persistent - Math.max(1, convertPulse.count || 1))
      : persistent;
    if (normal >= 5) {
      tokens.push({
        attribute,
        persist: false,
        count: normal,
        key: `${attribute}-n`,
      });
    } else {
      for (let index = 0; index < normal; index += 1) {
        tokens.push({
          attribute,
          persist: false,
          count: 1,
          key: `${attribute}-n-${index}`,
        });
      }
    }
    if (persistent >= 5) {
      tokens.push({
        attribute,
        persist: true,
        count: persistent,
        key: `${attribute}-p`,
        convertKey: convertPulse ? convertPulse.key : null,
      });
    } else {
      for (let index = 0; index < persistent; index += 1) {
        tokens.push({
          attribute,
          persist: true,
          count: 1,
          key: `${attribute}-p-${index}`,
          convertKey:
            convertPulse && index >= convertFrom ? convertPulse.key : null,
        });
      }
    }
    const delayed = Math.max(0, Number(delayedResources?.[attribute]) || 0);
    if (delayed >= 5) {
      tokens.push({
        attribute,
        delayed: true,
        count: delayed,
        key: `${attribute}-d`,
      });
    } else {
      for (let index = 0; index < delayed; index += 1) {
        tokens.push({
          attribute,
          delayed: true,
          count: 1,
          key: `${attribute}-d-${index}`,
        });
      }
    }
  });
  if (!tokens.length && !label) return null;
  const compact = tokens.length > 7;
  return (
    <div className={styles.actionBarResourcesBlock} ref={blockRef}>
      {label ? (
        <div className={styles.actionBarResourcesLabel}>{label}</div>
      ) : null}
      <div
        className={`${styles.actionBarResources} ${
          compact ? styles.actionBarResourcesCompact : ""
        }`}
      >
        {tokens.map((token, tokenIndex) => {
          const iconUrl = ATTR_ICON_URL[token.attribute];
          if (!iconUrl) return null;
          const counted = token.count > 1;
          const previous = tokens[tokenIndex - 1];
          const tokenKind = (entry) =>
            entry.delayed ? "d" : entry.persist ? "p" : "n";
          const groupStart =
            Boolean(previous) &&
            (previous.attribute !== token.attribute ||
              tokenKind(previous) !== tokenKind(token));
          const popPrefix = `${token.attribute}-${tokenKind(token)}-`;
          const matchingPopKeys = popKeys
            ? Object.keys(popKeys).filter((key) =>
                counted ? key.startsWith(popPrefix) : key === token.key,
              )
            : [];
          const popping = matchingPopKeys.length > 0;
          const converting = Boolean(token.convertKey);
          const keyword = getKeywordById(
            token.delayed ? "delayed" : token.attribute,
          );
          const canOpenKeyword =
            Boolean(keyword) &&
            typeof tipProps?.onKeywordClick === "function";
          const active =
            canOpenKeyword && tipProps.activeKeywordId === keyword.id;
          const className = `${styles.actionBarResourceIcon} ${
            counted ? styles.actionBarResourceIconCounted : ""
          }${groupStart ? ` ${styles.actionBarResourceGroupStart}` : ""}${
            token.persist ? ` ${styles.actionBarResourceIconPersist}` : ""
          }${
            token.delayed ? ` ${styles.actionBarResourceIconDelayed}` : ""
          } ${ATTR_CLASS[token.attribute] || ""}${
            popping ? ` ${styles.actionBarResourceIconPop}` : ""
          }${converting ? ` ${styles.actionBarResourceIconConvert}` : ""}${
            canOpenKeyword ? ` ${styles.keywordIconButton}` : ""
          }${active ? ` ${styles.keywordIconButtonActive}` : ""}`;
          const tokenKey = converting
            ? `${token.key}-c${token.convertKey}`
            : token.key;
          const content = (
            <>
              {counted ? (
                <span className={styles.actionBarResourceCount} aria-hidden="true">
                  {token.count}
                </span>
              ) : null}
              <MaskIcon src={iconUrl} className={styles.attrIcon} />
              {token.persist ? (
                <span className={styles.attrIconPersist} aria-hidden="true">
                  *
                </span>
              ) : null}
              {token.delayed ? <DelayedClockMark /> : null}
            </>
          );
          const sharedProps = {
            "data-resource-attr": token.attribute,
            className,
            "aria-label": `${token.count} ${token.attribute}${
              token.persist ? " persist" : token.delayed ? " delayed" : ""
            }`,
            onAnimationEnd:
              (popping && onPopEnd) || (converting && onConvertEnd)
                ? (event) => {
                    if (event.target !== event.currentTarget) return;
                    if (popping && onPopEnd) {
                      matchingPopKeys.forEach((key) => onPopEnd(key));
                    }
                    if (converting && onConvertEnd) {
                      onConvertEnd(token.attribute, token.convertKey);
                    }
                  }
                : undefined,
          };
          if (!canOpenKeyword) {
            return (
              <span key={tokenKey} {...sharedProps}>
                {content}
              </span>
            );
          }
          return (
            <button
              key={tokenKey}
              {...sharedProps}
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                tipProps.onKeywordClick(keyword);
              }}
            >
              {content}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function AttributeIcons({
  attribute,
  count,
  persist,
  delayed = false,
  className,
  keywordProps = null,
}) {
  const tipProps = useKeywordTipProps(keywordProps);
  const amount = Math.max(0, Number(count) || 0);
  const iconUrl = ATTR_ICON_URL[attribute];
  if (!iconUrl || amount <= 0) return null;
  const keyword = getKeywordById(attribute);
  const canOpenKeyword =
    Boolean(keyword) && typeof tipProps?.onKeywordClick === "function";
  const active = canOpenKeyword && tipProps.activeKeywordId === keyword.id;
  const tokens =
    amount >= 5
      ? [{ count: amount, key: `${attribute}-stack` }]
      : Array.from({ length: amount }, (_, index) => ({
          count: 1,
          key: `${attribute}-${index}`,
        }));
  return (
    <span
      className={`${styles.attrIconGroup} ${ATTR_CLASS[attribute] || ""} ${
        delayed ? styles.attrIconGroupDelayed : ""
      } ${className || ""}`}
    >
      {tokens.map((token) => {
        const counted = token.count > 1;
        const itemClass = `${styles.attrIconStackItem} ${
          counted ? styles.attrIconStackItemCounted : ""
        }${canOpenKeyword ? ` ${styles.keywordIconButton}` : ""}${
          active ? ` ${styles.keywordIconButtonActive}` : ""
        }`;
        const content = (
          <>
            {counted ? (
              <span className={styles.attrIconCount} aria-hidden="true">
                {token.count}
              </span>
            ) : null}
            <MaskIcon src={iconUrl} className={styles.attrIcon} />
          </>
        );
        if (!canOpenKeyword) {
          return (
            <span
              key={token.key}
              className={itemClass}
              aria-label={`${amount} ${attribute}${persist ? " persist" : ""}`}
            >
              {content}
            </span>
          );
        }
        return (
          <button
            key={token.key}
            type="button"
            className={itemClass}
            aria-label={`${amount} ${attribute}${persist ? " persist" : ""}`}
            onClick={(event) => {
              event.stopPropagation();
              tipProps.onKeywordClick(keyword);
            }}
          >
            {content}
          </button>
        );
      })}
      {persist ? (
        <span className={styles.attrIconPersist} aria-hidden="true">
          *
        </span>
      ) : null}
      {delayed ? <DelayedClockMark /> : null}
    </span>
  );
}

function AttributeIconList({
  entries,
  persist,
  className,
  keywordProps = null,
}) {
  const list = (entries || []).filter(([, value]) => (Number(value) || 0) > 0);
  if (!list.length) return null;
  return (
    <span className={`${styles.attrIconList} ${className || ""}`}>
      {list.map(([attribute, value]) => (
        <AttributeIcons
          key={attribute}
          attribute={attribute}
          count={value}
          persist={persist}
          keywordProps={keywordProps}
        />
      ))}
    </span>
  );
}

function IdentityModePrefix({ attribute, className }) {
  if (!attribute) return null;
  return (
    <span
      className={`${styles.identityModePrefix} ${className || ""}`}
      title={`If ${formatAttributeLabel(attribute)} Identity`}
    >
      <span className={styles.identityModePrefixLabel}>If</span>
      <AttributeIconList entries={[[attribute, 1]]} />
      <span className={styles.identityModePrefixComma}>,</span>
    </span>
  );
}

function ResourceIconStrip({ children, className }) {
  const items = React.Children.toArray(children).filter(Boolean);
  if (!items.length) return null;
  return (
    <div className={`${styles.resourceIconStrip} ${className || ""}`}>
      {items}
    </div>
  );
}

function ResourceGrantIconListWithShock({
  entries,
  shockStacks = 0,
  persist = false,
  className,
}) {
  const list = (entries || []).filter(([, value]) => (Number(value) || 0) > 0);
  if (!list.length) return null;
  if ((shockStacks || 0) <= 0) {
    return (
      <AttributeIconList entries={list} persist={persist} className={className} />
    );
  }
  const { gained, lost } = splitResourceGrantForShock(list);
  return (
    <span className={`${styles.attrIconList} ${className || ""}`}>
      {gained.length > 0 ? (
        <AttributeIconList entries={gained} persist={persist} />
      ) : null}
      {lost.length > 0 ? (
        <span className={styles.shockLostResourceGrant} aria-label="Shock reduces">
          (
          <AttributeIconList entries={lost} persist={persist} />
          )
        </span>
      ) : null}
    </span>
  );
}

// Faded resource icons with a clock for an equipment's delayedResourceGrant
// passives (shown beside its normal resource grant).
function DelayedResourceGrantIcons({ equipment, className = "" }) {
  const entries = delayedResourceGrantEntries(equipment);
  if (!entries.length) return null;
  return (
    <span className={`${styles.attrIconList} ${className}`}>
      {entries.map(([attribute, value]) => (
        <AttributeIcons
          key={`delayed-${attribute}`}
          attribute={attribute}
          count={value}
          delayed
        />
      ))}
    </span>
  );
}

function renderEquipmentEffectActionContent(action, shockStacks = 0, options = {}) {
  const resourceGrants = action.resourceGrants || [];
  const persistGrants = action.persistGrants || [];
  const hasResourceGrants = resourceGrants.length > 0;
  const hasPersistGrants = persistGrants.length > 0;
  const effect = options.effect || null;
  const equipment = options.equipment || null;
  const seat = options.seat || null;
  return (
    <>
      {hasResourceGrants ? (
        <>
          Gain
          <ResourceGrantIconListWithShock
            entries={resourceGrants}
            shockStacks={shockStacks}
          />
        </>
      ) : null}
      {hasPersistGrants ? (
        <>
          Gain
          <ResourceGrantIconListWithShock
            entries={persistGrants}
            shockStacks={shockStacks}
            persist
          />
        </>
      ) : null}
      {(hasResourceGrants || hasPersistGrants) && action.label ? " · " : null}
      {action.label ? (
        <EffectActionOutputFace
          effect={effect}
          equipment={equipment}
          seat={seat}
          fallbackText={action.label}
          outputDelta={action.outputDelta || 0}
          shorthand={false}
        />
      ) : null}
    </>
  );
}

function EffectActionOutputFace({
  effect,
  equipment = null,
  seat = null,
  fallbackText = null,
  outputDelta = 0,
  shorthand = true,
  compareEffect = null,
  compareEquipment = null,
  compareText = null,
}) {
  const display = effect
    ? formatEquipmentEffectQuickDisplay(effect, equipment, seat)
    : null;
  const compareDisplay =
    compareEffect && display
      ? formatEquipmentEffectQuickDisplay(
          compareEffect,
          compareEquipment || equipment,
          seat,
        )
      : null;
  const amount = Math.max(1, Math.floor(Number(display?.amount) || 1));
  const levelUpDelta =
    compareDisplay && compareDisplay.kind === display.kind
      ? Math.max(
          0,
          amount - Math.max(1, Math.floor(Number(compareDisplay.amount) || 1)),
        )
      : 0;
  const withLevelUp = (node) =>
    levelUpDelta > 0 ? (
      <span className={styles.levelUpOutput}>{node}</span>
    ) : (
      node
    );
  if (display?.kind === "heal") {
    return withLevelUp(
      <HealInline
        amount={amount}
        outputDelta={levelUpDelta > 0 ? levelUpDelta : outputDelta}
      />,
    );
  }
  if (!fallbackText) {
    if (display && STATUS_EFFECT_ICON_IDS.includes(display.kind)) {
      return withLevelUp(
        <StatusEffectIconStack
          effectId={display.kind}
          amount={amount}
          outputDelta={levelUpDelta > 0 ? levelUpDelta : outputDelta}
        />,
      );
    }
    return null;
  }
  return renderTextWithStatusEffectIcons(fallbackText, {
    outputDelta,
    shorthand,
    compareText,
  });
}

function getEquipmentBoardSlotKey(seat, equipment) {
  if (!equipment) return null;
  if (equipment.boardSlot) return equipment.boardSlot;
  if (!seat?.board || !equipment.instanceId) return null;
  const entry = Object.entries(seat.board).find(
    ([, item]) => item && item.instanceId === equipment.instanceId,
  );
  return entry ? entry[0] : null;
}

function countSlottedAbilitiesForEquipment(equipment, seat = null) {
  if (!equipment) return 0;
  const boardSlot = getEquipmentBoardSlotKey(seat, equipment);
  const slots =
    boardSlot && seat
      ? getColumnAbilitySlots(seat, boardSlot)
      : equipment.abilitySlots || [];
  let amount = 0;
  slots.forEach((slot) => {
    if (slot?.ability) amount += 1;
  });
  if (equipment.inherentAbility) amount += 1;
  return amount;
}

function resolveEffectChargeDamagePreview(effect, equipment = null) {
  if (!effect?.chargeDamage) return null;
  const charges = getChargeCount(equipment, effect.charge);
  const label = getChargeKindLabel(effect.charge);
  const singular = getChargeKindLabel(effect.charge, 1);
  const chargesPerDamage = Math.max(
    0,
    effect.chargeDamage.chargesPerDamage || 0,
  );
  if (chargesPerDamage > 0) {
    return {
      mode: "chargesPerDamage",
      charges,
      chargesPerDamage,
      label,
      singular,
      damage: Math.floor(charges / chargesPerDamage),
      consume: effect.chargeDamage.consume,
    };
  }
  const perCharge = Math.max(
    0,
    effect.chargeDamage.amountPerCharge ??
      effect.chargeDamage.perCharge ??
      0,
  );
  return {
    mode: "amountPerCharge",
    charges,
    perCharge,
    label,
    singular,
    damage: perCharge * charges,
    consume: effect.chargeDamage.consume,
  };
}

function getEffectQuickDamageAmount(effect, equipment = null) {
  if (!effect) return 0;
  if (effect.id === "damageOpponent") {
    const chargePreview = resolveEffectChargeDamagePreview(effect, equipment);
    if (chargePreview && equipment) {
      return (
        (effect.effectiveAmount ?? effect.amount ?? 0) + chargePreview.damage
      );
    }
    if (Array.isArray(effect.damageParts) && effect.damageParts.length) {
      return (
        effect.effectiveAmount ??
        effect.damageParts.reduce((sum, part) => sum + (part.amount || 0), 0)
      );
    }
    return effect.effectiveAmount ?? effect.amount ?? 0;
  }
  if (effect.id === "damageSelf") {
    return effect.amount || 0;
  }
  if (effect.id === "bleed" || effect.id === "poison") {
    const damage = effect.effectiveDamage ?? effect.damage ?? 0;
    if (damage > 0) return damage;
    return effect.effectiveAmount ?? effect.amount ?? 0;
  }
  if (
    effect.id === "convertPhysicalArmorToDamage" ||
    effect.id === "convertMagicArmorToDamage"
  ) {
    return effect.amount || 0;
  }
  if (convertStacksDealsDamage(effect)) {
    return effect.effectiveAmount ?? effect.amount ?? 0;
  }
  return 0;
}

function effectQuickDisplayIsDamage(effect) {
  if (!effect) return false;
  if (effect.id === "damageOpponent" || effect.id === "damageSelf") {
    return true;
  }
  if (
    effect.id === "convertPhysicalArmorToDamage" ||
    effect.id === "convertMagicArmorToDamage" ||
    convertStacksDealsDamage(effect)
  ) {
    return true;
  }
  if (effect.id === "bleed" || effect.id === "poison") {
    return (
      (effect.effectiveDamage ?? effect.damage ?? 0) > 0 ||
      (effect.effectiveAmount ?? effect.amount ?? 0) > 0
    );
  }
  return false;
}

function truncateEquipmentQuickLabel(text, max = 10) {
  const clean = String(text || "").trim();
  if (clean.length <= max) return clean;
  return `${clean.slice(0, max - 1)}…`;
}

function getEquipmentEffectForActionIndex(equipment, effectIndex) {
  const activated = equipment?.activatedEffect;
  if (effectIndex != null && Array.isArray(activated?.choose)) {
    return activated.choose[effectIndex];
  }
  return activated;
}

function formatEquipmentEffectQuickDisplay(
  effect,
  equipment = null,
  seat = null,
) {
  if (!effect || Array.isArray(effect)) return null;
  if (effect.id === "bleed") {
    return {
      kind: "bleed",
      amount: String(effect.effectiveAmount ?? effect.amount ?? 0),
    };
  }
  if (effect.id === "poison") {
    return {
      kind: "poison",
      amount: String(effect.effectiveAmount ?? effect.amount ?? 0),
    };
  }
  if (effect.id === "burn") {
    return {
      kind: "burn",
      amount: String(effect.effectiveAmount ?? effect.amount ?? 0),
    };
  }
  if (effect.id === "chill") {
    return {
      kind: "chill",
      amount: String(effect.effectiveAmount ?? effect.amount ?? 0),
    };
  }
  if (effect.id === "shock") {
    return {
      kind: "shock",
      amount: String(effect.effectiveAmount ?? effect.amount ?? 0),
    };
  }
  if (effect.id === "restock") {
    return {
      kind: "restock",
      amount: String(Math.max(1, effect.amount || 1)),
    };
  }
  if (effect.id === "dodgeRoll") {
    return {
      kind: "dodge",
      amount: String(Math.max(1, effect.amount || 1)),
    };
  }
  if (effect.id === "applyAilment") {
    const ailment = String(effect.ailment || effect.type || "")
      .trim()
      .toLowerCase();
    if (STATUS_EFFECT_ICON_IDS.includes(ailment)) {
      return {
        kind: ailment,
        amount: String(effect.effectiveAmount ?? effect.amount ?? 0),
      };
    }
  }
  if (effectQuickDisplayIsDamage(effect)) {
    return {
      kind: "damage",
      damageType: effect.damageType || "physical",
      amount: String(getEffectQuickDamageAmount(effect, equipment)),
    };
  }
  if (effect.id === "healSelf") {
    return { kind: "heal" };
  }
  if (convertStacksHeals(effect)) {
    return { kind: "heal" };
  }
  if (effect.id === "maxHealthIncrease") {
    return { top: String(effect.amount || 0), bottom: "MAX HP" };
  }
  if (effect.id === "temporaryPhysicalArmor") {
    return {
      kind: "physicalArmor",
      amount: String(effect.effectiveAmount ?? effect.amount ?? 0),
    };
  }
  if (effect.id === "temporaryMagicArmor") {
    return {
      kind: "magicArmor",
      amount: String(effect.effectiveAmount ?? effect.amount ?? 0),
    };
  }
  if (effect.id === "temporaryArmorFromIdentity") {
    const amount = String(effect.effectiveAmount ?? effect.amount ?? 0);
    const identity = equipment?.chosenIdentity;
    const armor = effect.identityArmor;
    if (identity === "strength" || armor === "physical") {
      return { kind: "physicalArmor", amount };
    }
    if (identity === "wisdom" || armor === "magic") {
      return { kind: "magicArmor", amount };
    }
    if (identity === "dexterity" || armor === "dodge") {
      return { kind: "dodge", amount: String(Math.max(1, Number(amount) || 1)) };
    }
  }
  if (effect.id === "attackFromIdentity") {
    const amount = String(effect.effectiveAmount ?? effect.amount ?? 0);
    const identity = equipment?.chosenIdentity;
    const identityEffect = effect.identityEffect;
    if (identity === "dexterity" || identityEffect === "shock") {
      return { kind: "shock", amount };
    }
    if (identity === "wisdom" || identityEffect === "lightning") {
      return {
        kind: "damage",
        damageType: "lightning",
        amount,
      };
    }
  }
  if (effect.id === "ailmentFromIdentity") {
    const amount = String(effect.effectiveAmount ?? effect.amount ?? 0);
    const identity = equipment?.chosenIdentity;
    const identityEffect = effect.identityEffect;
    if (identity === "strength" || identityEffect === "burn") {
      return { kind: "burn", amount };
    }
    if (identity === "dexterity" || identityEffect === "shock") {
      return { kind: "shock", amount };
    }
    if (identity === "wisdom" || identityEffect === "chill") {
      return { kind: "chill", amount };
    }
  }
  if (effect.id === "identityExpend") {
    const identity = equipment?.chosenIdentity;
    const resolved =
      identity && effect.byIdentity?.[identity]
        ? effect.byIdentity[identity]
        : Array.isArray(effect.identityEffects) && effect.identityEffects.length
          ? effect.identityEffects.length === 1
            ? effect.identityEffects[0]
            : effect.identityEffects
          : null;
    if (resolved && !Array.isArray(resolved)) {
      return formatEquipmentEffectQuickDisplay(resolved, equipment, seat);
    }
    if (Array.isArray(resolved) && resolved.length === 1) {
      return formatEquipmentEffectQuickDisplay(resolved[0], equipment, seat);
    }
  }
  if (effect.id === "gainThorns") {
    return {
      kind: "thorns",
      amount: String(Math.max(1, effect.amount || 1)),
    };
  }
  if (effect.id === "temporaryThorns") {
    return {
      top: String(
        effect.effectivePercent ?? effect.percent ?? effect.amount ?? 0,
      ),
      bottom: "THORNS",
    };
  }
  if (effect.id === "regeneration") {
    return {
      kind: "regeneration",
      amount: String(effect.effectiveAmount ?? effect.amount ?? 0),
    };
  }
  if (effect.id === "convertPhysicalArmorToDamage") {
    return {
      top: String(effect.amount || 0),
      bottom: "P.ARM»DMG",
    };
  }
  if (effect.id === "convertMagicArmorToDamage") {
    return {
      top: String(effect.amount || 0),
      bottom: "M.ARM»DMG",
    };
  }
  if (effect.id === "convertResource") {
    return { top: "⇄", bottom: "CONVERT" };
  }
  const fallback = truncateEquipmentQuickLabel(
    formatActivatedEffectText(effect, { equipment, seat }) ||
      effect.id ||
      "FX",
    12,
  );
  return { top: "•", bottom: fallback.toUpperCase() };
}

function normalizeEquipExpendConfirmActionText(text) {
  if (!text) return "use this effect";
  const trimmed = String(text).trim();
  if (!trimmed) return "use this effect";
  return trimmed.charAt(0).toLowerCase() + trimmed.slice(1);
}

function buildEquipEffectExpendConfirmText(effect, equipment = null, seat = null) {
  return normalizeEquipExpendConfirmActionText(
    formatActivatedEffectText(effect, {
      omitCost: true,
      equipment,
      seat,
    }),
  );
}

function buildEquipResourceExpendConfirmText(entries) {
  const grants = (entries || []).filter(
    ([, value]) => (Number(value) || 0) > 0,
  );
  if (!grants.length) return "gain resources";
  return `gain ${grants
    .map(([attribute, value]) => `${value} ${attribute}`)
    .join(", ")}`;
}

function buildEquipExpendConfirmKey(instanceId, mode, detail = "") {
  return `expend:${mode}:${instanceId}${detail ? `:${detail}` : ""}`;
}

function computeEquipExpendConfirmPopupStyle(buttonRect, popupWidth, popupHeight) {
  const margin = 12;
  const gap = 10;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const centerY = buttonRect.top + buttonRect.height / 2;
  const buttonLeft = buttonRect.left;
  const buttonRight = buttonRect.left + buttonRect.width;
  const buttonCenterX = buttonLeft + buttonRect.width / 2;
  const viewportCenterX = viewportWidth / 2;

  const overlapsButton = (left) =>
    left + popupWidth + gap > buttonLeft && left < buttonRight + gap;

  const clampLeft = (left) =>
    Math.max(margin, Math.min(left, viewportWidth - margin - popupWidth));

  let left = clampLeft(viewportCenterX - popupWidth / 2);

  if (overlapsButton(left)) {
    if (buttonCenterX >= viewportCenterX) {
      left = clampLeft(buttonLeft - gap - popupWidth);
    } else {
      left = clampLeft(buttonRight + gap);
    }
  }

  if (overlapsButton(left)) {
    left =
      buttonCenterX >= viewportCenterX
        ? clampLeft(buttonLeft - gap - popupWidth)
        : clampLeft(buttonRight + gap);
  }

  const halfHeight = popupHeight / 2;
  const top = Math.max(
    margin + halfHeight,
    Math.min(viewportHeight - margin - halfHeight, centerY),
  );

  return {
    position: "fixed",
    left,
    top,
    transform: "translateY(-50%)",
    zIndex: 12000,
  };
}

function ResourceGrantChoiceIconStrip({
  choices,
  persist,
  iconClassName,
  shockStacks = 0,
  stripClassName = "",
}) {
  const list = (choices || []).filter((entries) =>
    (entries || []).some(([, value]) => (Number(value) || 0) > 0),
  );
  if (!list.length) return null;
  return (
    <ResourceIconStrip className={stripClassName}>
      {list.map((entries, index) => (
        <React.Fragment key={`grant-choice-${index}`}>
          {index > 0 ? (
            <span className={styles.resourceGrantChoiceSep} aria-hidden="true">
              /
            </span>
          ) : null}
          <ResourceGrantIconListWithShock
            entries={entries}
            shockStacks={shockStacks}
            persist={persist}
            className={iconClassName}
          />
        </React.Fragment>
      ))}
    </ResourceIconStrip>
  );
}

function PaymentArrowPrefix({ cost }) {
  const entries = costEntries(cost);
  if (!entries.length) return null;
  return (
    <>
      <AttributeIconList entries={entries} />
      <span className={styles.cardEffectArrow} aria-hidden="true" />
    </>
  );
}

function ExpendActivateIcon({ className = "" }) {
  return (
    <img
      src={EXPEND_ACTIVATE_ICON_URL}
      alt=""
      className={`${styles.cardExpendStar}${className ? ` ${className}` : ""}`}
      draggable={false}
      aria-hidden="true"
    />
  );
}

function InvokeIcon({ className = "", label = null }) {
  return (
    <img
      src={INVOKE_ICON_URL}
      alt={label || ""}
      title={label || undefined}
      className={`${styles.invokeIcon}${className ? ` ${className}` : ""}`}
      draggable={false}
      aria-hidden={label ? undefined : "true"}
    />
  );
}

function ExpendArrowPrefix({
  cost,
  chargeCost = 0,
  durabilityCost = 0,
  selfDamage = null,
}) {
  const entries = costEntries(cost);
  return (
    <>
      <ExpendActivateIcon />
      {entries.length > 0 ? <AttributeIconList entries={entries} /> : null}
      <ChargeCostIcons amount={chargeCost} />
      <KeywordTipContext.Provider value={null}>
        <DurabilityCostIcons amount={durabilityCost} />
      </KeywordTipContext.Provider>
      <SelfDamageCostIcons entries={selfDamage} />
      <span className={styles.cardEffectArrow} aria-hidden="true" />
    </>
  );
}

function HealthCostIcons({ amount, keywordProps = null }) {
  const tipProps = useKeywordTipProps(keywordProps);
  const count = Math.max(0, Math.floor(Number(amount) || 0));
  if (count <= 0) return null;
  const keyword = getKeywordById("health");
  const canOpenKeyword =
    Boolean(keyword) && typeof tipProps?.onKeywordClick === "function";
  const active = canOpenKeyword && tipProps.activeKeywordId === keyword.id;
  const tokens =
    count >= 5
      ? [{ count, key: "health-stack" }]
      : Array.from({ length: count }, (_, index) => ({
          count: 1,
          key: `health-${index}`,
        }));
  const label = `${count} health`;
  return (
    <span className={styles.statusEffectIconRow}>
      {tokens.map((token) => {
        const counted = token.count > 1;
        const itemClass = [
          styles.statusEffectIconItem,
          counted ? styles.statusEffectIconItemCounted : "",
          canOpenKeyword ? styles.keywordIconButton : "",
          active ? styles.keywordIconButtonActive : "",
        ]
          .filter(Boolean)
          .join(" ");
        const content = (
          <>
            {counted ? (
              <span className={styles.effectHealCount} aria-hidden="true">
                {token.count}
              </span>
            ) : null}
            <img
              src={HEAL_ICON_URL}
              alt=""
              className={`${styles.statusEffectIcon} ${styles.effectHealIcon}`}
              draggable={false}
              aria-hidden="true"
            />
          </>
        );
        if (!canOpenKeyword) {
          return (
            <span key={token.key} className={itemClass} aria-label={label}>
              {content}
            </span>
          );
        }
        return (
          <button
            key={token.key}
            type="button"
            className={itemClass}
            aria-label={label}
            onClick={(event) => {
              event.stopPropagation();
              tipProps.onKeywordClick(keyword);
            }}
          >
            {content}
          </button>
        );
      })}
    </span>
  );
}

function AbilityActivatePrefix({
  cost,
  healthCost = 0,
  chargeCost = 0,
}) {
  const entries = costEntries(cost);
  return (
    <>
      <ExpendActivateIcon />
      {healthCost > 0 ? (
        <span className={styles.cardAbilityActivateHealthCost}>
          {healthCost}
        </span>
      ) : null}
      {entries.length > 0 ? <AttributeIconList entries={entries} /> : null}
      <ChargeCostIcons amount={chargeCost} />
      <span className={styles.cardEffectArrow} aria-hidden="true" />
    </>
  );
}

const TINT_CLASS = {
  strength: styles.tintStrength,
  dexterity: styles.tintDexterity,
  wisdom: styles.tintWisdom,
  neutral: styles.tintNeutral,
};

const ATTRIBUTE_COLOR_VAR = {
  strength: "var(--ai-strength)",
  dexterity: "var(--ai-dexterity)",
  wisdom: "var(--ai-wisdom)",
  neutral: "var(--ai-neutral)",
};

const TRADITIONAL_ATTRIBUTE_COLOR = {
  ...ATTRIBUTE_COLOR_VAR,
  dexterity: "#4F7942",
};

function primaryAttribute(cardOrPiece) {
  if (!cardOrPiece) return null;
  if (cardOrPiece.attribute) return cardOrPiece.attribute;
  if (Array.isArray(cardOrPiece.attributes) && cardOrPiece.attributes.length) {
    return cardOrPiece.attributes[0];
  }
  return null;
}

function cardAttributeList(cardOrPiece) {
  if (!cardOrPiece) return [];
  if (cardOrPiece.attribute) return [cardOrPiece.attribute];
  if (Array.isArray(cardOrPiece.attributes)) {
    return cardOrPiece.attributes.filter(Boolean);
  }
  return [];
}

function isHybridAttributeCard(cardOrPiece) {
  return cardAttributeList(cardOrPiece).length >= 2;
}

function getHybridOutlineProps(
  cardOrPiece,
  attributeColors = ATTRIBUTE_COLOR_VAR,
) {
  const attrs = cardAttributeList(cardOrPiece);
  if (attrs.length < 2) {
    return { className: "", style: undefined };
  }
  const style = {
    "--ai-hybrid-a":
      attributeColors[attrs[0]] || attributeColors.neutral,
    "--ai-hybrid-b":
      attributeColors[attrs[1]] || attributeColors.neutral,
  };
  if (attrs.length >= 3) {
    style["--ai-hybrid-c"] =
      attributeColors[attrs[2]] || attributeColors.neutral;
  }
  return {
    className:
      attrs.length >= 3 ? styles.hybridOutline3 : styles.hybridOutline2,
    style,
  };
}

function cardMatchesAttributeFilter(card, attributeFilter) {
  if (!attributeFilter || attributeFilter === "all") return true;
  if (card?.attribute === attributeFilter) return true;
  return cardAttributeList(card).includes(attributeFilter);
}

function cardHasType(card, type) {
  if (!card) return false;
  if (Array.isArray(card.type)) return card.type.includes(type);
  return card.type === type;
}

function isNinjutsuFreeThrowableTokenEquip(seat, catalogMap, handCard, card) {
  if (!handCard?.token) return false;
  if (!card || !cardHasType(card, "throwable")) return false;
  const aspect = catalogMap?.[seat?.aspectId];
  return (aspect?.passiveEffects || []).some(
    (effect) => effect?.id === "ninjutsuThrowableToken",
  );
}

const EQUIPMENT_PLACEHOLDER_IMAGE = "cards/placeholder.webp";

function getEquipmentCardImageUrl(card, options = {}) {
  if (!card) return null;
  const id = card.id || card.cardId;
  if (!id) return null;
  if (!cardHasType(card, "equipment") && !card.slot) return null;
  const relPath = `cards/${id}.webp`;
  // Equipment without art yet (e.g. prototypes) shows the shared placeholder.
  return aeternaAssetUrl(
    isKnownAsset(relPath) === false ? EQUIPMENT_PLACEHOLDER_IMAGE : relPath,
    { thumb: !options.full },
  );
}

function getAbilityCardImageUrl(card, options = {}) {
  if (!card) return null;
  const id = card.id || card.cardId;
  if (!id || !cardHasType(card, "ability")) return null;
  return aeternaAssetUrl(`abilities/${id}.webp`, { thumb: !options.full });
}

function getAspectCardImageUrl(card, options = {}) {
  if (!card) return null;
  const id = card.id || card.cardId;
  if (!id || !cardHasType(card, "aspect")) return null;
  return aeternaAssetUrl(`aspects/${id}.webp`, { thumb: !options.full });
}

// Card art defaults to the server-generated thumbnail; pass { full: true }
// for large views (focus, exam, art preview).
function getCardImageUrl(card, options = {}) {
  return (
    getEquipmentCardImageUrl(card, options) ||
    getAbilityCardImageUrl(card, options) ||
    getAspectCardImageUrl(card, options)
  );
}

function getCardFullImageUrl(card) {
  return getCardImageUrl(card, { full: true });
}

function findMatchFoilEffect(match, cardId) {
  if (!match?.seats || !cardId) return null;
  for (const seat of Object.values(match.seats)) {
    if (!seat) continue;
    if (seat.aspectId === cardId && seat.aspectFoil) return seat.aspectFoil;
    for (const card of seat.hand || []) {
      if (
        (card?.cardId === cardId || card?.id === cardId) &&
        card.foilEffect
      ) {
        return card.foilEffect;
      }
    }
    for (const equipment of Object.values(seat.board || {})) {
      if (!equipment) continue;
      if (equipment.cardId === cardId && equipment.foilEffect) {
        return equipment.foilEffect;
      }
      if (
        equipment.inherentAbility?.cardId === cardId &&
        equipment.inherentAbility.foilEffect
      ) {
        return equipment.inherentAbility.foilEffect;
      }
      for (const slot of equipment.abilitySlots || []) {
        const ability = slot?.ability;
        if (ability?.cardId === cardId && ability.foilEffect) {
          return ability.foilEffect;
        }
      }
    }
    for (const rack of Object.values(seat.abilityBoard || {})) {
      if (!rack) continue;
      for (const slot of rack.abilitySlots || []) {
        const ability = slot?.ability;
        if (ability?.cardId === cardId && ability.foilEffect) {
          return ability.foilEffect;
        }
      }
    }
  }
  return null;
}

function getEquipmentActiveMinions(equipment, seat = null) {
  if (Array.isArray(equipment?.activeMinions) && equipment.activeMinions.length) {
    return equipment.activeMinions.filter(
      (minion) => minion && (minion.hp || 0) > 0,
    );
  }
  if (equipment?.occupyingMinion && (equipment.occupyingMinion.hp || 0) > 0) {
    return [equipment.occupyingMinion];
  }
  if (!seat || !equipment?.instanceId) return [];
  return (Array.isArray(seat.minions) ? seat.minions : []).filter(
    (minion) =>
      minion &&
      minion.equipmentInstanceId === equipment.instanceId &&
      (minion.hp || 0) > 0,
  );
}

function getEquipmentOccupyingMinion(equipment, seat = null) {
  return getEquipmentActiveMinions(equipment, seat)[0] || null;
}

function findAbilityForMinion(equipment, minion, seat = null) {
  if (!minion?.abilityInstanceId) return null;
  if (equipment?.inherentAbility?.instanceId === minion.abilityInstanceId) {
    return equipment.inherentAbility;
  }
  for (const slot of equipment?.abilitySlots || []) {
    if (slot?.ability?.instanceId === minion.abilityInstanceId) {
      return slot.ability;
    }
  }
  for (const rack of Object.values(seat?.abilityBoard || {})) {
    for (const slot of rack?.abilitySlots || []) {
      if (slot?.ability?.instanceId === minion.abilityInstanceId) {
        return slot.ability;
      }
    }
  }
  return null;
}

function getEquipmentActiveAuras(equipment, seat = null) {
  if (Array.isArray(equipment?.activeAuras) && equipment.activeAuras.length) {
    return equipment.activeAuras.filter(Boolean);
  }
  if (equipment?.occupyingAura) {
    return [equipment.occupyingAura];
  }
  if (!seat || !equipment?.instanceId) return [];
  return (Array.isArray(seat.auras) ? seat.auras : []).filter(
    (aura) =>
      aura && aura.equipmentInstanceId === equipment.instanceId,
  );
}

function getEquipmentOccupyingAura(equipment, seat = null) {
  return getEquipmentActiveAuras(equipment, seat)[0] || null;
}

function findAbilityForAura(equipment, aura, seat = null) {
  return findAbilityForMinion(equipment, aura, seat);
}

function isAbilityRackHostSlot(slot) {
  return slot?.id === "host";
}

function isAbilityRackAugmentSlot(slot) {
  return typeof slot?.id === "string" && slot.id.startsWith("aug");
}

function getSeatAbilityRack(seat, boardSlot) {
  if (!seat || !boardSlot) return null;
  if (seat.abilityBoard?.[boardSlot]) return seat.abilityBoard[boardSlot];
  return {
    boardSlot,
    instanceId: null,
    name: boardSlot,
    isAbilityRack: true,
    abilitySlots: [
      { id: "host", linked: true, ability: null },
      { id: "aug0", linked: true, ability: null },
      { id: "aug1", linked: true, ability: null },
      { id: "aug2", linked: true, ability: null },
    ],
  };
}

function getColumnAbilitySlots(seat, boardSlot) {
  return getSeatAbilityRack(seat, boardSlot)?.abilitySlots || [];
}

function listAbilityBoardSlotKeys(_seat) {
  return ["weapon", "armor", "trinket"];
}

function equipmentHasBoardOccupant(equipment, seat = null) {
  return Boolean(
    getEquipmentOccupyingMinion(equipment, seat) ||
      getEquipmentOccupyingAura(equipment, seat),
  );
}

function getCardFrameKind(card) {
  if (!card) return null;
  if (cardHasType(card, "aspect")) return "aspect";
  if (cardHasType(card, "equipment") || card.slot) return "equipment";
  if (cardHasType(card, "ability")) return "ability";
  return null;
}

function getEquipmentFrameCard(equipment, catalogMap = {}) {
  if (!equipment) return null;
  const catalog =
    equipment.cardId && catalogMap[equipment.cardId]
      ? catalogMap[equipment.cardId]
      : null;
  if (equipment.chosenIdentity) {
    return {
      ...(catalog || {}),
      ...equipment,
      attributes: [equipment.chosenIdentity],
      type: equipment.type || catalog?.type,
    };
  }
  if (!catalog) return equipment;
  return {
    ...catalog,
    ...equipment,
    type: equipment.type || catalog.type,
    abilitySlots: equipment.abilitySlots?.length
      ? equipment.abilitySlots
      : catalog.abilitySlots,
  };
}

function getCardFrameUrl(card) {
  if (!card) return null;
  const kind = getCardFrameKind(card);
  if (!kind) return null;
  if (kind === "aspect") {
    const aspectFile = CARD_FRAME_FILES.aspect;
    if (!aspectFile) return null;
    return cardFrameAssetUrl(aspectFile);
  }
  const attrs = card.chosenIdentity
    ? [card.chosenIdentity]
    : cardAttributeList(card);
  const attributeKey =
    attrs.length >= 2
      ? attrs.join("+")
      : card.chosenIdentity || primaryAttribute(card);
  if (!attributeKey) return null;
  const file = CARD_FRAME_FILES[`${attributeKey}:${kind}`];
  if (!file) return null;
  return cardFrameAssetUrl(file);
}

function getSimpleCardFrameKey(card, foilEffect = null) {
  if (!card || !getCardFrameKind(card)) return null;
  if (!isTraditionalFoil(foilEffect)) return null;
  const attrs = card.chosenIdentity
    ? [card.chosenIdentity]
    : cardAttributeList(card);
  if (new Set(attrs).size >= 2) return "hybrid";
  return SIMPLE_CARD_FRAME_FILES[attrs[0]] ? attrs[0] : "neutral";
}

function getSimpleCardFrameUrl(frameKey) {
  const file = SIMPLE_CARD_FRAME_FILES[frameKey];
  if (!file) return null;
  return cardFrameAssetUrl(file);
}

function CardFrameOverlay({ frameUrl, className = "" }) {
  if (!frameUrl) return null;
  return (
    <AiLazyImage
      aria-hidden="true"
      className={`${styles.cardFrameOverlay} ${className || ""}`}
      src={frameUrl}
      alt=""
      draggable={false}
    />
  );
}

const CARD_TYPE_DISPLAY_NAMES = {
  handWraps: "hand wraps",
};

function formatCardTypes(card) {
  if (!card) return "";
  const types = Array.isArray(card.type)
    ? card.type.filter((type) => type !== "ability" && type !== "equipment")
    : card.type && card.type !== "ability" && card.type !== "equipment"
      ? [card.type]
      : [];
  if (card.token && !types.includes("token")) types.push("token");
  return types.map((type) => CARD_TYPE_DISPLAY_NAMES[type] || type).join(" · ");
}

function formatSlotLabel(slot) {
  if (slot === "weapon") return "Weapon";
  if (slot === "armor") return "Armor";
  if (slot === "trinket") return "Trinket";
  if (slot === "aspect") return "Aspect";
  if (slot === "oneHandedWeapon") return "One-Hand";
  if (slot === "twoHandedWeapon") return "Two-Hand";
  if (slot === "offHand") return "Off-Hand";
  if (slot === "head") return "Head";
  if (slot === "hands") return "Hands";
  if (slot === "body") return "Body";
  if (slot === "feet") return "Feet";
  return slot || "";
}

function resolveCardBoardSlot(card) {
  if (!card) return null;
  if (card.boardSlot === "weapon" || card.boardSlot === "armor" || card.boardSlot === "trinket") {
    return card.boardSlot;
  }
  const slot = card.slot;
  if (slot === "oneHandedWeapon" || slot === "twoHandedWeapon" || slot === "weapon") {
    return "weapon";
  }
  if (slot === "trinket") return "trinket";
  if (
    slot === "offHand" ||
    slot === "head" ||
    slot === "hands" ||
    slot === "feet" ||
    slot === "body" ||
    slot === "armor"
  ) {
    return "armor";
  }
  return null;
}

const CARD_TYPE_FILTER_PRIORITY = [
  "equipment",
  "ability",
  "aspect",
  "attack",
  "melee",
  "ranged",
  "magic",
  "magical",
  "physical",
  "minion",
  "aura",
  "healing",
  "health",
  "resource",
  "poison",
  "bleed",
  "fire",
  "cold",
  "lightning",
  "burn",
  "chill",
  "shock",
  "curse",
  "sacrifice",
  "dark",
  "neutral",
  "unarmed",
  "sword",
  "axe",
  "mace",
  "dagger",
  "club",
  "wand",
  "dart",
  "staff",
  "hammer",
  "bow",
  "crossbow",
  "launcher",
  "polearm",
  "hook",
  "throwable",
  "banner",
  "rod",
  "quiver",
  "artifact",
  "shield",
  "helmet",
  "hat",
  "crown",
  "cap",
  "circlet",
  "hood",
  "robes",
  "cuirass",
  "cloak",
  "mail",
  "coat",
  "vest",
  "bracers",
  "gloves",
  "gauntlets",
  "handWraps",
  "sandals",
  "boots",
  "greaves",
  "sabatons",
  "ring",
  "necklace",
  "earrings",
  "headband",
  "token",
];

// Item-kind tags (EQUIPMENT_ITEM_TYPES, from deckSuggest.js) get their own
// Equipment Type filter instead of mixing into the general Type filter.

const EQUIPMENT_TIER_FILTER_OPTIONS = ["1", "2", "3"];

const CARD_TYPE_FILTER_PRIORITY_INDEX = Object.fromEntries(
  CARD_TYPE_FILTER_PRIORITY.map((type, index) => [type, index]),
);

const AILMENT_FILTER_TYPES = ["burn", "chill", "shock"];

function ailmentFilterValuePresent(value) {
  if (typeof value === "number") return value > 0;
  if (Array.isArray(value)) return value.some(ailmentFilterValuePresent);
  return Boolean(value);
}

function effectTreeMentionsAilment(node, ailment) {
  if (node == null) return false;
  if (typeof node === "string") return node === ailment;
  if (Array.isArray(node)) {
    return node.some((entry) => effectTreeMentionsAilment(entry, ailment));
  }
  if (typeof node !== "object") return false;
  return Object.entries(node).some(([key, value]) => {
    if (
      key.toLowerCase().includes(ailment) &&
      ailmentFilterValuePresent(value)
    ) {
      return true;
    }
    return effectTreeMentionsAilment(value, ailment);
  });
}

function cardAilmentTypes(card) {
  if (!card) return [];
  const sources = [
    card.activatedEffect,
    card.effect,
    card.passiveEffects,
    card.augment,
    card.essence,
    card.invoke,
    ...EQUIPMENT_TRIGGER_FIELDS.map((field) => card[field.key]),
  ];
  return AILMENT_FILTER_TYPES.filter((ailment) =>
    effectTreeMentionsAilment(sources, ailment),
  );
}

function cardMatchesTypeFilter(card, type) {
  if (cardHasType(card, type)) return true;
  if (AILMENT_FILTER_TYPES.includes(type)) {
    return cardAilmentTypes(card).includes(type);
  }
  return false;
}

function collectCardTypeTokens(cards) {
  const types = new Set();
  (cards || []).forEach((card) => {
    (card?.type || []).forEach((type) => {
      if (type) types.add(type);
    });
    if (card?.token) types.add("token");
    cardAilmentTypes(card).forEach((ailment) => types.add(ailment));
  });
  return types;
}

function sortCardTypeFilterTokens(types) {
  return [...types].sort((a, b) => {
    const leftIndex = CARD_TYPE_FILTER_PRIORITY_INDEX[a];
    const rightIndex = CARD_TYPE_FILTER_PRIORITY_INDEX[b];
    if (leftIndex != null && rightIndex != null) return leftIndex - rightIndex;
    if (leftIndex != null) return -1;
    if (rightIndex != null) return 1;
    return formatTypeFilterLabel(a).localeCompare(formatTypeFilterLabel(b));
  });
}

function buildCardTypeFilterOptions(cards, { includeCategories = false } = {}) {
  const tokens = collectCardTypeTokens(cards);
  if (!includeCategories) {
    tokens.delete("equipment");
    tokens.delete("ability");
    tokens.delete("aspect");
  }
  EQUIPMENT_ITEM_TYPES.forEach((type) => tokens.delete(type));
  return sortCardTypeFilterTokens(tokens);
}

function buildEquipmentItemTypeFilterOptions(cards) {
  const tokens = collectCardTypeTokens(cards);
  return sortCardTypeFilterTokens(
    [...tokens].filter((type) => EQUIPMENT_ITEM_TYPES.has(type)),
  );
}

function cardMatchesEquipmentTierFilter(card, tier) {
  if (tier === "all") return true;
  return (
    cardCatalogCategory(card) === "equipment" &&
    getEquipmentTier(card) === Number(tier)
  );
}

function formatTypeFilterLabel(type) {
  if (type === "plain") return "No attack/magic";
  if (CARD_TYPE_DISPLAY_NAMES[type]) {
    return CARD_TYPE_DISPLAY_NAMES[type].replace(/\b\w/g, (char) => char.toUpperCase());
  }
  return formatAttributeLabel(type);
}

function renderEquipmentPickerFilters({
  itemTypeOptions,
  itemType,
  onItemTypeChange,
  tier,
  onTierChange,
}) {
  return (
    <>
      {itemTypeOptions.length ? (
        <select
          className={styles.select}
          value={itemType}
          onChange={(event) => onItemTypeChange(event.target.value)}
        >
          <option value="all">{t("deck.filters.allItemTypes")}</option>
          {renderCardTypeFilterOptions(itemTypeOptions)}
        </select>
      ) : null}
      <select
        className={styles.select}
        value={tier}
        onChange={(event) => onTierChange(event.target.value)}
      >
        <option value="all">{t("deck.filters.allTiers")}</option>
        {EQUIPMENT_TIER_FILTER_OPTIONS.map((option) => (
          <option key={option} value={option}>
            {t("match.panel.tierN", { tier: option })}
          </option>
        ))}
      </select>
    </>
  );
}

function renderCardTypeFilterOptions(typeTokens, { includePlain = false } = {}) {
  const options = (typeTokens || []).map((type) => (
    <option key={type} value={type}>
      {formatTypeFilterLabel(type)}
    </option>
  ));
  if (includePlain) {
    options.push(
      <option key="plain" value="plain">
        {formatTypeFilterLabel("plain")}
      </option>,
    );
  }
  return options;
}

function cardSortTypeLabel(card) {
  const types = formatCardTypes(card);
  if (types) return types;
  const category = cardCatalogCategory(card);
  return category ? formatTypeFilterLabel(category) : "";
}

// Tier 2+ equipment must upgrade an equipped piece one tier lower that shares
// an item type (mirrors canUpgradeEquipment in cardConstants.js).
function canUpgradeEquipmentClient(incoming, existing) {
  return canUpgradeEquipmentCard(incoming, existing);
}

function describeUpgradeRequirementClient(card) {
  const tier = getEquipmentTier(card);
  const types = (card.type || [])
    .filter((type) => EQUIPMENT_ITEM_TYPES.has(type))
    .map(formatTypeFilterLabel)
    .join(" or ");
  return `Tier ${tier}: play onto an equipped Tier ${tier - 1} ${
    types || "piece"
  } to upgrade it.`;
}

function getAvailableEquipmentEquipSlots(board, card, catalogMap = null) {
  if (!board || !card) return [];
  const boardSlot = resolveCardBoardSlot(card);
  if (!boardSlot) return [];
  const equipped = board[boardSlot];
  if (getEquipmentTier(card) >= 2) {
    const base = equipped
      ? catalogMap?.[equipped.cardId] || equipped
      : null;
    return canUpgradeEquipmentClient(card, base) ? [boardSlot] : [];
  }
  if (equipped) return [];
  return [boardSlot];
}

function slotsAreAdjacentLinked(slots, indexA, indexB) {
  if (Math.abs(indexA - indexB) !== 1) return false;
  const left = slots[Math.min(indexA, indexB)];
  const right = slots[Math.max(indexA, indexB)];
  if (!left?.linked || !right?.linked) return false;
  if (left.linkGroup != null || right.linkGroup != null) {
    return (
      left.linkGroup != null &&
      right.linkGroup != null &&
      left.linkGroup === right.linkGroup
    );
  }
  return true;
}

function groupLinkedAbilitySlots(slots) {
  const list = slots || [];
  const groups = [];
  let current = [];
  list.forEach((slot, index) => {
    if (!current.length) {
      current.push({ slot, index });
      return;
    }
    const prevIndex = current[current.length - 1].index;
    if (slotsAreAdjacentLinked(list, prevIndex, index)) {
      current.push({ slot, index });
      return;
    }
    groups.push(current);
    current = [{ slot, index }];
  });
  if (current.length) groups.push(current);
  return groups;
}

function convertStacksDealsDamage(effect) {
  if (!effect || effect.id !== "convertStacks") return false;
  return effect.convert === "damage" || effect.convert === "damageOpponent";
}

function convertStacksHeals(effect) {
  if (!effect || effect.id !== "convertStacks") return false;
  return effect.convert === "heal" || effect.convert === "healSelf";
}

function isDamagingEffect(effect) {
  return listCombatEffectEntries(effect).some(
    (entry) =>
      entry &&
      (entry.id === "damageOpponent" ||
        entry.id === "bleed" ||
        entry.id === "poison" ||
        convertStacksDealsDamage(entry) ||
        entry.id === "convertPhysicalArmorToDamage" ||
        entry.id === "convertMagicArmorToDamage"),
  );
}

function primaryDamagingEffect(effect) {
  return (
    listCombatEffectEntries(effect).find(
      (entry) =>
        entry &&
        (entry.id === "damageOpponent" ||
          entry.id === "bleed" ||
          entry.id === "poison" ||
          convertStacksDealsDamage(entry) ||
          entry.id === "convertPhysicalArmorToDamage" ||
          entry.id === "convertMagicArmorToDamage"),
    ) || null
  );
}

function abilityDealsDamageType(ability, damageType) {
  const effect = primaryDamagingEffect(ability?.effect);
  if (!effect) return false;
  return (effect.damageType || "physical") === damageType;
}

function abilityAppliesStackAdd(ability, stack) {
  if (!stack) return false;
  return listEffectEntries(ability?.effect).some((entry) => {
    if (!entry) return false;
    if (stack === "poison" && entry.id === "poison") return true;
    if (stack === "bleed" && entry.id === "bleed") return true;
    if (
      (stack === "burn" || stack === "chill" || stack === "shock") &&
      (entry.id === stack ||
        (entry.id === "applyAilment" &&
          (entry.ailment || entry.type) === stack) ||
        (entry[stack] || 0) > 0)
    ) {
      return true;
    }
    if (entry.id === "convertStacks" && entry.stack === stack) return true;
    return false;
  });
}

function augmentTypeRules(matchRule) {
  const typeRules = [];
  if (!matchRule) return typeRules;
  if (Array.isArray(matchRule.type)) typeRules.push(...matchRule.type);
  else if (matchRule.type) typeRules.push(matchRule.type);
  if (Array.isArray(matchRule.types)) typeRules.push(...matchRule.types);
  else if (matchRule.types) typeRules.push(matchRule.types);
  return typeRules;
}

function augmentModifyApplies(modify, activeAbility) {
  if (!modify || !activeAbility) return false;
  if (
    modify.physicalDamageAdd &&
    abilityDealsDamageType(activeAbility, "physical")
  ) {
    return true;
  }
  if (
    modify.magicalDamageAdd &&
    abilityDealsDamageType(activeAbility, "magical")
  ) {
    return true;
  }
  if (modify.damageAdd && isDamagingEffect(activeAbility.effect)) {
    return true;
  }
  if (
    (modify.fireDamageAdd ||
      modify.coldDamageAdd ||
      modify.lightningDamageAdd) &&
    isDamagingEffect(activeAbility.effect)
  ) {
    return true;
  }
  if (
    ["burn", "chill", "shock"].some(
      (stack) =>
        modify[`${stack}Add`] &&
        (isDamagingEffect(activeAbility.effect) ||
          abilityAppliesStackAdd(activeAbility, stack)),
    )
  ) {
    return true;
  }
  if (modify.bleedAdd && abilityAppliesStackAdd(activeAbility, "bleed")) {
    return true;
  }
  if (modify.poisonAdd && abilityAppliesStackAdd(activeAbility, "poison")) {
    return true;
  }
  if (modify.healAdd && activeAbility.effect?.id === "healSelf") {
    return true;
  }
  if (
    modify.healAdd &&
    activeAbility.effect?.id === "summonMinion" &&
    listEffectEntries(activeAbility.effect.action).some(
      (entry) => entry?.id === "healSelf",
    )
  ) {
    return true;
  }
  if (
    modify.minionHealthAdd &&
    activeAbility.effect?.id === "summonMinion"
  ) {
    return true;
  }
  if (
    modify.physicalArmorAdd &&
    activeAbility.effect?.id === "temporaryPhysicalArmor"
  ) {
    return true;
  }
  if (
    modify.magicalArmorAdd &&
    activeAbility.effect?.id === "temporaryMagicArmor"
  ) {
    return true;
  }
  if (modify.ignoreArmor && isDamagingEffect(activeAbility.effect)) {
    return true;
  }
  if (modify.echo) {
    return true;
  }
  const costMult = Number(modify.costMult);
  if (Number.isFinite(costMult) && costMult > 0 && costMult !== 1) {
    return true;
  }
  const outputMult = Number(modify.outputMult);
  if (Number.isFinite(outputMult) && outputMult > 0 && outputMult !== 1) {
    return true;
  }
  const costToHealth = Number(modify.costToHealth);
  if (Number.isFinite(costToHealth) && costToHealth > 0) {
    return true;
  }
  const levelAdd = Number(modify.levelAdd);
  if (Number.isFinite(levelAdd) && levelAdd !== 0) {
    return true;
  }
  if (
    activeAbility.effect?.id === "activateAura" ||
    activeAbility.effect?.id === "summonMinion"
  ) {
    if (modify.costAdd !== undefined) return true;
    if (Number(modify.outputAdd) > 0) return true;
  }
  return false;
}

const AUGMENT_GRANTED_ELEMENT_TYPES = [
  ["fireDamageAdd", "fire"],
  ["coldDamageAdd", "cold"],
  ["lightningDamageAdd", "lightning"],
];

function augmentGrantedTypes(augment) {
  const modify = resolveAugmentModifyForLevel(augment, 1);
  return AUGMENT_GRANTED_ELEMENT_TYPES.filter(
    ([key]) => Number(modify[key]) > 0,
  ).map(([, type]) => type);
}

function augmentLinkedGrantedTypes(augment, activeAbility, linkedAugments) {
  const types = new Set();
  (linkedAugments || []).forEach((other) => {
    if (!other || other === augment) return;
    const granted = augmentGrantedTypes(other);
    if (!granted.length || !augmentMatches(other, activeAbility)) return;
    granted.forEach((type) => types.add(type));
  });
  return types;
}

function listLinkedAugmentsForSlot(slots, slotIndex) {
  const list = slots || [];
  const entries =
    slotIndex == null || slotIndex < 0
      ? list.map((slot, index) => ({ slot, index }))
      : groupLinkedAbilitySlots(list).find((group) =>
          group.some((entry) => entry.index === slotIndex),
        ) || [];
  return entries
    .map(({ slot }) => slot?.ability)
    .filter((ability) => ability?.kind === "augment" && ability.augment)
    .map((ability) => ability.augment);
}

function augmentMatches(augment, activeAbility, linkedAugments = null) {
  if (!augment || !augment.match || !activeAbility) return false;
  const matchRule = augment.match;
  if (matchRule.kind && matchRule.kind !== activeAbility.kind) return false;
  if (matchRule.damaging && !isDamagingEffect(activeAbility.effect)) {
    return false;
  }
  const typesAny = Array.isArray(matchRule.typesAny)
    ? matchRule.typesAny.filter(Boolean)
    : [];
  if (
    typesAny.length > 0 &&
    !typesAny.some((type) => cardHasType(activeAbility, type))
  ) {
    return false;
  }
  const missingTypes = augmentTypeRules(matchRule).filter(
    (type) => !cardHasType(activeAbility, type),
  );
  if (missingTypes.length > 0) {
    const grantedTypes = augmentLinkedGrantedTypes(
      augment,
      activeAbility,
      linkedAugments,
    );
    if (!missingTypes.every((type) => grantedTypes.has(type))) return false;
  }
  if (matchRule.attribute && matchRule.attribute !== activeAbility.attribute) {
    return false;
  }
  if (
    !augmentModifyApplies(
      resolveAugmentModifyForLevel(augment, 1),
      activeAbility,
    )
  ) {
    return false;
  }
  return true;
}

function equipmentHasResourceExpend(equipment) {
  if (!equipment?.resources) return false;
  return Object.values(equipment.resources).some(
    (amount) => Number(amount) > 0,
  );
}

function equipmentCannotExpend(equipment) {
  return (
    Boolean(equipment?.expended) ||
    Boolean(equipment?.expendDisabled) ||
    (Boolean(equipment?.justEquipped) &&
      !(equipment?.passiveEffects || []).some(
        (effect) => effect?.id === "fastHands" || effect?.id === "swiftFeet",
      ))
  );
}

function equipmentCannotExpendForResources(equipment) {
  return (
    equipmentCannotExpend(equipment) || Boolean(equipment?.resourceExpendBlocked)
  );
}

function equipmentCannotExpendNonResource(equipment) {
  return equipmentCannotExpend(equipment) || Boolean(equipment?.broken);
}

function equipmentBuffActionDisabled(equipment) {
  return Boolean(equipment?.expendDisabled) || Boolean(equipment?.broken);
}

function equipmentEffectActionBlocked(equipment, action = null) {
  if (equipmentCannotExpend(equipment)) return true;
  if (!equipment?.broken) return false;
  if (
    action &&
    (action.resourceGrants?.length > 0 || action.persistGrants?.length > 0)
  ) {
    return false;
  }
  return true;
}

function equipmentExpendBlockMessage(equipment, targetingBlocksActions = false) {
  if (targetingBlocksActions) return "Resolve pending choice first.";
  if (equipment?.expended) {
    return "Already expended this equipment this turn.";
  }
  if (
    equipment?.justEquipped &&
    !(equipment?.passiveEffects || []).some(
      (effect) => effect?.id === "fastHands" || effect?.id === "swiftFeet",
    )
  ) {
    return "Cannot expend equipment the first turn it is played.";
  }
  if (equipment?.broken) {
    return "That equipment is broken and cannot be expended.";
  }
  if (equipment?.expendDisabled) return "That equipment cannot be expended.";
  if (equipment?.resourceExpendBlocked) return equipment.resourceExpendBlocked;
  return "Cannot use that action.";
}

function effectEntryDealsMagicalDamage(entry) {
  if (!entry || typeof entry !== "object") return false;
  if (
    entry.id === "damageOpponent" ||
    entry.id === "attackFromIdentity" ||
    entry.id === "convertPhysicalArmorToDamage" ||
    entry.id === "convertMagicArmorToDamage"
  ) {
    return (entry.damageType || "physical") === "magical";
  }
  return false;
}

function effectDealsMagicalDamageClient(effect) {
  if (!effect) return false;
  if (Array.isArray(effect.choose)) {
    return effect.choose.some((entry) => {
      if (Array.isArray(entry)) {
        return entry.some((nested) => effectEntryDealsMagicalDamage(nested));
      }
      return effectEntryDealsMagicalDamage(entry);
    });
  }
  if (Array.isArray(effect)) {
    return effect.some((entry) => effectEntryDealsMagicalDamage(entry));
  }
  return effectEntryDealsMagicalDamage(effect);
}

function cardHasArcanistMagicalInvokeEffectClient(card) {
  if (!card) return false;
  if (cardHasType(card, "ability") && effectDealsMagicalDamageClient(card.effect)) {
    return true;
  }
  if (
    cardHasType(card, "equipment") &&
    effectDealsMagicalDamageClient(card.activatedEffect)
  ) {
    return true;
  }
  return effectDealsMagicalDamageClient(card.invoke);
}

function seatHasArcanistMagicalInvoke(seat) {
  return (seat?.traits || []).some(
    (trait) => trait && trait.effect === "arcanistMagicalInvoke",
  );
}

function cardHasBrawlerMeleeInvokeEffectClient(card) {
  return Boolean(card && cardHasType(card, "melee"));
}

function invokeEffectIsPhysicalDamageAmount(effect, amount) {
  if (!effect || effect.id !== "damageOpponent") return false;
  const resolved = Math.max(0, Math.floor(Number(effect.amount) || 0));
  if (resolved !== amount) return false;
  const damageType = effect.damageType || "physical";
  return damageType === "physical";
}

function cardNativeInvokeHasPhysicalDamageAmount(card, amount) {
  if (!card?.invoke) return false;
  const effects = Array.isArray(card.invoke?.choose)
    ? card.invoke.choose
    : [card.invoke];
  return effects.some((effect) =>
    invokeEffectIsPhysicalDamageAmount(effect, amount),
  );
}

function seatHasBrawlerMeleeInvoke(seat) {
  return (seat?.traits || []).some(
    (trait) => trait && trait.effect === "brawlerMeleeInvoke",
  );
}

function listInvokeEffectActions(card) {
  if (!card?.invoke) return [];
  return Array.isArray(card.invoke?.choose)
    ? card.invoke.choose.map((effect, index) => ({
        effect,
        effectIndex: index,
      }))
    : [{ effect: card.invoke, effectIndex: null }];
}

// Invoke summaries (hand badge, Invoke buttons) are plain labels: their icons
// do not open keyword tips.
function renderInvokeEffectSummary(effect, card, seat, iconClassName) {
  return (
    <KeywordTipContext.Provider value={null}>
      {renderInvokeEffectSummaryContent(effect, card, seat, iconClassName)}
    </KeywordTipContext.Provider>
  );
}

function renderInvokeEffectSummaryContent(effect, card, seat, iconClassName) {
  const grantEntries = resourceGrantEntriesFromEffect(effect);
  const persistEntries =
    effect?.id === "persist" ? persistGrantEntries(effect) : [];
  const iconEntries =
    grantEntries.length > 0
      ? grantEntries
      : persistEntries.length > 0
        ? persistEntries
        : null;
  if (iconEntries) {
    return (
      <AttributeIconList
        entries={iconEntries}
        persist={persistEntries.length > 0}
        className={iconClassName}
      />
    );
  }
  const brawlerBuffsOnePhysical =
    seatHasBrawlerMeleeInvoke(seat) &&
    cardHasBrawlerMeleeInvokeEffectClient(card) &&
    invokeEffectIsPhysicalDamageAmount(effect, 1);
  const displayEffect = brawlerBuffsOnePhysical
    ? { ...effect, amount: 2 }
    : effect;
  const effectLabel =
    formatActivatedEffectText(displayEffect, { omitCost: true }) || "";
  const shortLabel = String(effectLabel).replace(/^Deal\s+/i, "");
  return (
    <span>
      {renderTextWithStatusEffectIcons(shortLabel, {
        outputDelta: brawlerBuffsOnePhysical ? 1 : 0,
      })}
    </span>
  );
}

function seatHasIroncladPhysicalInvoke(seat) {
  return (seat?.traits || []).some(
    (trait) => trait && trait.effect === "ironcladPhysicalInvoke",
  );
}

function cardHasIroncladPhysicalInvokeEffectClient(card) {
  return Boolean(card && cardHasType(card, "physical"));
}

function handInvokeBlockMessage(seat, { isWorkshopMatch = false } = {}) {
  if (!isWorkshopMatch && seat?.invokedHand) {
    return "Already invoked a card this turn.";
  }
  return null;
}

function handEquipBlockMessage(
  seat,
  handCard,
  card,
  catalogMap,
  {
    isMyTurn = false,
    isWorkshopMatch = false,
    targetingBlocksActions = false,
  } = {},
) {
  if (targetingBlocksActions) return "Resolve pending choice first.";
  if (!isMyTurn && !isWorkshopMatch) return "Not your turn.";
  if (getAvailableEquipmentEquipSlots(seat?.board, card, catalogMap).length === 0) {
    return getEquipmentTier(card) >= 2
      ? describeUpgradeRequirementClient(card)
      : "That equipment slot is already filled.";
  }
  if (
    !isWorkshopMatch &&
    seat?.playedEquipment &&
    !isNinjutsuFreeThrowableTokenEquip(seat, catalogMap, handCard, card)
  ) {
    return "Already played equipment this turn.";
  }
  return null;
}

function handSlotBlockMessage(
  seat,
  card,
  {
    isMyTurn = false,
    isWorkshopMatch = false,
    targetingBlocksActions = false,
    boardHasEmptyHostAbilitySlot = false,
    boardHasEmptyAugmentAbilitySlot = false,
    boardHasEmptyAbilitySlot = false,
  } = {},
) {
  if (targetingBlocksActions) return "Resolve pending choice first.";
  if (!isMyTurn && !isWorkshopMatch) return "Not your turn.";
  const kind = card?.kind;
  if (kind === "active" || kind === "essence") {
    if (!boardHasEmptyHostAbilitySlot && !boardHasEmptyAugmentAbilitySlot) {
      return "No empty ability slots.";
    }
  } else if (kind === "augment") {
    if (!boardHasEmptyAugmentAbilitySlot) return "No empty ability slots.";
  } else if (!boardHasEmptyAbilitySlot) {
    return "No empty ability slots.";
  }
  if (
    !isWorkshopMatch &&
    seat?.playedAbility &&
    !(seat.nimbleFingersCharges > 0)
  ) {
    return "Already played ability this turn.";
  }
  return null;
}

function emptySlotHasLinkedMatch(slots, slotIndex, selectedAbility) {
  if (!selectedAbility) return false;
  const group = groupLinkedAbilitySlots(slots).find((entries) =>
    entries.some((entry) => entry.index === slotIndex),
  );
  if (!group || group.length < 2) return false;
  const kind = selectedAbility.kind;
  const linkedAugments = listLinkedAugmentsForSlot(slots, slotIndex);
  return group.some(({ slot, index }) => {
    if (index === slotIndex) return false;
    const ability = slot.ability;
    if (!ability) return false;
    if (kind === "augment") {
      return (
        ability.kind === "active" &&
        augmentMatches(selectedAbility.augment, ability, linkedAugments)
      );
    }
    if (kind === "active") {
      return (
        ability.kind === "augment" &&
        augmentMatches(ability.augment, selectedAbility, linkedAugments)
      );
    }
    return false;
  });
}

function getDistantConnectionTargetSlots(equipment) {
  return (equipment?.passiveEffects || []).reduce((slots, effect) => {
    if (effect?.id !== "distantConnection") return slots;
    if (effect.weaponType) return slots;
    const listed = Array.isArray(effect.targetSlots)
      ? effect.targetSlots
      : effect.targetSlot
        ? [effect.targetSlot]
        : [];
    listed.forEach((slot) => {
      if (slot && !slots.includes(slot)) slots.push(slot);
    });
    return slots;
  }, []);
}

function emptySlotHasDistantMatch(equipment, selectedAbility, seat, boardSlotKey = null) {
  if (!selectedAbility || !equipment || !seat) return false;
  if (selectedAbility.kind === "augment") {
    const targetSlots = getDistantConnectionTargetSlots(equipment);
    if (!targetSlots.length) return false;
    return targetSlots.some((slotKey) => {
      const target = seat.board?.[slotKey];
      const columnSlots = getColumnAbilitySlots(seat, slotKey);
      if (
        target?.inherentAbility?.kind === "active" &&
        augmentMatches(
          selectedAbility.augment,
          target.inherentAbility,
          listLinkedAugmentsForSlot(columnSlots, -1),
        )
      ) {
        return true;
      }
      return columnSlots.some(
        (slot, index) =>
          slot?.ability?.kind === "active" &&
          augmentMatches(
            selectedAbility.augment,
            slot.ability,
            listLinkedAugmentsForSlot(columnSlots, index),
          ),
      );
    });
  }
  if (selectedAbility.kind === "active") {
    const boardSlot =
      boardSlotKey ||
      equipment?.boardSlot ||
      Object.entries(seat.board || {}).find(
        ([, item]) => item && item.instanceId === equipment.instanceId,
      )?.[0];
    if (!boardSlot) return false;
    return Object.entries(seat.board || {}).some(([sourceKey, source]) => {
      if (!source || source.instanceId === equipment.instanceId) return false;
      if (!getDistantConnectionTargetSlots(source).includes(boardSlot)) {
        return false;
      }
      const sourceSlots = getColumnAbilitySlots(seat, sourceKey);
      const linkedAugments = listLinkedAugmentsForSlot(sourceSlots, -1);
      return sourceSlots.some(
        (slot) =>
          slot?.ability?.kind === "augment" &&
          augmentMatches(slot.ability.augment, selectedAbility, linkedAugments),
      );
    });
  }
  return false;
}

function emptySlotHasInherentMatch(equipment, selectedAbility, slots = null) {
  if (!selectedAbility || selectedAbility.kind !== "augment") return false;
  const inherent = equipment?.inherentAbility;
  if (!inherent || inherent.kind !== "active") return false;
  return augmentMatches(
    selectedAbility.augment,
    inherent,
    listLinkedAugmentsForSlot(slots || equipment?.abilitySlots, -1),
  );
}

function emptySlotHasAugmentMatch(
  slots,
  slotIndex,
  selectedAbility,
  equipment,
  seat,
  boardSlotKey = null,
) {
  return (
    emptySlotHasLinkedMatch(slots, slotIndex, selectedAbility) ||
    emptySlotHasDistantMatch(equipment, selectedAbility, seat, boardSlotKey) ||
    emptySlotHasInherentMatch(equipment, selectedAbility, slots)
  );
}

function abilityLooksUsed(ability) {
  if (!ability) return false;
  const minionActive =
    Boolean(ability.minion) && (ability.minion.hp || 0) > 0;
  const auraActive = Boolean(ability.aura);
  return Boolean(ability.activated) && !minionActive && !auraActive;
}

function listMatchingLinkedActivesForAugment(
  slots,
  slotIndex,
  augmentAbility,
  equipment,
  seat,
  boardSlotKey = null,
) {
  if (!augmentAbility || augmentAbility.kind !== "augment") return [];
  const actives = [];
  const seen = new Set();
  const pushActive = (active, linkedAugments = null) => {
    if (!active?.instanceId || seen.has(active.instanceId)) return;
    if (!augmentMatches(augmentAbility.augment, active, linkedAugments)) return;
    seen.add(active.instanceId);
    actives.push(active);
  };
  const group = groupLinkedAbilitySlots(slots).find((entries) =>
    entries.some((entry) => entry.index === slotIndex),
  );
  if (group) {
    const linkedAugments = listLinkedAugmentsForSlot(slots, slotIndex);
    group.forEach(({ slot, index }) => {
      if (index === slotIndex) return;
      if (slot?.ability?.kind === "active") {
        pushActive(slot.ability, linkedAugments);
      }
    });
  }
  if (equipment?.inherentAbility?.kind === "active") {
    pushActive(
      equipment.inherentAbility,
      listLinkedAugmentsForSlot(slots, -1),
    );
  }
  const distantTargets = getDistantConnectionTargetSlots(equipment);
  if (seat && distantTargets.length) {
    distantTargets.forEach((slotKey) => {
      const target = seat.board?.[slotKey];
      const columnSlots = getColumnAbilitySlots(seat, slotKey);
      if (target?.inherentAbility?.kind === "active") {
        pushActive(
          target.inherentAbility,
          listLinkedAugmentsForSlot(columnSlots, -1),
        );
      }
      columnSlots.forEach((slot, index) => {
        if (slot?.ability?.kind === "active") {
          pushActive(
            slot.ability,
            listLinkedAugmentsForSlot(columnSlots, index),
          );
        }
      });
    });
  }
  if (boardSlotKey) {
    const columnSlots = getColumnAbilitySlots(seat, boardSlotKey);
    columnSlots.forEach((slot, index) => {
      if (index === slotIndex) return;
      if (slot?.ability?.kind === "active") {
        pushActive(slot.ability, listLinkedAugmentsForSlot(columnSlots, index));
      }
    });
  }
  return actives;
}

function augmentLooksUsed(slots, slotIndex, ability, equipment, seat) {
  if (ability?.kind !== "augment") return false;
  return listMatchingLinkedActivesForAugment(
    slots,
    slotIndex,
    ability,
    equipment,
    seat,
  ).some(abilityLooksUsed);
}

function slottedAbilityLooksUsed(slots, slotIndex, ability, equipment, seat) {
  if (ability?.kind === "augment") {
    return augmentLooksUsed(slots, slotIndex, ability, equipment, seat);
  }
  return abilityLooksUsed(ability);
}

function getSelectedHandAbility(selectedHand, seat, catalogMap) {
  if (selectedHand?.deck !== "ability") return null;
  const handCard = (seat?.hand || []).find(
    (card) => card.instanceId === selectedHand.instanceId,
  );
  if (!handCard) return null;
  const definition = catalogMap?.[handCard.cardId] || {};
  if (
    definition &&
    Object.keys(definition).length &&
    !cardHasType(definition, "ability")
  ) {
    return null;
  }
  return {
    ...definition,
    ...handCard,
    type: handCard.type || definition.type,
    kind: handCard.kind || definition.kind,
    attribute: handCard.attribute || definition.attribute,
    effect: handCard.effect || definition.effect,
    augment: handCard.augment || definition.augment || null,
  };
}

function getMovingBoardAbility(movingAbility, seat) {
  if (!movingAbility?.abilityInstanceId) return null;
  if (movingAbility.boardSlot) {
    for (const slot of getColumnAbilitySlots(seat, movingAbility.boardSlot)) {
      if (slot?.ability?.instanceId === movingAbility.abilityInstanceId) {
        return slot.ability;
      }
    }
  }
  if (movingAbility.equipmentInstanceId) {
    for (const equipment of Object.values(seat?.board || {})) {
      if (!equipment || equipment.instanceId !== movingAbility.equipmentInstanceId) {
        continue;
      }
      for (const slot of equipment.abilitySlots || []) {
        if (slot?.ability?.instanceId === movingAbility.abilityInstanceId) {
          return slot.ability;
        }
      }
      if (
        equipment.inherentAbility?.instanceId ===
        movingAbility.abilityInstanceId
      ) {
        return equipment.inherentAbility;
      }
    }
  }
  for (const boardSlot of listAbilityBoardSlotKeys(seat)) {
    for (const slot of getColumnAbilitySlots(seat, boardSlot)) {
      if (slot?.ability?.instanceId === movingAbility.abilityInstanceId) {
        return slot.ability;
      }
    }
  }
  return null;
}

function findSlottedAbilityOnSeat(seat, abilityInstanceId, boardSlotHint = null) {
  if (!seat || !abilityInstanceId) return null;
  const keys = boardSlotHint
    ? [boardSlotHint]
    : listAbilityBoardSlotKeys(seat);
  for (const boardSlot of keys) {
    const rack = getSeatAbilityRack(seat, boardSlot);
    const slots = rack?.abilitySlots || [];
    for (let slotIndex = 0; slotIndex < slots.length; slotIndex += 1) {
      const abilitySlot = slots[slotIndex];
      const ability = abilitySlot?.ability;
      if (ability?.instanceId === abilityInstanceId) {
        return {
          boardSlot,
          rack,
          slots,
          slotIndex,
          abilitySlot,
          ability,
          equipment: seat.board?.[boardSlot] || null,
        };
      }
    }
    const equipment = seat.board?.[boardSlot];
    if (equipment?.inherentAbility?.instanceId === abilityInstanceId) {
      return {
        boardSlot,
        rack,
        slots,
        slotIndex: -1,
        abilitySlot: null,
        ability: equipment.inherentAbility,
        equipment,
      };
    }
  }
  return null;
}

function getEquipmentAbilitySlotsForDisplay(card, catalogById = null) {
  if (!card) return [];
  let slots = [];
  if (Array.isArray(card.abilitySlots)) {
    slots = card.abilitySlots.map((slot) => ({ ...slot }));
  } else {
    const count = Math.max(0, Number(card.abilitySlots) || 0);
    slots = Array.from({ length: count }, (_, index) => ({
      id: `s${index}`,
      linked: false,
      ability: null,
    }));
  }
  const inherentSource =
    card.inherentAbility ||
    (card.inherentAbilityId && catalogById
      ? catalogById[card.inherentAbilityId]
      : null);
  if (!inherentSource) return slots;
  const inherentAbility =
    inherentSource.instanceId != null
      ? inherentSource
      : {
          ...inherentSource,
          instanceId: `inherent:${card.id || card.cardId || card.instanceId || "preview"}`,
          cardId:
            inherentSource.id ||
            inherentSource.cardId ||
            card.inherentAbilityId,
          inherent: true,
        };
  return [
    {
      id: "inherent",
      linked: false,
      inherent: true,
      ability: inherentAbility,
    },
    ...slots,
  ];
}

function getAbilitySlotGridPosition(index, total) {
  if (total <= 0) return { row: 0, col: 0 };
  if (total <= 2) return { row: 0, col: index };
  if (total === 3) {
    return [
      { row: 0, col: 0 },
      { row: 0, col: 1 },
      { row: 1, col: 1 },
    ][index];
  }
  if (total === 4) {
    return [
      { row: 0, col: 0 },
      { row: 0, col: 1 },
      { row: 1, col: 1 },
      { row: 1, col: 0 },
    ][index];
  }
  return { row: Math.floor(index / 2), col: index % 2 };
}

function getAbilitySlotLayout(total) {
  const count = Math.max(0, total || 0);
  const positions = [];
  for (let index = 0; index < count; index += 1) {
    positions.push(getAbilitySlotGridPosition(index, count));
  }
  const rowCount = positions.reduce(
    (max, position) => Math.max(max, position.row + 1),
    count > 0 ? 1 : 0,
  );
  const colCount = count <= 1 ? 1 : 2;
  return { positions, rowCount, colCount };
}

function scheduleResizeFit(fit) {
  let frame = 0;
  const run = () => {
    frame = 0;
    fit();
  };
  const schedule = () => {
    if (frame) return;
    frame = requestAnimationFrame(run);
  };
  const cancel = () => {
    if (!frame) return;
    cancelAnimationFrame(frame);
    frame = 0;
  };
  return { schedule, cancel };
}

function attachVisibleResizeFit(target, fit, { alsoObserve = null } = {}) {
  if (!target || typeof fit !== "function") return () => {};
  let lastW = -1;
  let lastH = -1;
  let fontsToken = 0;
  const isRoughlyVisible = () => {
    const rect = target.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return false;
    return (
      rect.bottom > -80 &&
      rect.top < window.innerHeight + 80 &&
      rect.right > -80 &&
      rect.left < window.innerWidth + 80
    );
  };
  let visible = isRoughlyVisible();
  const { schedule, cancel } = scheduleResizeFit(() => {
    if (!visible) return;
    lastW = target.clientWidth;
    lastH = target.clientHeight;
    fit();
  });
  const forceFit = () => {
    lastW = -1;
    lastH = -1;
    schedule();
  };
  const onResize = () => {
    if (!visible) return;
    const width = target.clientWidth;
    const height = target.clientHeight;
    if (
      lastW >= 0 &&
      Math.abs(width - lastW) < 0.5 &&
      Math.abs(height - lastH) < 0.5
    ) {
      return;
    }
    schedule();
  };
  let resizeObserver = null;
  let intersectionObserver = null;
  if (typeof ResizeObserver === "function") {
    resizeObserver = new ResizeObserver(onResize);
    resizeObserver.observe(target);
    if (alsoObserve && alsoObserve !== target) {
      resizeObserver.observe(alsoObserve);
    }
  }
  if (typeof IntersectionObserver === "function") {
    intersectionObserver = new IntersectionObserver(
      (entries) => {
        const nextVisible = entries.some((entry) => entry.isIntersecting);
        if (nextVisible === visible) return;
        visible = nextVisible;
        if (visible) forceFit();
      },
      { rootMargin: "80px 0px" },
    );
    intersectionObserver.observe(target);
  }
  if (visible) forceFit();
  if (document.fonts?.ready) {
    const token = ++fontsToken;
    document.fonts.ready.then(() => {
      if (token !== fontsToken) return;
      if (visible) forceFit();
    });
  }
  return () => {
    fontsToken += 1;
    cancel();
    resizeObserver?.disconnect();
    intersectionObserver?.disconnect();
  };
}

function FitEquipName({ children, className = "", trailing = null }) {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const row = el.parentElement;
    if (!row) return undefined;

    const fit = () => {
      const prev = el.style.fontSize;
      el.style.fontSize = "";
      const baseSize = parseFloat(getComputedStyle(el).fontSize) || 9;
      const minSize = 5;
      let low = minSize;
      let high = baseSize;
      let best = minSize;
      for (let i = 0; i < 12; i += 1) {
        const mid = (low + high) / 2;
        el.style.fontSize = `${mid}px`;
        if (el.scrollWidth <= el.clientWidth + 0.5) {
          best = mid;
          low = mid;
        } else {
          high = mid;
        }
      }
      const next = `${Math.max(minSize, best)}px`;
      if (prev === next) {
        el.style.fontSize = prev;
        return;
      }
      el.style.fontSize = next;
    };

    return attachVisibleResizeFit(row, fit);
  }, [children]);

  return (
    <div ref={ref} className={className}>
      {children}
      {trailing}
    </div>
  );
}

function AbilitySlotsVisual({ slots, variant = "card", renderSlot }) {
  const list = slots || [];
  const gridRef = useRef(null);
  const layout = useMemo(
    () => getAbilitySlotLayout(list.length),
    [list.length],
  );
  const isBoardVariant =
    variant !== "card" &&
    variant !== "handBoard" &&
    variant !== "hand" &&
    variant !== "actionBar";

  useLayoutEffect(() => {
    if (!isBoardVariant || list.length === 0) return undefined;
    const grid = gridRef.current;
    if (!grid) return undefined;
    const host = grid.closest(`.${CSS.escape(styles.equipBlock)}`);
    if (!host) return undefined;

    const fit = () => {
      let usedAbove = 0;
      for (const child of host.children) {
        if (child === grid) break;
        const childStyle = getComputedStyle(child);
        if (
          childStyle.position === "absolute" ||
          childStyle.position === "fixed"
        ) {
          continue;
        }
        usedAbove += child.getBoundingClientRect().height;
      }
      const availableW = Math.max(0, host.clientWidth - 2);
      const availableH = Math.max(0, host.clientHeight - usedAbove - 2);
      const pad = Math.max(
        1,
        Math.min(4, Math.round(Math.min(availableW, availableH) * 0.03)),
      );
      const sizeByW = availableW / Math.max(1, layout.colCount) - pad * 2;
      const sizeByH = availableH / Math.max(1, layout.rowCount) - pad * 2;
      const size = Math.max(
        8,
        Math.min(34, Math.floor(Math.min(sizeByW, sizeByH))),
      );
      const nextSize = `${size}px`;
      const nextPad = `${pad}px`;
      if (
        grid.style.getPropertyValue("--ai-slot-size") === nextSize &&
        grid.style.getPropertyValue("--ai-slot-pad") === nextPad
      ) {
        return;
      }
      grid.style.setProperty("--ai-slot-size", nextSize);
      grid.style.setProperty("--ai-slot-pad", nextPad);
    };

    return attachVisibleResizeFit(host, fit);
  }, [isBoardVariant, list.length, layout.colCount, layout.rowCount]);

  if (!list.length) return null;

  if (variant === "actionBar") {
    const groups = groupLinkedAbilitySlots(list);
    return (
      <div className={styles.abilitySlotActionBar}>
        {groups.map((group) => {
          const linked = group.length > 1;
          const hasHostAbility = group.some(
            ({ slot }) =>
              slot.ability && slot.ability.kind !== "augment",
          );
          const hostEntries = [];
          const augmentEntries = [];
          group.forEach((entry) => {
            if (isAbilityRackAugmentSlot(entry.slot)) {
              augmentEntries.push(entry);
              return;
            }
            if (isAbilityRackHostSlot(entry.slot)) {
              hostEntries.push(entry);
              return;
            }
            const kind = entry.slot.ability?.kind;
            if (kind === "augment") {
              augmentEntries.push(entry);
              return;
            }
            if (!entry.slot.ability && linked && hasHostAbility) {
              augmentEntries.push(entry);
              return;
            }
            hostEntries.push(entry);
          });
          return (
            <div
              key={group.map(({ slot }) => slot.id).join("-")}
              className={`${styles.abilitySlotActionBarGroup}${
                linked ? ` ${styles.abilitySlotActionBarGroupLinked}` : ""
              }`}
            >
              {hostEntries.map(({ slot, index }) => (
                <div
                  key={slot.id || `slot-${index}`}
                  className={styles.abilitySlotActionBarCell}
                >
                  {renderSlot(slot, index, { boardDisplay: "card" })}
                </div>
              ))}
              {augmentEntries.length > 0 ? (
                <div className={styles.abilitySlotActionBarAugments}>
                  {augmentEntries.map(({ slot, index }) => (
                    <div
                      key={slot.id || `slot-${index}`}
                      className={styles.abilitySlotActionBarAugmentCell}
                    >
                      {renderSlot(slot, index, { boardDisplay: "circle" })}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  }

  const links = [];
  for (let index = 0; index < list.length - 1; index += 1) {
    if (!slotsAreAdjacentLinked(list, index, index + 1)) continue;
    links.push({
      key: `${index}-${index + 1}`,
      from: layout.positions[index],
      to: layout.positions[index + 1],
    });
  }

  const cells = [];
  for (let row = 0; row < layout.rowCount; row += 1) {
    for (let col = 0; col < layout.colCount; col += 1) {
      const slotIndex = layout.positions.findIndex(
        (position) => position.row === row && position.col === col,
      );
      cells.push({ row, col, slotIndex });
    }
  }

  return (
    <div
      ref={gridRef}
      className={`${styles.abilitySlotGrid} ${
        variant === "card"
          ? styles.abilitySlotGridCard
          : variant === "handBoard"
            ? styles.abilitySlotGridHandBoard
            : variant === "hand"
              ? styles.abilitySlotGridHand
              : styles.abilitySlotGridBoard
      }`}
      style={{
        gridTemplateColumns: `repeat(${layout.colCount}, calc(var(--ai-slot-size) + var(--ai-slot-pad) * 2))`,
        "--ai-slot-cols": layout.colCount,
        "--ai-slot-rows": layout.rowCount,
      }}
    >
      {cells.map(({ row, col, slotIndex }) => {
        if (slotIndex < 0) {
          return (
            <div
              key={`empty-${row}-${col}`}
              className={styles.abilitySlotGridCellEmpty}
              style={{ gridRow: row + 1, gridColumn: col + 1 }}
            />
          );
        }
        const slot = list[slotIndex];
        return (
          <div
            key={slot.id || `slot-${slotIndex}`}
            className={styles.abilitySlotGridCell}
            style={{ gridRow: row + 1, gridColumn: col + 1 }}
          >
            {renderSlot(slot, slotIndex)}
          </div>
        );
      })}
      {links.length > 0 ? (
        <svg
          className={styles.abilitySlotGridLinks}
          viewBox={`0 0 ${layout.colCount} ${layout.rowCount}`}
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {links.map((link) => (
            <line
              key={link.key}
              x1={link.from.col + 0.5}
              y1={link.from.row + 0.5}
              x2={link.to.col + 0.5}
              y2={link.to.row + 0.5}
              className={styles.abilitySlotGridLinkLine}
            />
          ))}
        </svg>
      ) : null}
    </div>
  );
}

const BODY_LAYOUT = [
  { key: "weapon", label: "Weapon", className: styles.slotWeapon },
  { key: "armor", label: "Armor", className: styles.slotArmor },
  { key: "trinket", label: "Trinket", className: styles.slotTrinket },
];

const BOARD_SLOT_LABEL_KEYS = {
  weapon: "match.board.slotWeapon",
  armor: "match.board.slotArmor",
  trinket: "match.board.slotTrinket",
};

function boardSlotDisplayLabel(slotKey, fallback = "") {
  const key = BOARD_SLOT_LABEL_KEYS[slotKey];
  return key ? t(key) : fallback;
}

function boardSlotShortLabel(slotKey, equipment = null) {
  if (slotKey === "aspect") return t("match.board.aspect");
  const layout = BODY_LAYOUT.find((slot) => slot.key === slotKey);
  if (layout) return boardSlotDisplayLabel(layout.key, layout.label);
  return formatSlotLabel(equipment?.slot || slotKey) || "Eq";
}

function listSeatPeekPieces(seat, catalogMap = {}) {
  const gear = [];
  const abilities = [];
  if (!seat) return { gear, abilities };
  const aspectId =
    typeof seat.aspectId === "string" && seat.aspectId ? seat.aspectId : null;
  if (aspectId) {
    const card = catalogMap[aspectId] || {
      id: aspectId,
      name: aspectId,
      type: ["aspect"],
    };
    gear.push({
      kind: "aspect",
      key: `aspect:${aspectId}`,
      slotKey: "aspect",
      slotLabel: "Aspect",
      name: card.name || aspectId,
      cardId: aspectId,
      foil: seat.aspectFoil || null,
      card,
      equipment: null,
      ability: null,
      expended: false,
    });
  }
  const seen = new Set();
  BODY_LAYOUT.forEach(({ key, label }) => {
    const equipment = seat.board?.[key];
    if (equipment?.instanceId && !seen.has(equipment.instanceId)) {
      seen.add(equipment.instanceId);
      const card = catalogMap[equipment.cardId] || equipment;
      const hostLabel = boardSlotShortLabel(key, equipment);
      gear.push({
        kind: "equipment",
        key: equipment.instanceId,
        slotKey: key,
        slotLabel: hostLabel,
        name: equipment.name || card.name || label,
        cardId: equipment.cardId || null,
        foil: equipment.foilEffect || null,
        card,
        equipment,
        ability: null,
        expended: Boolean(equipment.expended),
      });
      if (equipment.inherentAbility?.instanceId) {
        const ability = equipment.inherentAbility;
        const abilityCard = catalogMap[ability.cardId] || ability;
        abilities.push({
          kind: "ability",
          key: ability.instanceId,
          slotKey: key,
          slotLabel: hostLabel,
          name: ability.name || abilityCard.name || "Ability",
          cardId: ability.cardId || null,
          foil: ability.foilEffect || null,
          card: abilityCard,
          equipment,
          ability,
          boardSlot: key,
          expended:
            ability.minion && (ability.minion.hp || 0) > 0
              ? Boolean(ability.minion.expended)
              : abilityLooksUsed(ability),
        });
      }
    }
    const rack = getSeatAbilityRack(seat, key);
    const slots = rack?.abilitySlots || [];
    const hostLabel = boardSlotShortLabel(key, equipment);
    slots.forEach((slot, slotIndex) => {
      const ability = slot?.ability;
      if (!ability?.instanceId) return;
      const abilityCard = catalogMap[ability.cardId] || ability;
      abilities.push({
        kind: "ability",
        key: ability.instanceId,
        slotKey: key,
        slotLabel: hostLabel,
        name: ability.name || abilityCard.name || "Ability",
        cardId: ability.cardId || null,
        foil: ability.foilEffect || null,
        card: abilityCard,
        equipment: equipment || rack,
        ability,
        boardSlot: key,
        expended:
          ability.minion && (ability.minion.hp || 0) > 0
            ? Boolean(ability.minion.expended)
            : slottedAbilityLooksUsed(
                slots,
                slotIndex,
                ability,
                equipment || rack,
                seat,
              ),
      });
    });
  });
  return { gear, abilities };
}

function formatSeatDeckCounts(seat) {
  if (!seat) return null;
  return {
    hand: seat.handCount ?? (seat.hand || []).length ?? 0,
    deck: seat.deckCount ?? (seat.deck || []).length ?? 0,
    discard: seat.discardCount ?? (seat.discard || []).length ?? 0,
  };
}

function formatSeatDeckCountLabel(counts) {
  if (!counts) return "";
  return `Deck: ${counts.deck}\nHand: ${counts.hand}\nDiscard: ${counts.discard}`;
}

const SEAT_DECK_PILE_KEYWORDS = [
  { key: "deck", keywordId: "deck", countKey: "deck", labelPrefix: "Deck" },
  { key: "hand", keywordId: "hand", countKey: "hand", labelPrefix: "Hand" },
  {
    key: "discard",
    keywordId: "discard",
    countKey: "discard",
    labelPrefix: "Discard",
  },
];

function StatusVitalsDeckCounts({ counts, keywordProps = {} }) {
  if (!counts) return null;
  return (
    <span className={styles.statusVitalsDecks}>
      {SEAT_DECK_PILE_KEYWORDS.map((part) => (
        <span key={part.key} className={styles.statusVitalsDeckChip}>
          {renderStatusKeywordBadge(
            part.keywordId,
            styles.statusVitalsDeckPart,
            `${part.labelPrefix}: ${counts[part.countKey] || 0}`,
            keywordProps,
          )}
        </span>
      ))}
    </span>
  );
}

function getWebSocketUrl() {
  return resolveWebSocketUrl(WS_PATH);
}

function formatCompletedGameEnded(endedBy, winner) {
  if (endedBy === "draw") return "Draw";
  if (endedBy === "forfeit") return `Forfeit (${winner || "—"})`;
  if (endedBy === "timeout") return `Timeout (${winner || "—"})`;
  return winner ? `Winner ${winner}` : endedBy || "—";
}

function formatMatchTimerMs(ms) {
  const totalSeconds = Math.max(0, Math.ceil(Number(ms) / 1000) || 0);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function matchTimerRemainingSeconds(endsAt) {
  return Math.max(0, Math.ceil((Number(endsAt) - Date.now()) / 1000) || 0);
}

function MatchTimerOverlay({ endsAt, kind, title, caption }) {
  const [remainingSec, setRemainingSec] = useState(() =>
    matchTimerRemainingSeconds(endsAt),
  );
  useEffect(() => {
    const tick = () => setRemainingSec(matchTimerRemainingSeconds(endsAt));
    tick();
    const id = window.setInterval(tick, 200);
    return () => window.clearInterval(id);
  }, [endsAt]);
  return (
    <div
      className={[
        styles.matchTimer,
        styles.matchTimerOverlay,
        remainingSec <= 15 ? styles.matchTimerUrgent : null,
        kind === "mulligan" ? styles.matchTimerMulligan : null,
      ]
        .filter(Boolean)
        .join(" ")}
      aria-live="polite"
      aria-label={title}
      title={title}
    >
      <span className={styles.matchTimerValue}>
        {formatMatchTimerMs(remainingSec * 1000)}
      </span>
      {caption ? (
        <span className={styles.matchTimerCaption}>{caption}</span>
      ) : null}
    </div>
  );
}

function formatAdminTimestamp(value) {
  const ms = Number(value) || 0;
  if (ms <= 0) return "—";
  try {
    return new Date(ms).toLocaleString();
  } catch (error) {
    return "—";
  }
}

function formatAdminDuration(ms) {
  const total = Math.max(0, Math.floor(Number(ms) || 0));
  if (total <= 0) return "—";
  const seconds = Math.floor(total / 1000);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`;
  }
  if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
}

function formatAdminVia(value) {
  if (value === "android") return "Android";
  if (value === "ios") return "iOS";
  if (value === "web") return "Web";
  return value || "—";
}

function formatAdminLocation(ip, geo) {
  const address = typeof ip === "string" ? ip.trim() : "";
  const place = typeof geo === "string" ? geo.trim() : "";
  if (address && place) return `${place} (${address})`;
  if (place) return place;
  if (address) return address;
  return "—";
}

// Player Database columns. Time/number columns sort newest/largest first.
const ADMIN_PLAYER_COLUMNS = [
  {
    id: "player",
    label: "Player",
    value: (entry) => String(entry.username || "").toLowerCase(),
  },
  {
    id: "status",
    label: "Status",
    value: (entry) =>
      `${entry.online ? 0 : 1}${String(entry.status || "").toLowerCase()}`,
  },
  {
    id: "chat",
    label: "Chat",
    value: (entry) => (entry.chatMuted ? "muted" : "open"),
  },
  {
    id: "view",
    label: "View",
    value: (entry) => String(entry.currentView || "").toLowerCase() || null,
  },
  {
    id: "session",
    label: "Session",
    numeric: true,
    value: (entry, now) =>
      entry.online && entry.sessionStartedAt
        ? Math.max(0, now - entry.sessionStartedAt)
        : null,
  },
  {
    id: "lastLogin",
    label: "Last Login",
    numeric: true,
    value: (entry) => Number(entry.lastLoginAt) || null,
  },
  {
    id: "location",
    label: "Location",
    value: (entry) => {
      const text = formatAdminLocation(entry.lastIp, entry.lastGeo);
      return text === "—" ? null : text.toLowerCase();
    },
  },
  {
    id: "created",
    label: "Created",
    numeric: true,
    value: (entry) => Number(entry.createdAt) || null,
  },
  {
    id: "origin",
    label: "Origin",
    value: (entry) => {
      const text = formatAdminLocation(entry.createdIp, entry.createdGeo);
      return text === "—" ? null : text.toLowerCase();
    },
  },
];

function sortAdminPlayers(entries, sort, now) {
  const column = ADMIN_PLAYER_COLUMNS.find((col) => col.id === sort?.key);
  if (!column) return entries;
  const direction = sort.dir === "desc" ? -1 : 1;
  return [...entries].sort((a, b) => {
    const av = column.value(a, now);
    const bv = column.value(b, now);
    // Missing values always sink to the bottom.
    if (av == null || bv == null) {
      if (av == null && bv == null) return 0;
      return av == null ? 1 : -1;
    }
    const diff = column.numeric ? av - bv : String(av).localeCompare(bv);
    return diff * direction;
  });
}

function buildReplayDisplay(game, replayIndex) {
  if (!game) return null;
  const log = Array.isArray(game.combatLog) ? game.combatLog : [];
  const frames = Array.isArray(game.frames) ? game.frames : [];
  const index = Math.max(0, Math.min(replayIndex, Math.max(0, log.length - 1)));
  const entry = log[index] || null;
  const frame =
    frames.find((item) => item.logId === entry?.id) || frames[index] || null;
  const turnIndexes = log
    .map((item, turnIndex) => (item.kind === "turn" ? turnIndex : -1))
    .filter((turnIndex) => turnIndex >= 0);
  const currentTurnPos = turnIndexes.reduce(
    (found, turnIndex, pos) => (turnIndex <= index ? pos : found),
    -1,
  );
  return {
    index,
    entry,
    frame,
    log,
    frames,
    turnIndexes,
    currentTurnPos,
    match: {
      id: game.id,
      createdAt: game.createdAt || game.completedAt || null,
      phase: frame?.phase || "main",
      players: [...(game.players || [])],
      activePlayer: frame?.activePlayer || null,
      firstPlayer: game.players?.[0] || null,
      round: Math.max(1, frame?.round || game.rounds || 1),
      winner: frame?.winner ?? game.winner ?? null,
      combatLog: log,
      seats: frame?.seats || {},
      endedBy: frame?.endedBy ?? game.endedBy ?? null,
      openingDamageBlocked: false,
      isCpu: true,
    },
  };
}

function countIds(ids) {
  return (ids || []).reduce((counts, id) => {
    if (!id) return counts;
    counts[id] = (counts[id] || 0) + 1;
    return counts;
  }, {});
}

function collectSeatCardIds(seat) {
  const ids = [];
  (seat?.hand || []).forEach((card) => {
    if (!card?.cardId || card.token) return;
    ids.push(card.cardId);
  });
  const seen = new Set();
  Object.values(seat?.board || {}).forEach((equipment) => {
    if (!equipment?.instanceId || seen.has(equipment.instanceId)) return;
    seen.add(equipment.instanceId);
    if (equipment.cardId && !equipment.token) ids.push(equipment.cardId);
    if (equipment.inherentAbility?.cardId) {
      ids.push(equipment.inherentAbility.cardId);
    }
  });
  Object.values(seat?.abilityBoard || {}).forEach((rack) => {
    (rack?.abilitySlots || []).forEach((slot) => {
      if (slot?.ability?.cardId) ids.push(slot.ability.cardId);
    });
  });
  return ids;
}

function padDeckSlots(ids) {
  const next = [...(ids || [])]
    .filter((id) => typeof id === "string")
    .slice(0, DECK_SLOTS);
  while (next.length < DECK_SLOTS) next.push(null);
  return next;
}

function padAspectSlot(aspectId) {
  return [typeof aspectId === "string" && aspectId ? aspectId : null];
}

function filledDeckIds(ids) {
  return (ids || []).filter((id) => typeof id === "string");
}

function getFoilArtFilterClass(foilEffect) {
  return resolveFoilArtFilterClass(foilEffect, styles);
}

const FoilEffectsQualityContext = createContext("maximum");
const FoilAnimateIdleContext = createContext(false);
const KeywordTipContext = createContext(null);

function useKeywordTipProps(explicit = null) {
  const contextProps = useContext(KeywordTipContext);
  if (explicit && typeof explicit.onKeywordClick === "function") {
    return explicit;
  }
  return contextProps;
}

const FOIL_SPOTLIGHT_VIEWS = new Set([
  "deck",
  "match",
  "matchIntro",
  "workshop",
  "summary",
  "draftDeck",
  "draftSummary",
  "adventureSummary",
  "marketplace",
  "replay",
]);

function normalizeFoilEffectsQuality(value) {
  if (value === "minimum" || value === "off") return value;
  return "maximum";
}

function normalizeToastsMode(value) {
  if (value === "off" || value === "card" || value === "text") return value;
  return "all";
}

function normalizeToastSpeed(value) {
  return value === "fast" ? "fast" : "standard";
}

function normalizeMatchGuides(value) {
  if (value === "on" || value === "off") return value;
  return "auto";
}

function loadMatchGuideMinimized() {
  try {
    return window.localStorage.getItem(MATCH_GUIDE_MINIMIZED_KEY) === "1";
  } catch (err) {
    return false;
  }
}

function saveMatchGuideMinimized(minimized) {
  try {
    window.localStorage.setItem(MATCH_GUIDE_MINIMIZED_KEY, minimized ? "1" : "0");
  } catch (err) {}
}

const SERVER_TOAST_MS = 9000;
const SERVER_MESSAGES_SEEN_KEY = "aeternaInvicta.serverMessagesSeenAt";

function loadServerMessagesSeenAt() {
  try {
    return Math.max(
      0,
      Number(window.localStorage.getItem(SERVER_MESSAGES_SEEN_KEY)) || 0,
    );
  } catch (err) {
    return 0;
  }
}

function saveServerMessagesSeenAt(at) {
  try {
    window.localStorage.setItem(SERVER_MESSAGES_SEEN_KEY, String(at));
  } catch (err) {}
}

function normalizeServerMessage(entry) {
  if (!entry || typeof entry.text !== "string" || !entry.text) return null;
  return {
    id: String(entry.id || ""),
    text: entry.text,
    kind: entry.kind === "maintenance" ? "maintenance" : "admin",
    at: Math.max(0, Number(entry.at) || 0),
    templateId: typeof entry.templateId === "string" ? entry.templateId : null,
    params:
      entry.params && typeof entry.params === "object" ? entry.params : null,
    note: typeof entry.note === "string" ? entry.note : "",
  };
}

function normalizeServerMaintenance(value) {
  if (!value || !Number(value.startsAt)) return null;
  return {
    startsAt: Number(value.startsAt),
    minutes: Math.max(0, Number(value.minutes) || 0),
    note: typeof value.note === "string" ? value.note : "",
    startedBy: typeof value.startedBy === "string" ? value.startedBy : "",
    started: value.started === true,
  };
}

const ENGLISH_T = createTranslator("en");

function serverMessageTitle(kind, t = ENGLISH_T) {
  return kind === "maintenance"
    ? t("serverMessages.kindMaintenance")
    : t("serverMessages.kindAdmin");
}

function formatServerCountdown(ms) {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (value) => String(value).padStart(2, "0");
  return hours
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${minutes}:${pad(seconds)}`;
}

function ServerMaintenanceCountdown({ startsAt, className }) {
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const timerId = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timerId);
  }, []);
  return (
    <span className={className}>{formatServerCountdown(startsAt - nowMs)}</span>
  );
}

function formatServerMessageTime(at, language) {
  if (!at) return "";
  try {
    return new Date(at).toLocaleTimeString(language ? [language] : [], {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (err) {
    return "";
  }
}

function resolveActiveFoilEffectsQuality(setting, animateSpotlight) {
  const quality = normalizeFoilEffectsQuality(setting);
  if (quality !== "maximum") return quality;
  return animateSpotlight ? "maximum" : "minimum";
}

function FoilOverlay({
  foilEffect = null,
  cardId = null,
  className = "",
  inheritPose = false,
  quality: qualityOverride = null,
  animateIdle = false,
}) {
  const contextQuality = useContext(FoilEffectsQualityContext);
  const contextAnimateIdle = useContext(FoilAnimateIdleContext);
  const quality = qualityOverride || contextQuality;
  if (!resolveFoilEffectId(foilEffect)) {
    return null;
  }
  const stampId = resolveFoilStampId(foilEffect);
  const stampIconFile = getFoilStampIconFile(stampId);
  const stampUrl = stampIconFile
    ? `${AETERNA_ASSET_ROOT}/icons/${stampIconFile}`
    : null;
  return (
    <>
      <AiFoilLayers
        foilEffect={foilEffect}
        cardId={cardId}
        styles={styles}
        className={className}
        inheritPose={inheritPose}
        quality={quality}
        animateIdle={Boolean(animateIdle || contextAnimateIdle)}
      />
      {stampUrl && quality !== "off" ? (
        <div className={styles.foilStampLayer} aria-hidden="true">
          <div
            className={styles.foilStampPattern}
            style={{ backgroundImage: `url("${stampUrl}")` }}
          />
        </div>
      ) : null}
    </>
  );
}

function padFoilSlots(foils) {
  const next = [...(foils || [])].slice(0, DECK_SLOTS);
  while (next.length < DECK_SLOTS) next.push(null);
  return next;
}
function filledDeckFoils(ids, foils) {
  const foilList = padFoilSlots(foils);
  const sourceIds = padDeckSlots(ids);
  const out = [];
  sourceIds.forEach((id, index) => {
    if (typeof id === "string") out.push(foilList[index] || null);
  });
  return out;
}
function deckFoilsEqual(a, b) {
  const left = Array.isArray(a) ? a : [];
  const right = Array.isArray(b) ? b : [];
  if (left.length !== right.length) return false;
  for (let i = 0; i < left.length; i += 1) {
    if ((left[i] || null) !== (right[i] || null)) return false;
  }
  return true;
}

const FLEX_PAYMENT_ATTRIBUTES = ["strength", "dexterity", "wisdom", "neutral"];
const PRIMARY_COST_ATTRIBUTES = ["strength", "dexterity", "wisdom"];
const RECOMMENDED_FLEX_PAYMENT_ORDER = [
  "neutral",
  "strength",
  "dexterity",
  "wisdom",
];

function formatCost(cost) {
  if (!cost) return "";
  if (cost.costValue != null && Array.isArray(cost.costAttributes)) {
    return formatHybridCost(cost.costValue, cost.costAttributes);
  }
  return Object.entries(cost)
    .filter(([, value]) => value > 0)
    .map(([key, value]) => {
      if (key === "neutral") {
        return `any ${value}`;
      }
      return `${key} ${value}`;
    })
    .join(" ");
}

function formatHybridCost(costValue, costAttributes) {
  const value = Math.max(0, Number(costValue) || 0);
  if (value <= 0) return "";
  return (costAttributes || [])
    .filter(Boolean)
    .map((attribute) => {
      const amount = attribute === "neutral" ? value * 2 : value;
      if (attribute === "neutral") return `any ${amount}`;
      return `${attribute} ${amount}`;
    })
    .join(" / ");
}

function hybridCostObject(costValue, costAttributes) {
  if (costValue == null || !Array.isArray(costAttributes)) return null;
  return { costValue, costAttributes };
}

function equipmentActionCost(equipment, prefix) {
  const hybrid = hybridCostObject(
    equipment?.[`${prefix}CostValue`],
    equipment?.[`${prefix}CostAttributes`],
  );
  if (hybrid) return hybrid;
  return equipment?.[`${prefix}Cost`] || {};
}

function canAffordEquipmentActionCost(resources, cost, affordContext = null) {
  if (cost?.costValue != null && Array.isArray(cost.costAttributes)) {
    const flex = Math.max(0, Number(cost.costValue) || 0) * 2;
    if (flex <= 0) return true;
    const weighted = (cost.costAttributes || []).reduce((sum, attribute) => {
      const amount = Math.max(0, resources?.[attribute] || 0);
      const weight = attribute === "neutral" ? 1 : 2;
      return sum + amount * weight;
    }, 0);
    return weighted >= flex;
  }
  return canAffordAbilityCost(resources, cost, affordContext);
}

function mergeCostMaps(...costs) {
  const result = {};
  costs.forEach((cost) => {
    Object.entries(cost || {}).forEach(([attribute, amount]) => {
      const value = Number(amount) || 0;
      if (value <= 0) return;
      result[attribute] = (result[attribute] || 0) + value;
    });
  });
  return result;
}

function getAbilityChoiceCost(ability, effectIndex) {
  const base = ability?.effectiveCost || ability?.cost || {};
  if (effectIndex == null || !Array.isArray(ability?.effect?.choose)) {
    return base;
  }
  const published = ability.effectiveChoiceCosts?.[effectIndex];
  if (published) return published;
  const entry = ability.effect.choose[effectIndex];
  return mergeCostMaps(base, Array.isArray(entry) ? null : entry?.cost);
}

function withAbilityChoiceCost(ability, effectIndex) {
  if (
    !ability ||
    effectIndex == null ||
    !Array.isArray(ability.effect?.choose)
  ) {
    return ability;
  }
  const healthCost = ability.effectiveChoiceHealthCosts?.[effectIndex];
  return {
    ...ability,
    effectiveCost: getAbilityChoiceCost(ability, effectIndex),
    ...(healthCost != null ? { effectiveHealthCost: healthCost } : {}),
  };
}

function abilityChoiceHasCostClient(card) {
  return (
    Array.isArray(card?.effect?.choose) &&
    card.effect.choose.some(
      (entry) =>
        entry && !Array.isArray(entry) && costEntries(entry.cost).length > 0,
    )
  );
}

function isHealthPaymentEffect(entry) {
  return Boolean(entry && entry.id === "damageSelf" && entry.ignoreArmor);
}

function getEffectHealthPaymentAmount(entry) {
  const list = Array.isArray(entry) ? entry : [entry];
  return list
    .filter(isHealthPaymentEffect)
    .reduce((total, item) => total + Math.max(0, item.amount || 0), 0);
}

function isSelfDamageCostEntry(entry) {
  return Boolean(entry && entry.id === "damageSelf" && !entry.ignoreArmor);
}

function splitSelfDamageCost(effect) {
  if (!Array.isArray(effect)) return { selfDamage: [], effect };
  const selfDamage = effect.filter(isSelfDamageCostEntry);
  const rest = effect.filter((entry) => !isSelfDamageCostEntry(entry));
  if (!selfDamage.length || !rest.length) return { selfDamage: [], effect };
  return {
    selfDamage: selfDamage.map((entry) => ({
      damageType: entry.damageType || "physical",
      amount: Math.max(0, Math.floor(Number(entry.amount) || 0)),
    })),
    effect: rest.length === 1 ? rest[0] : rest,
  };
}

function SelfDamageCostIcons({ entries, keywordProps = null }) {
  if (!Array.isArray(entries) || !entries.length) return null;
  return (
    <>
      {entries.map((entry, index) => (
        <ElementalDamageInline
          key={`self-damage-${entry.damageType}-${index}`}
          damageType={entry.damageType}
          amount={entry.amount}
          keywordProps={keywordProps}
        />
      ))}
    </>
  );
}

function stripHealthPaymentEffects(effect) {
  if (Array.isArray(effect)) {
    return effect.filter((entry) => !isHealthPaymentEffect(entry));
  }
  if (Array.isArray(effect?.choose)) {
    return { ...effect, choose: effect.choose.map(stripHealthPaymentEffects) };
  }
  return isHealthPaymentEffect(effect) ? null : effect;
}

function abilityChoiceHasHealthCostClient(card) {
  return (
    Array.isArray(card?.effect?.choose) &&
    card.effect.choose.some((entry) => getEffectHealthPaymentAmount(entry) > 0)
  );
}

function stripAbilityChoiceEntryCost(entry) {
  if (!entry || Array.isArray(entry) || typeof entry !== "object") {
    return entry;
  }
  if (!("cost" in entry)) return entry;
  return Object.fromEntries(
    Object.entries(entry).filter(([key]) => key !== "cost"),
  );
}

function stripAbilityChoiceCosts(effect) {
  if (!Array.isArray(effect?.choose)) return effect;
  return {
    ...effect,
    choose: effect.choose.map(stripAbilityChoiceEntryCost),
  };
}

function listAbilityChoiceAddedDamageEntries(elementalDamage) {
  if (!elementalDamage || typeof elementalDamage !== "object") return [];
  return Object.entries(elementalDamage)
    .filter(([, amount]) => (Number(amount) || 0) > 0)
    .map(([damageType, amount]) => ({
      id: "damageOpponent",
      amount,
      damageType,
    }));
}

function getAbilityChoiceDisplayEntry(card, index) {
  const rawEntry = stripAbilityChoiceEntryCost(card?.effect?.choose?.[index]);
  const entry = abilityUsesLevelTableScale(card)
    ? resolveEffectLevelTablesForLevel(
        rawEntry,
        Math.max(1, card?.effectiveLevel || card?.level || 1),
      )
    : rawEntry;
  const effective = card?.effectiveChoiceEffects?.[index];
  if (!entry || !effective) return entry;
  if (Array.isArray(entry)) {
    const added = listAbilityChoiceAddedDamageEntries(
      effective.effectiveElementalDamage,
    );
    return added.length ? [...entry, ...added] : entry;
  }
  const next = { ...entry };
  if (effective.effectiveAmount != null) {
    next.amount = effective.effectiveAmount;
    next.effectiveAmount = effective.effectiveAmount;
  }
  if (effective.effectiveElementalDamage && entry.id === "damageOpponent") {
    next.effectiveElementalDamage = effective.effectiveElementalDamage;
  }
  [
    ["burn", "effectiveBurn"],
    ["chill", "effectiveChill"],
    ["shock", "effectiveShock"],
  ].forEach(([ailment, key]) => {
    if (effective[key] != null) {
      next[ailment] = effective[key];
      next[key] = effective[key];
    }
  });
  if (entry.id !== "damageOpponent") {
    const added = listAbilityChoiceAddedDamageEntries(
      effective.effectiveElementalDamage,
    );
    if (added.length) return [next, ...added];
  }
  return next;
}

function getAbilityChoiceDisplayEffect(card) {
  if (!Array.isArray(card?.effect?.choose)) return card?.effect;
  return {
    ...card.effect,
    choose: card.effect.choose.map((_, index) =>
      getAbilityChoiceDisplayEntry(card, index),
    ),
  };
}

function getAbilityChoiceOutputDelta(card, index) {
  return Math.floor(
    Number(card?.effectiveChoiceEffects?.[index]?.outputDelta) || 0,
  );
}

function listAbilityChoiceModes(card, options = {}) {
  if (!Array.isArray(card?.effect?.choose)) return [];
  const { omitHealthPayment = false, ...formatOptions } = options;
  return card.effect.choose
    .map((entry, index) => {
      const displayEntry = getAbilityChoiceDisplayEntry(card, index);
      return {
        index,
        cost: getAbilityChoiceCost(card, index),
        healthCost: getEffectHealthPaymentAmount(entry),
        outputDelta: getAbilityChoiceOutputDelta(card, index),
        text: formatActivatedEffectText(
          omitHealthPayment
            ? stripHealthPaymentEffects(displayEntry)
            : displayEntry,
          formatOptions,
        ),
      };
    })
    .filter((mode) => mode.text);
}

function costEntries(cost) {
  if (cost?.costValue != null && Array.isArray(cost.costAttributes)) {
    return (cost.costAttributes || [])
      .map((attribute) => {
        const amount =
          attribute === "neutral"
            ? Math.max(0, Number(cost.costValue) || 0) * 2
            : Math.max(0, Number(cost.costValue) || 0);
        return [attribute, amount];
      })
      .filter(([, value]) => value > 0);
  }
  return Object.entries(cost || {}).filter(
    ([attribute, value]) =>
      (FLEX_PAYMENT_ATTRIBUTES.includes(attribute) ||
        attribute === "physicalArmor") &&
      Number(value) > 0,
  );
}

function totalResourceCostAmount(cost) {
  return costEntries(cost).reduce(
    (sum, [, value]) => sum + Math.max(0, Number(value) || 0),
    0,
  );
}

function getSeatSpendablePhysicalArmor(seat) {
  if (!seat) return 0;
  return (
    Math.max(0, seat.temporaryPhysicalArmor || 0) +
    Math.max(0, seat.persistentPhysicalArmor || 0)
  );
}

function canAffordPhysicalArmorCost(seat, cost) {
  return getSeatSpendablePhysicalArmor(seat) >= totalResourceCostAmount(cost);
}

function physicalArmorPayCostFromResourceCost(cost) {
  const total = totalResourceCostAmount(cost);
  if (total <= 0) return null;
  return { physicalArmor: total };
}

function effectEntryDealsPhysicalDamage(entry) {
  if (!entry || typeof entry !== "object") return false;
  if (
    entry.id === "damageOpponent" ||
    entry.id === "convertPhysicalArmorToDamage"
  ) {
    return !entry.damageType || entry.damageType === "physical";
  }
  if (
    (entry.id === "bleed" || entry.id === "poison") &&
    (entry.damage || 0) > 0
  ) {
    return !entry.damageType || entry.damageType === "physical";
  }
  return false;
}

function effectDealsPhysicalDamageClient(effect) {
  if (!effect) return false;
  if (Array.isArray(effect.choose)) {
    return effect.choose.some((entry) => {
      if (Array.isArray(entry)) {
        return entry.some((nested) => effectEntryDealsPhysicalDamage(nested));
      }
      return effectEntryDealsPhysicalDamage(entry);
    });
  }
  if (Array.isArray(effect)) {
    return effect.some((entry) => effectEntryDealsPhysicalDamage(entry));
  }
  return effectEntryDealsPhysicalDamage(effect);
}

function equipmentEffectDealsPhysicalDamage(effect) {
  return (
    (effectQuickDisplayIsDamage(effect) &&
      (effect?.damageType || "physical") === "physical") ||
    effectDealsPhysicalDamageClient(effect)
  );
}

function appendPhysicalArmorPayQuickActions(actions, equipment, seat) {
  const charge = Math.max(
    0,
    Math.floor(Number(seat?.physicalArmorPayCost) || 0),
  );
  if (charge <= 0) return;
  actions
    .filter((action) => action.mode === "effect" && !action.payWithPhysicalArmor)
    .forEach((action) => {
      const effect = getEquipmentEffectForActionIndex(
        equipment,
        action.effectIndex,
      );
      if (!equipmentEffectDealsPhysicalDamage(effect)) return;
      const armorCost = physicalArmorPayCostFromResourceCost(action.cost);
      if (!armorCost) return;
      actions.push({
        ...action,
        key: `effect-armorPay-${action.effectIndex ?? "all"}`,
        cost: armorCost,
        payWithPhysicalArmor: true,
      });
    });
}

function expandEquipmentResourceEffectActionsForArmorPay(
  effectActions,
  equipment,
  seat,
) {
  const charge = Math.max(
    0,
    Math.floor(Number(seat?.physicalArmorPayCost) || 0),
  );
  if (charge <= 0) return effectActions;
  const extras = [];
  effectActions.forEach((action) => {
    const effect = getEquipmentEffectForActionIndex(equipment, action.index);
    if (!equipmentEffectDealsPhysicalDamage(effect)) return;
    const armorCost = physicalArmorPayCostFromResourceCost(action.cost);
    if (!armorCost) return;
    extras.push({
      ...action,
      armorPayKey: `armorPay-${action.index ?? "all"}`,
      armorPayCost: armorCost,
      payWithPhysicalArmor: true,
    });
  });
  return [...effectActions, ...extras];
}

function expandAbilityActivateActionsForArmorPay(ability, seat, baseActions) {
  const base = Array.isArray(baseActions) ? baseActions : [];
  const charge = Math.max(
    0,
    Math.floor(Number(seat?.physicalArmorPayCost) || 0),
  );
  const costTotal = totalResourceCostAmount(
    ability?.effectiveCost || ability?.cost || {},
  );
  const extras = [];
  if (charge > 0 && costTotal > 0) {
    base.forEach((action) => {
      const effect =
        action.index != null && Array.isArray(ability.effect?.choose)
          ? ability.effect.choose[action.index]
          : ability.effect;
      if (!equipmentEffectDealsPhysicalDamage(effect)) return;
      extras.push({
        ...action,
        payWithPhysicalArmor: true,
        armorPayKey: `-armorPay-${action.index ?? "main"}`,
      });
    });
  }
  return [
    ...base.map((action) => ({ ...action, payWithPhysicalArmor: false })),
    ...extras,
  ];
}

function augmentCostIncreaseEntries(card) {
  const modify = resolveAugmentModifyForLevel(
    card?.augment,
    card?.effectiveLevel || card?.level || 1,
  );
  const costAdd =
    card?.effectiveCostAdd != null ? card.effectiveCostAdd : modify.costAdd;
  if (typeof costAdd === "number" && costAdd > 0) {
    return [[card.attribute || "neutral", costAdd]];
  }
  if (costAdd && typeof costAdd === "object") {
    return costEntries(costAdd);
  }
  return [];
}

function augmentCostMultiplier(card) {
  const modify = resolveAugmentModifyForLevel(
    card?.augment,
    card?.effectiveLevel || card?.level || 1,
  );
  const costMult = Number(
    card?.effectiveCostMult != null ? card.effectiveCostMult : modify.costMult,
  );
  if (!Number.isFinite(costMult) || costMult <= 1) return null;
  return costMult;
}

function countFlexPaymentPermutations(available, flex) {
  const needed = Math.max(0, Number(flex) || 0);
  if (needed <= 0) return 1;
  const capacities = FLEX_PAYMENT_ATTRIBUTES.map((attribute) =>
    Math.max(0, available?.[attribute] || 0),
  );
  const totalPool = capacities.reduce((sum, value) => sum + value, 0);
  if (totalPool < needed) return 0;

  function countFrom(index, remaining) {
    if (remaining === 0) return 1;
    if (index >= FLEX_PAYMENT_ATTRIBUTES.length) return 0;
    const max = Math.min(capacities[index], remaining);
    let total = 0;
    for (let use = 0; use <= max; use += 1) {
      total += countFrom(index + 1, remaining - use);
    }
    return total;
  }

  return countFrom(0, needed);
}

function getNeutralFlexPaymentInfo(resources, cost) {
  const flex = Math.max(0, Number(cost?.neutral) || 0);
  const fixed = {};
  Object.entries(cost || {}).forEach(([attribute, amount]) => {
    if (attribute !== "neutral" && amount > 0) {
      fixed[attribute] = amount;
    }
  });
  const available = Object.fromEntries(
    FLEX_PAYMENT_ATTRIBUTES.map((attribute) => [
      attribute,
      Math.max(
        0,
        (resources?.[attribute] || 0) - (fixed[attribute] || 0),
      ),
    ]),
  );
  return {
    flex,
    fixed,
    available,
    needsChoice:
      flex > 0 && countFlexPaymentPermutations(available, flex) > 1,
  };
}

function emptyFlexPaymentDraft() {
  return Object.fromEntries(
    FLEX_PAYMENT_ATTRIBUTES.map((attribute) => [attribute, 0]),
  );
}

function attributePaymentWeight(attribute, weights) {
  if (weights && Object.prototype.hasOwnProperty.call(weights, attribute)) {
    return Math.max(0, Number(weights[attribute]) || 0);
  }
  return 1;
}

function paymentTotal(payment, weights = null) {
  return FLEX_PAYMENT_ATTRIBUTES.reduce((sum, attribute) => {
    const amount = payment?.[attribute] || 0;
    return sum + amount * attributePaymentWeight(attribute, weights);
  }, 0);
}

function paymentMinimums(fixed) {
  return {
    strength: Math.max(0, fixed?.strength || 0),
    dexterity: Math.max(0, fixed?.dexterity || 0),
    wisdom: Math.max(0, fixed?.wisdom || 0),
    neutral: 0,
  };
}

function fullPaymentNeeded(fixed, flex, weights = null) {
  return (
    paymentTotal(paymentMinimums(fixed), weights) +
    Math.max(0, Number(flex) || 0)
  );
}

function poolFromAvailable(available, fixed) {
  const mins = paymentMinimums(fixed);
  return {
    strength: (available?.strength || 0) + mins.strength,
    dexterity: (available?.dexterity || 0) + mins.dexterity,
    wisdom: (available?.wisdom || 0) + mins.wisdom,
    neutral: available?.neutral || 0,
  };
}

function buildRecommendedFlexPayment(
  available,
  flex,
  fixed = {},
  weights = null,
  fillOrder = RECOMMENDED_FLEX_PAYMENT_ORDER,
) {
  const pool = poolFromAvailable(available, fixed);
  const mins = paymentMinimums(fixed);
  const payment = { ...mins };
  let left =
    fullPaymentNeeded(fixed, flex, weights) - paymentTotal(mins, weights);
  for (const attribute of fillOrder) {
    if (left <= 0) break;
    const weight = attributePaymentWeight(attribute, weights);
    if (weight <= 0) continue;
    const room = Math.max(0, (pool[attribute] || 0) - (payment[attribute] || 0));
    const take = Math.min(Math.floor(left / weight), room);
    payment[attribute] += take;
    left -= take * weight;
  }
  return payment;
}

function flexPaymentFromFull(full, fixed) {
  const mins = paymentMinimums(fixed);
  return {
    strength: Math.max(0, (full?.strength || 0) - mins.strength),
    dexterity: Math.max(0, (full?.dexterity || 0) - mins.dexterity),
    wisdom: Math.max(0, (full?.wisdom || 0) - mins.wisdom),
    neutral: Math.max(0, full?.neutral || 0),
  };
}

function redistributeFullPayment(
  attribute,
  next,
  available,
  flex,
  fixed = {},
  weights = null,
  fillOrder = RECOMMENDED_FLEX_PAYMENT_ORDER,
) {
  const pool = poolFromAvailable(available, fixed);
  const mins = paymentMinimums(fixed);
  const needed = fullPaymentNeeded(fixed, flex, weights);
  const others = FLEX_PAYMENT_ATTRIBUTES.filter((entry) => entry !== attribute);
  const thisWeight = attributePaymentWeight(attribute, weights);
  const otherMinWeighted = others.reduce(
    (sum, entry) =>
      sum +
      (mins[entry] || 0) * attributePaymentWeight(entry, weights),
    0,
  );
  const minThis = mins[attribute] || 0;
  const maxByNeed =
    thisWeight > 0
      ? Math.floor(Math.max(0, needed - otherMinWeighted) / thisWeight)
      : 0;
  const maxThis = Math.min(
    pool[attribute] || 0,
    Math.max(minThis, maxByNeed),
  );
  const clamped = Math.max(minThis, Math.min(maxThis, Number(next) || 0));
  const payment = emptyFlexPaymentDraft();
  payment[attribute] = clamped;
  let remaining = needed - clamped * thisWeight;
  others.forEach((entry) => {
    payment[entry] = mins[entry] || 0;
    remaining -=
      (mins[entry] || 0) * attributePaymentWeight(entry, weights);
  });
  const order = fillOrder.filter((entry) => entry !== attribute);
  for (const entry of order) {
    if (remaining <= 0) break;
    const weight = attributePaymentWeight(entry, weights);
    if (weight <= 0) continue;
    const room = Math.max(0, (pool[entry] || 0) - (payment[entry] || 0));
    const take = Math.min(Math.floor(remaining / weight), room);
    payment[entry] += take;
    remaining -= take * weight;
  }
  return payment;
}

function seatHasAspectPassive(seat, catalogMap, passiveId) {
  const aspectId = seat?.aspectId;
  if (!aspectId || !passiveId) return false;
  const aspect = catalogMap?.[aspectId];
  return (aspect?.passiveEffects || []).some((effect) => effect?.id === passiveId);
}

function canPayMonochromeCost(resources, cost) {
  const remaining = Object.fromEntries(
    FLEX_PAYMENT_ATTRIBUTES.map((attribute) => [
      attribute,
      Math.max(0, resources?.[attribute] || 0),
    ]),
  );
  const compact = Object.fromEntries(
    Object.entries(cost || {}).filter(([, amount]) => amount > 0),
  );
  for (const attribute of PRIMARY_COST_ATTRIBUTES) {
    const amount = compact[attribute] || 0;
    if (amount <= 0) continue;
    const pool = (remaining[attribute] || 0) + (remaining.neutral || 0);
    if (pool < amount) return false;
    const fromPrimary = Math.min(remaining[attribute] || 0, amount);
    remaining[attribute] = (remaining[attribute] || 0) - fromPrimary;
    remaining.neutral = (remaining.neutral || 0) - (amount - fromPrimary);
  }
  const flex = compact.neutral || 0;
  if (flex <= 0) return true;
  let left = flex;
  for (const attribute of FLEX_PAYMENT_ATTRIBUTES) {
    const take = Math.min(left, remaining[attribute] || 0);
    left -= take;
  }
  return left === 0;
}

function canAffordAbilityCost(resources, cost, affordContext = null) {
  const seat = affordContext?.seat || null;
  const catalogMap = affordContext?.catalogMap || null;
  if (seatHasAspectPassive(seat, catalogMap, "primaryCostPayWithNeutral")) {
    return canPayMonochromeCost(resources, cost);
  }
  const fixedOk = Object.entries(cost || {}).every(([attribute, amount]) => {
    if (attribute === "neutral" || !(amount > 0)) return true;
    return (resources?.[attribute] || 0) >= amount;
  });
  if (!fixedOk) return false;
  const info = getNeutralFlexPaymentInfo(resources, cost);
  if (info.flex <= 0) return true;
  const pool = FLEX_PAYMENT_ATTRIBUTES.reduce(
    (sum, attribute) => sum + (info.available[attribute] || 0),
    0,
  );
  return pool >= info.flex;
}

function canAffordAbilityActivation(seat, ability, catalogMap) {
  const healthCost = Math.max(0, ability?.effectiveHealthCost || 0);
  if (healthCost > 0) {
    return (seat?.hp || 0) > healthCost;
  }
  return canAffordAbilityCost(
    mergeSeatResources(seat),
    ability?.effectiveCost || ability?.cost || {},
    { seat, catalogMap },
  );
}

const ATTRIBUTE_SHORTFALL_LABELS = {
  strength: "Strength",
  dexterity: "Dexterity",
  wisdom: "Wisdom",
  neutral: "Neutral",
};

function formatAttributeShortfallLabel(attribute) {
  const fallback = ATTRIBUTE_SHORTFALL_LABELS[attribute] || attribute;
  return localizedOptionLabel("common.attribute", {
    id: attribute,
    label: fallback,
  });
}

function formatResourceShortfall(need, attribute, have) {
  return t("match.board.needResource", {
    need,
    attribute: formatAttributeShortfallLabel(attribute),
    have,
  });
}

function describeResourceShortfall(resources, cost, affordContext = null) {
  if (canAffordAbilityCost(resources, cost, affordContext)) return null;
  const parts = [];
  const seat = affordContext?.seat || null;
  const catalogMap = affordContext?.catalogMap || null;
  const monochrome = seatHasAspectPassive(
    seat,
    catalogMap,
    "primaryCostPayWithNeutral",
  );

  if (monochrome) {
    const remaining = Object.fromEntries(
      FLEX_PAYMENT_ATTRIBUTES.map((attribute) => [
        attribute,
        Math.max(0, Number(resources?.[attribute]) || 0),
      ]),
    );
    for (const attribute of PRIMARY_COST_ATTRIBUTES) {
      const need = Math.max(0, Number(cost?.[attribute]) || 0);
      if (need <= 0) continue;
      const havePrimary = remaining[attribute] || 0;
      const haveNeutral = remaining.neutral || 0;
      const pool = havePrimary + haveNeutral;
      if (pool < need) {
        parts.push(formatResourceShortfall(need, attribute, havePrimary));
      } else {
        const fromPrimary = Math.min(havePrimary, need);
        remaining[attribute] = havePrimary - fromPrimary;
        remaining.neutral = haveNeutral - (need - fromPrimary);
      }
    }
    const flex = Math.max(0, Number(cost?.neutral) || 0);
    if (flex > 0) {
      const pool = FLEX_PAYMENT_ATTRIBUTES.reduce(
        (sum, attribute) => sum + (remaining[attribute] || 0),
        0,
      );
      if (pool < flex) {
        parts.push(formatResourceShortfall(flex, "neutral", pool));
      }
    }
  } else {
    for (const attribute of PRIMARY_COST_ATTRIBUTES) {
      const need = Math.max(0, Number(cost?.[attribute]) || 0);
      if (need <= 0) continue;
      const have = Math.max(0, Number(resources?.[attribute]) || 0);
      if (have < need) {
        parts.push(formatResourceShortfall(need, attribute, have));
      }
    }
    const info = getNeutralFlexPaymentInfo(resources, cost);
    if (info.flex > 0) {
      const pool = FLEX_PAYMENT_ATTRIBUTES.reduce(
        (sum, attribute) => sum + (info.available[attribute] || 0),
        0,
      );
      if (pool < info.flex) {
        parts.push(formatResourceShortfall(info.flex, "neutral", pool));
      }
    }
  }

  return parts.length
    ? parts.join("; ")
    : t("match.board.notEnoughResources");
}

function describeAbilityActivationShortfall(seat, ability, catalogMap) {
  const healthCost = Math.max(0, ability?.effectiveHealthCost || 0);
  if (healthCost > 0) {
    const have = Math.max(0, seat?.hp || 0);
    if (!(have > healthCost)) {
      return t("match.board.needHealth", { need: healthCost, have });
    }
  }
  return describeResourceShortfall(
    mergeSeatResources(seat),
    ability?.effectiveCost || ability?.cost || {},
    { seat, catalogMap },
  );
}

function formatResources(resources) {
  return Object.entries(resources || {})
    .filter(([, value]) => value > 0)
    .map(([attribute, value]) => `${attribute} ${value}`);
}

function resourceGrantEntries(resources) {
  return Object.entries(resources || {}).filter(([, value]) => value > 0);
}

function resourceGrantEntriesFromEffect(effect) {
  if (!effect || effect.id !== "grantResources") return [];
  return resourceGrantEntries(effect.resources);
}

function listEquipmentIdentityAttributes(cardOrEquipment) {
  if (!cardOrEquipment) return [];
  let attrs = [];
  if (Array.isArray(cardOrEquipment.identityAttributes)) {
    attrs = cardOrEquipment.identityAttributes;
  } else {
    attrs = cardAttributeList(cardOrEquipment);
  }
  return [
    ...new Set(
      attrs.filter((attribute) => PRIMARY_COST_ATTRIBUTES.includes(attribute)),
    ),
  ];
}

function listEquipmentIdentityResourceGrantChoices(cardOrEquipment) {
  if (cardOrEquipment?.onEquip?.id !== "chooseIdentity") return [];
  if (cardOrEquipment?.chosenIdentity) return [];
  const attrs = listEquipmentIdentityAttributes(cardOrEquipment);
  if (!attrs.length) return [];
  return attrs.map((attribute) => [[attribute, 1]]);
}

function listEquipmentResourceGrantChoices(cardOrEquipment) {
  const direct = resourceGrantEntries(cardOrEquipment?.resources);
  if (direct.length) return [direct];

  const activated = cardOrEquipment?.activatedEffect;
  if (activated) {
    if (Array.isArray(activated.choose)) {
      const fromChoose = activated.choose
        .map((entry) => resourceGrantEntriesFromEffect(entry))
        .filter((entries) => entries.length > 0);
      if (fromChoose.length) return fromChoose;
    }

    const fromEffect = resourceGrantEntriesFromEffect(activated);
    if (fromEffect.length) return [fromEffect];
  }

  return listEquipmentIdentityResourceGrantChoices(cardOrEquipment);
}

function isGrantResourcesOnlyChooseEffect(effect) {
  if (!effect || !Array.isArray(effect.choose) || !effect.choose.length) {
    return false;
  }
  return effect.choose.every((entry) => entry?.id === "grantResources");
}

const SHOCK_RESOURCE_ORDER = [
  "strength",
  "dexterity",
  "wisdom",
  "neutral",
];

function entriesToResourceMap(entries) {
  const map = Object.fromEntries(
    SHOCK_RESOURCE_ORDER.map((attribute) => [attribute, 0]),
  );
  (entries || []).forEach(([attribute, value]) => {
    const amount = Math.max(0, Number(value) || 0);
    if (amount > 0 && attribute in map) {
      map[attribute] += amount;
    }
  });
  return map;
}

function reduceResourceGrantByClient(resources, amount = 1) {
  const result = entriesToResourceMap(
    Object.entries(resources || {}).filter(([, value]) => value > 0),
  );
  let remaining = Math.max(0, Number(amount) || 0);
  SHOCK_RESOURCE_ORDER.forEach((attribute) => {
    if (remaining <= 0) return;
    const have = Math.max(0, result[attribute] || 0);
    const take = Math.min(have, remaining);
    result[attribute] = have - take;
    remaining -= take;
  });
  return result;
}

function splitResourceGrantForShock(entries) {
  const resources = entriesToResourceMap(entries);
  const total = SHOCK_RESOURCE_ORDER.reduce(
    (sum, attribute) => sum + (resources[attribute] || 0),
    0,
  );
  if (total <= 0) {
    return { gained: [], lost: [] };
  }
  const afterShock = reduceResourceGrantByClient(resources, 1);
  const gained = [];
  const lost = [];
  SHOCK_RESOURCE_ORDER.forEach((attribute) => {
    const before = resources[attribute] || 0;
    const after = afterShock[attribute] || 0;
    const removed = before - after;
    if (after > 0) gained.push([attribute, after]);
    if (removed > 0) lost.push([attribute, removed]);
  });
  return { gained, lost };
}

function listEffectEntries(effect) {
  if (!effect) return [];
  if (Array.isArray(effect.choose)) return effect.choose.filter(Boolean);
  return Array.isArray(effect) ? effect : [effect];
}

function resolveCombatEffect(effect) {
  if (!effect) return null;
  if (effect.id === "summonMinion") return effect.action || null;
  return effect;
}

function listCombatEffectEntries(effect) {
  return listEffectEntries(resolveCombatEffect(effect));
}

function delayedResourceGrantEntries(card) {
  return (card?.passiveEffects || [])
    .filter(
      (passive) =>
        passive?.id === "delayedResourceGrant" &&
        passive.attribute &&
        (passive.amount || 0) > 0,
    )
    .map((passive) => [passive.attribute, passive.amount]);
}

function persistGrantEntries(effect, effectId = "persist") {
  const entries = [];
  listEffectEntries(effect).forEach((entry) => {
    if (entry?.id !== effectId) return;
    if (entry.attribute && (entry.amount || 0) > 0) {
      entries.push([entry.attribute, entry.amount]);
      return;
    }
    resourceGrantEntries(entry.resources).forEach((pair) => entries.push(pair));
  });
  return entries;
}

function mergeSeatResources(seat) {
  const resources = seat?.resources || {};
  const persistent = seat?.persistentResources || {};
  return Object.fromEntries(
    ["strength", "dexterity", "wisdom", "neutral"].map((attribute) => [
      attribute,
      Math.max(0, resources[attribute] || 0) +
        Math.max(0, persistent[attribute] || 0),
    ]),
  );
}

const EMPTY_RESOURCE_BAG = {
  strength: 0,
  dexterity: 0,
  wisdom: 0,
  neutral: 0,
};

function emptyResourceBag() {
  return { ...EMPTY_RESOURCE_BAG };
}

function emptyPendingResourceReveal() {
  return {
    resources: emptyResourceBag(),
    persistent: emptyResourceBag(),
  };
}

function snapshotSeatResourceBags(seat) {
  return {
    resources: {
      strength: Math.max(0, Number(seat?.resources?.strength) || 0),
      dexterity: Math.max(0, Number(seat?.resources?.dexterity) || 0),
      wisdom: Math.max(0, Number(seat?.resources?.wisdom) || 0),
      neutral: Math.max(0, Number(seat?.resources?.neutral) || 0),
    },
    persistent: {
      strength: Math.max(0, Number(seat?.persistentResources?.strength) || 0),
      dexterity: Math.max(0, Number(seat?.persistentResources?.dexterity) || 0),
      wisdom: Math.max(0, Number(seat?.persistentResources?.wisdom) || 0),
      neutral: Math.max(0, Number(seat?.persistentResources?.neutral) || 0),
    },
  };
}

function subtractResourceBag(actual, pending) {
  return {
    strength: Math.max(0, (actual?.strength || 0) - (pending?.strength || 0)),
    dexterity: Math.max(
      0,
      (actual?.dexterity || 0) - (pending?.dexterity || 0),
    ),
    wisdom: Math.max(0, (actual?.wisdom || 0) - (pending?.wisdom || 0)),
    neutral: Math.max(0, (actual?.neutral || 0) - (pending?.neutral || 0)),
  };
}

function addPendingResourceGains(pending, gains) {
  const next = {
    resources: { ...pending.resources },
    persistent: { ...pending.persistent },
  };
  gains.forEach((gain) => {
    const bag = gain.persist ? "persistent" : "resources";
    next[bag][gain.attribute] = (next[bag][gain.attribute] || 0) + 1;
  });
  return next;
}

function removePendingResourceGains(pending, gains) {
  const next = {
    resources: { ...pending.resources },
    persistent: { ...pending.persistent },
  };
  gains.forEach((gain) => {
    const bag = gain.persist ? "persistent" : "resources";
    next[bag][gain.attribute] = Math.max(
      0,
      (next[bag][gain.attribute] || 0) - 1,
    );
  });
  return next;
}

function collectResourceBagGains(prevBags, nextBags) {
  const gains = [];
  if (!prevBags || !nextBags) return gains;
  ["strength", "dexterity", "wisdom", "neutral"].forEach((attribute) => {
    const normalDelta = Math.max(
      0,
      (nextBags.resources[attribute] || 0) - (prevBags.resources[attribute] || 0),
    );
    const persistDelta = Math.max(
      0,
      (nextBags.persistent[attribute] || 0) -
        (prevBags.persistent[attribute] || 0),
    );
    for (let index = 0; index < Math.min(normalDelta, 6); index += 1) {
      gains.push({ attribute, persist: false });
    }
    for (let index = 0; index < Math.min(persistDelta, 6); index += 1) {
      gains.push({ attribute, persist: true });
    }
  });
  return gains;
}

function latestResourceConversionId(entries) {
  return (Array.isArray(entries) ? entries : []).reduce(
    (max, entry) => Math.max(max, Number(entry?.id) || 0),
    0,
  );
}

function sumResourceConversions(entries, afterId, target) {
  const totals = emptyResourceBag();
  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    if (!entry || (Number(entry.id) || 0) <= afterId) return;
    if (target && entry.target !== target) return;
    Object.entries(entry.resources || {}).forEach(([attribute, amount]) => {
      if (!(attribute in totals)) return;
      totals[attribute] += Math.max(0, Number(amount) || 0);
    });
  });
  return totals;
}

function splitConvertedResourceGains(gains, converted) {
  const remaining = { ...converted };
  const flyerGains = [];
  const convertCounts = {};
  gains.forEach((gain) => {
    if (gain.persist && (remaining[gain.attribute] || 0) > 0) {
      remaining[gain.attribute] -= 1;
      convertCounts[gain.attribute] = (convertCounts[gain.attribute] || 0) + 1;
      return;
    }
    flyerGains.push(gain);
  });
  return { flyerGains, convertCounts };
}

function pendingResourceRevealHasValues(pending) {
  if (!pending) return false;
  return ["strength", "dexterity", "wisdom", "neutral"].some(
    (attribute) =>
      (pending.resources?.[attribute] || 0) > 0 ||
      (pending.persistent?.[attribute] || 0) > 0,
  );
}

function formatStackLabel(stack) {
  if (!stack) return "stack";
  return String(stack).charAt(0).toUpperCase() + String(stack).slice(1);
}

function formatAdventureMonsterEffectText(effect, options = {}) {
  if (!options.adventureMonsterEffects || !effect?.id) return null;
  const amount = effect.effectiveAmount ?? effect.amount ?? 0;
  if (effect.id === "bleed") {
    const damage = effect.effectiveDamage ?? effect.damage ?? 0;
    const bleedText = `Bleed ${amount}: monster takes 1 damage, then loses 1 stack at end of enemy round`;
    return damage > 0
      ? `Deal ${damage} ${effect.damageType || "physical"} · ${bleedText}`
      : bleedText;
  }
  if (effect.id === "poison") {
    const damage = effect.effectiveDamage ?? effect.damage ?? 0;
    const poisonText = `Poison ${amount}: monster takes 1 damage, then loses 1 stack at end of enemy round`;
    return damage > 0
      ? `Deal ${damage} ${effect.damageType || "physical"} · ${poisonText}`
      : poisonText;
  }
  if (effect.id === "burn") {
    return `Burn ${amount}: monster takes 1 damage, then loses 1 stack at end of enemy round`;
  }
  if (effect.id === "applyAilment") {
    const ailment = effect.ailment || effect.type || "ailment";
    const label =
      typeof ailment === "string" && ailment
        ? ailment.charAt(0).toUpperCase() + ailment.slice(1)
        : "Ailment";
    if (effect.target === "self") {
      return `${label} ${amount} to Self`;
    }
    return `${label} ${amount}`;
  }
  if (effect.id === "chill") {
    return `Chill ${amount}: monster deals 1 less damage, then loses 1 stack at end of enemy round`;
  }
  if (effect.id === "shock") {
    return `Shock ${amount}: monster deals 1 less damage, then loses 1 stack at end of enemy round`;
  }
  if (effect.id === "wither") {
    return `Wither ${amount}: at start of enemy round, monster loses current and max health equal to stacks; loses 1 stack at end of enemy round`;
  }
  if (effect.id === "hexExpendCost") {
    const level = Math.max(1, effect.effectiveLevel ?? options.level ?? 1);
    const reduction = Math.min(90, 50 + (level - 1) * 5);
    return `Hex: monster deals ${reduction}% less damage (rounded up) until end of enemy round`;
  }
  if (effect.id === "damageOpponent") {
    const damageType = effect.damageType || "physical";
    const baseAmount =
      effect.effectiveAmount ??
      (Array.isArray(effect.damageParts) && effect.damageParts.length
        ? effect.damageParts.reduce(
            (total, part) => total + (part.amount || 0),
            0,
          )
        : effect.amount || 0);
    const parts = [
      `Deal ${baseAmount} ${damageType}${formatElementalDamageAddText(
        getEffectElementalDamageDisplay(effect),
      )} damage`,
    ];
    if ((effect.effectiveBurn ?? effect.burn ?? 0) > 0) {
      parts.push(
        `Burn ${effect.effectiveBurn ?? effect.burn}: monster takes 1 damage, then loses 1 stack at end of enemy round`,
      );
    }
    if ((effect.effectiveChill ?? effect.chill ?? 0) > 0) {
      parts.push(
        `Chill ${effect.effectiveChill ?? effect.chill}: monster deals 1 less damage, then loses 1 stack at end of enemy round`,
      );
    }
    if ((effect.effectiveShock ?? effect.shock ?? 0) > 0) {
      parts.push(
        `Shock ${effect.effectiveShock ?? effect.shock}: monster deals 1 less damage, then loses 1 stack at end of enemy round`,
      );
    }
    return parts.join(" · ");
  }
  if (effect.id === "convertStacks" && effect.target !== "self") {
    const stackLabel = formatStackLabel(effect.stack);
    const consumeLabel =
      effect.consume === "all" ? "all" : String(effect.consume ?? 0);
    const multiplier = Number(effect.multiplier);
    const multText =
      Number.isFinite(multiplier) && multiplier > 0 && multiplier !== 1
        ? `${multiplier}x `
        : "";
    const roundText = effect.round === "up" ? " (round up)" : "";
    const damageType = effect.damageType || "physical";
    if (effect.convert === "damage" || effect.convert === "damageOpponent") {
      return `Consume ${consumeLabel} enemy ${stackLabel} · ${multText}${damageType} damage${roundText}`;
    }
    return `Consume ${consumeLabel} enemy ${stackLabel}`;
  }
  return null;
}

function getEffectElementalDamageDisplay(effect) {
  if (effect?.effectiveElementalDamage) return effect.effectiveElementalDamage;
  const extraHit = effect?.extraHit;
  const amount = Math.max(0, Math.floor(Number(extraHit?.amount) || 0));
  if (!extraHit?.damageType || !(amount > 0)) return null;
  return { [extraHit.damageType]: amount };
}

function formatElementalDamageAddText(elementalDamage) {
  if (!elementalDamage || typeof elementalDamage !== "object") return "";
  return Object.entries(elementalDamage)
    .filter(([, amount]) => (amount || 0) > 0)
    .map(([damageType, amount]) => ` + ${amount} ${damageType}`)
    .join("");
}

const IDENTITY_COST_TEXT_EXCLUDED_IDS = new Set(["ailmentFromIdentity", "repair"]);

function formatIdentityCostText(effect, options = {}) {
  const fromIdentity = Math.max(
    0,
    Math.floor(Number(effect?.costFromIdentity) || 0),
  );
  if (!(fromIdentity > 0)) return "";
  const attribute =
    options.identityAttribute || options.equipment?.chosenIdentity || null;
  const parts = [
    `${fromIdentity} ${attribute ? formatAttributeLabel(attribute) : "Identity"}`,
  ];
  if (!options.omitCost) {
    Object.entries(effect.cost || {}).forEach(([key, value]) => {
      const amount = Math.max(0, Math.floor(Number(value) || 0));
      if (amount > 0) parts.push(`${amount} ${formatAttributeLabel(key)}`);
    });
  }
  return `Pay ${parts.join(" + ")}`;
}

function getEffectDurabilityCost(effect, options = {}) {
  if (!effect || Array.isArray(effect) || Array.isArray(effect.choose)) {
    return 0;
  }
  if (effect.consumeDurabilityByIdentity) {
    const identity =
      options.identityAttribute ||
      options.equipment?.chosenIdentity ||
      (effect.identityEffect === "shock"
        ? "dexterity"
        : effect.identityEffect === "lightning"
          ? "wisdom"
          : null);
    if (!identity) return 0;
    return Math.max(
      0,
      Math.floor(
        Number(
          effect.consumeDurabilityByIdentity[identity] ??
            effect.consumeDurability,
        ) || 0,
      ),
    );
  }
  return Math.max(0, Math.floor(Number(effect.consumeDurability) || 0));
}

function effectDurabilityIsSeparateCost(effect) {
  return (
    !(Number(effect?.costFromIdentity) > 0) ||
    IDENTITY_COST_TEXT_EXCLUDED_IDS.has(effect.id)
  );
}

function getEffectPrefixDurabilityCost(effect, options = {}) {
  if (Array.isArray(effect)) {
    return effect.reduce(
      (sum, entry) => sum + getEffectPrefixDurabilityCost(entry, options),
      0,
    );
  }
  if (!effect || !effectDurabilityIsSeparateCost(effect)) return 0;
  return getEffectDurabilityCost(effect, options);
}

function formatDurabilityCostText(amount) {
  const n = Math.max(0, Math.floor(Number(amount) || 0));
  return n > 0 ? `−${n} Durability` : "";
}

function formatActivatedEffectText(effect, options = {}) {
  const { omitDurability = false, ...coreOptions } = options;
  const text = formatActivatedEffectTextCore(effect, coreOptions);
  if (!text || !effect?.id || options.omitPayment) return text;
  const costText = IDENTITY_COST_TEXT_EXCLUDED_IDS.has(effect.id)
    ? ""
    : formatIdentityCostText(effect, options);
  const durabilityText =
    omitDurability && effectDurabilityIsSeparateCost(effect)
      ? ""
      : formatDurabilityCostText(getEffectDurabilityCost(effect, options));
  const payment = [costText, durabilityText].filter(Boolean).join(" + ");
  return payment ? `${payment} → ${text}` : text;
}

function formatActivatedEffectTextCore(effect, options = {}) {
  if (!effect) return null;
  if (Array.isArray(effect.choose)) {
    const parts = effect.choose
      .map((entry) => formatActivatedEffectText(entry, options))
      .filter(Boolean);
    return parts.length ? parts.join(" OR ") : null;
  }
  if (Array.isArray(effect)) {
    const parts = effect
      .map((entry) => formatActivatedEffectText(entry, options))
      .filter(Boolean);
    return parts.length ? parts.join(" · ") : null;
  }
  if (!effect.id) return null;
  const adventureLine = formatAdventureMonsterEffectText(effect, options);
  if (adventureLine) return adventureLine;
  if (effect.id === "damageOpponent") {
    const damageType = effect.damageType || "physical";
    const parts = [];
    if (Array.isArray(effect.damageParts) && effect.damageParts.length) {
      const partText = effect.damageParts
        .map(
          (part) =>
            `${part.amount || 0} ${part.damageType || damageType}`,
        )
        .join(" + ");
      parts.push(
        effect.combineBeforeMitigation
          ? `Deal ${partText} damage (single hit)`
          : `Deal ${partText} damage`,
      );
    } else if (effect.consumeDiscardTop) {
      const max = Math.max(0, effect.consumeDiscardTop.max || 0);
      const per = Math.max(
        0,
        effect.consumeDiscardTop.amountPerCard ??
          effect.consumeDiscardTop.amount ??
          1,
      );
      const baseAmount = Math.max(0, effect.amount || 0);
      const removeText = `Remove up to ${max} cards from the top of your discard pile from the game`;
      const currentText =
        effect.effectiveAmount != null && effect.effectiveAmount !== baseAmount
          ? ` (currently ${effect.effectiveAmount})`
          : "";
      parts.push(
        baseAmount > 0
          ? `Deal ${baseAmount} ${damageType} damage. ${removeText} to deal ${per} additional damage for each card removed this way${currentText}`
          : `${removeText} to deal ${per} ${damageType} damage for each card removed this way${currentText}`,
      );
    } else {
      parts.push(
        `Deal ${
          effect.effectiveAmount ?? effect.amount ?? 0
        } ${damageType}${formatElementalDamageAddText(
          getEffectElementalDamageDisplay(effect),
        )} damage`,
      );
    }
    if (effect.unarmed != null) {
      const unarmedAmount =
        typeof effect.unarmed === "number"
          ? effect.unarmed
          : effect.unarmed.amount || 0;
      parts.push(`Unarmed ${unarmedAmount}`);
    }
    if (effect.loneWeapon != null) {
      const loneAmount =
        typeof effect.loneWeapon === "number"
          ? effect.loneWeapon
          : effect.loneWeapon.amount || 0;
      parts.push(`Lone Weapon\u00a0${loneAmount}`);
    }
    if (effect.equipmentAffinity) {
      const slot = effect.equipmentAffinity.slot || "equipment";
      parts.push(
        `+${effect.equipmentAffinity.amount || 0} ${damageType} damage with equipped ${slot}`,
      );
    }
    if (effect.equippedAttributeBonus) {
      const attribute = formatAttributeLabel(
        effect.equippedAttributeBonus.attribute || "attribute",
      );
      const only = effect.equippedAttributeBonus.only ? "-only" : "";
      parts.push(
        `+${effect.equippedAttributeBonus.amount || 0} per ${attribute}${only} equipment`,
      );
    }
    if (effect.ownerDiscardBonus) {
      const piles = Array.isArray(effect.ownerDiscardBonus.piles)
        ? effect.ownerDiscardBonus.piles
        : null;
      const pileLabel =
        piles && piles.length === 1 && piles[0] === "equipment"
          ? "per card in your equipment discard pile"
          : piles && piles.length === 1 && piles[0] === "ability"
            ? "per card in your ability discard pile"
            : "per card in your discard piles";
      parts.push(
        `+${effect.ownerDiscardBonus.amount || 0} ${pileLabel}`,
      );
    }
    if (effect.consumeDiscardTop && effect.damageParts?.length) {
      const max = Math.max(0, effect.consumeDiscardTop.max || 0);
      const per = Math.max(
        0,
        effect.consumeDiscardTop.amountPerCard ??
          effect.consumeDiscardTop.amount ??
          1,
      );
      parts.push(
        `remove up to ${max} cards from the top of your discard pile from the game to deal ${per} additional damage for each card removed this way`,
      );
    }
    if (effect.chargeDamage) {
      const chargePreview = resolveEffectChargeDamagePreview(
        effect,
        options.equipment,
      );
      const chargesPerDamage = Math.max(
        0,
        effect.chargeDamage.chargesPerDamage || 0,
      );
      const chargeLabel = chargePreview.label;
      const chargeSingular = chargePreview.singular;
      if (chargesPerDamage > 0) {
        if (options.equipment) {
          parts[0] = options.omitChargeCost
            ? `${chargePreview.damage} ${damageType} damage`
            : `Consume ${chargePreview.charges} ${chargeLabel}, ${chargePreview.damage} ${damageType} damage`;
        } else {
          parts[0] = `Deal 1 ${damageType} damage per ${chargesPerDamage} ${chargeLabel}`;
          if (
            !options.omitChargeCost &&
            (effect.chargeDamage.consume || effect.consumeCharges)
          ) {
            parts.push(
              effect.chargeDamage.consume === "all"
                ? `consume all ${chargeLabel}`
                : `consume ${chargeLabel}`,
            );
          }
        }
      } else {
        const perCharge = chargePreview?.perCharge ?? 0;
        if ((effect.amount || 0) <= 0) {
          if (options.equipment) {
            parts[0] = options.omitChargeCost
              ? `${chargePreview.damage} ${damageType} damage`
              : `Consume ${chargePreview.charges} ${chargeLabel}, ${chargePreview.damage} ${damageType} damage`;
          } else if (options.omitChargeCost) {
            parts[0] = effect.chargeDamage?.asHits
              ? `${perCharge} ${damageType} damage each`
              : `${perCharge} ${damageType} damage per ${chargeSingular}`;
          } else {
            parts[0] = `Deal ${perCharge} ${damageType} damage per ${chargeSingular}`;
            if (effect.chargeDamage.consume || effect.consumeCharges) {
              parts.push(`consume ${chargeLabel}`);
            }
          }
        } else {
          parts.push(`+${perCharge} ${damageType} per ${chargeSingular}`);
          if (
            !options.omitChargeCost &&
            (effect.chargeDamage.consume || effect.consumeCharges)
          ) {
            parts.push(`consume ${chargeLabel}`);
          }
        }
      }
    } else if (effect.consumeCharges && !options.omitChargeCost) {
      parts.push(`consume ${getChargeKindLabel(effect.charge)}`);
    }
    if (effect.ignoreArmor) parts.push("Ignore Armor");
    if (
      (effect.effectiveIgnorePhysicalArmor ?? effect.ignorePhysicalArmor ?? 0) >
      0
    ) {
      parts.push(
        `Ignore ${
          effect.effectiveIgnorePhysicalArmor ?? effect.ignorePhysicalArmor
        } Physical Armor`,
      );
    }
    if (effect.noHpDamage) {
      parts.push(
        "excess damage does not reduce health if opponent has any physical armor",
      );
    }
    if (effect.encore) parts.push("Encore");
    if ((effect.effectiveBurn ?? effect.burn ?? 0) > 0) {
      parts.push(`Burn ${effect.effectiveBurn ?? effect.burn}`);
    }
    if ((effect.effectiveChill ?? effect.chill ?? 0) > 0) {
      parts.push(`Chill ${effect.effectiveChill ?? effect.chill}`);
    }
    if ((effect.effectiveShock ?? effect.shock ?? 0) > 0) {
      parts.push(`Shock ${effect.effectiveShock ?? effect.shock}`);
    }
    if (effect.healFromDamage) {
      const fraction = Number(effect.healFromDamage.fraction);
      const pct =
        Number.isFinite(fraction) && fraction > 0
          ? Math.round(fraction * 100)
          : 0;
      const roundText =
        effect.healFromDamage.round === "up"
          ? " (round up)"
          : effect.healFromDamage.round === "nearest"
            ? " (round nearest)"
            : "";
      parts.push(`Heal ${pct}% of damage dealt${roundText}`);
    }
    if (!options.omitCost) {
      const costText = formatCost(effect.cost);
      if (costText) parts.push(`Cost ${costText}`);
    }
    return parts.join(" · ");
  }
  if (effect.id === "convertResource") {
    const from = formatAttributeLabel(effect.from || "neutral");
    const amount = effect.amount || 1;
    if (effect.to) {
      const to = formatAttributeLabel(effect.to);
      if (effect.persistent) {
        return `Convert ${amount} ${from} into ${amount}* ${to}`;
      }
      return `Convert ${amount} ${from} into ${amount} ${to}`;
    }
    const choices = (effect.choices || [])
      .map((attribute) => formatAttributeLabel(attribute))
      .filter(Boolean);
    const choiceText = choices.length ? choices.join("/") : "an attribute";
    return `Convert ${amount} ${from} into ${choiceText}`;
  }
  if (effect.id === "damageSelf") {
    if (effect.ignoreArmor) {
      return `Pay ${effect.amount || 0} health`;
    }
    const damageType = effect.damageType || "physical";
    const parts = [`Take ${effect.amount || 0} ${damageType} damage`];
    return parts.join(" · ");
  }
  if (effect.id === "bleed") {
    const parts = [];
    if ((effect.damage || 0) > 0) {
      parts.push(
        `Deal ${effect.damage} ${effect.damageType || "physical"} damage`,
      );
    }
    parts.push(`Bleed ${effect.amount || 0}`);
    return parts.join(" · ");
  }
  if (effect.id === "poison") {
    const parts = [];
    if ((effect.damage || 0) > 0) {
      parts.push(
        `Deal ${effect.damage} ${effect.damageType || "physical"} damage`,
      );
    }
    parts.push(`Poison ${effect.amount || 0}`);
    return parts.join(" · ");
  }
  if (effect.id === "convertStacks") {
    const stackLabel = formatStackLabel(effect.stack);
    const targetLabel = effect.target === "self" ? "self" : "opponent";
    const consumeLabel =
      effect.consume === "all" ? "all" : String(effect.consume ?? 0);
    const multiplier = Number(effect.multiplier);
    const amountPerStack = Number(effect.amountPerStack);
    const multText =
      Number.isFinite(multiplier) && multiplier > 0 && multiplier !== 1
        ? `${multiplier}x `
        : "";
    const roundText = effect.round === "up" ? " (round up)" : "";
    const damageType = effect.damageType || "physical";
    if (effect.convert === "damage" || effect.convert === "damageOpponent") {
      if (effect.amount != null) {
        return `Convert ${consumeLabel === "all" ? "" : `${consumeLabel} `}${targetLabel} ${stackLabel} to ${effect.amount} ${damageType} damage${multText ? ` · ${multText}consumed${roundText}` : ""}`;
      }
      return `Consume ${consumeLabel} ${targetLabel} ${stackLabel} · ${multText}${damageType} damage${roundText}`;
    }
    if (effect.convert === "heal" || effect.convert === "healSelf") {
      const healPer =
        Number.isFinite(amountPerStack) && amountPerStack > 0
          ? amountPerStack
          : Number.isFinite(multiplier) && multiplier > 0
            ? multiplier
            : 1;
      return `Consume up to ${consumeLabel} ${targetLabel} ${stackLabel} to Heal ${healPer} per stack`;
    }
    return `Consume ${consumeLabel} ${targetLabel} ${stackLabel}`;
  }
  if (effect.id === "wither") {
    return `Wither ${effect.amount || 0}`;
  }
  if (effect.id === "burn") {
    return `Burn ${effect.amount || 0}`;
  }
  if (effect.id === "applyAilment") {
    const ailment = effect.ailment || effect.type || "ailment";
    const label =
      typeof ailment === "string" && ailment
        ? ailment.charAt(0).toUpperCase() + ailment.slice(1)
        : "Ailment";
    const target =
      effect.target === "self"
        ? "Self"
        : effect.target === "opponent"
          ? "Opponent"
          : null;
    return target
      ? `${label} ${effect.amount || 0} (${target})`
      : `${label} ${effect.amount || 0}`;
  }
  if (effect.id === "chill") {
    return `Chill ${effect.amount || 0}`;
  }
  if (effect.id === "shock") {
    return `Shock ${effect.amount || 0}`;
  }
  if (effect.id === "healSelf") {
    const healAmount = effect.effectiveAmount ?? effect.amount ?? 0;
    const cleanseAmount = effect.removePoison || 0;
    const parts = [];
    if (healAmount > 0 || cleanseAmount <= 0) {
      parts.push(`Heal ${healAmount}`);
    }
    if (cleanseAmount > 0) {
      parts.push(`Cleanse Poison ${cleanseAmount}`);
    }
    if (!options.omitCost) {
      const costText = formatCost(effect.cost);
      if (costText) parts.push(`Cost ${costText}`);
    }
    return parts.join(" · ");
  }
  if (effect.id === "summonMinion") {
    const name = effect.name || "Minion";
    if (options.omitMinionAction) return `Summon ${name}`;
    const hp = effect.effectiveMinionHp ?? effect.hp ?? 0;
    const actionText = formatActivatedEffectText(effect.action, options);
    const parts = [`Summon ${name} (${hp} HP)`];
    if (actionText) parts.push(`Expend: ${actionText}`);
    return parts.join(" · ");
  }
  if (effect.id === "activateAura") {
    const parts = ["Activate Aura"];
    (effect.passiveEffects || []).forEach((passive) => {
      if (!passive) return;
      if (passive.id === "maxHealthAdd") {
        parts.push(`+${passive.amount || 0} max and current health`);
        return;
      }
      if (passive.id === "auraPhysicalDamageAdd") {
        parts.push(`+${passive.amount || 0} physical damage`);
        return;
      }
      if (passive.id === "auraRangedCritical") {
        const bonus = Math.max(0, Math.floor(Number(passive.amount) || 0));
        parts.push(
          bonus > 0
            ? `Ranged attacks are Critical Hits (+${bonus} damage)`
            : "Ranged attacks are Critical Hits",
        );
        return;
      }
      if (passive.id === "auraAbilityCostReduce") {
        const amount = Math.max(0, Math.floor(Number(passive.amount) || 0));
        const minCost =
          passive.minCost == null ? 1 : Math.max(0, Number(passive.minCost) || 0);
        parts.push(`Wisdom ability costs -${amount} (min ${minCost})`);
        return;
      }
      if (passive.id === "auraEquipmentActionCostReduce") {
        const amount = Math.max(0, Math.floor(Number(passive.amount) || 0));
        const minCost =
          passive.minCost == null ? 1 : Math.max(0, Number(passive.minCost) || 0);
        parts.push(`Wisdom equipment action costs -${amount} (min ${minCost})`);
      }
    });
    return parts.join(" · ");
  }
  if (effect.id === "maxHealthIncrease") {
    return `Max Health ${effect.amount || 0}`;
  }
  if (effect.id === "convertPhysicalArmorToDamage") {
    const scale = resolveArmorDamageMultiplier(effect);
    const scaleText =
      scale === 1 ? "" : ` · ${scale}x consumed (round up)`;
    return `Convert Physical Armor to ${effect.amount || 0} physical damage${scaleText}`;
  }
  if (effect.id === "convertMagicArmorToDamage") {
    return `Convert Magic Armor to ${effect.amount || 0} magical damage`;
  }
  if (effect.id === "hexExpendCost") {
    const disableExpend =
      effect.effectiveDisableExpend ?? Boolean(effect.modify?.disableExpend);
    const scaledCostAdd = effect.effectiveSlottedAbilityCostAdd;
    const parts = [];
    if (disableExpend) parts.push("disable expend");
    const costAdd = scaledCostAdd;
    const costText =
      costAdd && typeof costAdd === "object"
        ? Object.entries(costAdd)
            .filter(([, value]) => value > 0)
            .map(([attribute, value]) => `+${value} ${attribute}`)
            .join(", ")
        : "";
    if (costText) {
      parts.push(`${costText} to slotted abilities`);
    } else if (scaledCostAdd == null && effect.modify?.slottedAbilityCostAdd) {
      parts.push("extra levels add Neutral to slotted abilities");
    }
    return `Hex opponent equipment (${parts.join(" · ") || "curse"})`;
  }
  if (effect.id === "temporaryPhysicalArmor") {
    if (effect.chargeDamage) {
      const chargePreview = resolveEffectChargeDamagePreview(
        effect,
        options.equipment,
      );
      const perCharge = chargePreview?.perCharge ?? 0;
      if (options.equipment) {
        return options.omitChargeCost
          ? `Physical Armor ${chargePreview.damage}`
          : `Consume ${chargePreview.charges} ${chargePreview.label}, Physical Armor ${chargePreview.damage}`;
      }
      const consumeText =
        effect.chargeDamage.consume || effect.consumeCharges
          ? ` (consume ${chargePreview.label})`
          : "";
      return `Physical Armor ${perCharge} per ${chargePreview.singular}${consumeText}`;
    }
    return `Physical Armor ${
      effect.effectiveAmount ?? effect.amount ?? 0
    }`;
  }
  if (effect.id === "temporaryMagicArmor") {
    return `Magic Armor ${effect.effectiveAmount ?? effect.amount ?? 0}`;
  }
  if (effect.id === "temporaryBleedResistance") {
    return `Bleed Resistance ${effect.amount || 0}`;
  }
  if (
    effect.id === "temporaryThorns" ||
    effect.id === "gainThorns" ||
    effect.id === "turnStartThorns"
  ) {
    const amount = Math.max(1, effect.amount || 1);
    if (effect.id === "turnStartThorns") {
      return `Turn Start Thorns ${amount}`;
    }
    return `Gain ${amount} Thorns`;
  }
  if (effect.id === "nextMeleePhysicalDamageAdd") {
    const amount = Math.max(1, effect.amount || 1);
    return `Next melee attack +${amount} physical damage this turn`;
  }
  if (effect.id === "dodgeRoll") {
    const amount = Math.max(1, effect.amount || 1);
    return amount > 1
      ? `Gain ${amount} Dodge ${DODGE_MITIGATION_PERCENT}%`
      : `Dodge ${DODGE_MITIGATION_PERCENT}%`;
  }
  if (effect.id === "shadow") {
    return `Shadow ${Math.max(1, effect.amount || 1)}`;
  }
  if (effect.id === "regeneration") {
    const parts = [
      `Regeneration ${effect.effectiveAmount ?? effect.amount ?? 0}`,
    ];
    if (!options.omitCost) {
      const costText = formatCost(effect.cost);
      if (costText) parts.push(`Cost ${costText}`);
    }
    return parts.join(" · ");
  }
  if (effect.id === "grantResources") {
    const grants = resourceGrantEntries(effect.resources);
    if (!grants.length) return "Gain resources";
    return `Gain ${grants
      .map(([attribute, value]) => `${value} ${attribute}`)
      .join(", ")}`;
  }
  if (effect.id === "grantXp") {
    const amount = effect.amount ?? 1;
    return `Gain ${amount} XP`;
  }
  if (effect.id === "grantSlottedAttributeResource") {
    const amount = effect.effectiveAmount ?? effect.amount ?? 1;
    const attribute = effect.effectiveAttribute;
    if (attribute) {
      return `Gain ${amount} ${formatAttributeLabel(attribute)} (from slotted ability)`;
    }
    return `Gain ${amount} resource matching slotted ability attribute (Neutral if empty, Neutral, or mixed)`;
  }
  if (effect.id === "delay") {
    const grants = persistGrantEntries(effect, "delay");
    if (!grants.length) return "Gain delayed resources";
    return `Delayed ${grants
      .map(([attribute, value]) => `${value} ${attribute}`)
      .join(", ")} (next turn)`;
  }
  if (effect.id === "persist") {
    const grants = persistGrantEntries(effect);
    if (!grants.length) return "Gain persistent resources";
    return grants
      .map(([attribute, value]) => `${value}* ${attribute}`)
      .join(", ");
  }
  if (effect.id === "temporaryEncore") {
    return "Next ability played gains Encore";
  }
  if (effect.id === "nimbleFingers") {
    return "Nimble Fingers";
  }
  if (effect.id === "chooseIdentity") {
    return "Choose Identity";
  }
  if (effect.id === "enhanceNextResourceExpend") {
    const amount = effect.amount || 1;
    return `Next resource expend +${amount} of host attribute (Neutral if mixed or Neutral). Once per turn.`;
  }
  if (effect.id === "temporarySlottedAbilityLevelAdd") {
    const parts = ["Slotted"];
    parts.push(...formatPassiveMatchParts(effect.match));
    parts.push("Ability Level");
    parts.push(`+${effect.amount || 1}`);
    parts.push("until end of turn");
    return parts.join(" ");
  }
  if (effect.id === "temporarySlottedAbilityPoisonAdd") {
    const parts = ["Slotted"];
    parts.push(
      ...formatPassiveMatchParts(
        effect.match || { type: "poison", kind: "active" },
      ),
    );
    parts.push("Abilities");
    parts.push(`+${effect.amount || 1}`);
    parts.push("Poison until end of turn");
    return parts.join(" ");
  }
  if (effect.id === "temporarySlottedAbilityDamageAdd") {
    const nextActivation = effect.duration === "nextActivation";
    const parts = ["Slotted"];
    const matchParts = formatPassiveMatchParts(effect.match);
    const damageTypeLabel = formatSlottedDamageTypeLabel(effect).replace(
      / Damage$/,
      "",
    );
    if (!matchParts.includes(damageTypeLabel)) parts.push(damageTypeLabel);
    parts.push(...matchParts);
    parts.push(nextActivation ? "Ability's next activation" : "Abilities");
    parts.push(`+${Math.max(1, effect.amount || 1)}`);
    parts.push(formatSlottedDamageTypeLabel(effect));
    if (!nextActivation) parts.push("until end of turn");
    return parts.join(" ");
  }
  if (effect.id === "temporarySlottedAbilityCostReduce") {
    const parts = ["Slotted"];
    parts.push(...formatPassiveMatchParts(effect.match));
    parts.push("Abilities");
    const amount = Math.max(1, effect.amount || 1);
    if (effect.attribute) {
      parts.push(
        `-${amount} ${formatAttributeLabel(effect.attribute)} Cost`,
      );
    } else {
      parts.push(`-${amount} Cost`);
    }
    if (effect.minCost != null) {
      parts.push(
        `(minimum ${Math.max(0, Math.floor(Number(effect.minCost) || 0))})`,
      );
    }
    parts.push("until end of turn");
    return parts.join(" ");
  }
  if (effect.id === "sharpen") {
    const amount = Math.max(1, effect.amount || 1);
    return effect.forExpend
      ? `Sharpen ${amount} (expend)`
      : `Sharpen ${amount}`;
  }
  if (effect.id === "hone") {
    return `Hone ${Math.max(1, effect.amount || 1)}`;
  }
  if (effect.id === "fletch") {
    return `Fletch ${Math.max(1, effect.amount || 1)}`;
  }
  if (effect.id === "research") {
    return "Research";
  }
  if (effect.id === "repair") {
    if (effect.costFromIdentity != null && !Object.keys(effect.cost || {}).length) {
      return `Repair Identity ${Math.max(1, Math.floor(Number(effect.costFromIdentity) || 1))}`;
    }
    return "Repair";
  }
  if (effect.id === "durability") {
    return `Durability ${Math.max(1, effect.amount || 1)}`;
  }
  if (effect.id === "enableEncore") {
    return "Encore";
  }
  if (effect.id === "fromDiscard") {
    const amount = effect.amount || 1;
    const pick = effect.pick === "random" ? "random " : "";
    const destination =
      effect.destination === "deck"
        ? "into the deck"
        : effect.destination || "into the deck";
    return `From Discard: shuffle ${amount} ${pick}card ${destination}`;
  }
  if (effect.id === "returnToEquipmentDeck") {
    return options.equipment?.token
      ? "Remove Permanently"
      : "Return to bottom of Deck";
  }
  if (effect.id === "discardHandThenDraw") {
    const draw = effect.draw ?? effect.amount ?? 1;
    return `Discard and Draw ${draw} ability`;
  }
  if (effect.id === "discardSelfThenDraw") {
    const draw = effect.draw ?? effect.amount ?? 1;
    const deck = effect.deck === "equipment" ? "equipment" : "ability";
    return `Discard Self and Draw ${draw} ${deck}`;
  }
  if (effect.id === "draw") {
    const amount = effect.amount ?? effect.draw ?? 1;
    if (effect.deck === "equipment" || effect.deck === "ability") {
      return `Draw ${amount} ${effect.deck}`;
    }
    return `Draw ${amount}`;
  }
  if (effect.id === "restock") {
    return `Restock ${Math.max(1, effect.amount || 1)}`;
  }
  if (effect.id === "gainCharges") {
    const label = getChargeKindLabel(effect.charge);
    if (effect.amountFromSlottedAbilities) {
      if (options.equipment || options.slottedAbilityCount != null) {
        const amount =
          options.slottedAbilityCount != null
            ? Math.max(0, options.slottedAbilityCount)
            : countSlottedAbilitiesForEquipment(
                options.equipment,
                options.seat,
              );
        return `Gain ${amount} ${getChargeKindLabel(effect.charge, amount)}`;
      }
      return `Gain ${label} equal to slotted abilities`;
    }
    const amount = effect.amount || 0;
    return `Gain ${amount} ${getChargeKindLabel(effect.charge, amount)}`;
  }
  if (effect.id === "addChargeToWeapon") {
    const amount = Math.max(1, effect.amount || 1);
    return `Add ${amount} ${getChargeKindLabel(effect.charge, amount)} to an equipped Weapon`;
  }
  if (effect.id === "restoreWeaponDurability") {
    return `Restore ${Math.max(1, effect.amount || 1)} Durability on equipped weapon`;
  }
  if (effect.id === "increaseCombo") {
    return `Increase current Combo by ${Math.max(1, effect.amount || 1)}`;
  }
  if (effect.id === "refreshWeapon") {
    return "Refresh equipped weapon";
  }
  if (effect.id === "meleeWeaponDamageAdd") {
    const amount = Math.max(1, effect.amount || 1);
    return `Equipped melee weapon damage +${amount} until end of turn`;
  }
  if (effect.id === "nextWeaponExpendCostReduce") {
    const amount = Math.max(1, effect.amount || 1);
    const minCost =
      effect.minCost == null ? 0 : Math.max(0, effect.minCost || 0);
    return `Next weapon expend cost -${amount} (min ${minCost})`;
  }
  if (effect.id === "nextActiveAbilityCostReduce") {
    const amount = Math.max(1, effect.amount || 1);
    const minCost =
      effect.minCost == null ? 0 : Math.max(0, effect.minCost || 0);
    return `Next active ability cost -${amount} (min ${minCost})`;
  }
  if (effect.id === "nextAttrCostReduce") {
    const attribute = formatAttributeLabel(effect.attribute || "wisdom");
    return `Next ${attribute} Active Ability cost -${Math.max(
      1,
      effect.amount || 1,
    )} (min 1)`;
  }
  if (effect.id === "temporaryArmorFromIdentity") {
    const amount = effect.effectiveAmount ?? effect.amount ?? 1;
    const identity = options.equipment?.chosenIdentity;
    const armor = effect.identityArmor;
    if (identity === "strength" || armor === "physical") {
      return `Physical Armor ${amount}`;
    }
    if (identity === "wisdom" || armor === "magic") {
      return `Magic Armor ${amount}`;
    }
    if (identity === "dexterity" || armor === "dodge") {
      return "Dodge";
    }
    const attrs = listEquipmentIdentityAttributes(options.equipment);
    const parts = [];
    if (!attrs.length || attrs.includes("strength")) {
      parts.push(`Physical Armor ${amount} (STR)`);
    }
    if (!attrs.length || attrs.includes("dexterity")) {
      parts.push("Dodge (DEX)");
    }
    if (!attrs.length || attrs.includes("wisdom")) {
      parts.push(`Magic Armor ${amount} (WIS)`);
    }
    return parts.join(" OR ");
  }
  if (effect.id === "attackFromIdentity") {
    const amount = effect.effectiveAmount ?? effect.amount ?? 1;
    const identity = options.equipment?.chosenIdentity;
    const identityEffect = effect.identityEffect;
    const dexDur =
      effect.consumeDurabilityByIdentity?.dexterity ??
      effect.consumeDurability ??
      0;
    const wisDur =
      effect.consumeDurabilityByIdentity?.wisdom ??
      effect.consumeDurability ??
      0;
    if (identity === "dexterity" || identityEffect === "shock") {
      return `Shock ${amount}`;
    }
    if (identity === "wisdom" || identityEffect === "lightning") {
      return `${amount} lightning damage`;
    }
    const withDurability = (durability, text) =>
      durability > 0 ? `${formatDurabilityCostText(durability)} → ${text}` : text;
    return `${withDurability(dexDur, `Shock ${amount}`)} if DEX OR ${withDurability(wisDur, `${amount} lightning damage`)} if WIS`;
  }
  if (effect.id === "ailmentFromIdentity") {
    const amount = effect.effectiveAmount ?? effect.amount ?? 1;
    const cost = Math.max(1, effect.costFromIdentity || 1);
    const identity = options.equipment?.chosenIdentity;
    const identityEffect = effect.identityEffect;
    const withPayment = (label, text) =>
      options.omitPayment ? text : `Pay ${cost} ${label}: ${text}`;
    if (identity === "strength" || identityEffect === "burn") {
      return withPayment("STR", `Burn ${amount}`);
    }
    if (identity === "dexterity" || identityEffect === "shock") {
      return withPayment("DEX", `Shock ${amount}`);
    }
    if (identity === "wisdom" || identityEffect === "chill") {
      return withPayment("WIS", `Chill ${amount}`);
    }
    return `Pay ${cost} STR: Burn ${amount} OR Pay ${cost} DEX: Shock ${amount} OR Pay ${cost} WIS: Chill ${amount}`;
  }
  if (effect.id === "identityExpend") {
    const identity = options.equipment?.chosenIdentity;
    if (identity && effect.byIdentity?.[identity]) {
      return formatActivatedEffectText(effect.byIdentity[identity], {
        ...options,
        identityAttribute: identity,
      });
    }
    if (Array.isArray(effect.identityEffects) && effect.identityEffects.length) {
      return formatActivatedEffectText(effect.identityEffects, options);
    }
    if (effect.byIdentity) {
      const parts = Object.entries(effect.byIdentity)
        .map(([attribute, entry]) => {
          const text = formatActivatedEffectText(entry, {
            ...options,
            identityAttribute: attribute,
          });
          if (!text) return null;
          return `${formatAttributeLabel(attribute)}: ${text}`;
        })
        .filter(Boolean);
      if (parts.length) return parts.join(" OR ");
    }
    return "Identity Expend";
  }
  if (effect.id === "temporaryFireResistance") {
    return `Gain ${effect.amount || 1} Fire Resistance`;
  }
  if (effect.id === "temporaryColdResistance") {
    return `Gain ${effect.amount || 1} Cold Resistance`;
  }
  if (effect.id === "temporaryLightningResistance") {
    return `Gain ${effect.amount || 1} Lightning Resistance`;
  }
  if (effect.id === "temporarySlottedAbilityOutputAdd") {
    const attribute = effect.match?.attribute
      ? `${formatAttributeLabel(effect.match.attribute)} `
      : "";
    const kind = effect.match?.kind === "active" ? "Active " : "";
    return `Slotted ${attribute}${kind}Ability Output +${
      effect.amount || 1
    } until end of turn`;
  }
  if (effect.id === "spendChargesForResources") {
    const per = Math.max(1, effect.chargesPerResource || 3);
    const label = getChargeKindLabel(effect.charge, per);
    return `Spend ${per} ${label} each for Strength, Dexterity, or Wisdom`;
  }
  if (effect.id === "distantConnection") {
    const weaponType =
      typeof effect.weaponType === "string" ? effect.weaponType.trim() : "";
    const typeLabel = weaponType ? formatAttributeLabel(weaponType) : "weapon";
    const amount = Math.max(1, effect.amount || 1);
    return `Distant Connection: next ${typeLabel} expend damage +${amount} this turn`;
  }
  return effect.id;
}

function formatActivatedEffectTextWithoutPersist(effect, options = {}) {
  if (!effect) return null;
  if (Array.isArray(effect.choose)) {
    const parts = effect.choose
      .filter((entry) => entry?.id !== "persist")
      .map((entry) => formatActivatedEffectText(entry, options))
      .filter(Boolean);
    return parts.length ? parts.join(" OR ") : null;
  }
  if (Array.isArray(effect)) {
    const parts = effect
      .filter((entry) => entry?.id !== "persist")
      .map((entry) => formatActivatedEffectText(entry, options))
      .filter(Boolean);
    return parts.length ? parts.join(" · ") : null;
  }
  if (effect.id === "persist") return null;
  return formatActivatedEffectText(effect, options);
}

const IDENTITY_DEPENDENT_EFFECT_IDS = new Set([
  "identityExpend",
  "temporaryArmorFromIdentity",
  "attackFromIdentity",
  "ailmentFromIdentity",
]);

function effectDependsOnIdentity(effect) {
  if (!effect || Array.isArray(effect?.choose)) return false;
  return listEffectEntries(effect).some(
    (entry) =>
      entry &&
      (IDENTITY_DEPENDENT_EFFECT_IDS.has(entry.id) ||
        entry.costFromIdentity != null ||
        Boolean(entry.consumeDurabilityByIdentity)),
  );
}

function listEquipmentIdentityModes(card) {
  if (card?.onEquip?.id !== "chooseIdentity") return [];
  if (card.chosenIdentity) return [card.chosenIdentity];
  return listEquipmentIdentityAttributes(card);
}

function getIdentityCostAmount(effect) {
  if (effect?.costFromIdentity == null) return 0;
  return Math.max(1, Math.floor(Number(effect.costFromIdentity) || 1));
}

function resolveIdentityEffectEntries(effect, attribute) {
  return listEffectEntries(effect).flatMap((entry) => {
    if (!entry) return [];
    if (entry.id === "identityExpend") {
      return resolveIdentityEffectEntries(
        entry.byIdentity?.[attribute] ?? entry.identityEffects ?? null,
        attribute,
      );
    }
    return [entry];
  });
}

function listIdentityModeActivatedFaceLines(effect, card) {
  if (!effectDependsOnIdentity(effect)) return null;
  const modes = listEquipmentIdentityModes(card);
  if (!modes.length) return null;
  const showIdentity = !card.chosenIdentity;
  return modes
    .map((attribute) => {
      const entries = resolveIdentityEffectEntries(effect, attribute).filter(
        (entry) =>
          entry?.id && entry.id !== "persist" && entry.id !== "grantResources",
      );
      if (!entries.length) return null;
      const options = {
        equipment: { ...card, chosenIdentity: attribute },
        identityAttribute: attribute,
      };
      const cost = {};
      let durabilityCost = 0;
      entries.forEach((entry) => {
        const fromIdentity = getIdentityCostAmount(entry);
        if (fromIdentity > 0) {
          cost[attribute] = (cost[attribute] || 0) + fromIdentity;
        }
        Object.entries(entry.cost || {}).forEach(([key, value]) => {
          const amount = Math.max(0, Math.floor(Number(value) || 0));
          if (amount > 0) cost[key] = (cost[key] || 0) + amount;
        });
        durabilityCost += getEffectDurabilityCost(entry, options);
      });
      const text = entries
        .map((entry) =>
          formatActivatedEffectText(entry, {
            ...options,
            omitCost: true,
            omitChargeCost: true,
            omitPayment: true,
          }),
        )
        .filter(Boolean)
        .join(" · ");
      if (!text) return null;
      return {
        cost: costEntries(cost),
        chargeCost:
          entries.map(getEffectConsumeChargeCost).find(Boolean) || 0,
        durabilityCost,
        text,
        outputDelta: resolveActivatedEffectOutputDelta(entries),
        identity: showIdentity ? attribute : null,
        expend: true,
      };
    })
    .filter(Boolean);
}

function listActivatedEffectFaceLines(effect, options = {}) {
  const identityLines = listIdentityModeActivatedFaceLines(
    effect,
    options.equipment,
  );
  if (identityLines) return identityLines;
  const isChoice = Array.isArray(effect?.choose);
  const entries = listEffectEntries(effect).filter((entry) =>
    // A choice option may itself be a list of effects (all resolve together).
    isChoice && Array.isArray(entry)
      ? entry.some(Boolean)
      : entry?.id && entry.id !== "persist" && entry.id !== "grantResources",
  );
  if (!entries.length) return [];
  const anyCost = entries.some(
    (entry) =>
      costEntries(entry.cost).length > 0 ||
      getEffectConsumeChargeCost(entry),
  );
  const splitChoose = isChoice && entries.length > 0;
  if (splitChoose || anyCost) {
    return entries
      .map((entry) =>
        Array.isArray(entry)
          ? {
              cost: [],
              chargeCost: 0,
              durabilityCost: 0,
              text: entry
                .filter(Boolean)
                .map((part) =>
                  formatActivatedEffectText(part, { omitCost: true, ...options }),
                )
                .filter(Boolean)
                .join(" + "),
              outputDelta: 0,
              expend: true,
            }
          : {
              cost: costEntries(entry.cost),
              chargeCost: getEffectConsumeChargeCost(entry),
              durabilityCost: getEffectPrefixDurabilityCost(entry, options),
              text: formatActivatedEffectText(entry, {
                omitCost: true,
                omitChargeCost: true,
                omitDurability: true,
                ...options,
              }),
              outputDelta: Math.floor(Number(entry?.outputDelta) || 0),
              expend: true,
            },
      )
      .filter((entry) => entry.text);
  }
  const { selfDamage, effect: outputEntries } = splitSelfDamageCost(entries);
  const textSource =
    entries.length === 1
      ? entries[0]
      : entries.length > 1
        ? outputEntries
        : effect;
  const text = formatActivatedEffectTextWithoutPersist(textSource, {
    ...options,
    omitDurability: true,
  });
  const deltaSource = entries.length === 1 ? entries[0] : outputEntries;
  return text
    ? [
        {
          cost: [],
          chargeCost: 0,
          durabilityCost: getEffectPrefixDurabilityCost(deltaSource, options),
          selfDamage,
          text,
          outputDelta: resolveActivatedEffectOutputDelta(deltaSource),
        },
      ]
    : [];
}

function resolveActivatedEffectOutputDelta(effect) {
  if (!effect) return 0;
  if (Array.isArray(effect)) {
    const withDelta = effect.find(
      (entry) => Math.floor(Number(entry?.outputDelta) || 0) !== 0,
    );
    return Math.floor(Number(withDelta?.outputDelta) || 0);
  }
  return Math.floor(Number(effect.outputDelta) || 0);
}

const ATTR_SORT_ORDER = {
  strength: 0,
  dexterity: 1,
  wisdom: 2,
  neutral: 3,
};

const CARD_CATEGORY_SORT_ORDER = {
  equipment: 0,
  ability: 1,
  aspect: 2,
};

const SLOT_SORT_ORDER = {
  oneHandedWeapon: 0,
  twoHandedWeapon: 1,
  offHand: 2,
  head: 3,
  hands: 4,
  body: 5,
  feet: 6,
  trinket: 7,
  weapon: 0,
  armor: 3,
};

const EFFECT_FILTER_LABELS = {
  damageOpponent: "Damage",
  damageSelf: "Damage Self",
  healSelf: "Heal",
  maxHealthIncrease: "Max Health",
  maxHealthAdd: "Equipped Max Health",
  dodgeRoll: "Dodge",
  bleed: "Bleed",
  poison: "Poison",
  convertStacks: "Convert Stacks",
  applyAilment: "Apply Ailment",
  burn: "Burn",
  chill: "Chill",
  shock: "Shock",
  wither: "Wither",
  increasePoison: "Increase Poison",
  slottedActivePoisonAdd: "Slotted Poison",
  physicalArmor: "Physical Armor",
  magicArmor: "Magic Armor",
  fireResistance: "Fire Resistance",
  coldResistance: "Cold Resistance",
  lightningResistance: "Lightning Resistance",
  distantConnection: "Distant Connection",
  refresh: "Refresh",
  fastHands: "Fast Hands",
  swiftFeet: "Swift Feet",
  armorBreaker: "Armor Breaker",
  parry: "Parry",
  identityExpendRequiresMatchingActive: "Identity Match",
  charges: "Charges",
  addCharge: "Add Charge",
  thorns: "Thorns",
  gainThorns: "Thorns",
  turnStartThorns: "Thorns",
  temporaryThorns: "Thorns",
  kaleidoscopic: "Kaleidoscopic",
  nextMeleePhysicalDamageAdd: "Next Melee Damage",
  temporaryPhysicalArmor: "Temporary Physical Armor",
  temporaryMagicArmor: "Temporary Magic Armor",
  temporaryBleedResistance: "Temporary Bleed Resistance",
  convertPhysicalArmorToDamage: "Convert Physical Armor",
  convertMagicArmorToDamage: "Convert Magic Armor",
  onEquip: "On Equip",
  onExpend: "On Expend",
  onSlot: "On Slot",
  onDiscardFromEquippedSlot: "On Discard (Equipped)",
  onDiscardFromHand: "On Discard (Hand)",
  hexExpendCost: "Hex Equipment",
  curse: "Curse",
  sacrifice: "Sacrifice",
  dark: "Dark",
  magical: "Magical",
  regeneration: "Regeneration",
  grantResources: "Grant Resources",
  grantXp: "Grant XP",
  grantSlottedAttributeResource: "Slotted Attribute Resource",
  persist: "Persist",
  temporaryEncore: "Temporary Encore",
  nimbleFingers: "Nimble Fingers",
  enhanceNextResourceExpend: "Enhance Attributes",
  temporarySlottedAbilityLevelAdd: "Temporary Slotted Ability Level",
  temporarySlottedAbilityPoisonAdd: "Temporary Slotted Poison",
  temporarySlottedAbilityDamageAdd: "Slotted Damage",
  temporarySlottedAbilityCostReduce: "Temporary Slotted Cost Reduce",
  temporarySlottedAbilityOutputAdd: "Slotted Output",
  sharpen: "Sharpen",
  hone: "Hone",
  fletch: "Fletch",
  research: "Research",
  repair: "Repair",
  durability: "Durability",
  enableEncore: "Encore",
  nextResourceExpendAdd: "Next Resource Expend",
  fromDiscard: "From Discard",
  returnToEquipmentDeck: "Return to Bottom of Deck",
  discardHandThenDraw: "Discard and Draw",
  discardSelfThenDraw: "Discard Self and Draw",
  draw: "Draw",
  restock: "Restock",
  gainCharges: "Gain Charges",
  addChargeToWeapon: "Charge Weapon",
  restoreWeaponDurability: "Restore Weapon Durability",
  increaseCombo: "Increase Combo",
  refreshWeapon: "Refresh Weapon",
  meleeWeaponDamageAdd: "Melee Weapon Damage",
  nextWeaponExpendCostReduce: "Next Weapon Cost",
  nextActiveAbilityCostReduce: "Next Active Cost",
  nextAttrCostReduce: "Next Ability Cost",
  temporaryArmorFromIdentity: "Identity Armor",
  attackFromIdentity: "Identity Attack",
  identityExpend: "Identity Expend",
  ailmentFromIdentity: "Identity Ailment",
  temporaryFireResistance: "Fire Resistance",
  temporaryColdResistance: "Cold Resistance",
  temporaryLightningResistance: "Lightning Resistance",
  spendChargesForResources: "Spend Charges for Resources",
  convertResource: "Convert Resource",
  equipmentAffinity: "Equipment Affinity",
  equippedAttributeBonus: "Equipped Attribute",
  ownerDiscardBonus: "Discard Bonus",
  consumeDiscardTop: "Exile Discard Top",
  unarmed: "Unarmed",
  loneWeapon: "Lone Weapon",
  parry: "Parry",
  physicalDamageAdd: "Physical Damage",
  magicalDamageAdd: "Magical Damage",
  fireDamageAdd: "Fire Damage",
  coldDamageAdd: "Cold Damage",
  lightningDamageAdd: "Lightning Damage",
  healAdd: "Heal Add",
  burnAdd: "Burn",
  chillAdd: "Chill",
  shockAdd: "Shock",
  physicalArmorAdd: "Physical Armor Add",
  magicalArmorAdd: "Magic Armor Add",
  encore: "Encore",
  ignoreArmor: "Ignore Armor",
  globalAbilityLevelAdd: "Ability Level",
  slottedAbilityLevelAdd: "Slotted Ability Level",
  slottedAbilityCostReduce: "Slotted Ability Cost",
  levelAdd: "Ability Level",
  primaryCostReduce: "Primary Cost Reduce",
  slottedActiveDamageAdd: "Slotted Damage",
  slottedActiveOutputAdd: "Slotted Output",
  filledSocketDamage: "Filled Socket Damage",
  filledSocketPhysicalArmor: "Filled Socket Physical Armor",
  augment: "Augment",
  essence: "Essence",
  inherent: "Inherent",
  combo: "Combo",
  turnStartGrantResources: "Turn Start Resources",
  turnEndConvertToPersistent: "Turn End Persist",
  turnStartGrantDodge: "Turn Start Dodge",
  turnStartTemporaryMagicArmor: "Turn Start Magic Armor",
  turnStartTemporaryPhysicalArmor: "Turn Start Physical Armor",
  turnStartChoosePrimaryResource: "Turn Start Choose Resource",
  chooseIdentity: "Choose Identity",
  oncePerTurnOutgoingDamageAdd: "Once Per Turn Damage",
  oncePerTurnPhysicalArmorGainAdd: "Once Per Turn Physical Armor Gain",
  oncePerTurnAbilityOutputAdd: "Once Per Turn Ability Output",
  outgoingDamageAdd: "Outgoing Damage",
  outgoingHealAdd: "Outgoing Heal",
  primaryResourceGainAsNeutral: "Primary Gain as Neutral",
  primaryCostPayWithNeutral: "Pay Primary with Neutral",
  outgoingOutputMult: "Output Multiplier",
  lowLifeOutgoingDamageAdd: "Low Life Damage",
  agnosticExclusiveNeutralThresholds: "Agnostic Thresholds",
  neutralSourceOutgoingDamage: "Neutral Source Damage",
  weaponSlotDamageAdd: "Weapon Slot Damage",
  poisonStackAdd: "Poison Stacks",
  ailmentStackAdd: "Ailment Stacks",
  cannotGainResources: "Cannot Gain Resources",
  restrictOutgoingDamageTypes: "Restrict Damage Types",
  restrictAilments: "Restrict Ailments",
  incomingDamageTakenAdd: "Incoming Damage",
  activeAbilityOutputMult: "Active Output",
  blockExpendEffect: "Block Expend Effect",
  ninjutsuThrowableToken: "Throwable Token",
};

const EFFECT_FILTER_ALIASES = {
  temporaryEncore: ["encore"],
};

const EQUIPMENT_TRIGGER_FIELDS = [
  {
    key: "onEquip",
    keywordId: "onEquip",
    label: "ON EQUIP",
    examLabel: "On Equip",
  },
  {
    key: "onExpend",
    keywordId: "onExpend",
    label: "ON EXPEND",
    examLabel: "On Expend",
  },
  {
    key: "onSlot",
    keywordId: "onSlot",
    label: "ON SLOT",
    examLabel: "On Slot",
  },
  {
    key: "onDiscardFromEquippedSlot",
    keywordId: "onDiscardFromEquippedSlot",
    label: "ON DISCARD",
    examLabel: "On Discard (Equipped)",
  },
  {
    key: "onDiscardFromHand",
    keywordId: "onDiscardFromHand",
    label: "ON DISCARD (HAND)",
    examLabel: "On Discard (Hand)",
  },
];

const ABILITY_TRIGGER_FIELDS = [
  {
    key: "onSlot",
    keywordId: "onSlot",
    label: "ON SLOT",
    examLabel: "On Slot",
  },
];

function getCardTriggerFields(card) {
  if (!card) return [];
  if (cardHasType(card, "equipment")) return EQUIPMENT_TRIGGER_FIELDS;
  if (cardHasType(card, "ability")) return ABILITY_TRIGGER_FIELDS;
  return [];
}

function getEquipmentTriggerDisplays(card) {
  if (!card) return [];
  return getCardTriggerFields(card)
    .map((field) => {
      const text = formatActivatedEffectText(card[field.key]);
      if (!text) return null;
      return { ...field, text };
    })
    .filter(Boolean);
}

function getEquipmentTriggerLinePrefixes() {
  const prefixes = [];
  EQUIPMENT_TRIGGER_FIELDS.forEach((field) => {
    if (field.label) prefixes.push(String(field.label));
    if (field.examLabel) prefixes.push(String(field.examLabel));
  });
  KEYWORD_TOOLTIPS.forEach((tip) => {
    if (
      !EQUIPMENT_TRIGGER_FIELDS.some(
        (field) => field.keywordId === tip.id || field.key === tip.id,
      )
    ) {
      return;
    }
    if (tip.label) prefixes.push(String(tip.label));
    (tip.aliases || []).forEach((alias) => prefixes.push(String(alias)));
  });
  return prefixes;
}

function lineStartsWithAnyPrefix(line, prefixes) {
  const raw = String(line || "").trim();
  if (!raw) return false;
  const upper = raw.toUpperCase();
  return prefixes.some((prefix) => {
    const needle = String(prefix || "").trim().toUpperCase();
    if (!needle) return false;
    return (
      upper === needle ||
      upper.startsWith(`${needle}:`) ||
      upper.startsWith(`${needle} `)
    );
  });
}

function isEquipmentTriggerDetailLine(line) {
  return lineStartsWithAnyPrefix(line, getEquipmentTriggerLinePrefixes());
}

function isEquipmentPassiveDetailLine(line) {
  return lineStartsWithAnyPrefix(line, ["PASSIVE", "Passive"]);
}

function normalizeEquipmentTextClause(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/^passive:\s*/i, "")
    .replace(/[.:]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeEquipmentTextClauseLoose(value) {
  return normalizeEquipmentTextClause(value)
    .replace(/\b\d+\b/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function isEquipmentStructuredTextClause(card, clause) {
  if (!card || !clause) return false;
  for (let i = 0; i < EQUIPMENT_TRIGGER_FIELDS.length; i += 1) {
    const field = EQUIPMENT_TRIGGER_FIELDS[i];
    const prefixes = [];
    if (field.label) prefixes.push(String(field.label));
    if (field.examLabel) prefixes.push(String(field.examLabel));
    const tip = KEYWORD_TOOLTIPS.find(
      (entry) => entry.id === field.keywordId || entry.id === field.key,
    );
    if (tip?.label) prefixes.push(String(tip.label));
    (tip?.aliases || []).forEach((alias) => prefixes.push(String(alias)));
    if (lineStartsWithAnyPrefix(clause, prefixes)) {
      return Boolean(formatActivatedEffectText(card[field.key]));
    }
  }
  if (
    lineStartsWithAnyPrefix(clause, ["Expend", "EXPEND", "Expend effect"]) &&
    card.activatedEffect
  ) {
    return true;
  }
  if (/\bpay\b.+\bexpend\b/i.test(clause) && card.activatedEffect) {
    return true;
  }
  if (isEquipmentPassiveDetailLine(clause)) {
    return (card.passiveEffects || []).length > 0;
  }
  const normalizedClause = normalizeEquipmentTextClause(clause);
  if (!normalizedClause) return false;
  const looseClause = normalizeEquipmentTextClauseLoose(clause);
  return (card.passiveEffects || []).some((effect) => {
    const line = formatPassiveEffectLine(effect);
    if (!line) return false;
    const normalizedLine = normalizeEquipmentTextClause(line);
    const normalizedPassive = normalizeEquipmentTextClause(`PASSIVE: ${line}`);
    if (
      normalizedClause === normalizedLine ||
      normalizedClause === normalizedPassive ||
      normalizedPassive.includes(normalizedClause) ||
      normalizedClause.includes(normalizedLine)
    ) {
      return true;
    }
    if (effect?.id === "addCharge") {
      const label = normalizeEquipmentTextClause(
        typeof effect.label === "string" && effect.label.trim()
          ? effect.label
          : `Add ${getChargeKindLabel(effect.charge, 1)}`,
      );
      if (label && normalizedClause.includes(label)) return true;
    }
    const tip = KEYWORD_TOOLTIPS.find((entry) => entry.id === effect?.id);
    if (tip?.description && looseClause) {
      const looseTip = normalizeEquipmentTextClauseLoose(tip.description);
      if (
        looseTip &&
        (looseClause === looseTip ||
          looseClause.includes(looseTip) ||
          looseTip.includes(looseClause))
      ) {
        return true;
      }
    }
    return false;
  });
}

function getEquipmentUniqueTextClauses(card) {
  const text = String(card?.text || "").trim();
  if (!text) return [];
  return text
    .split(/\.\s*/)
    .map((clause) => clause.trim())
    .filter(Boolean)
    .filter((clause) => !isEquipmentStructuredTextClause(card, clause))
    .filter((clause) => !/^(PASSIVE:\s*)?Durability\b/i.test(clause));
}

function getEquipmentPassiveDescriptionLines(card) {
  return getEquipmentPassiveDescriptionEntries(card).map((entry) => entry.text);
}

function getEquipmentPassiveDescriptionEntries(card) {
  if (!card) return [];
  return (card.passiveEffects || [])
    .filter(
      (effect) =>
        effect?.id !== "sharpen" &&
        effect?.id !== "hone" &&
        effect?.id !== "fletch" &&
        effect?.id !== "enableEncore" &&
        effect?.id !== "research" &&
        effect?.id !== "repair" &&
        effect?.id !== "refresh" &&
        effect?.id !== "addCharge" &&
        effect?.id !== "durability" &&
        effect?.id !== "delayedResourceGrant" &&
        !(
          effect?.id === "slottedActiveDamageAdd" &&
          (effect.costValue != null ||
            Object.values(effect.cost || {}).some(
              (amount) => Math.max(0, Number(amount) || 0) > 0,
            ))
        ),
    )
    .map((effect) => {
      const line = formatPassiveEffectLine(effect);
      return line
        ? {
            text: `PASSIVE: ${line}.`,
            outputDelta: Math.floor(Number(effect?.outputDelta) || 0),
          }
        : null;
    })
    .filter(Boolean);
}

function formatSharpenLabel(amount) {
  return `Sharpen ${Math.max(1, amount || 1)}`;
}

function formatHoneLabel(amount) {
  return `Hone ${Math.max(1, amount || 1)}`;
}

function formatFletchLabel(amount) {
  return `Fletch ${Math.max(1, amount || 1)}`;
}

function listPaidTempEffectFaceLines(card) {
  if (!card) return [];
  return (card.passiveEffects || [])
    .filter(
      (effect) =>
        effect?.id === "sharpen" ||
        effect?.id === "hone" ||
        effect?.id === "fletch" ||
        effect?.id === "enableEncore" ||
        effect?.id === "research" ||
        effect?.id === "repair" ||
        effect?.id === "refresh" ||
        effect?.id === "addCharge" ||
        (effect?.id === "slottedActiveDamageAdd" &&
          (effect.costValue != null ||
            Object.values(effect.cost || {}).some(
              (amount) => Math.max(0, Number(amount) || 0) > 0,
            ))),
    )
    .flatMap((effect) => {
      if (
        effect.id === "repair" &&
        effect.costFromIdentity != null &&
        !Object.keys(effect.cost || {}).length
      ) {
        const modes = listEquipmentIdentityModes(card);
        if (modes.length) {
          const amount = getIdentityCostAmount(effect);
          return modes.map((attribute) => ({
            cost: [[attribute, amount]],
            text: "Repair",
            identity: card.chosenIdentity ? null : attribute,
          }));
        }
      }
      let text = null;
      if (effect.id === "sharpen") text = formatSharpenLabel(effect.amount);
      else if (effect.id === "hone") text = formatHoneLabel(effect.amount);
      else if (effect.id === "fletch") text = formatFletchLabel(effect.amount);
      else if (effect.id === "enableEncore") text = "Encore";
      else if (effect.id === "research") text = "Research";
      else if (effect.id === "refresh") text = "Refresh";
      else if (effect.id === "addCharge") {
        const amount = Math.max(1, effect.amount || 1);
        text =
          typeof effect.label === "string" && effect.label.trim()
            ? effect.label.trim()
            : `Add ${amount} ${getChargeKindLabel(effect.charge, amount)}`;
      }
      else if (effect.id === "repair") {
        text =
          effect.costFromIdentity != null &&
          !Object.keys(effect.cost || {}).length
            ? "Repair Identity"
            : "Repair";
      }
      else if (effect.id === "slottedActiveDamageAdd") {
        text = formatPassiveEffectLine(effect);
      }
      if (!text) return null;
      const cost =
        effect.costValue != null
          ? costEntries({
              costValue: effect.costValue,
              costAttributes: effect.costAttributes,
            })
          : costEntries(effect.cost);
      return {
        cost,
        text,
        durabilityCost: Math.max(0, effect.consumeDurability || 0),
      };
    })
    .filter(Boolean);
}

const KEYWORD_TOOLTIPS = [
  {
    id: "expend",
    label: "Expend",
    description:
      "Expend this equipment for the turn, for resources or an effect. An expended equipment cannot be expended again. Expended equipment becomes unexpended at the start of your next turn.",
  },
  {
    id: "traitTree",
    label: "Trait Tree",
    description:
      "Each level up during a match offers a Trait choice from this Aspect.",
  },
  {
    id: "equipmentActions",
    label: "Equipment Actions",
    description:
      "Pay the listed cost to use. These do not expend the equipment.",
  },
  {
    id: "triggers",
    label: "Triggers",
    description:
      "Effects that resolve automatically when their condition is met, such as when the card is equipped, expended, or slotted.",
  },
  {
    id: "invoke",
    label: "Invoke",
    description:
      "Once per turn, discard this card from your hand to resolve its Invoke effect.",
  },
  {
    id: "levelProgression",
    label: "Level Progression",
    description:
      "Abilities start at Level 1. Certain Effects from Traits, linking them to specific Augments, or Equipment Effects like Research can increase them up to level 4.",
  },
  {
    id: "deck",
    label: "Deck",
    description:
      "Cards remaining in that deck pile. Drawing takes from the top. Equipment and Ability each have their own deck.",
  },
  {
    id: "power",
    label: "Power",
    autoLink: false,
    description: `A deckbuilding cost that reflects how strong a card is. The combined Power of your Aspect, Equipment deck, and Ability deck must be ${MAX_DECK_POWER} or less for the decks to be valid.`,
  },
  {
    id: "hand",
    label: "Hand",
    description:
      "Cards currently in that hand. Equipment and Ability hands are separate, and each has its own hand size limit.",
  },
  {
    id: "discard",
    label: "Discard",
    description:
      "Cards discarded from your hand or from play go into your discard pile.",
  },
  {
    id: "onEquip",
    label: "On Equip",
    aliases: ["ON EQUIP"],
    description:
      "Resolves immediately when this equipment is successfully played onto the board.",
  },
  {
    id: "onExpend",
    label: "On Expend",
    aliases: ["ON EXPEND"],
    description:
      "Resolves whenever this equipment is expended, whether for resources or its activated effect.",
  },
  {
    id: "onSlot",
    label: "On Slot",
    aliases: ["ON SLOT"],
    description:
      "Resolves when this ability is slotted.",
  },
  {
    id: "damageSelf",
    label: "Damage Self",
    description:
      "Deals the listed typed damage to you. Physical and magical self-damage is reduced by your matching armor, then that hit removes 1 matching armor stack.",
  },
  {
    id: "passive",
    label: "Passive",
    aliases: ["PASSIVE"],
    description:
      "An ongoing effect while this equipment remains in play. Passives do not require expending the card.",
  },
  {
    id: "temporaryPhysicalArmor",
    label: "Temporary Physical Armor",
    description:
      "Adds Physical Armor until your opponent ends their turn, then it clears. Matching physical hits still reduce damage by the current total, then remove 1 Physical Armor.",
  },
  {
    id: "temporaryMagicArmor",
    label: "Temporary Magic Armor",
    aliases: ["Temporary Magical Armor"],
    description:
      "Adds Magic Armor until your opponent ends their turn, then it clears. Matching magical hits still reduce damage by the current total, then remove 1 Magic Armor.",
  },
  {
    id: "physicalArmor",
    label: "Physical Armor",
    description:
      "Reduces incoming physical damage, then that hit removes 1 Physical Armor. Temporary Physical Armor from expend effects lasts until your opponent ends their turn.",
  },
  {
    id: "thorns",
    label: "Thorns",
    description:
      "When you take physical attack damage, the attacker takes 1 Thorns damage and you lose 1 Thorns stack. Remaining Thorns stacks clear at the start of your turn.",
  },
  {
    id: "kaleidoscopic",
    label: "Kaleidoscopic",
    description:
      "Adds physical damage equal to the number of different non-Neutral primary attributes among slotted abilities, plus 1 for each slotted Essence.",
  },
  {
    id: "turnStartThorns",
    label: "Turn Start Thorns",
    description: "At the start of your turn, gain Thorns stacks.",
  },
  {
    id: "gainThorns",
    label: "Gain Thorns",
    description: "Gain Thorns stacks.",
  },
  {
    id: "nextMeleePhysicalDamageAdd",
    label: "Next Melee Damage",
    description:
      "This turn, your next melee-tagged physical attack deals additional damage.",
  },
  {
    id: "magicArmor",
    label: "Magic Armor",
    aliases: ["Magical Armor"],
    description:
      "Reduces incoming magical damage, then that hit removes 1 Magic Armor. Temporary Magic Armor from expend effects lasts until your opponent ends their turn.",
  },
  {
    id: "fireResistance",
    label: "Fire Resistance",
    description:
      "Reduces incoming fire damage. At the start of your turn, removes Burn stacks up to your Fire Resistance before Burn ticks.",
  },
  {
    id: "coldResistance",
    label: "Cold Resistance",
    description:
      "Reduces incoming cold damage. At the start of your turn, removes Chill stacks up to your Cold Resistance.",
  },
  {
    id: "lightningResistance",
    label: "Lightning Resistance",
    description:
      "Reduces incoming lightning damage. At the start of your turn, removes Shock stacks up to your Lightning Resistance before Shock can apply.",
  },
  {
    id: "distantConnection",
    label: "Distant Connection",
    description:
      "The next time you expend equipment of the named weapon type (for example a Crossbow) for damage this turn, it deals the listed amount more. Abilities, including Inherent abilities, are not affected. Multiple uses stack; the bonus is used up by the first matching expend and lost at end of turn.",
  },
  {
    id: "refresh",
    label: "Refresh",
    description:
      "While this equipment is expended, pay the Refresh cost to unexpend it so you can expend it again this turn.",
  },
  {
    id: "fastHands",
    label: "Fast Hands",
    description:
      "This equipment can be expended for an effect immediately after it is equipped.",
  },
  {
    id: "swiftFeet",
    label: "Swift Feet",
    description:
      "Boots with Swift Feet can be expended for an effect immediately after they are equipped.",
  },
  {
    id: "armorBreaker",
    label: "Armor Breaker",
    description:
      "When this deals damage, if the opponent has at least 1 Physical Armor, deal additional physical damage and lose additional Durability.",
  },
  {
    id: "parry",
    label: "Parry",
    description:
      "If you took physical damage from a melee equipment or active ability last turn, this equipment's damage expend deals 1 physical damage instead of 0 (once per turn).",
  },
  {
    id: "identityExpendRequiresMatchingActive",
    label: "Identity Match",
    description:
      "You can expend this equipment for 1 resource of its chosen Identity only while an active ability with that same attribute is slotted in its column. For example, with a Wisdom Identity, slot a Wisdom active here to expend for Wisdom. Actives of any attribute can still be slotted and activated in this column.",
  },
  {
    id: "charges",
    label: "Charges",
    description:
      "Local counters stored on a card. Every charge has a specific kind (Bolt, Fletch, Arcane, Earth, Nature, Bulwark, Focus). Kinds never mix: an effect that adds, spends, or deals damage from charges only uses its own kind. Whether a kind clears at end of turn is part of the kind.",
  },
  ...getChargeKindKeywordEntries(),
  {
    id: "addCharge",
    label: "Add Charge",
    aliases: ["Load Bolt"],
    description:
      "Pay the listed cost to add charges of this equipment's kind. Does not expend the equipment. Load Bolt is Hand Crossbow's action for adding Bolt Charges.",
  },
  {
    id: "durability",
    label: "Durability",
    description:
      "Some weapons have Durability charges when played. Effects that consume Durability reduce it; at 0 the equipment is broken. Broken equipment can still be expended for resources, but cannot use other expend effects or buffs until repaired.",
  },
  {
    id: "consumeDurability",
    label: "Use Durability",
    aliases: ["Consume Durability", "Spend Durability"],
    description:
      "A cost paid by removing Durability from this equipment, shown as one Durability icon per point. You need at least that much Durability remaining, and broken equipment cannot pay it. If this drops the equipment to 0 Durability, it becomes broken until repaired.",
  },
  {
    id: "repair",
    label: "Repair",
    description:
      "Pay the listed cost to restore this equipment's Durability to its maximum. Repair works while damaged or broken and does not expend the equipment.",
  },
  {
    id: "sharpen",
    label: "Sharpen",
    description:
      "Pay the listed cost to give slotted Physical or Melee Active Abilities damage equal to this Sharpen value. Permanent Sharpen lasts while the equipment remains equipped; otherwise it lasts until end of turn.",
  },
  {
    id: "hone",
    label: "Hone",
    description:
      "Pay the listed cost to give slotted Active Abilities that deal physical damage or are Melee +1 damage until end of turn. Does not expend the equipment.",
  },
  {
    id: "fletch",
    label: "Fletch",
    description:
      "Pay the listed cost to place the listed number of Fletch Charges on one slotted Ranged Active Ability. Fletch Charges stack with no limit. When that ability is activated, all of its Fletch Charges are consumed and it deals +1 physical damage per charge consumed.",
  },
  {
    id: "research",
    label: "Research",
    description:
      "Pay the listed cost to permanently increase the level of matching abilities slotted to this equipment's host column (for example Wisdom abilities on armor) while it remains equipped.",
  },
  {
    id: "gainCharges",
    label: "Gain Charges",
    description:
      "Adds the listed number of charges of the named kind to this equipment, such as on equip.",
  },
  {
    id: "addChargeToWeapon",
    label: "Charge Weapon",
    description:
      "Add charges of the named kind to your equipped weapon. Only works if that weapon holds that kind of charge (for example, Arcane Charges go on Charge Wand, not on Hand Crossbow).",
  },
  {
    id: "restoreWeaponDurability",
    label: "Restore Weapon Durability",
    description:
      "Restore Durability on your equipped weapon. The weapon's attributes do not need to match this equipment's Identity.",
  },
  {
    id: "increaseCombo",
    label: "Increase Combo",
    description: "Increase your current Combo count by the listed amount.",
  },
  {
    id: "refreshWeapon",
    label: "Refresh Weapon",
    description:
      "Unexpend your equipped weapon so it can be expended again. The weapon's attributes do not need to match this equipment's Identity.",
  },
  {
    id: "meleeWeaponDamageAdd",
    label: "Melee Weapon Damage",
    description:
      "Increase the damage of your equipped melee-type weapon by the listed amount until the end of your turn.",
  },
  {
    id: "nextWeaponExpendCostReduce",
    label: "Next Weapon Cost",
    description:
      "Reduce the cost of your next weapon action expend by the listed amount, to the listed minimum.",
  },
  {
    id: "nextActiveAbilityCostReduce",
    label: "Next Active Cost",
    description:
      "Reduce the cost of your next active ability by the listed amount, to the listed minimum.",
  },
  {
    id: "nextAttrCostReduce",
    label: "Next Ability Cost",
    description:
      "Reduce the cost of your next matching Active Ability by the listed amount, to a minimum of 1.",
  },
  {
    id: "temporaryArmorFromIdentity",
    label: "Identity Armor",
    description:
      "Gain temporary Physical Armor if this equipment's Identity is Strength, Dodge if Dexterity, or temporary Magic Armor if Wisdom.",
  },
  {
    id: "attackFromIdentity",
    label: "Identity Attack",
    description:
      "Expend for an Identity-based attack: Shock (Dexterity) or lightning damage (Wisdom), often consuming Durability by Identity.",
  },
  {
    id: "ailmentFromIdentity",
    label: "Identity Ailment",
    description:
      "Pay this equipment's Identity cost to apply Burn (Strength), Shock (Dexterity), or Chill (Wisdom).",
  },
  {
    id: "identityExpend",
    label: "Identity Expend",
    description:
      "Expend for an effect that depends on this equipment's chosen Identity.",
  },
  {
    id: "oncePerTurnPoisonStackAdd",
    label: "Once-Per-Turn Poison",
    description:
      "Once per turn, the next poison-type equipment effect or poison-type active ability that applies Poison applies additional Poison.",
  },
  {
    id: "temporaryFireResistance",
    label: "Temporary Fire Resistance",
    description: "Gain temporary Fire Resistance until your next turn.",
  },
  {
    id: "temporaryColdResistance",
    label: "Temporary Cold Resistance",
    description: "Gain temporary Cold Resistance until your next turn.",
  },
  {
    id: "temporaryLightningResistance",
    label: "Temporary Lightning Resistance",
    description: "Gain temporary Lightning Resistance until your next turn.",
  },
  {
    id: "temporaryBleedResistance",
    label: "Temporary Bleed Resistance",
    description:
      "Gain temporary Bleed Resistance until your opponent ends their turn. Incoming Bleed stacks are reduced by your Bleed Resistance.",
  },
  {
    id: "temporarySlottedAbilityOutputAdd",
    label: "Slotted Output",
    description:
      "Increase the output of matching slotted Active abilities until the end of your turn.",
  },
  {
    id: "temporarySlottedAbilityPoisonAdd",
    label: "Temporary Slotted Poison",
    description:
      "Temporarily increase Poison applied by matching slotted Active abilities.",
  },
  {
    id: "temporarySlottedAbilityDamageAdd",
    label: "Slotted Damage",
    description:
      "Increase the damage of matching slotted Active abilities. Next-activation bonuses are consumed when a matching ability is activated.",
  },
  {
    id: "temporarySlottedAbilityCostReduce",
    label: "Temporary Slotted Cost Reduce",
    description:
      "Temporarily reduce the cost of matching slotted Active abilities.",
  },
  {
    id: "spendChargesForResources",
    label: "Spend Charges for Resources",
    description:
      "Spend this equipment's charges of the named kind in bundles to gain Strength, Dexterity, and/or Wisdom. You choose how to split the resources up to the number of bundles available.",
  },
  {
    id: "convertPhysicalArmorToDamage",
    label: "Convert Physical Armor",
    aliases: ["Convert Physical Armor to Damage"],
    description:
      "Consumes all of your Physical Armor, including passive and temporary, and deals physical damage from the consumed armor plus the ability's base damage. Some abilities scale consumed armor above 1 to 1.",
  },
  {
    id: "convertMagicArmorToDamage",
    label: "Convert Magic Armor",
    aliases: ["Convert Magic Armor to Damage"],
    description:
      "Consumes all of your Magic Armor, including passive and temporary, and deals that much magical damage plus the ability's base damage.",
  },
  {
    id: "curse",
    label: "Curse",
    description:
      "A hostile magic effect that weakens or taxes the opponent, often through targeted board disruption.",
  },
  {
    id: "sacrifice",
    label: "Sacrifice",
    description:
      "Pays health or other personal cost to produce a powerful effect.",
  },
  {
    id: "dark",
    label: "Dark",
    description:
      "Forbidden or infernal magic. Often tied to Sacrifice and self-harming pacts.",
  },
  {
    id: "hexExpendCost",
    label: "Hex Equipment",
    aliases: ["Hexed Possessions"],
    description:
      "Choose one of the opponent's equipped items. Until that opponent ends their turn, that item cannot be expended for resources or its activated effect. Passive bonuses still apply. Extra ability levels add Neutral to the cost of slotted abilities on that item (level 2: +1, level 3: +2, and so on).",
  },
  {
    id: "delayed",
    label: "Delayed",
    aliases: ["Delay"],
    description:
      "A delayed resource. Shown faded with a small clock. It cannot be spent this turn. At the start of your next turn it becomes a normal resource.",
  },
  {
    id: "persist",
    label: "Persist",
    aliases: ["Persistent"],
    description:
      "A persistent resource. Marked with *. Normal resources of the same attribute are spent first. Persistent resources remain between turns and rounds until spent.",
  },
  {
    id: "attribute",
    label: "Attribute",
    description:
      "A card's resource identity: Strength, Dexterity, Wisdom, or Neutral. Equipment usually grants that resource. Abilities usually cost it.",
  },
  {
    id: "strength",
    label: "Strength",
    description:
      "Power tempered by endurance. Strength favors heavy physical attacks, Bleed, and Physical Armor, often turning defense into greater offense.",
  },
  {
    id: "dexterity",
    label: "Dexterity",
    description:
      "Speed sharpened into opportunity. Dexterity favors rapid or repeated attacks, Dodge, Poison, and precise effects that bypass armor.",
  },
  {
    id: "wisdom",
    label: "Wisdom",
    description:
      "Arcane mastery shaped into power and protection. Wisdom favors magical and elemental damage, healing, Magic Armor, and effects that strengthen or repeat abilities.",
  },
  {
    id: "neutral",
    label: "Neutral",
    description:
      "Versatility without allegiance. Neutral offers attacks, healing, and protection useful in any build. A Neutral cost can be paid with Strength, Dexterity, Wisdom, or Neutral, but Neutral cannot pay a named attribute cost.",
  },
  {
    id: "encore",
    label: "Encore",
    description:
      "Can be used repeatedly in the same turn while you can pay. Slotted abilities are not marked used. An expend effect with Encore does not expend the equipment.",
  },
  {
    id: "temporaryEncore",
    label: "Temporary Encore",
    description:
      "The next ability you play this turn gains Encore, so it can be activated repeatedly while you can pay its cost.",
  },
  {
    id: "nimbleFingers",
    label: "Nimble Fingers",
    description:
      "You may slot one additional ability this turn beyond the usual one-per-turn limit. Clears at end of turn unless you gain Nimble Fingers again.",
  },
  {
    id: "enhanceNextResourceExpend",
    label: "Enhance Attributes",
    aliases: ["Next resource expend"],
    description:
      "The host equipment's next expend for resources this turn grants 1 extra resource of its inherent attribute. Mixed or Neutral attributes grant Neutral. Cannot gain Encore or be used more than once per turn.",
  },
  {
    id: "shadow",
    label: "Shadow",
    description:
      "A buff stack. The next hit that would deal 2 or more damage to you deals 1 instead, and 1 Shadow is removed. Shadow is checked before Dodge. All Shadow is removed when your opponent ends their turn.",
  },
  {
    id: "block",
    label: "Block",
    description:
      "If you took any physical damage last turn, this equipment's Physical Armor effects grant extra Physical Armor equal to its Block value.",
  },
  {
    id: "dodgeRoll",
    label: `Dodge ${DODGE_MITIGATION_PERCENT}%`,
    aliases: ["Dodge", "Dodge Roll"],
    description:
      `Gain a Dodge stack. The next time you would take damage, that hit is reduced by ${DODGE_MITIGATION_PERCENT}% (round down) and 1 Dodge is removed. If the hit would deal at least 1 damage, it still deals at least 1 after Dodge.`,
  },
  {
    id: "bleed",
    label: "Bleed",
    description:
      "Bleed stacks damage you when you expend equipment. Each tick deals 1 damage and removes 1 stack. You lose 1 Bleed at the end of your turn.",
  },
  {
    id: "poison",
    label: "Poison",
    description:
      "Poison stacks damage you at the start of your turn. Each tick deals 1 damage and removes 1 stack.",
  },
  {
    id: "convertStacks",
    label: "Convert Stacks",
    description:
      "Remove all or a set number of a stack type from yourself or the opponent, then convert what was removed into another effect such as damage or healing. Effects that add those stacks apply first, then the stacks are consumed.",
  },
  {
    id: "burn",
    label: "Burn",
    description:
      "Burn stacks damage you at the start of your turn. Each tick deals 1 damage and removes 1 stack.",
  },
  {
    id: "chill",
    label: "Chill",
    description:
      "While you have at least 1 Chill, your active abilities cost 1 extra Neutral. Using an ability removes 1 stack. Extra stacks do not increase the cost further. 1 stack is also removed at the end of your turn.",
  },
  {
    id: "shock",
    label: "Shock",
    description:
      "While you have at least 1 Shock, the next equipment you expend for resources or ability that grants resources gives 1 less. Strength, Dexterity, and Wisdom are reduced before Neutral. Expending for resources removes 1 stack. 1 stack is also removed at the end of your turn.",
  },
  {
    id: "wither",
    label: "Wither",
    description:
      "At the start of your turn, lose current and maximum health equal to your Wither stacks. At the end of your turn, lose 1 stack and restore 1 maximum health. Current health is not restored.",
  },
  {
    id: "regeneration",
    label: "Regeneration",
    description:
      "Regeneration stacks heal you at the start of your turn before Burn and Poison. Each tick heals 1 and removes 1 stack.",
  },
  {
    id: "heal",
    label: "Heal",
    description: "Restore your HP, up to your maximum.",
  },
  {
    id: "maxHealthIncrease",
    label: "Max Health",
    aliases: ["Max Health Increase"],
    description:
      "Raises your maximum health by the listed amount and increases your current health by the same amount.",
  },
  {
    id: "maxHealthAdd",
    label: "Equipped Max Health",
    description:
      "While equipped, raises your maximum health by the listed amount. When this equipment leaves play, you lose that maximum health and do not heal.",
  },
  {
    id: "linked",
    label: "Linked",
    aliases: ["linked"],
    description:
      "Ability slots marked linked share augment connections. Augments in a linked group can modify matching actives in that group.",
  },
  {
    id: "combo",
    label: "Combo",
    description:
      "Combo X: each time you use this same card again in a row, it deals +X damage and costs +X of its attribute per step. Steps stack up to this ability's current level, including temporary level boosts (a level 2 ability caps at +2X). Equipment combos cap at 1 step. Doing anything else or ending your turn resets the streak.",
  },
  {
    id: "unarmed",
    label: "Unarmed",
    description:
      "Uses a special damage value when you have no weapons equipped.",
  },
  {
    id: "loneWeapon",
    label: "Lone Weapon",
    description:
      "If this is the only weapon in your equipment deck, it starts in your hand (including after Mulligan) and uses a higher damage value when expended.",
  },
  {
    id: "consumeDiscardTop",
    label: "Exile Discard Top",
    description:
      "Removes up to the listed number of cards from the top of your discard pile from the game, dealing damage for each card removed this way.",
  },
  {
    id: "convertResource",
    label: "Convert Resource",
    description:
      "Spend the listed non-persistent resource only (Neutral costs are not paid with Strength, Dexterity, or Wisdom), then gain the same amount as another attribute (or as persistent if listed). Choice converts offer Strength, Dexterity, or Wisdom.",
  },
  {
    id: "physicalDamage",
    label: "Physical Damage",
    aliases: ["physical"],
    description:
      "Damage reduced by Physical Armor unless an effect ignores armor. A matching hit then removes 1 Physical Armor.",
  },
  {
    id: "ignoreArmor",
    label: "Ignore Armor",
    aliases: ["Critical Strike"],
    description:
      "This damage is not reduced by Physical Armor or Magic Armor, and it does not remove armor stacks.",
  },
  {
    id: "ignorePhysicalArmor",
    label: "Ignore Physical Armor",
    description:
      "Treat the defender's Physical Armor as reduced by this amount when mitigating this hit. Matching Physical Armor can still be removed.",
  },
  {
    id: "noHpDamage",
    label: "No Health Damage",
    description:
      "Excess damage does not reduce health if the opponent has any Physical Armor. The hit still chips Physical Armor when applicable. With no Physical Armor, it deals health damage normally.",
  },
  {
    id: "magicalDamage",
    label: "Magical Damage",
    description:
      "Damage reduced by Magic Armor unless an effect ignores armor. A matching hit then removes 1 Magic Armor.",
  },
  {
    id: "active",
    label: "Active",
    description:
      "An ability you activate from slotted equipment by paying its cost. Linked Augments can modify matching Active abilities.",
  },
  {
    id: "augment",
    label: "Augment",
    description:
      "A support ability that modifies linked matching active abilities, often adding damage, healing, or armor at an added cost.",
  },
  {
    id: "essence",
    label: "Essence",
    description:
      "Enhances the Inherent effects of the equipment it is slotted into. Duplicate same-named Essences on one item only apply once.",
  },
  {
    id: "inherent",
    label: "Inherent",
    description:
      "Effects that belong to the host equipment itself, such as its damage or armor. Essence abilities enhance those effects on the equipment they are slotted into.",
  },
  {
    id: "melee",
    label: "Melee",
    description:
      "Close-range card tag. Melee equipment often boosts slotted melee actives. Melee does not change damage type — physical melee attacks still deal physical damage mitigated by Physical Armor.",
  },
  {
    id: "ranged",
    label: "Ranged",
    description:
      "Ranged card tag. Ranged equipment often boosts slotted ranged actives. Ranged does not change damage type — physical ranged attacks still deal physical damage mitigated by Physical Armor.",
  },
  {
    id: "magic",
    label: "Magic",
    description:
      "Magic attack type used by many wisdom weapons and abilities. Distinct from the magical damage type.",
  },
  {
    id: "magical",
    label: "Magical",
    description:
      "Magical abilities and effects. Often deal magical damage and interact with Magic Armor.",
  },
  {
    id: "fire",
    label: "Fire",
    description:
      "Fire damage. Reduced by Fire Resistance. Often applies Burn.",
  },
  {
    id: "cold",
    label: "Cold",
    description:
      "Cold damage. Reduced by Cold Resistance. Often applies Chill.",
  },
  {
    id: "lightning",
    label: "Lightning",
    description:
      "Lightning damage. Reduced by Lightning Resistance. Often applies Shock.",
  },
  {
    id: "healing",
    label: "Healing",
    description: "Restores health.",
  },
  {
    id: "health",
    label: "Health",
    description:
      "Pays or interacts with your health total rather than resources.",
  },
  {
    id: "resource",
    label: "Resource",
    description:
      "Generates or manipulates Strength, Dexterity, Wisdom, or Neutral resources.",
  },
  {
    id: "aspect",
    label: "Aspect",
    description:
      "A class card. Each deck includes exactly one Aspect. It sits on your body board for the match and applies its passive effects for the whole duel.",
  },
  {
    id: "equipment",
    label: "Equipment",
    description:
      "One of the two main deck types. Equipment plays into Weapon, Armor, or Trinket board slots, can be expended for resources or effects, and can host slotted Abilities.",
  },
  {
    id: "ability",
    label: "Ability",
    description:
      "One of the two main deck types. Abilities are slotted onto Equipment. Active abilities are activated from their host slot; Augments and Essences apply while slotted.",
  },
  {
    id: "attack",
    label: "Attack",
    description:
      "Equipment or ability that deals damage when expended or activated.",
  },
  {
    id: "sword",
    label: "Sword",
    description: "Sword-type weapon equipment.",
  },
  {
    id: "axe",
    label: "Axe",
    description: "Axe-type weapon equipment.",
  },
  {
    id: "mace",
    label: "Mace",
    description: "Mace-type weapon equipment.",
  },
  {
    id: "dagger",
    label: "Dagger",
    description: "Dagger-type weapon equipment.",
  },
  {
    id: "throwable",
    label: "Throwable",
    description: "Thrown weapon equipment. Expending often returns it to your equipment deck.",
  },
  {
    id: "token",
    label: "Token",
    description:
      "Temporary equipment created by an Aspect. Tokens are free, and when they leave your hand or board they leave play instead of going to decks or discard.",
  },
  {
    id: "throwableToken",
    label: "Throwable Token",
    aliases: ["throwable tokens"],
    description:
      "Equipping a throwable token does not use your one equipment play per turn. Tokens are free to play and leave play forever instead of returning to the deck or discard when used, Invoked, or discarded. Tokens cannot be Invoked and they do not grant anything if Invoked.",
  },
  {
    id: "minion",
    label: "Minion",
    description:
      "An active ability that summons a persistent companion into the equipment slot where it is played. Pay its cost to summon. While active, that equipment is replaced by the minion card and cannot be used for its own effects. Expend the minion for its action, or Unsummon it from the occupied slot. Incoming damage hits your oldest minions first, then you. When a minion reaches 0 health it is destroyed and must be summoned again.",
  },
  {
    id: "aura",
    label: "Aura",
    description:
      "An active ability that creates a persistent aura on the equipment slot where it is played. Pay its cost to activate. While active, that equipment is replaced by the Aura card and cannot be used for its own effects. The Aura provides ongoing benefits until you deactivate it. Deactivating does not refund the activation cost.",
  },
  {
    id: "crossbow",
    label: "Crossbow",
    description: "Crossbow-type ranged weapon equipment.",
  },
  {
    id: "wand",
    label: "Wand",
    description: "Wand-type magic weapon equipment.",
  },
  {
    id: "club",
    label: "Club",
    description: "Club-type blunt weapon equipment.",
  },
  {
    id: "bow",
    label: "Bow",
    description: "Bow-type ranged weapon equipment.",
  },
  {
    id: "launcher",
    label: "Launcher",
    description: "Launcher-type ranged weapon equipment.",
  },
  {
    id: "staff",
    label: "Staff",
    description: "Staff-type magic weapon equipment.",
  },
  {
    id: "polearm",
    label: "Polearm",
    description: "Polearm-type two-handed weapon equipment, such as spears, pikes, halberds, and glaives.",
  },
  {
    id: "hammer",
    label: "Hammer",
    description: "Hammer-type two-handed weapon equipment.",
  },
  {
    id: "dart",
    label: "Dart",
    description: "Dart-type one-handed weapon equipment.",
  },
  {
    id: "hook",
    label: "Hook",
    description: "Hook-type weapon equipment.",
  },
  {
    id: "banner",
    label: "Banner",
    description: "Banner-type two-handed support equipment.",
  },
  {
    id: "rod",
    label: "Rod",
    description: "Rod-type two-handed equipment.",
  },
  {
    id: "quiver",
    label: "Quiver",
    description: "Quiver-type off-hand equipment.",
  },
  {
    id: "shield",
    label: "Shield",
    description: "Shield-type off-hand equipment.",
  },
  {
    id: "artifact",
    label: "Artifact",
    description: "Artifact-type off-hand equipment, such as orbs, tomes, lenses, and prisms.",
  },
  {
    id: "helmet",
    label: "Helmet",
    description: "Helmet-type head equipment.",
  },
  {
    id: "hat",
    label: "Hat",
    description: "Hat-type head equipment.",
  },
  {
    id: "crown",
    label: "Crown",
    description: "Crown-type head equipment.",
  },
  {
    id: "cap",
    label: "Cap",
    description: "Cap-type head equipment.",
  },
  {
    id: "circlet",
    label: "Circlet",
    description: "Circlet-type head equipment.",
  },
  {
    id: "hood",
    label: "Hood",
    description: "Hood-type head equipment.",
  },
  {
    id: "robes",
    label: "Robes",
    description: "Robes-type body equipment.",
  },
  {
    id: "cuirass",
    label: "Cuirass",
    description: "Cuirass-type body equipment.",
  },
  {
    id: "cloak",
    label: "Cloak",
    description: "Cloak-type body equipment.",
  },
  {
    id: "mail",
    label: "Mail",
    description: "Mail-type body equipment.",
  },
  {
    id: "coat",
    label: "Coat",
    description: "Coat-type body equipment.",
  },
  {
    id: "vest",
    label: "Vest",
    description: "Vest-type body equipment.",
  },
  {
    id: "bracers",
    label: "Bracers",
    description: "Bracers-type hands equipment.",
  },
  {
    id: "gloves",
    label: "Gloves",
    description: "Gloves-type hands equipment.",
  },
  {
    id: "gauntlets",
    label: "Gauntlets",
    description: "Gauntlets-type hands equipment.",
  },
  {
    id: "handWraps",
    label: "Hand Wraps",
    description: "Hand Wraps-type hands equipment.",
  },
  {
    id: "sandals",
    label: "Sandals",
    description: "Sandals-type feet equipment.",
  },
  {
    id: "boots",
    label: "Boots",
    description: "Boots-type feet equipment.",
  },
  {
    id: "greaves",
    label: "Greaves",
    description: "Greaves-type feet equipment.",
  },
  {
    id: "sabatons",
    label: "Sabatons",
    description: "Sabatons-type feet equipment.",
  },
  {
    id: "ring",
    label: "Ring",
    description: "Ring-type trinket equipment.",
  },
  {
    id: "necklace",
    label: "Necklace",
    description: "Necklace-type trinket equipment, including charms and amulets.",
  },
  {
    id: "earrings",
    label: "Earrings",
    description: "Earrings-type trinket equipment.",
  },
  {
    id: "headband",
    label: "Headband",
    description: "Headband-type trinket equipment.",
  },
  {
    id: "echo",
    label: "Echo",
    description:
      "After you pay the linked active ability's cost, it activates a second time. Multiplicative cost increases apply after additive augment costs.",
  },
  {
    id: "fromDiscard",
    label: "From Discard",
    description:
      "Pull a card from your discard pile. A random pick chooses one discarded card, then does the listed follow-up, such as shuffling it into the deck.",
  },
  {
    id: "chooseIdentity",
    label: "Choose Identity",
    aliases: ["Choose Identity", "Identity"],
    description:
      "When this equipment is equipped, choose one of its primary attributes. While equipped it counts as only that attribute and expends for 1 of that resource. Leaving play restores its printed attributes.",
  },
  {
    id: "returnToEquipmentDeck",
    label: "Return to Bottom of Deck",
    aliases: ["Return to Deck", "Return to Equipment Deck", "Remove Permanently"],
    description:
      "Unequip this equipment from its slot and put it on the bottom of your deck. Tokens are removed from the game permanently instead.",
  },
  {
    id: "discardSelfThenDraw",
    label: "Discard Self and Draw",
    description:
      "Discard this equipment from play, then draw from your deck. If the effect names a card type, such as Ability, you draw a card of that type. If none remain, nothing is drawn.",
  },
  {
    id: "draw",
    label: "Draw",
    description:
      "Draw cards from your deck into your hand, up to the listed amount. Stops early if your hand is full or your deck is empty.",
  },
  {
    id: "restock",
    label: "Restock",
    description:
      "Gain the listed Restock charges. At the start of your next turn, all Restock charges are consumed and you draw that many extra cards in addition to your normal draw, up to your maximum hand size.",
  },
  {
    id: "set_base",
    label: "Base",
    aliases: ["Base Set"],
    description: CARD_SET_DESCRIPTIONS.base,
  },
];

const GLOSSARY_ENTRIES = KEYWORD_TOOLTIPS.map((entry) => ({
  ...entry,
  searchText: [entry.label, entry.id, entry.description, ...(entry.aliases || [])]
    .filter(Boolean)
    .join(" ")
    .toLowerCase(),
})).sort((a, b) =>
  String(a.label || "").localeCompare(String(b.label || "")),
);

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

const KEYWORD_LOOKUP = (() => {
  const byPhrase = new Map();
  KEYWORD_TOOLTIPS.forEach((entry) => {
    if (entry.autoLink === false) return;
    const phrases = [entry.label, ...(entry.aliases || [])];
    phrases.forEach((phrase) => {
      byPhrase.set(String(phrase).toLowerCase(), entry);
    });
  });
  const phrases = [...byPhrase.keys()].sort((a, b) => b.length - a.length);
  const pattern = phrases.length
    ? new RegExp(`\\b(${phrases.map(escapeRegExp).join("|")})\\b`, "gi")
    : null;
  return { byPhrase, pattern };
})();

function splitTextWithKeywords(text) {
  const source = String(text || "");
  if (!source || !KEYWORD_LOOKUP.pattern) {
    return [{ text: source, keyword: null }];
  }
  const parts = [];
  let lastIndex = 0;
  source.replace(KEYWORD_LOOKUP.pattern, (match, _group, offset) => {
    if (offset > lastIndex) {
      parts.push({ text: source.slice(lastIndex, offset), keyword: null });
    }
    const keyword =
      KEYWORD_LOOKUP.byPhrase.get(String(match).toLowerCase()) || null;
    parts.push({ text: match, keyword });
    lastIndex = offset + match.length;
    return match;
  });
  if (lastIndex < source.length) {
    parts.push({ text: source.slice(lastIndex), keyword: null });
  }
  return parts.length ? parts : [{ text: source, keyword: null }];
}

function buildCardNameMatcher(catalog) {
  const byName = new Map();
  (catalog || []).forEach((card) => {
    const name = String(card?.name || "").trim();
    if (!card?.id || name.length < 3) return;
    const key = name.toLowerCase();
    if (!byName.has(key)) byName.set(key, card);
  });
  const names = [...byName.keys()].sort((a, b) => b.length - a.length);
  return {
    byName,
    pattern: names.length
      ? new RegExp(`\\b(${names.map(escapeRegExp).join("|")})\\b`, "gi")
      : null,
  };
}

function splitTextWithCardNames(text, matcher) {
  const source = String(text || "");
  if (!source || !matcher?.pattern) {
    return [{ text: source, card: null }];
  }
  const parts = [];
  let lastIndex = 0;
  source.replace(matcher.pattern, (match, _group, offset) => {
    if (offset > lastIndex) {
      parts.push({ text: source.slice(lastIndex, offset), card: null });
    }
    const card = matcher.byName.get(String(match).toLowerCase()) || null;
    parts.push({ text: match, card });
    lastIndex = offset + match.length;
    return match;
  });
  if (lastIndex < source.length) {
    parts.push({ text: source.slice(lastIndex), card: null });
  }
  return parts.length ? parts : [{ text: source, card: null }];
}

function CombatLogRichText({ text, matcher, onCardClick }) {
  const parts = splitTextWithCardNames(text, matcher);
  return (
    <>
      {parts.map((part, index) => {
        if (!part.card || typeof onCardClick !== "function") {
          return <span key={`t-${index}`}>{part.text}</span>;
        }
        return (
          <button
            key={`c-${index}-${part.card.id}`}
            type="button"
            className={styles.combatLogCardLink}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onCardClick(part.card.id);
            }}
          >
            {part.text}
          </button>
        );
      })}
    </>
  );
}

function ExamRichText({
  text,
  matcher,
  onCardClick,
  activeKeywordId,
  onKeywordClick,
}) {
  const parts = splitTextWithCardNames(text, matcher);
  return (
    <>
      {parts.map((part, index) => {
        if (part.card && typeof onCardClick === "function") {
          return (
            <button
              key={`c-${index}-${part.card.id}`}
              type="button"
              className={`${styles.keywordLink} ${styles.examCardLink}`}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onCardClick(part.card.id);
              }}
            >
              {part.text}
            </button>
          );
        }
        return (
          <KeywordRichText
            key={`k-${index}`}
            text={part.text}
            activeKeywordId={activeKeywordId}
            onKeywordClick={onKeywordClick}
          />
        );
      })}
    </>
  );
}


function computeGameHintPopupPosition(anchorEl, popupEl) {
  const margin = 8;
  const anchor = anchorEl.getBoundingClientRect();
  const popup = popupEl.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let top = anchor.bottom + margin;
  let left = anchor.left + anchor.width / 2 - popup.width / 2;
  left = Math.max(margin, Math.min(left, vw - popup.width - margin));
  if (top + popup.height > vh - margin) {
    top = anchor.top - popup.height - margin;
  }
  top = Math.max(margin, Math.min(top, vh - popup.height - margin));
  return { top, left };
}

function GameHintPopup({ anchorEl, text, onClose }) {
  const popupRef = useRef(null);
  const [position, setPosition] = useState(null);

  const syncPosition = useCallback(() => {
    if (!anchorEl || !popupRef.current) return;
    setPosition(computeGameHintPopupPosition(anchorEl, popupRef.current));
  }, [anchorEl]);

  useLayoutEffect(() => {
    syncPosition();
    const rafId = requestAnimationFrame(syncPosition);
    return () => cancelAnimationFrame(rafId);
  }, [syncPosition, text]);

  useEffect(() => {
    const handleResize = () => syncPosition();
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleResize, true);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleResize, true);
    };
  }, [syncPosition]);

  return (
    <div className={styles.gameHintOverlay} onClick={onClose}>
      <div
        ref={popupRef}
        className={styles.gameHintPopup}
        role="dialog"
        aria-label={t("common.hint")}
        style={
          position
            ? { top: position.top, left: position.left, visibility: "visible" }
            : { visibility: "hidden" }
        }
      >
        {text}
      </div>
    </div>
  );
}

function GameHintButton({ hintId, text, className, activeHintId, onToggle }) {
  const buttonRef = useRef(null);
  const isOpen = activeHintId === hintId;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`${styles.gameHintButton} ${className || ""}`}
        aria-label={t("common.showHint")}
        aria-expanded={isOpen}
        onClick={(event) => {
          event.stopPropagation();
          onToggle(hintId, buttonRef.current);
        }}
      >
        ?
      </button>
      {isOpen ? (
        <GameHintPopup
          anchorEl={buttonRef.current}
          text={text}
          onClose={() => onToggle(null, null)}
        />
      ) : null}
    </>
  );
}

function RemainingActionChip({
  hintId,
  label,
  count,
  hintText,
  activeHintId,
  onToggle,
}) {
  const buttonRef = useRef(null);
  const isOpen = activeHintId === hintId;
  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`${styles.remainingActionChip}${
          count <= 0 ? ` ${styles.remainingActionChipEmpty}` : ""
        }`}
        aria-label={t("common.labelCountShowHint", { label, count })}
        aria-expanded={isOpen}
        onClick={(event) => {
          event.stopPropagation();
          onToggle(hintId, buttonRef.current);
        }}
      >
        {label} {count}
      </button>
      {isOpen ? (
        <GameHintPopup
          anchorEl={buttonRef.current}
          text={hintText}
          onClose={() => onToggle(null, null)}
        />
      ) : null}
    </>
  );
}

function assignScrollHintRef(node, localRef, forwardedRef) {
  localRef.current = node;
  if (typeof forwardedRef === "function") {
    forwardedRef(node);
    return;
  }
  if (forwardedRef) {
    forwardedRef.current = node;
  }
}

const ScrollHintSurface = forwardRef(function ScrollHintSurface(
  { className = "", children, onScroll, ...rest },
  forwardedRef,
) {
  const localRef = useRef(null);
  const [showHint, setShowHint] = useState(false);

  const syncScrollHint = useCallback(() => {
    const el = localRef.current;
    if (!el) {
      setShowHint(false);
      return;
    }
    const maxScroll = el.scrollHeight - el.clientHeight;
    if (maxScroll <= 4) {
      setShowHint(false);
      return;
    }
    setShowHint(el.scrollTop < maxScroll - 4);
  }, []);

  useLayoutEffect(() => {
    syncScrollHint();
    const rafId = requestAnimationFrame(syncScrollHint);
    return () => cancelAnimationFrame(rafId);
  }, [syncScrollHint]);

  useEffect(() => {
    const el = localRef.current;
    if (!el) return undefined;

    syncScrollHint();

    const handleScroll = () => syncScrollHint();
    el.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", syncScrollHint);

    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(syncScrollHint);
      resizeObserver.observe(el);
    }

    let mutationObserver = null;
    if (typeof MutationObserver !== "undefined") {
      mutationObserver = new MutationObserver(syncScrollHint);
      mutationObserver.observe(el, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    return () => {
      el.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", syncScrollHint);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [syncScrollHint]);

  return (
    <div
      ref={(node) => assignScrollHintRef(node, localRef, forwardedRef)}
      className={[styles.scrollHintSurface, className].filter(Boolean).join(" ")}
      onScroll={(event) => {
        syncScrollHint();
        onScroll?.(event);
      }}
      {...rest}
    >
      {children}
      {showHint ? (
        <div className={styles.modalScrollHint} aria-hidden="true">
          <span className={styles.modalScrollHintArrow}>▼</span>
        </div>
      ) : null}
    </div>
  );
});

function KeywordRichText({ text, activeKeywordId, onKeywordClick }) {
  const parts = splitTextWithKeywords(text);
  return (
    <>
      {parts.map((part, index) => {
        if (!part.keyword) {
          return <span key={`t-${index}`}>{part.text}</span>;
        }
        const active = activeKeywordId === part.keyword.id;
        return (
          <button
            key={`k-${index}-${part.keyword.id}`}
            type="button"
            className={`${styles.keywordLink} ${
              active ? styles.keywordLinkActive : ""
            }`}
            onClick={(event) => {
              event.stopPropagation();
              onKeywordClick(part.keyword);
            }}
          >
            {part.text}
          </button>
        );
      })}
    </>
  );
}

function getKeywordById(id) {
  return KEYWORD_TOOLTIPS.find((entry) => entry.id === id) || null;
}

function renderStatusKeywordBadge(
  keywordId,
  className,
  children,
  keywordProps = {},
) {
  const { activeKeywordId, onKeywordClick } = keywordProps;
  const keyword = getKeywordById(keywordId);
  if (!keyword || typeof onKeywordClick !== "function") {
    return <span className={className}>{children}</span>;
  }
  const active = activeKeywordId === keyword.id;
  return (
    <button
      type="button"
      className={`${className} ${styles.statusKeywordBadge} ${
        active ? styles.keywordLinkActive : ""
      }`}
      onClick={(event) => {
        event.stopPropagation();
        onKeywordClick(keyword);
      }}
      aria-label={keyword.label}
    >
      {children}
    </button>
  );
}

const CARD_TYPE_KEYWORD_IDS = {
  active: "active",
  aspect: "aspect",
  equipment: "equipment",
  ability: "ability",
  augment: "augment",
  essence: "essence",
  attack: "attack",
  melee: "melee",
  ranged: "ranged",
  magic: "magic",
  magical: "magical",
  physical: "physicalDamage",
  fire: "fire",
  cold: "cold",
  lightning: "lightning",
  poison: "poison",
  curse: "curse",
  sacrifice: "sacrifice",
  dark: "dark",
  health: "health",
  healing: "healing",
  resource: "resource",
  unarmed: "unarmed",
  sword: "sword",
  axe: "axe",
  mace: "mace",
  dagger: "dagger",
  throwable: "throwable",
  token: "token",
  minion: "minion",
  aura: "aura",
  crossbow: "crossbow",
  wand: "wand",
  club: "club",
  bow: "bow",
  launcher: "launcher",
  dart: "dart",
  staff: "staff",
  hammer: "hammer",
  polearm: "polearm",
  hook: "hook",
  banner: "banner",
  rod: "rod",
  quiver: "quiver",
  artifact: "artifact",
  shield: "shield",
  helmet: "helmet",
  hat: "hat",
  crown: "crown",
  cap: "cap",
  circlet: "circlet",
  hood: "hood",
  robes: "robes",
  cuirass: "cuirass",
  cloak: "cloak",
  mail: "mail",
  coat: "coat",
  vest: "vest",
  bracers: "bracers",
  gloves: "gloves",
  gauntlets: "gauntlets",
  handwraps: "handWraps",
  sandals: "sandals",
  boots: "boots",
  greaves: "greaves",
  sabatons: "sabatons",
  ring: "ring",
  necklace: "necklace",
  earrings: "earrings",
  headband: "headband",
  "hand wraps": "handWraps",
};

function getCardTypeKeywordId(typeToken) {
  return (
    CARD_TYPE_KEYWORD_IDS[String(typeToken || "").trim().toLowerCase()] || null
  );
}

function HandExamTypeTags({ tags, activeKeywordId, onKeywordClick }) {
  const parts = String(tags || "")
    .split(" · ")
    .map((part) => part.trim())
    .filter(Boolean);
  if (!parts.length) return null;
  return (
    <>
      {parts.map((part, index) => {
        const keywordId = getCardTypeKeywordId(part);
        return (
          <React.Fragment key={`${part}-${index}`}>
            {index > 0 ? <span> - </span> : null}
            {keywordId ? (
              <KeywordBadge
                keywordId={keywordId}
                label={part}
                className={styles.keywordLink}
                activeKeywordId={activeKeywordId}
                onKeywordClick={onKeywordClick}
              />
            ) : (
              <span>{part}</span>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
}

function KeywordBadge({
  keywordId,
  label,
  className,
  activeKeywordId,
  onKeywordClick,
}) {
  const keyword = getKeywordById(keywordId);
  if (!keyword || typeof onKeywordClick !== "function") {
    return <span className={className}>{label}</span>;
  }
  const active = activeKeywordId === keyword.id;
  return (
    <button
      type="button"
      className={`${className} ${styles.keywordBadgeButton} ${
        active ? styles.keywordLinkActive : ""
      }`}
      aria-label={`${keyword.label} keyword`}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onKeywordClick(keyword);
      }}
    >
      {label}
    </button>
  );
}

function formatEffectFilterLabel(effectId) {
  if (EFFECT_FILTER_LABELS[effectId]) return EFFECT_FILTER_LABELS[effectId];
  return String(effectId || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/^./, (char) => char.toUpperCase());
}

function listEffectFilterOptions(cards) {
  const byLabel = new Map();
  (cards || []).forEach((card) => {
    cardEffectKeys(card).forEach((key) => {
      const label = formatEffectFilterLabel(key);
      if (!byLabel.has(label)) byLabel.set(label, key);
    });
  });
  return [...byLabel.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, key]) => key);
}

function cardMatchesEffectFilter(card, effectFilter) {
  if (!effectFilter || effectFilter === "all") return true;
  const label = formatEffectFilterLabel(effectFilter);
  return cardEffectKeys(card).some(
    (key) => key === effectFilter || formatEffectFilterLabel(key) === label,
  );
}

function cardCostTotal(card) {
  if (!card) return 0;
  if (cardHasType(card, "ability")) {
    const resources = Object.values(card.cost || {}).reduce(
      (sum, value) => sum + (Number(value) || 0),
      0,
    );
    return resources + getAbilityHealthCostAmount(card.effect);
  }
  return Object.values(card.resources || {}).reduce(
    (sum, value) => sum + (Number(value) || 0),
    0,
  );
}

function cardEffectKeys(card) {
  if (!card) return [];
  const keys = new Set();
  const add = (effect) => {
    if (!effect) return;
    if (Array.isArray(effect)) {
      effect.forEach(add);
      return;
    }
    if (Array.isArray(effect.choose)) {
      effect.choose.forEach(add);
      return;
    }
    if (effect.id) {
      keys.add(effect.id);
      (EFFECT_FILTER_ALIASES[effect.id] || []).forEach((alias) =>
        keys.add(alias),
      );
    }
    if (effect.byIdentity && typeof effect.byIdentity === "object") {
      Object.values(effect.byIdentity).forEach(add);
    }
    if (effect.action) add(effect.action);
    if (Array.isArray(effect.passiveEffects)) effect.passiveEffects.forEach(add);
    if (effect.unarmed != null) keys.add("unarmed");
    if (effect.loneWeapon != null) keys.add("loneWeapon");
    if (effect.equipmentAffinity) keys.add("equipmentAffinity");
    if (effect.equippedAttributeBonus) keys.add("equippedAttributeBonus");
    if (effect.ownerDiscardBonus) keys.add("ownerDiscardBonus");
    if (effect.consumeDiscardTop) keys.add("consumeDiscardTop");
    if (effect.ignoreArmor) keys.add("ignoreArmor");
    if ((effect.ignorePhysicalArmor || 0) > 0) keys.add("ignorePhysicalArmor");
    if (effect.noHpDamage) keys.add("noHpDamage");
    if (effect.encore) keys.add("encore");
    if ((effect.burn || 0) > 0) keys.add("burn");
    if ((effect.chill || 0) > 0) keys.add("chill");
    if ((effect.shock || 0) > 0) keys.add("shock");
  };
  add(card.activatedEffect);
  add(card.effect);
  EQUIPMENT_TRIGGER_FIELDS.forEach((field) => {
    add(card[field.key]);
    if (card[field.key]) keys.add(field.key);
  });
  (card.passiveEffects || []).forEach(add);
  add(card.invoke);
  if (card.kind === "augment" || card.augment) keys.add("augment");
  if (card.kind === "essence" || card.essence) {
    keys.add("essence");
    const modify = resolveEssenceModifyForLevel(
      card.essence,
      card.effectiveLevel || card.level || 1,
    );
    if (
      (modify.inherentPhysicalDamageAdd || 0) !== 0 ||
      (modify.inherentMagicalDamageAdd || 0) !== 0 ||
      (modify.inherentPhysicalArmorAdd || 0) !== 0 ||
      (modify.inherentMagicArmorAdd || 0) !== 0
    ) {
      keys.add("inherent");
    }
  }
  if (card.augment?.modify?.echo) keys.add("echo");
  if (cardHasType(card, "curse")) keys.add("curse");
  if (cardHasType(card, "sacrifice")) keys.add("sacrifice");
  if (cardHasType(card, "dark")) keys.add("dark");
  if (cardHasType(card, "magical")) keys.add("magical");
  if (card.effect?.id === "hexExpendCost") keys.add("hexExpendCost");
  if (card.combo > 0) keys.add("combo");
  return [...keys];
}

function cardSearchHaystack(card) {
  if (!card) return "";
  const parts = [
    card.name,
    card.text,
    card.kind,
    card.slot,
    formatSlotLabel(card.slot),
    primaryAttribute(card),
    ...(card.attributes || []),
    ...(Array.isArray(card.type) ? card.type : [card.type]),
    ...cardEffectKeys(card).map(formatEffectFilterLabel),
    ...cardEffectKeys(card),
    formatActivatedEffectText(card.activatedEffect),
    ...EQUIPMENT_TRIGGER_FIELDS.map((field) =>
      formatActivatedEffectText(card[field.key]),
    ),
    formatActivatedEffectText(card.effect),
    formatCost(card.cost),
    ...formatResources(card.resources),
  ];
  return parts.filter(Boolean).join(" ").toLowerCase();
}

function cardReferencesKeyword(card, keyword) {
  if (!card || !keyword) return false;
  const id = keyword.id;
  if (cardHasType(card, id)) return true;
  if (card.kind === id) return true;
  if (primaryAttribute(card) === id) return true;
  if ((card.attributes || []).includes(id)) return true;
  if (id.startsWith("set_") && normalizeCardSet(card.set) === id.slice(4)) {
    return true;
  }
  const effectKeys = cardEffectKeys(card);
  if (effectKeys.includes(id)) return true;
  if (id === "heal" && effectKeys.includes("healSelf")) return true;
  if (id === "active" && card.kind === "active") return true;
  if (id === "augment" && (card.kind === "augment" || card.augment)) {
    return true;
  }
  if (id === "essence" && (card.kind === "essence" || card.essence)) {
    return true;
  }
  if (id === "inherent" && (card.kind === "essence" || card.essence)) {
    return true;
  }
  if (
    id === "linked" &&
    (card.abilitySlots || []).some((slot) => slot && slot.linked)
  ) {
    return true;
  }
  if (id === "throwableToken") {
    if (cardHasType(card, "throwable")) return true;
    if (
      (card?.passiveEffects || []).some(
        (effect) => effect?.id === "ninjutsuThrowableToken",
      )
    ) {
      return true;
    }
  }
  if (id === "attribute" && primaryAttribute(card)) return true;
  if (id === "expend" && cardHasType(card, "equipment")) return true;
  if (id === "passive" && (card.passiveEffects || []).length > 0) return true;
  const haystack = cardSearchHaystack(card);
  const phrases = [keyword.label, ...(keyword.aliases || [])]
    .map((phrase) => String(phrase || "").toLowerCase().trim())
    .filter(Boolean);
  return phrases.some((phrase) => haystack.includes(phrase));
}

function cardCatalogCategory(card) {
  if (!card) return "";
  if (cardHasType(card, "aspect")) return "aspect";
  if (cardHasType(card, "equipment") || card.slot) return "equipment";
  if (cardHasType(card, "ability")) return "ability";
  return "";
}

function comparePickerCards(a, b, sortKey) {
  if (sortKey === "attribute") {
    const attrDiff =
      (ATTR_SORT_ORDER[primaryAttribute(a)] ?? 99) -
      (ATTR_SORT_ORDER[primaryAttribute(b)] ?? 99);
    if (attrDiff !== 0) return attrDiff;
  } else if (sortKey === "cost") {
    const costDiff = cardCostTotal(a) - cardCostTotal(b);
    if (costDiff !== 0) return costDiff;
  } else if (sortKey === "power") {
    const powerDiff = getCardPower(b) - getCardPower(a);
    if (powerDiff !== 0) return powerDiff;
  } else if (sortKey === "slot") {
    const slotDiff =
      (SLOT_SORT_ORDER[a.slot] ?? 99) - (SLOT_SORT_ORDER[b.slot] ?? 99);
    if (slotDiff !== 0) return slotDiff;
  } else if (sortKey === "tier") {
    const tierDiff = getEquipmentTier(b) - getEquipmentTier(a);
    if (tierDiff !== 0) return tierDiff;
  } else if (sortKey === "kind") {
    const kindDiff = String(a.kind || "").localeCompare(String(b.kind || ""));
    if (kindDiff !== 0) return kindDiff;
  } else if (sortKey === "category") {
    const categoryDiff =
      (CARD_CATEGORY_SORT_ORDER[cardCatalogCategory(a)] ?? 99) -
      (CARD_CATEGORY_SORT_ORDER[cardCatalogCategory(b)] ?? 99);
    if (categoryDiff !== 0) return categoryDiff;
  } else if (sortKey === "type") {
    const typeDiff = cardSortTypeLabel(a).localeCompare(cardSortTypeLabel(b));
    if (typeDiff !== 0) return typeDiff;
  }
  return String(a.name || "").localeCompare(String(b.name || ""));
}

function catalogHoloCount(cardId, unlockedHolos) {
  const list = unlockedHolos?.[cardId];
  return Array.isArray(list) ? list.length : 0;
}

function compareCatalogCards(a, b, sortKey, unlockedHolos, collection) {
  if (collection) {
    const aOwned = playerOwnsCard(collection, a.id) ? 0 : 1;
    const bOwned = playerOwnsCard(collection, b.id) ? 0 : 1;
    if (aOwned !== bOwned) return aOwned - bOwned;
  }
  if (sortKey === "holo") {
    const holoDiff =
      catalogHoloCount(b.id, unlockedHolos) -
      catalogHoloCount(a.id, unlockedHolos);
    if (holoDiff !== 0) return holoDiff;
  }
  return comparePickerCards(a, b, sortKey);
}

function formatAugmentMatchTags(augment) {
  const match = augment?.match || {};
  const tags = [];
  const types = [];
  if (Array.isArray(match.type)) types.push(...match.type);
  else if (match.type) types.push(match.type);
  if (Array.isArray(match.types)) types.push(...match.types);
  else if (match.types) types.push(match.types);
  types.forEach((type) => tags.push(String(type)));
  const typesAny = Array.isArray(match.typesAny)
    ? match.typesAny.filter(Boolean)
    : [];
  if (typesAny.length) tags.push(typesAny.map(String).join(" / "));
  if (match.attribute) tags.push(String(match.attribute));
  if (match.damaging) tags.push("damage");
  if (match.kind && match.kind !== "active") tags.push(String(match.kind));
  return tags;
}

function resolveEssenceModifyForLevel(essence, level = 1) {
  if (!essence) return {};
  if (Array.isArray(essence.modifyByLevel) && essence.modifyByLevel.length) {
    const lv = Math.max(1, Math.floor(Number(level) || 1));
    const index = Math.min(lv - 1, essence.modifyByLevel.length - 1);
    return { ...(essence.modifyByLevel[index] || {}) };
  }
  return { ...(essence.modify || {}) };
}

function formatEssenceEffectLines(ability) {
  if (!ability?.essence) return [];
  const modify = resolveEssenceModifyForLevel(
    ability.essence,
    ability.effectiveLevel || ability.level || 1,
  );
  const lines = [];
  if ((modify.inherentPhysicalDamageAdd || 0) !== 0) {
    lines.push(`Inherent Physical Damage +${modify.inherentPhysicalDamageAdd}`);
  }
  if ((modify.inherentMagicalDamageAdd || 0) !== 0) {
    lines.push(`Inherent Magical Damage +${modify.inherentMagicalDamageAdd}`);
  }
  if ((modify.inherentPhysicalArmorAdd || 0) !== 0) {
    lines.push(`Inherent Physical Armor +${modify.inherentPhysicalArmorAdd}`);
  }
  if ((modify.inherentMagicArmorAdd || 0) !== 0) {
    lines.push(`Inherent Magic Armor +${modify.inherentMagicArmorAdd}`);
  }
  if ((modify.inherentFireResistanceAdd || 0) !== 0) {
    lines.push(`Inherent Fire Resistance +${modify.inherentFireResistanceAdd}`);
  }
  if ((modify.inherentColdResistanceAdd || 0) !== 0) {
    lines.push(`Inherent Cold Resistance +${modify.inherentColdResistanceAdd}`);
  }
  if ((modify.inherentLightningResistanceAdd || 0) !== 0) {
    lines.push(
      `Inherent Lightning Resistance +${modify.inherentLightningResistanceAdd}`,
    );
  }
  if ((modify.inherentBurnAdd || 0) !== 0) {
    lines.push(`Inherent Burn +${modify.inherentBurnAdd}`);
  }
  if ((modify.inherentChillAdd || 0) !== 0) {
    lines.push(`Inherent Chill +${modify.inherentChillAdd}`);
  }
  if ((modify.inherentShockAdd || 0) !== 0) {
    lines.push(`Inherent Shock +${modify.inherentShockAdd}`);
  }
  const expendCostAdd = modify.expendCostAdd;
  if (typeof expendCostAdd === "number" && expendCostAdd > 0) {
    lines.push(
      `Expend Cost +${expendCostAdd} ${ability.attribute || "neutral"}`,
    );
  } else if (expendCostAdd && typeof expendCostAdd === "object") {
    costEntries(expendCostAdd).forEach(([attribute, value]) => {
      lines.push(`Expend Cost +${value} ${attribute}`);
    });
  }
  return lines;
}

function formatAugmentEffectLines(
  ability,
  {
    includeMatchTags = true,
    includeCostIncrease = true,
    includeCostMult = includeCostIncrease,
  } = {},
) {
  if (!ability?.augment) return [];
  const matchTags = formatAugmentMatchTags(ability.augment);
  const modify =
    ability.augment.modify && Object.keys(ability.augment.modify).length
      ? ability.augment.modify
      : resolveAugmentModifyForLevel(
          ability.augment,
          ability.effectiveLevel || ability.level || 1,
        );
  const damageAdd =
    ability.effectiveDamageAdd != null
      ? ability.effectiveDamageAdd
      : modify.damageAdd;
  const physicalDamageAdd =
    ability.effectivePhysicalDamageAdd != null
      ? ability.effectivePhysicalDamageAdd
      : modify.physicalDamageAdd;
  const magicalDamageAdd =
    ability.effectiveMagicalDamageAdd != null
      ? ability.effectiveMagicalDamageAdd
      : modify.magicalDamageAdd;
  const fireDamageAdd =
    ability.effectiveFireDamageAdd != null
      ? ability.effectiveFireDamageAdd
      : modify.fireDamageAdd;
  const coldDamageAdd =
    ability.effectiveColdDamageAdd != null
      ? ability.effectiveColdDamageAdd
      : modify.coldDamageAdd;
  const lightningDamageAdd =
    ability.effectiveLightningDamageAdd != null
      ? ability.effectiveLightningDamageAdd
      : modify.lightningDamageAdd;
  const healAdd =
    ability.effectiveHealAdd != null
      ? ability.effectiveHealAdd
      : modify.healAdd;
  const physicalArmorAdd =
    ability.effectivePhysicalArmorAdd != null
      ? ability.effectivePhysicalArmorAdd
      : modify.physicalArmorAdd;
  const magicalArmorAdd =
    ability.effectiveMagicalArmorAdd != null
      ? ability.effectiveMagicalArmorAdd
      : modify.magicalArmorAdd;
  const bleedAdd =
    ability.effectiveBleedAdd != null
      ? ability.effectiveBleedAdd
      : modify.bleedAdd;
  const poisonAdd =
    ability.effectivePoisonAdd != null
      ? ability.effectivePoisonAdd
      : modify.poisonAdd;
  const burnAdd =
    ability.effectiveBurnAdd != null
      ? ability.effectiveBurnAdd
      : modify.burnAdd;
  const chillAdd =
    ability.effectiveChillAdd != null
      ? ability.effectiveChillAdd
      : modify.chillAdd;
  const shockAdd =
    ability.effectiveShockAdd != null
      ? ability.effectiveShockAdd
      : modify.shockAdd;
  const levelAdd =
    ability.effectiveLevelAdd != null
      ? ability.effectiveLevelAdd
      : modify.levelAdd;
  const costAdd =
    ability.effectiveCostAdd != null ? ability.effectiveCostAdd : modify.costAdd;
  const lines = [];
  if (includeMatchTags && matchTags.length) {
    lines.push(matchTags.join(" · "));
  }
  if (levelAdd != null && levelAdd !== 0) {
    lines.push(`Ability Level +${levelAdd}`);
  }
  const primaryCostReduce = Math.floor(Number(modify.primaryCostReduce) || 0);
  if (primaryCostReduce > 0) {
    lines.push(`Cost -${primaryCostReduce} Primary (max)`);
  }
  if (physicalDamageAdd != null && physicalDamageAdd !== 0) {
    lines.push(`+ ${physicalDamageAdd} Physical Damage`);
  }
  if (magicalDamageAdd != null && magicalDamageAdd !== 0) {
    lines.push(`+ ${magicalDamageAdd} Magical Damage`);
  }
  if (fireDamageAdd != null && fireDamageAdd !== 0) {
    lines.push(`+ ${fireDamageAdd} Fire Damage`);
  }
  if (coldDamageAdd != null && coldDamageAdd !== 0) {
    lines.push(`+ ${coldDamageAdd} Cold Damage`);
  }
  if (lightningDamageAdd != null && lightningDamageAdd !== 0) {
    lines.push(`+ ${lightningDamageAdd} Lightning Damage`);
  }
  if (damageAdd != null && damageAdd !== 0) {
    lines.push(`Damage +${damageAdd}`);
  }
  if (healAdd != null && healAdd !== 0) {
    lines.push(`+ Heal ${healAdd}`);
  }
  if (physicalArmorAdd != null && physicalArmorAdd !== 0) {
    lines.push(`+ Physical Armor ${physicalArmorAdd}`);
  }
  if (magicalArmorAdd != null && magicalArmorAdd !== 0) {
    lines.push(`+ Magic Armor ${magicalArmorAdd}`);
  }
  if (bleedAdd != null && bleedAdd !== 0) {
    lines.push(`+ Bleed ${bleedAdd}`);
  }
  if (poisonAdd != null && poisonAdd !== 0) {
    lines.push(`+ Poison ${poisonAdd}`);
  }
  if (burnAdd != null && burnAdd !== 0) {
    lines.push(`+ Burn ${burnAdd}`);
  }
  if (chillAdd != null && chillAdd !== 0) {
    lines.push(`+ Chill ${chillAdd}`);
  }
  if (shockAdd != null && shockAdd !== 0) {
    lines.push(`+ Shock ${shockAdd}`);
  }
  if (modify.ignoreArmor) {
    lines.push("Ignore Armor");
  }
  if (modify.echo) {
    lines.push("Echo");
  }
  if (includeCostIncrease) {
    if (typeof costAdd === "number" && costAdd !== 0) {
      lines.push(`Cost +${costAdd}`);
    } else if (costAdd && typeof costAdd === "object") {
      costEntries(costAdd).forEach(([attribute, value]) => {
        lines.push(`Cost +${value} ${attribute}`);
      });
    }
  }
  if (includeCostMult) {
    const costMult = modify.costMult;
    if (typeof costMult === "number" && costMult !== 1 && costMult > 0) {
      lines.push(`Cost ×${costMult}`);
    }
  }
  const outputMult = modify.outputMult;
  if (typeof outputMult === "number" && outputMult !== 1 && outputMult > 0) {
    lines.push(`Output ×${outputMult}`);
  }
  const outputAdd =
    ability.effectiveOutputAdd != null
      ? ability.effectiveOutputAdd
      : modify.outputAdd;
  if (outputAdd != null && Number(outputAdd) !== 0) {
    lines.push(`Output +${outputAdd}`);
  }
  const minionHealthAdd =
    ability.effectiveMinionHealthAdd != null
      ? ability.effectiveMinionHealthAdd
      : modify.minionHealthAdd;
  if (minionHealthAdd != null && minionHealthAdd !== 0) {
    lines.push(`Minion Health +${minionHealthAdd}`);
  }
  const costToHealth = modify.costToHealth;
  if (typeof costToHealth === "number" && costToHealth > 0) {
    lines.push(`Cost uses health (${costToHealth} per resource)`);
  }
  return lines;
}

function formatAttributeLabel(attribute) {
  if (!attribute) return "";
  return String(attribute).charAt(0).toUpperCase() + String(attribute).slice(1);
}

function formatTemporaryEffectDuration(duration) {
  if (duration === "untilTargetControllerEndTurn") {
    return "until end of controller's turn";
  }
  if (duration === "nextActivation") return "next activation";
  if (!duration) return null;
  return String(duration);
}

function formatTemporaryEffectLine(effect) {
  if (!effect?.id) return null;
  if (effect.id === "hexEquipment" || effect.disableExpend || effect.slottedAbilityCostAdd) {
    const durationText = formatTemporaryEffectDuration(effect.duration);
    const parts = ["Curse"];
    if (effect.disableExpend) parts.push("Expend disabled");
    const costText = formatCost(effect.slottedAbilityCostAdd);
    if (costText) parts.push(`Slotted abilities +${costText}`);
    if (durationText) parts.push(`(${durationText})`);
    return parts.join(" ");
  }
  if (effect.id === "expendCostAdd") {
    const costText = formatCost(effect.costAdd || effect.expendCostAdd);
    const durationText = formatTemporaryEffectDuration(effect.duration);
    const parts = ["Curse · Expend"];
    if (costText) parts.push(`+${costText}`);
    if (durationText) parts.push(`(${durationText})`);
    return parts.join(" ");
  }
  if (effect.id === "nextResourceExpendAdd") {
    const amount = effect.amount || 1;
    const durationText = formatTemporaryEffectDuration(effect.duration);
    const parts = [`Next resource expend +${amount}`];
    if (durationText) parts.push(`(${durationText})`);
    return parts.join(" ");
  }
  if (effect.id === "sharpen") {
    const amount = Math.max(1, effect.amount || 1);
    const durationText = formatTemporaryEffectDuration(effect.duration);
    const label = formatSharpenLabel(amount);
    return durationText ? `${label} (${durationText})` : label;
  }
  if (effect.id === "hone") {
    const amount = Math.max(1, effect.amount || 1);
    const durationText = formatTemporaryEffectDuration(effect.duration);
    const label = formatHoneLabel(amount);
    return durationText ? `${label} (${durationText})` : label;
  }
  if (effect.id === "slottedActiveDamageAdd") {
    const durationText = formatTemporaryEffectDuration(effect.duration);
    const label = formatPassiveEffectLine(effect);
    return durationText && label
      ? `${label} (${durationText})`
      : label;
  }
  if (effect.id === "fletch") {
    const amount = Math.max(1, effect.amount || 1);
    const durationText = formatTemporaryEffectDuration(effect.duration);
    const label = formatFletchLabel(amount);
    return durationText ? `${label} (${durationText})` : label;
  }
  if (effect.id === "encore") {
    const durationText = formatTemporaryEffectDuration(effect.duration);
    return durationText ? `Encore (${durationText})` : "Encore";
  }
  const durationText = formatTemporaryEffectDuration(effect.duration);
  const label = formatEffectFilterLabel(effect.id) || effect.id;
  return durationText ? `${label} (${durationText})` : label;
}

function listEquipmentAppliedMarks(equipment) {
  if (!equipment) return [];
  const effects = (equipment.temporaryEffects || []).filter(
    (effect) => effect?.id,
  );
  const marks = [];
  EQUIPMENT_STATUS_MARK_ICON_IDS.forEach((id) => {
    const matching = effects.filter((effect) => effect.id === id);
    if (!matching.length) return;
    const amount = matching.reduce(
      (total, effect) => total + Math.max(0, effect.amount || 0),
      0,
    );
    const label = getKeywordById(id)?.label || id;
    marks.push({
      key: id,
      iconUrl: STATUS_EFFECT_ICON_URL[id],
      label: amount > 0 ? `${label} ${amount}` : label,
      lines: matching.map(formatTemporaryEffectLine).filter(Boolean),
    });
  });
  effects.forEach((effect, index) => {
    if (EQUIPMENT_STATUS_MARK_ICON_IDS.includes(effect.id)) return;
    const line = formatTemporaryEffectLine(effect);
    marks.push({
      key: `${effect.id}-${index}`,
      iconUrl: PLACEHOLDER_COUNTER_ICON_URL,
      label: line || t("card.statusEffect"),
      lines: line ? [line] : [],
    });
  });
  const physicalArmorAdd = Math.max(
    0,
    Math.floor(Number(equipment.traitPhysicalArmorGrantAdd) || 0),
  );
  if (physicalArmorAdd > 0) {
    const line = t("card.empoweredPhysicalArmor", { amount: physicalArmorAdd });
    marks.push({
      key: "traitPhysicalArmorGrantAdd",
      iconUrl: PLACEHOLDER_COUNTER_ICON_URL,
      label: line,
      lines: [line],
    });
  }
  const magicArmorAdd = Math.max(
    0,
    Math.floor(Number(equipment.traitMagicArmorGrantAdd) || 0),
  );
  if (magicArmorAdd > 0) {
    const line = t("card.empoweredMagicArmor", { amount: magicArmorAdd });
    marks.push({
      key: "traitMagicArmorGrantAdd",
      iconUrl: PLACEHOLDER_COUNTER_ICON_URL,
      label: line,
      lines: [line],
    });
  }
  return marks;
}

function equipmentHasStatusEffects(equipment) {
  return listEquipmentAppliedMarks(equipment).length > 0;
}

function EquipmentStatusMarks({ equipment }) {
  const marks = listEquipmentAppliedMarks(equipment);
  if (!marks.length) return null;
  return (
    <span className={styles.equipStatusMark}>
      {marks.map((mark) => (
        <span
          key={mark.key}
          className={styles.equipStatusMarkIcon}
          title={mark.label}
          aria-label={mark.label}
        >
          <MaskIcon
            src={mark.iconUrl}
            className={styles.equipStatusMarkIconGlyph}
          />
        </span>
      ))}
    </span>
  );
}

function getEquipmentLocalCounters(equipment) {
  if (!equipment) return [];
  const counters = [];
  (equipment.chargeCounters || []).forEach((counter) => {
    counters.push({
      id: getChargeKindKeywordId(counter.id),
      label: counter.label || getChargeKindLabel(counter.id),
      amount: Math.max(0, counter.amount || 0),
      chargeIcon: true,
    });
  });
  if (equipment.maxDurability != null) {
    counters.push({
      id: "durability",
      label: "Durability",
      amount: Math.max(0, equipment.durability ?? 0),
      max: Math.max(0, equipment.maxDurability || 0),
      iconUrl: DURABILITY_ICON_URL,
    });
  }
  return counters;
}

function formatSlottedDamageTypeLabel(effect) {
  const damageType =
    effect?.damageType ||
    (effect?.match?.attribute === "wisdom" ? "magical" : "physical");
  return damageType === "magical" ? "Magical Damage" : "Physical Damage";
}

function formatPassiveMatchParts(match) {
  const parts = [];
  const typeRules = [];
  if (Array.isArray(match?.type)) typeRules.push(...match.type);
  else if (match?.type) typeRules.push(match.type);
  if (Array.isArray(match?.types)) typeRules.push(...match.types);
  else if (match?.types) typeRules.push(match.types);
  typeRules.forEach((type) => parts.push(formatAttributeLabel(type)));
  if (match?.attribute) {
    parts.push(formatAttributeLabel(match.attribute));
  }
  if (match?.kind === "active") {
    parts.push("Active");
  }
  return parts;
}

function formatPassiveEffectLine(effect) {
  if (!effect?.id) return null;
  if (effect.id === "block") return `Block ${Math.max(0, effect.amount || 0)}`;
  if (effect.id === "slottedActiveDamageAdd") {
    const parts = ["Slotted"];
    parts.push(...formatPassiveMatchParts(effect.match));
    parts.push("Abilities");
    if (effect.amount != null) {
      parts.push(`+${effect.amount}`);
    }
    parts.push(formatSlottedDamageTypeLabel(effect));
    return parts.join(" ");
  }
  if (effect.id === "slottedActivePoisonAdd" || effect.id === "increasePoison") {
    const parts = ["Slotted"];
    parts.push(...formatPassiveMatchParts(effect.match || { type: "poison", kind: "active" }));
    parts.push("Abilities");
    if (effect.amount != null) {
      parts.push(`+${effect.amount}`);
    }
    parts.push("Poison");
    return parts.join(" ");
  }
  if (effect.id === "slottedActiveOutputAdd") {
    const parts = ["Slotted"];
    parts.push(...formatPassiveMatchParts(effect.match));
    parts.push("Abilities");
    if (effect.amount != null) {
      parts.push(`+${effect.amount}`);
    }
    return parts.join(" ");
  }
  if (effect.id === "slottedAbilityLevelAdd") {
    const parts = ["Slotted"];
    parts.push(...formatPassiveMatchParts(effect.match));
    parts.push("Ability Level");
    if (effect.amount != null) {
      parts.push(`+${effect.amount}`);
    }
    return parts.join(" ");
  }
  if (effect.id === "slottedAbilityCostReduce") {
    const parts = ["Slotted"];
    parts.push(...formatPassiveMatchParts(effect.match));
    parts.push("Abilities");
    const amount = Math.max(1, effect.amount || 1);
    parts.push(`-${amount} Cost`);
    return parts.join(" ");
  }
  if (effect.id === "globalAbilityLevelAdd") {
    const parts = formatPassiveMatchParts(effect.match);
    parts.push("Ability Level");
    if (effect.amount != null) {
      parts.push(`+${effect.amount}`);
    }
    return parts.join(" ");
  }
  if (effect.id === "thorns" || effect.id === "turnStartThorns") {
    const amount = Math.max(1, effect.amount || 1);
    if (effect.id === "turnStartThorns") {
      return `Turn Start Thorns ${amount}`;
    }
    return `Thorns ${amount}`;
  }
  if (effect.id === "kaleidoscopic") {
    return "Kaleidoscopic";
  }
  if (effect.id === "maxHealthAdd") {
    const amount = Math.max(0, effect.amount || 0);
    return `+${amount} max and current health while equipped. Lose ${amount} health when removed`;
  }
  if (effect.id === "filledSocketPhysicalArmor") {
    const amount = Math.max(0, effect.amount || 0);
    return `Filled Socket Physical Armor ${amount}`;
  }
  if (effect.id === "distantConnection") {
    const weaponType =
      typeof effect.weaponType === "string" ? effect.weaponType.trim() : "";
    const amount = Math.max(0, effect.amount || 0);
    const costText = formatCost(effect.cost || {});
    const typeLabel = weaponType
      ? formatAttributeLabel(weaponType) || weaponType
      : "weapon";
    const once = effect.oncePerTurn ? " once per turn" : "";
    const core =
      amount > 0
        ? `Distant Connection (${typeLabel} +${amount}${once})`
        : `Distant Connection (${typeLabel}${once})`;
    return costText ? `${costText} → ${core}` : core;
  }
  if (effect.id === "refresh") {
    const costText = formatCost(effect.cost || {});
    return costText ? `${costText} → Refresh` : "Refresh";
  }
  if (effect.id === "fastHands") {
    return "Fast Hands";
  }
  if (effect.id === "swiftFeet") {
    return "Swift Feet";
  }
  if (effect.id === "armorBreaker") {
    const amount = Math.max(1, effect.amount || 1);
    return `Armor Breaker ${amount}`;
  }
  if (effect.id === "parry") {
    return "Parry";
  }
  if (effect.id === "identityExpendRequiresMatchingActive") {
    return "Identity Match: expend for resources of the chosen Identity only while a matching active is slotted in this column";
  }
  if (effect.id === "charges") {
    const label = getChargeKindLabel(effect.charge);
    return chargeKindClearsAtTurnEnd(effect.charge)
      ? `${label} (clear at end of turn)`
      : label;
  }
  if (effect.id === "addCharge") {
    const amount = Math.max(1, effect.amount || 1);
    return typeof effect.label === "string" && effect.label.trim()
      ? effect.label.trim()
      : `Add ${amount} ${getChargeKindLabel(effect.charge, amount)}`;
  }
  if (effect.id === "sharpen") {
    const amount = Math.max(1, effect.amount || 1);
    const costText = formatCost(effect.cost || {});
    const target = effect.forExpend ? "expend damage" : "slotted damage";
    const label = `Sharpen ${amount} (${target})`;
    return costText ? `${costText} → ${label}` : label;
  }
  if (effect.id === "hone") {
    return formatHoneLabel(effect.amount);
  }
  if (effect.id === "fletch") {
    return formatFletchLabel(effect.amount);
  }
  if (effect.id === "research") {
    return "Research";
  }
  if (effect.id === "repair") {
    if (effect.costFromIdentity != null && !Object.keys(effect.cost || {}).length) {
      return `Repair Identity ${Math.max(1, Math.floor(Number(effect.costFromIdentity) || 1))}`;
    }
    return "Repair";
  }
  if (effect.id === "durability") {
    return `Durability ${Math.max(1, effect.amount || 1)}`;
  }
  if (effect.id === "enableEncore") {
    return "Encore";
  }
  if (effect.id === "turnStartGrantResources") {
    const grants = costEntries(effect.resources || {});
    if (!grants.length) return "Turn Start Resources";
    return `Turn Start ${grants
      .map(([attr, value]) => `+${value} ${attr}`)
      .join(", ")}`;
  }
  if (effect.id === "turnEndConvertToPersistent") {
    const grants = costEntries(effect.resources || {});
    if (!grants.length) return "Turn End Persist";
    return `Turn End Persist ${grants
      .map(([attr, value]) => `${value} ${attr}`)
      .join(", ")}`;
  }
  if (effect.id === "turnStartGrantDodge") {
    const amount = Math.max(0, effect.amount || 0);
    return amount ? `Turn Start Dodge +${amount}` : "Turn Start Dodge";
  }
  if (effect.id === "turnStartTemporaryMagicArmor") {
    const amount = Math.max(0, effect.amount || 0);
    return amount
      ? `Turn Start Magic Armor +${amount}`
      : "Turn Start Magic Armor";
  }
  if (effect.id === "turnStartTemporaryPhysicalArmor") {
    const amount = Math.max(0, effect.amount || 0);
    return amount
      ? `Turn Start Physical Armor +${amount}`
      : "Turn Start Physical Armor";
  }
  if (effect.id === "oncePerTurnOutgoingDamageAdd") {
    const amount = Math.max(0, effect.amount || 0);
    const typeParts = [];
    if (effect.match?.type) {
      typeParts.push(formatAttributeLabel(effect.match.type));
    }
    if (effect.damageType) {
      typeParts.push(formatAttributeLabel(effect.damageType));
    }
    const typeLabel = typeParts.length ? typeParts.join(" ") : "Damage";
    return amount
      ? `Once Per Turn Next ${typeLabel} +${amount}`
      : "Once Per Turn Damage";
  }
  if (effect.id === "oncePerTurnPhysicalArmorGainAdd") {
    const amount = Math.max(0, effect.amount || 0);
    return amount
      ? `Once Per Turn Physical Armor Gain +${amount}`
      : "Once Per Turn Physical Armor Gain";
  }
  if (effect.id === "oncePerTurnAbilityOutputAdd") {
    const amount = Math.max(0, effect.amount || 0);
    return amount
      ? `Once Per Turn Ability Output +${amount}`
      : "Once Per Turn Ability Output";
  }
  if (effect.id === "ninjutsuThrowableToken") {
    const names = (effect.cardIds || [])
      .map((id) =>
        String(id || "")
          .replace(/^eq_/, "")
          .replace(/_/g, " ")
          .replace(/\b\w/g, (ch) => ch.toUpperCase())
          .trim(),
      )
      .filter(Boolean);
    return names.length
      ? `Throwable Token (${names.join(" · ")})`
      : "Throwable Token";
  }
  if (effect.id === "incomingDamageTakenAdd") {
    const percent = Math.max(0, effect.percent || 0);
    const typeLabel = effect.damageType
      ? ` ${formatAttributeLabel(effect.damageType)}`
      : "";
    return percent
      ? `Incoming${typeLabel} Damage +${percent}%`
      : "Incoming Damage";
  }
  if (effect.id === "activeAbilityOutputMult") {
    const mult = Number(effect.mult);
    if (!Number.isFinite(mult)) return "Active Output";
    return `Active Output x${mult}`;
  }
  if (effect.id === "outgoingOutputMult") {
    const mult = Number(effect.mult);
    if (!Number.isFinite(mult)) return "Output Multiplier";
    const typesAny = Array.isArray(effect.match?.typesAny)
      ? effect.match.typesAny.filter(Boolean)
      : Array.isArray(effect.match?.types)
        ? effect.match.types.filter(Boolean)
        : effect.match?.type
          ? [effect.match.type]
          : [];
    const typeLabel = typesAny.length
      ? ` (${typesAny.map((entry) => formatAttributeLabel(entry)).join(" / ")})`
      : "";
    const min =
      effect.min != null ? Math.max(0, Math.floor(Number(effect.min) || 0)) : 0;
    const minLabel = min > 0 ? `, min ${min}` : "";
    return `Output x${mult}${typeLabel}${minLabel}`;
  }
  if (effect.id === "cannotGainResources") {
    const attrs = (effect.attributes || []).filter(Boolean);
    return attrs.length
      ? `Cannot Gain ${attrs.join(" · ")}`
      : "Cannot Gain Resources";
  }
  if (effect.id === "restrictOutgoingDamageTypes") {
    const types = (effect.types || []).filter(Boolean);
    return types.length
      ? `Restrict Damage Types (${types.join(" · ")})`
      : "Restrict Damage Types";
  }
  if (effect.id === "restrictAilments") {
    const ailments = (effect.ailments || []).filter(Boolean);
    return ailments.length
      ? `Restrict Ailments (${ailments.join(" · ")})`
      : "Restrict Ailments";
  }
  const label = formatEffectFilterLabel(effect.id);
  const matchTags = formatAugmentMatchTags({ match: effect.match });
  const parts = [label];
  if (effect.amount != null) parts.push(String(effect.amount));
  if (effect.percent != null) parts.push(`${effect.percent}%`);
  if (effect.mult != null) parts.push(`x${effect.mult}`);
  if (effect.damageType) parts.push(String(effect.damageType));
  if (Array.isArray(effect.ailments) && effect.ailments.length) {
    parts.push(`(${effect.ailments.join(" · ")})`);
  }
  if (matchTags.length) parts.push(`(${matchTags.join(" · ")})`);
  return parts.join(" ");
}

function getEquipmentDescriptionLines(card, options = {}) {
  if (!card || !cardHasType(card, "equipment")) return [];
  const lines = [];
  const effectOptions = { ...options, equipment: card };
  getEquipmentTriggerDisplays(card).forEach((entry) => {
    lines.push(`${entry.label}: ${entry.text}.`);
  });

  getEquipmentPassiveDescriptionEntries(card).forEach((entry) => {
    lines.push(
      entry.outputDelta
        ? { kind: "passive", text: entry.text, outputDelta: entry.outputDelta }
        : entry.text,
    );
  });
  getEquipmentUniqueTextClauses(card).forEach((clause) => {
    lines.push(/[.!?]$/.test(clause) ? clause : `${clause}.`);
  });

  const persistGrants = persistGrantEntries(card.activatedEffect);
  if (persistGrants.length) {
    lines.push({ kind: "expendIcons", entries: persistGrants, persist: true });
  }
  listActivatedEffectFaceLines(
    isGrantResourcesOnlyChooseEffect(card.activatedEffect)
      ? null
      : card.activatedEffect,
    effectOptions,
  ).forEach((entry) => {
    lines.push({
      kind: "expendEffect",
      text: entry.text,
      cost: entry.cost,
      chargeCost: entry.chargeCost,
      durabilityCost: entry.durabilityCost,
      selfDamage: entry.selfDamage || null,
      identity: entry.identity || null,
      outputDelta: entry.outputDelta || 0,
    });
  });

  listPaidTempEffectFaceLines(card).forEach((entry) => {
    lines.push({
      kind: "paidEffect",
      text: entry.text,
      cost: entry.cost,
      identity: entry.identity || null,
    });
  });

  return lines;
}

function getCardDetailLines(card, options = {}) {
  if (!card) return [];
  const isEquipment = cardHasType(card, "equipment");
  const lines = [];
  const attribute = isEquipment
    ? (card.attributes || []).join(" · ") || primaryAttribute(card)
    : primaryAttribute(card);
  if (attribute) lines.push(`Attribute ${attribute}`);
  if (isEquipment && card.slot) {
    lines.push(`Slot ${formatSlotLabel(card.slot)}`);
  }
  const tags = isEquipment
    ? formatEquipmentCardTags(card)
    : formatAbilityCardTags(card);
  if (tags) lines.push(tags);
  if (!isEquipment && card.kind === "active") {
    const costs = costEntries(card.cost);
    if (costs.length && !abilityChoiceHasCostClient(card)) {
      lines.push(
        `Cost ${costs.map(([attr, value]) => `${value} ${attr}`).join(", ")}`,
      );
    }
    const healthCost = getAbilityHealthCostAmount(card.effect);
    if (healthCost > 0) {
      lines.push(`Cost ${healthCost} health`);
    }
  }
  if (isEquipment) {
    const grantChoices = listEquipmentResourceGrantChoices(card);
    if (grantChoices.length) {
      lines.push(
        `Resources ${grantChoices
          .map((entries) =>
            entries.map(([attr, value]) => `${value} ${attr}`).join(", "),
          )
          .join(" / ")}`,
      );
    }
    getEquipmentTriggerDisplays(card).forEach((entry) => {
      lines.push(`${entry.examLabel}: ${entry.text}`);
    });
    const persistGrants = persistGrantEntries(card.activatedEffect);
    if (persistGrants.length) {
      lines.push(
        `Expend persist ${persistGrants
          .map(([attr, value]) => `${value}* ${attr}`)
          .join(", ")}`,
      );
    }
    const activated = isGrantResourcesOnlyChooseEffect(card.activatedEffect)
      ? null
      : formatActivatedEffectTextWithoutPersist(card.activatedEffect, {
          ...options,
          equipment: card,
        });
    if (activated) lines.push(`Expend effect: ${activated}`);
    (card.passiveEffects || []).forEach((effect) => {
      if (
        effect?.id === "sharpen" ||
        effect?.id === "hone" ||
        effect?.id === "fletch" ||
        effect?.id === "enableEncore" ||
        effect?.id === "research" ||
        effect?.id === "repair" ||
        effect?.id === "refresh" ||
        effect?.id === "addCharge" ||
        (effect?.id === "slottedActiveDamageAdd" &&
          (effect.costValue != null ||
            Object.values(effect.cost || {}).some(
              (amount) => Math.max(0, Number(amount) || 0) > 0,
            )))
      ) {
        return;
      }
      const line = formatPassiveEffectLine(effect);
      if (line) lines.push(`Passive: ${line}`);
    });
    listPaidTempEffectFaceLines(card).forEach((entry) => {
      const costText = entry.cost
        .map(([attr, value]) => `${value} ${attr}`)
        .join(", ");
      lines.push(costText ? `${costText} → ${entry.text}` : entry.text);
    });
  } else if (card.kind === "augment") {
    formatAugmentEffectLines(card).forEach((line) => lines.push(line));
  } else if (card.kind === "essence") {
    formatEssenceEffectLines(card).forEach((line) => lines.push(line));
    if (!lines.length && card.text) lines.push(card.text);
  } else if (card.kind === "active" && abilityChoiceHasCostClient(card)) {
    lines.push(...formatAbilityChoiceCostLines(card, options));
  } else {
    const effectText = formatActivatedEffectText(card.effect, options);
    if (effectText) lines.push(effectText);
  }
  const invokeText = formatActivatedEffectText(card.invoke, options);
  if (invokeText) lines.push(`Invoke: ${invokeText}`);
  const comboText = formatComboHelperText(card);
  if (comboText) lines.push(comboText);
  return lines;
}

function formatHandExamTagText(tags) {
  if (!tags) return "";
  return String(tags).replace(/ · /g, " - ");
}

function getHandExamLabeledDetailEntries(card, options = {}) {
  if (!card) return [];
  const isEquipment = cardHasType(card, "equipment");
  const isAspect = cardHasType(card, "aspect");
  const entries = [];
  const setId = normalizeCardSet(card.set);
  const expansionSetEntry = {
    label: "Expansion Set",
    value: formatCardSetLabel(setId),
    key: `set-${setId}`,
  };
  const cardTypeValue = isEquipment
    ? "Equipment"
    : isAspect
      ? "Aspect"
      : "Ability";
  entries.push({
    label: "Card Type",
    value: cardTypeValue,
    keywordId: cardTypeValue.toLowerCase(),
    key: "card-type",
  });
  const attribute = isEquipment || isAspect
    ? (card.attributes || []).join(" · ") || primaryAttribute(card)
    : primaryAttribute(card);
  if (attribute) {
    entries.push({ label: "Attribute", value: attribute });
  }
  entries.push({
    label: "Power",
    value: String(getCardPower(card)),
    powerIcon: true,
  });
  if (isEquipment && card.slot) {
    entries.push({ label: "Slot", value: formatSlotLabel(card.slot) });
  }
  if (isEquipment && getEquipmentTier(card) >= 2) {
    const tier = getEquipmentTier(card);
    const types = (card.type || [])
      .filter((type) => EQUIPMENT_ITEM_TYPES.has(type))
      .map(formatTypeFilterLabel)
      .join(" or ");
    entries.push({
      label: "Upgrades",
      value: `Tier ${tier - 1} ${types || "piece"}`,
    });
  }
  const underCards = card.underCards || options.equipment?.underCards;
  if (isEquipment && Array.isArray(underCards) && underCards.length) {
    entries.push({
      label: "Upgraded from",
      value: underCards.map((under) => under.name || under.cardId).join(" → "),
    });
  }
  if (isAspect) {
    entries.push({
      label: "Type",
      value: formatHandExamTagText(formatCardTypes(card) || "aspect"),
      rawTags: formatCardTypes(card) || "aspect",
    });
    entries.push(expansionSetEntry);
    (card.passiveEffects || []).forEach((effect, index) => {
      if (effect?.id === "agnosticExclusiveNeutralThresholds") return;
      if (
        effect?.id === "turnEndConvertToPersistent" &&
        formatAspectExamEffectText(card)
      ) {
        return;
      }
      const line = formatPassiveEffectLine(effect);
      if (!line) return;
      entries.push({
        label: "Passive",
        value: line,
        key: `passive-${effect.id || index}-${index}`,
      });
    });
    return entries;
  }
  const tags = isEquipment
    ? formatEquipmentCardTags(card)
    : formatAbilityCardTags(card);
  if (tags) {
    entries.push({
      label: "Type",
      value: formatHandExamTagText(tags),
      rawTags: tags,
    });
  }
  if (!isEquipment && card.kind === "active") {
    const costs = costEntries(card.cost);
    if (costs.length && !abilityChoiceHasCostClient(card)) {
      entries.push({
        label: "Activation Cost",
        value: costs.map(([attr, value]) => `${value} ${attr}`).join(", "),
        costEntries: costs,
      });
    }
    const healthCost = getAbilityHealthCostAmount(card.effect);
    if (healthCost > 0 && !abilityChoiceHasHealthCostClient(card)) {
      entries.push({
        label: "Activation Cost",
        value: `${healthCost} health`,
      });
    }
  }
  if (isEquipment) {
    const grantChoices = listEquipmentResourceGrantChoices(card);
    if (grantChoices.length) {
      entries.push({
        label: "Resources",
        value: grantChoices
          .map((choice) =>
            choice.map(([attr, value]) => `${value} ${attr}`).join(", "),
          )
          .join(" / "),
        resourceChoices: grantChoices,
      });
    }
    const durabilityPassive = (card.passiveEffects || []).find(
      (effect) => effect?.id === "durability",
    );
    if (durabilityPassive) {
      const durabilityAmount = Math.max(
        1,
        Math.floor(Number(durabilityPassive.amount) || 1),
      );
      entries.push({
        label: "Durability",
        value: String(durabilityAmount),
        keywordId: "durability",
        key: "durability",
      });
    }
    entries.push(expansionSetEntry);
    const invokeText = formatActivatedEffectText(card.invoke, options);
    if (invokeText) {
      entries.push({ label: "Invoke", value: invokeText });
    }
    return entries;
  }
  entries.push(expansionSetEntry);
  if (card.kind === "augment") {
    formatAugmentEffectLines(card, {
      includeCostIncrease: false,
      includeCostMult: true,
    }).forEach((line, index) => {
      entries.push({ label: "Effect", value: line, key: `augment-${index}` });
    });
  } else if (card.kind === "essence") {
    const essenceLines = formatEssenceEffectLines(card);
    if (essenceLines.length) {
      essenceLines.forEach((line, index) => {
        entries.push({ label: "Effect", value: line, key: `essence-${index}` });
      });
    } else if (card.text) {
      entries.push({ label: "Effect", value: card.text });
    }
  } else if (
    card.kind === "active" &&
    (abilityChoiceHasCostClient(card) || abilityChoiceHasHealthCostClient(card))
  ) {
    listAbilityChoiceModes(card, { ...options, omitHealthPayment: true }).forEach(
      (mode) => {
        entries.push({
          label: "Effect",
          value: mode.text,
          key: `choice-${mode.index}`,
          abilityMode: {
            cost: costEntries(mode.cost),
            healthCost: mode.healthCost,
            text: mode.text,
            outputDelta: mode.outputDelta,
          },
        });
      },
    );
  } else {
    const effectText = formatActivatedEffectText(card.effect, options);
    if (effectText) {
      entries.push({ label: "Effect", value: effectText });
    }
  }
  const invokeText = formatActivatedEffectText(card.invoke, options);
  if (invokeText) {
    entries.push({ label: "Invoke", value: invokeText });
  }
  const comboText = formatComboHelperText(card);
  if (comboText) {
    entries.push({ label: "Effect", value: comboText });
  }
  return entries;
}

const CARD_EXAM_STAT_LABELS = {
  "Activation Cost": "Cost",
};

function groupCardExamEntries(entries) {
  const groups = {
    cardType: null,
    tags: null,
    setEntry: null,
    stats: [],
    effects: [],
    passives: [],
    invokes: [],
  };
  (entries || []).forEach((entry) => {
    if (entry.label === "Card Type") groups.cardType = entry;
    else if (entry.label === "Type") groups.tags = entry;
    else if (entry.label === "Expansion Set") groups.setEntry = entry;
    else if (entry.label === "Effect") groups.effects.push(entry);
    else if (entry.label === "Passive") groups.passives.push(entry);
    else if (entry.label === "Invoke") groups.invokes.push(entry);
    else if (entry.label !== "Attribute" && entry.label !== "Power") {
      const label = CARD_EXAM_STAT_LABELS[entry.label] || entry.label;
      const existing = groups.stats.find((stat) => stat.label === label);
      if (existing) existing.entries.push(entry);
      else groups.stats.push({ label, entries: [entry] });
    }
  });
  return groups;
}

function splitEquipmentExamDescriptionLines(lines) {
  const expend = [];
  const actions = [];
  const passives = [];
  const rules = [];
  (lines || []).forEach((line) => {
    if (line && typeof line === "object") {
      if (line.kind === "paidEffect") actions.push(line);
      else if (line.kind === "passive") {
        passives.push({
          ...line,
          text: String(line.text || "").replace(/^passive:\s*/i, ""),
        });
      } else expend.push(line);
    } else if (isEquipmentPassiveDetailLine(line)) {
      passives.push(String(line).replace(/^passive:\s*/i, ""));
    } else if (line) {
      rules.push(line);
    }
  });
  return { expend, actions, passives, rules };
}

function getCardExamV2PanelProps(card) {
  const attribute = primaryAttribute(card);
  const hybrid = getHybridOutlineProps(card);
  return {
    className: hybrid.className || TINT_CLASS[attribute] || "",
    style: {
      ...(hybrid.style || {}),
      "--exam-accent": ATTRIBUTE_COLOR_VAR[attribute] || "var(--ai-accent)",
    },
  };
}

function summarizeExamCostEntries(entries) {
  return (entries || [])
    .filter(([, value]) => (Number(value) || 0) > 0)
    .map(([attribute, value]) => [attribute, Number(value) || 0]);
}

function summarizeExamEntry(entry) {
  if (entry?.abilityMode) {
    return {
      text: String(entry.abilityMode.text || entry.value || ""),
      cost: summarizeExamCostEntries(entry.abilityMode.cost),
      healthCost: Math.max(0, Number(entry.abilityMode.healthCost) || 0),
    };
  }
  return {
    text: String(entry?.value ?? ""),
    cost: summarizeExamCostEntries(entry?.costEntries),
    grantChoices: (entry?.resourceChoices || []).map(summarizeExamCostEntries),
  };
}

function summarizeEquipmentExamLine(line) {
  if (!line || typeof line !== "object") return { text: String(line || "") };
  if (line.kind === "passive") return { text: String(line.text || "") };
  const iconChoices =
    line.kind === "expendIcons" && line.choices?.length ? line.choices : [];
  return {
    text: String(line.text || ""),
    expend: line.kind !== "paidEffect",
    identity: line.identity || null,
    cost: summarizeExamCostEntries(line.cost),
    chargeCost: line.chargeCost ?? null,
    durabilityCost: Math.max(0, Math.floor(Number(line.durabilityCost) || 0)),
    selfDamage: (line.selfDamage || []).map((entry) => ({
      amount: entry.amount,
      damageType: entry.damageType,
    })),
    grants:
      line.kind === "expendIcons" && !iconChoices.length
        ? summarizeExamCostEntries(line.entries)
        : [],
    grantChoices: iconChoices.map(summarizeExamCostEntries),
    persist: Boolean(line.persist),
  };
}

export function buildCardExamSummary(card) {
  if (!card) return null;
  const isEquipment = cardHasType(card, "equipment");
  const isAspect = cardHasType(card, "aspect");
  const isAbility = !isEquipment && !isAspect;
  const groups = groupCardExamEntries(getHandExamLabeledDetailEntries(card));
  const kindKey = isAbility ? String(card.kind || "").toLowerCase() : "";
  const tags = String(groups.tags?.rawTags || "")
    .split(" · ")
    .map((part) => part.trim())
    .filter(Boolean)
    .filter((part) => {
      const lower = part.toLowerCase();
      return lower !== kindKey && lower !== "aspect";
    })
    .map(formatAttributeLabel);
  const equipmentLines = isEquipment
    ? splitEquipmentExamDescriptionLines(
        getEquipmentDescriptionLines(card).filter(
          (line) =>
            typeof line !== "string" || !isEquipmentTriggerDetailLine(line),
        ),
      )
    : { expend: [], actions: [], passives: [], rules: [] };
  const abilityFaceText = isAbility ? formatAbilityFaceText(card) : null;
  const abilityFaceLines =
    abilityFaceText &&
    !groups.effects.length &&
    card.effect?.id !== "summonMinion"
      ? abilityFaceText.split("\n").filter((line) => line.trim())
      : [];
  const augmentMatchTags =
    kindKey === "augment" ? formatAugmentMatchTags(card.augment) : [];
  const augmentMatchText = augmentMatchTags.join(" · ");
  const effectEntries = augmentMatchText
    ? groups.effects.filter((entry) => entry.value !== augmentMatchText)
    : groups.effects;
  const aspectEffectText = isAspect ? formatAspectExamEffectText(card) : null;
  const setId = normalizeCardSet(card.set);
  const sections = [];
  const pushSection = (key, title, lines) => {
    const list = lines.filter((line) => String(line?.text || "").trim());
    if (list.length) sections.push({ key, title, lines: list });
  };
  if (aspectEffectText) {
    pushSection(
      "aspect-effect",
      t("match.panel.matchEffect"),
      String(aspectEffectText)
        .split("\n")
        .map((text) => ({ text })),
    );
  } else {
    pushSection(
      "passive",
      t("match.panel.passive"),
      groups.passives.map(summarizeExamEntry),
    );
  }
  pushSection(
    "effect",
    kindKey
      ? t("match.panel.kindEffect", { kind: formatAttributeLabel(kindKey) })
      : t("match.panel.effect"),
    [
      ...effectEntries.map(summarizeExamEntry),
      ...abilityFaceLines.map((text) => ({ text })),
    ],
  );
  pushSection(
    "triggers",
    t("match.panel.triggers"),
    getEquipmentTriggerDisplays(card).map((entry) => ({
      label: entry.examLabel,
      text: entry.text,
    })),
  );
  pushSection(
    "expend",
    t("match.panel.expend"),
    equipmentLines.expend.map(summarizeEquipmentExamLine),
  );
  pushSection(
    "equipment-actions",
    t("match.panel.equipmentActions"),
    equipmentLines.actions.map(summarizeEquipmentExamLine),
  );
  pushSection(
    "equipment-passive",
    t("match.panel.passive"),
    equipmentLines.passives.map(summarizeEquipmentExamLine),
  );
  pushSection(
    "card-text",
    t("match.panel.cardText"),
    equipmentLines.rules.map(summarizeEquipmentExamLine),
  );
  pushSection(
    "invoke",
    t("match.panel.invoke"),
    groups.invokes.map(summarizeExamEntry),
  );
  return {
    id: card.id,
    name: card.name,
    cardType: groups.cardType?.value || null,
    tags,
    primaryAttribute: primaryAttribute(card) || null,
    attributes: cardAttributeList(card),
    power: getCardPower(card),
    tier: isEquipment ? getEquipmentTier(card) : null,
    kind: kindKey ? formatAttributeLabel(kindKey) : null,
    linksTo: augmentMatchTags.map(String),
    slot: isEquipment ? formatSlotLabel(card.slot) : null,
    boardSlot: isEquipment ? formatSlotLabel(resolveCardBoardSlot(card)) : null,
    set: { id: setId, label: formatCardSetLabel(setId) },
    stats: groups.stats
      .filter((stat) => stat.label !== "Slot")
      .map((stat) => ({
        label: stat.label,
        values: stat.entries.map(summarizeExamEntry),
      })),
    sections,
    levelTitle: t("match.panel.levelProgression"),
    levels: cardHasType(card, "ability")
      ? getAbilityLevelTableRows(card).map((row) => ({
          level: row.level,
          mode: row.mode || 0,
          effect: String(row.effect || "—"),
          cost: summarizeExamCostEntries(row.cost?.entries),
          healthCost: Math.max(0, Number(row.cost?.healthCost) || 0),
        }))
      : [],
    traitTitle: t("match.panel.traitTree"),
    traits: listAspectTraitExamLevels(card).map((entry) => ({
      level: entry.level,
      options: entry.options.map((option) => ({
        label: String(option.label || ""),
        detail: String(option.detail || ""),
      })),
    })),
  };
}

function formatAbilityChoiceCostLines(card, options = {}) {
  return listAbilityChoiceModes(card, options).map((mode) => {
    const costText = costEntries(mode.cost)
      .map(([attr, value]) => `${value} ${attr}`)
      .join(", ");
    return costText ? `${costText} → ${mode.text}` : mode.text;
  });
}

function formatAbilityCardTags(card) {
  if (!card) return "";
  const parts = [];
  if (card.kind) parts.push(card.kind);
  if (cardHasType(card, "melee")) parts.push("melee");
  if (cardHasType(card, "ranged")) parts.push("ranged");
  if (cardHasType(card, "poison")) parts.push("poison");
  else if (cardHasType(card, "fire")) parts.push("fire");
  else if (cardHasType(card, "cold")) parts.push("cold");
  else if (cardHasType(card, "lightning")) parts.push("lightning");
  else if (cardHasType(card, "curse")) parts.push("curse");
  else if (cardHasType(card, "magical")) parts.push("magical");
  else if (card.effect?.damageType) parts.push(card.effect.damageType);
  else if (cardHasType(card, "magic")) parts.push("magic");
  if (cardHasType(card, "sacrifice")) parts.push("sacrifice");
  if (cardHasType(card, "dark")) parts.push("dark");
  if (cardHasType(card, "health")) parts.push("health");
  if (cardHasType(card, "healing")) parts.push("healing");
  if (cardHasType(card, "resource")) parts.push("resource");
  return parts.join(" · ");
}

function formatEquipmentCardTags(card) {
  if (!card) return "";
  return formatCardTypes(card);
}

function formatComboHelperText(card) {
  if (!(card?.combo > 0)) return null;
  const costEntries = Object.entries(card.comboCost || {}).filter(
    ([, amount]) => (Number(amount) || 0) > 0,
  );
  const defaultAttribute = primaryAttribute(card);
  const usesDefaultCost =
    !costEntries.length ||
    (costEntries.length === 1 &&
      costEntries[0][0] === defaultAttribute &&
      Number(costEntries[0][1]) === card.combo);
  if (usesDefaultCost) return `Combo ${card.combo}`;
  const costText = costEntries
    .map(([attr, amount]) => `+${amount} ${attr}`)
    .join(", ");
  return `Combo ${card.combo} (${costText} cost per step)`;
}

function seatArmorValues(seat) {
  return {
    physicalArmor: Math.max(0, seat?.physicalArmor || 0),
    magicArmor: Math.max(0, seat?.magicArmor || 0),
    fireResistance: Math.max(0, seat?.fireResistance || 0),
    coldResistance: Math.max(0, seat?.coldResistance || 0),
    lightningResistance: Math.max(0, seat?.lightningResistance || 0),
  };
}

function renderSeatArmorBadges(
  seat,
  className,
  { keywordProps = {} } = {},
) {
  const {
    physicalArmor,
    magicArmor,
    fireResistance,
    coldResistance,
    lightningResistance,
  } = seatArmorValues(seat);
  if (
    physicalArmor <= 0 &&
    magicArmor <= 0 &&
    fireResistance <= 0 &&
    coldResistance <= 0 &&
    lightningResistance <= 0
  ) {
    return null;
  }
  const fireLabel = "FIRE RES";
  const coldLabel = "COLD RES";
  const lightningLabel = "LTN RES";
  return (
    <>
      {physicalArmor > 0 ? (
        <StatusEffectIconStack
          effectId="physicalArmor"
          amount={physicalArmor}
          className={className}
          keywordProps={keywordProps}
        />
      ) : null}
      {magicArmor > 0 ? (
        <StatusEffectIconStack
          effectId="magicArmor"
          amount={magicArmor}
          className={className}
          keywordProps={keywordProps}
        />
      ) : null}
      {fireResistance > 0
        ? renderStatusKeywordBadge(
            "fireResistance",
            `${className} ${styles.fireResistanceBadge}`,
            <>
              {fireLabel} {fireResistance}
            </>,
            keywordProps,
          )
        : null}
      {coldResistance > 0
        ? renderStatusKeywordBadge(
            "coldResistance",
            `${className} ${styles.coldResistanceBadge}`,
            <>
              {coldLabel} {coldResistance}
            </>,
            keywordProps,
          )
        : null}
      {lightningResistance > 0
        ? renderStatusKeywordBadge(
            "lightningResistance",
            `${className} ${styles.lightningResistanceBadge}`,
            <>
              {lightningLabel} {lightningResistance}
            </>,
            keywordProps,
          )
        : null}
    </>
  );
}

function renderSeatStatusBadges(
  seat,
  { board = false, keywordProps = {} } = {},
) {
  if (!seat) return null;
  const {
    physicalArmor,
    magicArmor,
    fireResistance,
    coldResistance,
    lightningResistance,
  } = seatArmorValues(seat);
  const dodgeStacks = Math.max(0, seat.dodgeStacks || 0);
  const shadowStacks = Math.max(0, seat.shadowStacks || 0);
  const thornsStacks = Math.max(0, seat.thornsStacks || 0);
  const bleedStacks = Math.max(0, seat.bleedStacks || 0);
  const poisonStacks = Math.max(0, seat.poisonStacks || 0);
  const regenerationStacks = Math.max(0, seat.regenerationStacks || 0);
  const burnStacks = Math.max(0, seat.burnStacks || 0);
  const chillStacks = Math.max(0, seat.chillStacks || 0);
  const shockStacks = Math.max(0, seat.shockStacks || 0);
  const witherStacks = Math.max(0, seat.witherStacks || 0);
  const restockStacks = Math.max(0, seat.restockStacks || 0);
  if (
    physicalArmor <= 0 &&
    magicArmor <= 0 &&
    fireResistance <= 0 &&
    coldResistance <= 0 &&
    lightningResistance <= 0 &&
    dodgeStacks <= 0 &&
    shadowStacks <= 0 &&
    thornsStacks <= 0 &&
    bleedStacks <= 0 &&
    poisonStacks <= 0 &&
    regenerationStacks <= 0 &&
    burnStacks <= 0 &&
    chillStacks <= 0 &&
    shockStacks <= 0 &&
    witherStacks <= 0 &&
    restockStacks <= 0
  ) {
    return null;
  }
  const witherClass = board ? styles.witherBoardIcon : styles.witherStatusBadge;
  return (
    <>
      {renderSeatArmorBadges(seat, "", { keywordProps })}
      {dodgeStacks > 0 ? (
        <StatusEffectIconStack
          effectId="dodge"
          amount={dodgeStacks}
          keywordProps={keywordProps}
        />
      ) : null}
      {shadowStacks > 0 ? (
        <StatusEffectIconStack
          effectId="shadow"
          amount={shadowStacks}
          keywordProps={keywordProps}
        />
      ) : null}
      {thornsStacks > 0 ? (
        <StatusEffectIconStack
          effectId="thorns"
          amount={thornsStacks}
          keywordProps={keywordProps}
        />
      ) : null}
      {regenerationStacks > 0 ? (
        <StatusEffectIconStack
          effectId="regeneration"
          amount={regenerationStacks}
          keywordProps={keywordProps}
        />
      ) : null}
      {bleedStacks > 0 ? (
        <StatusEffectIconStack
          effectId="bleed"
          amount={bleedStacks}
          keywordProps={keywordProps}
        />
      ) : null}
      {poisonStacks > 0 ? (
        <StatusEffectIconStack
          effectId="poison"
          amount={poisonStacks}
          keywordProps={keywordProps}
        />
      ) : null}
      {burnStacks > 0 ? (
        <StatusEffectIconStack
          effectId="burn"
          amount={burnStacks}
          keywordProps={keywordProps}
        />
      ) : null}
      {chillStacks > 0 ? (
        <StatusEffectIconStack
          effectId="chill"
          amount={chillStacks}
          keywordProps={keywordProps}
        />
      ) : null}
      {shockStacks > 0 ? (
        <StatusEffectIconStack
          effectId="shock"
          amount={shockStacks}
          keywordProps={keywordProps}
        />
      ) : null}
      {witherStacks > 0
        ? renderStatusKeywordBadge(
            "wither",
            witherClass,
            <>Wither {witherStacks}</>,
            keywordProps,
          )
        : null}
      {restockStacks > 0 ? (
        <StatusEffectIconStack
          effectId="restock"
          amount={restockStacks}
          keywordProps={keywordProps}
        />
      ) : null}
    </>
  );
}

const MAX_ABILITY_LEVEL = 4;

function getAbilityDisplayLevel(abilityOrCard) {
  if (!abilityOrCard) return 1;
  const level =
    abilityOrCard.effectiveLevel != null
      ? abilityOrCard.effectiveLevel
      : abilityOrCard.level;
  return Math.max(1, Math.floor(Number(level) || 1));
}

function getEquipmentTier(card) {
  const tier = Math.floor(Number(card?.tier) || 0);
  return tier >= 1 ? tier : 1;
}

function CardRankIcons({ count: rawCount, iconUrl, label, className = "" }) {
  const count = Math.max(0, Math.floor(Number(rawCount) || 0));
  if (count <= 0) return null;
  const tokens =
    count >= 5
      ? [{ count, key: "level-stack" }]
      : Array.from({ length: count }, (_, index) => ({
          count: 1,
          key: `level-${index}`,
        }));
  return (
    <span
      className={`${styles.abilityLevelIconRow} ${className || ""}`}
      aria-label={label(count)}
    >
      {tokens.map((token) => (
        <span
          key={token.key}
          className={`${styles.abilityLevelIconItem}${
            token.count > 1 ? ` ${styles.abilityLevelIconItemCounted}` : ""
          }`}
        >
          {token.count > 1 ? (
            <span className={styles.abilityLevelIconCount} aria-hidden="true">
              {token.count}
            </span>
          ) : null}
          <img
            src={iconUrl}
            alt=""
            className={styles.abilityLevelIcon}
            draggable={false}
            aria-hidden="true"
          />
        </span>
      ))}
    </span>
  );
}

function AbilityLevelIcons({ level = 1, className = "" }) {
  return (
    <CardRankIcons
      count={level}
      iconUrl={ABILITY_LEVEL_ICON_URL}
      label={(count) => t("match.panel.levelN", { level: count })}
      className={className}
    />
  );
}

function EquipmentTierIcons({ tier = 1, className = "" }) {
  return (
    <CardRankIcons
      count={tier}
      iconUrl={EQUIPMENT_TIER_ICON_URL}
      label={(count) => t("match.panel.tierN", { tier: count })}
      className={`${styles.equipmentTierIconRow} ${className || ""}`}
    />
  );
}

function getAbilityHealthCostAmount(effect, level = 1) {
  const entries = listEffectEntries(effect).flatMap((entry) =>
    Array.isArray(entry) && !entry.id ? entry : [entry],
  );
  const self = entries.find((entry) => entry?.id === "damageSelf");
  if (!self) return 0;
  return Math.max(0, (self.amount || 0) * Math.max(1, level || 1));
}

function isNonAdditiveLevelScaleEffectId(id) {
  return (
    id === "dodgeRoll" ||
    id === "enhanceNextResourceExpend" ||
    id === "hexExpendCost" ||
    id === "activateAura" ||
    id === "summonMinion"
  );
}

function abilityUsesAdditiveLevelScale(ability) {
  if (!ability || ability.kind !== "active") return false;
  const effect = ability.effect;
  if (!effect) return false;
  if (Array.isArray(effect.choose)) return false;
  if (Array.isArray(effect)) {
    return effect.every(
      (entry) =>
        entry &&
        typeof entry.amount === "number" &&
        !isNonAdditiveLevelScaleEffectId(entry.id),
    );
  }
  if (typeof effect.amount !== "number") return false;
  return !isNonAdditiveLevelScaleEffectId(effect.id);
}

function getAbilityLevelOutputBonus(level) {
  const lv = Math.max(1, Math.floor(Number(level) || 1));
  let bonus = 0;
  if (lv >= 2) bonus += 1;
  if (lv >= 3) bonus += 2;
  if (lv >= 4) bonus += 3;
  return bonus;
}

function effectEntryHasLevelTable(entry) {
  return Boolean(
    entry &&
      !isNonAdditiveLevelScaleEffectId(entry.id) &&
      ((Array.isArray(entry.amountByLevel) && entry.amountByLevel.length) ||
        (Array.isArray(entry.extraHit?.amountByLevel) &&
          entry.extraHit.amountByLevel.length)),
  );
}

function abilityUsesLevelTableScale(ability) {
  if (!ability || ability.kind !== "active") return false;
  return listEffectEntries(ability.effect)
    .flatMap((entry) => (Array.isArray(entry) ? entry : [entry]))
    .some(effectEntryHasLevelTable);
}

function resolveLevelTableValue(table, level, fallback) {
  const lv = Math.max(1, Math.floor(Number(level) || 1));
  if (Array.isArray(table) && table.length) {
    return Math.max(
      0,
      Math.floor(Number(table[Math.min(lv - 1, table.length - 1)]) || 0),
    );
  }
  return Math.max(0, Math.floor(Number(fallback) || 0));
}

function resolveEffectLevelTablesForLevel(effect, level = 1) {
  if (!effect || typeof effect !== "object") return effect;
  if (Array.isArray(effect)) {
    return effect.map((entry) => resolveEffectLevelTablesForLevel(entry, level));
  }
  if (Array.isArray(effect.choose)) {
    return {
      ...effect,
      choose: effect.choose.map((entry) =>
        resolveEffectLevelTablesForLevel(entry, level),
      ),
    };
  }
  if (!effectEntryHasLevelTable(effect)) return effect;
  const next = { ...effect };
  if (Array.isArray(effect.amountByLevel) && effect.amountByLevel.length) {
    next.amount = resolveLevelTableValue(
      effect.amountByLevel,
      level,
      effect.amount,
    );
  }
  if (
    Array.isArray(effect.extraHit?.amountByLevel) &&
    effect.extraHit.amountByLevel.length
  ) {
    next.extraHit = {
      ...effect.extraHit,
      amount: resolveLevelTableValue(
        effect.extraHit.amountByLevel,
        level,
        effect.extraHit.amount,
      ),
    };
  }
  return next;
}

function abilityDisplayScale(ability) {
  const level = Math.max(1, ability?.effectiveLevel || ability?.level || 1);
  const outputMult = Number(ability?.outputMult);
  const mult = Number.isFinite(outputMult) && outputMult > 0 ? outputMult : 1;
  if (abilityUsesAdditiveLevelScale(ability)) return mult;
  if (abilityUsesLevelTableScale(ability)) return mult;
  return level * mult;
}

function formatAbilityOutputEffectText(effect) {
  const outputs = listEffectEntries(effect).filter(
    (entry) => entry?.id !== "damageSelf",
  );
  if (!outputs.length) return null;
  return formatActivatedEffectText(outputs.length === 1 ? outputs[0] : outputs);
}

function scaleCostByLevel(cost, level = 1) {
  const mult = Math.max(1, Number(level) || 1);
  const result = {};
  FLEX_PAYMENT_ATTRIBUTES.forEach((attribute) => {
    const value = Math.floor((Number(cost?.[attribute]) || 0) * mult);
    if (value > 0) result[attribute] = value;
  });
  return result;
}

function getAbilityCostByLevelTable(cost) {
  if (!cost || typeof cost !== "object" || Array.isArray(cost)) return null;
  if (!Array.isArray(cost.byLevel) || !cost.byLevel.length) return null;
  return cost.byLevel;
}

function resolveAbilityCostFromByLevelEntry(entry, ability = null) {
  if (entry == null) return null;
  if (typeof entry === "number") {
    const attribute = FLEX_PAYMENT_ATTRIBUTES.includes(ability?.attribute)
      ? ability.attribute
      : "neutral";
    const value = Math.max(0, Math.floor(entry || 0));
    return value > 0 ? { [attribute]: value } : {};
  }
  if (typeof entry === "object" && !Array.isArray(entry)) {
    const result = {};
    FLEX_PAYMENT_ATTRIBUTES.forEach((attribute) => {
      const value = Math.max(0, Math.floor(Number(entry[attribute]) || 0));
      if (value > 0) result[attribute] = value;
    });
    return result;
  }
  return null;
}

function resolveConvertStacksMultiplier(effect, level = 1) {
  const lv = Math.max(1, Math.floor(Number(level) || 1));
  if (Array.isArray(effect?.multiplierByLevel) && effect.multiplierByLevel.length) {
    const index = Math.min(lv - 1, effect.multiplierByLevel.length - 1);
    const value = Number(effect.multiplierByLevel[index]);
    if (Number.isFinite(value) && value > 0) return value;
  }
  const multiplier = Number(effect?.multiplier);
  return Number.isFinite(multiplier) && multiplier > 0 ? multiplier : 1;
}

function resolveArmorDamageMultiplier(effect, level = 1) {
  const lv = Math.max(1, Math.floor(Number(level) || 1));
  if (
    Array.isArray(effect?.armorDamageMultiplierByLevel) &&
    effect.armorDamageMultiplierByLevel.length
  ) {
    const index = Math.min(
      lv - 1,
      effect.armorDamageMultiplierByLevel.length - 1,
    );
    const value = Number(effect.armorDamageMultiplierByLevel[index]);
    if (Number.isFinite(value) && value > 0) return value;
  }
  const multiplier = Number(effect?.armorDamageMultiplier);
  return Number.isFinite(multiplier) && multiplier > 0 ? multiplier : 1;
}

function scaleAbilityCostForLevel(cost, level = 1, ability = null) {
  const lv = Math.max(1, Math.floor(Number(level) || 1));
  const table =
    getAbilityCostByLevelTable(cost) ||
    getAbilityCostByLevelTable(ability?.cost);
  if (table) {
    const entry = table[Math.min(lv - 1, table.length - 1)];
    const resolved = resolveAbilityCostFromByLevelEntry(entry, ability);
    if (resolved) return resolved;
  }
  if (!abilityUsesAdditiveLevelScale(ability)) {
    return scaleCostByLevel(cost, lv);
  }
  const result = {};
  FLEX_PAYMENT_ATTRIBUTES.forEach((attribute) => {
    const value = Math.max(0, Math.floor(Number(cost?.[attribute]) || 0));
    if (value > 0) result[attribute] = value;
  });
  if (lv >= 3) {
    result.neutral = (result.neutral || 0) + 1;
  }
  if (lv >= 4) {
    const primary = ability?.attribute;
    if (FLEX_PAYMENT_ATTRIBUTES.includes(primary)) {
      result[primary] = (result[primary] || 0) + 1;
    }
  }
  return result;
}

function scaleEffectAmounts(effect, level = 1, { additive = false } = {}) {
  if (!effect) return effect;
  const lv = Math.max(1, Number(level) || 1);
  const bonus = additive ? getAbilityLevelOutputBonus(lv) : 0;
  const mult = additive ? 1 : lv;
  if (mult === 1 && bonus === 0) return effect;
  return listEffectEntries(effect).map((entry) => {
    const next = { ...entry };
    if (entry?.amount != null) {
      next.amount = additive
        ? entry.amount + bonus
        : entry.amount * mult;
    }
    if (!additive) {
      if (entry?.damage != null) next.damage = entry.damage * mult;
      if (entry?.burn != null) next.burn = entry.burn * mult;
      if (entry?.chill != null) next.chill = entry.chill * mult;
      if (entry?.shock != null) next.shock = entry.shock * mult;
    }
    return next;
  });
}

function scaleAbilityEffectForLevel(effect, level = 1, options = {}) {
  const additive = Boolean(options.additive);
  const mult = Math.max(1, Number(level) || 1);
  const bonus = additive ? getAbilityLevelOutputBonus(mult) : 0;
  if (!effect) return effect;
  if (Array.isArray(effect.choose)) {
    return {
      ...effect,
      choose: effect.choose.map((entry) =>
        scaleAbilityEffectForLevel(entry, level, options),
      ),
    };
  }
  if (Array.isArray(effect)) {
    return effect.map((entry) =>
      scaleAbilityEffectForLevel(entry, level, options),
    );
  }
  if (effect.id === "temporaryThorns") {
    const base = Math.max(0, effect.amount ?? effect.percent ?? 10);
    const percent = Math.min(50, base + Math.max(0, mult - 1) * 5);
    return {
      ...effect,
      amount: percent,
      percent,
      effectivePercent: percent,
    };
  }
  if (effect.id === "dodgeRoll") {
    if (!Array.isArray(effect.amountByLevel) || !effect.amountByLevel.length) {
      return effect;
    }
    const lv = Math.max(1, Math.floor(Number(level) || 1));
    const index = Math.min(lv - 1, effect.amountByLevel.length - 1);
    return {
      ...effect,
      amount: Math.max(0, Math.floor(Number(effect.amountByLevel[index]) || 0)),
    };
  }
  if (effect.id === "summonMinion") {
    const lv = Math.max(1, Math.floor(Number(level) || 1));
    const hp =
      Array.isArray(effect.hpByLevel) && effect.hpByLevel.length
        ? Math.max(
            0,
            Math.floor(
              Number(
                effect.hpByLevel[
                  Math.min(lv - 1, effect.hpByLevel.length - 1)
                ],
              ) || 0,
            ),
          )
        : effect.hp;
    const scaleAction = (action) => {
      if (!action) return action;
      if (Array.isArray(action.choose)) {
        return { choose: action.choose.map(scaleAction) };
      }
      if (Array.isArray(action)) {
        return action.map(scaleAction);
      }
      const next = { ...action };
      if (Array.isArray(action.amountByLevel) && action.amountByLevel.length) {
        const index = Math.min(lv - 1, action.amountByLevel.length - 1);
        next.amount = Math.max(
          0,
          Math.floor(Number(action.amountByLevel[index]) || 0),
        );
      }
      if (Array.isArray(action.burnByLevel) && action.burnByLevel.length) {
        const index = Math.min(lv - 1, action.burnByLevel.length - 1);
        next.burn = Math.max(
          0,
          Math.floor(Number(action.burnByLevel[index]) || 0),
        );
      }
      return next;
    };
    return {
      ...effect,
      hp,
      action: effect.action ? scaleAction(effect.action) : effect.action,
    };
  }
  if (effect.id === "activateAura") {
    const lv = Math.max(1, Math.floor(Number(level) || 1));
    return {
      ...effect,
      passiveEffects: (effect.passiveEffects || []).map((passive) => {
        if (!passive) return passive;
        const next = { ...passive };
        if (Array.isArray(passive.amountByLevel) && passive.amountByLevel.length) {
          const index = Math.min(lv - 1, passive.amountByLevel.length - 1);
          next.amount = Math.max(
            0,
            Math.floor(Number(passive.amountByLevel[index]) || 0),
          );
        }
        return next;
      }),
    };
  }
  if (effect.id === "convertStacks") {
    const multiplier = resolveConvertStacksMultiplier(effect, level);
    return {
      ...effect,
      multiplier,
      ...(additive && effect.amount != null && bonus !== 0
        ? { amount: effect.amount + bonus }
        : {}),
    };
  }
  if (effectEntryHasLevelTable(effect)) {
    return resolveEffectLevelTablesForLevel(effect, level);
  }
  if (additive) {
    if (bonus === 0 && Math.max(1, Number(level) || 1) === 1) return effect;
    const next = { ...effect };
    if (effect.amount != null && bonus !== 0) next.amount = effect.amount + bonus;
    if (effect.ignorePhysicalArmor != null) {
      next.ignorePhysicalArmor =
        effect.ignorePhysicalArmor * Math.max(1, Number(level) || 1);
    }
    return next;
  }
  if (mult === 1) return effect;
  const next = {
    ...effect,
    amount: effect.amount != null ? effect.amount * mult : effect.amount,
    damage: effect.damage != null ? effect.damage * mult : effect.damage,
    burn: effect.burn != null ? effect.burn * mult : effect.burn,
    chill: effect.chill != null ? effect.chill * mult : effect.chill,
    shock: effect.shock != null ? effect.shock * mult : effect.shock,
    ignorePhysicalArmor:
      effect.ignorePhysicalArmor != null
        ? effect.ignorePhysicalArmor * mult
        : effect.ignorePhysicalArmor,
  };
  if (effect.resources) {
    const resources = {};
    Object.entries(effect.resources).forEach(([attribute, amount]) => {
      resources[attribute] = (Number(amount) || 0) * mult;
    });
    next.resources = resources;
  }
  if (Array.isArray(effect.damageParts)) {
    next.damageParts = effect.damageParts.map((part) => ({
      ...part,
      amount: part?.amount != null ? part.amount * mult : part.amount,
    }));
  }
  return next;
}

function scaleModifyValuesForLevel(modify, level = 1, { scaleCostAdd = false } = {}) {
  const mult = Math.max(1, Number(level) || 1);
  if (!modify) return {};
  if (mult === 1) return { ...modify };
  const next = { ...modify };
  Object.entries(modify).forEach(([key, value]) => {
    if (
      key === "costMult" ||
      key === "outputMult" ||
      key === "costToHealth" ||
      key === "ignoreArmor" ||
      key === "echo" ||
      key === "match" ||
      key === "outputAdd"
    ) {
      return;
    }
    if (key === "costAdd" && !scaleCostAdd) return;
    if (typeof value === "number") {
      next[key] = value * mult;
      return;
    }
    if (key === "costAdd" && value && typeof value === "object") {
      next[key] = scaleCostByLevel(value, level);
    }
  });
  return next;
}

function resolveAugmentModifyForLevel(augment, level = 1) {
  if (!augment) return {};
  if (Array.isArray(augment.modifyByLevel) && augment.modifyByLevel.length) {
    const lv = Math.max(1, Math.floor(Number(level) || 1));
    const index = Math.min(lv - 1, augment.modifyByLevel.length - 1);
    return { ...(augment.modifyByLevel[index] || {}) };
  }
  return scaleModifyValuesForLevel(augment.modify, level);
}

function formatAbilityLevelTableEffect(card, level = 1) {
  if (!card) return "—";
  if (card.kind === "augment") {
    const lines = formatAugmentEffectLines(
      {
        ...card,
        augment: {
          ...(card.augment || {}),
          modify: resolveAugmentModifyForLevel(card.augment, level),
        },
        effectiveDamageAdd: null,
        effectivePhysicalDamageAdd: null,
        effectiveMagicalDamageAdd: null,
        effectiveFireDamageAdd: null,
        effectiveColdDamageAdd: null,
        effectiveLightningDamageAdd: null,
        effectiveHealAdd: null,
        effectivePhysicalArmorAdd: null,
        effectiveMagicalArmorAdd: null,
        effectiveBleedAdd: null,
        effectivePoisonAdd: null,
        effectiveBurnAdd: null,
        effectiveChillAdd: null,
        effectiveShockAdd: null,
        effectiveLevelAdd: null,
        effectiveMinionHealthAdd: null,
      },
      {
        includeMatchTags: false,
        includeCostIncrease: false,
        includeCostMult: true,
      },
    );
    return lines.length ? lines.join(" · ") : "—";
  }
  if (card.kind === "essence") {
    const essenceModify = resolveEssenceModifyForLevel(
      card.essence || card.augment,
      level,
    );
    const lines = formatEssenceEffectLines({
      ...card,
      effectiveLevel: level,
      essence: {
        modify: essenceModify,
      },
    });
    return lines.length ? lines.join(" · ") : "—";
  }
  const scaled = scaleAbilityEffectForLevel(card.effect, level, {
    additive: abilityUsesAdditiveLevelScale(card),
  });
  const text = formatAbilityShorthandText(
    formatAbilityOutputEffectText(scaled) ||
      formatActivatedEffectText(scaled),
  );
  return text || "—";
}

function getAbilityLevelTableCost(card, level = 1) {
  if (!card) return { entries: [], healthCost: 0 };
  const entries = [];
  let healthCost = 0;
  if (card.kind === "active") {
    costEntries(scaleAbilityCostForLevel(card.cost, level, card)).forEach(
      (entry) => {
        entries.push(entry);
      },
    );
    healthCost = getAbilityHealthCostAmount(card.effect, level);
  } else if (card.kind === "augment") {
    const modify = resolveAugmentModifyForLevel(card.augment, level);
    const costAdd = modify.costAdd;
    if (typeof costAdd === "number" && costAdd > 0) {
      entries.push([card.attribute || "neutral", costAdd]);
    } else if (costAdd && typeof costAdd === "object") {
      costEntries(costAdd).forEach((entry) => {
        entries.push(entry);
      });
    }
  }
  return { entries, healthCost };
}

function resolveChoiceEntryCostForLevel(cost, level = 1, ability = null) {
  const table = getAbilityCostByLevelTable(cost);
  if (!table) return cost;
  const lv = Math.max(1, Math.floor(Number(level) || 1));
  return (
    resolveAbilityCostFromByLevelEntry(
      table[Math.min(lv - 1, table.length - 1)],
      ability,
    ) || cost
  );
}

function getAbilityLevelTableModeRows(card, level) {
  const additive = abilityUsesAdditiveLevelScale(card);
  const baseCost = scaleAbilityCostForLevel(card.cost, level, card);
  const healthCost = getAbilityHealthCostAmount(card.effect, level);
  const modes = card.effect.choose.filter(
    (entry) => entry && typeof entry === "object",
  );
  return modes.map((entry, mode) => {
    const modeHealthCost = getEffectHealthPaymentAmount(entry);
    const scaled = scaleAbilityEffectForLevel(
      stripHealthPaymentEffects(stripAbilityChoiceEntryCost(entry)),
      level,
      { additive },
    );
    const text = formatAbilityShorthandText(
      formatAbilityOutputEffectText(scaled) ||
        formatActivatedEffectText(scaled),
    );
    return {
      level,
      mode,
      modeCount: modes.length,
      effect: text || "—",
      cost: {
        entries: costEntries(
          mergeCostMaps(
            baseCost,
            Array.isArray(entry)
              ? null
              : resolveChoiceEntryCostForLevel(entry.cost, level, card),
          ),
        ),
        healthCost:
          modeHealthCost > 0
            ? modeHealthCost * Math.max(1, level || 1)
            : healthCost,
      },
    };
  });
}

function getAbilityLevelTableRows(card) {
  if (!card || !cardHasType(card, "ability")) return [];
  const rows = [];
  if (
    card.kind === "active" &&
    (abilityChoiceHasCostClient(card) || abilityChoiceHasHealthCostClient(card))
  ) {
    for (let level = 1; level <= MAX_ABILITY_LEVEL; level += 1) {
      rows.push(...getAbilityLevelTableModeRows(card, level));
    }
    return rows;
  }
  for (let level = 1; level <= MAX_ABILITY_LEVEL; level += 1) {
    rows.push({
      level,
      effect: formatAbilityLevelTableEffect(card, level),
      cost: getAbilityLevelTableCost(card, level),
    });
  }
  return rows;
}

function renderAbilityLevelTableCost(cost) {
  const entries = (cost?.entries || []).filter(
    ([, value]) => (Number(value) || 0) > 0,
  );
  const healthCost = Math.max(0, Number(cost?.healthCost) || 0);
  if (!entries.length && healthCost <= 0) {
    return "—";
  }
  return (
    <span className={styles.abilityLevelTableCost}>
      <HealthCostIcons amount={healthCost} />

      <AttributeIconList
        entries={entries}
        className={styles.abilityLevelTableCostIcons}
      />
    </span>
  );
}

function renderAbilityLevelTable(card, { showLabel = true } = {}) {
  const rows = getAbilityLevelTableRows(card);
  if (!rows.length) return null;
  const showCost = rows.some(
    (row) =>
      (row.cost?.entries || []).some(([, value]) => (Number(value) || 0) > 0) ||
      (Number(row.cost?.healthCost) || 0) > 0,
  );
  return (
    <div className={styles.abilityLevelTableWrap}>
      {showLabel ? (
        <div className={styles.abilityLevelTableLabel}>{t("card.levels")}</div>
      ) : null}
      <table className={styles.abilityLevelTable}>
        <thead>
          <tr>
            <th scope="col" className={styles.abilityLevelTableLevelCell}>
              {t("card.level")}
            </th>
            <th scope="col">{t("match.panel.effect")}</th>
            {showCost ? (
              <th scope="col" className={styles.abilityLevelTableCostCell}>
                {t("card.cost")}
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`ability-level-${row.level}-${row.mode ?? 0}`}>
              {row.mode ? null : (
                <td
                  className={styles.abilityLevelTableLevelCell}
                  rowSpan={row.modeCount > 1 ? row.modeCount : undefined}
                >
                  <AbilityLevelIcons
                    level={row.level}
                    className={styles.abilityLevelTableLevelIcons}
                  />
                </td>
              )}
              <td>
                {row.effect === "—" ? (
                  "—"
                ) : (
                  <span className={styles.abilityLevelTableEffect}>
                    {renderTextWithStatusEffectIcons(row.effect)}
                  </span>
                )}
              </td>
              {showCost ? (
                <td className={styles.abilityLevelTableCostCell}>
                  {renderAbilityLevelTableCost(row.cost)}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function previewSelfDamageAmount(effects, seat, level = 1) {
  return listEffectEntries(effects).reduce((total, effect) => {
    if (effect?.id !== "damageSelf") return total;
    let amount = Math.max(0, (effect.amount || 0) * Math.max(1, level || 1));
    if (!effect.ignoreArmor) {
      const damageType = effect.damageType || "physical";
      if (damageType === "physical") {
        amount = Math.max(0, amount - (seat?.physicalArmor || 0));
      } else if (damageType === "magical") {
        amount = Math.max(0, amount - (seat?.magicArmor || 0));
      } else if (damageType === "fire") {
        amount = Math.max(0, amount - (seat?.fireResistance || 0));
      } else if (damageType === "cold") {
        amount = Math.max(0, amount - (seat?.coldResistance || 0));
      } else if (damageType === "lightning") {
        amount = Math.max(0, amount - (seat?.lightningResistance || 0));
      }
    }
    return total + amount;
  }, 0);
}

function actionWouldCauseLoss(seat, effects, level = 1) {
  if (!seat) return false;
  const damage = previewSelfDamageAmount(effects, seat, level);
  return damage > 0 && seat.hp - damage <= 0;
}

function formatAbilityShorthandText(text) {
  if (!text) return text;
  return String(text)
    .replace(/\bDeal /g, "")
    .replace(/\bPhysical Armor\b/gi, "PHYS. ARM.")
    .replace(/\bMagical Armor\b/gi, "MAG. ARM.")
    .replace(/\bMagic Armor\b/gi, "MAG. ARM.")
    .replace(/\bPhysical Damage\b/gi, "PHYS. DAM.")
    .replace(/\bMagical Damage\b/gi, "MAG. DAM.")
    .replace(/\bStrength\b/gi, "STR")
    .replace(/\bDexterity\b/gi, "DEX")
    .replace(/\bWisdom\b/gi, "WIS")
    .replace(/\bNeutral\b/gi, "NEU")
    .replace(/→/g, "»")
    .replace(/★/g, "*");
}

function renderActionOutputText(text, outputDelta = 0, { shorthand = true } = {}) {
  const display = shorthand ? formatAbilityShorthandText(text) : text;
  if (!display) return display;
  const delta = Math.floor(Number(outputDelta) || 0);
  if (!delta) return display;
  const match = String(display).match(/\d+/);
  if (!match) return display;
  const toneClass =
    delta > 0 ? styles.actionOutputUp : styles.actionOutputDown;
  const start = match.index;
  const end = start + match[0].length;
  return (
    <>
      {display.slice(0, start)}
      <span className={toneClass}>{match[0]}</span>
      {display.slice(end)}
    </>
  );
}

function resolveStatusEffectIdFromLabel(label) {
  const normalized = String(label || "")
    .toLowerCase()
    .replace(/\./g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (normalized === "burn") return "burn";
  if (normalized === "bleed") return "bleed";
  if (normalized === "chill") return "chill";
  if (normalized === "poison") return "poison";
  if (normalized === "shock") return "shock";
  if (normalized === "restock") return "restock";
  if (normalized === "regeneration" || normalized === "regen") {
    return "regeneration";
  }
  if (normalized === "dodge") return "dodge";
  if (normalized === "thorns" || normalized === "thorn") return "thorns";
  if (normalized === "sharpen") return "sharpen";
  if (normalized === "hone") return "hone";
  if (normalized === "fletch") return "fletch";
  if (normalized === "physical armor" || normalized === "phys arm") {
    return "physicalArmor";
  }
  if (
    normalized === "magic armor" ||
    normalized === "magical armor" ||
    normalized === "mag arm"
  ) {
    return "magicArmor";
  }
  return null;
}

const STATUS_EFFECT_INLINE_PATTERN =
  /\b((?:Burn|Bleed|Chill|Poison|Shock|Restock|Regeneration|Regen|Dodge|Thorns?|Sharpen|Hone|Fletch|Physical\s+Armor|Magical\s+Armor|Magic\s+Armor|PHYS\.?\s*ARM\.?|MAG\.?\s*ARM\.?))(?:\s+(\d+)\s*%|\s+(\+)?(\d+)\b)/gi;

const ARMOR_INLINE_LABEL_SOURCE =
  "(Physical\\s+Armor|Magical\\s+Armor|Magic\\s+Armor|PHYS\\.?\\s*ARM\\.?|MAG\\.?\\s*ARM\\.?)(?![A-Za-z])";

const ARMOR_LEADING_AMOUNT_PATTERN = new RegExp(
  `\\b(\\d+)\\s+${ARMOR_INLINE_LABEL_SOURCE}`,
  "gi",
);

const ARMOR_BARE_PATTERN = new RegExp(`\\b${ARMOR_INLINE_LABEL_SOURCE}`, "gi");

const STATUS_EFFECT_BARE_DODGE_PATTERN = /\bDodge\b(?!\s*\d)/gi;

const STATUS_EFFECT_BARE_NAME_PATTERN =
  /\b(?:(\d+)\s+)?(Burn|Bleed|Chill|Poison|Shock|Restock|Regeneration|Sharpen|Hone|Fletch)\b(?!\s*\+?\d)/gi;

const STATUS_EFFECT_LEADING_THORNS_PATTERN =
  /\b(\d+)\s+Thorns?\b(?!\s+damage\b)/gi;

const STATUS_EFFECT_BARE_THORNS_PATTERN = /\bThorns?\b/gi;

const STATUS_EFFECT_BARE_RESEARCH_PATTERN = /\bResearch\b/gi;

const ELEMENTAL_DAMAGE_INLINE_PATTERN =
  /\b(?:(\d+)\s+)?(?:(fire|cold|lightning|physical|magical|magic)(?:\s+(?:melee|ranged))?\s+damage\b|(PHYS)\.\s*DAM\b\.?|(MAG)\.\s*DAM\b\.?)/gi;

const ELEMENTAL_DAMAGE_PART_INLINE_PATTERN =
  /\b(\d+)\s+(fire|cold|lightning|physical|magical)\b(?=\s*\+)/gi;

const HEAL_INLINE_PATTERN = /\bHeal\s+(\d+)\b(?!\s*%)/gi;

const CHARGE_INLINE_PATTERN = /\bCharges?\b/gi;
const DURABILITY_COST_INLINE_PATTERN = /[−-](\d+)\s+Durability\b/g;
const DURABILITY_RESTORE_INLINE_PATTERN = /\b(Restore\s+)(\d+)\s+Durability\b/gi;
const ATTRIBUTE_INLINE_PATTERN =
  /\b(STR|DEX|WIS|NEU|Strength|Dexterity|Wisdom|Neutral)\b/g;
const ATTRIBUTE_INLINE_IDS = {
  STR: "strength",
  DEX: "dexterity",
  WIS: "wisdom",
  NEU: "neutral",
  Strength: "strength",
  Dexterity: "dexterity",
  Wisdom: "wisdom",
  Neutral: "neutral",
};
const RESOURCE_GRANT_ATTRIBUTE_SOURCE =
  "(?:strength|dexterity|wisdom|neutral|STR|DEX|WIS|NEU)";
const RESOURCE_GRANT_INLINE_PATTERN = new RegExp(
  `\\bGain\\s+(\\d+\\s+${RESOURCE_GRANT_ATTRIBUTE_SOURCE}(?:\\s*,\\s*\\d+\\s+${RESOURCE_GRANT_ATTRIBUTE_SOURCE})*)\\b`,
  "gi",
);
const RESOURCE_GRANT_ENTRY_PATTERN = new RegExp(
  `(\\d+)\\s+(${RESOURCE_GRANT_ATTRIBUTE_SOURCE})`,
  "gi",
);

function parseResourceGrantInlineEntries(text) {
  const entries = [];
  RESOURCE_GRANT_ENTRY_PATTERN.lastIndex = 0;
  let match = RESOURCE_GRANT_ENTRY_PATTERN.exec(text);
  while (match) {
    const amount = Math.max(0, Math.floor(Number(match[1]) || 0));
    const key = String(match[2]);
    const attribute =
      ATTRIBUTE_INLINE_IDS[key.toUpperCase()] ||
      ATTRIBUTE_INLINE_IDS[key.charAt(0).toUpperCase() + key.slice(1).toLowerCase()];
    if (attribute && amount > 0 && ATTR_ICON_URL[attribute]) {
      entries.push([attribute, amount]);
    }
    match = RESOURCE_GRANT_ENTRY_PATTERN.exec(text);
  }
  return entries;
}

function resolveInlineDamageType(match) {
  if (match[2]) {
    const type = String(match[2]).toLowerCase();
    return type === "magic" ? "magical" : type;
  }
  if (match[3]) return "physical";
  if (match[4]) return "magical";
  return "";
}

function collectStatusEffectInlineMatches(display) {
  const matches = [];
  RESOURCE_GRANT_INLINE_PATTERN.lastIndex = 0;
  let grantMatch = RESOURCE_GRANT_INLINE_PATTERN.exec(display);
  while (grantMatch) {
    const entries = parseResourceGrantInlineEntries(grantMatch[1]);
    if (entries.length) {
      matches.push({
        type: "resourceGrant",
        index: grantMatch.index,
        length: grantMatch[0].length,
        entries,
      });
    }
    grantMatch = RESOURCE_GRANT_INLINE_PATTERN.exec(display);
  }
  STATUS_EFFECT_INLINE_PATTERN.lastIndex = 0;
  let match = STATUS_EFFECT_INLINE_PATTERN.exec(display);
  while (match) {
    const effectId = resolveStatusEffectIdFromLabel(match[1]);
    const percentAmount = match[2];
    const flatAmount = match[4];
    const rawAmount = percentAmount != null ? percentAmount : flatAmount;
    let amount = Math.max(0, Math.floor(Number(rawAmount) || 0));
    let matchIndex = match.index;
    let matchLength = match[0].length;
    if (
      (effectId === "dodge" || effectId === "thorns") &&
      percentAmount != null
    ) {
      amount = 1;
      if (effectId === "dodge") {
        const leadingCount = /\b(\d+)\s+$/.exec(display.slice(0, match.index));
        if (leadingCount) {
          amount = Math.max(1, Math.floor(Number(leadingCount[1]) || 1));
          matchIndex = leadingCount.index;
          matchLength += match.index - leadingCount.index;
        }
      }
    }
    if (effectId && amount > 0 && STATUS_EFFECT_ICON_URL[effectId]) {
      matches.push({
        type: "status",
        index: matchIndex,
        length: matchLength,
        effectId,
        amount,
        prefix: match[3] || "",
      });
    }
    match = STATUS_EFFECT_INLINE_PATTERN.exec(display);
  }
  [ARMOR_LEADING_AMOUNT_PATTERN, ARMOR_BARE_PATTERN].forEach((pattern) => {
    const leading = pattern === ARMOR_LEADING_AMOUNT_PATTERN;
    pattern.lastIndex = 0;
    let armorMatch = pattern.exec(display);
    while (armorMatch) {
      const current = armorMatch;
      const effectId = resolveStatusEffectIdFromLabel(
        leading ? current[2] : current[1],
      );
      const amount = leading
        ? Math.max(0, Math.floor(Number(current[1]) || 0))
        : 1;
      const overlaps = matches.some(
        (entry) =>
          current.index < entry.index + entry.length &&
          current.index + current[0].length > entry.index,
      );
      if (!overlaps && effectId && amount > 0 && STATUS_EFFECT_ICON_URL[effectId]) {
        matches.push({
          type: "status",
          index: current.index,
          length: current[0].length,
          effectId,
          amount,
        });
      }
      armorMatch = pattern.exec(display);
    }
  });
  STATUS_EFFECT_BARE_DODGE_PATTERN.lastIndex = 0;
  match = STATUS_EFFECT_BARE_DODGE_PATTERN.exec(display);
  while (match) {
    const overlaps = matches.some(
      (entry) =>
        match.index < entry.index + entry.length &&
        match.index + match[0].length > entry.index,
    );
    if (!overlaps && STATUS_EFFECT_ICON_URL.dodge) {
      matches.push({
        type: "status",
        index: match.index,
        length: match[0].length,
        effectId: "dodge",
        amount: 1,
      });
    }
    match = STATUS_EFFECT_BARE_DODGE_PATTERN.exec(display);
  }
  STATUS_EFFECT_BARE_NAME_PATTERN.lastIndex = 0;
  match = STATUS_EFFECT_BARE_NAME_PATTERN.exec(display);
  while (match) {
    const current = match;
    const effectId = resolveStatusEffectIdFromLabel(current[2]);
    const amount =
      current[1] != null ? Math.max(0, Math.floor(Number(current[1]) || 0)) : 1;
    const overlaps = matches.some(
      (entry) =>
        current.index < entry.index + entry.length &&
        current.index + current[0].length > entry.index,
    );
    if (!overlaps && effectId && amount > 0 && STATUS_EFFECT_ICON_URL[effectId]) {
      matches.push({
        type: "status",
        index: current.index,
        length: current[0].length,
        effectId,
        amount,
      });
    }
    match = STATUS_EFFECT_BARE_NAME_PATTERN.exec(display);
  }
  STATUS_EFFECT_LEADING_THORNS_PATTERN.lastIndex = 0;
  match = STATUS_EFFECT_LEADING_THORNS_PATTERN.exec(display);
  while (match) {
    const amount = Math.max(0, Math.floor(Number(match[1]) || 0));
    const overlaps = matches.some(
      (entry) =>
        match.index < entry.index + entry.length &&
        match.index + match[0].length > entry.index,
    );
    if (!overlaps && amount > 0) {
      matches.push({
        type: "status",
        index: match.index,
        length: match[0].length,
        effectId: "thorns",
        amount,
      });
    }
    match = STATUS_EFFECT_LEADING_THORNS_PATTERN.exec(display);
  }
  STATUS_EFFECT_BARE_THORNS_PATTERN.lastIndex = 0;
  match = STATUS_EFFECT_BARE_THORNS_PATTERN.exec(display);
  while (match) {
    const overlaps = matches.some(
      (entry) =>
        match.index < entry.index + entry.length &&
        match.index + match[0].length > entry.index,
    );
    if (!overlaps) {
      matches.push({
        type: "status",
        index: match.index,
        length: match[0].length,
        effectId: "thorns",
        amount: 1,
      });
    }
    match = STATUS_EFFECT_BARE_THORNS_PATTERN.exec(display);
  }
  STATUS_EFFECT_BARE_RESEARCH_PATTERN.lastIndex = 0;
  match = STATUS_EFFECT_BARE_RESEARCH_PATTERN.exec(display);
  while (match) {
    const overlaps = matches.some(
      (entry) =>
        match.index < entry.index + entry.length &&
        match.index + match[0].length > entry.index,
    );
    if (!overlaps) {
      matches.push({
        type: "status",
        index: match.index,
        length: match[0].length,
        effectId: "research",
        amount: 1,
      });
    }
    match = STATUS_EFFECT_BARE_RESEARCH_PATTERN.exec(display);
  }
  ELEMENTAL_DAMAGE_INLINE_PATTERN.lastIndex = 0;
  match = ELEMENTAL_DAMAGE_INLINE_PATTERN.exec(display);
  while (match) {
    const damageType = resolveInlineDamageType(match);
    const amount =
      match[1] != null ? Math.max(0, Math.floor(Number(match[1]) || 0)) : null;
    const overlaps = matches.some(
      (entry) =>
        match.index < entry.index + entry.length &&
        match.index + match[0].length > entry.index,
    );
    if (!overlaps && ELEMENTAL_DAMAGE_ICON_URL[damageType]) {
      if (amount === 0) {
        match = ELEMENTAL_DAMAGE_INLINE_PATTERN.exec(display);
        continue;
      }
      matches.push({
        type: "elementalDamage",
        index: match.index,
        length: match[0].length,
        damageType,
        amount: amount != null && amount > 0 ? amount : null,
      });
    }
    match = ELEMENTAL_DAMAGE_INLINE_PATTERN.exec(display);
  }
  ELEMENTAL_DAMAGE_PART_INLINE_PATTERN.lastIndex = 0;
  match = ELEMENTAL_DAMAGE_PART_INLINE_PATTERN.exec(display);
  while (match) {
    const damageType = String(match[2] || "").toLowerCase();
    const amount = Math.max(0, Math.floor(Number(match[1]) || 0));
    const overlaps = matches.some(
      (entry) =>
        match.index < entry.index + entry.length &&
        match.index + match[0].length > entry.index,
    );
    if (!overlaps && amount > 0 && ELEMENTAL_DAMAGE_ICON_URL[damageType]) {
      matches.push({
        type: "elementalDamage",
        index: match.index,
        length: match[0].length,
        damageType,
        amount,
      });
    }
    match = ELEMENTAL_DAMAGE_PART_INLINE_PATTERN.exec(display);
  }
  HEAL_INLINE_PATTERN.lastIndex = 0;
  match = HEAL_INLINE_PATTERN.exec(display);
  while (match) {
    const amount = Math.max(0, Math.floor(Number(match[1]) || 0));
    const overlaps = matches.some(
      (entry) =>
        match.index < entry.index + entry.length &&
        match.index + match[0].length > entry.index,
    );
    if (!overlaps && amount > 0) {
      matches.push({
        type: "heal",
        index: match.index,
        length: match[0].length,
        amount,
      });
    }
    match = HEAL_INLINE_PATTERN.exec(display);
  }
  CHARGE_INLINE_PATTERN.lastIndex = 0;
  match = CHARGE_INLINE_PATTERN.exec(display);
  while (match) {
    const overlaps = matches.some(
      (entry) =>
        match.index < entry.index + entry.length &&
        match.index + match[0].length > entry.index,
    );
    if (!overlaps) {
      matches.push({
        type: "charge",
        index: match.index,
        length: match[0].length,
      });
    }
    match = CHARGE_INLINE_PATTERN.exec(display);
  }
  DURABILITY_COST_INLINE_PATTERN.lastIndex = 0;
  match = DURABILITY_COST_INLINE_PATTERN.exec(display);
  while (match) {
    const amount = Math.max(0, Math.floor(Number(match[1]) || 0));
    const overlaps = matches.some(
      (entry) =>
        match.index < entry.index + entry.length &&
        match.index + match[0].length > entry.index,
    );
    if (!overlaps && amount > 0) {
      matches.push({
        type: "durability",
        index: match.index,
        length: match[0].length,
        amount,
      });
    }
    match = DURABILITY_COST_INLINE_PATTERN.exec(display);
  }
  DURABILITY_RESTORE_INLINE_PATTERN.lastIndex = 0;
  match = DURABILITY_RESTORE_INLINE_PATTERN.exec(display);
  while (match) {
    const amount = Math.max(0, Math.floor(Number(match[2]) || 0));
    const index = match.index + match[1].length;
    const length = match[0].length - match[1].length;
    const overlaps = matches.some(
      (entry) =>
        index < entry.index + entry.length &&
        index + length > entry.index,
    );
    if (!overlaps && amount > 0) {
      matches.push({
        type: "durability",
        index,
        length,
        amount,
        restore: true,
      });
    }
    match = DURABILITY_RESTORE_INLINE_PATTERN.exec(display);
  }
  ATTRIBUTE_INLINE_PATTERN.lastIndex = 0;
  match = ATTRIBUTE_INLINE_PATTERN.exec(display);
  while (match) {
    const attribute = ATTRIBUTE_INLINE_IDS[match[1]];
    const overlaps = matches.some(
      (entry) =>
        match.index < entry.index + entry.length &&
        match.index + match[0].length > entry.index,
    );
    if (!overlaps && attribute && ATTR_ICON_URL[attribute]) {
      matches.push({
        type: "attribute",
        index: match.index,
        length: match[0].length,
        attribute,
      });
    }
    match = ATTRIBUTE_INLINE_PATTERN.exec(display);
  }
  matches.sort((a, b) => a.index - b.index);
  return matches;
}

function buildStatusEffectTextParts(display, matches) {
  const parts = [];
  let lastIndex = 0;
  if (
    matches.length === 1 &&
    /^\s*gain\s*$/i.test(display.slice(0, matches[0].index)) &&
    !display.slice(matches[0].index + matches[0].length).trim()
  ) {
    lastIndex = matches[0].index;
  }
  matches.forEach((entry) => {
    if (entry.index > lastIndex) {
      parts.push({
        type: "text",
        value: display
          .slice(lastIndex, entry.index)
          .replace(/\s*[·•]\s*$/, " ")
          .replace(/(^\s*|[·•:→»]\s*)Deal\s*$/i, "$1")
          .replace(/\s+$/g, " "),
      });
    }
    if (entry.type === "elementalDamage") {
      parts.push({
        type: "elementalDamage",
        damageType: entry.damageType,
        amount: entry.amount,
      });
    } else if (entry.type === "heal") {
      parts.push({
        type: "heal",
        amount: entry.amount,
      });
    } else if (entry.type === "charge") {
      parts.push({
        type: "charge",
      });
    } else if (entry.type === "durability") {
      parts.push({
        type: "durability",
        amount: entry.amount,
        restore: Boolean(entry.restore),
      });
    } else if (entry.type === "attribute") {
      parts.push({
        type: "attribute",
        attribute: entry.attribute,
      });
    } else if (entry.type === "resourceGrant") {
      parts.push({
        type: "resourceGrant",
        entries: entry.entries,
      });
    } else {
      parts.push({
        type: "status",
        effectId: entry.effectId,
        amount: entry.amount,
        prefix: entry.prefix || "",
      });
    }
    lastIndex = entry.index + entry.length;
  });
  if (lastIndex < display.length) {
    parts.push({
      type: "text",
      value: display
        .slice(lastIndex)
        .replace(/^\s*[·•]\s*/, " · ")
        .replace(/^(\s*(?:·\s*)?)Deal\b\s*/i, "$1"),
    });
  }
  return parts;
}

function statusEffectTextPartKey(part) {
  if (!part || part.amount == null) return null;
  if (part.type === "status") return `status:${part.effectId}`;
  if (part.type === "elementalDamage") return `damage:${part.damageType}`;
  if (part.type === "heal") return "heal";
  return null;
}

function listTextPartNumbers(parts) {
  return parts
    .filter((part) => part.type === "text")
    .flatMap((part) =>
      (String(part.value || "").match(/\d+/g) || []).map(Number),
    );
}

function buildLevelUpCompareState(compareDisplay) {
  const compareParts = buildStatusEffectTextParts(
    compareDisplay,
    collectStatusEffectInlineMatches(compareDisplay),
  );
  const amountsByKey = {};
  compareParts.forEach((part) => {
    const key = statusEffectTextPartKey(part);
    if (!key) return;
    (amountsByKey[key] = amountsByKey[key] || []).push(
      Math.max(0, Math.floor(Number(part.amount) || 0)),
    );
  });
  return {
    amountsByKey,
    numbers: listTextPartNumbers(compareParts),
    ordinalsByKey: {},
    numberIndex: 0,
  };
}

function resolveLevelUpPartDelta(part, compareState) {
  if (!compareState) return 0;
  const key = statusEffectTextPartKey(part);
  if (!key) return 0;
  const ordinal = compareState.ordinalsByKey[key] || 0;
  compareState.ordinalsByKey[key] = ordinal + 1;
  const previous = compareState.amountsByKey[key]?.[ordinal] ?? 0;
  return Math.max(0, Math.floor(Number(part.amount) || 0) - previous);
}

function renderLevelUpNumberText(value, compareState, keyPrefix = "lvl") {
  const source = String(value || "");
  const nodes = [];
  let lastIndex = 0;
  const pattern = /\d+/g;
  let match = pattern.exec(source);
  while (match) {
    const current = Number(match[0]);
    const previous = compareState.numbers[compareState.numberIndex];
    compareState.numberIndex += 1;
    if (previous == null || current > previous) {
      if (match.index > lastIndex) {
        nodes.push(source.slice(lastIndex, match.index));
      }
      nodes.push(
        <span
          key={`${keyPrefix}-${match.index}`}
          className={styles.levelUpOutputText}
        >
          {match[0]}
        </span>,
      );
      lastIndex = match.index + match[0].length;
    }
    match = pattern.exec(source);
  }
  if (!nodes.length) return source;
  if (lastIndex < source.length) nodes.push(source.slice(lastIndex));
  return <>{nodes}</>;
}

function renderTextWithStatusEffectIcons(
  text,
  {
    outputDelta = 0,
    shorthand = true,
    keywordProps = null,
    cardMatcher = null,
    onCardClick = null,
    compareText = null,
  } = {},
) {
  const normalizeDisplay = (value) =>
    shorthand
      ? formatAbilityShorthandText(value)
      : String(value || "")
          .replace(/→/g, "»")
          .replace(/★/g, "*");
  const display = normalizeDisplay(text);
  if (!display) return display;
  const compareDisplay =
    compareText != null ? normalizeDisplay(compareText) || "" : null;
  const compareState =
    compareDisplay != null && compareDisplay !== display
      ? buildLevelUpCompareState(compareDisplay)
      : null;
  const matches = collectStatusEffectInlineMatches(display);
  if (!matches.length) {
    if (cardMatcher && typeof onCardClick === "function") {
      return (
        <ExamRichText
          text={display}
          matcher={cardMatcher}
          onCardClick={onCardClick}
          activeKeywordId={keywordProps?.activeKeywordId}
          onKeywordClick={keywordProps?.onKeywordClick}
        />
      );
    }
    if (keywordProps) {
      return (
        <KeywordRichText
          text={display}
          activeKeywordId={keywordProps.activeKeywordId}
          onKeywordClick={keywordProps.onKeywordClick}
        />
      );
    }
    if (compareState) {
      compareState.numbers = (compareDisplay.match(/\d+/g) || []).map(Number);
      return renderLevelUpNumberText(display, compareState);
    }
    return renderActionOutputText(display, outputDelta, { shorthand: false });
  }
  const parts = buildStatusEffectTextParts(display, matches);
  const renderTextPart = (value, index, applyOutputDelta) => {
    if (!value || !String(value).trim()) return null;
    if (compareState && !cardMatcher && !keywordProps) {
      return (
        <React.Fragment key={`text-${index}`}>
          {renderLevelUpNumberText(value, compareState, `lvl-${index}`)}
        </React.Fragment>
      );
    }
    if (cardMatcher && typeof onCardClick === "function") {
      return (
        <ExamRichText
          key={`text-${index}`}
          text={value}
          matcher={cardMatcher}
          onCardClick={onCardClick}
          activeKeywordId={keywordProps?.activeKeywordId}
          onKeywordClick={keywordProps?.onKeywordClick}
        />
      );
    }
    if (keywordProps) {
      return (
        <KeywordRichText
          key={`text-${index}`}
          text={value}
          activeKeywordId={keywordProps.activeKeywordId}
          onKeywordClick={keywordProps.onKeywordClick}
        />
      );
    }
    if (applyOutputDelta) {
      return (
        <React.Fragment key={`text-${index}`}>
          {renderActionOutputText(value, outputDelta, { shorthand: false })}
        </React.Fragment>
      );
    }
    return <React.Fragment key={`text-${index}`}>{value}</React.Fragment>;
  };
  let appliedOutputDelta = false;
  const wrapLevelUp = (levelUpDelta, key, node) =>
    levelUpDelta > 0 ? (
      <span key={key} className={styles.levelUpOutput}>
        {node}
      </span>
    ) : (
      <React.Fragment key={key}>{node}</React.Fragment>
    );
  return (
    <span className={styles.effectTextWithStatusIcons}>
      {parts.map((part, index) => {
        if (part.type === "status") {
          const applyDelta = !appliedOutputDelta && part.amount != null;
          if (applyDelta) appliedOutputDelta = true;
          const levelUpDelta = resolveLevelUpPartDelta(part, compareState);
          return (
            <React.Fragment key={`status-${part.effectId}-${index}`}>
              {part.prefix}
              {wrapLevelUp(
                levelUpDelta,
                "stack",
                <StatusEffectIconStack
                  effectId={part.effectId}
                  amount={part.amount}
                  outputDelta={
                    levelUpDelta > 0 ? levelUpDelta : applyDelta ? outputDelta : 0
                  }
                  keywordProps={keywordProps}
                />,
              )}
            </React.Fragment>
          );
        }
        if (part.type === "elementalDamage") {
          const applyDelta = !appliedOutputDelta && part.amount != null;
          if (applyDelta) appliedOutputDelta = true;
          const levelUpDelta = resolveLevelUpPartDelta(part, compareState);
          return wrapLevelUp(
            levelUpDelta,
            `damage-${part.damageType}-${index}`,
            <ElementalDamageInline
              damageType={part.damageType}
              amount={part.amount}
              outputDelta={
                levelUpDelta > 0 ? levelUpDelta : applyDelta ? outputDelta : 0
              }
              keywordProps={keywordProps}
            />,
          );
        }
        if (part.type === "heal") {
          const applyDelta = !appliedOutputDelta && part.amount != null;
          if (applyDelta) appliedOutputDelta = true;
          const levelUpDelta = resolveLevelUpPartDelta(part, compareState);
          return wrapLevelUp(
            levelUpDelta,
            `heal-${index}`,
            <HealInline
              amount={part.amount}
              outputDelta={
                levelUpDelta > 0 ? levelUpDelta : applyDelta ? outputDelta : 0
              }
              keywordProps={keywordProps}
            />,
          );
        }
        if (part.type === "charge") {
          const chargeKeyword = getKeywordById("charges");
          const tipProps = keywordProps || null;
          const canOpenKeyword =
            Boolean(chargeKeyword) &&
            typeof tipProps?.onKeywordClick === "function";
          const active =
            canOpenKeyword && tipProps.activeKeywordId === chargeKeyword.id;
          if (canOpenKeyword) {
            return (
              <button
                key={`charge-${index}`}
                type="button"
                className={`${styles.chargeIconInlineButton}${
                  active ? ` ${styles.keywordIconButtonActive}` : ""
                }`}
                aria-label={chargeKeyword.label || "Charges"}
                onClick={(event) => {
                  event.stopPropagation();
                  tipProps.onKeywordClick(chargeKeyword);
                }}
              >
                <ChargeIcon className={styles.chargeIconInline} />
              </button>
            );
          }
          return (
            <ChargeIcon
              key={`charge-${index}`}
              className={styles.chargeIconInline}
            />
          );
        }
        if (part.type === "durability") {
          return (
            <DurabilityCostIcons
              key={`durability-${index}`}
              amount={part.amount}
              className={styles.durabilityCostIconsInline}
              keywordProps={keywordProps}
              restore={part.restore}
            />
          );
        }
        if (part.type === "attribute") {
          return (
            <AttributeIcons
              key={`attribute-${part.attribute}-${index}`}
              attribute={part.attribute}
              count={1}
              keywordProps={keywordProps}
              className={styles.attrIconInline}
            />
          );
        }
        if (part.type === "resourceGrant") {
          return part.entries.map(([attribute, count]) => (
            <AttributeIcons
              key={`grant-${attribute}-${index}`}
              attribute={attribute}
              count={count}
              keywordProps={keywordProps}
              className={styles.attrIconInline}
            />
          ));
        }
        const applyOutputDelta = !appliedOutputDelta;
        if (applyOutputDelta) appliedOutputDelta = true;
        return renderTextPart(part.value, index, applyOutputDelta);
      })}
    </span>
  );
}

function formatAgnosticAspectExamText() {
  return [
    "Strength, Dexterity, Wisdom, and hybrid sources deal no damage.",
    "Tiers from exclusive-Neutral equipment and Neutral active abilities (max 6):",
    "1: Gain 2 XP (once)",
    "2: Gain 1 Restock (once)",
    "3: at turn start, gain 1 Physical Armor",
    "4: at turn start, gain 1 Magic Armor",
    "5: +5 max health while at this tier",
    "6: at turn start, gain 1 Neutral",
  ].join("\n");
}

const ASPECT_TRAIT_PRIMARY_ATTRIBUTES = ["strength", "dexterity", "wisdom"];

const SPECIAL_ASPECT_TRAIT_ATTR = {
  as_fighter: "strength",
  as_scout: "dexterity",
  as_mage: "wisdom",
};

const ASPECT_TRAIT_ATTR_LABEL = {
  strength: "Strength",
  dexterity: "Dexterity",
  wisdom: "Wisdom",
  neutral: "Neutral",
};

function aspectTraitAttributeLabel(attribute) {
  return ASPECT_TRAIT_ATTR_LABEL[attribute] || String(attribute || "");
}

function getAspectTraitAttribute(aspectId, aspectAttributes = []) {
  const special = SPECIAL_ASPECT_TRAIT_ATTR[aspectId];
  if (special) return special;
  const list = Array.isArray(aspectAttributes) ? aspectAttributes : [];
  const primary = list.find((entry) =>
    ASPECT_TRAIT_PRIMARY_ATTRIBUTES.includes(entry),
  );
  if (primary) return primary;
  if (list.includes("neutral")) return "neutral";
  return list[0] || "neutral";
}

function makeAspectTraitExamOption({
  id,
  label,
  detail,
  shortLabel,
  captionParts,
  branch,
  mark,
}) {
  return {
    id,
    label,
    detail,
    shortLabel: shortLabel || label,
    captionParts: Array.isArray(captionParts) ? captionParts : null,
    ...(branch ? { branch: String(branch) } : {}),
    ...(mark ? { mark: String(mark) } : {}),
  };
}

function generalAspectTraitExamOptions(level, attribute) {
  return primaryAspectTraitExamOptions(level, attribute || "neutral");
}

function level5AspectTraitExamOptions(attribute) {
  return primaryAspectTraitExamOptions(5, attribute || "neutral");
}

const ASPECT_TRAIT_ATTR_ABBR = {
  strength: "STR",
  dexterity: "DEX",
  wisdom: "WIS",
  neutral: "NEU",
};

function aspectTraitDefenseStatus(attribute) {
  if (attribute === "dexterity") return "dodge";
  if (attribute === "wisdom") return "magicArmor";
  return "physicalArmor";
}

function traitAttrGrantCaptionPart(attribute, amount = 1, suffix = "/turn") {
  return { type: "attrGrant", attribute, amount, suffix };
}

function traitStatusCaptionPart(status, amount) {
  return { type: "status", status, amount };
}

function traitHealCaptionPart(amount) {
  return { type: "heal", amount };
}

function traitTextCaptionPart(text) {
  return { type: "text", text };
}

function traitSepCaptionPart() {
  return { type: "sep" };
}

function primaryAspectTraitExamOptions(level, attribute) {
  const attr = attribute;
  const name = aspectTraitAttributeLabel(attr);
  const abbr = ASPECT_TRAIT_ATTR_ABBR[attr] || name;
  const defenseStatus = aspectTraitDefenseStatus(attr);
  const defenseLabel =
    attr === "strength" || attr === "neutral"
      ? "Physical Armor"
      : attr === "dexterity"
        ? "Dodge"
        : "Magic Armor";
  if (level === 2) {
    return [
      makeAspectTraitExamOption({
        id: `start_grant_defense_${attr}`,
        label: `${name} at Start & +1 ${defenseLabel}`,
        shortLabel: `+1 ${abbr}/turn · +1 ${defenseLabel}`,
        captionParts: [
          traitAttrGrantCaptionPart(attr),
          traitSepCaptionPart(),
          traitStatusCaptionPart(defenseStatus, 1),
        ],
        detail: `Gain one ${name} resource at the start of every turn. Immediately gain 1 ${defenseLabel}.`,
      }),
      makeAspectTraitExamOption({
        id: `start_grant_health_${attr}`,
        label: `${name} at Start & +5 Health`,
        shortLabel: `+1 ${abbr}/turn · +5 HP`,
        captionParts: [
          traitAttrGrantCaptionPart(attr),
          traitSepCaptionPart(),
          traitHealCaptionPart(5),
        ],
        detail: `Gain one ${name} resource at the start of every turn. Immediately gain +5 current and max health.`,
      }),
    ];
  }
  if (level === 3) {
    return [
      makeAspectTraitExamOption({
        id: `ability_level_defense_${attr}`,
        label: `Level Up Ability & +1 ${defenseLabel}`,
        shortLabel: `Ability +1 · +1 ${defenseLabel}`,
        captionParts: [
          traitTextCaptionPart("Ability +1"),
          traitSepCaptionPart(),
          traitStatusCaptionPart(defenseStatus, 1),
        ],
        detail: `Choose a slotted ${name} Ability (active, augment, or essence) on your board to permanently Level Up, or Skip for +3 XP. If you have no matching slotted Abilities, gain +3 XP. Immediately gain 1 ${defenseLabel}.`,
      }),
      makeAspectTraitExamOption({
        id: `ability_level_health_${attr}`,
        label: "Level Up Ability & +5 Health",
        shortLabel: "Ability +1 · +5 HP",
        captionParts: [
          traitTextCaptionPart("Ability +1"),
          traitSepCaptionPart(),
          traitHealCaptionPart(5),
        ],
        detail: `Choose a slotted ${name} Ability (active, augment, or essence) on your board to permanently Level Up, or Skip for +3 XP. If you have no matching slotted Abilities, gain +3 XP. Immediately gain +5 current and max health.`,
      }),
    ];
  }
  if (level === 4) {
    return [
      makeAspectTraitExamOption({
        id: `start_grant_persistent_${attr}`,
        label: `${name} at Start & +3 Persistent ${name}`,
        shortLabel: `+1 ${abbr}/turn · +3 Persist`,
        captionParts: [
          traitAttrGrantCaptionPart(attr),
          traitSepCaptionPart(),
          {
            type: "attrGrant",
            attribute: attr,
            amount: 3,
            suffix: "",
            persistent: true,
          },
        ],
        detail: `Gain one ${name} resource at the start of every turn. Immediately gain 3 Persistent ${name} resources.`,
      }),
      makeAspectTraitExamOption({
        id: `start_grant_cost_reduce_${attr}`,
        label: `${name} at Start & Next ${name} Cost -4`,
        shortLabel: `+1 ${abbr}/turn · Cost -4`,
        captionParts: [
          traitAttrGrantCaptionPart(attr),
          traitSepCaptionPart(),
          {
            type: "attrGrant",
            attribute: attr,
            amount: null,
            suffix: "",
            costReduce: 4,
          },
        ],
        detail: `Gain one ${name} resource at the start of every turn. One time, the next ${name}-based equipment effect or active ability costs 4 less ${name} (minimum of 1 ${name}).`,
      }),
    ];
  }
  if (level === 5) {
    return [
      makeAspectTraitExamOption({
        id: `next_equipment_output_${attr}`,
        label: `Next ${name} Equipment Output +2`,
        shortLabel: `Equip Out +2`,
        captionParts: [
          {
            type: "attrGrant",
            attribute: attr,
            amount: 2,
            suffix: "",
            prefix: "Equip",
          },
        ],
        detail: `Once per turn, the next non-resource effect from a ${name} equipment card has +2 output (damage, healing, charges, etc.).`,
      }),
      makeAspectTraitExamOption({
        id: `next_output_${attr}`,
        label: `Next ${name} Ability Output +2`,
        shortLabel: `Ability Out +2`,
        captionParts: [
          {
            type: "attrGrant",
            attribute: attr,
            amount: 2,
            suffix: "",
            prefix: "Ability",
          },
        ],
        detail: `Once per turn, the next ${name} active ability has +2 output (damage, healing, armor, etc.).`,
      }),
    ];
  }
  return [];
}

function aegisAspectTraitExamOptions(level) {
  if (level === 2) {
    return [
      makeAspectTraitExamOption({
        id: "aegis_l2_wisdom_magic_armor",
        label: "WIS at Start & +1 Magic Armor/turn",
        shortLabel: "+1 WIS/turn · +1 Mag. Armor/turn",
        captionParts: [
          traitAttrGrantCaptionPart("wisdom"),
          traitSepCaptionPart(),
          traitStatusCaptionPart("magicArmor", 1),
        ],
        detail:
          "Gain one Wisdom resource at the start of every turn. Also gain +1 Magic Armor at the beginning of your turn (stacks with your Aspect).",
      }),
    ];
  }
  if (level === 3) {
    return [
      makeAspectTraitExamOption({
        id: "aegis_l3a_ma_cost_health",
        label: "Magic Armor Cost -1 & +5 Health",
        shortLabel: "MA Cost -1 · +5 HP",
        captionParts: [
          traitTextCaptionPart("MA Cost -1"),
          traitSepCaptionPart(),
          traitHealCaptionPart(5),
        ],
        detail:
          "Once per turn, equipment effects or active abilities that would grant Magic Armor cost 1 less (minimum of 1). Immediately gain +5 current and max health.",
        branch: "A",
      }),
      makeAspectTraitExamOption({
        id: "aegis_l3b_ma_cost_persist",
        label: "Magic Armor Cost -1 & +2 Persistent WIS",
        shortLabel: "MA Cost -1 · +2 Persist",
        captionParts: [
          traitTextCaptionPart("MA Cost -1"),
          traitSepCaptionPart(),
          {
            type: "attrGrant",
            attribute: "wisdom",
            amount: 2,
            suffix: "",
            persistent: true,
          },
        ],
        detail:
          "Once per turn, equipment effects or active abilities that would grant Magic Armor cost 1 less (minimum of 1). Immediately gain 2 Persistent Wisdom resources.",
        branch: "B",
      }),
    ];
  }
  if (level === 4) {
    return [
      makeAspectTraitExamOption({
        id: "aegis_l4a_equip_ma_bump",
        label: "WIS at Start & Empower Magic Armor Gear",
        shortLabel: "+1 WIS/turn · Gear MA +1",
        captionParts: [
          traitAttrGrantCaptionPart("wisdom"),
          traitSepCaptionPart(),
          traitTextCaptionPart("Gear MA +1"),
        ],
        detail:
          "Gain one Wisdom resource at the start of every turn. Choose a Wisdom equipment that grants Magic Armor to permanently increase both its passive Magic Armor and the Magic Armor it grants when expended by 1 for the rest of the match, or Skip for +3 XP.",
        branch: "A",
      }),
      makeAspectTraitExamOption({
        id: "aegis_l4b_ability_level",
        label: "WIS at Start & Level Magic Armor Ability",
        shortLabel: "+1 WIS/turn · Ability +1",
        captionParts: [
          traitAttrGrantCaptionPart("wisdom"),
          traitSepCaptionPart(),
          traitTextCaptionPart("Ability +1"),
        ],
        detail:
          "Gain one Wisdom resource at the start of every turn. Choose a Wisdom Active Ability with a Magic Armor effect to permanently Level Up, or Skip for +3 XP.",
        branch: "B",
      }),
    ];
  }
  if (level === 5) {
    return [
      makeAspectTraitExamOption({
        id: "aegis_l5_ma_grant_damage",
        label: "Magic Armor Retaliation",
        shortLabel: "On MA Gain: 1 Mag Dmg",
        captionParts: [traitTextCaptionPart("On MA → 1 Mag Dmg")],
        detail:
          "Every time you would gain any amount of Magic Armor, deal 1 magical damage to the opponent.",
      }),
    ];
  }
  return [];
}

function arcanistAspectTraitExamOptions(level) {
  if (level === 2) {
    return [
      makeAspectTraitExamOption({
        id: "arcanist_l2a_wisdom_health",
        label: "WIS at Start & +5 Health",
        shortLabel: "+1 WIS/turn · +5 HP",
        captionParts: [
          traitAttrGrantCaptionPart("wisdom"),
          traitSepCaptionPart(),
          traitHealCaptionPart(5),
        ],
        detail:
          "Gain one Wisdom resource at the start of every turn. Immediately gain +5 current and max health.",
        branch: "A",
      }),
      makeAspectTraitExamOption({
        id: "arcanist_l2b_magical_invoke",
        label: "Invoke → +2 WIS & +5 Health",
        shortLabel: "Invoke → +2 WIS · +5 HP",
        captionParts: [
          {
            type: "attrGrant",
            attribute: "wisdom",
            amount: 2,
            suffix: "",
            prefix: "Invoke",
          },
          traitSepCaptionPart(),
          traitHealCaptionPart(5),
        ],
        detail:
          "Once per turn, you may Invoke a card that deals magical damage to gain 2 Wisdom. Uses your one Invoke per turn. Immediately gain +5 current and max health.",
        branch: "B",
      }),
    ];
  }
  if (level === 3) {
    return [
      makeAspectTraitExamOption({
        id: "arcanist_l3a_ability_level",
        label: "Level Up Wisdom Ability",
        shortLabel: "Ability +1",
        captionParts: [traitTextCaptionPart("Ability +1")],
        detail:
          "Choose a slotted Wisdom Ability (active, augment, or essence) on your board to permanently Level Up, or Skip for +3 XP. If you have no matching slotted Abilities, gain +3 XP.",
        branch: "A",
      }),
      makeAspectTraitExamOption({
        id: "arcanist_l3b_magic_damage",
        label: "Magical Damage +1 ×2",
        shortLabel: "Mag Dmg +1 ×2",
        captionParts: [traitTextCaptionPart("Mag Dmg +1 ×2")],
        detail:
          "Twice per turn, the next effect from an equipment or active ability that would deal magical damage deals 1 additional magical damage. Each charge applies to a separate instance (stacks with your Aspect for more instances, not more bonus on one hit).",
        branch: "B",
      }),
    ];
  }
  if (level === 4) {
    return [
      makeAspectTraitExamOption({
        id: "arcanist_l4a_wisdom_health",
        label: "WIS at Start & +5 Health",
        shortLabel: "+1 WIS/turn · +5 HP",
        captionParts: [
          traitAttrGrantCaptionPart("wisdom"),
          traitSepCaptionPart(),
          traitHealCaptionPart(5),
        ],
        detail:
          "Gain one Wisdom resource at the start of every turn. Immediately gain +5 current and max health.",
        branch: "A",
      }),
      makeAspectTraitExamOption({
        id: "arcanist_l4b_magic_cost",
        label: "Magical Effect Cost -1 ×2",
        shortLabel: "Mag Cost -1 ×2",
        captionParts: [traitTextCaptionPart("Mag Cost -1 ×2")],
        detail:
          "Twice per turn, reduce the cost of the next effect from an equipment or active ability that deals magical damage by 1 (minimum of 1 cost). Each charge applies to a separate instance.",
        branch: "B",
      }),
    ];
  }
  if (level === 5) {
    return [
      makeAspectTraitExamOption({
        id: "arcanist_l5a_ability_persist",
        label: "Level Up Ability & +4 Persistent WIS",
        shortLabel: "Ability +1 · +4 Persist",
        captionParts: [
          traitTextCaptionPart("Ability +1"),
          traitSepCaptionPart(),
          {
            type: "attrGrant",
            attribute: "wisdom",
            amount: 4,
            suffix: "",
            persistent: true,
          },
        ],
        detail:
          "Choose a slotted Wisdom Ability (active, augment, or essence) on your board to permanently Level Up, or Skip for +3 XP. Immediately gain 4 Persistent Wisdom resources.",
        branch: "A",
      }),
      makeAspectTraitExamOption({
        id: "arcanist_l5b_discard_to_deck",
        label: "Recycle Discard",
        shortLabel: "Discard → Deck",
        captionParts: [traitTextCaptionPart("Discard → Deck")],
        detail:
          "Once per turn at the beginning of your turn and before drawing a card, a random card from your discard pile is shuffled into your deck.",
        branch: "B",
      }),
    ];
  }
  return [];
}

function toxicologistAspectTraitExamOptions(level) {
  if (level === 2) {
    return [
      makeAspectTraitExamOption({
        id: "toxicologist_l2a_poison_cost_stack",
        label: "Poison Cost -1 & Poison +1",
        shortLabel: "Poison Cost -1 · Poison +1",
        captionParts: [
          traitTextCaptionPart("Poison Cost -1"),
          traitSepCaptionPart(),
          traitTextCaptionPart("Poison +1"),
        ],
        detail:
          "Once per turn, the next poison-type equipment effect or poison-type active ability costs 1 less (minimum 1). Once per turn, the next poison-type equipment effect or poison-type active ability that applies at least 1 Poison applies 1 additional Poison. This Poison bonus is a second separate instance from your Aspect.",
        branch: "A",
      }),
      makeAspectTraitExamOption({
        id: "toxicologist_l2b_poison_cost_heal_curse",
        label: "Poison Cost -1 & Heal → Poison",
        shortLabel: "Poison Cost -1 · Heal → Poison",
        captionParts: [
          traitTextCaptionPart("Poison Cost -1"),
          traitSepCaptionPart(),
          traitTextCaptionPart("Heal → Poison"),
        ],
        detail:
          "Once per turn, the next poison-type equipment effect or poison-type active ability costs 1 less (minimum 1). Once per turn on the opponent's turn, when the opponent would heal any amount, the opponent gains 1 Poison.",
        branch: "B",
      }),
    ];
  }
  if (level === 3) {
    return [
      makeAspectTraitExamOption({
        id: "toxicologist_l3a_ability_health",
        label: "Level Poison Ability & +5 Health",
        shortLabel: "Ability +1 · +5 HP",
        captionParts: [
          traitTextCaptionPart("Ability +1"),
          traitSepCaptionPart(),
          traitHealCaptionPart(5),
        ],
        detail:
          "Choose a Poison-type Ability on your board to permanently Level Up, or Skip for +3 XP. Immediately gain +5 current and max health.",
        branch: "A",
      }),
      makeAspectTraitExamOption({
        id: "toxicologist_l3b_ability_dodge",
        label: "Level Poison Ability & Dodge/turn",
        shortLabel: "Ability +1 · Dodge/turn",
        captionParts: [
          traitTextCaptionPart("Ability +1"),
          traitSepCaptionPart(),
          traitStatusCaptionPart("dodge", 1),
        ],
        detail:
          "Choose a Poison-type Ability on your board to permanently Level Up, or Skip for +3 XP. At the start of each turn, gain Dodge 25%.",
        branch: "B",
      }),
      makeAspectTraitExamOption({
        id: "toxicologist_l3c_ability_heal",
        label: "Level Poison Ability & Heal 1/turn",
        shortLabel: "Ability +1 · Heal 1/turn",
        captionParts: [
          traitTextCaptionPart("Ability +1"),
          traitSepCaptionPart(),
          traitHealCaptionPart(1),
        ],
        detail:
          "Choose a Poison-type Ability on your board to permanently Level Up, or Skip for +3 XP. At the start of each turn, heal 1.",
        branch: "C",
      }),
    ];
  }
  if (level === 4) {
    return [
      makeAspectTraitExamOption({
        id: "toxicologist_l4a_poison_neutral",
        label: "Poison → +2 NEU",
        shortLabel: "Poison → +2 NEU",
        captionParts: [
          {
            type: "attrGrant",
            attribute: "neutral",
            amount: 2,
            suffix: "",
            prefix: "Poison",
          },
        ],
        detail:
          "Once per turn, when you would apply any amount of Poison, gain 2 Neutral resources.",
        branch: "A",
      }),
      makeAspectTraitExamOption({
        id: "toxicologist_l4b_poison_dex",
        label: "Poison → +1 DEX",
        shortLabel: "Poison → +1 DEX",
        captionParts: [
          {
            type: "attrGrant",
            attribute: "dexterity",
            amount: 1,
            suffix: "",
            prefix: "Poison",
          },
        ],
        detail:
          "Once per turn, when you would apply Poison, gain 1 DEX resource.",
        branch: "B",
      }),
    ];
  }
  if (level === 5) {
    return [
      makeAspectTraitExamOption({
        id: "toxicologist_l5_poison_ability_level",
        label: "Poison Abilities +1 Level & Cost -1",
        shortLabel: "Poison Ability +1 · Cost -1",
        captionParts: [
          traitTextCaptionPart("Poison Ability +1"),
          traitSepCaptionPart(),
          traitTextCaptionPart("Cost -1"),
        ],
        detail:
          "All slotted Poison-type Abilities have +1 level. This applies to current and future slotted Poison-type Abilities. Each Poison Ability permanently costs 1 less (minimum of 1). Once-per-turn Poison cost reduction from earlier Traits still applies separately.",
      }),
    ];
  }
  return [];
}

function ironcladAspectTraitExamOptions(level) {
  if (level === 2) {
    return [
      makeAspectTraitExamOption({
        id: "ironclad_l2a_str",
        label: "STR at Start",
        shortLabel: "+1 STR/turn",
        captionParts: [traitAttrGrantCaptionPart("strength")],
        detail: "Gain one Strength resource at the start of every turn.",
        branch: "A",
      }),
      makeAspectTraitExamOption({
        id: "ironclad_l2b_armor_pay",
        label: "Pay Physical Actions with Armor",
        shortLabel: "Pay with Phys. Armor",
        captionParts: [traitTextCaptionPart("Pay with Phys. Armor")],
        detail:
          "Once per turn, pay for a physical-damage equipment action or active ability at a 1-to-1 conversion using Physical Armor instead of resources. Matching cards show an additional action button with Physical Armor cost icons.",
        branch: "B",
      }),
      makeAspectTraitExamOption({
        id: "ironclad_l2c_empower",
        label: "Empower Physical Armor Card",
        shortLabel: "PA Card +1",
        captionParts: [traitTextCaptionPart("PA Card +1")],
        detail:
          "Choose a slotted ability that grants Physical Armor to permanently Level Up, or a played equipment card whose expend effect grants Physical Armor to permanently increase the Physical Armor it grants when expended by 1 for the rest of the match (passive Physical Armor is unchanged). Skip for +3 XP.",
        branch: "C",
      }),
    ];
  }
  if (level === 3) {
    return [
      makeAspectTraitExamOption({
        id: "ironclad_l3a_armor_gain",
        label: "Physical Armor Gain +1",
        shortLabel: "PA Gain +1",
        captionParts: [traitTextCaptionPart("PA Gain +1")],
        detail:
          "Once per turn, when you would gain physical armor, gain 1 additional physical armor.",
        branch: "A",
      }),
      makeAspectTraitExamOption({
        id: "ironclad_l3b_armor_gain",
        label: "Physical Armor Gain +1",
        shortLabel: "PA Gain +1",
        captionParts: [traitTextCaptionPart("PA Gain +1")],
        detail:
          "Once per turn, when you would gain physical armor, gain 1 additional physical armor.",
        branch: "B",
      }),
      makeAspectTraitExamOption({
        id: "ironclad_l3c_armor_gain",
        label: "Physical Armor Gain +1",
        shortLabel: "PA Gain +1",
        captionParts: [traitTextCaptionPart("PA Gain +1")],
        detail:
          "Once per turn, when you would gain physical armor, gain 1 additional physical armor.",
        branch: "C",
      }),
    ];
  }
  if (level === 4) {
    return [
      makeAspectTraitExamOption({
        id: "ironclad_l4a_heal_cost",
        label: "Armor Gain → Heal & Cost -1",
        shortLabel: "PA Gain → Heal · Cost -1",
        captionParts: [
          traitTextCaptionPart("PA Gain → Heal"),
          traitSepCaptionPart(),
          traitTextCaptionPart("PA Cost -1"),
        ],
        detail:
          "Once per turn, when you would gain physical armor, heal equal to 50% of the amount of physical armor gained (rounded down). Also reduce the cost of the next equipment action or ability effect that would grant physical armor by 1 (minimum 1).",
        branch: "A",
      }),
      makeAspectTraitExamOption({
        id: "ironclad_l4b_physical_invoke",
        label: "Invoke Physical → +1 Armor & +5 Health",
        shortLabel: "Invoke → +1 PA · +5 HP",
        captionParts: [
          traitTextCaptionPart("Invoke → +1 PA"),
          traitSepCaptionPart(),
          traitHealCaptionPart(5),
        ],
        detail:
          "You may Invoke physical-type equipment or ability cards to gain 1 Physical Armor. Uses your one Invoke per turn. Immediately gain +5 current and max health.",
        branch: "B",
      }),
      makeAspectTraitExamOption({
        id: "ironclad_l4c_persistent",
        label: "Persistent Physical Armor & +5 Health",
        shortLabel: "Persist PA · +5 HP",
        captionParts: [
          traitTextCaptionPart("Persist PA"),
          traitSepCaptionPart(),
          traitHealCaptionPart(5),
        ],
        detail:
          "All physical armor you gain and your current temporary physical armor become persistent physical armor (remains between rounds). Immediately gain +5 current and max health.",
        branch: "C",
      }),
    ];
  }
  if (level === 5) {
    return [
      makeAspectTraitExamOption({
        id: "ironclad_l5a_armor_to_heal",
        label: "Turn Start: Armor → Heal",
        shortLabel: "PA → Heal",
        captionParts: [traitTextCaptionPart("PA → Heal")],
        detail:
          "At the start of your turn, after gaining any physical armor but before you can play cards, remove all physical armor and heal equal to the amount removed.",
        branch: "A",
      }),
      makeAspectTraitExamOption({
        id: "ironclad_l5b_thorns",
        label: "+1 Thorns/turn",
        shortLabel: "+1 Thorns/turn",
        captionParts: [traitStatusCaptionPart("thorns", 1)],
        detail: "At the start of each of your turns, gain 1 Thorns stack.",
        branch: "B",
      }),
      makeAspectTraitExamOption({
        id: "ironclad_l5c_turn_start_armor",
        label: "+1 Physical Armor/turn",
        shortLabel: "+1 Phys. Armor/turn",
        captionParts: [traitStatusCaptionPart("physicalArmor", 1)],
        detail: "At the start of each of your turns, gain 1 physical armor.",
        branch: "C",
      }),
    ];
  }
  return [];
}

function travelerAspectTraitExamOptions(level) {
  if (level === 2) {
    return [
      makeAspectTraitExamOption({
        id: "traveler_l2a_str",
        label: "STR at Start",
        shortLabel: "+1 STR/turn",
        captionParts: [traitAttrGrantCaptionPart("strength")],
        detail: "At the start of your turn, gain 1 Strength.",
        branch: "A",
      }),
      makeAspectTraitExamOption({
        id: "traveler_l2b_dex",
        label: "DEX at Start",
        shortLabel: "+1 DEX/turn",
        captionParts: [traitAttrGrantCaptionPart("dexterity")],
        detail: "At the start of your turn, gain 1 Dexterity.",
        branch: "B",
      }),
      makeAspectTraitExamOption({
        id: "traveler_l2c_wis",
        label: "WIS at Start",
        shortLabel: "+1 WIS/turn",
        captionParts: [traitAttrGrantCaptionPart("wisdom")],
        detail: "At the start of your turn, gain 1 Wisdom.",
        branch: "C",
      }),
    ];
  }
  if (level === 3) {
    return [
      makeAspectTraitExamOption({
        id: "traveler_l3a_physical_armor",
        label: "+1 Physical Armor/turn",
        shortLabel: "+1 Phys. Armor/turn",
        captionParts: [traitStatusCaptionPart("physicalArmor", 1)],
        detail: "At the start of your turn, gain 1 physical armor.",
        branch: "A",
      }),
      makeAspectTraitExamOption({
        id: "traveler_l3b_dodge",
        label: "+1 Dodge/turn",
        shortLabel: "+1 Dodge/turn",
        captionParts: [traitStatusCaptionPart("dodge", 1)],
        detail: "At the start of your turn, gain 1 dodge.",
        branch: "B",
      }),
      makeAspectTraitExamOption({
        id: "traveler_l3c_magic_armor",
        label: "+1 Magic Armor/turn",
        shortLabel: "+1 Mag. Armor/turn",
        captionParts: [traitStatusCaptionPart("magicArmor", 1)],
        detail: "At the start of your turn, gain 1 magic armor.",
        branch: "C",
      }),
    ];
  }
  if (level === 4) {
    return [
      makeAspectTraitExamOption({
        id: "traveler_l4a_str_cost",
        label: "Next Strength Cost -1",
        shortLabel: "STR Cost -1",
        captionParts: [traitTextCaptionPart("STR Cost -1")],
        detail:
          "Once per turn, the next Strength equipment or ability effect costs 1 less Strength (minimum of 0).",
        branch: "A",
      }),
      makeAspectTraitExamOption({
        id: "traveler_l4b_dex_cost",
        label: "Next Dexterity Cost -1",
        shortLabel: "DEX Cost -1",
        captionParts: [traitTextCaptionPart("DEX Cost -1")],
        detail:
          "Once per turn, the next Dexterity equipment or ability effect costs 1 less Dexterity (minimum of 0).",
        branch: "B",
      }),
      makeAspectTraitExamOption({
        id: "traveler_l4c_wis_cost",
        label: "Next Wisdom Cost -1",
        shortLabel: "WIS Cost -1",
        captionParts: [traitTextCaptionPart("WIS Cost -1")],
        detail:
          "Once per turn, the next Wisdom equipment or ability effect costs 1 less Wisdom (minimum of 0).",
        branch: "C",
      }),
    ];
  }
  if (level === 5) {
    return [
      makeAspectTraitExamOption({
        id: "traveler_l5_health",
        label: "+15 Health",
        shortLabel: "+15 HP",
        captionParts: [traitHealCaptionPart(15)],
        detail: "Immediately gain +15 current and max health.",
      }),
    ];
  }
  return [];
}

function brawlerAspectTraitExamOptions(level) {
  if (level === 2) {
    return [
      makeAspectTraitExamOption({
        id: "brawler_2a",
        label: "STR at Start & Melee +1 & +5 Health",
        shortLabel: "+1 STR/turn · Melee +1 · +5 HP",
        captionParts: [
          traitAttrGrantCaptionPart("strength"),
          traitSepCaptionPart(),
          traitTextCaptionPart("Melee +1"),
          traitSepCaptionPart(),
          traitHealCaptionPart(5),
        ],
        detail:
          "Gain one Strength resource at the start of every turn. Once per turn, the next melee-type equipment or ability that deals physical damage deals 1 additional physical damage. Immediately gain +5 current and max health.",
        branch: "A",
      }),
      makeAspectTraitExamOption({
        id: "brawler_2b",
        label: "Melee Cost -1 & Melee +1",
        shortLabel: "Melee Cost -1 · Melee +1",
        captionParts: [
          traitTextCaptionPart("Melee Cost -1"),
          traitSepCaptionPart(),
          traitTextCaptionPart("Melee +1"),
        ],
        detail:
          "Once per turn, decrease the cost of the next melee-type equipment or active ability by 1 (minimum of 1). Once per turn, the next melee-type equipment or ability that deals physical damage deals 1 additional physical damage.",
        branch: "B",
      }),
    ];
  }
  if (level === 3) {
    return [
      makeAspectTraitExamOption({
        id: "brawler_3a",
        label: "Melee +1 & +5 Health",
        shortLabel: "Melee +1 · +5 HP",
        captionParts: [
          traitTextCaptionPart("Melee +1"),
          traitSepCaptionPart(),
          traitHealCaptionPart(5),
        ],
        detail:
          "Once per turn, the next melee-type equipment or ability that deals physical damage deals 1 additional physical damage. Immediately gain +5 current and max health.",
        branch: "A",
      }),
      makeAspectTraitExamOption({
        id: "brawler_3b",
        label: "Melee +1 & Invoke Melee → 2 Physical Damage",
        shortLabel: "Melee +1 · Invoke → 2 Physical Damage",
        captionParts: [
          traitTextCaptionPart("Melee +1"),
          traitSepCaptionPart(),
          traitTextCaptionPart("Invoke → 2 Physical Damage"),
        ],
        detail:
          "Once per turn, the next melee-type equipment or ability that deals physical damage deals 1 additional physical damage. For the rest of the game, you may Invoke any melee-type card in your hand to deal 2 physical damage. If that card already Invokes for 1 physical damage, it Invokes for 1 additional physical damage instead (2 total). Uses your one Invoke per turn.",
        branch: "B",
      }),
    ];
  }
  if (level === 4) {
    return [
      makeAspectTraitExamOption({
        id: "brawler_4a",
        label: "STR at Start & Melee +1 & +5 Health",
        shortLabel: "+1 STR/turn · Melee +1 · +5 HP",
        captionParts: [
          traitAttrGrantCaptionPart("strength"),
          traitSepCaptionPart(),
          traitTextCaptionPart("Melee +1"),
          traitSepCaptionPart(),
          traitHealCaptionPart(5),
        ],
        detail:
          "Gain one Strength resource at the start of every turn. Once per turn, the next melee-type equipment or ability that deals physical damage deals 1 additional physical damage. Immediately gain +5 current and max health.",
        branch: "A",
      }),
      makeAspectTraitExamOption({
        id: "brawler_4b",
        label: "Melee +1 & Next Melee Unexpend",
        shortLabel: "Melee +1 · Unexpend",
        captionParts: [
          traitTextCaptionPart("Melee +1"),
          traitSepCaptionPart(),
          traitTextCaptionPart("Unexpend"),
        ],
        detail:
          "Once per turn, the next melee-type equipment or ability that deals physical damage deals 1 additional physical damage. Once per turn, the next melee-type equipment or ability used for physical damage does not expend (it remains ready).",
        branch: "B",
      }),
    ];
  }
  if (level === 5) {
    return [
      makeAspectTraitExamOption({
        id: "brawler_5a",
        label: "Next Melee Equipment Free",
        shortLabel: "Equip Free",
        captionParts: [traitTextCaptionPart("Equip Free")],
        detail:
          "Once per turn, the next melee-type equipment action that can be expended to deal physical damage can be used for free.",
        branch: "A",
      }),
      makeAspectTraitExamOption({
        id: "brawler_5b",
        label: "Next Melee Ability Free",
        shortLabel: "Ability Free",
        captionParts: [traitTextCaptionPart("Ability Free")],
        detail:
          "Once per turn, the next melee-type active ability that can be activated to deal physical damage can be used for free.",
        branch: "B",
      }),
    ];
  }
  return [];
}

function ninjutsuAspectTraitExamOptions() {
  return [
    makeAspectTraitExamOption({
      id: "ninjutsu_2a",
      label: "+5 Health & Throwable Echo",
      shortLabel: "+5 HP · Echo",
      mark: "A",
      captionParts: [
        traitHealCaptionPart(5),
        traitSepCaptionPart(),
        traitTextCaptionPart("Echo"),
      ],
      detail:
        "Immediately gain +5 current and max health. Once per turn, when you would expend or activate a throwable-type equipment or ability that deals damage or applies an effect to the opponent, make a copy of that effect and apply it to the opponent.",
    }),
    makeAspectTraitExamOption({
      id: "ninjutsu_2b",
      label: "Next Throwable Output +1",
      shortLabel: "Throw +1",
      mark: "B",
      captionParts: [traitTextCaptionPart("Throw +1")],
      detail:
        "Once per turn, the next throwable's output is increased by 1 (damage, poison, and other effect amounts).",
    }),
    makeAspectTraitExamOption({
      id: "ninjutsu_2c",
      label: "Throwable Damage → Token",
      shortLabel: "Throw → Token",
      mark: "C",
      captionParts: [traitTextCaptionPart("Throw → Token")],
      detail:
        "Once per turn, when you deal damage to a player with a throwable weapon, gain a random Throwable Token (Shuriken, Kunai, or Poison Dart) if your hand has space.",
    }),
    makeAspectTraitExamOption({
      id: "ninjutsu_2d",
      label: "Turn Start Token & +5 Health",
      shortLabel: "Token · +5 HP",
      mark: "D",
      captionParts: [
        traitTextCaptionPart("Token"),
        traitSepCaptionPart(),
        traitHealCaptionPart(5),
      ],
      detail:
        "Immediately gain +5 current and max health. At the start of your turn, gain a random Throwable Token (Shuriken, Kunai, or Poison Dart) if your hand has space.",
    }),
    makeAspectTraitExamOption({
      id: "ninjutsu_2e",
      label: "Turn Start Token & +1 Dodge",
      shortLabel: "Token · Dodge",
      mark: "E",
      captionParts: [
        traitTextCaptionPart("Token"),
        traitSepCaptionPart(),
        traitStatusCaptionPart("dodge", 1),
      ],
      detail:
        "At the start of your turn, gain a random Throwable Token (Shuriken, Kunai, or Poison Dart) if your hand has space, and gain 1 additional Dodge 25% stack.",
    }),
    makeAspectTraitExamOption({
      id: "ninjutsu_2f",
      label: "Level Up Ranged Ability",
      shortLabel: "Ranged +1",
      mark: "F",
      captionParts: [traitTextCaptionPart("Ranged +1")],
      detail:
        "Choose a ranged Active Ability on your board to permanently Level Up, or Skip for +3 XP. If you have no matching slotted Abilities, gain +3 XP.",
    }),
    makeAspectTraitExamOption({
      id: "ninjutsu_2g",
      label: "Physical → 2 Neutral",
      shortLabel: "+2 Neutral",
      mark: "G",
      captionParts: [traitAttrGrantCaptionPart("neutral", 2, "")],
      detail:
        "Once per turn, when you deal physical damage with a one-handed weapon or an ability, gain 2 Neutral.",
    }),
    makeAspectTraitExamOption({
      id: "ninjutsu_2h",
      label: "Melee Physical Critical Strike",
      shortLabel: "Crit",
      mark: "H",
      captionParts: [traitTextCaptionPart("Crit")],
      detail:
        "Once per turn, when you expend a melee one-handed weapon that deals physical damage or activate a melee ability that deals physical damage, those effects gain Critical Strike (ignore armor).",
    }),
  ];
}

const TRAIT_TREE_LEVELS = [2, 3, 4, 5];
const TRAIT_TREE_ROOT_ID = "aspect-root";
const TRAIT_TREE_NODE_POSITIONS = {
  [TRAIT_TREE_ROOT_ID]: { x: 50, y: 8 },
  2: [
    { x: 28, y: 28 },
    { x: 72, y: 28 },
  ],
  3: [
    { x: 28, y: 48 },
    { x: 72, y: 48 },
  ],
  4: [
    { x: 28, y: 68 },
    { x: 72, y: 68 },
  ],
  5: [
    { x: 28, y: 88 },
    { x: 72, y: 88 },
  ],
};
const AEGIS_TRAIT_TREE_NODE_POSITIONS = {
  [TRAIT_TREE_ROOT_ID]: { x: 50, y: 8 },
  2: [{ x: 50, y: 28 }],
  3: [
    { x: 28, y: 48 },
    { x: 72, y: 48 },
  ],
  4: [
    { x: 28, y: 68 },
    { x: 72, y: 68 },
  ],
  5: [{ x: 50, y: 88 }],
};
const TOXICOLOGIST_TRAIT_TREE_NODE_POSITIONS = {
  [TRAIT_TREE_ROOT_ID]: { x: 50, y: 8 },
  2: [
    { x: 28, y: 28 },
    { x: 72, y: 28 },
  ],
  3: [
    { x: 18, y: 48 },
    { x: 50, y: 48 },
    { x: 82, y: 48 },
  ],
  4: [
    { x: 28, y: 68 },
    { x: 72, y: 68 },
  ],
  5: [{ x: 50, y: 88 }],
};
const IRONCLAD_TRAIT_TREE_NODE_POSITIONS = {
  [TRAIT_TREE_ROOT_ID]: { x: 50, y: 8 },
  2: [
    { x: 18, y: 28 },
    { x: 50, y: 28 },
    { x: 82, y: 28 },
  ],
  3: [
    { x: 18, y: 48 },
    { x: 50, y: 48 },
    { x: 82, y: 48 },
  ],
  4: [
    { x: 18, y: 68 },
    { x: 50, y: 68 },
    { x: 82, y: 68 },
  ],
  5: [
    { x: 18, y: 88 },
    { x: 50, y: 88 },
    { x: 82, y: 88 },
  ],
};
const TRAVELER_TRAIT_TREE_NODE_POSITIONS = {
  [TRAIT_TREE_ROOT_ID]: { x: 50, y: 8 },
  2: [
    { x: 18, y: 28 },
    { x: 50, y: 28 },
    { x: 82, y: 28 },
  ],
  3: [
    { x: 18, y: 48 },
    { x: 50, y: 48 },
    { x: 82, y: 48 },
  ],
  4: [
    { x: 18, y: 68 },
    { x: 50, y: 68 },
    { x: 82, y: 68 },
  ],
  5: [{ x: 50, y: 88 }],
};
const NINJUTSU_TRAIT_TREE_NODE_POSITIONS = {
  [TRAIT_TREE_ROOT_ID]: { x: 50, y: 50 },
  ninjutsu_2a: { x: 50, y: 18 },
  ninjutsu_2b: { x: 73, y: 27 },
  ninjutsu_2c: { x: 82, y: 50 },
  ninjutsu_2d: { x: 73, y: 73 },
  ninjutsu_2e: { x: 50, y: 82 },
  ninjutsu_2f: { x: 27, y: 73 },
  ninjutsu_2g: { x: 18, y: 50 },
  ninjutsu_2h: { x: 27, y: 27 },
};

const BRAWLER_TRAIT_TREE_NODE_POSITIONS = {
  [TRAIT_TREE_ROOT_ID]: { x: 50, y: 50 },
  2: [
    { x: 78, y: 44 },
    { x: 22, y: 56 },
  ],
  3: [
    { x: 68, y: 28 },
    { x: 32, y: 72 },
  ],
  4: [
    { x: 54, y: 22 },
    { x: 46, y: 78 },
  ],
  5: [
    { x: 40, y: 18 },
    { x: 60, y: 82 },
  ],
};

function traitOptionShortLabel(option) {
  if (!option) return "";
  if (option.shortLabel) return option.shortLabel;
  const label = String(option.label || "");
  if (label.includes(" & ")) {
    return label.split(" & ").slice(1).join(" & ");
  }
  return label;
}

const TRAIT_CAPTION_ICON_TOKENS = (() => {
  const byPhrase = new Map();
  const add = (phrases, src) => {
    if (!src) return;
    phrases.forEach((phrase) => byPhrase.set(phrase.toLowerCase(), src));
  };
  add(["Magical Damage", "Mag Dmg", "Mag"], ELEMENTAL_DAMAGE_ICON_URL.magical);
  add(
    ["Physical Damage", "Phys Dmg", "Phys"],
    ELEMENTAL_DAMAGE_ICON_URL.physical,
  );
  add(["Fire Damage", "Fire Dmg"], ELEMENTAL_DAMAGE_ICON_URL.fire);
  add(["Cold Damage", "Cold Dmg"], ELEMENTAL_DAMAGE_ICON_URL.cold);
  add(
    ["Lightning Damage", "Lightning Dmg"],
    ELEMENTAL_DAMAGE_ICON_URL.lightning,
  );
  add(
    ["Magic Armor", "Mag. Armor", "MA"],
    STATUS_EFFECT_ICON_URL.magicArmor,
  );
  add(
    ["Physical Armor", "Phys. Armor", "PA"],
    STATUS_EFFECT_ICON_URL.physicalArmor,
  );
  ["poison", "bleed", "burn", "shock", "chill", "thorns", "dodge"].forEach(
    (status) => add([status], STATUS_EFFECT_ICON_URL[status]),
  );
  add(["Heal"], HEAL_ICON_URL);
  const phrases = [...byPhrase.keys()].sort((a, b) => b.length - a.length);
  return {
    byPhrase,
    pattern: new RegExp(
      `\\b(${phrases.map(escapeRegExp).join("|")})\\b`,
      "gi",
    ),
  };
})();

function splitTraitCaptionTextIcons(text) {
  const source = String(text || "");
  const segments = [];
  let lastIndex = 0;
  source.replace(TRAIT_CAPTION_ICON_TOKENS.pattern, (match, _group, offset) => {
    if (offset > lastIndex) {
      segments.push({ text: source.slice(lastIndex, offset) });
    }
    segments.push({
      text: match,
      src: TRAIT_CAPTION_ICON_TOKENS.byPhrase.get(match.toLowerCase()),
    });
    lastIndex = offset + match.length;
    return match;
  });
  if (lastIndex < source.length) {
    segments.push({ text: source.slice(lastIndex) });
  }
  return segments;
}

function renderTraitTreeCaptionParts(parts, fallbackText) {
  if (!Array.isArray(parts) || !parts.length) {
    return fallbackText
      ? renderTraitTreeCaptionParts([traitTextCaptionPart(fallbackText)])
      : null;
  }
  return parts.map((part, index) => {
    if (!part) return null;
    if (part.type === "sep") {
      return (
        <span key={`sep-${index}`} className={styles.traitTreeCaptionSep}>
          ·
        </span>
      );
    }
    if (part.type === "text") {
      return (
        <span key={`text-${index}`} className={styles.traitTreeCaptionPart}>
          {splitTraitCaptionTextIcons(part.text).map((segment, segmentIndex) => {
            if (!segment.src) return segment.text;
            return (
              <img
                key={`icon-${segmentIndex}`}
                src={segment.src}
                alt={segment.text}
                title={segment.text}
                className={styles.traitTreeCaptionImgIcon}
                draggable={false}
              />
            );
          })}
        </span>
      );
    }
    if (part.type === "status") {
      const iconUrl = STATUS_EFFECT_ICON_URL[part.status];
      return (
        <span key={`status-${index}`} className={styles.traitTreeCaptionPart}>
          +{part.amount}
          {iconUrl ? (
            <img
              src={iconUrl}
              alt=""
              className={styles.traitTreeCaptionImgIcon}
              draggable={false}
              aria-hidden="true"
            />
          ) : null}
        </span>
      );
    }
    if (part.type === "heal") {
      return (
        <span
          key={`heal-${index}`}
          className={`${styles.traitTreeCaptionPart} ${styles.traitTreeCaptionHeal}`}
        >
          +{part.amount}
          <img
            src={HEAL_ICON_URL}
            alt=""
            className={styles.traitTreeCaptionImgIcon}
            draggable={false}
            aria-hidden="true"
          />
        </span>
      );
    }
    if (part.type === "attrGrant") {
      const iconUrl = ATTR_ICON_URL[part.attribute];
      const attrClass = ATTR_CLASS[part.attribute] || "";
      let body = null;
      if (part.costReduce != null) {
        body = (
          <>
            Cost -{part.costReduce}
            {iconUrl ? (
              <MaskIcon src={iconUrl} className={styles.traitTreeCaptionIcon} />
            ) : null}
          </>
        );
      } else if (part.prefix) {
        body = (
          <>
            {part.prefix} +{part.amount}
            {iconUrl ? (
              <MaskIcon src={iconUrl} className={styles.traitTreeCaptionIcon} />
            ) : null}
          </>
        );
      } else {
        body = (
          <>
            +{part.amount}
            {iconUrl ? (
              <MaskIcon src={iconUrl} className={styles.traitTreeCaptionIcon} />
            ) : null}
            {part.persistent ? (
              <span
                className={`${styles.attrIconPersist} ${styles.traitTreeCaptionPersist}`}
                aria-label="persist"
              >
                *
              </span>
            ) : null}
            {part.suffix || ""}
          </>
        );
      }
      return (
        <span
          key={`attr-${index}`}
          className={`${styles.traitTreeCaptionPart} ${attrClass}`}
        >
          {body}
        </span>
      );
    }
    return null;
  });
}

function traitTreeNodeMark(node) {
  if (!node) return "";
  if (node.kind === "aspect") return "A";
  if (node.mark) return node.mark;
  return node.level;
}

function buildNinjutsuTraitTreeNodes(aspectCard, selectedTraits, pendingTraitChoice) {
  const selectedIds = new Set(
    (Array.isArray(selectedTraits) ? selectedTraits : [])
      .map((trait) => trait?.id)
      .filter(Boolean),
  );
  const pendingById = new Map(
    (pendingTraitChoice?.options || [])
      .filter((option) => option?.id)
      .map((option) => [option.id, option]),
  );
  const root = {
    id: TRAIT_TREE_ROOT_ID,
    kind: "aspect",
    level: 1,
    index: 0,
    branch: null,
    mark: null,
    label: aspectCard?.name || "Aspect",
    shortLabel: aspectCard?.name || "Aspect",
    detail:
      formatAspectExamEffectText(aspectCard) ||
      "Your Aspect's base effect for this match.",
    selected: true,
    available: false,
    x: NINJUTSU_TRAIT_TREE_NODE_POSITIONS[TRAIT_TREE_ROOT_ID].x,
    y: NINJUTSU_TRAIT_TREE_NODE_POSITIONS[TRAIT_TREE_ROOT_ID].y,
  };
  const nodes = ninjutsuAspectTraitExamOptions().map((examOption, index) => {
    const pending = pendingById.get(examOption.id);
    const option = pending
      ? {
          ...examOption,
          label: pending.label || examOption.label,
          detail: pending.detail || examOption.detail,
        }
      : examOption;
    const position = NINJUTSU_TRAIT_TREE_NODE_POSITIONS[option.id] || {
      x: 50,
      y: 50,
    };
    return {
      id: option.id,
      kind: "trait",
      level: 2,
      index,
      branch: null,
      mark: option.mark || null,
      label: option.label,
      shortLabel: traitOptionShortLabel(option),
      captionParts: option.captionParts || null,
      detail: option.detail,
      selected: selectedIds.has(option.id),
      available: pendingById.has(option.id),
      x: position.x,
      y: position.y,
    };
  });
  return [root, ...nodes];
}

function buildTraitTreeNodes(aspectCard, selectedTraits, pendingTraitChoice) {
  const aspectId = aspectCard?.id || null;
  if (aspectId === "as_ninjutsu") {
    return buildNinjutsuTraitTreeNodes(
      aspectCard,
      selectedTraits,
      pendingTraitChoice,
    );
  }
  const aspectAttributes = aspectCard?.attributes || [];
  const isAegis = aspectId === "as_aegis";
  const isBrawler = aspectId === "as_brawler";
  const isToxicologist = aspectId === "as_toxicologist";
  const isIronclad = aspectId === "as_ironclad";
  const isTraveler = aspectId === "as_traveler";
  const positions = isBrawler
    ? BRAWLER_TRAIT_TREE_NODE_POSITIONS
    : isAegis
      ? AEGIS_TRAIT_TREE_NODE_POSITIONS
      : isToxicologist
        ? TOXICOLOGIST_TRAIT_TREE_NODE_POSITIONS
        : isIronclad
          ? IRONCLAD_TRAIT_TREE_NODE_POSITIONS
          : isTraveler
            ? TRAVELER_TRAIT_TREE_NODE_POSITIONS
            : TRAIT_TREE_NODE_POSITIONS;
  const selectedIds = new Set(
    (Array.isArray(selectedTraits) ? selectedTraits : [])
      .map((trait) => trait?.id)
      .filter(Boolean),
  );
  const pendingById = new Map(
    (pendingTraitChoice?.options || [])
      .filter((option) => option?.id)
      .map((option) => [option.id, option]),
  );
  const root = {
    id: TRAIT_TREE_ROOT_ID,
    kind: "aspect",
    level: 1,
    index: 0,
    branch: null,
    label: aspectCard?.name || "Aspect",
    shortLabel: aspectCard?.name || "Aspect",
    detail:
      formatAspectExamEffectText(aspectCard) ||
      "Your Aspect's base effect for this match.",
    selected: true,
    available: false,
    x: positions[TRAIT_TREE_ROOT_ID].x,
    y: positions[TRAIT_TREE_ROOT_ID].y,
  };
  const levelNodes = TRAIT_TREE_LEVELS.flatMap((level) => {
    const examOptions = getAspectTraitExamOptionsForLevel(
      aspectId,
      level,
      aspectAttributes,
    );
    const options = examOptions.map((examOption) => {
      const pending = pendingById.get(examOption.id);
      return pending
        ? {
            ...examOption,
            label: pending.label || examOption.label,
            detail: pending.detail || examOption.detail,
            branch: pending.branch || examOption.branch || null,
          }
        : examOption;
    });
    return options.map((option, index) => {
      const position = positions[level]?.[index] ||
        positions[level]?.[0] || { x: 50, y: 50 };
      return {
        id: option.id,
        kind: "trait",
        level,
        index,
        branch: option.branch || null,
        label: option.label,
        shortLabel: traitOptionShortLabel(option),
        captionParts: option.captionParts || null,
        detail: option.detail,
        selected: selectedIds.has(option.id),
        available: pendingById.has(option.id),
        x: position.x,
        y: position.y,
      };
    });
  });
  return [root, ...levelNodes];
}

function buildTraitTreeEdges(nodes, aspectId = null) {
  if (aspectId === "as_ninjutsu") {
    const root = nodes.find((node) => node.id === TRAIT_TREE_ROOT_ID);
    if (!root) return [];
    return nodes
      .filter((node) => node.id !== TRAIT_TREE_ROOT_ID)
      .map((to) => ({
        id: `${root.id}->${to.id}`,
        from: root,
        to,
        active: Boolean(root.selected && (to.selected || to.available)),
      }));
  }
  const byLevel = new Map();
  nodes.forEach((node) => {
    const level = node.level;
    if (!byLevel.has(level)) byLevel.set(level, []);
    byLevel.get(level).push(node);
  });
  const edges = [];
  const levels = [...byLevel.keys()].sort((a, b) => a - b);
  const branched =
    aspectId === "as_aegis" ||
    aspectId === "as_arcanist" ||
    aspectId === "as_brawler" ||
    aspectId === "as_ironclad";
  for (let i = 0; i < levels.length - 1; i += 1) {
    const fromLevel = levels[i];
    const toLevel = levels[i + 1];
    const fromNodes = byLevel.get(fromLevel) || [];
    const toNodes = byLevel.get(toLevel) || [];
    fromNodes.forEach((from) => {
      toNodes.forEach((to) => {
        if (branched) {
          if (
            aspectId === "as_arcanist" ||
            aspectId === "as_brawler" ||
            aspectId === "as_ironclad"
          ) {
            if (from.branch && to.branch && from.branch !== to.branch) {
              return;
            }
          } else if (fromLevel >= 3 && toLevel <= 4) {
            if (from.branch && to.branch && from.branch !== to.branch) {
              return;
            }
          }
        }
        edges.push({
          id: `${from.id}->${to.id}`,
          from,
          to,
          active: Boolean(from.selected && (to.selected || to.available)),
        });
      });
    });
  }
  return edges;
}

function getAspectTraitExamOptionsForLevel(aspectId, level, aspectAttributes = []) {
  const normalizedLevel = Math.max(0, Math.floor(Number(level) || 0));
  if (normalizedLevel < 2 || normalizedLevel > 5) return [];
  if (aspectId === "as_aegis") {
    return aegisAspectTraitExamOptions(normalizedLevel);
  }
  if (aspectId === "as_arcanist") {
    return arcanistAspectTraitExamOptions(normalizedLevel);
  }
  if (aspectId === "as_toxicologist") {
    return toxicologistAspectTraitExamOptions(normalizedLevel);
  }
  if (aspectId === "as_brawler") {
    return brawlerAspectTraitExamOptions(normalizedLevel);
  }
  if (aspectId === "as_ironclad") {
    return ironcladAspectTraitExamOptions(normalizedLevel);
  }
  if (aspectId === "as_traveler") {
    return travelerAspectTraitExamOptions(normalizedLevel);
  }
  if (aspectId === "as_ninjutsu") {
    return normalizedLevel === 2 ? ninjutsuAspectTraitExamOptions() : [];
  }
  const attribute = getAspectTraitAttribute(aspectId, aspectAttributes);
  return primaryAspectTraitExamOptions(normalizedLevel, attribute);
}

function listAspectTraitExamLevels(card) {
  if (!card || !cardHasType(card, "aspect")) return [];
  return [2, 3, 4, 5]
    .map((level) => ({
      level,
      options: getAspectTraitExamOptionsForLevel(
        card.id,
        level,
        card.attributes || [],
      ),
    }))
    .filter((entry) => entry.options.length > 0);
}

function formatAspectExamEffectText(card) {
  if (!card) return null;
  if (
    card.id === "as_way_of_the_agnostic" ||
    (card.passiveEffects || []).some(
      (effect) => effect?.id === "agnosticExclusiveNeutralThresholds",
    )
  ) {
    return formatAgnosticAspectExamText();
  }
  return typeof card.text === "string" && card.text.trim()
    ? card.text.trim()
    : null;
}

function resolveAbilityFaceOutputDelta(card) {
  if (!card) return 0;
  if (card.effectiveAmount != null && card.effect && card.effect.amount != null) {
    const rawBase = Math.max(0, Math.floor(Number(card.effect.amount) || 0));
    const useAdditive = abilityUsesAdditiveLevelScale(card);
    const scaleLevel = useAdditive
      ? Math.max(1, card.effectiveLevel || card.level || 1)
      : abilityDisplayScale(card);
    let inherent = useAdditive
      ? rawBase + getAbilityLevelOutputBonus(scaleLevel)
      : Math.floor(rawBase * scaleLevel);
    const mult = Number(card.outputMult);
    if (Number.isFinite(mult) && mult > 0) {
      inherent = Math.floor(inherent * mult);
    }
    return Math.floor(Number(card.effectiveAmount) || 0) - inherent;
  }
  return Math.floor(Number(card.outputDelta) || 0);
}

function abilityFaceDisplayEffect(card) {
  if (!card?.effect) return null;
  if (Array.isArray(card.effect?.choose)) {
    return getAbilityChoiceDisplayEffect(card);
  }
  const hasRuntimeValues =
    card.effectiveAmount != null ||
    card.effectiveDamage != null ||
    card.effectiveBurn != null ||
    card.effectiveChill != null ||
    card.effectiveShock != null ||
    card.effectivePercent != null ||
    card.effectiveMinionHp != null ||
    card.effectiveLevel != null ||
    card.outputMult != null;
  const useAdditive = abilityUsesAdditiveLevelScale(card);
  const scaleLevel = useAdditive
    ? Math.max(1, card.effectiveLevel || card.level || 1)
    : abilityDisplayScale(card);
  const base = hasRuntimeValues
    ? scaleAbilityEffectForLevel(card.effect, scaleLevel, {
        additive: useAdditive,
      })
    : card.effect;
  if (
    card.effectiveAmount == null &&
    card.effectiveDamage == null &&
    card.effectiveBurn == null &&
    card.effectiveChill == null &&
    card.effectiveShock == null &&
    card.effectivePercent == null &&
    card.effectiveMinionHp == null
  ) {
    return base;
  }
  const overlay = (effect) => {
    if (!effect || typeof effect !== "object") return effect;
    if (Array.isArray(effect)) return effect.map(overlay);
    if (Array.isArray(effect.choose)) {
      return { ...effect, choose: effect.choose.map(overlay) };
    }
    const next = { ...effect };
    if (effect.action) {
      next.action = overlay(effect.action);
    }
    if (card.effectiveMinionHp != null && effect.hp != null) {
      next.hp = card.effectiveMinionHp;
    }
    if (card.effectiveAmount != null) {
      next.amount = card.effectiveAmount;
      next.effectiveAmount = card.effectiveAmount;
    }
    if (card.effectiveElementalDamage && effect.id === "damageOpponent") {
      next.effectiveElementalDamage = card.effectiveElementalDamage;
    }
    if (card.effectiveDamage != null) {
      next.damage = card.effectiveDamage;
      next.effectiveDamage = card.effectiveDamage;
    }
    if (card.effectiveBurn != null) {
      next.burn = card.effectiveBurn;
      next.effectiveBurn = card.effectiveBurn;
    }
    if (card.effectiveChill != null) {
      next.chill = card.effectiveChill;
      next.effectiveChill = card.effectiveChill;
    }
    if (card.effectiveShock != null) {
      next.shock = card.effectiveShock;
      next.effectiveShock = card.effectiveShock;
    }
    if (card.effectivePercent != null) {
      next.percent = card.effectivePercent;
      next.effectivePercent = card.effectivePercent;
    }
    return next;
  };
  return overlay(base);
}

function getAbilityEchoPreviewCard(card) {
  if (!card?.echo || !card.echoPreview || card.kind !== "active") return null;
  return {
    ...card,
    ...card.echoPreview,
    echo: false,
    echoPreview: null,
    ignoreArmor: false,
    effectiveIgnorePhysicalArmor: 0,
  };
}

function formatAbilityActiveEffectText(card) {
  const displayEffect = abilityFaceDisplayEffect(card);
  if (displayEffect?.id === "summonMinion") {
    return formatActivatedEffectText(displayEffect, { omitMinionAction: true });
  }
  return formatAbilityShorthandText(
    formatAbilityOutputEffectText(displayEffect) ||
      formatActivatedEffectText(displayEffect),
  );
}

// Card-face stat block for the Minion a summon active creates: its name,
// health, and its own expend line(s), shown beneath the summon cost line.
function getSummonMinionFaceDisplay(card) {
  if (card?.kind !== "active" || card.effect?.id !== "summonMinion") return null;
  const displayEffect = abilityFaceDisplayEffect(card);
  if (displayEffect?.id !== "summonMinion") return null;
  const outputDelta = resolveAbilityFaceOutputDelta(card);
  return {
    name: displayEffect.name || "Minion",
    hp: Math.max(
      0,
      Math.floor(
        Number(displayEffect.effectiveMinionHp ?? displayEffect.hp) || 0,
      ),
    ),
    lines: listActivatedEffectFaceLines(displayEffect.action).map((line) => ({
      ...line,
      text: formatAbilityShorthandText(line.text),
      outputDelta: line.outputDelta || outputDelta,
    })),
  };
}

function formatAbilityEchoChoiceText(card, index) {
  const echoCard = getAbilityEchoPreviewCard(card);
  if (!echoCard) return null;
  const displayEntry = getAbilityChoiceDisplayEntry(echoCard, index);
  return (
    formatAbilityShorthandText(
      formatAbilityOutputEffectText(displayEntry) ||
        formatActivatedEffectText(displayEntry),
    ) || null
  );
}

function withAbilityEchoSuffix(text, echoText) {
  if (!text || !echoText) return text;
  return `${text} + ${echoText}`;
}

function formatAbilityFaceText(card) {
  if (!card) return null;
  const lines = [];
  if (card.kind === "active") {
    const effectText = formatAbilityActiveEffectText(card);
    if (effectText) lines.push(effectText);
    const echoCard = abilityChoiceHasCostClient(card)
      ? null
      : getAbilityEchoPreviewCard(card);
    const echoText = echoCard ? formatAbilityActiveEffectText(echoCard) : null;
    if (effectText && echoText) lines.push(`+ ${echoText}`);
  } else if (card.kind === "augment") {
    const augmentLines = formatAugmentEffectLines(card, {
      includeMatchTags: false,
      includeCostIncrease: false,
    }).map(formatAbilityShorthandText);
    if (augmentLines.length) lines.push(...augmentLines);
    else if (card.text) lines.push(formatAbilityShorthandText(card.text));
  } else if (card.kind === "essence") {
    const essenceLines = formatEssenceEffectLines(card).map(
      formatAbilityShorthandText,
    );
    if (essenceLines.length) lines.push(...essenceLines);
    else if (card.text) lines.push(formatAbilityShorthandText(card.text));
  } else if (card.text) {
    lines.push(formatAbilityShorthandText(card.text));
  }
  const comboText = formatComboHelperText(card);
  if (comboText) {
    lines.push(formatAbilityShorthandText(comboText));
  } else if (
    card.text &&
    card.kind === "active" &&
    card.effect?.id !== "summonMinion" &&
    card.effect?.id !== "activateAura"
  ) {
    lines.push(formatAbilityShorthandText(card.text));
  }
  return lines.length ? lines.join("\n") : null;
}

function FittedCardBodyFooter({
  traditional,
  fitKey = "",
  skipFit = false,
  children,
}) {
  const footerRef = useRef(null);

  useLayoutEffect(() => {
    const footer = footerRef.current;
    if (!footer || skipFit) return undefined;

    const measureNaturalSize = () => {
      const body = footer.parentElement;
      const unlockProps = {
        height: "auto",
        "max-height": "none",
        overflow: "visible",
        flex: "0 0 auto",
      };
      const restore = [];
      const pushRestore = (el, props) => {
        const prev = {};
        Object.keys(props).forEach((key) => {
          prev[key] = el.style.getPropertyValue(key);
        });
        restore.push({ el, prev, props });
        Object.entries(props).forEach(([key, value]) => {
          if (value == null) el.style.removeProperty(key);
          else el.style.setProperty(key, value);
        });
      };

      pushRestore(footer, unlockProps);
      Array.from(footer.children).forEach((group) => {
        pushRestore(group, unlockProps);
        Array.from(group.children).forEach((line) => {
          pushRestore(line, {
            "max-height": "none",
            overflow: "visible",
            flex: "0 0 auto",
          });
        });
      });

      const footerStyle = window.getComputedStyle(footer);
      const padY =
        (Number.parseFloat(footerStyle.paddingTop) || 0) +
        (Number.parseFloat(footerStyle.paddingBottom) || 0);
      const contentHeight = Math.max(0, footer.scrollHeight - padY);
      const contentWidth = footer.scrollWidth;

      restore.forEach(({ el, prev, props }) => {
        Object.keys(props).forEach((key) => {
          if (prev[key]) el.style.setProperty(key, prev[key]);
          else el.style.removeProperty(key);
        });
      });

      const bodyHeight = Math.max(0, body?.clientHeight || 0);
      return {
        contentHeight,
        contentWidth,
        limitHeight: Math.max(0, bodyHeight - padY),
        limitWidth: Math.max(0, body?.clientWidth || footer.clientWidth || 0),
      };
    };

    const overflows = () => {
      const {
        contentHeight,
        contentWidth,
        limitHeight,
        limitWidth,
      } = measureNaturalSize();
      if (!(limitHeight > 0 || limitWidth > 0)) return false;
      if (limitHeight > 0 && contentHeight > limitHeight + 1) return true;
      if (limitWidth > 0 && contentWidth > limitWidth + 1) return true;
      return false;
    };

    const applySize = (size) => {
      footer.style.fontSize = `${size}px`;
    };

    const fit = () => {
      footer.style.removeProperty("font-size");
      const computed = Number.parseFloat(
        window.getComputedStyle(footer).fontSize,
      );
      if (!Number.isFinite(computed) || computed <= 0) return;
      const body = footer.parentElement;
      const availableH = Math.max(0, body?.clientHeight || footer.clientHeight || 0);
      const availableW = Math.max(
        0,
        body?.clientWidth || footer.clientWidth || 0,
      );
      if (!(availableH > 0 && availableW > 0)) return;

      const compact = availableW < 160;
      const minSize = compact ? 4.5 : traditional ? 5 : 4.5;
      const maxSize = compact
        ? Math.min(traditional ? 12 : 11, Math.max(computed, 9))
        : Math.min(
            traditional ? 16 : 14,
            Math.max(computed, traditional ? 12 : 11),
          );

      applySize(maxSize);
      if (!overflows()) return;

      let lo = minSize;
      let hi = maxSize;
      let best = minSize;
      for (let i = 0; i < 20; i += 1) {
        const mid = (lo + hi) / 2;
        applySize(mid);
        if (overflows()) {
          hi = mid;
        } else {
          best = mid;
          lo = mid;
        }
      }
      applySize(best);
      if (overflows()) applySize(minSize);
    };

    return attachVisibleResizeFit(footer, fit);
  }, [traditional, fitKey, skipFit]);

  return (
    <div ref={footerRef} className={styles.cardBodyFooter}>
      {children}
    </div>
  );
}

function FittedCardName({
  traditional,
  fitKey = "",
  trailing = null,
  skipFit = false,
  children,
}) {
  const nameRef = useRef(null);

  useLayoutEffect(() => {
    const name = nameRef.current;
    if (!name || skipFit) return undefined;

    const header = name.parentElement;
    const card = header?.parentElement;
    if (!header || !card) return undefined;
    const fit = () => {
      name.style.removeProperty("font-size");
      name.style.removeProperty("width");
      name.style.removeProperty("max-width");
      name.style.removeProperty("margin-left");
      name.style.removeProperty("white-space");
      name.style.removeProperty("display");
      name.style.removeProperty("text-align");
      name.style.removeProperty("-webkit-line-clamp");
      name.style.removeProperty("line-clamp");

      const headerBounds = header.getBoundingClientRect();
      const rawHeaderScale =
        header.offsetWidth > 0 && headerBounds.width > 0
          ? headerBounds.width / header.offsetWidth
          : 1;
      const headerScale =
        Math.abs(rawHeaderScale - 1) > 0.05 ? rawHeaderScale : 1;
      const leftAccessory = Array.from(card.children).find((element) =>
        element.classList.contains(styles.cardHeaderCornerLeft),
      );
      const rightAccessory = Array.from(card.children).find((element) =>
        element.classList.contains(styles.cardHeaderResources),
      );
      let safeLeft = headerBounds.left;
      let safeRight = headerBounds.right;
      if (
        leftAccessory &&
        window.getComputedStyle(leftAccessory).visibility !== "hidden"
      ) {
        const accessoryBounds = leftAccessory.getBoundingClientRect();
        if (accessoryBounds.bottom <= headerBounds.bottom + 4) {
          safeLeft = Math.max(safeLeft, accessoryBounds.right + 2);
        }
      }
      if (
        rightAccessory &&
        window.getComputedStyle(rightAccessory).visibility !== "hidden"
      ) {
        safeRight = Math.min(
          safeRight,
          rightAccessory.getBoundingClientRect().left - 2,
        );
      }
      const availableWidth = Math.max(1, (safeRight - safeLeft) / headerScale);
      const offsetLeft = Math.max(
        0,
        (safeLeft - headerBounds.left) / headerScale,
      );
      name.style.marginLeft = `${offsetLeft}px`;
      name.style.maxWidth = `${availableWidth}px`;
      name.style.width = "max-content";
      name.style.whiteSpace = "nowrap";
      name.style.display = "inline-flex";
      name.style.textAlign = "left";

      const label = name.querySelector(`.${styles.cardNameLabel}`) || name;
      const initialSize = Number.parseFloat(
        window.getComputedStyle(name).fontSize,
      );
      if (!Number.isFinite(initialSize)) return;
      let fittedSize = initialSize;
      const minSize = traditional ? 6 : 5;
      while (name.scrollWidth > availableWidth + 1 && fittedSize > minSize) {
        fittedSize = Math.max(minSize, fittedSize - 0.5);
        name.style.fontSize = `${fittedSize}px`;
      }
      if (label.scrollWidth > label.clientWidth + 1 && fittedSize > minSize) {
        while (label.scrollWidth > label.clientWidth + 1 && fittedSize > minSize) {
          fittedSize = Math.max(minSize, fittedSize - 0.5);
          name.style.fontSize = `${fittedSize}px`;
        }
      }
      if (fittedSize === initialSize) {
        name.style.removeProperty("font-size");
      }
    };
    return attachVisibleResizeFit(card, fit, { alsoObserve: name });
  }, [traditional, fitKey, skipFit]);

  return (
    <div ref={nameRef} className={styles.cardName}>
      <span className={styles.cardNameLabel}>{children}</span>
      {trailing}
    </div>
  );
}

const CARD_FOCUS_MS = 280;
const CARD_FOCUS_EASE = "cubic-bezier(0.2, 0.8, 0.2, 1)";
const cardFocusHiddenCounts = new WeakMap();

function hideCardFocusSource(el) {
  if (!el) return;
  cardFocusHiddenCounts.set(el, (cardFocusHiddenCounts.get(el) || 0) + 1);
  el.style.visibility = "hidden";
}

function releaseCardFocusSource(el) {
  if (!el) return;
  const remaining = (cardFocusHiddenCounts.get(el) || 0) - 1;
  if (remaining > 0) {
    cardFocusHiddenCounts.set(el, remaining);
    return;
  }
  cardFocusHiddenCounts.delete(el);
  el.style.visibility = "";
}

function cardFocusSourceElement() {
  const selector = [
    styles.handCardSlotActionsOpen,
    styles.boardCardSlotActionsOpen,
  ]
    .map((name) => `.${CSS.escape(name)}`)
    .join(", ");
  const el = document.querySelector(selector);
  return el instanceof HTMLElement ? el : null;
}

function cardFocusSourceRect(source) {
  if (!source?.isConnected) return null;
  const target = source.firstElementChild || source;
  const rect = target.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0 ? rect : null;
}

function cardFocusFlipTransform(fromRect, toRect) {
  const scale = Math.min(
    fromRect.width / toRect.width,
    fromRect.height / toRect.height,
  );
  const dx =
    fromRect.left + fromRect.width / 2 - (toRect.left + toRect.width / 2);
  const dy =
    fromRect.top + fromRect.height / 2 - (toRect.top + toRect.height / 2);
  return `translate(${dx}px, ${dy}px) scale(${scale})`;
}

function CardFocusOverlay({ size, className = "", children }) {
  const ref = useRef(null);
  const [scale, setScale] = useState(null);
  const baseWidth = size?.width || 0;

  useLayoutEffect(() => {
    const el = ref.current;
    const parent = el?.parentElement;
    if (!el || !parent || !(baseWidth > 0)) return undefined;
    const update = () => {
      const width = parent.clientWidth;
      if (width > 0) setScale(width / baseWidth);
    };
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(parent);
    return () => observer.disconnect();
  }, [baseWidth]);

  if (!children || !(baseWidth > 0)) return null;
  return (
    <div
      ref={ref}
      className={`${styles.cardFocusOverlay}${className ? ` ${className}` : ""}`}
      style={{
        width: size.width,
        height: size.height,
        transform: scale != null ? `scale(${scale})` : undefined,
        visibility: scale != null ? undefined : "hidden",
      }}
    >
      {children}
    </div>
  );
}

function CardFocusView({
  card,
  foilEffect,
  hideName,
  overlay = null,
  overlayClassName = "",
  onReturn,
  onDismiss,
  children,
}) {
  const cardRef = useRef(null);
  const actionsRef = useRef(null);
  const latestRef = useRef(null);
  const [overlaySize, setOverlaySize] = useState(null);
  latestRef.current = {
    card,
    foilEffect,
    hideName,
    overlay,
    overlayClassName,
    overlaySize,
    onReturn,
  };

  useLayoutEffect(() => {
    const cardEl = cardRef.current;
    const actionsEl = actionsRef.current;
    const latest = latestRef;
    const source = cardFocusSourceElement();
    hideCardFocusSource(source);
    const sourceTarget = source?.firstElementChild || source;
    if (sourceTarget instanceof HTMLElement && sourceTarget.offsetWidth > 0) {
      setOverlaySize({
        width: sourceTarget.offsetWidth,
        height: sourceTarget.offsetHeight,
      });
    }
    const layoutRect = cardEl ? cardEl.getBoundingClientRect() : null;
    const animate =
      !prefersReducedCombatMotion() && typeof cardEl?.animate === "function";
    const fromRect = cardFocusSourceRect(source);
    if (animate && fromRect && layoutRect?.width > 0) {
      cardEl.animate(
        [
          {
            transform: cardFocusFlipTransform(fromRect, layoutRect),
            opacity: 0.85,
          },
          { transform: "none", opacity: 1 },
        ],
        { duration: CARD_FOCUS_MS, easing: CARD_FOCUS_EASE },
      );
    }
    if (animate && actionsEl) {
      actionsEl.animate(
        [
          { opacity: 0, transform: "translateY(-10px)" },
          { opacity: 1, transform: "none" },
        ],
        {
          duration: 200,
          delay: CARD_FOCUS_MS * 0.5,
          easing: CARD_FOCUS_EASE,
          fill: "backwards",
        },
      );
    }
    return () => {
      if (!animate || !cardEl?.isConnected || !(layoutRect?.width > 0)) {
        releaseCardFocusSource(source);
        return;
      }
      const computed = window.getComputedStyle(cardEl).transform;
      const transform = computed && computed !== "none" ? computed : "none";
      const rect =
        transform === "none" ? cardEl.getBoundingClientRect() : layoutRect;
      const lastProps = latest.current;
      lastProps.onReturn({
        card: lastProps.card,
        foilEffect: lastProps.foilEffect,
        hideName: lastProps.hideName,
        overlay: lastProps.overlay,
        overlayClassName: lastProps.overlayClassName,
        overlaySize: lastProps.overlaySize,
        source,
        transform,
        rect: {
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
        },
      });
    };
  }, []);

  return (
    <div
      className={styles.cardFocusPanel}
      role="dialog"
      aria-label={card?.name || "Selected card"}
      onClick={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <div
        ref={cardRef}
        className={styles.cardFocusCard}
        title={t("card.tapToReturn")}
        onClick={onDismiss}
      >
        <CardFace
          card={card}
          foilEffect={foilEffect}
          hideName={hideName}
          showPowerBadge={false}
          foilAnimateIdle
          fullArt
        />
        <CardFocusOverlay size={overlaySize} className={overlayClassName}>
          {overlay}
        </CardFocusOverlay>
      </div>
      <div ref={actionsRef} className={styles.cardFocusActions}>
        {children}
      </div>
    </div>
  );
}

function CardFocusGhost({ ghost, onDone }) {
  const ref = useRef(null);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useLayoutEffect(() => {
    const el = ref.current;
    const { source, rect, transform, key } = ghost;
    let released = false;
    const release = () => {
      if (released) return;
      released = true;
      releaseCardFocusSource(source);
    };
    if (!el || typeof el.animate !== "function") {
      release();
      onDoneRef.current(key);
      return undefined;
    }
    const targetRect = cardFocusSourceRect(source);
    const animation = targetRect
      ? el.animate(
          [
            { transform },
            { transform: cardFocusFlipTransform(targetRect, rect) },
          ],
          {
            duration: CARD_FOCUS_MS,
            easing: CARD_FOCUS_EASE,
            fill: "forwards",
          },
        )
      : el.animate(
          [
            { transform, opacity: 1 },
            { transform: "scale(0.8)", opacity: 0 },
          ],
          { duration: 180, easing: "ease-in", fill: "forwards" },
        );
    animation.onfinish = () => onDoneRef.current(key);
    return () => {
      animation.onfinish = null;
      animation.cancel();
      release();
    };
  }, [ghost]);

  return (
    <div
      ref={ref}
      className={styles.cardFocusGhost}
      style={{
        left: ghost.rect.left,
        top: ghost.rect.top,
        width: ghost.rect.width,
        height: ghost.rect.height,
        transform: ghost.transform,
      }}
      aria-hidden="true"
    >
      <CardFace
        card={ghost.card}
        foilEffect={ghost.foilEffect}
        hideName={ghost.hideName}
        showPowerBadge={false}
        fullArt
      />
      <CardFocusOverlay
        size={ghost.overlaySize}
        className={ghost.overlayClassName}
      >
        {ghost.overlay}
      </CardFocusOverlay>
    </div>
  );
}

function ScaledCardFace({ className = "", frameOverlay = null, ...cardProps }) {
  const frameRef = useRef(null);
  const innerRef = useRef(null);
  const [scale, setScale] = useState(null);

  useLayoutEffect(() => {
    const frame = frameRef.current;
    const inner = innerRef.current;
    if (!frame || !inner) return undefined;
    const update = () => {
      const base = inner.offsetWidth;
      const width = frame.clientWidth;
      if (!(base > 0 && width > 0)) return;
      const next = width / base;
      setScale((current) =>
        current != null && Math.abs(current - next) < 0.0005 ? current : next,
      );
    };
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(frame);
    observer.observe(inner);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={frameRef}
      className={`${styles.cardScaleFrame}${className ? ` ${className}` : ""}`}
    >
      <div
        ref={innerRef}
        className={styles.cardScaleInner}
        style={
          scale != null
            ? { transform: `scale(${scale})` }
            : { visibility: "hidden" }
        }
      >
        <CardFace {...cardProps} />
      </div>
      {frameOverlay}
    </div>
  );
}

export function CardFace({
  card,
  selected,
  targeted,
  onClick,
  count,
  countBadge = false,
  activeKeywordId,
  onKeywordClick,
  compact,
  faded,
  locked = false,
  showLockedIcon = false,
  artOnly = false,
  highlightClass = "",
  hideName = false,
  hideSlotIcon = false,
  foilEffect: rawFoilEffect = null,
  equipmentPassiveOnly = false,
  inheritFoilPose = false,
  foilQuality = null,
  foilAnimateIdle = false,
  fitText = true,
  showPowerBadge = true,
  compareCard = null,
  fullArt = false,
}) {
  if (!card) return null;
  const isToken = Boolean(card.token);
  const foilEffect = isToken ? null : rawFoilEffect;
  const traditional = isTraditionalFoil(foilEffect);
  const hideChrome = Boolean(artOnly);
  const hideCardName = hideChrome || hideName;
  const showCountInName = count != null && !countBadge;
  const showCountBadge = countBadge && count != null && count > 0;
  const cardPower = getCardPower(card);
  const showCardPowerBadge =
    showPowerBadge && !hideChrome && !compact && cardPower >= 1;
  const isEquipment = cardHasType(card, "equipment");
  const isAspect = cardHasType(card, "aspect");
  const cardImageUrl = getCardImageUrl(card);
  const borderless = isBorderlessFoil(foilEffect);
  const simpleFrameKey = getSimpleCardFrameKey(card, foilEffect);
  const cardFrameUrl = simpleFrameKey
    ? getSimpleCardFrameUrl(simpleFrameKey)
    : borderless || traditional
      ? null
      : getCardFrameUrl(card);
  const showCardBody = (!compact || traditional) && !hideChrome && !isAspect;
  const resourceGrantChoices = isEquipment
    ? listEquipmentResourceGrantChoices(card)
    : [];
  const hasResourceGrantDisplay = resourceGrantChoices.length > 0;
  const abilityCosts =
    !isEquipment && !isAspect && card.kind === "active"
      ? costEntries(card.cost)
      : [];
  const augmentCostIncrease =
    !isEquipment && !isAspect && card.kind === "augment"
      ? augmentCostIncreaseEntries(card)
      : [];
  const augmentCostMult =
    !isEquipment && !isAspect && card.kind === "augment"
      ? augmentCostMultiplier(card)
      : null;
  const abilityHealthCost =
    !isEquipment && !isAspect && card.kind === "active"
      ? getAbilityHealthCostAmount(card.effect)
      : 0;
  const persistGrants = isEquipment
    ? persistGrantEntries(card.activatedEffect)
    : [];
  const delayedGrants = isEquipment ? delayedResourceGrantEntries(card) : [];
  const activatedEffectLines =
    showCardBody && isEquipment
      ? [
          ...listActivatedEffectFaceLines(
            isGrantResourcesOnlyChooseEffect(card.activatedEffect)
              ? null
              : card.activatedEffect,
            { equipment: card },
          ),
          ...listPaidTempEffectFaceLines(card),
        ]
      : [];
  const triggerDisplays =
    showCardBody && isEquipment && !equipmentPassiveOnly
      ? getEquipmentTriggerDisplays(card)
      : [];
  const passiveDescriptionLines =
    showCardBody && isEquipment
      ? getEquipmentPassiveDescriptionLines(card)
      : [];
  const uniqueEquipmentTextClauses =
    showCardBody && isEquipment ? getEquipmentUniqueTextClauses(card) : [];
  const abilitySlots = isEquipment ? card.abilitySlots || [] : [];
  const abilityFaceText =
    showCardBody && !isEquipment && !isAspect
      ? formatAbilityFaceText(card)
      : null;
  const summonMinionDisplay =
    showCardBody && !isEquipment && !isAspect
      ? getSummonMinionFaceDisplay(card)
      : null;
  const compareAbilityFaceLines =
    compareCard && abilityFaceText
      ? String(formatAbilityFaceText(compareCard) || "").split("\n")
      : null;
  const buildAbilityChoiceText = (source, index) => {
    const displayEntry = stripHealthPaymentEffects(
      getAbilityChoiceDisplayEntry(source, index),
    );
    return withAbilityEchoSuffix(
      formatAbilityShorthandText(
        formatAbilityOutputEffectText(displayEntry) ||
          formatActivatedEffectText(displayEntry),
      ),
      formatAbilityEchoChoiceText(source, index),
    );
  };
  const abilityChoiceLines =
    showCardBody &&
    !isEquipment &&
    !isAspect &&
    card.kind === "active" &&
    (abilityChoiceHasCostClient(card) || abilityChoiceHasHealthCostClient(card))
      ? card.effect.choose
          .map((entry, index) => ({
            cost: costEntries(getAbilityChoiceCost(card, index)),
            healthCost:
              card.effectiveChoiceHealthCosts?.[index] ??
              getEffectHealthPaymentAmount(entry),
            outputDelta: getAbilityChoiceOutputDelta(card, index),
            text: buildAbilityChoiceText(card, index),
            compareText:
              compareCard && Array.isArray(compareCard.effect?.choose)
                ? buildAbilityChoiceText(compareCard, index)
                : null,
          }))
          .filter((line) => line.text)
      : [];
  const abilityLevel =
    !isEquipment &&
    !isAspect &&
    (card.instanceId || card.effectiveLevel != null)
      ? getAbilityDisplayLevel(card)
      : 0;
  const equipmentTier = isEquipment ? getEquipmentTier(card) : 0;
  const skipFit = !fitText;
  const nameFitKey = `${card.name || ""}|${showCountInName ? count : ""}|L${abilityLevel}|T${equipmentTier}`;
  const bodyFitKey = showCardBody
    ? [
        abilityFaceText || "",
        summonMinionDisplay
          ? `${summonMinionDisplay.name}:${summonMinionDisplay.hp}>${summonMinionDisplay.lines
              .map((line) => line.text)
              .join("|")}`
          : "",
        abilityChoiceLines
          .map(
            (line) =>
              `${line.healthCost || 0}:${line.cost.map(([attr, value]) => `${attr}:${value}`).join(",")}>${line.text}`,
          )
          .join("|"),
        abilityCosts.map(([attr, value]) => `${attr}:${value}`).join(","),
        String(abilityHealthCost || 0),
        triggerDisplays.map((entry) => `${entry.key}:${entry.text}`).join("|"),
        activatedEffectLines
          .map((line) => `${line.identity || ""}${line.text}`)
          .join("|"),
        passiveDescriptionLines.join("|"),
        uniqueEquipmentTextClauses.join("|"),
        abilitySlots
          .map((slot) => `${slot?.id || ""}:${slot?.linked ? 1 : 0}`)
          .join(","),
        String(abilityLevel || 0),
        compareAbilityFaceLines ? compareAbilityFaceLines.join("|") : "",
      ].join("::")
    : "";
  const hybridOutline =
    !cardFrameUrl && !borderless
      ? getHybridOutlineProps(
          card,
          traditional ? TRADITIONAL_ATTRIBUTE_COLOR : ATTRIBUTE_COLOR_VAR,
        )
      : { className: "", style: undefined };
  const tint =
    cardFrameUrl || hybridOutline.className || borderless
      ? ""
      : TINT_CLASS[primaryAttribute(card)] || "";
  const interactive = typeof onClick === "function";
  const foilArtFilter = getFoilArtFilterClass(foilEffect);
  return (
    <div
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={hideCardName ? card.name : undefined}
      data-gamepad-item={interactive ? "" : undefined}
      className={`${styles.card} ${tint} ${hybridOutline.className} ${
        simpleFrameKey
          ? `${styles.cardSimpleFrame} ${
              simpleFrameKey === "hybrid" ? styles.cardSimpleFrameBronze : ""
            }`
          : cardFrameUrl
            ? styles.cardWithFrame
            : ""
      } ${isToken ? styles.cardToken : ""} ${borderless ? styles.cardBorderless : ""} ${
        traditional ? styles.cardTraditional : ""
      } ${isAspect ? styles.cardAspect : ""} ${
        cardImageUrl ? styles.cardWithArt : ""
      } ${compact ? styles.cardCompact : ""} ${
        hideCardName ? styles.cardNameHidden : ""
      } ${hideChrome ? styles.cardArtOnly : ""} ${selected ? styles.cardSelected : ""} ${targeted ? styles.cardTarget : ""} ${
        faded ? styles.workshopCardDisabled : ""
      } ${locked ? styles.cardLocked : ""}${highlightClass}`}
      style={hybridOutline.style}
      onPointerEnter={(event) => playCardHoverSfx(event.currentTarget)}
      onClick={onClick}
      onKeyDown={
        interactive
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onClick(event);
              }
            }
          : undefined
      }
    >
      {cardImageUrl ? (
        <div
          aria-hidden="true"
          className={[styles.cardArt, foilArtFilter].filter(Boolean).join(" ")}
          style={
            traditional ? { "--ai-art-url": `url("${cardImageUrl}")` } : undefined
          }
        >
          <AiLazyImage
            className={styles.cardArtMedia}
            src={fullArt ? getCardFullImageUrl(card) : cardImageUrl}
            placeholderSrc={fullArt ? cardImageUrl : null}
            alt=""
            decoding="async"
            draggable={false}
            eager={Boolean(foilEffect || fullArt)}
          />
          {traditional ? (
            <FoilOverlay
              foilEffect={foilEffect}
              cardId={card.id}
              inheritPose={inheritFoilPose}
              quality={foilQuality}
              animateIdle={foilAnimateIdle}
            />
          ) : null}
        </div>
      ) : null}
      {cardFrameUrl ? <CardFrameOverlay frameUrl={cardFrameUrl} /> : null}
      {!hideChrome && !hideSlotIcon && isEquipment && card.slot ? (
        <div className={styles.cardHeaderCornerLeft}>
          <EquipmentSlotIcon
            slot={card.slot}
            className={compact ? styles.equipmentSlotIconCompact : ""}
          />
        </div>
      ) : null}
      {!hideChrome ? (
        <CardHeaderResources
          resourceGrantChoices={isEquipment ? resourceGrantChoices : null}
          persistGrants={isEquipment ? persistGrants : null}
          delayedGrants={isEquipment ? delayedGrants : null}
          abilityCosts={
            !isEquipment &&
            card.kind === "active" &&
            compact &&
            !traditional
              ? abilityCosts
              : null
          }
          abilityHealthCost={
            !isEquipment &&
            card.kind === "active" &&
            compact &&
            !traditional
              ? abilityHealthCost
              : null
          }
          augmentCostIncrease={
            !isEquipment && card.kind === "augment"
              ? augmentCostIncrease
              : null
          }
          augmentCostMult={
            !isEquipment && card.kind === "augment" ? augmentCostMult : null
          }
          compact={compact}
        />
      ) : null}
      <div className={styles.cardHeader}>
        {!hideCardName ? (
          <FittedCardName
            traditional={traditional}
            fitKey={nameFitKey}
            skipFit={skipFit}
            trailing={
              abilityLevel > 0 ? (
                <AbilityLevelIcons
                  level={abilityLevel}
                  className={`${styles.cardNameLevelIcons}${
                    compact ? ` ${styles.abilityLevelIconRowCompact}` : ""
                  }`}
                />
              ) : equipmentTier > 0 ? (
                <EquipmentTierIcons
                  tier={equipmentTier}
                  className={`${styles.cardNameLevelIcons}${
                    compact ? ` ${styles.abilityLevelIconRowCompact}` : ""
                  }`}
                />
              ) : null
            }
          >
            {card.name}
            {showCountInName ? ` ×${count}` : ""}
          </FittedCardName>
        ) : null}
      </div>
      {traditional && !hideChrome && abilitySlots.length > 0 ? (
        <div className={styles.cardTraditionalArtSlots}>
          <AbilitySlotsVisual
            slots={abilitySlots}
            variant="card"
            renderSlot={(slot) => (
              <span
                className={`${styles.cardSlotCircle} ${
                  slot.linked ? styles.cardSlotLinked : ""
                }`}
              />
            )}
          />
        </div>
      ) : null}
      {showCardBody ? (
        <div className={styles.cardBody}>
          <FittedCardBodyFooter
            traditional={traditional}
            fitKey={bodyFitKey}
            skipFit={skipFit}
          >
            {!traditional && abilitySlots.length > 0 ? (
              <div className={styles.cardBodySlots}>
                <AbilitySlotsVisual
                  slots={abilitySlots}
                  variant="card"
                  renderSlot={(slot) => (
                    <span
                      className={`${styles.cardSlotCircle} ${
                        slot.linked ? styles.cardSlotLinked : ""
                      }`}
                    />
                  )}
                />
              </div>
            ) : null}
            {!isEquipment &&
            (abilityFaceText ||
              (card.kind === "active" &&
                (abilityCosts.some(([, value]) => (Number(value) || 0) > 0) ||
                  abilityHealthCost > 0))) ? (
              <div className={styles.cardAbilityEffect}>
                {card.kind === "active" && abilityChoiceLines.length ? (
                  <>
                    {abilityChoiceLines.map((line, index) => (
                      <div
                        key={`choice-${index}`}
                        className={`${styles.cardExpendLine} ${styles.cardAbilityActivateCost}`}
                        data-card-fit-text=""
                      >
                        <ExpendActivateIcon />
                        {line.healthCost > 0 ? (
                          <HealthCostIcons amount={line.healthCost} />
                        ) : abilityHealthCost > 0 ? (
                          <span className={styles.cardAbilityActivateHealthCost}>
                            {abilityHealthCost}
                          </span>
                        ) : null}
                        <AttributeIconList
                          entries={line.cost}
                          className={styles.cardAbilityActivateCostIcons}
                        />
                        <span
                          className={styles.cardEffectArrow}
                          aria-hidden="true"
                        />
                        <span className={styles.cardEffectText}>
                          {renderTextWithStatusEffectIcons(line.text, {
                            outputDelta: line.outputDelta,
                            compareText: line.compareText,
                          })}
                        </span>
                      </div>
                    ))}
                    {(abilityFaceText ? abilityFaceText.split("\n").slice(1) : [])
                      .filter(Boolean)
                      .map((line, index) => (
                        <div
                          key={`${line}-${index}`}
                          className={styles.cardExpendLine}
                        >
                          <span
                            className={styles.cardEffectText}
                            data-card-fit-text=""
                          >
                            {renderTextWithStatusEffectIcons(line, {
                              shorthand: false,
                              compareText: compareAbilityFaceLines
                                ?.slice(1)
                                .filter(Boolean)[index],
                            })}
                          </span>
                        </div>
                      ))}
                  </>
                ) : card.kind === "active" ? (
                  <>
                    <div
                      className={`${styles.cardExpendLine} ${styles.cardAbilityActivateCost}`}
                      data-card-fit-text=""
                    >
                      <ExpendActivateIcon />
                      {abilityHealthCost > 0 ? (
                        <span className={styles.cardAbilityActivateHealthCost}>
                          {abilityHealthCost}
                        </span>
                      ) : null}
                      <AttributeIconList
                        entries={abilityCosts.filter(
                          ([, value]) => (Number(value) || 0) > 0,
                        )}
                        className={styles.cardAbilityActivateCostIcons}
                      />
                      <ChargeCostIcons
                        amount={getEffectConsumeChargeCost(card.effect)}
                      />
                      <span className={styles.cardEffectArrow} aria-hidden="true" />
                      {abilityFaceText ? (
                        <span className={styles.cardEffectText}>
                          {renderTextWithStatusEffectIcons(
                            abilityFaceText.split("\n")[0],
                            {
                              outputDelta: resolveAbilityFaceOutputDelta(card),
                              compareText: compareAbilityFaceLines?.[0],
                            },
                          )}
                        </span>
                      ) : null}
                    </div>
                    {(abilityFaceText ? abilityFaceText.split("\n").slice(1) : [])
                      .filter(Boolean)
                      .map((line, index) => (
                        <div
                          key={`${line}-${index}`}
                          className={styles.cardExpendLine}
                        >
                          <span
                            className={styles.cardEffectText}
                            data-card-fit-text=""
                          >
                            {renderTextWithStatusEffectIcons(line, {
                              shorthand: false,
                              compareText: compareAbilityFaceLines
                                ?.slice(1)
                                .filter(Boolean)[index],
                            })}
                          </span>
                        </div>
                      ))}
                  </>
                ) : (
                  (abilityFaceText ? abilityFaceText.split("\n") : []).map(
                    (line, index) => (
                      <div
                        key={`${line}-${index}`}
                        className={styles.cardExpendLine}
                      >
                        {line ? (
                          <span
                            className={styles.cardEffectText}
                            data-card-fit-text=""
                          >
                            {renderTextWithStatusEffectIcons(line, {
                              shorthand: false,
                              compareText: compareAbilityFaceLines?.[index],
                            })}
                          </span>
                        ) : null}
                      </div>
                    ),
                  )
                )}
                {summonMinionDisplay ? (
                  <div className={styles.cardMinionBlock}>
                    <div
                      className={styles.cardMinionHeader}
                      data-card-fit-text=""
                    >
                      <span className={styles.cardMinionName}>
                        {summonMinionDisplay.name}
                      </span>
                      <span className={styles.cardMinionHp}>
                        {summonMinionDisplay.hp} HP
                      </span>
                    </div>
                    {summonMinionDisplay.lines.map((line, index) => (
                      <div
                        key={`minion-${line.text}-${index}`}
                        className={styles.cardExpendLine}
                        data-card-fit-text=""
                      >
                        <ExpendActivateIcon />
                        {line.cost.length > 0 ? (
                          <AttributeIconList entries={line.cost} />
                        ) : null}
                        <span
                          className={styles.cardEffectArrow}
                          aria-hidden="true"
                        />
                        <span className={styles.cardEffectText}>
                          {renderTextWithStatusEffectIcons(line.text, {
                            outputDelta: line.outputDelta,
                          })}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
            {triggerDisplays.length > 0 ? (
              <div className={styles.cardOnEquipEffects}>
                {triggerDisplays.map((entry) => (
                  <div
                    key={entry.key}
                    className={styles.cardOnEquipLine}
                    data-card-fit-text=""
                  >
                    <KeywordBadge
                      keywordId={entry.keywordId}
                      label={entry.label}
                      className={styles.cardOnEquipBadge}
                      activeKeywordId={activeKeywordId}
                      onKeywordClick={onKeywordClick}
                    />
                    <span className={styles.cardEffectArrow} aria-hidden="true" />
                    <span className={styles.cardEffectText}>
                      {renderTextWithStatusEffectIcons(entry.text)}
                    </span>
                  </div>
                ))}
              </div>
            ) : null}
            {isEquipment && activatedEffectLines.length > 0 ? (
              <div className={styles.cardExpendEffects}>
                {activatedEffectLines.map((line, index) => (
                  <div
                    key={`effect-${line.text}-${index}`}
                    className={styles.cardExpendLine}
                    data-card-fit-text=""
                  >
                    {line.identity ? (
                      <IdentityModePrefix attribute={line.identity} />
                    ) : null}
                    {line.expend || line.cost.length === 0 ? (
                      <ExpendActivateIcon />
                    ) : null}
                    {line.cost.length > 0 ? (
                      <AttributeIconList entries={line.cost} />
                    ) : null}
                    <ChargeCostIcons amount={line.chargeCost} />
                    <DurabilityCostIcons amount={line.durabilityCost} />
                    <SelfDamageCostIcons entries={line.selfDamage} />
                    <span className={styles.cardEffectArrow} aria-hidden="true" />
                    <span className={styles.cardEffectText}>
                      {renderTextWithStatusEffectIcons(line.text, {
                        outputDelta: line.outputDelta || 0,
                      })}
                    </span>
                  </div>
                ))}
              </div>
            ) : null}
            {isEquipment &&
            (passiveDescriptionLines.length > 0 ||
              uniqueEquipmentTextClauses.length > 0) ? (
              <div className={styles.cardPassiveText}>
                {[
                  ...passiveDescriptionLines.map((line) =>
                    line.replace(/\.$/, ""),
                  ),
                  ...uniqueEquipmentTextClauses,
                ].map((line, index) => (
                  <div
                    key={`passive-${index}-${line}`}
                    className={styles.cardPassiveLine}
                    data-card-fit-text=""
                  >
                    {renderTextWithStatusEffectIcons(line)}
                  </div>
                ))}
              </div>
            ) : null}
          </FittedCardBodyFooter>
        </div>
      ) : null}
      {showCountBadge ? (
        <span className={styles.cardCountBadge} aria-hidden="true">
          ×{count}
        </span>
      ) : null}
      {showCardPowerBadge ? (
        <span
          className={styles.cardPowerBadge}
          aria-label={t("card.powerN", { power: cardPower })}
          title={t("card.powerN", { power: cardPower })}
        >
          <MaskIcon
            src={POWER_ICON_URL}
            className={styles.cardPowerBadgeIcon}
          />
          <span className={styles.cardPowerBadgeValue}>{cardPower}</span>
        </span>
      ) : null}
      {locked && showLockedIcon ? (
        <span
          className={styles.libraryDeckLockedIcon}
          aria-hidden="true"
          title={t("card.locked")}
        >
          <MaskIcon
            src={LOCKED_ICON_URL}
            className={styles.libraryDeckLockedIconGlyph}
          />
        </span>
      ) : null}
      {compact &&
      !traditional &&
      !hideChrome &&
      isEquipment &&
      abilitySlots.length > 0 ? (
        <div className={styles.cardHandEquipSlots}>
          <AbilitySlotsVisual
            slots={abilitySlots}
            variant="handBoard"
            renderSlot={() => (
              <span
                className={styles.abilitySlotCircle}
                aria-hidden="true"
              />
            )}
          />
        </div>
      ) : null}
      {!traditional && foilEffect ? (
        <FoilOverlay
          foilEffect={foilEffect}
          cardId={card.id}
          inheritPose={inheritFoilPose}
          quality={foilQuality}
          animateIdle={foilAnimateIdle}
        />
      ) : null}
    </div>
  );
}

function DeferredFoilCardFace({
  revealIndex = 0,
  foilQuality = null,
  foilAnimateIdle = false,
  ...props
}) {
  const ready = useDeferredFoilReveal(revealIndex);
  return (
    <CardFace
      {...props}
      foilQuality={ready ? foilQuality : "off"}
      foilAnimateIdle={ready && foilAnimateIdle}
    />
  );
}

const DENSE_GRID_FIRST_PAINT = 48;
const DENSE_GRID_PAINT_STEP = 72;

const DenseCardGrid = memo(function DenseCardGrid({
  cards,
  selectedCardId = null,
  collection = null,
  lockUnowned = false,
  onOpenCard,
  className = "",
}) {
  const [paintCount, setPaintCount] = useState(() =>
    (cards?.length || 0) <= DENSE_GRID_FIRST_PAINT ? cards?.length || 0 : 0,
  );
  const gridClassName = className
    ? `${styles.cardGrid} ${className}`
    : styles.cardGrid;
  const total = cards?.length || 0;
  const cardsKey = cards;

  useEffect(() => {
    const length = cardsKey?.length || 0;
    if (length <= DENSE_GRID_FIRST_PAINT) {
      setPaintCount(length);
      return undefined;
    }
    setPaintCount(0);
    const rafId = window.requestAnimationFrame(() => {
      setPaintCount(Math.min(DENSE_GRID_FIRST_PAINT, cardsKey?.length || 0));
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [cardsKey]);

  useEffect(() => {
    if (paintCount <= 0 || paintCount >= total) return undefined;
    const rafId = window.requestAnimationFrame(() => {
      setPaintCount((count) =>
        Math.min(count + DENSE_GRID_PAINT_STEP, total),
      );
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [paintCount, total]);

  if (paintCount <= 0) {
    return <div className={gridClassName} />;
  }

  const visibleCards =
    total > paintCount ? cards.slice(0, paintCount) : cards || [];

  return (
    <div className={gridClassName}>
      {visibleCards.map((card) => (
        <CardFace
          key={card.id}
          card={card}
          selected={selectedCardId === card.id}
          locked={lockUnowned ? !playerOwnsCard(collection, card.id) : false}
          fitText={false}
          onClick={onOpenCard ? () => onOpenCard(card.id) : undefined}
        />
      ))}
    </div>
  );
});

function formatAdventureNodePreview(preview) {
  if (!preview) return t("adventure.preview.unknown");
  if (preview.kind === "combat") return t("adventure.preview.combat");
  if (preview.kind === "shop") return t("adventure.preview.shop");
  if (preview.kind === "reward") return t("adventure.preview.reward");
  if (preview.kind === "story") return t("adventure.preview.story");
  return t("adventure.preview.unknown");
}

function adventureCombatSummaryActive(run) {
  if (!run) return false;
  return (
    run.phase === "combatSummary" ||
    (run.combatSummary && run.phase !== "hub")
  );
}

function AdventureMapPanel({
  run,
  onRequestTravel,
  deckReady = true,
  party = null,
  username = "",
  onVoteNode = null,
}) {
  const mapRef = useRef(null);
  const nodeRefs = useRef({});
  const [links, setLinks] = useState([]);
  const currentId = run?.map?.currentNodeId;
  const nodes = run?.map?.nodes || [];
  const current = nodes.find((node) => node.id === currentId);
  const currentRow = current?.row ?? 0;
  const colSpan = 5;
  const inParty = Boolean(party?.members?.length > 1);
  const isLeader = party?.leader === username;
  const pendingNodeId = party?.pendingNodeId || null;
  const nodeVotes = party?.nodeVotes || {};
  const rows = useMemo(() => {
    const maxRow = Math.max(
      currentRow,
      ...nodes.map((node) => node.row),
      currentRow,
    );
    const next = [];
    for (let row = currentRow; row <= maxRow; row += 1) {
      const rowNodes = nodes.filter((node) => node.row === row);
      if (rowNodes.length) next.push(rowNodes);
    }
    return next;
  }, [nodes, currentRow]);
  const selectable = useMemo(
    () => new Set(current?.childIds || []),
    [current?.childIds],
  );

  const updateLinks = useCallback(() => {
    const root = mapRef.current;
    if (!root) return;
    const rootRect = root.getBoundingClientRect();
    const visibleIds = new Set(rows.flatMap((rowNodes) => rowNodes.map((n) => n.id)));
    const nextLinks = [];
    nodes.forEach((node) => {
      if (!visibleIds.has(node.id)) return;
      (node.childIds || []).forEach((childId) => {
        if (!visibleIds.has(childId)) return;
        const fromEl = nodeRefs.current[node.id];
        const toEl = nodeRefs.current[childId];
        if (!fromEl || !toEl) return;
        const fromRect = fromEl.getBoundingClientRect();
        const toRect = toEl.getBoundingClientRect();
        nextLinks.push({
          key: `${node.id}-${childId}`,
          x1: fromRect.left + fromRect.width / 2 - rootRect.left,
          y1: fromRect.top + fromRect.height / 2 - rootRect.top,
          x2: toRect.left + toRect.width / 2 - rootRect.left,
          y2: toRect.top + toRect.height / 2 - rootRect.top,
          active: node.id === currentId && selectable.has(childId),
        });
      });
    });
    setLinks(nextLinks);
  }, [nodes, currentId, selectable, rows]);

  useLayoutEffect(() => {
    updateLinks();
    const root = mapRef.current;
    if (!root || typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateLinks);
      return () => window.removeEventListener("resize", updateLinks);
    }
    const observer = new ResizeObserver(() => updateLinks());
    observer.observe(root);
    window.addEventListener("resize", updateLinks);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateLinks);
    };
  }, [updateLinks, rows]);

  return (
    <div className={styles.adventureMap} ref={mapRef}>
      <svg className={styles.adventureMapLinks} aria-hidden="true">
        {links.map((link) => (
          <line
            key={link.key}
            x1={link.x1}
            y1={link.y1}
            x2={link.x2}
            y2={link.y2}
            className={
              link.active
                ? styles.adventureMapLinkActive
                : styles.adventureMapLink
            }
          />
        ))}
      </svg>
      {rows.map((rowNodes) => {
        const rowNum = rowNodes[0]?.row ?? 0;
        const byCol = {};
        rowNodes.forEach((node) => {
          byCol[node.col] = node;
        });
        return (
          <div key={`row-${rowNum}`} className={styles.adventureMapRow}>
            {Array.from({ length: colSpan }, (_, col) => {
              const node = byCol[col];
              if (!node) {
                return (
                  <div
                    key={`spacer-${rowNum}-${col}`}
                    className={styles.adventureMapNodeSpacer}
                    aria-hidden="true"
                  />
                );
              }
              const isCurrent = node.id === currentId;
              const canSelect =
                run?.phase === "hub" &&
                !pendingNodeId &&
                selectable.has(node.id) &&
                deckReady;
              const voters = Object.entries(nodeVotes)
                .filter(([, votedId]) => votedId === node.id)
                .map(([name]) => name);
              const nodeLabel = adventureNodeLabel(node);
              const label = isCurrent
                ? nodeLabel && nodeLabel !== "?"
                  ? nodeLabel
                  : t("match.view.you")
                : nodeLabel || "?";
              const nodeTypeClass = getAdventureMapNodeTypeClass(
                node.revealed ? node.type : "unknown",
              );
              return (
                <button
                  key={node.id}
                  type="button"
                  ref={(el) => {
                    if (el) nodeRefs.current[node.id] = el;
                    else delete nodeRefs.current[node.id];
                  }}
                  className={`${styles.adventureMapNode} ${nodeTypeClass} ${
                    isCurrent ? styles.adventureMapNodeCurrent : ""
                  } ${canSelect ? styles.adventureMapNodeSelectable : ""} ${
                    node.revealed ? "" : styles.adventureMapNodeHidden
                  } ${
                    pendingNodeId === node.id
                      ? styles.adventureMapNodePending
                      : ""
                  }`}
                  disabled={!canSelect}
                  onClick={() => {
                    if (!canSelect) return;
                    if (inParty && !isLeader) {
                      if (onVoteNode) onVoteNode(node.id);
                      return;
                    }
                    onRequestTravel(node);
                  }}
                >
                  <span className={styles.adventureMapNodeMark} aria-hidden="true" />
                  <span className={styles.adventureMapNodeLabel}>{label}</span>
                  {voters.length ? (
                    <span className={styles.adventureMapNodeVotes}>
                      {voters.map((name) => (
                        <span key={name} title={name}>
                          ✓
                        </span>
                      ))}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function requestAppFullscreen(el) {
  if (!el) return Promise.resolve();
  const req =
    el.requestFullscreen ||
    el.webkitRequestFullscreen ||
    el.webkitRequestFullScreen ||
    el.msRequestFullscreen;
  if (!req) return Promise.resolve();
  try {
    const result = req.call(el, { navigationUI: "hide" });
    return result && typeof result.then === "function" ? result : Promise.resolve();
  } catch (_) {
    try {
      const result = req.call(el);
      return result && typeof result.then === "function" ? result : Promise.resolve();
    } catch (_) {
      return Promise.resolve();
    }
  }
}

function exitAppFullscreen() {
  if (!document.fullscreenElement && !document.webkitFullscreenElement) {
    return Promise.resolve();
  }
  const exit = document.exitFullscreen || document.webkitExitFullscreen;
  if (!exit) return Promise.resolve();
  try {
    const result = exit.call(document);
    return result && typeof result.then === "function" ? result : Promise.resolve();
  } catch (_) {
    return Promise.resolve();
  }
}

function hideBrowserChrome() {
  window.scrollTo(0, 1);
  requestAnimationFrame(() => {
    window.scrollTo(0, 1);
  });
}

function enterImmersiveMode(shellEl) {
  hideBrowserChrome();
  return requestAppFullscreen(shellEl || document.documentElement).finally(() => {
    hideBrowserChrome();
  });
}

export default function AeternaInvicta() {
  const dispatch = useDispatch();
  const user = useSelector((store) => store.user);
  const username = user?.username || "Guest";
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const hasConnectedOnceRef = useRef(false);
  const shellRef = useRef(null);

  const [view, setView] = useState("menu");
  const [enteredGame, setEnteredGame] = useState(false);
  const [assetLoadProgress, setAssetLoadProgress] = useState(null);
  const [assetVersionsState, setAssetVersionsState] = useState(null);
  const [connected, setConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [player, setPlayer] = useState(null);
  const [storedLanguage, setStoredLanguage] = useState(loadStoredLanguage);
  const language = player?.settings?.language
    ? normalizeLanguage(player.settings.language)
    : storedLanguage;
  setActiveLanguage(language);
  const [match, setMatch] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [starterDecks, setStarterDecks] = useState([]);
  const [librarySection, setLibrarySection] = useState(null);
  const [libraryDeckId, setLibraryDeckId] = useState(null);
  const [libraryResetConfirm, setLibraryResetConfirm] = useState(false);
  const [glossarySearch, setGlossarySearch] = useState("");
  const [glossarySelectedId, setGlossarySelectedId] = useState(null);
  const keepCollectionViewRef = useRef(false);
  const keepGlossaryViewRef = useRef(false);
  const [queued, setQueued] = useState(false);
  const [queueOpponent, setQueueOpponent] = useState("cpu");
  const [playersInQueue, setPlayersInQueue] = useState(0);
  const [queueElapsedSec, setQueueElapsedSec] = useState(0);
  const queueEnteredAtRef = useRef(null);
  const [matchIntro, setMatchIntro] = useState(null);
  const matchIntroSeenRef = useRef(new Set());
  const [portraitPreview, setPortraitPreview] = useState(null);
  const [discardViewer, setDiscardViewer] = useState(null);
  useEffect(() => {
    if (!portraitPreview) setDiscardViewer(null);
  }, [portraitPreview]);
  const [draftQueued, setDraftQueued] = useState(false);
  const [tutorials, setTutorials] = useState([]);
  const [tutorialHintMinimized, setTutorialHintMinimized] = useState(false);
  const [matchGuideMinimized, setMatchGuideMinimized] = useState(
    loadMatchGuideMinimized,
  );
  const [matchGuideIdle, setMatchGuideIdle] = useState(false);
  const [queueDeckPreviewOpen, setQueueDeckPreviewOpen] = useState(false);
  const [draftedCardsOpen, setDraftedCardsOpen] = useState(false);
  const [draftForfeitConfirm, setDraftForfeitConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const [catalogCategory, setCatalogCategory] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterAttr, setFilterAttr] = useState("all");
  const [catalogSearch, setCatalogSearch] = useState("");
  const [catalogSlot, setCatalogSlot] = useState("all");
  const [catalogItemType, setCatalogItemType] = useState("all");
  const [catalogTier, setCatalogTier] = useState("all");
  const [catalogKind, setCatalogKind] = useState("all");
  const [catalogEffect, setCatalogEffect] = useState("all");
  const [catalogCost, setCatalogCost] = useState("all");
  const [catalogHolo, setCatalogHolo] = useState("all");
  const [catalogOwned, setCatalogOwned] = useState("all");
  const [catalogSort, setCatalogSort] = useState("name");
  const [catalogFiltersOpen, setCatalogFiltersOpen] = useState(false);
  const [catalogPageSize, setCatalogPageSize] = useState(9);
  const [catalogPage, setCatalogPage] = useState(0);
  const catalogPagerTopRef = useRef(null);
  const [draftEquipment, setDraftEquipment] = useState(() =>
    padDeckSlots([]),
  );
  const [draftAbility, setDraftAbility] = useState(() => padDeckSlots([]));
  const [draftAspect, setDraftAspect] = useState(null);
  const [draftAspectFoil, setDraftAspectFoil] = useState(null);
  const [draftEquipmentFoils, setDraftEquipmentFoils] = useState(() =>
    padFoilSlots([]),
  );
  const [draftAbilityFoils, setDraftAbilityFoils] = useState(() =>
    padFoilSlots([]),
  );
  const [draftName, setDraftName] = useState("Starter");
  const [editingDeckId, setEditingDeckId] = useState(null);
  const [unsavedNewDeck, setUnsavedNewDeck] = useState(false);
  const [deckShare, setDeckShare] = useState(null);
  const [deckAnalyzeOpen, setDeckAnalyzeOpen] = useState(false);
  const [deckRecommend, setDeckRecommend] = useState(null);
  const [deckRecommendHintOpen, setDeckRecommendHintOpen] = useState(false);
  const deckRecommendButtonRef = useRef(null);
  const deckRecommendPanelRef = useRef(null);
  const deckRecommendTimerRef = useRef(null);
  const deckRecommendRequestRef = useRef(0);
  const deckRecommendEscapeRef = useRef(null);
  const deckShareTextareaRef = useRef(null);
  const [nameInputFocused, setNameInputFocused] = useState(false);
  const nameInputFocusedRef = useRef(false);
  const [slotPicker, setSlotPicker] = useState(null);
  const [deckVariantPicker, setDeckVariantPicker] = useState(null);
  const [workshopPicker, setWorkshopPicker] = useState(false);
  const [workshopPickerKind, setWorkshopPickerKind] = useState("all");
  const [workshopControlsHidden, setWorkshopControlsHidden] = useState(false);
  const workshopActingAsRef = useRef(null);
  const isWorkshopMatchRef = useRef(false);
  const [examCardId, setExamCardId] = useState(null);
  const [examFoilEffect, setExamFoilEffect] = useState(null);
  const openCardExam = useCallback((cardId, foilEffect = null) => {
    if (!cardId) return;
    playSfx("uiSwitch");
    setAspectExamTraitFocusId(null);
    setExamFoilEffect(foilEffect || null);
    setExamCardId(cardId);
  }, []);
  const closeCardExam = () => {
    setExamCardId(null);
    setExamFoilEffect(null);
    setAspectExamTraitFocusId(null);
  };
  const [examArtPreview, setExamArtPreview] = useState(null);
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerAttr, setPickerAttr] = useState("all");
  const [pickerSlot, setPickerSlot] = useState("all");
  const [pickerItemType, setPickerItemType] = useState("all");
  const [pickerTier, setPickerTier] = useState("all");
  const [pickerKind, setPickerKind] = useState("all");
  const [pickerType, setPickerType] = useState("all");
  const [pickerEffect, setPickerEffect] = useState("all");
  const [pickerCost, setPickerCost] = useState("all");
  const [pickerHolo, setPickerHolo] = useState("all");
  const [pickerSort, setPickerSort] = useState("name");
  const [pickerSuggest, setPickerSuggest] = useState(false);
  const [pickerFiltersOpen, setPickerFiltersOpen] = useState(false);
  const [selectedHand, setSelectedHand] = useState(null);
  const [movingAbility, setMovingAbility] = useState(null);
  const [adventureTab, setAdventureTab] = useState("map");
  const [adventureEquipment, setAdventureEquipment] = useState(() =>
    padDeckSlots([]),
  );
  const [adventureAbility, setAdventureAbility] = useState(() =>
    padDeckSlots([]),
  );
  const [adventureAspect, setAdventureAspect] = useState(DEFAULT_ASPECT_ID);
  const [adventureResetConfirm, setAdventureResetConfirm] = useState(false);
  const [logoutConfirm, setLogoutConfirm] = useState(false);
  const [customizePurchaseArmed, setCustomizePurchaseArmed] = useState(null);
  const [settingsSectionId, setSettingsSectionId] = useState(null);
  useEffect(() => {
    if (view !== "settings") setSettingsSectionId(null);
  }, [view]);
  useEffect(() => {
    if (!customizePurchaseArmed) return undefined;
    const timer = window.setTimeout(
      () => setCustomizePurchaseArmed(null),
      CUSTOMIZE_PURCHASE_CONFIRM_MS,
    );
    return () => window.clearTimeout(timer);
  }, [customizePurchaseArmed]);
  const [migrateOpen, setMigrateOpen] = useState(false);
  const [migrateUsername, setMigrateUsername] = useState("");
  const [migratePassword, setMigratePassword] = useState("");
  const [migrateError, setMigrateError] = useState("");
  const [migrateBusy, setMigrateBusy] = useState(false);
  const [accountPanel, setAccountPanel] = useState(null);
  const [accountPassword, setAccountPassword] = useState("");
  const [accountNewPassword, setAccountNewPassword] = useState("");
  const [accountConfirmPassword, setAccountConfirmPassword] = useState("");
  const [accountDeleteConfirm, setAccountDeleteConfirm] = useState("");
  const [accountError, setAccountError] = useState("");
  const [accountBusy, setAccountBusy] = useState(false);
  const loggingOutRef = useRef(false);
  const [adventureTravelConfirm, setAdventureTravelConfirm] = useState(null);
  const [marketplaceBuyConfirm, setMarketplaceBuyConfirm] = useState(null);
  const [dailyActionArmed, setDailyActionArmed] = useState(null);
  const [achievementsModal, setAchievementsModal] = useState(null);
  const [dailiesNow, setDailiesNow] = useState(() => Date.now());
  const dailiesSyncDeadlineRef = useRef(null);
  const [adventureDiscardIds, setAdventureDiscardIds] = useState([]);
  const [adventureTreasureTakeIds, setAdventureTreasureTakeIds] = useState([]);
  const [adventureCombatRewardTakeIds, setAdventureCombatRewardTakeIds] =
    useState([]);
  const [adventureInventorySelection, setAdventureInventorySelection] =
    useState(null);
  const [adventureMonsterExamId, setAdventureMonsterExamId] = useState(null);
  const [adventureSummaryEnemyExamId, setAdventureSummaryEnemyExamId] =
    useState(null);
  const [adventureEffectsOpen, setAdventureEffectsOpen] = useState(false);
  const [adventureParty, setAdventureParty] = useState(null);
  const [adventurePartyOpen, setAdventurePartyOpen] = useState(false);
  const [incomingPartyInvite, setIncomingPartyInvite] = useState(null);
  const [adventureBoardViewIndex, setAdventureBoardViewIndex] = useState(0);
  const [handMenu, setHandMenu] = useState(null);
  const [boardMenu, setBoardMenu] = useState(null);
  const [raisedHandInstanceId, setRaisedHandInstanceId] = useState(null);
  const [selectedEquipmentId, setSelectedEquipmentId] = useState(null);
  const [slottedAbilityExam, setSlottedAbilityExam] = useState(null);
  const [equipExpendConfirm, setEquipExpendConfirm] = useState(null);
  const [equipExpendConfirmPopupStyle, setEquipExpendConfirmPopupStyle] =
    useState(null);
  const equipExpendConfirmPopupRef = useRef(null);
  const [equipCounterExam, setEquipCounterExam] = useState(null);
  const [equipCounterExamPopupStyle, setEquipCounterExamPopupStyle] =
    useState(null);
  const equipCounterExamPopupRef = useRef(null);
  const [summaryDeckView, setSummaryDeckView] = useState(null);
  const [keywordTip, setKeywordTip] = useState(null);
  const [activeGameHint, setActiveGameHint] = useState(null);
  const [forfeitConfirm, setForfeitConfirm] = useState(false);
  const [loseConfirm, setLoseConfirm] = useState(null);
  const [contactFormOpen, setContactFormOpen] = useState(false);
  const [contactKind, setContactKind] = useState("support");
  const [contactEmail, setContactEmail] = useState("");
  const [contactMessage, setContactMessage] = useState("");
  const [contactSending, setContactSending] = useState(false);
  const [contactError, setContactError] = useState(null);
  const [contactConfirmation, setContactConfirmation] = useState(null);
  const [privacyPolicyOpen, setPrivacyPolicyOpen] = useState(false);
  const [privacyPolicyHtml, setPrivacyPolicyHtml] = useState("");
  const [privacyPolicyLoading, setPrivacyPolicyLoading] = useState(false);
  const [privacyPolicyError, setPrivacyPolicyError] = useState(null);
  const [monetizationInfoOpen, setMonetizationInfoOpen] = useState(false);
  const [designPhilosophyOpen, setDesignPhilosophyOpen] = useState(false);
  const [creditsOpen, setCreditsOpen] = useState(false);
  const [supportInfoId, setSupportInfoId] = useState(null);
  const [loreEntryId, setLoreEntryId] = useState(null);
  const [chatOptInOpen, setChatOptInOpen] = useState(false);
  const [adRewardPromptOpen, setAdRewardPromptOpen] = useState(false);
  const [adRewardBusy, setAdRewardBusy] = useState(false);
  const [resetSavesConfirm, setResetSavesConfirm] = useState(false);
  const [deleteDeckConfirm, setDeleteDeckConfirm] = useState(false);
  const [socialPlayers, setSocialPlayers] = useState([]);
  const [socialMessages, setSocialMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [socialProfile, setSocialProfile] = useState(null);
  const [socialMenuPlayer, setSocialMenuPlayer] = useState(null);
  const [socialDeckView, setSocialDeckView] = useState(null);
  const [reportTarget, setReportTarget] = useState(null);
  const [reportCategory, setReportCategory] = useState("");
  const [reportDetails, setReportDetails] = useState("");
  const [reportBusy, setReportBusy] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [adminPlayerReports, setAdminPlayerReports] = useState([]);
  const [adminReportFilter, setAdminReportFilter] = useState("open");
  const [incomingChallenge, setIncomingChallenge] = useState(null);
  const [challengeForfeitPrompt, setChallengeForfeitPrompt] = useState(null);
  const [rankingSortBy, setRankingSortBy] = useState("cpu");
  const [rankingPage, setRankingPage] = useState(0);
  const [rankingEntries, setRankingEntries] = useState([]);
  const [rankingTotalPages, setRankingTotalPages] = useState(1);
  const [rankingTotal, setRankingTotal] = useState(0);
  const rankingSortByRef = useRef("cpu");
  const rankingPageRef = useRef(0);
  const chatListRef = useRef(null);
  const chatStickBottomRef = useRef(true);
  const [completedGameSummaries, setCompletedGameSummaries] = useState([]);
  const [adminGame, setAdminGame] = useState(null);
  const [adminPreviewGame, setAdminPreviewGame] = useState(null);
  const [adminReplayIndex, setAdminReplayIndex] = useState(0);
  const [adminPlayerDecks, setAdminPlayerDecks] = useState([]);
  const [adminPlayerBots, setAdminPlayerBots] = useState({
    enabled: false,
    bots: [],
    limits: null,
  });
  const [adminPlayerBotAnalysis, setAdminPlayerBotAnalysis] = useState(null);
  const [simDeckA, setSimDeckA] = useState("");
  const [simDeckB, setSimDeckB] = useState("");
  const [simCpuADifficulty, setSimCpuADifficulty] = useState("normal");
  const [simCpuAPersonality, setSimCpuAPersonality] = useState("balanced");
  const [simCpuBDifficulty, setSimCpuBDifficulty] = useState("normal");
  const [simCpuBPersonality, setSimCpuBPersonality] = useState("balanced");
  const [simRunning, setSimRunning] = useState(false);
  const [adminPlayerTarget, setAdminPlayerTarget] = useState("");
  const [adminPlayerSort, setAdminPlayerSort] = useState(null);
  const [adminHidePlayerBots, setAdminHidePlayerBots] = useState(false);
  const [adminUnlockCardId, setAdminUnlockCardId] = useState("");
  const [adminUnlockFoilEffect, setAdminUnlockFoilEffect] = useState("");
  const [adminCurrencyAmount, setAdminCurrencyAmount] = useState("10");
  const [adminWipeConfirm, setAdminWipeConfirm] = useState(false);
  const [adminResetDecksConfirm, setAdminResetDecksConfirm] = useState(false);
  const [adminCardCatalog, setAdminCardCatalog] = useState(null);
  const [adminSubView, setAdminSubView] = useState(null);
  const [adminTrackingNow, setAdminTrackingNow] = useState(() => Date.now());
  const isReplayViewRef = useRef(false);
  const isSpectateViewRef = useRef(false);
  const viewRef = useRef("menu");
  const [opponentBoardExpanded, setOpponentBoardExpanded] = useState(false);
  const [handDrawerOpen, setHandDrawerOpenState] = useState(() => {
    try {
      return window.localStorage.getItem(HAND_DRAWER_STORAGE_KEY) !== "closed";
    } catch (error) {
      return true;
    }
  });
  const [socialPlayersCollapsed, setSocialPlayersCollapsedState] = useState(
    () => {
      try {
        return window.localStorage.getItem(SOCIAL_PLAYERS_STORAGE_KEY) === "1";
      } catch (error) {
        return false;
      }
    },
  );
  const toggleSocialPlayersCollapsed = () => {
    setSocialPlayersCollapsedState((collapsed) => {
      const next = !collapsed;
      try {
        window.localStorage.setItem(SOCIAL_PLAYERS_STORAGE_KEY, next ? "1" : "0");
      } catch (error) {}
      return next;
    });
  };
  const setHandDrawerOpen = useCallback((open) => {
    setHandDrawerOpenState(open);
    try {
      window.localStorage.setItem(
        HAND_DRAWER_STORAGE_KEY,
        open ? "open" : "closed",
      );
    } catch (error) {}
  }, []);
  const [sideNavExpanded, setSideNavExpanded] = useState(() => {
    if (typeof window === "undefined") return true;
    return !window.matchMedia("(max-width: 800px)").matches;
  });
  const [paymentPrompt, setPaymentPrompt] = useState(null);
  const [paymentDraft, setPaymentDraft] = useState({
    strength: 0,
    dexterity: 0,
    wisdom: 0,
    neutral: 0,
  });
  const [chargeResourceDraft, setChargeResourceDraft] = useState({
    strength: 0,
    dexterity: 0,
    wisdom: 0,
  });
  const [traitChoiceBoardPeek, setTraitChoiceBoardPeek] = useState(false);
  const [identityChoiceBoardPeek, setIdentityChoiceBoardPeek] = useState(false);
  const [traitLevelPromptCollapsed, setTraitLevelPromptCollapsed] =
    useState(false);
  const [traitTreeFocusId, setTraitTreeFocusId] = useState(null);
  const [aspectExamTraitFocusId, setAspectExamTraitFocusId] = useState(null);
  const [levelUpOverlay, setLevelUpOverlay] = useState(null);
  const [readyEndTurnLocked, setReadyEndTurnLocked] = useState(false);
  const readyEndTurnLockTimerRef = useRef(null);
  const previousMatchPhaseRef = useRef(null);
  const [traitChoiceModalReady, setTraitChoiceModalReady] = useState(false);
  const traitChoiceDelayTimerRef = useRef(null);
  const [cancelAbilityConfirm, setCancelAbilityConfirm] = useState(false);
  const [combatLogOpen, setCombatLogOpen] = useState(false);
  const [gameLogExportBusy, setGameLogExportBusy] = useState(false);
  const [patchNotesOpen, setPatchNotesOpen] = useState(false);
  // version -> index of the next Discord part to copy (admin export).
  const [patchNotesCopyPart, setPatchNotesCopyPart] = useState({});
  const [xpQuestsOpen, setXpQuestsOpen] = useState(false);
  const [xpQuestsBottomInset, setXpQuestsBottomInset] = useState(0);
  const matchBottomDockRef = useRef(null);
  const matchViewRef = useRef(null);
  const combatLogListRef = useRef(null);
  const combatLogStickTopRef = useRef(true);
  const toastTimerRef = useRef(null);
  const toastQueueRef = useRef([]);
  const toastActiveRef = useRef(false);
  const toastCurrentEntryRef = useRef(null);
  const toastIntroHoldRef = useRef(false);
  const toastIntroHoldTimerRef = useRef(null);
  const toastSeqRef = useRef(0);
  const toastExitingRef = useRef(false);
  const toastCardRef = useRef(null);
  const toastStackRef = useRef(null);
  const toastsModeRef = useRef("all");
  const toastSpeedRef = useRef("standard");
  const [completionToast, setCompletionToast] = useState(null);
  const completionToastQueueRef = useRef([]);
  const completionToastTimerRef = useRef(null);
  const completionToastSeqRef = useRef(0);
  const [serverMessages, setServerMessages] = useState([]);
  const [serverMaintenance, setServerMaintenance] = useState(null);
  const [serverMessagesUnread, setServerMessagesUnread] = useState(false);
  const [serverMessagesOpen, setServerMessagesOpen] = useState(false);
  const [serverToast, setServerToast] = useState(null);
  const serverToastTimerRef = useRef(null);
  const serverToastedIdsRef = useRef(new Set());
  const [adminServerTemplates, setAdminServerTemplates] = useState([]);
  const [adminCountdownOptions, setAdminCountdownOptions] = useState([]);
  const [adminServerMessageMax, setAdminServerMessageMax] = useState(280);
  const [adminMaintenanceNoteMax, setAdminMaintenanceNoteMax] = useState(160);
  const [adminServerMessageText, setAdminServerMessageText] = useState("");
  const [adminMaintenanceMinutes, setAdminMaintenanceMinutes] = useState(60);
  const [adminMaintenanceNote, setAdminMaintenanceNote] = useState("");
  const [adminMaintenanceConfirm, setAdminMaintenanceConfirm] = useState(false);
  const actionBarResourcesRef = useRef(null);
  const lastTrackedResourcesRef = useRef(null);
  const lastTrackedResourcesMatchIdRef = useRef(null);
  const lastTrackedConversionIdRef = useRef(0);
  const resourceConvertSeqRef = useRef(0);
  const [resourceConvertPulses, setResourceConvertPulses] = useState({});
  const resourceFlyerSeqRef = useRef(0);
  const resourceFlyerRetryTimerRef = useRef(null);
  const resourceFlyerSpawnQueueRef = useRef([]);
  const [resourceFlyers, setResourceFlyers] = useState([]);
  const [pendingResourceReveal, setPendingResourceReveal] = useState(
    emptyPendingResourceReveal,
  );
  const [resourceIconPops, setResourceIconPops] = useState({});
  const pendingResourceRevealRef = useRef(pendingResourceReveal);
  pendingResourceRevealRef.current = pendingResourceReveal;
  const prevMatchPhaseRef = useRef(null);
  const postCombatToastHoldTimerRef = useRef(null);
  const postCombatToastHoldActiveRef = useRef(false);
  const postCombatToastHoldTargetRef = useRef(null);
  const lastLiveMatchRef = useRef(null);
  const [postCombatToastHold, setPostCombatToastHold] = useState(false);
  const wasMyTurnRef = useRef(false);
  const wasOpponentTurnRef = useRef(false);
  const suppressOpponentAutoViewRef = useRef(false);
  const [turnGlowId, setTurnGlowId] = useState(0);
  const [actionFlash, setActionFlash] = useState(null);
  const [vitalsFloaterDisplay, setVitalsFloaterDisplay] = useState([]);
  const graphicsTierRef = useRef("high");
  const combatProjectileStore = useDisplayListStore();
  const combatHitFlashStore = useDisplayListStore();
  const setCombatProjectileDisplay = combatProjectileStore.set;
  const setCombatHitFlashDisplay = combatHitFlashStore.set;
  const [cardPlayAnims, setCardPlayAnims] = useState([]);
  const [cardPlayPendingIds, setCardPlayPendingIds] = useState(() => new Set());
  const [cardFocusHost, setCardFocusHost] = useState(null);
  const [cardFocusGhosts, setCardFocusGhosts] = useState([]);
  const cardFocusGhostSeqRef = useRef(0);
  const [emotePanelOpen, setEmotePanelOpen] = useState(false);
  const [hideMatchEmotes, setHideMatchEmotes] = useState(false);
  const [matchEmoteDisplay, setMatchEmoteDisplay] = useState([]);
  const [vitalsHitFlash, setVitalsHitFlash] = useState({
    player: 0,
    opponent: 0,
  });
  const [visualVitalsByUser, setVisualVitalsByUser] = useState({});
  const pendingActionFlashRef = useRef(null);
  const actionFlashTimerRef = useRef(null);
  const lastVitalsFloaterIdRef = useRef(0);
  const vitalsFloaterTimersRef = useRef(new Set());
  const vitalsFloaterQueueRef = useRef([]);
  const vitalsFloaterPumpActiveRef = useRef(false);
  const vitalsFloaterPumpTimerRef = useRef(null);
  const lastCombatProjectileIdRef = useRef(0);
  const combatProjectileTimersRef = useRef(new Set());
  const combatProjectileCleanupsRef = useRef(new Set());
  const combatEchoGhostsRef = useRef(new Map());
  const combatProjectileQueueRef = useRef([]);
  const combatProjectilePumpActiveRef = useRef(false);
  const combatProjectilePumpTimerRef = useRef(null);
  const boardPiecesSnapshotRef = useRef(null);
  const cardPlaySeqRef = useRef(0);
  const cardPlayTimersRef = useRef(new Set());
  const cardPlayMatchIdRef = useRef(null);
  const matchEmoteSeqRef = useRef(0);
  const matchEmoteTimersRef = useRef(new Set());
  const lastMatchEmoteSentAtRef = useRef(0);
  const hideMatchEmotesRef = useRef(false);
  const blockedUsernamesRef = useRef(new Set());
  const vitalsHitFlashTimersRef = useRef({ player: null, opponent: null });
  const pendingCombatImpactCountRef = useRef({});
  const pendingCombatSfxRef = useRef([]);
  const playActionFlashRef = useRef(() => {});
  const tutorialSummaryTimerRef = useRef(null);
  const tutorialAutoFinishHandledRef = useRef(false);

  const catalogMap = useMemo(
    () => Object.fromEntries((catalog || []).map((card) => [card.id, card])),
    [catalog],
  );
  const marketplacePrewarmEntries = useMemo(() => {
    const market = player?.marketplace;
    if (!market) return [];
    return [
      ...(Array.isArray(market.foils) ? market.foils : []),
      ...(Array.isArray(market.cards) ? market.cards : []),
    ]
      .filter((entry) => entry && !entry.purchased && catalogMap[entry.cardId])
      .map((entry) => {
        const card = catalogMap[entry.cardId];
        const foilEffect = entry.foilEffect || null;
        const stampFile = getFoilStampIconFile(resolveFoilStampId(foilEffect));
        return {
          cardId: card.id,
          foilEffect,
          imageUrls: [
            getCardImageUrl(card),
            stampFile ? `${AETERNA_ASSET_ROOT}/icons/${stampFile}` : null,
          ],
        };
      });
  }, [player?.marketplace, catalogMap]);
  const marketplacePrewarmKey = marketplacePrewarmEntries
    .map((entry) => `${entry.cardId}:${entry.foilEffect || ""}`)
    .join("|");
  const marketplacePrewarmEntriesRef = useRef(marketplacePrewarmEntries);
  marketplacePrewarmEntriesRef.current = marketplacePrewarmEntries;
  useEffect(() => {
    if (!marketplacePrewarmKey) return undefined;
    return prewarmFoilAssets(marketplacePrewarmEntriesRef.current);
  }, [marketplacePrewarmKey]);
  const deckRecommendOpen = Boolean(deckRecommend);
  const deckRecommendCatalogKey = useMemo(
    () => (deckRecommendOpen ? catalogFingerprint(catalog) : ""),
    [catalog, deckRecommendOpen],
  );
  const deckRecommendOwnershipKey = useMemo(
    () => (deckRecommendOpen ? ownershipFingerprint(player?.collection) : ""),
    [player?.collection, deckRecommendOpen],
  );
  useEffect(() => {
    if (view === "deck") return;
    deckRecommendRequestRef.current += 1;
    setDeckRecommend(null);
  }, [view]);
  useEffect(() => {
    if (!deckRecommendOpen) return undefined;
    const trigger = document.activeElement;
    const focusFrame = window.requestAnimationFrame(() => {
      deckRecommendPanelRef.current?.focus({ preventScroll: true });
    });
    const handleKeyDown = (event) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      event.preventDefault();
      deckRecommendEscapeRef.current?.();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", handleKeyDown);
      window.requestAnimationFrame(() => {
        if (trigger && trigger.isConnected && trigger.focus) {
          trigger.focus({ preventScroll: true });
        }
      });
    };
  }, [deckRecommendOpen]);
  const cardNameMatcher = useMemo(
    () => buildCardNameMatcher(catalog),
    [catalog],
  );
  const catalogMapRef = useRef(catalogMap);
  catalogMapRef.current = catalogMap;
  const matchRef = useRef(match);
  matchRef.current = match;

  const clearToastTimers = () => {
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
  };

  const advanceToastQueue = () => {
    clearToastTimers();
    toastExitingRef.current = false;
    if (toastIntroHoldRef.current) {
      toastActiveRef.current = false;
      toastCurrentEntryRef.current = null;
      setToast(null);
      return;
    }
    const next = toastQueueRef.current.shift();
    if (!next) {
      toastActiveRef.current = false;
      toastCurrentEntryRef.current = null;
      setToast(null);
      return;
    }
    const entry =
      typeof next === "string"
        ? { message: next, cardId: null, foilEffect: null }
        : next;
    toastCurrentEntryRef.current = entry;
    const card = entry.cardId
      ? catalogMapRef.current[entry.cardId] || null
      : null;
    const imageUrl = getCardImageUrl(card);
    const foilEffect =
      entry.foilEffect ||
      findMatchFoilEffect(matchRef.current, entry.cardId) ||
      null;
    const borderless = isBorderlessFoil(foilEffect);
    const traditional = isTraditionalFoil(foilEffect);
    const frameUrl =
      borderless || traditional ? null : getCardFrameUrl(card);
    const hybridOutline =
      !frameUrl && !borderless
        ? getHybridOutlineProps(
            card,
            traditional ? TRADITIONAL_ATTRIBUTE_COLOR : ATTRIBUTE_COLOR_VAR,
          )
        : { className: "", style: undefined };
    toastActiveRef.current = true;
    toastSeqRef.current += 1;
    setToast({
      id: toastSeqRef.current,
      message: entry.message,
      cardId: entry.cardId || null,
      card,
      imageUrl,
      frameUrl,
      borderless,
      traditional,
      hybridClass: hybridOutline.className || "",
      hybridStyle: hybridOutline.style,
      foilEffect,
      artFailed: false,
      exiting: false,
    });
    const holdMs = toastSpeedRef.current === "fast" ? 250 : 500;
    toastTimerRef.current = window.setTimeout(() => {
      finishCurrentToast(500);
    }, holdMs);
  };

  const finishCurrentToast = (exitMs = 500) => {
    if (toastExitingRef.current) return;
    toastExitingRef.current = true;
    clearToastTimers();
    setToast((current) =>
      current ? { ...current, exiting: true } : null,
    );
    toastTimerRef.current = window.setTimeout(() => {
      setToast(null);
      toastTimerRef.current = window.setTimeout(() => {
        toastTimerRef.current = null;
        advanceToastQueue();
      }, 50);
    }, exitMs);
  };

  const dismissToast = () => {
    if (!toastActiveRef.current) return;
    finishCurrentToast(180);
  };

  const dismissToastRef = useRef(dismissToast);
  dismissToastRef.current = dismissToast;

  const advanceCompletionToast = () => {
    const next = completionToastQueueRef.current.shift();
    if (!next) {
      completionToastTimerRef.current = null;
      setCompletionToast(null);
      return;
    }
    completionToastSeqRef.current += 1;
    const id = completionToastSeqRef.current;
    setCompletionToast({ ...next, id, exiting: false });
    completionToastTimerRef.current = window.setTimeout(() => {
      setCompletionToast((current) =>
        current?.id === id ? { ...current, exiting: true } : current,
      );
      completionToastTimerRef.current = window.setTimeout(
        advanceCompletionToast,
        360,
      );
    }, 3200);
  };

  const showCompletionToast = (category, name) => {
    if (!name || toastsModeRef.current === "off") return;
    completionToastQueueRef.current.push({
      category: category === "achievement" ? "achievement" : "daily",
      name: String(name),
    });
    if (!completionToastTimerRef.current && !toastIntroHoldRef.current) {
      advanceCompletionToast();
    }
  };

  const clearServerToastTimer = () => {
    if (serverToastTimerRef.current) {
      window.clearTimeout(serverToastTimerRef.current);
      serverToastTimerRef.current = null;
    }
  };

  const hideServerToast = (exitMs = 360) => {
    clearServerToastTimer();
    setServerToast((current) =>
      current ? { ...current, exiting: true } : current,
    );
    serverToastTimerRef.current = window.setTimeout(() => {
      serverToastTimerRef.current = null;
      setServerToast(null);
    }, exitMs);
  };

  const showServerToast = (message) => {
    if (!message?.id || serverToastedIdsRef.current.has(message.id)) return;
    serverToastedIdsRef.current.add(message.id);
    clearServerToastTimer();
    setServerToast({ ...message, exiting: false });
    serverToastTimerRef.current = window.setTimeout(
      () => hideServerToast(),
      SERVER_TOAST_MS,
    );
  };

  const openServerMessages = () => {
    hideServerToast(180);
    setServerMessagesOpen(true);
  };

  const applyServerMessagesInit = (data) => {
    const list = (Array.isArray(data.messages) ? data.messages : [])
      .map(normalizeServerMessage)
      .filter(Boolean);
    setServerMessages(list);
    setServerMaintenance(normalizeServerMaintenance(data.maintenance));
    const seenAt = loadServerMessagesSeenAt();
    const unread = list.filter((entry) => entry.at > seenAt);
    setServerMessagesUnread(unread.length > 0);
    if (unread.length) showServerToast(unread[unread.length - 1]);
  };

  const applyServerMessage = (data) => {
    const message = normalizeServerMessage(data.message);
    setServerMaintenance(normalizeServerMaintenance(data.maintenance));
    if (!message) return;
    setServerMessages((current) =>
      [...current.filter((entry) => entry.id !== message.id), message].slice(
        -10,
      ),
    );
    setServerMessagesUnread(true);
    showServerToast(message);
  };

  const releaseToastIntroHold = () => {
    if (toastIntroHoldTimerRef.current) {
      window.clearTimeout(toastIntroHoldTimerRef.current);
      toastIntroHoldTimerRef.current = null;
    }
    if (!toastIntroHoldRef.current) return;
    toastIntroHoldRef.current = false;
    if (!toastActiveRef.current && toastQueueRef.current.length) {
      advanceToastQueue();
    }
    if (
      !completionToastTimerRef.current &&
      completionToastQueueRef.current.length
    ) {
      advanceCompletionToast();
    }
  };

  const holdToastsForIntro = () => {
    toastIntroHoldRef.current = true;
    if (toastActiveRef.current) {
      clearToastTimers();
      if (toastCurrentEntryRef.current) {
        toastQueueRef.current.unshift(toastCurrentEntryRef.current);
      }
      toastCurrentEntryRef.current = null;
      toastActiveRef.current = false;
      toastExitingRef.current = false;
      setToast(null);
    }
    if (toastIntroHoldTimerRef.current) {
      window.clearTimeout(toastIntroHoldTimerRef.current);
    }
    toastIntroHoldTimerRef.current = window.setTimeout(
      releaseToastIntroHold,
      MATCH_INTRO_MS + 1000,
    );
  };

  const showToast = (message, cardId = null, foilEffect = null) => {
    if (!message) return;
    const mode = toastsModeRef.current;
    if (mode === "off") return;
    const resolvedCardId = cardId || null;
    if (mode === "card" && !resolvedCardId) return;
    toastQueueRef.current.push({
      message,
      cardId: mode === "text" ? null : resolvedCardId,
      foilEffect: mode === "text" ? null : foilEffect || null,
    });
    if (!toastActiveRef.current) {
      advanceToastQueue();
    }
  };

  const clearPendingActionFlash = () => {
    pendingActionFlashRef.current = null;
  };

  const playActionFlash = (key) => {
    if (!key) return;
    if (actionFlashTimerRef.current) {
      window.clearTimeout(actionFlashTimerRef.current);
      actionFlashTimerRef.current = null;
    }
    setActionFlash(null);
    window.requestAnimationFrame(() => {
      setActionFlash({ key });
      actionFlashTimerRef.current = window.setTimeout(() => {
        setActionFlash((current) => (current?.key === key ? null : current));
        actionFlashTimerRef.current = null;
      }, ACTION_FLASH_MS);
    });
  };
  playActionFlashRef.current = playActionFlash;

  const showVitalsFloaterItem = useCallback((item) => {
    setVitalsFloaterDisplay((current) => [...current, item]);
    if (
      !isXpVitalsFloaterText(item.text) &&
      (item.side === "player" || item.side === "opponent")
    ) {
      const side = item.side;
      if (vitalsHitFlashTimersRef.current[side]) {
        window.clearTimeout(vitalsHitFlashTimersRef.current[side]);
      }
      setVitalsHitFlash((current) => ({
        ...current,
        [side]: (current[side] || 0) + 1,
      }));
      vitalsHitFlashTimersRef.current[side] = window.setTimeout(() => {
        vitalsHitFlashTimersRef.current[side] = null;
      }, VITALS_HIT_FLASH_MS);
    }
    const timerId = window.setTimeout(() => {
      vitalsFloaterTimersRef.current.delete(timerId);
      setVitalsFloaterDisplay((current) =>
        current.filter((entry) => entry.key !== item.key),
      );
    }, VITALS_FLOATER_CLEANUP_MS);
    vitalsFloaterTimersRef.current.add(timerId);
  }, []);

  const pumpVitalsFloaterQueue = useCallback(() => {
    const step = () => {
      const next = vitalsFloaterQueueRef.current.shift();
      if (!next) {
        vitalsFloaterPumpActiveRef.current = false;
        vitalsFloaterPumpTimerRef.current = null;
        return;
      }
      showVitalsFloaterItem(next);
      if (vitalsFloaterQueueRef.current.length > 0) {
        vitalsFloaterPumpTimerRef.current = window.setTimeout(
          step,
          VITALS_FLOATER_STAGGER_MS,
        );
      } else {
        vitalsFloaterPumpActiveRef.current = false;
        vitalsFloaterPumpTimerRef.current = null;
      }
    };

    if (vitalsFloaterPumpActiveRef.current) return;
    vitalsFloaterPumpActiveRef.current = true;
    step();
  }, [showVitalsFloaterItem]);

  const clearVitalsFloaterRuntime = useCallback(() => {
    if (vitalsFloaterPumpTimerRef.current) {
      window.clearTimeout(vitalsFloaterPumpTimerRef.current);
      vitalsFloaterPumpTimerRef.current = null;
    }
    vitalsFloaterTimersRef.current.forEach((timerId) =>
      window.clearTimeout(timerId),
    );
    vitalsFloaterTimersRef.current.clear();
    if (vitalsHitFlashTimersRef.current.player) {
      window.clearTimeout(vitalsHitFlashTimersRef.current.player);
      vitalsHitFlashTimersRef.current.player = null;
    }
    if (vitalsHitFlashTimersRef.current.opponent) {
      window.clearTimeout(vitalsHitFlashTimersRef.current.opponent);
      vitalsHitFlashTimersRef.current.opponent = null;
    }
    vitalsFloaterQueueRef.current = [];
    vitalsFloaterPumpActiveRef.current = false;
    setVitalsFloaterDisplay([]);
    setVitalsHitFlash({ player: 0, opponent: 0 });
    lastVitalsFloaterIdRef.current = 0;
    pendingCombatImpactCountRef.current = {};
  }, []);

  const revealCombatImpact = useCallback((item) => {
    if (!item) return;
    playMatchSfxList(item.impactSfx);
    const floater = item.deferredFloater || null;
    if (floater) {
      showVitalsFloaterItem(floater);
    }
    const target = item.target;
    if (!target) return;
    const pending = pendingCombatImpactCountRef.current;
    pending[target] = Math.max(0, (pending[target] || 1) - 1);
    const remaining = pending[target] || 0;
    const finalHp = Math.max(0, Math.floor(Number(item.impactFinalHp) || 0));
    const finalMaxHp = Math.max(
      0,
      Math.floor(Number(item.impactFinalMaxHp) || 0),
    );
    const hpDelta = floater ? Number(floater.hpDelta) || 0 : 0;
    setVisualVitalsByUser((current) => {
      const prev = current[target] || {
        hp: finalHp,
        maxHp: finalMaxHp || finalHp,
      };
      if (remaining <= 0) {
        return {
          ...current,
          [target]: {
            hp: finalHp,
            maxHp: Math.max(finalMaxHp, finalHp),
          },
        };
      }
      const maxHp = Math.max(prev.maxHp, finalMaxHp, 0);
      const hp = Math.max(0, Math.min(maxHp, prev.hp + hpDelta));
      return {
        ...current,
        [target]: { hp, maxHp },
      };
    });
  }, [showVitalsFloaterItem]);

  const showCombatHitFlash = useCallback((targetEl, tone, flashKey) => {
    if (!targetEl) return () => {};
    const rect = targetEl.getBoundingClientRect();
    if (!(rect.width > 0 && rect.height > 0)) return () => {};
    const key = flashKey || `combat-hit-${Date.now()}`;
    const item = {
      key,
      tone: tone || "physical",
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
    };
    setCombatHitFlashDisplay((current) => [...current, item]);
    const cancelShake = shakeCombatMeleeTarget(targetEl);
    const timerId = window.setTimeout(() => {
      combatProjectileTimersRef.current.delete(timerId);
      setCombatHitFlashDisplay((current) =>
        current.filter((entry) => entry.key !== key),
      );
      cancelShake();
    }, COMBAT_HIT_FLASH_MS);
    combatProjectileTimersRef.current.add(timerId);
    return () => {
      window.clearTimeout(timerId);
      combatProjectileTimersRef.current.delete(timerId);
      setCombatHitFlashDisplay((current) =>
        current.filter((entry) => entry.key !== key),
      );
      cancelShake();
    };
  }, []);

  const showCombatProjectileItem = useCallback((item) => {
    const triggerImpact = () => {
      revealCombatImpact(item);
      const targetEl = combatAnchorQuery(`aspect:${item.target}`);
      if (!targetEl) return;
      const cancelHit = showCombatHitFlash(
        targetEl,
        item.tone || "physical",
        `${item.key}-hit`,
      );
      combatProjectileCleanupsRef.current.add(cancelHit);
    };

    // nockMs > 0 shows the projectile resting on the source for that long
    // (an arrow drawn back during the windup) before it flies.
    const spawnProjectile = (points, nockMs = 0) => {
      if (!points) return false;
      const displayItem = {
        ...item,
        sx: points.sx,
        sy: points.sy,
        ex: points.ex,
        ey: points.ey,
        nockMs,
      };
      setCombatProjectileDisplay((current) => [...current, displayItem]);
      const impactTimerId = window.setTimeout(() => {
        combatProjectileTimersRef.current.delete(impactTimerId);
        triggerImpact();
      }, nockMs + Math.round(COMBAT_PROJECTILE_ANIM_MS * 0.7));
      combatProjectileTimersRef.current.add(impactTimerId);
      const timerId = window.setTimeout(() => {
        combatProjectileTimersRef.current.delete(timerId);
        setCombatProjectileDisplay((current) =>
          current.filter((entry) => entry.key !== item.key),
        );
      }, nockMs + COMBAT_PROJECTILE_CLEANUP_MS);
      combatProjectileTimersRef.current.add(timerId);
      return true;
    };

    const acquireEchoGhost = (points) => {
      if (!item.echo || !points?.sourceEl) return null;
      const ghosts = combatEchoGhostsRef.current;
      const existing = ghosts.get(item.sourceKey);
      if (!item.echoLead && existing?.isConnected) {
        return { el: existing, animate: false };
      }
      const ghost = spawnCombatEchoGhost(points.sourceEl);
      if (!ghost) return null;
      ghosts.set(item.sourceKey, ghost);
      return { el: ghost, animate: true };
    };

    const withEchoGhostRelease = (cancelAnim, ghost) => {
      if (!ghost) return cancelAnim;
      return () => {
        cancelAnim();
        ghost.remove();
        if (combatEchoGhostsRef.current.get(item.sourceKey) === ghost) {
          combatEchoGhostsRef.current.delete(item.sourceKey);
        }
      };
    };

    const beginMelee = (points) => {
      if (!points?.sourceEl) return false;
      const reduced = prefersReducedCombatMotion();
      if (reduced) {
        triggerImpact();
        return true;
      }
      const echoGhost = acquireEchoGhost(points);
      const hitTimerId = window.setTimeout(() => {
        combatProjectileTimersRef.current.delete(hitTimerId);
        triggerImpact();
      }, COMBAT_MELEE_HIT_AT_MS);
      combatProjectileTimersRef.current.add(hitTimerId);
      if (echoGhost && !echoGhost.animate) return true;
      const cancelMelee = withEchoGhostRelease(
        applyCombatSourceMelee(
          echoGhost?.el || points.sourceEl,
          points,
          item.tone || "physical",
        ),
        echoGhost?.el,
      );
      combatProjectileCleanupsRef.current.add(cancelMelee);
      const settleTimerId = window.setTimeout(() => {
        combatProjectileTimersRef.current.delete(settleTimerId);
        cancelMelee();
        combatProjectileCleanupsRef.current.delete(cancelMelee);
      }, COMBAT_MELEE_SETTLE_MS);
      combatProjectileTimersRef.current.add(settleTimerId);
      return true;
    };

    const beginProjectile = (points) => {
      if (!points) return false;
      const reduced = prefersReducedCombatMotion();
      if (reduced || !points.sourceEl) {
        spawnProjectile(points);
        return true;
      }
      const echoGhost = acquireEchoGhost(points);
      const animEl = echoGhost?.el || points.sourceEl;
      if (item.shape === "arrow") {
        spawnProjectile(points, COMBAT_SOURCE_FIRE_AT_MS);
      } else {
        const fireTimerId = window.setTimeout(() => {
          combatProjectileTimersRef.current.delete(fireTimerId);
          const fireEl = animEl.isConnected ? animEl : points.sourceEl;
          const livePoints =
            resolveCombatProjectilePoints(item, fireEl) || points;
          spawnProjectile(livePoints);
        }, COMBAT_SOURCE_FIRE_AT_MS);
        combatProjectileTimersRef.current.add(fireTimerId);
      }
      if (echoGhost && !echoGhost.animate) return true;
      const cancelWindup = withEchoGhostRelease(
        applyCombatSourceWindup(animEl, points, item.tone || "physical"),
        echoGhost?.el,
      );
      combatProjectileCleanupsRef.current.add(cancelWindup);
      const settleTimerId = window.setTimeout(() => {
        combatProjectileTimersRef.current.delete(settleTimerId);
        cancelWindup();
        combatProjectileCleanupsRef.current.delete(cancelWindup);
      }, COMBAT_SOURCE_SETTLE_MS);
      combatProjectileTimersRef.current.add(settleTimerId);
      return true;
    };

    const begin = (points) => {
      if (!points) return false;
      if (item.motion === "melee") return beginMelee(points);
      return beginProjectile(points);
    };

    const immediate = resolveCombatProjectilePoints(item);
    if (begin(immediate)) return;
    window.requestAnimationFrame(() => {
      if (!begin(resolveCombatProjectilePoints(item))) {
        revealCombatImpact(item);
      }
    });
  }, [revealCombatImpact, showCombatHitFlash]);

  const pumpCombatProjectileQueue = useCallback(() => {
    const step = () => {
      const next = combatProjectileQueueRef.current.shift();
      if (!next) {
        combatProjectilePumpActiveRef.current = false;
        combatProjectilePumpTimerRef.current = null;
        return;
      }
      showCombatProjectileItem(next);
      if (combatProjectileQueueRef.current.length > 0) {
        const upcoming = combatProjectileQueueRef.current[0];
        combatProjectilePumpTimerRef.current = window.setTimeout(
          step,
          upcoming?.echoLead
            ? COMBAT_ECHO_GHOST_DELAY_MS
            : COMBAT_PROJECTILE_STAGGER_MS,
        );
      } else {
        combatProjectilePumpActiveRef.current = false;
        combatProjectilePumpTimerRef.current = null;
      }
    };

    if (combatProjectilePumpActiveRef.current) return;
    combatProjectilePumpActiveRef.current = true;
    step();
  }, [showCombatProjectileItem]);

  const clearCombatProjectileRuntime = useCallback(() => {
    if (combatProjectilePumpTimerRef.current) {
      window.clearTimeout(combatProjectilePumpTimerRef.current);
      combatProjectilePumpTimerRef.current = null;
    }
    combatProjectileTimersRef.current.forEach((timerId) =>
      window.clearTimeout(timerId),
    );
    combatProjectileTimersRef.current.clear();
    combatProjectileCleanupsRef.current.forEach((cleanup) => cleanup());
    combatProjectileCleanupsRef.current.clear();
    combatEchoGhostsRef.current.forEach((ghost) => ghost.remove());
    combatEchoGhostsRef.current.clear();
    combatProjectileQueueRef.current = [];
    combatProjectilePumpActiveRef.current = false;
    setCombatProjectileDisplay([]);
    setCombatHitFlashDisplay([]);
    lastCombatProjectileIdRef.current = 0;
    pendingCombatImpactCountRef.current = {};
  }, []);

  const clearCardPlayRuntime = useCallback(() => {
    cardPlayTimersRef.current.forEach((timerId) =>
      window.clearTimeout(timerId),
    );
    cardPlayTimersRef.current.clear();
    setCardPlayAnims([]);
    setCardPlayPendingIds(new Set());
    boardPiecesSnapshotRef.current = null;
    cardPlayMatchIdRef.current = null;
  }, []);

  const clearMatchEmoteRuntime = useCallback(() => {
    matchEmoteTimersRef.current.forEach((timerId) =>
      window.clearTimeout(timerId),
    );
    matchEmoteTimersRef.current.clear();
    setMatchEmoteDisplay([]);
  }, []);

  const spawnMatchEmote = useCallback((fromUsername, emoji) => {
    if (hideMatchEmotesRef.current) return;
    if (!MATCH_EMOTE_SET.has(emoji) || !fromUsername) return;
    if (blockedUsernamesRef.current.has(fromUsername)) return;
    const fromEl = combatAnchorQuery(`aspect:${fromUsername}`);
    const fromRect = fromEl?.getBoundingClientRect?.() || null;
    const pad = 8;
    const sx = fromRect
      ? fromRect.left +
        pad +
        Math.random() * Math.max(8, fromRect.width - pad * 2)
      : window.innerWidth * (0.35 + Math.random() * 0.3);
    const sy = fromRect
      ? fromRect.top +
        pad +
        Math.random() * Math.max(8, fromRect.height - pad * 2)
      : window.innerHeight * (0.62 + Math.random() * 0.18);

    const otherBoards = [
      ...document.querySelectorAll("[data-board-owner]"),
    ].filter((el) => el.getAttribute("data-board-owner") !== fromUsername);
    const oppBoard =
      otherBoards[0] ||
      (() => {
        const otherAspect = [
          ...document.querySelectorAll("[data-combat-anchor^='aspect:']"),
        ].find((el) => {
          const key = el.getAttribute("data-combat-anchor") || "";
          return key !== `aspect:${fromUsername}`;
        });
        return otherAspect?.closest("[data-board-owner]") || null;
      })();
    const oppRect = oppBoard?.getBoundingClientRect?.() || null;
    const rising = !fromRect || !oppRect || oppRect.top + oppRect.height / 2 < sy;
    const ex = oppRect
      ? oppRect.left + oppRect.width * (0.22 + Math.random() * 0.56)
      : window.innerWidth * (0.3 + Math.random() * 0.4);
    const ey = oppRect
      ? oppRect.top +
        oppRect.height *
          (rising
            ? 0.08 + Math.random() * 0.28
            : 0.64 + Math.random() * 0.28)
      : rising
        ? window.innerHeight * (0.12 + Math.random() * 0.12)
        : window.innerHeight * (0.72 + Math.random() * 0.12);

    const dx = ex - sx;
    const dy = ey - sy;
    const pathLen = Math.hypot(dx, dy) || 1;
    const nx = -dy / pathLen;
    const ny = dx / pathLen;
    const sway = Math.min(14 + Math.random() * 16, pathLen * 0.08);
    const side = Math.random() < 0.5 ? -1 : 1;
    const pointAt = (t, lateral) => ({
      x: sx + dx * t + nx * lateral,
      y: sy + dy * t + ny * lateral,
    });
    const p1 = pointAt(0.22 + Math.random() * 0.06, side * sway);
    const p2 = pointAt(0.48 + Math.random() * 0.06, -side * sway * 0.85);
    const p3 = pointAt(0.72 + Math.random() * 0.06, side * sway * 0.55);

    matchEmoteSeqRef.current += 1;
    const key = `emote-${matchEmoteSeqRef.current}`;
    const item = {
      key,
      emoji,
      from: fromUsername,
      sx,
      sy,
      x1: p1.x,
      y1: p1.y,
      x2: p2.x,
      y2: p2.y,
      x3: p3.x,
      y3: p3.y,
      ex,
      ey,
      rot: (Math.random() - 0.5) * 18,
    };
    setMatchEmoteDisplay((current) => [...current.slice(-40), item]);
    const timerId = window.setTimeout(() => {
      matchEmoteTimersRef.current.delete(timerId);
      setMatchEmoteDisplay((current) =>
        current.filter((entry) => entry.key !== key),
      );
    }, MATCH_EMOTE_CLEANUP_MS);
    matchEmoteTimersRef.current.add(timerId);
  }, []);

  const removeMatchEmote = useCallback((key) => {
    if (!key) return;
    setMatchEmoteDisplay((current) =>
      current.filter((entry) => entry.key !== key),
    );
  }, []);

  const revealCardPlayTarget = useCallback((instanceId) => {
    if (!instanceId) return;
    setCardPlayPendingIds((current) => {
      if (!current.has(instanceId)) return current;
      const next = new Set(current);
      next.delete(instanceId);
      return next;
    });
  }, []);

  const spawnCardPlayAnim = useCallback(
    (piece, side) => {
      if (!piece?.instanceId || prefersReducedCombatMotion()) {
        revealCardPlayTarget(piece?.instanceId);
        return;
      }
      const trySpawn = (attemptsLeft) => {
        const anchorKey =
          piece.kind === "ability"
            ? `ability:${piece.instanceId}`
            : `equipment:${piece.instanceId}`;
        const targetEl = combatAnchorQuery(anchorKey);
        if (!targetEl) {
          if (attemptsLeft > 0) {
            const retryId = window.setTimeout(() => {
              cardPlayTimersRef.current.delete(retryId);
              trySpawn(attemptsLeft - 1);
            }, 32);
            cardPlayTimersRef.current.add(retryId);
            return;
          }
          revealCardPlayTarget(piece.instanceId);
          return;
        }
        const rect = targetEl.getBoundingClientRect();
        if (!(rect.width > 0 && rect.height > 0)) {
          revealCardPlayTarget(piece.instanceId);
          return;
        }
        const catalogCard =
          (piece.cardId && catalogMap[piece.cardId]) || null;
        const boardCard = piece.boardCard || null;
        const faceCard = {
          ...(catalogCard || {}),
          ...(boardCard || {}),
          id: piece.cardId || boardCard?.id || catalogCard?.id,
          type:
            boardCard?.type ||
            catalogCard?.type ||
            (piece.kind === "ability" ? ["ability"] : ["equipment"]),
          cost:
            boardCard?.effectiveCost ||
            boardCard?.cost ||
            catalogCard?.cost,
        };
        const foilEffect = piece.foilEffect || boardCard?.foilEffect || null;
        const ghostNode =
          side === "opponent" ? buildCardPlayGhost(targetEl) : null;
        const ex = rect.left + rect.width / 2;
        const ey = rect.top + rect.height / 2;
        const sx = ex + (Math.random() - 0.5) * Math.min(56, rect.width * 0.45);
        const sy =
          side === "player"
            ? ey + Math.max(rect.height * 2.2, window.innerHeight * 0.42)
            : ey - Math.max(rect.height * 2.2, window.innerHeight * 0.42);
        const spinStart = side === "player" ? -10 - Math.random() * 8 : 10 + Math.random() * 8;
        cardPlaySeqRef.current += 1;
        const key = `card-play-${cardPlaySeqRef.current}`;
        const dust = buildCardPlayDust(
          ex,
          ey,
          rect.width * 0.52,
          rect.height * 0.52,
          CARD_PLAY_DUST_COUNT_BY_GRAPHICS_TIER[graphicsTierRef.current],
        );
        const anim = {
          key,
          instanceId: piece.instanceId,
          side,
          card: faceCard,
          foilEffect,
          ghostNode,
          cardId: piece.cardId,
          width: rect.width,
          height: rect.height,
          sx,
          sy,
          ex,
          ey,
          spinStart,
          dust,
        };
        setCardPlayAnims((current) => [...current, anim]);
        const revealId = window.setTimeout(() => {
          cardPlayTimersRef.current.delete(revealId);
          revealCardPlayTarget(piece.instanceId);
        }, CARD_PLAY_SLAM_AT_MS);
        cardPlayTimersRef.current.add(revealId);
        const cleanupId = window.setTimeout(() => {
          cardPlayTimersRef.current.delete(cleanupId);
          setCardPlayAnims((current) =>
            current.filter((entry) => entry.key !== key),
          );
        }, CARD_PLAY_CLEANUP_MS);
        cardPlayTimersRef.current.add(cleanupId);
      };
      if (side !== "opponent") {
        trySpawn(12);
        return;
      }
      const settleId = window.setTimeout(() => {
        cardPlayTimersRef.current.delete(settleId);
        trySpawn(12);
      }, CARD_PLAY_GHOST_SETTLE_MS);
      cardPlayTimersRef.current.add(settleId);
    },
    [catalogMap, revealCardPlayTarget],
  );

  const spawnCardFocusGhost = useCallback((ghost) => {
    cardFocusGhostSeqRef.current += 1;
    const key = `card-focus-ghost-${cardFocusGhostSeqRef.current}`;
    setCardFocusGhosts((current) => [...current, { ...ghost, key }]);
  }, []);

  const removeCardFocusGhost = useCallback((key) => {
    setCardFocusGhosts((current) =>
      current.filter((entry) => entry.key !== key),
    );
  }, []);

  const closeCardFocus = useCallback(() => {
    setHandMenu(null);
    setBoardMenu(null);
  }, []);

  const renderInCardFocus = (actionsNode, focus) => {
    if (!cardFocusHost || !actionsNode || !focus?.card) return actionsNode;
    return createPortal(
      <KeywordTipContext.Provider value={null}>
        <CardFocusView
          card={focus.card}
          foilEffect={focus.foilEffect || null}
          hideName={!showCardNames}
          overlay={focus.overlay || null}
          overlayClassName={focus.overlayClassName || ""}
          onReturn={spawnCardFocusGhost}
          onDismiss={closeCardFocus}
        >
          {actionsNode}
        </CardFocusView>
      </KeywordTipContext.Provider>,
      cardFocusHost,
      focus.key,
    );
  };

  const actionSuccessClass = (key) =>
    actionFlash?.key === key ? ` ${styles.actionSuccess}` : "";

  const applyPayload = (data) => {
    if (data.player) setPlayer(data.player);
    if ("match" in data) {
      if (data.match) {
        lastLiveMatchRef.current = data.match;
      }
      if (Array.isArray(data.match?.sfx) && data.match.sfx.length) {
        pendingCombatSfxRef.current.push(
          ...data.match.sfx.filter((sound) => COMBAT_IMPACT_SFX.has(sound)),
        );
        playMatchSfxList(
          data.match.sfx.filter((sound) => !COMBAT_IMPACT_SFX.has(sound)),
        );
      }
      setMatch(
        data.match
          ? {
              ...data.match,
              sfx: [],
            }
          : data.match,
      );
      if (data.match && pendingActionFlashRef.current) {
        const key = pendingActionFlashRef.current;
        pendingActionFlashRef.current = null;
        playActionFlashRef.current(key);
      }
    }
    if (data.assetVersions && typeof data.assetVersions === "object") {
      setAssetVersions(data.assetVersions);
      setAssetVersionsState(data.assetVersions);
    }
    if (data.catalog) setCatalog(data.catalog);
    if (Array.isArray(data.starterDecks)) setStarterDecks(data.starterDecks);
    if ("queued" in data) setQueued(Boolean(data.queued));
    if ("draftQueued" in data) setDraftQueued(Boolean(data.draftQueued));
    if ("playersInQueue" in data) {
      setPlayersInQueue(Math.max(0, Number(data.playersInQueue) || 0));
    }
    if (Array.isArray(data.tutorials)) setTutorials(data.tutorials);
    if ("adventureParty" in data) setAdventureParty(data.adventureParty || null);
  };

  const restoreViewAfterReconnect = (data) => {
    if (viewRef.current === "replay") return;

    const activeMatch = data.match;
    const activePlayer = data.player;

    if (activePlayer?.matchSummary) {
      setView("summary");
      return;
    }

    if (activeMatch && activeMatch.phase !== "ended") {
      matchIntroSeenRef.current.add(activeMatch.id);
      setMatchIntro(null);
      releaseToastIntroHold();
      setView(activeMatch.workshop ? "workshop" : "match");
      return;
    }

    if (adventureCombatSummaryActive(activePlayer?.adventureRun)) {
      setView("adventureSummary");
      return;
    }

    const draftPhase = activePlayer?.draftRun?.phase;
    if (draftPhase === "picking") {
      setView("draft");
      return;
    }
    if (draftPhase === "deckbuilding") {
      setView("draftDeck");
      return;
    }
    if (draftPhase === "complete") {
      setView("draftSummary");
      return;
    }

    const restored = activePlayer?.currentView;
    if (
      typeof restored === "string" &&
      restored &&
      restored !== "workshop" &&
      restored !== "match" &&
      restored !== "summary" &&
      restored !== "replay" &&
      restored !== "spectate"
    ) {
      setView(restored);
      return;
    }

    if (activePlayer?.adventureRun) {
      setView("adventure");
      return;
    }

    setView("menu");
  };

  useEffect(() => {
    if (!serverMessagesOpen) return;
    setServerMessagesUnread(false);
    const newest = serverMessages.reduce(
      (latest, entry) => Math.max(latest, entry.at || 0),
      0,
    );
    if (newest) saveServerMessagesSeenAt(newest);
  }, [serverMessagesOpen, serverMessages]);

  const send = (payload) => {
    if (isReplayViewRef.current) return;
    if (
      isSpectateViewRef.current &&
      MATCH_PLAY_MESSAGE_TYPES.has(payload?.type)
    ) {
      return;
    }
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    if (MATCH_PLAY_MESSAGE_TYPES.has(payload?.type)) {
      setSelectedHand(null);
      setMovingAbility(null);
      if (match?.tutorial) {
        setTutorialHintMinimized(true);
      }
    }
    const flashKey = actionFlashKeyFromPayload(payload);
    if (flashKey) pendingActionFlashRef.current = flashKey;
    const next = { ...payload };
    if (
      isWorkshopMatchRef.current &&
      MATCH_PLAY_MESSAGE_TYPES.has(payload?.type) &&
      workshopActingAsRef.current
    ) {
      next.asPlayer = workshopActingAsRef.current;
    }
    wsRef.current.send(JSON.stringify(next));
  };

  const connect = () => {
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
    }
    const ws = new WebSocket(getWebSocketUrl());
    wsRef.current = ws;
    ws.onopen = () => {
      setConnected(true);
      const session = loadSession();
      const kind =
        session?.kind === "account" || user?.kind === "account"
          ? "account"
          : "guest";
      const token =
        (typeof session?.token === "string" && session.token) ||
        (typeof user?.token === "string" && user.token) ||
        "";
      ws.send(
        JSON.stringify({
          type: "ai:identify",
          username,
          token,
          kind,
          guestExpiresAt:
            kind === "guest"
              ? session?.expiresAt || user?.expiresAt || null
              : null,
          activity: viewRef.current,
          via: getNativePlatform(),
        }),
      );
    };
    ws.onmessage = (event) => {
      let data;
      try {
        data = JSON.parse(event.data);
      } catch (err) {
        return;
      }
      if (data.type === "ai:error") {
        setSimRunning(false);
        setMigrateBusy(false);
        setGameLogExportBusy(false);
        clearPendingActionFlash();
        showToast(data.message || t("game.toast.error"));
        return;
      }
      if (data.type === "ai:loggedOut") {
        loggingOutRef.current = true;
        clearSession();
        dispatch({ type: "UNSET_USER" });
        return;
      }
      if (data.type === "ai:migrated") {
        const nextUser = {
          id: 0,
          username: data.username || username,
          token: typeof data.token === "string" ? data.token : "",
          kind: "account",
        };
        if (!nextUser.token) {
          clearSession();
          dispatch({ type: "UNSET_USER" });
          setMigrateBusy(false);
          showToast(t("account.createdSignInRequired"));
          return;
        }
        saveAccountSession(nextUser);
        dispatch({ type: "SET_USER", payload: nextUser });
        setMigrateOpen(false);
        setMigrateUsername("");
        setMigratePassword("");
        setMigrateError("");
        setMigrateBusy(false);
        showToast(t("account.createdPlayingAs", { name: nextUser.username }));
        if (data.player) setPlayer(data.player);
        return;
      }
      if (data.type === "ai:toast") {
        const toastKey = data.toastId ? `game.serverToast.${data.toastId}` : null;
        const localizedToast = toastKey ? t(toastKey, data.params || {}) : null;
        showToast(
          localizedToast && localizedToast !== toastKey
            ? localizedToast
            : data.message || "",
          data.cardId || null,
          data.foilEffect || null,
        );
        return;
      }
      if (data.type === "ai:completionToast") {
        showCompletionToast(data.category, data.name);
        return;
      }
      if (data.type === "ai:serverMessages") {
        applyServerMessagesInit(data);
        return;
      }
      if (data.type === "ai:serverMessage") {
        applyServerMessage(data);
        return;
      }
      if (data.type === "ai:adminServerMessages") {
        applyServerMessagesInit(data);
        setAdminServerTemplates(
          Array.isArray(data.templates) ? data.templates : [],
        );
        const countdownOptions = Array.isArray(data.countdownMinutes)
          ? data.countdownMinutes.filter((value) => Number(value) > 0)
          : [];
        setAdminCountdownOptions(countdownOptions);
        setAdminMaintenanceMinutes((current) =>
          countdownOptions.includes(current)
            ? current
            : countdownOptions[0] || current,
        );
        if (Number(data.maxLength) > 0) {
          setAdminServerMessageMax(Number(data.maxLength));
        }
        if (Number(data.maxNoteLength) > 0) {
          setAdminMaintenanceNoteMax(Number(data.maxNoteLength));
        }
        return;
      }
      if (data.type === "ai:emote") {
        spawnMatchEmote(data.from, data.emoji);
        return;
      }
      if (data.type === "ai:gameLogExport") {
        const exported = data.export;
        deliverGameLogExport(exported).then((result) => {
          setGameLogExportBusy(false);
          if (result === "failed") {
            showToast(t("game.gameLog.exportFailed"));
            return;
          }
          const entries = exported?.summary?.logEntries || 0;
          showToast(
            result === "copied"
              ? t("game.gameLog.copied", { entries })
              : t("game.gameLog.exported", { entries }),
          );
        });
        return;
      }
      if (data.type === "ai:needsPayment") {
        clearPendingActionFlash();
        const available = data.available || {
          strength: 0,
          dexterity: 0,
          wisdom: 0,
          neutral: 0,
        };
        const fixed = data.fixed && typeof data.fixed === "object" ? data.fixed : {};
        const weights =
          data.weights && typeof data.weights === "object" ? data.weights : null;
        const fillOrder = RECOMMENDED_FLEX_PAYMENT_ORDER;
        const paymentKind = data.paymentKind || "ability";
        const kindNames = {
          ability: t("game.payment.kind.ability"),
          sharpen: t("game.payment.kind.sharpen"),
          hone: t("game.payment.kind.hone"),
          fletch: t("game.payment.kind.fletch"),
          enableEncore: t("game.payment.kind.encore"),
          research: t("game.payment.kind.research"),
          repair: t("game.payment.kind.repair"),
        };
        setPaymentPrompt({
          kind: paymentKind,
          boardSlot: data.boardSlot || null,
          equipmentInstanceId: data.equipmentInstanceId || null,
          abilityInstanceId: data.abilityInstanceId || null,
          instanceId: data.instanceId || null,
          flex: data.flexCost || 0,
          available,
          fixed,
          weights,
          fillOrder,
          name: kindNames[paymentKind] || t("game.payment.kind.ability"),
        });
        setPaymentDraft(
          buildRecommendedFlexPayment(
            available,
            data.flexCost || 0,
            fixed,
            weights,
            fillOrder,
          ),
        );
        return;
      }
      if (data.type === "ai:connected" || data.type === "ai:state") {
        if (data.type === "ai:connected" && data.sessionToken) {
          const nextKind = data.kind === "account" ? "account" : "guest";
          const nextUser = {
            id: 0,
            username: data.username || data.player?.username || username,
            token: data.sessionToken,
            kind: nextKind,
            expiresAt:
              nextKind === "guest" && Number(data.guestExpiresAt)
                ? Number(data.guestExpiresAt)
                : undefined,
          };
          if (nextKind === "account") {
            saveAccountSession(nextUser);
          } else {
            saveGuestSession(nextUser);
          }
          dispatch({ type: "SET_USER", payload: nextUser });
        }
        applyPayload(data);
      }
      if (data.type === "ai:connected") {
        const wasReconnect = hasConnectedOnceRef.current;
        hasConnectedOnceRef.current = true;
        setReconnecting(false);
        if (wasReconnect) {
          setEnteredGame(true);
        }
        restoreViewAfterReconnect(data);
      }
      if (data.type === "ai:socialInit") {
        setSocialPlayers(Array.isArray(data.players) ? data.players : []);
        setSocialMessages(Array.isArray(data.messages) ? data.messages : []);
      }
      if (data.type === "ai:socialChatSync") {
        setSocialMessages(Array.isArray(data.messages) ? data.messages : []);
      }
      if (data.type === "ai:ranking") {
        setRankingSortBy(data.sortBy === "human" ? "human" : "cpu");
        setRankingPage(Math.max(0, Math.floor(Number(data.page) || 0)));
        setRankingEntries(Array.isArray(data.entries) ? data.entries : []);
        setRankingTotalPages(Math.max(1, Math.floor(Number(data.totalPages) || 1)));
        setRankingTotal(Math.max(0, Math.floor(Number(data.total) || 0)));
      }
      if (data.type === "ai:socialPresence") {
        setSocialPlayers(Array.isArray(data.players) ? data.players : []);
        if (viewRef.current === "ranking") {
          send({
            type: "ai:rankingRequest",
            sortBy: rankingSortByRef.current,
            page: rankingPageRef.current,
          });
        }
      }
      if (data.type === "ai:queueStatus") {
        setPlayersInQueue(Math.max(0, Number(data.playersInQueue) || 0));
      }
      if (data.type === "ai:socialChat") {
        if (data.message && data.message.id) {
          setSocialMessages((current) => {
            if (current.some((entry) => entry.id === data.message.id)) {
              return current;
            }
            return [...current, data.message].slice(-1000);
          });
        }
      }
      if (data.type === "ai:socialProfile") {
        setSocialProfile(data.profile || null);
      }
      if (data.type === "ai:socialReportResult") {
        setReportBusy(false);
        if (data.ok) {
          setReportTarget(null);
          setReportError(null);
          showToast(t("social.reportSubmitted"));
        } else {
          setReportError(data.message || t("social.reportFailed"));
        }
      }
      if (data.type === "ai:adminPlayerReports") {
        setAdminPlayerReports(Array.isArray(data.reports) ? data.reports : []);
      }
      if (data.type === "ai:socialChallenge") {
        if (data.challenge?.id) {
          setIncomingChallenge(data.challenge);
          setChallengeForfeitPrompt(null);
        }
      }
      if (data.type === "ai:adventurePartyInvite") {
        if (data.invite?.id) {
          setIncomingPartyInvite(data.invite);
        }
      }
      if (data.type === "ai:adventurePartyInviteSent") {
        showToast(
          t("social.partyInviteSent", {
            name: data.username || t("social.playerFallback"),
          }),
        );
      }
      if (data.type === "ai:adventureParty") {
        setAdventureParty(data.adventureParty || null);
      }
      if (data.type === "ai:socialChallengeSent") {
        showToast(
          t("social.challengeSent", {
            name: data.username || t("social.playerFallback"),
          }),
        );
      }
      if (data.type === "ai:socialChallengeNeedForfeit") {
        setChallengeForfeitPrompt({
          challengeId: data.challengeId,
          from: data.from,
        });
        setIncomingChallenge(null);
      }
      if (data.type === "ai:socialChallengeResponse") {
        if (!data.accepted) {
          showToast(
            data.to === username
              ? t("social.challengeDeclined")
              : t("social.challengeDeclinedBy", {
                  name: data.to || t("common.player"),
                }),
          );
        }
        setIncomingChallenge((current) =>
          current?.id === data.challengeId ? null : current,
        );
        setChallengeForfeitPrompt((current) =>
          current?.challengeId === data.challengeId ? null : current,
        );
      }
      if (data.type === "ai:adminCompletedGames") {
        setCompletedGameSummaries(
          Array.isArray(data.games) ? data.games : [],
        );
      }
      if (data.type === "ai:adminCompletedGame") {
        setAdminGame(data.game || null);
        setAdminPreviewGame(data.game || null);
        setAdminReplayIndex(0);
        setSimRunning(false);
      }
      if (data.type === "ai:adminPlayerDecks") {
        setAdminPlayerDecks(Array.isArray(data.players) ? data.players : []);
      }
      if (data.type === "ai:adminPlayerBots") {
        setAdminPlayerBots({
          enabled: data.enabled === true,
          bots: Array.isArray(data.bots) ? data.bots : [],
          limits: data.limits || null,
        });
      }
      if (data.type === "ai:adminPlayerBotAnalysis") {
        setAdminPlayerBotAnalysis(data.analysis || null);
      }
      if (data.type === "ai:adminCardCatalog") {
        setAdminCardCatalog({
          kind: data.kind,
          columns: Array.isArray(data.columns) ? data.columns : [],
          rows: Array.isArray(data.rows) ? data.rows : [],
          file: data.file || "",
          revision: Date.now(),
        });
      }
      if (data.type === "ai:wiped") {
        applyPayload(data);
        setView("menu");
        setSelectedEquipmentId(null);
        setSlottedAbilityExam(null);
        setHandMenu(null);
        setBoardMenu(null);
        setSelectedHand(null);
        setResetSavesConfirm(false);
        setDeleteDeckConfirm(false);
        setCombatLogOpen(false);
        closeCardExam();
        setCompletedGameSummaries([]);
        setAdminGame(null);
        setAdminPreviewGame(null);
        setAdminPlayerDecks([]);
        setSimDeckA("");
        setSimDeckB("");
        setSimCpuADifficulty("normal");
        setSimCpuAPersonality("balanced");
        setSimCpuBDifficulty("normal");
        setSimCpuBPersonality("balanced");
        setSimRunning(false);
      }
    };
    ws.onclose = () => {
      setConnected(false);
      if (loggingOutRef.current) {
        return;
      }
      if (hasConnectedOnceRef.current) {
        setReconnecting(true);
      }
      reconnectTimer.current = setTimeout(connect, 1500);
    };
  };

  useEffect(() => {
    connect();
    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
      }
      clearToastTimers();
      if (toastIntroHoldTimerRef.current) {
        window.clearTimeout(toastIntroHoldTimerRef.current);
        toastIntroHoldTimerRef.current = null;
      }
      if (completionToastTimerRef.current) {
        window.clearTimeout(completionToastTimerRef.current);
        completionToastTimerRef.current = null;
      }
      clearServerToastTimer();
      if (actionFlashTimerRef.current) {
        window.clearTimeout(actionFlashTimerRef.current);
        actionFlashTimerRef.current = null;
      }
      vitalsFloaterTimersRef.current.forEach((timerId) =>
        window.clearTimeout(timerId),
      );
      vitalsFloaterTimersRef.current.clear();
      if (vitalsFloaterPumpTimerRef.current) {
        window.clearTimeout(vitalsFloaterPumpTimerRef.current);
        vitalsFloaterPumpTimerRef.current = null;
      }
      if (vitalsHitFlashTimersRef.current.player) {
        window.clearTimeout(vitalsHitFlashTimersRef.current.player);
        vitalsHitFlashTimersRef.current.player = null;
      }
      if (vitalsHitFlashTimersRef.current.opponent) {
        window.clearTimeout(vitalsHitFlashTimersRef.current.opponent);
        vitalsHitFlashTimersRef.current.opponent = null;
      }
      vitalsFloaterQueueRef.current = [];
      vitalsFloaterPumpActiveRef.current = false;
      combatProjectileTimersRef.current.forEach((timerId) =>
        window.clearTimeout(timerId),
      );
      combatProjectileTimersRef.current.clear();
      if (combatProjectilePumpTimerRef.current) {
        window.clearTimeout(combatProjectilePumpTimerRef.current);
        combatProjectilePumpTimerRef.current = null;
      }
      combatProjectileCleanupsRef.current.forEach((cleanup) => cleanup());
      combatProjectileCleanupsRef.current.clear();
      combatProjectileQueueRef.current = [];
      combatProjectilePumpActiveRef.current = false;
      cardPlayTimersRef.current.forEach((timerId) =>
        window.clearTimeout(timerId),
      );
      cardPlayTimersRef.current.clear();
      matchEmoteTimersRef.current.forEach((timerId) =>
        window.clearTimeout(timerId),
      );
      matchEmoteTimersRef.current.clear();
      toastQueueRef.current = [];
      toastActiveRef.current = false;
      toastExitingRef.current = false;
      if (resourceFlyerRetryTimerRef.current) {
        window.clearTimeout(resourceFlyerRetryTimerRef.current);
        resourceFlyerRetryTimerRef.current = null;
      }
      setResourceFlyers([]);
      setCardPlayAnims([]);
      setCardPlayPendingIds(new Set());
      setMatchEmoteDisplay([]);
    };
  }, [username]);

  useEffect(() => {
    if (!toast || toast.exiting) return undefined;
    const onPointerDown = () => {
      dismissToastRef.current();
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
    };
  }, [toast?.id, toast?.exiting]);

  useEffect(() => {
    if (!handMenu && !boardMenu) return undefined;
    const handOpenSel = `.${CSS.escape(styles.handCardSlotActionsOpen)}`;
    const boardOpenSel = `.${CSS.escape(styles.boardCardSlotActionsOpen)}`;
    const focusPanelSel = `.${CSS.escape(styles.cardFocusPanel)}`;
    const keywordTipSel = `.${CSS.escape(styles.keywordTooltipOverlay)}`;
    let releaseSwallowTimer = null;
    const swallowClick = (event) => {
      event.stopPropagation();
      event.preventDefault();
    };
    const releaseSwallow = () => {
      if (releaseSwallowTimer != null) {
        window.clearTimeout(releaseSwallowTimer);
        releaseSwallowTimer = null;
      }
      document.removeEventListener("click", swallowClick, true);
    };
    const onPointerDown = (event) => {
      const target = event.target;
      if (
        target instanceof Element &&
        (target.closest(handOpenSel) ||
          target.closest(boardOpenSel) ||
          target.closest(focusPanelSel) ||
          target.closest(keywordTipSel))
      ) {
        return;
      }
      if (document.querySelector(focusPanelSel)) {
        event.stopPropagation();
        releaseSwallow();
        document.addEventListener("click", swallowClick, {
          capture: true,
          once: true,
        });
        releaseSwallowTimer = window.setTimeout(releaseSwallow, 600);
      }
      setHandMenu(null);
      setBoardMenu(null);
    };
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      setHandMenu(null);
      setBoardMenu(null);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [handMenu, boardMenu]);

  useLayoutEffect(() => {
    if (!handMenu && !boardMenu) return undefined;
    const clampOpenActions = () => {
      const openRoot = document.querySelector(
        handMenu
          ? `.${CSS.escape(styles.handCardSlotActionsOpen)}`
          : `.${CSS.escape(styles.boardCardSlotActionsOpen)}`,
      );
      const menu = openRoot?.querySelector(
        handMenu
          ? `.${CSS.escape(styles.handCardActions)}`
          : `.${CSS.escape(styles.boardCardActions)}`,
      );
      if (!(menu instanceof HTMLElement)) return;
      const dxVar = handMenu ? "--hand-actions-dx" : "--board-actions-dx";
      const dyVar = handMenu ? "--hand-actions-dy" : "--board-actions-dy";
      menu.style.setProperty(dxVar, "0px");
      menu.style.setProperty(dyVar, "0px");
      const pad = 8;
      const view =
        document.querySelector(`.${CSS.escape(styles.matchView)}`) ||
        document.querySelector(`.${CSS.escape(styles.main)}`);
      const viewRect =
        view instanceof HTMLElement ? view.getBoundingClientRect() : null;
      const leftBound = Math.max(pad, viewRect ? viewRect.left + pad : pad);
      const topBound = Math.max(pad, viewRect ? viewRect.top + pad : pad);
      const rightBound = Math.min(
        window.innerWidth - pad,
        viewRect ? viewRect.right - pad : window.innerWidth - pad,
      );
      const bottomBound = Math.min(
        window.innerHeight - pad,
        viewRect ? viewRect.bottom - pad : window.innerHeight - pad,
      );
      const availW = Math.max(96, rightBound - leftBound);
      menu.style.maxWidth = `${Math.min(560, availW)}px`;
      const rect = menu.getBoundingClientRect();
      let dx = 0;
      let dy = 0;
      if (rect.left < leftBound) dx += leftBound - rect.left;
      if (rect.right + dx > rightBound) {
        dx -= rect.right + dx - rightBound;
      }
      if (rect.top < topBound) dy += topBound - rect.top;
      if (rect.bottom + dy > bottomBound) {
        dy -= rect.bottom + dy - bottomBound;
      }
      menu.style.setProperty(dxVar, `${dx}px`);
      menu.style.setProperty(dyVar, `${dy}px`);
    };
    clampOpenActions();
    const frame = window.requestAnimationFrame(() => {
      clampOpenActions();
      window.requestAnimationFrame(clampOpenActions);
    });
    window.addEventListener("resize", clampOpenActions);
    window.addEventListener("scroll", clampOpenActions, true);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", clampOpenActions);
      window.removeEventListener("scroll", clampOpenActions, true);
    };
  }, [handMenu, boardMenu]);

  useEffect(() => {
    if (!combatLogOpen) return;
    if (!combatLogStickTopRef.current) return;
    const list = combatLogListRef.current;
    if (list) list.scrollTop = 0;
  }, [combatLogOpen, match?.combatLog]);

  useEffect(() => {
    if (!privacyPolicyOpen) return undefined;
    if (privacyPolicyHtml) return undefined;
    let cancelled = false;
    setPrivacyPolicyLoading(true);
    setPrivacyPolicyError(null);
    fetch(PRIVACY_POLICY_PAGE_URL)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Could not load Privacy Policy.");
        }
        return response.text();
      })
      .then((html) => {
        if (cancelled) return;
        const doc = new DOMParser().parseFromString(html, "text/html");
        const updated = doc.querySelector(".updated");
        const content = doc.querySelector(".content");
        if (!content) {
          throw new Error("Could not load Privacy Policy.");
        }
        const parts = [];
        if (updated) parts.push(updated.outerHTML);
        parts.push(content.innerHTML);
        setPrivacyPolicyHtml(parts.join(""));
        setPrivacyPolicyLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setPrivacyPolicyError(t("support.privacyLoadFailed"));
        setPrivacyPolicyLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [privacyPolicyOpen, privacyPolicyHtml]);

  useEffect(() => {
    if (!player?.deckPairs?.length) return;
    setEditingDeckId((current) => {
      if (current && player.deckPairs.some((pair) => pair.id === current)) {
        return current;
      }
      if (current && unsavedNewDeck) {
        return current;
      }
      return player.activeDeckPairId || player.deckPairs[0].id;
    });
    if (
      unsavedNewDeck &&
      editingDeckId &&
      player.deckPairs.some((pair) => pair.id === editingDeckId)
    ) {
      setUnsavedNewDeck(false);
    }
  }, [player?.deckPairs, player?.activeDeckPairId, unsavedNewDeck, editingDeckId]);

  const editingDeckKey = useMemo(() => {
    const pair = (player?.deckPairs || []).find(
      (entry) => entry.id === editingDeckId,
    );
    if (!pair) return null;
    return JSON.stringify({
      id: pair.id,
      equipmentIds: pair.equipmentIds || [],
      abilityIds: pair.abilityIds || [],
      aspectId: pair.aspectId || null,
      aspectFoil: pair.aspectFoil || null,
      equipmentFoils: pair.equipmentFoils || [],
      abilityFoils: pair.abilityFoils || [],
    });
  }, [player?.deckPairs, editingDeckId]);

  useEffect(() => {
    if (view === "draftDeck") return;
    if (!editingDeckKey || !editingDeckId || !player?.deckPairs?.length) {
      return;
    }
    if (unsavedNewDeck) return;
    if (nameInputFocusedRef.current) return;
    const pair = player.deckPairs.find((entry) => entry.id === editingDeckId);
    if (!pair) return;
    const pairMatchesDraft =
      deckIdsEqual(pair.equipmentIds || [], filledDeckIds(draftEquipment)) &&
      deckIdsEqual(pair.abilityIds || [], filledDeckIds(draftAbility)) &&
      (pair.aspectId || DEFAULT_ASPECT_ID) ===
        (draftAspect || DEFAULT_ASPECT_ID) &&
      (pair.aspectFoil || null) === (draftAspectFoil || null) &&
      deckFoilsEqual(
        pair.equipmentFoils || [],
        filledDeckFoils(draftEquipment, draftEquipmentFoils),
      ) &&
      deckFoilsEqual(
        pair.abilityFoils || [],
        filledDeckFoils(draftAbility, draftAbilityFoils),
      );
    setDraftEquipment(padDeckSlots(pair.equipmentIds || []));
    setDraftAbility(padDeckSlots(pair.abilityIds || []));
    setDraftAspect(pair.aspectId || DEFAULT_ASPECT_ID);
    setDraftAspectFoil(pair.aspectFoil || null);
    setDraftEquipmentFoils(padFoilSlots(pair.equipmentFoils || []));
    setDraftAbilityFoils(padFoilSlots(pair.abilityFoils || []));
    setDraftName(pair.name || "Deck");
    if (pairMatchesDraft) return;
    setSlotPicker(null);
    setDeckVariantPicker(null);
  }, [editingDeckKey, editingDeckId, player?.deckPairs, unsavedNewDeck, view]);

  useEffect(() => {
    setTutorialHintMinimized(false);
  }, [match?.id, match?.tutorialStep, match?.tutorialHint?.message]);

  useEffect(() => {
    if (!match?.tutorial) return;
    if (!traitChoiceModalReady) return;
    setTutorialHintMinimized(true);
  }, [match?.tutorial, traitChoiceModalReady]);

  useEffect(() => {
    if (!match?.tutorial) return;
    if (
      selectedHand ||
      selectedEquipmentId ||
      handMenu ||
      boardMenu ||
      slottedAbilityExam
    ) {
      setTutorialHintMinimized(true);
    }
  }, [
    match?.tutorial,
    selectedHand,
    selectedEquipmentId,
    handMenu,
    boardMenu,
    slottedAbilityExam,
  ]);

  useEffect(() => {
    if (view !== "workshop") {
      setWorkshopControlsHidden(false);
    }
  }, [view]);

  useEffect(() => {
    setOpponentBoardExpanded(false);
    suppressOpponentAutoViewRef.current = false;
    wasOpponentTurnRef.current = false;
    prevMatchPhaseRef.current = match?.phase || null;
    if (match && match.phase !== "ended" && !match.workshop) {
      if (adventureCombatSummaryActive(player?.adventureRun)) {
        return;
      }
      if (matchIntroSeenRef.current.has(match.id)) {
        setView((current) => {
          if (current === "replay" || current === "matchIntro") return current;
          return "match";
        });
        return;
      }
      if (shouldShowQueueMatchIntro(match)) {
        matchIntroSeenRef.current.add(match.id);
        const opponentName =
          match.players?.find((name) => name !== username) ||
          match.players?.[1] ||
          "Opponent";
        const selfSeat = match.seats?.[username] || null;
        const opponentSeat = match.seats?.[opponentName] || null;
        setMatchIntro({
          matchId: match.id,
          selfUsername: username,
          opponentUsername: opponentName,
          selfAspectId: selfSeat?.aspectId || null,
          selfAspectFoil: selfSeat?.aspectFoil || null,
          opponentAspectId: opponentSeat?.aspectId || null,
          opponentAspectFoil: opponentSeat?.aspectFoil || null,
          selfNameColorId:
            match.nameColors?.[username]?.id ||
            normalizeNameColorId(player?.nameColor),
          opponentNameColorId:
            match.nameColors?.[opponentName]?.id || DEFAULT_NAME_COLOR_ID,
        });
        if (viewRef.current !== "replay") holdToastsForIntro();
        setView((current) => (current === "replay" ? current : "matchIntro"));
        return;
      }
      setView((current) => {
        if (current === "replay") return current;
        return "match";
      });
    }
  }, [match?.id, player?.adventureRun?.phase, player?.adventureRun?.combatSummary, player?.nameColor, username]);

  useEffect(() => {
    if (view !== "matchIntro" || !matchIntro) return undefined;
    const timer = window.setTimeout(() => {
      setMatchIntro(null);
      setView((current) => (current === "matchIntro" ? "match" : current));
      releaseToastIntroHold();
    }, MATCH_INTRO_MS);
    return () => window.clearTimeout(timer);
  }, [view, matchIntro?.matchId]);

  useEffect(() => {
    if (!queued || draftQueued) {
      queueEnteredAtRef.current = null;
      setQueueElapsedSec(0);
      return undefined;
    }
    if (!queueEnteredAtRef.current) {
      queueEnteredAtRef.current = Date.now();
    }
    const tick = () => {
      const startedAt = queueEnteredAtRef.current || Date.now();
      setQueueElapsedSec(
        Math.max(0, Math.floor((Date.now() - startedAt) / 1000)),
      );
    };
    tick();
    const timer = window.setInterval(tick, 250);
    return () => window.clearInterval(timer);
  }, [queued, draftQueued]);

  useEffect(() => {
    if (match?.spectating) {
      prevMatchPhaseRef.current = match?.phase || null;
      return undefined;
    }
    if (match?.workshop) {
      prevMatchPhaseRef.current = match?.phase || null;
      return undefined;
    }

    const clearPostCombatToastHold = () => {
      if (postCombatToastHoldTimerRef.current) {
        window.clearInterval(postCombatToastHoldTimerRef.current);
        postCombatToastHoldTimerRef.current = null;
      }
      postCombatToastHoldActiveRef.current = false;
      postCombatToastHoldTargetRef.current = null;
      setPostCombatToastHold(false);
    };

    const beginPostCombatToastHold = (targetView) => {
      if (postCombatToastHoldActiveRef.current) {
        postCombatToastHoldTargetRef.current = targetView;
        return;
      }
      postCombatToastHoldActiveRef.current = true;
      postCombatToastHoldTargetRef.current = targetView;
      setPostCombatToastHold(true);
      setView((current) => (current === "replay" ? current : "match"));
      const startedAt = Date.now();
      const minMs = 2200;
      const maxMs = 3500;
      const settleMs = 350;
      let settledAt = null;
      if (postCombatToastHoldTimerRef.current) {
        window.clearInterval(postCombatToastHoldTimerRef.current);
      }
      postCombatToastHoldTimerRef.current = window.setInterval(() => {
        const elapsed = Date.now() - startedAt;
        const toastsClear =
          !toastActiveRef.current && toastQueueRef.current.length === 0;
        if (toastsClear) {
          if (settledAt == null) settledAt = Date.now();
        } else {
          settledAt = null;
        }
        const settledLongEnough =
          settledAt != null && Date.now() - settledAt >= settleMs;
        if ((elapsed >= minMs && settledLongEnough) || elapsed >= maxMs) {
          const nextView = postCombatToastHoldTargetRef.current || targetView;
          if (postCombatToastHoldTimerRef.current) {
            window.clearInterval(postCombatToastHoldTimerRef.current);
            postCombatToastHoldTimerRef.current = null;
          }
          postCombatToastHoldActiveRef.current = false;
          postCombatToastHoldTargetRef.current = null;
          setPostCombatToastHold(false);
          setView((current) =>
            current === "replay" ? current : nextView,
          );
        }
      }, 100);
    };

    const adventureSummaryReady = adventureCombatSummaryActive(
      player?.adventureRun,
    );
    const matchEnded = Boolean(
      player?.matchSummary ||
        match?.phase === "ended" ||
        adventureSummaryReady,
    );

    if (matchEnded) {
      const endedBy =
        player?.matchSummary?.endedBy ||
        match?.endedBy ||
        lastLiveMatchRef.current?.endedBy ||
        "combat";
      const targetView =
        adventureSummaryReady || (match?.adventure && match?.phase === "ended")
          ? "adventureSummary"
          : "summary";
      const previousPhase = prevMatchPhaseRef.current;
      prevMatchPhaseRef.current = match?.phase || "ended";
      const justEndedInCombat =
        endedBy !== "forfeit" &&
        (endedBy === "combat" ||
          endedBy === "draw" ||
          targetView === "adventureSummary") &&
        previousPhase &&
        previousPhase !== "ended";
      const isTutorialEnd =
        endedBy === "tutorialComplete" ||
        Boolean(match?.tutorial) ||
        Boolean(player?.matchSummary?.isTutorial);

      if (postCombatToastHoldActiveRef.current) {
        postCombatToastHoldTargetRef.current = targetView;
        return undefined;
      }

      if (justEndedInCombat) {
        beginPostCombatToastHold(targetView);
        return undefined;
      }

      if (isTutorialEnd) {
        return undefined;
      }

      clearPostCombatToastHold();
      setView((current) => (current === "replay" ? current : targetView));
      return undefined;
    }

    if (postCombatToastHoldActiveRef.current && match && match.phase !== "ended") {
      clearPostCombatToastHold();
    }

    prevMatchPhaseRef.current = match?.phase || null;
    if (match && match.phase !== "ended") {
      return undefined;
    }
    setView((current) => {
      if (current === "replay") return current;
      if (adventureCombatSummaryActive(player?.adventureRun)) {
        return "adventureSummary";
      }
      if (current === "summary") {
        return "menu";
      }
      if (
        current === "match" ||
        current === "workshop" ||
        current === "adventure"
      ) {
        return player?.draftRun?.phase === "complete"
          ? "draftSummary"
          : player?.adventureRun
            ? "adventure"
            : "menu";
      }
      return current;
    });
    return undefined;
  }, [player?.matchSummary, match?.id, match?.phase, match?.endedBy, match?.workshop, match?.spectating, match?.adventure, player?.draftRun?.phase, player?.adventureRun?.phase, player?.adventureRun?.combatSummary]);

  useEffect(() => {
    return () => {
      if (postCombatToastHoldTimerRef.current) {
        window.clearInterval(postCombatToastHoldTimerRef.current);
        postCombatToastHoldTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (match?.adventure && match.phase !== "ended") {
      if (adventureCombatSummaryActive(player?.adventureRun)) return;
      setView((current) =>
        current === "replay" ||
        current === "summary" ||
        current === "adventureSummary"
          ? current
          : "match",
      );
    }
  }, [match?.id, match?.adventure, match?.phase, player?.adventureRun?.phase, player?.adventureRun?.combatSummary]);

  useEffect(() => {
    const run = player?.adventureRun;
    if (!run || run.phase !== "reward") {
      setAdventureTreasureTakeIds([]);
      return;
    }
    setAdventureTreasureTakeIds([]);
  }, [
    player?.adventureRun?.phase,
    player?.adventureRun?.encounter?.nodeId,
  ]);

  useEffect(() => {
    if (view !== "workshop") {
      setWorkshopPicker(false);
    }
  }, [view]);

  useEffect(() => {
    if (!match || match.phase === "ended" || view !== "match") {
      setForfeitConfirm(false);
    }
  }, [match?.id, match?.phase, view]);

  useEffect(() => {
    setKeywordTip(null);
    setExamArtPreview(null);
  }, [handMenu, boardMenu, selectedEquipmentId, slottedAbilityExam, examCardId, slotPicker, workshopPicker]);

  useEffect(() => {
    setEquipExpendConfirm(null);
    setEquipExpendConfirmPopupStyle(null);
    setEquipCounterExam(null);
    setEquipCounterExamPopupStyle(null);
  }, [
    match?.id,
    match?.turn,
    match?.phase,
    handMenu,
    boardMenu,
    selectedEquipmentId,
    slottedAbilityExam,
  ]);

  useLayoutEffect(() => {
    if (!xpQuestsOpen) {
      setXpQuestsBottomInset(0);
      return undefined;
    }
    const updateInset = () => {
      const dock = matchBottomDockRef.current;
      if (!dock || !dock.isConnected) {
        setXpQuestsBottomInset(0);
        return;
      }
      const rect = dock.getBoundingClientRect();
      if (rect.height <= 0) {
        setXpQuestsBottomInset(0);
        return;
      }
      const inset = Math.max(
        0,
        Math.min(
          Math.round(window.innerHeight - rect.top),
          window.innerHeight - 240,
        ),
      );
      setXpQuestsBottomInset((prev) => (prev === inset ? prev : inset));
    };
    updateInset();
    const dock = matchBottomDockRef.current;
    let resizeObserver = null;
    if (dock && typeof ResizeObserver === "function") {
      resizeObserver = new ResizeObserver(updateInset);
      resizeObserver.observe(dock);
    }
    window.addEventListener("resize", updateInset);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateInset);
    };
  }, [xpQuestsOpen]);

  useLayoutEffect(() => {
    if (!equipExpendConfirm) {
      setEquipExpendConfirmPopupStyle(null);
      return undefined;
    }
    const updatePopupPosition = () => {
      const popup = equipExpendConfirmPopupRef.current;
      if (!popup || !equipExpendConfirm?.rect) return;
      const popupRect = popup.getBoundingClientRect();
      setEquipExpendConfirmPopupStyle(
        computeEquipExpendConfirmPopupStyle(
          equipExpendConfirm.rect,
          popupRect.width,
          popupRect.height,
        ),
      );
    };
    updatePopupPosition();
    window.addEventListener("resize", updatePopupPosition);
    window.addEventListener("scroll", updatePopupPosition, true);
    return () => {
      window.removeEventListener("resize", updatePopupPosition);
      window.removeEventListener("scroll", updatePopupPosition, true);
    };
  }, [equipExpendConfirm]);

  useLayoutEffect(() => {
    if (!equipCounterExam) {
      setEquipCounterExamPopupStyle(null);
      return undefined;
    }
    const updatePopupPosition = () => {
      const popup = equipCounterExamPopupRef.current;
      if (!popup || !equipCounterExam?.rect) return;
      const popupRect = popup.getBoundingClientRect();
      setEquipCounterExamPopupStyle(
        computeEquipExpendConfirmPopupStyle(
          equipCounterExam.rect,
          popupRect.width,
          popupRect.height,
        ),
      );
    };
    updatePopupPosition();
    window.addEventListener("resize", updatePopupPosition);
    window.addEventListener("scroll", updatePopupPosition, true);
    return () => {
      window.removeEventListener("resize", updatePopupPosition);
      window.removeEventListener("scroll", updatePopupPosition, true);
    };
  }, [equipCounterExam]);

  useEffect(() => {
    if (!selectedEquipmentId) return;
    setSlottedAbilityExam(null);
  }, [selectedEquipmentId]);

  const clearPickerFilters = () => {
    setPickerSearch("");
    setPickerAttr("all");
    setPickerSlot("all");
    setPickerItemType("all");
    setPickerTier("all");
    setPickerKind("all");
    setPickerType("all");
    setPickerEffect("all");
    setPickerCost("all");
    setPickerHolo("all");
    setPickerSort("name");
    setPickerSuggest(false);
  };

  const clearCatalogFilters = () => {
    setCatalogCategory("all");
    setFilterType("all");
    setFilterAttr("all");
    setCatalogSearch("");
    setCatalogSlot("all");
    setCatalogItemType("all");
    setCatalogTier("all");
    setCatalogKind("all");
    setCatalogEffect("all");
    setCatalogCost("all");
    setCatalogHolo("all");
    setCatalogOwned("all");
    setCatalogSort("name");
  };

  const catalogCategoryCards = useMemo(
    () =>
      catalogCategory === "all"
        ? catalog || []
        : (catalog || []).filter(
            (card) => cardCatalogCategory(card) === catalogCategory,
          ),
    [catalog, catalogCategory],
  );

  const catalogEffectOptions = useMemo(
    () => listEffectFilterOptions(catalogCategoryCards),
    [catalogCategoryCards],
  );

  const catalogSlotOptions = useMemo(
    () =>
      [
        ...new Set(
          catalogCategoryCards.map((card) => card.slot).filter(Boolean),
        ),
      ].sort(
        (a, b) => (SLOT_SORT_ORDER[a] ?? 99) - (SLOT_SORT_ORDER[b] ?? 99),
      ),
    [catalogCategoryCards],
  );

  const catalogTypeOptions = useMemo(
    () => buildCardTypeFilterOptions(catalogCategoryCards),
    [catalogCategoryCards],
  );

  const catalogItemTypeOptions = useMemo(
    () => buildEquipmentItemTypeFilterOptions(catalogCategoryCards),
    [catalogCategoryCards],
  );

  // Item type and tier only apply to equipment.
  const catalogShowsEquipmentFilters =
    catalogCategory === "all" || catalogCategory === "equipment";

  const playerHasAnyHolo = useMemo(
    () =>
      Object.values(player?.unlockedHolos || {}).some(
        (list) => Array.isArray(list) && list.length > 0,
      ),
    [player?.unlockedHolos],
  );

  const catalogSortOptions = useMemo(() => {
    const t = createTranslator(language);
    const options = [{ value: "name", label: t("deck.sort.name") }];
    if (catalogCategory === "all") {
      options.push({ value: "category", label: t("deck.sort.category") });
    }
    if (catalogCategory !== "aspect") {
      options.push({ value: "type", label: t("deck.sort.type") });
    }
    options.push({ value: "attribute", label: t("deck.sort.attribute") });
    if (catalogCategory !== "aspect") {
      options.push({
        value: "cost",
        label: catalogCategory === "equipment" ? t("deck.sort.resources") : t("deck.sort.cost"),
      });
    }
    options.push({ value: "power", label: t("deck.sort.power") });
    if (catalogCategory === "equipment") {
      options.push({ value: "slot", label: t("deck.sort.slot") });
      options.push({ value: "tier", label: t("deck.sort.tier") });
    }
    if (catalogCategory === "ability") {
      options.push({ value: "kind", label: t("deck.sort.kind") });
    }
    if (playerHasAnyHolo) {
      options.push({ value: "holo", label: t("deck.sort.holo") });
    }
    return options;
  }, [catalogCategory, playerHasAnyHolo, language]);

  const effectiveCatalogSort = catalogSortOptions.some(
    (option) => option.value === catalogSort,
  )
    ? catalogSort
    : "name";

  const changeCatalogCategory = (category) => {
    setCatalogCategory(category);
    setFilterType("all");
    setCatalogEffect("all");
    if (category !== "equipment") setCatalogSlot("all");
    if (category !== "equipment" && category !== "all") {
      setCatalogItemType("all");
      setCatalogTier("all");
    }
    if (category !== "ability") setCatalogKind("all");
    if (category === "aspect") setCatalogCost("all");
  };

  const filteredCatalog = useMemo(() => {
    const searchQuery = catalogSearch.trim().toLowerCase();
    return catalogCategoryCards
      .filter((card) => {
        if (filterType !== "all") {
          if (filterType === "plain") {
            if (cardHasType(card, "attack") || cardHasType(card, "magic")) {
              return false;
            }
          } else if (!cardMatchesTypeFilter(card, filterType)) {
            return false;
          }
        }
        if (filterAttr !== "all" && !cardMatchesAttributeFilter(card, filterAttr)) {
          return false;
        }
        if (catalogSlot !== "all") {
          if (!cardHasType(card, "equipment") || card.slot !== catalogSlot) {
            return false;
          }
        }
        if (catalogItemType !== "all" && !cardHasType(card, catalogItemType)) {
          return false;
        }
        if (!cardMatchesEquipmentTierFilter(card, catalogTier)) {
          return false;
        }
        if (catalogKind !== "all") {
          if (!cardHasType(card, "ability") || card.kind !== catalogKind) {
            return false;
          }
        }
        if (catalogEffect !== "all") {
          if (!cardMatchesEffectFilter(card, catalogEffect)) return false;
        }
        if (catalogCost !== "all") {
          if (cardCatalogCategory(card) === "aspect") return false;
          const total = cardCostTotal(card);
          if (catalogCost === "3+") {
            if (total < 3) return false;
          } else if (total !== Number(catalogCost)) {
            return false;
          }
        }
        if (catalogHolo === "holo" && playerHasAnyHolo) {
          if (catalogHoloCount(card.id, player?.unlockedHolos) <= 0) {
            return false;
          }
        }
        if (catalogOwned === "unlocked") {
          if (!playerOwnsCard(player?.collection, card.id)) return false;
        } else if (catalogOwned === "locked") {
          if (playerOwnsCard(player?.collection, card.id)) return false;
        }
        if (searchQuery && !cardSearchHaystack(card).includes(searchQuery)) {
          return false;
        }
        return true;
      })
      .sort((a, b) =>
        compareCatalogCards(
          a,
          b,
          effectiveCatalogSort,
          player?.unlockedHolos,
          player?.collection,
        ),
      );
  }, [
    catalogCategoryCards,
    playerHasAnyHolo,
    effectiveCatalogSort,
    filterType,
    filterAttr,
    catalogSearch,
    catalogSlot,
    catalogItemType,
    catalogTier,
    catalogKind,
    catalogEffect,
    catalogCost,
    catalogHolo,
    catalogOwned,
    player?.unlockedHolos,
    player?.collection,
  ]);

  useEffect(() => {
    setCatalogPage(0);
  }, [
    catalogCategory,
    filterType,
    filterAttr,
    catalogSearch,
    catalogSlot,
    catalogItemType,
    catalogTier,
    catalogKind,
    catalogEffect,
    catalogCost,
    catalogHolo,
    catalogOwned,
    catalogSort,
    catalogPageSize,
  ]);

  const catalogPageCount =
    catalogPageSize === "all"
      ? 1
      : Math.max(1, Math.ceil(filteredCatalog.length / catalogPageSize));
  const currentCatalogPage = Math.min(catalogPage, catalogPageCount - 1);

  const pagedCatalog = useMemo(() => {
    if (catalogPageSize === "all") return filteredCatalog;
    const start = currentCatalogPage * catalogPageSize;
    return filteredCatalog.slice(start, start + catalogPageSize);
  }, [filteredCatalog, catalogPageSize, currentCatalogPage]);

  useEffect(() => {
    if (view !== "collection" || catalogPageSize === "all") return undefined;
    const urls = [
      currentCatalogPage + 1,
      currentCatalogPage - 1,
      currentCatalogPage + 2,
    ]
      .filter((page) => page >= 0 && page < catalogPageCount)
      .flatMap((page) =>
        filteredCatalog
          .slice(page * catalogPageSize, (page + 1) * catalogPageSize)
          .map(getCardImageUrl),
      )
      .filter(Boolean);
    if (!urls.length) return undefined;
    if (typeof window.requestIdleCallback === "function") {
      const idleId = window.requestIdleCallback(() => warmImages(urls), {
        timeout: 600,
      });
      return () => window.cancelIdleCallback(idleId);
    }
    const timer = window.setTimeout(() => warmImages(urls), 150);
    return () => window.clearTimeout(timer);
  }, [
    view,
    filteredCatalog,
    catalogPageSize,
    catalogPageCount,
    currentCatalogPage,
  ]);

  const deferredGlossarySearch = useDeferredValue(glossarySearch);
  const glossaryEntries = useMemo(() => {
    const query = deferredGlossarySearch.trim().toLowerCase();
    if (!query) return GLOSSARY_ENTRIES;
    return GLOSSARY_ENTRIES.filter((entry) => entry.searchText.includes(query));
  }, [deferredGlossarySearch]);
  const glossarySelectedEntry = useMemo(() => {
    if (!glossarySelectedId) return null;
    return (
      GLOSSARY_ENTRIES.find((entry) => entry.id === glossarySelectedId) || null
    );
  }, [glossarySelectedId]);
  const glossaryRelatedCards = useMemo(() => {
    if (!glossarySelectedEntry) return [];
    return (catalog || [])
      .filter((card) => cardReferencesKeyword(card, glossarySelectedEntry))
      .sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || "")),
      );
  }, [catalog, glossarySelectedEntry]);

  const setDraftSlot = (deckType, index, cardId, foilEffect = null) => {
    if (deckType === "aspect") {
      setDraftAspect(cardId || null);
      setDraftAspectFoil(cardId ? foilEffect || null : null);
      return;
    }
    if (deckType === "equipment") {
      const nextIds = padDeckSlots(draftEquipment);
      const nextFoils = padFoilSlots(draftEquipmentFoils);
      nextIds[index] = cardId;
      nextFoils[index] = cardId ? foilEffect || null : null;
      const compactedFoils = [];
      nextIds.forEach((id, slotIndex) => {
        if (typeof id === "string") {
          compactedFoils.push(nextFoils[slotIndex] || null);
        }
      });
      setDraftEquipment(padDeckSlots(nextIds));
      setDraftEquipmentFoils(padFoilSlots(compactedFoils));
      return;
    }
    const nextIds = padDeckSlots(draftAbility);
    const nextFoils = padFoilSlots(draftAbilityFoils);
    nextIds[index] = cardId;
    nextFoils[index] = cardId ? foilEffect || null : null;
    const compactedFoils = [];
    nextIds.forEach((id, slotIndex) => {
      if (typeof id === "string") {
        compactedFoils.push(nextFoils[slotIndex] || null);
      }
    });
    setDraftAbility(padDeckSlots(nextIds));
    setDraftAbilityFoils(padFoilSlots(compactedFoils));
  };

  const setAdventureSlot = (deckType, index, cardId) => {
    if (deckType === "aspect") {
      setAdventureAspect(cardId || DEFAULT_ASPECT_ID);
      return;
    }
    if (deckType === "equipment") {
      setAdventureEquipment((current) => {
        const next = padDeckSlots(current);
        next[index] = cardId;
        return padDeckSlots(next);
      });
      return;
    }
    setAdventureAbility((current) => {
      const next = padDeckSlots(current);
      next[index] = cardId;
      return padDeckSlots(next);
    });
  };

  const clearDeckSlot = (deckType, index) => {
    if (view === "adventure") {
      setAdventureSlot(deckType, index, null);
      return;
    }
    setDraftSlot(deckType, index, null, null);
  };

  const saveDeck = (overrides = {}) => {
    const equipmentIds = filledDeckIds(
      overrides.equipment || draftEquipment,
    );
    const abilityIds = filledDeckIds(overrides.ability || draftAbility);
    const aspectId =
      typeof overrides.aspectId === "string"
        ? overrides.aspectId
        : draftAspect;
    const aspectFoil =
      overrides.aspectFoil !== undefined ? overrides.aspectFoil : draftAspectFoil;
    if (
      equipmentIds.length !== MIN_DECK ||
      abilityIds.length !== MIN_DECK ||
      !aspectId ||
      !deckUsesOnlyOwnedCards(
        equipmentIds,
        abilityIds,
        aspectId,
        player?.collection,
      ) ||
      !isDeckPairWithinPower(equipmentIds, abilityIds, aspectId, catalogMap)
    ) {
      return false;
    }
    const deckId =
      overrides.deckId || editingDeckId || player?.activeDeckPairId;
    if (!deckId) return false;
    const name =
      typeof overrides.name === "string"
        ? overrides.name.trim().slice(0, 40) || "Deck"
        : (draftName || "Deck").trim().slice(0, 40) || "Deck";
    const equipmentFoils = filledDeckFoils(
      overrides.equipment || draftEquipment,
      overrides.equipmentFoils !== undefined
        ? overrides.equipmentFoils
        : draftEquipmentFoils,
    );
    const abilityFoils = filledDeckFoils(
      overrides.ability || draftAbility,
      overrides.abilityFoils !== undefined
        ? overrides.abilityFoils
        : draftAbilityFoils,
    );
    send({
      type: "ai:saveDeckPair",
      deckId,
      name,
      equipmentIds,
      abilityIds,
      aspectId,
      aspectFoil: aspectFoil || null,
      equipmentFoils,
      abilityFoils,
    });
    return true;
  };

  const getCurrentDeckRecommendBlocker = () =>
    getDeckRecommendBlocker({
      equipment: draftEquipment,
      ability: draftAbility,
      aspectId: draftAspect,
      catalog: catalogMap,
      collection: player?.collection,
      maxPower: MAX_DECK_POWER,
    });

  const buildCurrentDeckRecommendKey = (catalogKey, ownershipKey) =>
    buildRecommendationKey({
      equipment: draftEquipment,
      ability: draftAbility,
      aspectId: draftAspect,
      catalogKey,
      ownershipKey,
    });

  const scheduleDeckRecommend = (
    baseline,
    shownSignatures,
    previousProposal,
    requestId,
  ) => {
    window.clearTimeout(deckRecommendTimerRef.current);
    deckRecommendTimerRef.current = window.setTimeout(() => {
      deckRecommendTimerRef.current = null;
      if (deckRecommendRequestRef.current !== requestId) return;
      const proposal = recommendDeckChanges({
        equipmentIds: filledDeckIds(baseline.equipment),
        abilityIds: filledDeckIds(baseline.ability),
        aspectId: baseline.aspectId,
        catalog: baseline.catalog,
        collection: baseline.collection,
        excludeSignatures: shownSignatures,
        maxPower: MAX_DECK_POWER,
      });
      setDeckRecommend((current) => {
        if (!current || current.requestId !== requestId) return current;
        if (proposal) {
          return {
            ...current,
            status: "ready",
            proposal,
            exhausted: false,
            shownSignatures: [...shownSignatures, proposal.signature],
          };
        }
        if (previousProposal) {
          return {
            ...current,
            status: "ready",
            proposal: previousProposal,
            exhausted: true,
          };
        }
        return { ...current, status: "empty", proposal: null, exhausted: false };
      });
    }, 40);
  };

  const openDeckRecommend = () => {
    const blocker = getCurrentDeckRecommendBlocker();
    if (blocker) {
      setDeckRecommendHintOpen(true);
      showToast(blocker);
      return;
    }
    setDeckRecommendHintOpen(false);
    setDeleteDeckConfirm(false);
    setDeckAnalyzeOpen(false);
    setSlotPicker(null);
    const collection = player?.collection || {};
    const baseline = {
      equipment: [...draftEquipment],
      ability: [...draftAbility],
      aspectId: draftAspect,
      catalog,
      collection,
    };
    const key = buildCurrentDeckRecommendKey(
      catalogFingerprint(catalog),
      ownershipFingerprint(collection),
    );
    deckRecommendRequestRef.current += 1;
    const requestId = deckRecommendRequestRef.current;
    setDeckRecommend({
      baseline,
      key,
      requestId,
      status: "loading",
      proposal: null,
      exhausted: false,
      shownSignatures: [],
    });
    scheduleDeckRecommend(baseline, [], null, requestId);
  };

  const retryDeckRecommend = () => {
    if (!deckRecommend || deckRecommend.status === "loading") return;
    deckRecommendRequestRef.current += 1;
    const requestId = deckRecommendRequestRef.current;
    const previousProposal = deckRecommend.proposal;
    setDeckRecommend({
      ...deckRecommend,
      requestId,
      status: "loading",
      exhausted: false,
    });
    scheduleDeckRecommend(
      deckRecommend.baseline,
      deckRecommend.shownSignatures,
      previousProposal,
      requestId,
    );
  };

  const closeDeckRecommend = () => {
    window.clearTimeout(deckRecommendTimerRef.current);
    deckRecommendTimerRef.current = null;
    deckRecommendRequestRef.current += 1;
    setDeckRecommend(null);
  };

  const applyDeckRecommend = () => {
    const proposal = deckRecommend?.proposal;
    if (!proposal || deckRecommend.status !== "ready") return;
    const liveKey = buildCurrentDeckRecommendKey(
      catalogFingerprint(catalog),
      ownershipFingerprint(player?.collection),
    );
    if (liveKey !== deckRecommend.key) {
      showToast(t("game.deck.recommendStale"));
      return;
    }
    const validation = validateRecommendedDeck({
      equipmentIds: proposal.equipmentIds,
      abilityIds: proposal.abilityIds,
      aspectId: proposal.aspectId,
      expectedAspectId: draftAspect,
      catalog: catalogMap,
      collection: player?.collection,
      maxPower: MAX_DECK_POWER,
    });
    if (!validation.ok) {
      showToast(validation.message || t("game.deck.recommendInvalid"));
      return;
    }
    const next = applyRecommendationToDraft(
      {
        equipment: draftEquipment,
        ability: draftAbility,
        equipmentFoils: draftEquipmentFoils,
        abilityFoils: draftAbilityFoils,
      },
      proposal,
    );
    if (!next) {
      showToast(t("game.deck.recommendStale"));
      return;
    }
    setDraftEquipment(padDeckSlots(next.equipment));
    setDraftAbility(padDeckSlots(next.ability));
    setDraftEquipmentFoils(padFoilSlots(next.equipmentFoils));
    setDraftAbilityFoils(padFoilSlots(next.abilityFoils));
    setSlotPicker(null);
    closeDeckRecommend();
    showToast(
      t("game.deck.recommendApplied", { count: proposal.swapCount }),
    );
  };

  deckRecommendEscapeRef.current = () => {
    if (examCardId) {
      closeCardExam();
      return;
    }
    closeDeckRecommend();
  };

  const deleteEditingDeck = () => {
    if (!editingDeckId) return;
    if ((player?.deckPairs || []).length <= 1) return;
    if (!(player?.deckPairs || []).some((pair) => pair.id === editingDeckId)) {
      return;
    }
    send({
      type: "ai:deleteDeckPair",
      deckId: editingDeckId,
    });
    setDeleteDeckConfirm(false);
  };

  const startNewDeck = () => {
    if ((player?.deckPairs || []).length >= MAX_DECK_PAIRS) return;
    const deckId = `pair_${Date.now()}`;
    setDeleteDeckConfirm(false);
    setUnsavedNewDeck(true);
    setEditingDeckId(deckId);
    setDraftName("New Deck");
    setDraftEquipment(padDeckSlots([]));
    setDraftAbility(padDeckSlots([]));
    setDraftAspect(DEFAULT_ASPECT_ID);
    setDraftAspectFoil(null);
    setDraftEquipmentFoils(padFoilSlots([]));
    setDraftAbilityFoils(padFoilSlots([]));
    setSlotPicker(null);
    setDeckVariantPicker(null);
  };

  const cloneCurrentDeck = () => {
    if (
      !isClassicDeckValid(
        draftEquipment,
        draftAbility,
        draftAspect,
        catalogMap,
        player?.collection,
      )
    ) {
      return false;
    }
    if ((player?.deckPairs || []).length >= MAX_DECK_PAIRS) return false;
    const deckId = `pair_${Date.now()}`;
    const baseName = (draftName || "Deck").trim().slice(0, 40) || "Deck";
    const copyName = `${baseName.slice(0, 35)}_copy`;
    setUnsavedNewDeck(true);
    setEditingDeckId(deckId);
    setDraftName(copyName);
    return saveDeck({ deckId, name: copyName });
  };

  const openDeckExport = () => {
    const equipmentIds = filledDeckIds(draftEquipment);
    const abilityIds = filledDeckIds(draftAbility);
    if (
      equipmentIds.length !== MIN_DECK ||
      abilityIds.length !== MIN_DECK ||
      !draftAspect ||
      !deckUsesOnlyOwnedCards(
        equipmentIds,
        abilityIds,
        draftAspect,
        player?.collection,
      ) ||
      !isDeckPairWithinPower(
        equipmentIds,
        abilityIds,
        draftAspect,
        catalogMap,
      )
    ) {
      return;
    }
    setDeckShare({
      mode: "export",
      text: JSON.stringify(
        buildDeckSharePayload(draftName, equipmentIds, abilityIds, draftAspect),
        null,
        2,
      ),
      error: null,
      copied: false,
    });
  };

  const openLibraryDeckExport = (pair) => {
    if (!pair) return;
    const equipmentIds = filledDeckIds(pair.equipmentIds);
    const abilityIds = filledDeckIds(pair.abilityIds);
    const aspectId = pair.aspectId || DEFAULT_ASPECT_ID;
    if (
      equipmentIds.length !== MIN_DECK ||
      abilityIds.length !== MIN_DECK ||
      !aspectId ||
      !deckUsesOnlyOwnedCards(
        equipmentIds,
        abilityIds,
        aspectId,
        player?.collection,
      ) ||
      !isDeckPairWithinPower(equipmentIds, abilityIds, aspectId, catalogMap)
    ) {
      return;
    }
    setDeckShare({
      mode: "export",
      text: JSON.stringify(
        buildDeckSharePayload(pair.name, equipmentIds, abilityIds, aspectId),
        null,
        2,
      ),
      error: null,
      copied: false,
    });
  };

  const openDeckImport = () => {
    if ((player?.deckPairs || []).length >= MAX_DECK_PAIRS) return;
    setDeckShare({
      mode: "import",
      text: "",
      error: null,
      copied: false,
    });
  };

  const closeDeckShare = () => {
    setDeckShare(null);
  };

  const copyDeckShareText = async () => {
    const text = deckShare?.text || "";
    if (!text) return;
    let copied = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        copied = true;
      }
    } catch {
      copied = false;
    }
    if (!copied) {
      const area = deckShareTextareaRef.current;
      if (!area) return;
      area.focus();
      area.select();
      copied = document.execCommand("copy");
    }
    if (!copied) return;
    setDeckShare((current) =>
      current ? { ...current, copied: true, error: null } : current,
    );
  };

  const importSharedDeck = () => {
    if ((player?.deckPairs || []).length >= MAX_DECK_PAIRS) {
      setDeckShare((current) =>
        current
          ? { ...current, error: t("game.deck.limitReached"), copied: false }
          : current,
      );
      return;
    }
    const parsed = parseDeckShareText(deckShare?.text, catalogMap, {
      collection: player?.collection,
    });
    if (!parsed.ok) {
      setDeckShare((current) =>
        current ? { ...current, error: parsed.message, copied: false } : current,
      );
      return;
    }
    const deckId = `pair_${Date.now()}`;
    setUnsavedNewDeck(true);
    setEditingDeckId(deckId);
    setDraftName(parsed.name);
    setDraftEquipment(padDeckSlots(parsed.equipmentIds));
    setDraftAbility(padDeckSlots(parsed.abilityIds));
    setDraftAspect(parsed.aspectId || DEFAULT_ASPECT_ID);
    setDraftAspectFoil(null);
    setDraftEquipmentFoils(padFoilSlots([]));
    setDraftAbilityFoils(padFoilSlots([]));
    setSlotPicker(null);
    setDeckVariantPicker(null);
    saveDeck({
      deckId,
      name: parsed.name,
      equipment: parsed.equipmentIds,
      ability: parsed.abilityIds,
      aspectId: parsed.aspectId || DEFAULT_ASPECT_ID,
      equipmentFoils: filledDeckFoils(parsed.equipmentIds, []),
      abilityFoils: filledDeckFoils(parsed.abilityIds, []),
    });
    setDeckShare(null);
    showToast(t("game.deck.imported", { name: parsed.name }));
  };

  const pickSlotCard = (card) => {
    if (!slotPicker) return;
    const { deckType, index } = slotPicker;
    if (deckType === "aspect") {
      if (!cardHasType(card, "aspect")) return;
      if (view === "draftDeck") {
        const poolAspects = [
          ...(player?.draftRun?.pool?.aspectIds || []),
          player?.draftRun?.pool?.aspectId,
        ].filter(Boolean);
        if (poolAspects.length && !poolAspects.includes(card.id)) return;
      }
      if (view === "adventure") {
        setAdventureSlot("aspect", 0, card.id);
      } else {
        const unlocked =
          view === "deck" ? player?.unlockedHolos?.[card.id] || [] : [];
        if (view === "deck" && unlocked.length > 0) {
          setDeckVariantPicker({
            deckType,
            index,
            cardId: card.id,
            foilEffect: null,
          });
          return;
        }
        setDraftSlot("aspect", 0, card.id, null);
      }
      setSlotPicker(null);
      return;
    }
    const deckSource =
      view === "adventure"
        ? deckType === "equipment"
          ? adventureEquipment
          : adventureAbility
        : deckType === "equipment"
          ? draftEquipment
          : draftAbility;
    const draft = padDeckSlots(deckSource);
    const counts = countIds(
      draft.map((id, slotIndex) => (slotIndex === index ? null : id)),
    );
    const poolIds =
      view === "adventure"
        ? deckType === "equipment"
          ? player?.adventureRun?.inventory?.equipmentIds || []
          : player?.adventureRun?.inventory?.abilityIds || []
        : view === "draftDeck"
        ? deckType === "equipment"
          ? player?.draftRun?.pool?.equipmentIds || []
          : player?.draftRun?.pool?.abilityIds || []
        : null;
    const limit = poolIds ? countIds(poolIds)[card.id] || 0 : MAX_COPIES;
    if ((counts[card.id] || 0) >= limit) return;
    if (view === "adventure") {
      setAdventureSlot(deckType, index, card.id);
      setSlotPicker(null);
      return;
    }
    const unlocked =
      view === "deck" ? player?.unlockedHolos?.[card.id] || [] : [];
    if (view === "deck" && unlocked.length > 0) {
      setDeckVariantPicker({
        deckType,
        index,
        cardId: card.id,
        foilEffect: null,
      });
      return;
    }
    setDraftSlot(deckType, index, card.id, null);
    setSlotPicker(null);
  };

  useEffect(() => {
    if (view !== "deck" || !editingDeckId) return undefined;
    if (nameInputFocused) return undefined;
    if (
      !isClassicDeckValid(
        draftEquipment,
        draftAbility,
        draftAspect,
        catalogMap,
        player?.collection,
      )
    ) {
      return undefined;
    }

    const equipmentIds = filledDeckIds(draftEquipment);
    const abilityIds = filledDeckIds(draftAbility);
    const equipmentFoils = filledDeckFoils(draftEquipment, draftEquipmentFoils);
    const abilityFoils = filledDeckFoils(draftAbility, draftAbilityFoils);
    const name = (draftName || "Deck").trim().slice(0, 40) || "Deck";
    const pair = (player?.deckPairs || []).find(
      (entry) => entry.id === editingDeckId,
    );
    if (
      pair &&
      pair.name === name &&
      deckIdsEqual(pair.equipmentIds || [], equipmentIds) &&
      deckIdsEqual(pair.abilityIds || [], abilityIds) &&
      (pair.aspectId || "") === (draftAspect || "") &&
      (pair.aspectFoil || null) === (draftAspectFoil || null) &&
      deckFoilsEqual(pair.equipmentFoils || [], equipmentFoils) &&
      deckFoilsEqual(pair.abilityFoils || [], abilityFoils)
    ) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      send({
        type: "ai:saveDeckPair",
        deckId: editingDeckId,
        name,
        equipmentIds,
        abilityIds,
        aspectId: draftAspect,
        aspectFoil: draftAspectFoil || null,
        equipmentFoils,
        abilityFoils,
      });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [
    view,
    editingDeckId,
    draftEquipment,
    draftAbility,
    draftAspect,
    draftAspectFoil,
    draftEquipmentFoils,
    draftAbilityFoils,
    draftName,
    nameInputFocused,
    player?.deckPairs,
    catalogMap,
  ]);

  useEffect(() => {
    if (view !== "draftDeck") return undefined;
    if (!isDraftDeckValid(draftEquipment, draftAbility, draftAspect)) return undefined;
    const equipmentIds = filledDeckIds(draftEquipment);
    const abilityIds = filledDeckIds(draftAbility);
    const run = player?.draftRun;
    if (!run) return undefined;
    if (
      deckIdsEqual(run.deck?.equipmentIds || [], equipmentIds) &&
      deckIdsEqual(run.deck?.abilityIds || [], abilityIds) &&
      (run.deck?.aspectId || "") === (draftAspect || "")
    ) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      send({
        type: "ai:draftSaveDeck",
        equipmentIds,
        abilityIds,
        aspectId: draftAspect,
      });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [view, draftEquipment, draftAbility, draftAspect, player?.draftRun]);

  useEffect(() => {
    if (view !== "draftDeck") return;
    const run = player?.draftRun;
    if (!run) return;
    setDraftEquipment(padDeckSlots(run.deck?.equipmentIds || []));
    setDraftAbility(padDeckSlots(run.deck?.abilityIds || []));
    setDraftAspect(
      run.deck?.aspectId ||
        run.pool?.aspectId ||
        (run.pool?.aspectIds || [])[0] ||
        DEFAULT_ASPECT_ID,
    );
    setDraftEquipmentFoils(padFoilSlots([]));
    setDraftAbilityFoils(padFoilSlots([]));
    setDraftName("Draft Deck");
    setSlotPicker(null);
    setDeckVariantPicker(null);
  }, [view, player?.draftRun?.id]);

  useEffect(() => {
    if (view !== "draft") setDraftedCardsOpen(false);
  }, [view]);

  useEffect(() => {
    const deck = player?.adventureRun?.deck;
    if (!deck) return;
    setAdventureEquipment(padDeckSlots(deck.equipmentIds || []));
    setAdventureAbility(padDeckSlots(deck.abilityIds || []));
    setAdventureAspect(deck.aspectId || DEFAULT_ASPECT_ID);
  }, [player?.adventureRun?.id, player?.adventureRun?.deck?.equipmentIds, player?.adventureRun?.deck?.abilityIds, player?.adventureRun?.deck?.aspectId]);

  useEffect(() => {
    if (view !== "adventure") return undefined;
    const equipmentIds = filledDeckIds(adventureEquipment);
    const abilityIds = filledDeckIds(adventureAbility);
    const run = player?.adventureRun;
    if (
      run &&
      run.deck?.equipmentIds?.join(",") === equipmentIds.join(",") &&
      run.deck?.abilityIds?.join(",") === abilityIds.join(",") &&
      (run.deck?.aspectId || "") === (adventureAspect || "")
    ) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      send({
        type: "ai:adventureSaveDeck",
        equipmentIds,
        abilityIds,
        aspectId: adventureAspect || DEFAULT_ASPECT_ID,
      });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [view, adventureEquipment, adventureAbility, adventureAspect, player?.adventureRun?.id]);

  useEffect(() => {
    if (!adventureCombatSummaryActive(player?.adventureRun)) {
      setAdventureCombatRewardTakeIds([]);
      setAdventureSummaryEnemyExamId(null);
    }
  }, [player?.adventureRun?.phase, player?.adventureRun?.combatSummary]);

  useEffect(() => {
    if (
      match?.adventure &&
      match?.phase === "ended" &&
      !adventureCombatSummaryActive(player?.adventureRun)
    ) {
      send({ type: "ai:getState" });
    }
  }, [
    match?.id,
    match?.adventure,
    match?.phase,
    player?.adventureRun?.phase,
    player?.adventureRun?.combatSummary,
  ]);

  useEffect(() => {
    if (view !== "adventureSummary") return;
    if (
      player?.adventureRun?.phase === "hub" ||
      !adventureCombatSummaryActive(player?.adventureRun)
    ) {
      setView("adventure");
    }
  }, [
    view,
    player?.adventureRun?.phase,
    player?.adventureRun?.combatSummary,
  ]);

  useEffect(() => {
    const postCombat = Boolean(
      adventureCombatSummaryActive(player?.adventureRun) ||
        (match?.adventure && match?.phase === "ended"),
    );
    if (!postCombat) return;
    if (postCombatToastHoldActiveRef.current || postCombatToastHold) return;
    setView((current) =>
      current === "replay" || current === "match" ? current : "adventureSummary",
    );
  }, [
    player?.adventureRun?.phase,
    player?.adventureRun?.combatSummary,
    match?.id,
    match?.phase,
    match?.adventure,
    postCombatToastHold,
  ]);

  useEffect(() => {
    const run = player?.adventureRun;
    if (
      view === "adventure" &&
      run?.phase === "combat" &&
      run?.encounter &&
      !run?.combatSummary &&
      !match
    ) {
      send({ type: "ai:adventureStartCombat" });
    }
    if (
      view === "adventure" &&
      run?.phase === "combat" &&
      match?.adventure &&
      match.phase !== "ended"
    ) {
      requestView("match");
    }
  }, [view, player?.adventureRun?.phase, player?.adventureRun?.id, match?.id, match?.phase, match?.adventure]);

  const isAdmin = Boolean(player?.isAdmin);
  const isGuestAccount =
    player?.accountKind === "guest" || user?.kind === "guest";
  const chatOptIn = player?.settings?.chatOptIn === true;
  const chatMuted = player?.chatMuted === true;
  const blockedUsernames = useMemo(
    () =>
      Array.isArray(player?.blockedUsernames)
        ? player.blockedUsernames.filter(
            (entry) => typeof entry === "string" && entry,
          )
        : [],
    [player?.blockedUsernames],
  );
  const blockedUsernameSet = useMemo(
    () => new Set(blockedUsernames),
    [blockedUsernames],
  );
  const chatCompletedGames = Math.max(
    0,
    Math.floor(Number(player?.completedGamesWithoutForfeit) || 0),
  );
  const chatAccountAgeMs = Math.max(
    0,
    Math.floor(Number(player?.createdAt) || 0),
  )
    ? Date.now() - Math.max(0, Math.floor(Number(player?.createdAt) || 0))
    : 0;
  const chatEligibleByAge =
    !isGuestAccount &&
    Math.max(0, Math.floor(Number(player?.createdAt) || 0)) > 0 &&
    chatAccountAgeMs >= CHAT_ACCOUNT_AGE_MS;
  const chatEligibleByGames =
    !isGuestAccount && chatCompletedGames >= CHAT_MIN_COMPLETED_GAMES;
  const chatEligible = chatEligibleByAge || chatEligibleByGames;
  const canUseSocialChat = chatEligible && chatOptIn && !chatMuted;
  const showQuickUse = Boolean(player?.settings?.showQuickUse);
  const showHints = player?.settings?.showHints !== false;
  const matchGuidesSetting = normalizeMatchGuides(player?.settings?.matchGuides);
  const matchGuidesEnabled =
    matchGuidesSetting === "on" ||
    (matchGuidesSetting === "auto" &&
      (isGuestAccount || chatCompletedGames < MATCH_GUIDES_AUTO_GAMES));
  const showCardNames = player?.settings?.showCardNames !== false;
  const leftHanded = player?.settings?.leftHanded === true;
  const musicEnabled = Boolean(player?.settings?.music);
  const soundEnabled = Boolean(player?.settings?.sound);
  const [musicVolumeLevel, setMusicVolumeLevel] = useState(() =>
    getAudioMusicVolume(),
  );
  const [soundVolumeLevel, setSoundVolumeLevel] = useState(() =>
    getAudioSoundVolume(),
  );
  // A slider at 0 means the channel is off; the saved level is kept so
  // turning it back on restores it. Raising a slider turns the channel on.
  const updateMusicVolume = (percent) => {
    const clamped = Math.max(0, Math.min(100, Math.round(percent)));
    if (clamped <= 0) {
      if (musicEnabled) setMusicSetting(false);
      return;
    }
    setAudioMusicVolume(clamped / 100);
    setMusicVolumeLevel(clamped / 100);
    if (!musicEnabled) setMusicSetting(true);
  };
  const updateSoundVolume = (percent) => {
    const clamped = Math.max(0, Math.min(100, Math.round(percent)));
    if (clamped <= 0) {
      if (soundEnabled) setSoundSetting(false);
      return;
    }
    setAudioSoundVolume(clamped / 100);
    setSoundVolumeLevel(clamped / 100);
    if (!soundEnabled) setSoundSetting(true);
  };
  const previewSoundVolume = () => playSfx("uiSwitch");
  const showWorkshop = Boolean(player?.settings?.showWorkshop);
  const theme = player?.settings?.theme === "light" ? "light" : "dark";
  const forceFullscreen = Boolean(player?.settings?.forceFullscreen);
  const foilEffectsQuality = normalizeFoilEffectsQuality(
    player?.settings?.foilEffects,
  );
  const [graphicsQuality, setGraphicsQuality] = useState(loadGraphicsQuality);
  const [autoGraphicsTier, setAutoGraphicsTier] = useState(
    resolveAutoGraphicsTier,
  );
  const graphicsTier =
    graphicsQuality === "auto" ? autoGraphicsTier : graphicsQuality;
  graphicsTierRef.current = graphicsTier;
  useLayoutEffect(() => {
    applyGraphicsTierToDocument(graphicsTier);
  }, [graphicsTier]);
  const toastsMode = normalizeToastsMode(player?.settings?.toasts);
  const toastSpeed = normalizeToastSpeed(player?.settings?.toastSpeed);
  toastsModeRef.current = toastsMode;
  toastSpeedRef.current = toastSpeed;
  const handDisplay =
    player?.settings?.handDisplay === "dynamic" ? "dynamic" : "drawer";

  useEffect(() => {
    saveStoredLanguage(language);
    document.documentElement.lang = language;
  }, [language]);

  useEffect(() => {
    if (!player?.settings) return;
    syncAudioPrefsFromSettings(player.settings);
  }, [player?.settings?.music, player?.settings?.sound]);

  const ensureImmersive = useCallback(() => {
    if (forceFullscreen) {
      enterImmersiveMode(shellRef.current);
    }
  }, [forceFullscreen]);
  const isReplayView = view === "replay" && Boolean(adminGame);
  isReplayViewRef.current = isReplayView;
  viewRef.current = view;
  rankingSortByRef.current = rankingSortBy;
  rankingPageRef.current = rankingPage;
  const replayDisplay = isReplayView
    ? buildReplayDisplay(adminGame, adminReplayIndex)
    : null;
  const displayMatch = isReplayView
    ? replayDisplay?.match || null
    : match ||
      (postCombatToastHold ? lastLiveMatchRef.current : null);
  const isSpectateView =
    Boolean(displayMatch?.spectating) && !isReplayView;
  isSpectateViewRef.current = isSpectateView;
  const displayUsername = isReplayView
    ? adminGame?.players?.[0] || "Player 1"
    : isSpectateView
      ? displayMatch?.spectateFocus ||
        displayMatch?.players?.[0] ||
        username
      : username;
  const selfSeat = displayMatch?.seats?.[displayUsername];
  const showMulliganAction =
    !isReplayView &&
    !isSpectateView &&
    displayMatch?.phase === "mulligan" &&
    Boolean(selfSeat) &&
    !selfSeat.ready;
  const costAffordContext = { seat: selfSeat, catalogMap };
  const toastIfUnaffordableCost = (resources, cost) => {
    const message = describeResourceShortfall(
      resources,
      cost,
      costAffordContext,
    );
    if (!message) return false;
    showToast(message);
    return true;
  };
  const toastIfBlockedAction = (blocked, message) => {
    if (!blocked) return false;
    if (message) showToast(message);
    return true;
  };
  const opponentUsername =
    displayMatch?.players?.find((name) => name !== displayUsername) || null;
  const opponentSeat = opponentUsername
    ? displayMatch?.seats?.[opponentUsername]
    : null;
  const matchTimerEndsAt =
    displayMatch?.timer &&
    displayMatch.phase !== "ended" &&
    Number.isFinite(Number(displayMatch.timer.endsAt))
      ? Number(displayMatch.timer.endsAt)
      : null;
  const matchTimerKind = displayMatch?.timer?.kind || null;
  const showMatchHud =
    ((view === "match" && Boolean(match)) ||
      view === "workshop" ||
      isReplayView) &&
    Boolean(displayMatch);

  useEffect(() => {
    clearVitalsFloaterRuntime();
    clearCombatProjectileRuntime();
    clearCardPlayRuntime();
    clearMatchEmoteRuntime();
    setEmotePanelOpen(false);
    const floaters = match?.vitalsFloaters || displayMatch?.vitalsFloaters || [];
    const projectiles =
      match?.combatProjectiles || displayMatch?.combatProjectiles || [];
    if (Array.isArray(floaters) && floaters.length) {
      lastVitalsFloaterIdRef.current = floaters.reduce(
        (max, entry) => Math.max(max, Number(entry?.id) || 0),
        0,
      );
    }
    if (Array.isArray(projectiles) && projectiles.length) {
      lastCombatProjectileIdRef.current = projectiles.reduce(
        (max, entry) => Math.max(max, Number(entry?.id) || 0),
        0,
      );
    }
    const seats = displayMatch?.seats || match?.seats || {};
    const nextVisual = {};
    Object.entries(seats).forEach(([seatUsername, seat]) => {
      if (!seat) return;
      nextVisual[seatUsername] = {
        hp: Math.max(0, seat.hp ?? 0),
        maxHp: Math.max(0, seat.maxHp ?? 0),
      };
    });
    setVisualVitalsByUser(nextVisual);
    const sourceMatch = displayMatch || match;
    if (sourceMatch) {
      boardPiecesSnapshotRef.current = collectMatchBoardPieces(sourceMatch);
      cardPlayMatchIdRef.current = sourceMatch.id || null;
    }
  }, [
    displayMatch?.id,
    clearVitalsFloaterRuntime,
    clearCombatProjectileRuntime,
    clearCardPlayRuntime,
    clearMatchEmoteRuntime,
  ]);

  useEffect(() => {
    hideMatchEmotesRef.current = hideMatchEmotes;
    if (hideMatchEmotes) {
      clearMatchEmoteRuntime();
    }
  }, [hideMatchEmotes, clearMatchEmoteRuntime]);

  useEffect(() => {
    blockedUsernamesRef.current = blockedUsernameSet;
  }, [blockedUsernameSet]);

  useEffect(() => {
    const combatSfx = pendingCombatSfxRef.current;
    pendingCombatSfxRef.current = [];
    const sourceMatch = match || displayMatch;
    if (!sourceMatch || !displayUsername) {
      playMatchSfxList(combatSfx);
      return;
    }
    const floaters = sourceMatch.vitalsFloaters || [];
    const projectiles = sourceMatch.combatProjectiles || [];
    const lastFloaterId = lastVitalsFloaterIdRef.current;
    const lastProjectileId = lastCombatProjectileIdRef.current;
    const freshFloaters = floaters.filter((entry) => entry.id > lastFloaterId);
    const freshProjectiles = projectiles.filter(
      (entry) => entry.id > lastProjectileId,
    );
    if (!freshFloaters.length && !freshProjectiles.length) {
      playMatchSfxList(combatSfx);
      return;
    }

    if (freshFloaters.length) {
      lastVitalsFloaterIdRef.current = Math.max(
        lastFloaterId,
        ...freshFloaters.map((entry) => entry.id),
      );
    }
    if (freshProjectiles.length) {
      lastCombatProjectileIdRef.current = Math.max(
        lastProjectileId,
        ...freshProjectiles.map((entry) => entry.id),
      );
    }

    const floatersByTarget = new Map();
    const immediateFloaters = [];
    freshFloaters.forEach((entry) => {
      const side = entry.target === displayUsername ? "player" : "opponent";
      const item = {
        key: `vitals-floater-${entry.id}`,
        side,
        text: entry.text,
        target: entry.target,
        hpDelta: parseVitalsFloaterHpDelta(entry.text),
      };
      if (isXpVitalsFloaterText(entry.text) || !entry.target) {
        immediateFloaters.push(item);
        return;
      }
      if (!floatersByTarget.has(entry.target)) {
        floatersByTarget.set(entry.target, []);
      }
      floatersByTarget.get(entry.target).push(item);
    });

    const projectileGroups = [];
    const projectileItems = freshProjectiles.map((entry) => {
      const target = entry.target;
      const queue = target ? floatersByTarget.get(target) || [] : [];
      const deferredFloater = queue.length ? queue.shift() : null;
      const seat = sourceMatch.seats?.[target] || null;
      if (target) {
        pendingCombatImpactCountRef.current[target] =
          (pendingCombatImpactCountRef.current[target] || 0) + 1;
      }
      const sourceKey = [
        entry.actor,
        entry.sourceKind,
        entry.abilityInstanceId,
        entry.equipmentInstanceId,
        entry.cardId,
      ].join("|");
      const lastGroup = projectileGroups[projectileGroups.length - 1];
      const previousInGroup =
        lastGroup && lastGroup.sourceKey === sourceKey
          ? lastGroup.items[lastGroup.items.length - 1]
          : null;
      const echo = Boolean(entry.echo);
      const item = {
        key: `combat-projectile-${entry.id}`,
        actor: entry.actor,
        target,
        sourceKind: entry.sourceKind,
        sourceKey,
        abilityInstanceId: entry.abilityInstanceId || null,
        equipmentInstanceId: entry.equipmentInstanceId || null,
        cardId: entry.cardId || null,
        tone: entry.tone || "physical",
        motion: entry.motion === "melee" ? "melee" : "projectile",
        shape: combatProjectileShape(
          catalogMapRef.current?.[entry.cardId],
          entry,
        ),
        echo,
        echoLead: echo && !previousInGroup?.echo,
        deferredFloater,
        impactFinalHp: Math.max(0, seat?.hp ?? 0),
        impactFinalMaxHp: Math.max(0, seat?.maxHp ?? 0),
        impactSfx: [],
      };
      if (lastGroup && lastGroup.sourceKey === sourceKey) {
        lastGroup.items.push(item);
      } else {
        projectileGroups.push({ sourceKey, items: [item] });
      }
      return item;
    });

    combatSfx.forEach((sound, index) => {
      const group = projectileGroups[index];
      if (!group) {
        playSfx(sound);
        return;
      }
      const carrier =
        group.items.find((item) => item.target && item.target !== item.actor) ||
        group.items[0];
      carrier.impactSfx.push(sound);
      const echoCarrier = group.items.find(
        (item) =>
          item.echoLead &&
          item !== carrier &&
          item.target &&
          item.target !== item.actor,
      );
      if (echoCarrier) echoCarrier.impactSfx.push(sound);
    });

    projectileItems.forEach((item) => {
      combatProjectileQueueRef.current.push(item);
    });

    floatersByTarget.forEach((queue) => {
      queue.forEach((item) => immediateFloaters.push(item));
    });

    immediateFloaters.forEach((item) => {
      vitalsFloaterQueueRef.current.push(item);
      if (!item.target || (pendingCombatImpactCountRef.current[item.target] || 0) > 0) {
        return;
      }
      const seat = sourceMatch.seats?.[item.target];
      if (!seat) return;
      setVisualVitalsByUser((current) => {
        const prev = current[item.target] || {
          hp: Math.max(0, seat.hp ?? 0),
          maxHp: Math.max(0, seat.maxHp ?? 0),
        };
        const maxHp = Math.max(prev.maxHp, seat.maxHp ?? 0);
        const hp = Math.max(0, Math.min(maxHp, prev.hp + (item.hpDelta || 0)));
        return {
          ...current,
          [item.target]: {
            hp:
              item.hpDelta === 0
                ? Math.max(0, seat.hp ?? 0)
                : hp,
            maxHp: Math.max(0, seat.maxHp ?? maxHp),
          },
        };
      });
    });

    if (freshProjectiles.length) pumpCombatProjectileQueue();
    if (immediateFloaters.length) pumpVitalsFloaterQueue();
  }, [
    match?.vitalsFloaters,
    match?.combatProjectiles,
    match?.seats,
    displayMatch?.vitalsFloaters,
    displayMatch?.combatProjectiles,
    displayMatch?.seats,
    displayUsername,
    pumpCombatProjectileQueue,
    pumpVitalsFloaterQueue,
  ]);

  useEffect(() => {
    const seats = (match || displayMatch)?.seats || {};
    setVisualVitalsByUser((current) => {
      let changed = false;
      const next = { ...current };
      Object.entries(seats).forEach(([username, seat]) => {
        if (!seat) return;
        if ((pendingCombatImpactCountRef.current[username] || 0) > 0) return;
        const hp = Math.max(0, seat.hp ?? 0);
        const maxHp = Math.max(0, seat.maxHp ?? 0);
        if (!next[username] || next[username].hp !== hp || next[username].maxHp !== maxHp) {
          next[username] = { hp, maxHp };
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [match?.seats, displayMatch?.seats]);

  useLayoutEffect(() => {
    const sourceMatch = match || displayMatch;
    if (!sourceMatch || !displayUsername) return;
    const nextPieces = collectMatchBoardPieces(sourceMatch);
    const matchId = sourceMatch.id || null;
    if (
      boardPiecesSnapshotRef.current == null ||
      cardPlayMatchIdRef.current !== matchId
    ) {
      boardPiecesSnapshotRef.current = nextPieces;
      cardPlayMatchIdRef.current = matchId;
      return;
    }
    const prevPieces = boardPiecesSnapshotRef.current;
    const fresh = [];
    nextPieces.forEach((piece, instanceId) => {
      if (!prevPieces.has(instanceId)) fresh.push(piece);
    });
    boardPiecesSnapshotRef.current = nextPieces;
    if (!fresh.length) return;
    if (prefersReducedCombatMotion()) return;
    setCardPlayPendingIds((current) => {
      const next = new Set(current);
      fresh.forEach((piece) => next.add(piece.instanceId));
      return next;
    });
    fresh.forEach((piece) => {
      const side =
        piece.username === displayUsername ? "player" : "opponent";
      spawnCardPlayAnim(piece, side);
    });
  }, [match?.seats, displayMatch?.seats, displayUsername, spawnCardPlayAnim]);

  const isWorkshopMatch = Boolean(displayMatch?.workshop) && !isReplayView;
  const isTutorialMatch = Boolean(displayMatch?.tutorial) && !isReplayView;
  const tutorialCompleted = Boolean(displayMatch?.tutorialCompleted);
  const tutorialHint = isTutorialMatch ? displayMatch?.tutorialHint || null : null;
  const shouldAutoFinishTutorial =
    !isReplayView &&
    !isSpectateView &&
    (Boolean(displayMatch?.tutorial && displayMatch?.phase === "ended") ||
      Boolean(player?.matchSummary?.isTutorial));
  isWorkshopMatchRef.current = isWorkshopMatch;

  const tutorialHighlightClass = (
    equipment,
    actionKind = "effect",
    actionIndex = null,
  ) => {
    if (!tutorialHint?.highlight || !equipment) return "";
    const highlight = tutorialHint.highlight;
    if (
      !highlight.highlightAllResourceExpends &&
      highlight.equipmentInstanceId !== equipment.instanceId
    ) {
      return "";
    }
    if (
      highlight.cardId &&
      equipment.cardId !== highlight.cardId &&
      !highlight.highlightAllResourceExpends
    ) {
      return "";
    }
    if (highlight.kind === "expendResources") {
      if (actionKind !== "resources") return "";
      if (highlight.highlightAllResourceExpends) {
        const resources = equipment.resources || {};
        const hasResources = Object.values(resources).some(
          (amount) => Number(amount) > 0,
        );
        if (!hasResources) return "";
        return ` ${styles.tutorialHighlight} ${styles.actionReady}${actionSuccessClass(
          `expend:${equipment.instanceId}:resources`,
        )}`;
      }
      return ` ${styles.tutorialHighlight} ${styles.actionReady}${actionSuccessClass(
        `expend:${equipment.instanceId}:resources`,
      )}`;
    }
    if (highlight.kind !== "expendEffect") return "";
    if (actionKind !== "effect") return "";
    const effectKey =
      actionIndex == null
        ? `expend:${equipment.instanceId}:effect:all`
        : `expend:${equipment.instanceId}:effect:${actionIndex}`;
    return ` ${styles.tutorialHighlight} ${styles.actionReady}${actionSuccessClass(effectKey)}`;
  };

  const tutorialHandHighlightClass = (handCard) => {
    if (!tutorialHint?.highlight || !handCard) return "";
    const highlight = tutorialHint.highlight;
    if (highlight.kind !== "playEquipment") return "";
    if (highlight.handInstanceId !== handCard.instanceId) return "";
    if (highlight.cardId && handCard.cardId !== highlight.cardId) return "";
    return ` ${styles.tutorialEquipmentHighlight}`;
  };

  const tutorialAbilityHandHighlightClass = (handCard) => {
    if (!tutorialHint?.highlight || !handCard) return "";
    const highlight = tutorialHint.highlight;
    if (highlight.kind !== "slotAbility") return "";
    if (selectedHand?.instanceId === handCard.instanceId) return "";
    if (highlight.handInstanceId !== handCard.instanceId) return "";
    if (highlight.cardId && handCard.cardId !== highlight.cardId) return "";
    return ` ${styles.tutorialEquipmentHighlight}`;
  };

  const tutorialSlotButtonHighlightClass = (handCard) => {
    if (!tutorialHint?.highlight || !handCard) return "";
    const highlight = tutorialHint.highlight;
    if (highlight.kind !== "slotAbility") return "";
    if (highlight.handInstanceId !== handCard.instanceId) return "";
    if (highlight.cardId && handCard.cardId !== highlight.cardId) return "";
    if (selectedHand?.instanceId === handCard.instanceId) return "";
    return ` ${styles.tutorialHighlight} ${styles.actionReady}`;
  };

  const tutorialAbilitySlotHighlightClass = (
    boardSlotKey,
    abilitySlot,
    equipment = null,
  ) => {
    if (!tutorialHint?.highlight || !abilitySlot) return "";
    const highlight = tutorialHint.highlight;
    if (highlight.kind !== "slotAbility") return "";
    if (abilitySlot.ability) return "";
    if (
      highlight.boardSlot &&
      highlight.boardSlot !== boardSlotKey
    ) {
      return "";
    }
    if (
      highlight.equipmentInstanceId &&
      equipment &&
      highlight.equipmentInstanceId !== equipment.instanceId
    ) {
      return "";
    }
    if (
      highlight.equipmentCardId &&
      equipment &&
      equipment.cardId !== highlight.equipmentCardId
    ) {
      return "";
    }
    if (highlight.slotId && abilitySlot.id !== highlight.slotId) {
      return "";
    }
    if (
      selectedHand?.deck !== "ability" ||
      selectedHand.instanceId !== highlight.handInstanceId
    ) {
      return "";
    }
    if (highlight.linkedSlotsOnly) {
      const slots = getColumnAbilitySlots(actingSeat, boardSlotKey);
      const slotIndex = slots.findIndex((slot) => slot.id === abilitySlot.id);
      if (slotIndex < 0) return "";
      const selectedAbility = getSelectedHandAbility(
        selectedHand,
        actingSeat,
        catalogMap,
      );
      if (
        !emptySlotHasAugmentMatch(
          slots,
          slotIndex,
          selectedAbility,
          equipment,
          actingSeat,
          boardSlotKey,
        )
      ) {
        return "";
      }
    }
    return ` ${styles.tutorialEquipmentHighlight}`;
  };

  const tutorialEquipHighlightClass = (handCard) => {
    if (!tutorialHint?.highlight || !handCard) return "";
    const highlight = tutorialHint.highlight;
    if (highlight.kind !== "playEquipment") return "";
    if (highlight.handInstanceId !== handCard.instanceId) return "";
    if (highlight.cardId && handCard.cardId !== highlight.cardId) return "";
    return ` ${styles.tutorialHighlight} ${styles.actionReady}`;
  };

  const tutorialEquipmentHighlightClass = (equipment) => {
    if (!tutorialHint?.highlight || !equipment) return "";
    const highlight = tutorialHint.highlight;
    if (highlight.cardId && equipment.cardId !== highlight.cardId) return "";
    if (highlight.kind === "expendResources" && highlight.highlightAllResourceExpends) {
      if (!equipmentHasResourceExpend(equipment)) return "";
      return ` ${styles.tutorialEquipmentHighlight}`;
    }
    if (highlight.equipmentInstanceId !== equipment.instanceId) return "";
    if (
      highlight.kind !== "expendResources" &&
      highlight.kind !== "expendEffect" &&
      highlight.kind !== "activateAbility"
    ) {
      return "";
    }
    return ` ${styles.tutorialEquipmentHighlight}`;
  };

  const tutorialAbilityHighlightClass = (boardSlotKey, ability) => {
    if (!tutorialHint?.highlight || !ability) return "";
    const highlight = tutorialHint.highlight;
    if (highlight.kind !== "activateAbility") return "";
    if (
      highlight.boardSlot &&
      highlight.boardSlot !== boardSlotKey
    ) {
      return "";
    }
    if (highlight.abilityInstanceId !== ability.instanceId) return "";
    if (highlight.cardId && ability.cardId !== highlight.cardId) return "";
    return ` ${styles.tutorialHighlight} ${styles.actionReady}${actionSuccessClass(
      `activate:${ability.instanceId}`,
    )}`;
  };
  const pendingAbilityTarget =
    selfSeat?.pendingAbilityTarget ||
    (isWorkshopMatch || isSpectateView
      ? opponentSeat?.pendingAbilityTarget || null
      : null);
  const pendingAdventureEnemyTarget = displayMatch?.pendingAdventureEnemyTarget || null;
  const pendingTraitChoice =
    selfSeat?.pendingTraitChoice &&
    (selfSeat.pendingTraitChoice.options || []).length > 0
      ? selfSeat.pendingTraitChoice
      : null;
  const pendingTraitAbilityLevel =
    selfSeat?.pendingTraitAbilityLevel &&
    (selfSeat.pendingTraitAbilityLevel.options || []).length > 0
      ? selfSeat.pendingTraitAbilityLevel
      : null;
  const pendingTraitEquipmentChoice =
    selfSeat?.pendingTraitEquipmentChoice &&
    (selfSeat.pendingTraitEquipmentChoice.options || []).length > 0
      ? selfSeat.pendingTraitEquipmentChoice
      : null;
  const pendingTraitAbilityLevelIds = new Set(
    (pendingTraitAbilityLevel?.options || [])
      .map((option) => option.abilityInstanceId)
      .filter(Boolean),
  );
  const canLevelUpTraitAbility = (ability) =>
    Boolean(ability?.instanceId) &&
    isMyTurn &&
    !isReplayView &&
    !isSpectateView &&
    pendingTraitAbilityLevelIds.has(ability.instanceId);
  const levelUpPreviewOptions = [
    ...(pendingTraitAbilityLevel?.options || []),
    ...(pendingTraitEquipmentChoice?.options || []),
  ].filter((option) => option?.preview);
  const getAbilityLevelUpCompare = (ability) => {
    if (!ability?.instanceId || isReplayView || isSpectateView) return null;
    const option = isMyTurn
      ? levelUpPreviewOptions.find(
          (entry) => entry.abilityInstanceId === ability.instanceId,
        )
      : null;
    return option ? { display: option.preview, compare: ability } : null;
  };
  const getEquipmentLevelUpCompare = (equipment) => {
    if (!equipment?.instanceId || isReplayView || isSpectateView) return null;
    const option = isMyTurn
      ? levelUpPreviewOptions.find(
          (entry) =>
            !entry.abilityInstanceId &&
            entry.equipmentInstanceId === equipment.instanceId,
        )
      : null;
    return option ? { display: option.preview, compare: equipment } : null;
  };
  const renderAbilityLevelUpHint = (ability) => {
    const level = Math.max(1, Math.floor(Number(ability?.level) || 1));
    if (level >= MAX_ABILITY_LEVEL) return null;
    return (
      <span className={`${styles.levelUpOutputText} ${styles.levelUpActionHint}`}>
        {level} → {level + 1}
      </span>
    );
  };
  const chooseTraitAbilityLevel = (ability) => {
    if (!canLevelUpTraitAbility(ability)) return;
    send({
      type: "ai:resolveTraitAbilityLevel",
      abilityInstanceId: ability.instanceId,
    });
  };
  const pendingTraitEquipmentIds = new Set(
    (pendingTraitEquipmentChoice?.options || [])
      .map((option) => option.equipmentInstanceId)
      .filter(Boolean),
  );
  const pendingTraitEquipmentAbilityIds = new Set(
    (pendingTraitEquipmentChoice?.options || [])
      .map((option) => option.abilityInstanceId)
      .filter(Boolean),
  );
  const canEmpowerTraitEquipment = (instanceId) =>
    Boolean(instanceId) &&
    isMyTurn &&
    !isReplayView &&
    !isSpectateView &&
    (pendingTraitEquipmentIds.has(instanceId) ||
      pendingTraitEquipmentAbilityIds.has(instanceId));
  const chooseTraitEquipment = (instanceId) => {
    if (!canEmpowerTraitEquipment(instanceId)) return;
    send({
      type: "ai:resolveTraitEquipmentChoice",
      equipmentInstanceId: instanceId,
    });
  };
  const selectedTraits = Array.isArray(selfSeat?.traits) ? selfSeat.traits : [];
  const targetingBlocksActions =
    Boolean(pendingAbilityTarget) ||
    Boolean(pendingAdventureEnemyTarget) ||
    Boolean(pendingTraitChoice) ||
    Boolean(pendingTraitAbilityLevel) ||
    Boolean(pendingTraitEquipmentChoice) ||
    Boolean(selfSeat?.pendingIdentityChoice?.attributes?.length);
  const pendingTargetEquipmentIds = new Set(
    pendingAbilityTarget?.validEquipmentInstanceIds || [],
  );
  const pendingTargetHandAbilityIds = new Set(
    pendingAbilityTarget?.validHandAbilityInstanceIds || [],
  );
  const pendingValidAspectTarget = Boolean(
    pendingAbilityTarget?.validAspectTarget,
  );
  const pendingTargetsHandAbilities = pendingTargetHandAbilityIds.size > 0;
  const pendingAttributeChoice =
    pendingAbilityTarget?.choiceKind === "attribute" &&
    (pendingAbilityTarget?.validAttributes || []).length > 0;
  const pendingAspectResourceChoice =
    selfSeat?.pendingAspectResourceChoice &&
    (selfSeat.pendingAspectResourceChoice.attributes || []).length > 0
      ? selfSeat.pendingAspectResourceChoice
      : null;
  const pendingIdentityChoice =
    selfSeat?.pendingIdentityChoice &&
    (selfSeat.pendingIdentityChoice.attributes || []).length > 0
      ? selfSeat.pendingIdentityChoice
      : null;
  const pendingIdentityChoiceKey = pendingIdentityChoice
    ? pendingIdentityChoice.equipmentInstanceId || "identity"
    : null;
  useEffect(() => {
    setIdentityChoiceBoardPeek(false);
  }, [pendingIdentityChoiceKey]);
  const pendingChargeResourceChoice =
    pendingAbilityTarget?.choiceKind === "chargeResources" &&
    (pendingAbilityTarget?.validAttributes || []).length > 0 &&
    (pendingAbilityTarget?.maxResources || 0) > 0;
  useEffect(() => {
    if (!pendingChargeResourceChoice) {
      setChargeResourceDraft({ strength: 0, dexterity: 0, wisdom: 0 });
      return;
    }
    setChargeResourceDraft({ strength: 0, dexterity: 0, wisdom: 0 });
    setSelectedEquipmentId(null);
    setSlottedAbilityExam(null);
    setEquipExpendConfirm(null);
    setEquipExpendConfirmPopupStyle(null);
    setEquipCounterExam(null);
    setEquipCounterExamPopupStyle(null);
  }, [
    pendingChargeResourceChoice,
    pendingAbilityTarget?.sourceEquipmentInstanceId,
    pendingAbilityTarget?.maxResources,
  ]);
  useEffect(() => {
    if (!targetingBlocksActions) return;
    setSelectedEquipmentId(null);
    setSlottedAbilityExam(null);
    setEquipExpendConfirm(null);
    setEquipExpendConfirmPopupStyle(null);
    setEquipCounterExam(null);
    setEquipCounterExamPopupStyle(null);
  }, [targetingBlocksActions]);
  useEffect(() => {
    if (!paymentPrompt) return;
    setSelectedEquipmentId(null);
    setSlottedAbilityExam(null);
    setEquipExpendConfirm(null);
    setEquipExpendConfirmPopupStyle(null);
    setEquipCounterExam(null);
    setEquipCounterExamPopupStyle(null);
  }, [
    paymentPrompt?.equipmentInstanceId,
    paymentPrompt?.abilityInstanceId,
    paymentPrompt?.flex,
  ]);
  const workshopActingAsOpponent =
    isWorkshopMatch && opponentBoardExpanded && !targetingBlocksActions;
  const spectateActingAsOpponent = isSpectateView && opponentBoardExpanded;
  const actingUsername =
    workshopActingAsOpponent || spectateActingAsOpponent
      ? opponentUsername
      : displayUsername;
  const actingSeat =
    workshopActingAsOpponent || spectateActingAsOpponent
      ? opponentSeat
      : selfSeat;
  workshopActingAsRef.current = actingUsername || displayUsername;

  const actingResourceBags = actingSeat
    ? snapshotSeatResourceBags(actingSeat)
    : null;
  const actingResourceMatchId = displayMatch?.id || null;
  if (
    !isReplayView &&
    !isSpectateView &&
    actingResourceMatchId &&
    actingResourceBags
  ) {
    if (lastTrackedResourcesMatchIdRef.current !== actingResourceMatchId) {
      lastTrackedResourcesMatchIdRef.current = actingResourceMatchId;
      lastTrackedResourcesRef.current = actingResourceBags;
      lastTrackedConversionIdRef.current = latestResourceConversionId(
        displayMatch?.resourceConversions,
      );
      resourceFlyerSpawnQueueRef.current = [];
      if (Object.keys(resourceConvertPulses).length) {
        setResourceConvertPulses({});
      }
      if (pendingResourceRevealHasValues(pendingResourceRevealRef.current)) {
        const cleared = emptyPendingResourceReveal();
        pendingResourceRevealRef.current = cleared;
        setPendingResourceReveal(cleared);
        setResourceIconPops({});
      }
    } else {
      const prevBags = lastTrackedResourcesRef.current;
      lastTrackedResourcesRef.current = actingResourceBags;
      const conversionEntries = displayMatch?.resourceConversions;
      const converted = sumResourceConversions(
        conversionEntries,
        lastTrackedConversionIdRef.current,
        actingUsername,
      );
      lastTrackedConversionIdRef.current = Math.max(
        lastTrackedConversionIdRef.current,
        latestResourceConversionId(conversionEntries),
      );
      const { flyerGains: gains, convertCounts } = splitConvertedResourceGains(
        collectResourceBagGains(prevBags, actingResourceBags),
        converted,
      );
      if (Object.keys(convertCounts).length) {
        const pulseUpdates = {};
        Object.entries(convertCounts).forEach(([attribute, count]) => {
          resourceConvertSeqRef.current += 1;
          pulseUpdates[attribute] = { key: resourceConvertSeqRef.current, count };
        });
        setResourceConvertPulses((current) => ({ ...current, ...pulseUpdates }));
      }
      if (gains.length) {
        const pendingWithGains = addPendingResourceGains(
          pendingResourceRevealRef.current,
          gains,
        );
        pendingResourceRevealRef.current = pendingWithGains;
        setPendingResourceReveal(pendingWithGains);
        resourceFlyerSpawnQueueRef.current.push({
          gains,
          nextBags: actingResourceBags,
          pendingWithGains,
          attempts: 0,
        });
      }
    }
  } else if (
    isReplayView ||
    isSpectateView ||
    !actingResourceMatchId ||
    !actingResourceBags
  ) {
    if (
      lastTrackedResourcesMatchIdRef.current != null ||
      lastTrackedResourcesRef.current != null
    ) {
      lastTrackedResourcesMatchIdRef.current = null;
      lastTrackedResourcesRef.current = null;
      resourceFlyerSpawnQueueRef.current = [];
    }
    if (pendingResourceRevealHasValues(pendingResourceRevealRef.current)) {
      const cleared = emptyPendingResourceReveal();
      pendingResourceRevealRef.current = cleared;
      setPendingResourceReveal(cleared);
      setResourceIconPops({});
    }
  }

  useEffect(() => {
    const pumpSpawnQueue = () => {
      const job = resourceFlyerSpawnQueueRef.current[0];
      if (!job) return;
      const cardEl = toastCardRef.current;
      const stackEl = toastStackRef.current;
      const targetBlock = actionBarResourcesRef.current;
      if (!targetBlock || !cardEl || !stackEl) {
        job.attempts += 1;
        if (job.attempts > 20) {
          resourceFlyerSpawnQueueRef.current.shift();
          setPendingResourceReveal((current) => {
            const next = removePendingResourceGains(current, job.gains);
            pendingResourceRevealRef.current = next;
            return next;
          });
          window.setTimeout(pumpSpawnQueue, 0);
          return;
        }
        resourceFlyerRetryTimerRef.current = window.setTimeout(
          pumpSpawnQueue,
          50,
        );
        return;
      }
      const stackW = stackEl.offsetWidth || 0;
      const stackH = stackEl.offsetHeight || 0;
      const cardW = cardEl.offsetWidth || 0;
      const cardH = cardEl.offsetHeight || 0;
      if (!stackW || !stackH || !cardW || !cardH) {
        job.attempts += 1;
        if (job.attempts > 20) {
          resourceFlyerSpawnQueueRef.current.shift();
          setPendingResourceReveal((current) => {
            const next = removePendingResourceGains(current, job.gains);
            pendingResourceRevealRef.current = next;
            return next;
          });
          window.setTimeout(pumpSpawnQueue, 0);
          return;
        }
        resourceFlyerRetryTimerRef.current = window.setTimeout(
          pumpSpawnQueue,
          50,
        );
        return;
      }
      resourceFlyerRetryTimerRef.current = null;
      resourceFlyerSpawnQueueRef.current.shift();
      const displayedBags = {
        resources: subtractResourceBag(
          job.nextBags.resources,
          job.pendingWithGains.resources,
        ),
        persistent: subtractResourceBag(
          job.nextBags.persistent,
          job.pendingWithGains.persistent,
        ),
      };
      const revealOffsets = {
        resources: emptyResourceBag(),
        persistent: emptyResourceBag(),
      };
      const stackRestCx = window.innerWidth * 0.5;
      const stackRestCy = window.innerHeight * 0.42;
      const stackRestLeft = stackRestCx - stackW / 2;
      const stackRestTop = stackRestCy - stackH / 2;
      const originX = stackRestLeft + cardEl.offsetLeft + cardW / 2;
      const originY = stackRestTop + cardEl.offsetTop + cardH / 2;
      const targetRect = targetBlock.getBoundingClientRect();
      const endX = targetRect.left + targetRect.width / 2;
      const endY = targetRect.top + targetRect.height / 2;
      let staggerMs = 0;
      const nextFlyers = job.gains.map((gain) => {
        const bag = gain.persist ? "persistent" : "resources";
        const revealIndex =
          (displayedBags[bag][gain.attribute] || 0) +
          (revealOffsets[bag][gain.attribute] || 0);
        revealOffsets[bag][gain.attribute] =
          (revealOffsets[bag][gain.attribute] || 0) + 1;
        const startX = originX + (Math.random() - 0.5) * 18;
        const startY = originY + (Math.random() - 0.5) * 14;
        const spinDir = Math.random() < 0.5 ? 1 : -1;
        const spinDeg = spinDir * (300 + Math.floor(Math.random() * 220));
        resourceFlyerSeqRef.current += 1;
        return {
          key: `resource-flyer-${resourceFlyerSeqRef.current}`,
          attribute: gain.attribute,
          persist: gain.persist,
          revealIndex,
          startX,
          startY,
          dx: endX - startX + (Math.random() - 0.5) * 20,
          dy: endY - startY + (Math.random() - 0.5) * 8,
          delay: 0,
          duration: 1.05 + Math.random() * 0.3,
          spin: spinDeg,
          popX: (Math.random() - 0.5) * 16,
          popY: -8 - Math.random() * 10,
        };
      });
      nextFlyers.forEach((flyer, index) => {
        if (index > 0) {
          staggerMs += 50 + Math.floor(Math.random() * 51);
        }
        flyer.delay = staggerMs + Math.floor(Math.random() * 25);
      });
      setResourceFlyers((current) => [...current, ...nextFlyers]);
      if (resourceFlyerSpawnQueueRef.current.length) {
        window.setTimeout(pumpSpawnQueue, 0);
      }
    };

    if (resourceFlyerRetryTimerRef.current) {
      window.clearTimeout(resourceFlyerRetryTimerRef.current);
      resourceFlyerRetryTimerRef.current = null;
    }
    if (resourceFlyerSpawnQueueRef.current.length) {
      pumpSpawnQueue();
    }
    return () => {
      if (resourceFlyerRetryTimerRef.current) {
        window.clearTimeout(resourceFlyerRetryTimerRef.current);
        resourceFlyerRetryTimerRef.current = null;
      }
    };
  }, [
    pendingResourceReveal,
    toast?.id,
    actingResourceMatchId,
    isReplayView,
    isSpectateView,
  ]);

  const canUseAbilitySlotAction =
    isWorkshopMatch ||
    !actingSeat?.playedAbility ||
    (actingSeat?.nimbleFingersCharges || 0) > 0;
  useEffect(() => {
    if (canUseAbilitySlotAction) return;
    setSelectedHand((current) =>
      current?.deck === "ability" ? null : current,
    );
    setMovingAbility(null);
  }, [canUseAbilitySlotAction]);
  const abilityHandHasSpace =
    (actingSeat?.hand || []).length < MATCH_HAND_SLOTS;
  const boardHasEmptyAbilitySlot = listAbilityBoardSlotKeys(actingSeat).some(
    (boardSlot) =>
      getColumnAbilitySlots(actingSeat, boardSlot).some((slot) => !slot?.ability),
  );
  const boardHasEmptyHostAbilitySlot = listAbilityBoardSlotKeys(actingSeat).some(
    (boardSlot) =>
      getColumnAbilitySlots(actingSeat, boardSlot).some(
        (slot) => isAbilityRackHostSlot(slot) && !slot?.ability,
      ),
  );
  const boardHasEmptyAugmentAbilitySlot = listAbilityBoardSlotKeys(
    actingSeat,
  ).some((boardSlot) =>
    getColumnAbilitySlots(actingSeat, boardSlot).some(
      (slot) => isAbilityRackAugmentSlot(slot) && !slot?.ability,
    ),
  );
  const isMyTurn =
    !isReplayView &&
    !isSpectateView &&
    displayMatch?.phase === "main" &&
    (isWorkshopMatch ||
      displayMatch?.activePlayer === displayUsername ||
      (displayMatch?.adventure &&
        Array.isArray(displayMatch?.adventureParty) &&
        displayMatch.adventureParty.includes(displayUsername) &&
        !(displayMatch.adventureTurnEnded || []).includes(displayUsername) &&
        Math.max(0, displayMatch?.seats?.[displayUsername]?.hp || 0) > 0));
  const traitChoiceDelayKey =
    pendingTraitChoice && isMyTurn && !isReplayView
      ? `${pendingTraitChoice.level}:${(pendingTraitChoice.options || [])
          .map((option) => option.id)
          .join(",")}`
      : null;
  useEffect(() => {
    if (traitChoiceDelayTimerRef.current) {
      window.clearTimeout(traitChoiceDelayTimerRef.current);
      traitChoiceDelayTimerRef.current = null;
    }
    if (!traitChoiceDelayKey) {
      setLevelUpOverlay(null);
      setTraitChoiceModalReady(false);
      setTraitTreeFocusId(null);
      return;
    }
    const level = Number(String(traitChoiceDelayKey).split(":")[0]) || 0;
    setTraitChoiceBoardPeek(false);
    setTraitTreeFocusId(null);
    setTraitChoiceModalReady(false);
    setLevelUpOverlay({
      level,
      key: traitChoiceDelayKey,
    });
    traitChoiceDelayTimerRef.current = window.setTimeout(() => {
      traitChoiceDelayTimerRef.current = null;
      setLevelUpOverlay(null);
      setTraitChoiceModalReady(true);
    }, 2000);
    return () => {
      if (traitChoiceDelayTimerRef.current) {
        window.clearTimeout(traitChoiceDelayTimerRef.current);
        traitChoiceDelayTimerRef.current = null;
      }
    };
  }, [traitChoiceDelayKey]);
  useEffect(() => {
    if (pendingTraitAbilityLevel && isMyTurn && !isReplayView) {
      setTraitChoiceBoardPeek(true);
    }
  }, [pendingTraitAbilityLevel, isMyTurn, isReplayView]);
  const traitLevelPromptActive = Boolean(pendingTraitAbilityLevel);
  useEffect(() => {
    setTraitLevelPromptCollapsed(false);
  }, [traitLevelPromptActive]);
  const traitLevelInspecting = Boolean(
    boardMenu || slottedAbilityExam || selectedEquipmentId || equipCounterExam,
  );
  useEffect(() => {
    if (traitLevelPromptActive && traitLevelInspecting) {
      setTraitLevelPromptCollapsed(true);
    }
  }, [traitLevelPromptActive, traitLevelInspecting]);
  const isOpponentTurn =
    !isReplayView &&
    !isWorkshopMatch &&
    displayMatch?.phase === "main" &&
    Boolean(displayMatch?.activePlayer) &&
    displayMatch.activePlayer !== displayUsername &&
    !(
      displayMatch?.adventure &&
      Array.isArray(displayMatch?.adventureParty) &&
      displayMatch.adventureParty.includes(displayUsername)
    );
  const matchGuideActive =
    matchGuidesEnabled &&
    view === "match" &&
    Boolean(displayMatch) &&
    Boolean(selfSeat) &&
    !isReplayView &&
    !isSpectateView &&
    !isTutorialMatch &&
    !isWorkshopMatch &&
    !displayMatch?.adventure &&
    displayMatch?.phase !== "ended";
  const matchGuideIdleWatch = matchGuideActive && isMyTurn;
  useEffect(() => {
    setMatchGuideIdle(false);
    if (!matchGuideIdleWatch) return undefined;
    let idleTimerId = null;
    const armIdleTimer = () => {
      if (idleTimerId) window.clearTimeout(idleTimerId);
      idleTimerId = window.setTimeout(() => {
        idleTimerId = null;
        setMatchGuideIdle(true);
      }, MATCH_GUIDE_IDLE_MS);
    };
    const handleActivity = () => {
      setMatchGuideIdle(false);
      armIdleTimer();
    };
    armIdleTimer();
    window.addEventListener("pointerdown", handleActivity, true);
    window.addEventListener("keydown", handleActivity, true);
    return () => {
      if (idleTimerId) window.clearTimeout(idleTimerId);
      window.removeEventListener("pointerdown", handleActivity, true);
      window.removeEventListener("keydown", handleActivity, true);
    };
  }, [matchGuideIdleWatch, displayMatch?.id, displayMatch?.round]);
  const toggleMatchGuideMinimized = () => {
    setMatchGuideMinimized((current) => {
      const next = !current;
      saveMatchGuideMinimized(next);
      return next;
    });
  };
  const matchSummary = player?.matchSummary || null;
  const inAdventureCombatSummary = adventureCombatSummaryActive(
    player?.adventureRun,
  );
  const inAdventurePostCombat = Boolean(
    inAdventureCombatSummary ||
      (match?.adventure && match?.phase === "ended"),
  );
  const showAdventureCombatSummaryUi =
    inAdventurePostCombat && !postCombatToastHold;
  const showMatchSummaryUi = view === "summary" && !postCombatToastHold;
  const showMatchBoardUi =
    !showAdventureCombatSummaryUi &&
    ((view === "match" || view === "workshop") &&
      Boolean(match || (postCombatToastHold && lastLiveMatchRef.current)));
  const animateFoilSpotlight =
    FOIL_SPOTLIGHT_VIEWS.has(view) || inAdventurePostCombat;
  const activeFoilEffectsQuality = capFoilQualityForGraphics(
    resolveActiveFoilEffectsQuality(foilEffectsQuality, animateFoilSpotlight),
    graphicsTier,
  );
  const examFoilQuality =
    graphicsTier === "low"
      ? capFoilQualityForGraphics(foilEffectsQuality, graphicsTier)
      : foilEffectsQuality;
  const awaitingSummary = Boolean(
    !inAdventureCombatSummary &&
      (matchSummary ||
        (match &&
          match.phase === "ended" &&
          !match.workshop &&
          !match.spectating &&
          !match.adventure)),
  );
  const inLiveMatch = Boolean(
    match &&
      match.phase !== "ended" &&
      !match.workshop &&
      !match.spectating,
  );
  const inSpectateMatch = Boolean(
    match && match.spectating && match.phase !== "ended",
  );
  const skipSplashForActiveSession = Boolean(
    inLiveMatch ||
      inSpectateMatch ||
      awaitingSummary ||
      inAdventurePostCombat ||
      player?.draftRun?.phase === "picking" ||
      player?.draftRun?.phase === "deckbuilding" ||
      player?.adventureRun?.phase === "combat" ||
      player?.adventureRun?.phase === "combatSummary",
  );
  const showSplash = !enteredGame && !skipSplashForActiveSession;
  const isNewPlayer =
    Boolean(player) &&
    Object.keys(player.completedTutorials || {}).length === 0;

  const gamepadOverlayClassNames = useMemo(
    () =>
      [
        styles.actionPopup,
        styles.gameHintOverlay,
        styles.equipExpendConfirmPopup,
      ].filter(Boolean),
    [],
  );

  const handleGamepadBack = useCallback(() => {
    if (equipExpendConfirm) {
      setEquipExpendConfirm(null);
      return;
    }
    if (equipCounterExam) {
      setEquipCounterExam(null);
      return;
    }
    if (keywordTip) {
      setKeywordTip(null);
      return;
    }
    if (activeGameHint) {
      setActiveGameHint(null);
      return;
    }
    if (examArtPreview) {
      setExamArtPreview(null);
      return;
    }
    if (adventureMonsterExamId) {
      setAdventureMonsterExamId(null);
      return;
    }
    if (adventureSummaryEnemyExamId) {
      setAdventureSummaryEnemyExamId(null);
      return;
    }
    if (slottedAbilityExam) {
      setSlottedAbilityExam(null);
      return;
    }
    if (selectedEquipmentId) {
      setSelectedEquipmentId(null);
      return;
    }
    if (boardMenu) {
      setBoardMenu(null);
      return;
    }
    if (handMenu) {
      setHandMenu(null);
      return;
    }
    if (examCardId) {
      closeCardExam();
      return;
    }
    if (paymentPrompt) {
      setPaymentPrompt(null);
      return;
    }
    if (cancelAbilityConfirm) {
      setCancelAbilityConfirm(false);
      return;
    }
    if (forfeitConfirm) {
      setForfeitConfirm(false);
      return;
    }
    if (loseConfirm) {
      setLoseConfirm(null);
      return;
    }
    if (deleteDeckConfirm) {
      setDeleteDeckConfirm(false);
      return;
    }
    if (resetSavesConfirm) {
      setResetSavesConfirm(false);
      return;
    }
    if (adminWipeConfirm) {
      setAdminWipeConfirm(false);
      return;
    }
    if (adminResetDecksConfirm) {
      setAdminResetDecksConfirm(false);
      return;
    }
    if (adventureResetConfirm) {
      setAdventureResetConfirm(false);
      return;
    }
    if (logoutConfirm) {
      setLogoutConfirm(false);
      return;
    }
    if (accountPanel) {
      setAccountPanel(null);
      setAccountError("");
      setAccountPassword("");
      setAccountNewPassword("");
      setAccountConfirmPassword("");
      setAccountDeleteConfirm("");
      return;
    }
    if (migrateOpen) {
      setMigrateOpen(false);
      setMigrateError("");
      return;
    }
    if (libraryResetConfirm) {
      setLibraryResetConfirm(false);
      return;
    }
    if (adventureTravelConfirm) {
      setAdventureTravelConfirm(null);
      return;
    }
    if (marketplaceBuyConfirm) {
      setMarketplaceBuyConfirm(null);
      return;
    }
    if (dailyActionArmed) {
      setDailyActionArmed(null);
      return;
    }
    if (achievementsModal) {
      setAchievementsModal(
        achievementsModal.categoryId ? { categoryId: null } : null,
      );
      return;
    }
    if (draftForfeitConfirm) {
      setDraftForfeitConfirm(false);
      return;
    }
    if (challengeForfeitPrompt) {
      setChallengeForfeitPrompt(null);
      return;
    }
    if (incomingChallenge?.id) {
      send({
        type: "ai:socialChallengeRespond",
        challengeId: incomingChallenge.id,
        accepted: false,
        forfeitCpu: false,
      });
      setIncomingChallenge(null);
      setChallengeForfeitPrompt(null);
      return;
    }
    if (combatLogOpen) {
      setCombatLogOpen(false);
      return;
    }
    if (patchNotesOpen) {
      setPatchNotesOpen(false);
      return;
    }
    if (privacyPolicyOpen) {
      setPrivacyPolicyOpen(false);
      return;
    }
    if (monetizationInfoOpen) {
      setMonetizationInfoOpen(false);
      return;
    }
    if (designPhilosophyOpen) {
      setDesignPhilosophyOpen(false);
      return;
    }
    if (creditsOpen) {
      setCreditsOpen(false);
      return;
    }
    if (supportInfoId) {
      setSupportInfoId(null);
      return;
    }
    if (loreEntryId) {
      setLoreEntryId(null);
      return;
    }
    if (chatOptInOpen) {
      setChatOptInOpen(false);
      return;
    }
    if (reportTarget) {
      if (reportBusy) return;
      setReportTarget(null);
      setReportError(null);
      return;
    }
    if (adRewardPromptOpen) {
      if (adRewardBusy) return;
      setAdRewardPromptOpen(false);
      return;
    }
    if (contactFormOpen) {
      setContactFormOpen(false);
      setContactError(null);
      return;
    }
    if (contactConfirmation) {
      setContactConfirmation(null);
      return;
    }
    if (xpQuestsOpen) {
      setXpQuestsOpen(false);
      return;
    }
    if (adventureEffectsOpen) {
      setAdventureEffectsOpen(false);
      return;
    }
    if (draftedCardsOpen) {
      setDraftedCardsOpen(false);
      return;
    }
    if (summaryDeckView) {
      setSummaryDeckView(null);
      return;
    }
    if (socialDeckView) {
      setSocialDeckView(null);
      return;
    }
    if (socialMenuPlayer) {
      setSocialMenuPlayer(null);
      return;
    }
    if (socialProfile) {
      setSocialProfile(null);
      return;
    }
    if (adminPreviewGame) {
      setAdminPreviewGame(null);
      return;
    }
    if (deckVariantPicker) {
      setDeckVariantPicker(null);
      return;
    }
    if (slotPicker) {
      setSlotPicker(null);
      return;
    }
    if (workshopPicker) {
      setWorkshopPicker(false);
      return;
    }
    if (deckShare) {
      setDeckShare(null);
      return;
    }
    if (deckAnalyzeOpen) {
      setDeckAnalyzeOpen(false);
      return;
    }
    if (deckRecommend) {
      deckRecommendRequestRef.current += 1;
      setDeckRecommend(null);
      return;
    }
    if (view === "settings" && settingsSectionId) {
      setSettingsSectionId(null);
      return;
    }
    if (movingAbility) {
      setMovingAbility(null);
      return;
    }
    if (selectedHand) {
      setSelectedHand(null);
      return;
    }
    if (pendingAbilityTarget && isMyTurn && !isReplayView && !isSpectateView) {
      send({ type: "ai:cancelAbilityTarget" });
      return;
    }
    if (
      pendingAdventureEnemyTarget &&
      isMyTurn &&
      !isReplayView &&
      !isSpectateView
    ) {
      send({ type: "ai:cancelAdventureEnemyTarget" });
    }
  }, [
    equipExpendConfirm,
    equipCounterExam,
    keywordTip,
    activeGameHint,
    examArtPreview,
    adventureMonsterExamId,
    adventureSummaryEnemyExamId,
    slottedAbilityExam,
    selectedEquipmentId,
    handMenu,
    boardMenu,
    examCardId,
    paymentPrompt,
    cancelAbilityConfirm,
    forfeitConfirm,
    loseConfirm,
    deleteDeckConfirm,
    resetSavesConfirm,
    adminWipeConfirm,
    adminResetDecksConfirm,
    adventureResetConfirm,
    logoutConfirm,
    accountPanel,
    migrateOpen,
    libraryResetConfirm,
    adventureTravelConfirm,
    marketplaceBuyConfirm,
    dailyActionArmed,
    achievementsModal,
    draftForfeitConfirm,
    challengeForfeitPrompt,
    incomingChallenge,
    combatLogOpen,
    patchNotesOpen,
    privacyPolicyOpen,
    monetizationInfoOpen,
    designPhilosophyOpen,
    creditsOpen,
    supportInfoId,
    loreEntryId,
    chatOptInOpen,
    reportTarget,
    reportBusy,
    adRewardPromptOpen,
    adRewardBusy,
    contactFormOpen,
    contactConfirmation,
    xpQuestsOpen,
    adventureEffectsOpen,
    draftedCardsOpen,
    summaryDeckView,
    socialDeckView,
    socialMenuPlayer,
    socialProfile,
    adminPreviewGame,
    deckVariantPicker,
    slotPicker,
    workshopPicker,
    deckShare,
    deckAnalyzeOpen,
    deckRecommend,
    movingAbility,
    selectedHand,
    pendingAbilityTarget,
    pendingAdventureEnemyTarget,
    isMyTurn,
    isReplayView,
    isSpectateView,
    view,
    settingsSectionId,
  ]);

  const gamepadDepsKey = [
    view,
    settingsSectionId || "",
    showSplash,
    Boolean(equipExpendConfirm),
    Boolean(equipCounterExam),
    Boolean(keywordTip),
    Boolean(activeGameHint),
    Boolean(examArtPreview),
    Boolean(examCardId),
    Boolean(handMenu),
    Boolean(boardMenu),
    Boolean(selectedEquipmentId),
    Boolean(slottedAbilityExam),
    Boolean(paymentPrompt),
    Boolean(combatLogOpen),
    Boolean(patchNotesOpen),
    Boolean(privacyPolicyOpen),
    Boolean(monetizationInfoOpen),
    Boolean(designPhilosophyOpen),
    Boolean(creditsOpen),
    Boolean(supportInfoId),
    Boolean(loreEntryId),
    Boolean(chatOptInOpen),
    Boolean(reportTarget),
    Boolean(adRewardPromptOpen),
    Boolean(contactFormOpen),
    Boolean(contactConfirmation),
    Boolean(xpQuestsOpen),
    Boolean(slotPicker),
    Boolean(workshopPicker),
    Boolean(deckVariantPicker),
    Boolean(deckShare),
    Boolean(deckAnalyzeOpen),
    Boolean(deckRecommend),
    Boolean(forfeitConfirm),
    Boolean(loseConfirm),
    Boolean(incomingChallenge),
    Boolean(adventureTravelConfirm),
    Boolean(marketplaceBuyConfirm),
    Boolean(dailyActionArmed),
    achievementsModal ? achievementsModal.categoryId || "list" : "",
    Boolean(adventureMonsterExamId),
    Boolean(pendingAbilityTarget),
    Boolean(selectedHand),
    Boolean(movingAbility),
    Boolean(match?.phase),
    Boolean(isMyTurn),
  ].join("|");

  const handleGamepadEnterNav = useCallback(() => {
    setSideNavExpanded(true);
  }, []);

  const { connected: gamepadConnected, padId: gamepadPadId, activeRegion: gamepadRegion } =
    useGamepadNavigation({
      rootRef: shellRef,
      enabled: true,
      overlayClassNames: gamepadOverlayClassNames,
      onBack: handleGamepadBack,
      onEnterNavRegion: handleGamepadEnterNav,
      depsKey: gamepadDepsKey,
    });

  const prevGamepadConnectedRef = useRef(false);
  useEffect(() => {
    if (gamepadConnected && !prevGamepadConnectedRef.current) {
      const label =
        typeof gamepadPadId === "string" && gamepadPadId.length
          ? gamepadPadId.length > 42
            ? `${gamepadPadId.slice(0, 42)}…`
            : gamepadPadId
          : t("settings.controller.label");
      showToast(t("gamepad.connectedToast", { label }));
    }
    prevGamepadConnectedRef.current = gamepadConnected;
  }, [gamepadConnected, gamepadPadId]);

  const inActiveMatchView =
    (view === "match" &&
      Boolean(displayMatch) &&
      (!inAdventurePostCombat || postCombatToastHold)) ||
    view === "workshop" ||
    view === "summary" ||
    isReplayView;

  const showMatchForfeit = Boolean(
    view === "match" &&
      match &&
      match.phase !== "ended" &&
      !match.workshop &&
      !(
        match.adventure &&
        match.adventureLeader &&
        match.adventureLeader !== username &&
        Array.isArray(match.adventureParty) &&
        match.adventureParty.length > 1
      ),
  );
  const showMatchEmote = !isReplayView && !isSpectateView;

  // Live matches and the workshop fit the screen instead of scrolling.
  const matchNoScroll =
    !isReplayView &&
    Boolean(displayMatch) &&
    (view === "match" || view === "workshop");

  useEffect(() => {
    if (!matchNoScroll) return undefined;
    // Only in-game code sets these page styles, so leaving the match always
    // clears them (never "restore" a value the fit effect may already have
    // set, or menus would stay locked).
    const html = document.documentElement;
    const body = document.body;
    // overflow itself is set by the board-fit effect below, which leaves
    // scrolling on when the boards cannot fit (phones in landscape).
    window.scrollTo(0, 0);
    html.style.overscrollBehavior = "none";
    body.style.overscrollBehavior = "none";
    // Overflow hidden still allows programmatic scrolling (scrollIntoView,
    // focus); snap back so the fitted layout never drifts.
    const resetScroll = () => {
      if (html.style.overflow === "hidden" && window.scrollY > 1) {
        window.scrollTo(0, 0);
      }
    };
    window.addEventListener("scroll", resetScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", resetScroll);
      html.style.overflow = "";
      body.style.overflow = "";
      html.style.overscrollBehavior = "";
      body.style.overscrollBehavior = "";
    };
  }, [matchNoScroll]);

  // Size the board column so both boards sit between the top bar and the
  // hand (and the Ready/End Turn row when it would cover the board). Board
  // height grows roughly linearly with column width, so a few secant steps
  // converge on the widest column that fits.
  useLayoutEffect(() => {
    if (!matchNoScroll) return undefined;
    const root = matchViewRef.current;
    if (!root) return undefined;
    let frame = 0;
    // Returns false when the boards cannot fit even at the minimum width.
    const fitColumn = (layout, targetBottom) => {
      root.style.removeProperty("--ai-fit-col-w");
      let rect = layout.getBoundingClientRect();
      const available = targetBottom - rect.top;
      if (rect.height <= available || rect.width <= 0) return true;
      let prevWidth = rect.width;
      let prevHeight = rect.height;
      let width = Math.max(
        MATCH_BOARD_MIN_COL_PX,
        (rect.width * available) / rect.height,
      );
      for (let step = 0; step < 4; step += 1) {
        root.style.setProperty("--ai-fit-col-w", `${Math.floor(width)}px`);
        rect = layout.getBoundingClientRect();
        if (rect.height <= available && available - rect.height < 4) break;
        if (width <= MATCH_BOARD_MIN_COL_PX && rect.height > available) break;
        const slope =
          Math.abs(rect.width - prevWidth) > 1
            ? (rect.height - prevHeight) / (rect.width - prevWidth)
            : rect.height / rect.width;
        prevWidth = rect.width;
        prevHeight = rect.height;
        width = Math.max(
          MATCH_BOARD_MIN_COL_PX,
          rect.width + (available - rect.height) / (slope > 0.1 ? slope : 1.8),
        );
      }
      if (rect.height > available && width > MATCH_BOARD_MIN_COL_PX) {
        width = Math.max(
          MATCH_BOARD_MIN_COL_PX,
          rect.width - (rect.height - available) / 1.6 - 2,
        );
        root.style.setProperty("--ai-fit-col-w", `${Math.floor(width)}px`);
        rect = layout.getBoundingClientRect();
      }
      return rect.height <= available + 4;
    };
    // Screens too short for both boards (phones in landscape) keep scrolling
    // so the player's own board stays reachable.
    const setScrollLocked = (locked) => {
      const value = locked ? "hidden" : "";
      if (document.documentElement.style.overflow !== value) {
        document.documentElement.style.overflow = value;
        document.body.style.overflow = value;
      }
    };
    const fit = () => {
      frame = 0;
      const layout = root.querySelector(`.${CSS.escape(styles.matchLayout)}`);
      const dock = matchBottomDockRef.current;
      const viewportHeight = window.innerHeight;
      // fitHandTop is where the boards stop and the bottom controls sit. In
      // Drawer mode it is the lowered hand, so opening the drawer never
      // shrinks the boards or moves the toggle / Emote / End Turn buttons
      // (the raised hand slides over the board's bottom edge instead).
      let fitHandTop = viewportHeight;
      const handRow = dock?.querySelector(`.${CSS.escape(styles.handRow)}`);
      if (dock && handRow) {
        const handHeight = handRow.offsetHeight;
        const fitVisible =
          handDisplay === "dynamic"
            ? handHeight * 0.55
            : Math.max(24, handHeight * 0.18) + HAND_BADGE_SPACE_PX;
        fitHandTop = dock.getBoundingClientRect().bottom - fitVisible;
      }
      root.style.setProperty(
        "--ai-hand-reserve",
        `${Math.max(0, Math.round(viewportHeight - fitHandTop))}px`,
      );
      // Start the boards just below the fixed top bar (and workshop bar).
      let topEdge = 0;
      [styles.statusRowMatch, styles.workshopBar].forEach((className) => {
        document.querySelectorAll(`.${CSS.escape(className)}`).forEach((el) => {
          const rect = el.getBoundingClientRect();
          if (rect.height > 0 && rect.top < viewportHeight / 2) {
            topEdge = Math.max(topEdge, rect.bottom);
          }
        });
      });
      if (topEdge > 0) {
        root.style.paddingTop = "0px";
        const rootTop = root.getBoundingClientRect().top;
        root.style.paddingTop = `${Math.max(0, Math.round(topEdge + 4 - rootTop))}px`;
      }
      if (!layout) return;
      // Fit to the screen bottom first, then stop above anything at the
      // bottom (hand, Ready/End Turn row, hand toggle, adventure effects)
      // that shares horizontal space with the boards.
      let fits = fitColumn(layout, viewportHeight - 4);
      const obstacles = [];
      if (handRow) {
        const handRect = handRow.getBoundingClientRect();
        // The lowered hand may tuck under the board frame's outer edge.
        obstacles.push({
          left: handRect.left + HAND_EDGE_TUCK_PX,
          right: handRect.right - HAND_EDGE_TUCK_PX,
          top: fitHandTop,
        });
      }
      [
        styles.matchPrimaryActionRow,
        styles.handDrawerToggle,
        styles.matchFloatingView,
      ].forEach((className) => {
        const rect = root
          .querySelector(`.${CSS.escape(className)}`)
          ?.getBoundingClientRect();
        if (rect && rect.height > 0) {
          obstacles.push(rect);
        }
      });
      const layoutRect = layout.getBoundingClientRect();
      let obstacleTop = Infinity;
      obstacles.forEach((rect) => {
        if (layoutRect.right > rect.left && layoutRect.left < rect.right) {
          obstacleTop = Math.min(obstacleTop, rect.top);
        }
      });
      if (layoutRect.bottom > obstacleTop - 3) {
        fits = fitColumn(layout, obstacleTop - 3);
      }
      setScrollLocked(fits);
    };
    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(fit);
    };
    fit();
    window.addEventListener("resize", schedule);
    window.visualViewport?.addEventListener("resize", schedule);
    let observer = null;
    if (typeof ResizeObserver === "function") {
      observer = new ResizeObserver(schedule);
      const layout = root.querySelector(`.${CSS.escape(styles.matchLayout)}`);
      if (layout) observer.observe(layout);
      if (matchBottomDockRef.current) observer.observe(matchBottomDockRef.current);
    }
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", schedule);
      window.visualViewport?.removeEventListener("resize", schedule);
      observer?.disconnect();
      root.style.removeProperty("--ai-fit-col-w");
      root.style.removeProperty("padding-top");
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    };
  }, [matchNoScroll, handDisplay, displayMatch?.id]);

  useEffect(() => {
    if (
      graphicsQuality !== "auto" ||
      autoGraphicsTier === "low" ||
      !inActiveMatchView
    ) {
      return undefined;
    }
    return watchFrameRate(() => {
      const next = lowerGraphicsTier(autoGraphicsTier);
      saveLearnedGraphicsTier(next);
      setAutoGraphicsTier(next);
      showToast(
        t("settings.graphics.autoLowered", {
          tier: t(`settings.graphics.${next}`),
        }),
      );
    });
  }, [graphicsQuality, autoGraphicsTier, inActiveMatchView]);

  const matchMusicActive = Boolean(
    inLiveMatch ||
      inSpectateMatch ||
      view === "matchIntro" ||
      (view === "match" &&
        displayMatch &&
        (!inAdventurePostCombat || postCombatToastHold)) ||
      view === "workshop" ||
      isReplayView ||
      player?.adventureRun?.phase === "combat",
  );

  useEffect(() => {
    setMusicContext(matchMusicActive ? "match" : "menu");
  }, [matchMusicActive, musicEnabled]);

  useEffect(() => {
    return () => {
      stopMenuMusic();
    };
  }, []);

  useEffect(() => {
    if (!inActiveMatchView) return;
    setSideNavExpanded(false);
  }, [inActiveMatchView]);
  const matchTopographyStyle = useMemo(() => {
    if (!inActiveMatchView) return undefined;
    return getMatchTopographyStyle(displayMatch);
  }, [
    inActiveMatchView,
    displayMatch?.id,
    displayMatch?.createdAt,
  ]);
  const queueNavLabel = inAdventurePostCombat
    ? t("nav.summary")
    : awaitingSummary
      ? t("nav.summary")
      : inLiveMatch || inSpectateMatch
        ? t("nav.game")
        : t("nav.queue");
  const queueNavActive = inAdventurePostCombat
    ? view === "adventureSummary"
    : awaitingSummary
      ? view === "summary"
      : inLiveMatch || inSpectateMatch
        ? view === "match" || view === "matchIntro"
        : view === "menu" ||
          view === "tutorials" ||
          view === "draft" ||
          view === "draftDeck" ||
          view === "draftSummary";

  useEffect(() => {
    if (inLiveMatch || awaitingSummary) return;
    const phase = player?.draftRun?.phase;
    if (view === "draft" && phase === "deckbuilding") {
      setView("draftDeck");
    }
    if (
      (view === "draft" || view === "draftDeck") &&
      phase === "complete" &&
      player?.draftRun?.summary
    ) {
      setView("draftSummary");
    }
  }, [
    player?.draftRun?.phase,
    player?.draftRun?.summary,
    view,
    inLiveMatch,
    awaitingSummary,
  ]);

  useEffect(() => {
    if (skipSplashForActiveSession) {
      setEnteredGame(true);
    }
  }, [skipSplashForActiveSession]);

  const deckPreloadKey = useMemo(
    () => collectDeckCardIds(player).join("|"),
    [player],
  );

  // Splash: UI art plus the player's own deck cards, with a progress bar.
  useEffect(() => {
    if (!catalog?.length) return undefined;
    const deckCards = deckPreloadKey
      ? deckPreloadKey.split("|").map((id) => catalogMap[id])
      : [];
    const urls = [
      ...collectStaticPreloadUrls(assetVersionsState),
      ...collectCardArtPreloadUrls(deckCards),
    ];
    const trackSplashProgress = !enteredGame && !skipSplashForActiveSession;
    let cancelled = false;
    let hideTimer = null;
    preloadImageUrls(urls, {
      priority: "high",
      onProgress: (loaded, total) => {
        if (cancelled || !trackSplashProgress || total <= 0) return;
        setAssetLoadProgress({ loaded, total });
        if (loaded >= total && hideTimer == null) {
          hideTimer = window.setTimeout(() => {
            if (!cancelled) setAssetLoadProgress(null);
          }, 480);
        }
      },
    });
    return () => {
      cancelled = true;
      if (hideTimer != null) window.clearTimeout(hideTimer);
    };
  }, [
    catalog,
    catalogMap,
    assetVersionsState,
    deckPreloadKey,
    enteredGame,
    skipSplashForActiveSession,
  ]);

  // Background: every card thumbnail, a few at a time, so collection pages,
  // drafts and opponents' cards are already cached when they first appear.
  useEffect(() => {
    if (!catalog?.length) return undefined;
    const start = () =>
      preloadImageUrls(collectCardArtPreloadUrls(catalog), { priority: "low" });
    if (typeof window.requestIdleCallback === "function") {
      const idleId = window.requestIdleCallback(start, { timeout: 4000 });
      return () => window.cancelIdleCallback(idleId);
    }
    const timer = window.setTimeout(start, 1500);
    return () => window.clearTimeout(timer);
  }, [catalog, assetVersionsState]);

  // Match: cards on the board, in hand, and Aspects jump the background queue.
  const matchPreloadKey = useMemo(
    () => collectMatchCardIds(displayMatch).sort().join("|"),
    [displayMatch],
  );
  useEffect(() => {
    if (!matchPreloadKey || !catalog?.length) return;
    preloadImageUrls(
      collectCardArtPreloadUrls(
        matchPreloadKey.split("|").map((id) => catalogMap[id]),
      ),
      { priority: "high" },
    );
  }, [matchPreloadKey, catalog, catalogMap, assetVersionsState]);

  useEffect(() => {
    if (enteredGame || skipSplashForActiveSession) {
      setAssetLoadProgress(null);
    }
  }, [enteredGame, skipSplashForActiveSession]);

  const myOpeningDamageBlocked =
    isMyTurn &&
    (displayMatch?.round || 1) === 1 &&
    Boolean(displayMatch?.openingDamageBlocked);

  useEffect(() => {
    const becameMyTurn = isMyTurn && !wasMyTurnRef.current;
    wasMyTurnRef.current = isMyTurn;
    if (!becameMyTurn || isWorkshopMatch || isSpectateView) return undefined;
    showToast(
      myOpeningDamageBlocked
        ? t("game.toast.yourTurnOpeningBlocked")
        : t("game.toast.yourTurn"),
    );
    setTurnGlowId((current) => current + 1);
    return undefined;
  }, [isMyTurn, isWorkshopMatch, isSpectateView, myOpeningDamageBlocked]);

  const lockEndTurnAfterReady = useCallback(() => {
    if (readyEndTurnLockTimerRef.current) {
      window.clearTimeout(readyEndTurnLockTimerRef.current);
    }
    setReadyEndTurnLocked(true);
    readyEndTurnLockTimerRef.current = window.setTimeout(() => {
      readyEndTurnLockTimerRef.current = null;
      setReadyEndTurnLocked(false);
    }, READY_END_TURN_LOCK_MS);
  }, []);

  useEffect(
    () => () => {
      if (readyEndTurnLockTimerRef.current) {
        window.clearTimeout(readyEndTurnLockTimerRef.current);
        readyEndTurnLockTimerRef.current = null;
      }
    },
    [],
  );

  const currentMatchPhase = displayMatch?.phase || null;
  useEffect(() => {
    const previousPhase = previousMatchPhaseRef.current;
    previousMatchPhaseRef.current = currentMatchPhase;
    if (
      previousPhase === "mulligan" &&
      currentMatchPhase &&
      currentMatchPhase !== "mulligan" &&
      !isReplayView &&
      !isSpectateView
    ) {
      lockEndTurnAfterReady();
    }
  }, [currentMatchPhase, isReplayView, isSpectateView, lockEndTurnAfterReady]);

  useEffect(() => {
    if (!shouldAutoFinishTutorial) {
      tutorialAutoFinishHandledRef.current = false;
      if (tutorialSummaryTimerRef.current) {
        window.clearTimeout(tutorialSummaryTimerRef.current);
        tutorialSummaryTimerRef.current = null;
      }
      return;
    }
    if (tutorialAutoFinishHandledRef.current) return;
    tutorialAutoFinishHandledRef.current = true;
    setSelectedEquipmentId(null);
    setSlottedAbilityExam(null);
    setSelectedHand(null);
    setHandMenu(null);
    setBoardMenu(null);
    closeCardExam();
    setExamArtPreview(null);
    setPaymentPrompt(null);
    setCancelAbilityConfirm(false);
    setForfeitConfirm(false);
    setLoseConfirm(null);
    setSlotPicker(null);
    setWorkshopPicker(false);
    closeKeywordTip();
    if (!tutorialSummaryTimerRef.current) {
      tutorialSummaryTimerRef.current = window.setTimeout(() => {
        tutorialSummaryTimerRef.current = null;
        requestView("summary");
      }, 2600);
    }
    return () => {
      if (tutorialSummaryTimerRef.current) {
        window.clearTimeout(tutorialSummaryTimerRef.current);
        tutorialSummaryTimerRef.current = null;
      }
    };
  }, [shouldAutoFinishTutorial]);

  useEffect(() => {
    if (!match?.adventure) return;
    if (match.adventureView === "enemies") {
      setOpponentBoardExpanded(true);
      const allies = (match.adventureParty || []).filter(
        (name) => name && name !== username,
      );
      setAdventureBoardViewIndex(1 + allies.length);
      return;
    }
    if (
      match.adventureView === "player" &&
      !match.pendingAdventureEnemyTarget &&
      !match.adventureDamageSweep
    ) {
      setOpponentBoardExpanded(false);
      setAdventureBoardViewIndex(0);
    }
  }, [
    match?.id,
    match?.adventure,
    match?.adventureView,
    match?.pendingAdventureEnemyTarget,
    match?.adventureDamageSweep,
    match?.adventureParty,
    username,
  ]);

  useEffect(() => {
    if (!match?.adventure) return;
    if (!match.pendingAdventureEnemyTarget && !match.adventureDamageSweep) return;
    setSelectedEquipmentId(null);
    setSlottedAbilityExam(null);
    closeCardExam();
    setKeywordTip(null);
  }, [
    match?.adventure,
    match?.pendingAdventureEnemyTarget,
    match?.adventureDamageSweep,
  ]);

  useEffect(() => {
    if (isReplayView) return;
    const opponentTurnStarted = isOpponentTurn && !wasOpponentTurnRef.current;
    const opponentTurnEnded = !isOpponentTurn && wasOpponentTurnRef.current;
    wasOpponentTurnRef.current = isOpponentTurn;
    if (opponentTurnStarted) {
      suppressOpponentAutoViewRef.current = false;
      setOpponentBoardExpanded(true);
      return;
    }
    if (opponentTurnEnded && !pendingAbilityTarget) {
      setOpponentBoardExpanded(false);
    }
  }, [isOpponentTurn, isReplayView, pendingAbilityTarget]);

  const leaveReplay = () => {
    isReplayViewRef.current = false;
    setCombatLogOpen(false);
    setSelectedEquipmentId(null);
    setSlottedAbilityExam(null);
    setHandMenu(null);
    setBoardMenu(null);
    setSelectedHand(null);
    setAdminReplayIndex(0);
    setAdminPreviewGame(null);
    setAdminSubView("logs");
    setView("admin");
    if (isAdmin) {
      send({ type: "ai:adminListCompletedGames" });
      send({ type: "ai:adminListPlayerDecks" });
    }
  };

  const startAdminReplay = () => {
    if (!adminGame && !adminPreviewGame) return;
    const game = adminPreviewGame || adminGame;
    setAdminGame(game);
    setAdminPreviewGame(null);
    setAdminReplayIndex(0);
    setSelectedEquipmentId(null);
    setSlottedAbilityExam(null);
    setHandMenu(null);
    setBoardMenu(null);
    setSelectedHand(null);
    setOpponentBoardExpanded(false);
    setCombatLogOpen(false);
    setView("replay");
  };

  useEffect(() => {
    if (view !== "social") return undefined;
    send({ type: "ai:socialInit" });
    return undefined;
  }, [view]);

  useEffect(() => {
    if (!canUseSocialChat) {
      setSocialMessages([]);
    }
  }, [canUseSocialChat]);

  useEffect(() => {
    if (view === "summary") return;
    setAdRewardPromptOpen(false);
    setAdRewardBusy(false);
  }, [view]);

  useEffect(() => {
    if (view !== "social") {
      setChatOptInOpen(false);
      return;
    }
    if (canUseSocialChat) {
      setChatOptInOpen(false);
      return;
    }
    if (chatEligible && !chatMuted) {
      setChatOptInOpen(true);
    }
  }, [view, canUseSocialChat, chatEligible, chatMuted]);

  useEffect(() => {
    if (view !== "ranking") return undefined;
    send({
      type: "ai:rankingRequest",
      sortBy: rankingSortBy,
      page: rankingPage,
    });
    return undefined;
  }, [view, rankingSortBy, rankingPage]);

  useEffect(() => {
    if (!connected) return undefined;
    send({ type: "ai:socialActivity", activity: view });
    const interval = window.setInterval(() => {
      send({ type: "ai:socialActivity", activity: viewRef.current });
    }, 60000);
    return () => window.clearInterval(interval);
  }, [view, connected]);

  useEffect(() => {
    if (view !== "achievements") return undefined;
    setDailiesNow(Date.now());
    if (connected) send({ type: "ai:dailiesSync" });
    const interval = window.setInterval(() => {
      setDailiesNow(Date.now());
    }, 1000);
    return () => window.clearInterval(interval);
  }, [view, connected]);

  useEffect(() => {
    if (!dailyActionArmed) return undefined;
    const timeout = window.setTimeout(() => setDailyActionArmed(null), 4000);
    return () => window.clearTimeout(timeout);
  }, [dailyActionArmed]);

  const hasCollectableRewards = useMemo(() => {
    const slots = Array.isArray(player?.dailies?.slots)
      ? player.dailies.slots
      : [];
    if (slots.some((slot) => slot?.kind && slot.completed)) return true;
    const categories = Array.isArray(player?.achievements?.categories)
      ? player.achievements.categories
      : [];
    return categories.some(
      (category) =>
        (category.complete && !category.collected) ||
        (category.achievements || []).some(
          (entry) => entry.completed && !entry.collected,
        ),
    );
  }, [player?.dailies, player?.achievements]);

  useEffect(() => {
    if (view !== "achievements" || !connected) return;
    const slots = Array.isArray(player?.dailies?.slots)
      ? player.dailies.slots
      : [];
    const dueDeadline = slots.reduce((due, slot) => {
      const deadline = slot?.kind
        ? slot.progress > 0 || slot.completed
          ? 0
          : Number(slot.expiresAt) || 0
        : Number(slot?.refillAt) || 0;
      if (deadline <= 0 || deadline > dailiesNow) return due;
      return Math.max(due, deadline);
    }, 0);
    if (!dueDeadline) return;
    const lastSync = dailiesSyncDeadlineRef.current;
    if (
      lastSync &&
      lastSync.deadline === dueDeadline &&
      dailiesNow - lastSync.at < 10000
    ) {
      return;
    }
    dailiesSyncDeadlineRef.current = { deadline: dueDeadline, at: dailiesNow };
    send({ type: "ai:dailiesSync" });
  }, [view, connected, player?.dailies, dailiesNow]);

  useEffect(() => {
    if (view !== "admin" || adminSubView !== "players") return undefined;
    setAdminTrackingNow(Date.now());
    const interval = window.setInterval(() => {
      setAdminTrackingNow(Date.now());
    }, 1000);
    return () => window.clearInterval(interval);
  }, [view, adminSubView]);

  useEffect(() => {
    if (view !== "admin" || adminSubView !== "players") return undefined;
    const interval = window.setInterval(() => {
      send({ type: "ai:adminListPlayerDecks" });
    }, 15000);
    return () => window.clearInterval(interval);
  }, [view, adminSubView]);

  useEffect(() => {
    if (view !== "admin" || adminSubView !== "playerBots") return undefined;
    const interval = window.setInterval(() => {
      send({ type: "ai:adminListPlayerBots" });
    }, 15000);
    return () => window.clearInterval(interval);
  }, [view, adminSubView]);

  useEffect(() => {
    if (view !== "admin" || !isAdmin || !connected) return;
    send({ type: "ai:adminListPlayerReports" });
  }, [view, isAdmin, connected]);

  useEffect(() => {
    if (!pendingAbilityTarget) {
      setCancelAbilityConfirm(false);
      return;
    }
    const targetsOpponentEquipment =
      pendingAbilityTarget.target?.player === "opponent" &&
      pendingTargetEquipmentIds.size > 0;
    if (!targetsOpponentEquipment) return;
    if (
      isWorkshopMatch &&
      opponentSeat?.pendingAbilityTarget &&
      !selfSeat?.pendingAbilityTarget
    ) {
      setOpponentBoardExpanded(false);
      return;
    }
    setOpponentBoardExpanded(true);
  }, [
    pendingAbilityTarget,
    pendingTargetEquipmentIds.size,
    isWorkshopMatch,
    opponentSeat?.pendingAbilityTarget,
    selfSeat?.pendingAbilityTarget,
  ]);

  useEffect(() => {
    if (view !== "social") return;
    chatStickBottomRef.current = true;
    const el = chatListRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [view]);

  useEffect(() => {
    if (view !== "social") return;
    if (!chatStickBottomRef.current) return;
    const el = chatListRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [socialMessages, view]);

  const requestView = (nextView) => {
    const targetView = nextView === "gallery" ? "collection" : nextView;
    if (view === "matchIntro" && targetView !== "match") return;
    if (inAdventurePostCombat && targetView !== "adventureSummary") {
      return;
    }
    if (awaitingSummary && targetView !== "summary") return;
    if (view === "replay" && targetView !== "replay") {
      setCombatLogOpen(false);
      setSelectedEquipmentId(null);
      setSlottedAbilityExam(null);
      setHandMenu(null);
      setBoardMenu(null);
      setSelectedHand(null);
      setAdminReplayIndex(0);
      setAdminPreviewGame(null);
    }
    if (
      view === "workshop" &&
      targetView !== "workshop" &&
      match?.workshop
    ) {
      send({ type: "ai:workshopLeave" });
      setWorkshopControlsHidden(false);
    }
    closeCardExam();
    setDeckVariantPicker(null);
    if (targetView !== "deck") {
      setDeleteDeckConfirm(false);
    }
    if (targetView !== "library") {
      setLibraryDeckId(null);
      setLibraryResetConfirm(false);
      setLoreEntryId(null);
    }
    if (targetView === "library") {
      setLibrarySection(null);
    }
    if (targetView !== "deck" && targetView !== "library") {
      setDeckShare(null);
    }
    if (targetView !== "deck") {
      setDeckAnalyzeOpen(false);
      deckRecommendRequestRef.current += 1;
      setDeckRecommend(null);
      setDeckRecommendHintOpen(false);
    }
    if (targetView !== "social" && targetView !== "ranking") {
      setSocialProfile(null);
      setSocialMenuPlayer(null);
      setSocialDeckView(null);
    }
    if (targetView !== "ranking") {
      setRankingPage(0);
    }
    if (targetView !== "achievements") {
      setDailyActionArmed(null);
      setAchievementsModal(null);
    }
    setView(targetView);
    if (targetView === "admin" && isAdmin) {
      setAdminSubView(null);
    }
  };

  const requestViewFromNav = (nextView) => {
    playSfx("uiSwitch");
    requestView(nextView);
  };

  const enterGame = () => {
    setEnteredGame(true);
    ensureImmersive();
  };

  const enterTutorialsFromSplash = () => {
    setEnteredGame(true);
    ensureImmersive();
    requestView("tutorials");
  };

  const openQueueNav = () => {
    playSfx("uiSwitch");
    if (inAdventurePostCombat) {
      requestView("adventureSummary");
      return;
    }
    if (awaitingSummary) {
      requestView("summary");
      return;
    }
    if (inLiveMatch || inSpectateMatch) {
      requestView("match");
      return;
    }
    requestView("menu");
  };

  const openWorkshopNav = () => {
    if (awaitingSummary || isReplayView || inLiveMatch || inSpectateMatch) return;
    playSfx("uiSwitch");
    if (match?.workshop) {
      requestView("workshop");
      return;
    }
    send({ type: "ai:workshopStart" });
    requestView("workshop");
  };

  const openTutorialsNav = () => {
    if (awaitingSummary) {
      requestView("summary");
      return;
    }
    if (inLiveMatch || inSpectateMatch) {
      requestView("match");
      return;
    }
    requestView("tutorials");
  };

  const startTutorialScenario = (scenarioId) => {
    if (!scenarioId || awaitingSummary || inLiveMatch || inSpectateMatch) return;
    send({ type: "ai:tutorialStart", scenarioId });
  };

  const workshopSeatLabel = workshopActingAsOpponent
    ? opponentUsername || t("game.workshop.seatOpponent")
    : t("game.workshop.seatYou");

  const addWorkshopCard = (card) => {
    if (!card?.id || !isWorkshopMatch) return;
    if (cardHasType(card, "aspect")) {
      send({
        type: "ai:workshopSetAspect",
        aspectId: card.id,
        seatUsername: actingUsername,
      });
      setWorkshopPicker(false);
      return;
    }
    const counts = countIds(collectSeatCardIds(actingSeat));
    if ((counts[card.id] || 0) >= MAX_COPIES) {
      showToast(t("game.workshop.maxCopies", { max: MAX_COPIES, name: card.name }));
      return;
    }
    send({
      type: "ai:workshopAddCard",
      cardId: card.id,
      seatUsername: actingUsername,
      autoPlay: false,
    });
    if (cardHasType(card, "ability")) {
      showToast(t("game.workshop.addedToAbilityHand"));
    }
  };

  const openWorkshopAspectPicker = () => {
    if (!isWorkshopMatch) return;
    clearPickerFilters();
    setWorkshopPickerKind("aspect");
    startTransition(() => {
      setWorkshopPicker(true);
    });
  };

  const adjustWorkshopResource = (attribute, delta) => {
    if (!isWorkshopMatch || !actingSeat) return;
    const current = Math.max(0, actingSeat.resources?.[attribute] || 0);
    send({
      type: "ai:workshopSetResources",
      seatUsername: actingUsername,
      resources: {
        [attribute]: Math.max(0, current + delta),
      },
    });
  };

  const adjustWorkshopHp = (delta) => {
    if (!isWorkshopMatch || !actingSeat) return;
    send({
      type: "ai:workshopSetHp",
      seatUsername: actingUsername,
      hp: Math.max(0, (actingSeat.hp || 0) + delta),
      maxHp: actingSeat.maxHp,
    });
  };

  const setWorkshopTurn = (seatUsername) => {
    send({
      type: "ai:workshopSetActivePlayer",
      seatUsername,
      startTurn: true,
    });
  };

  const boardEquipmentForSlot = (seat, slotKey) => {
    if (!seat?.board) return null;
    return seat.board[slotKey] || null;
  };

  const findBoardEquipment = (instanceId) => {
    if (!instanceId) return null;
    const searchSeat = (seat, own) => {
      if (!seat) return null;
      const equipment = Object.values(seat.board || {}).find(
        (item) => item && item.instanceId === instanceId,
      );
      return equipment ? { equipment, own } : null;
    };
    const opponent =
      displayMatch?.players?.find((name) => name !== displayUsername) ||
      matchSummary?.them?.username ||
      null;
    const opponentSeat = opponent ? displayMatch?.seats?.[opponent] : null;
    return (
      searchSeat(selfSeat, !(workshopActingAsOpponent || spectateActingAsOpponent)) ||
      searchSeat(
        opponentSeat,
        workshopActingAsOpponent || spectateActingAsOpponent,
      ) ||
      searchSeat(matchSummary?.you, true) ||
      searchSeat(matchSummary?.them, false)
    );
  };

  const findBoardAbility = (abilityInstanceId, boardSlotHint = null) => {
    if (!abilityInstanceId) return null;
    const searchSeat = (seat, own) => {
      const found = findSlottedAbilityOnSeat(seat, abilityInstanceId, boardSlotHint);
      return found ? { ...found, own, seat } : null;
    };
    const opponent =
      displayMatch?.players?.find((name) => name !== displayUsername) ||
      matchSummary?.them?.username ||
      null;
    const opponentSeatLocal = opponent ? displayMatch?.seats?.[opponent] : null;
    return (
      searchSeat(selfSeat, !(workshopActingAsOpponent || spectateActingAsOpponent)) ||
      searchSeat(
        opponentSeatLocal,
        workshopActingAsOpponent || spectateActingAsOpponent,
      ) ||
      searchSeat(matchSummary?.you, true) ||
      searchSeat(matchSummary?.them, false)
    );
  };

  const renderAbilitySlots = (
    boardSlotKey,
    ownBoard,
    inspectOnly = false,
    seat = actingSeat,
    variant = "board",
  ) => {
    const rack = getSeatAbilityRack(seat, boardSlotKey);
    const equipment = boardEquipmentForSlot(seat, boardSlotKey);
    const slots = rack?.abilitySlots || [];
    if (!slots.length) return null;
    const selectedAbility =
      getSelectedHandAbility(selectedHand, actingSeat, catalogMap) ||
      getMovingBoardAbility(movingAbility, actingSeat);
    const isActionBar = variant === "actionBar";
    return (
      <AbilitySlotsVisual
        slots={slots}
        variant={variant}
        renderSlot={(abilitySlot, slotIndex, slotOptions = {}) => {
          const ability = abilitySlot.ability;
          const boardDisplay =
            slotOptions.boardDisplay ||
            (ability?.kind === "augment" ? "circle" : "card");
          const asCircle = isActionBar && boardDisplay === "circle";
          const isMismatch =
            ability?.kind === "augment" && ability.augmentStatus === "mismatch";
          const effectiveCost =
            ability?.kind === "active"
              ? ability.effectiveCost || ability.cost
              : null;
          const effectiveHealthCost =
            ability?.kind === "active"
              ? Math.max(0, ability.effectiveHealthCost || 0)
              : 0;
          const costText = effectiveHealthCost
            ? t("match.board.healthCost", { amount: effectiveHealthCost })
            : effectiveCost
              ? formatCost(effectiveCost)
              : "";
          const targetingEmptySlot =
            ownBoard &&
            !inspectOnly &&
            !ability &&
            isMyTurn &&
            canUseAbilitySlotAction &&
            (selectedHand?.deck === "ability" || Boolean(movingAbility));
          const isHostSlot = isAbilityRackHostSlot(abilitySlot);
          const isAugSlot = isAbilityRackAugmentSlot(abilitySlot);
          // Actives and Essences only work from the host slot.
          const abilityInert =
            isAugSlot &&
            (ability?.kind === "active" || ability?.kind === "essence");
          const targetingAugmentMatch =
            targetingEmptySlot &&
            selectedAbility &&
            !(
              isAugSlot &&
              (selectedAbility.kind === "active" ||
                selectedAbility.kind === "essence")
            ) &&
            emptySlotHasAugmentMatch(
              slots,
              slotIndex,
              selectedAbility,
              equipment,
              seat,
              boardSlotKey,
            );
          const traitAbilityLevelTarget =
            ownBoard &&
            !inspectOnly &&
            isMyTurn &&
            Boolean(ability) &&
            pendingTraitAbilityLevelIds.has(ability.instanceId);
          const traitEquipmentEmpowerTarget =
            ownBoard &&
            !inspectOnly &&
            isMyTurn &&
            Boolean(ability) &&
            pendingTraitEquipmentAbilityIds.has(ability.instanceId);
          const emptyTargetHighlight =
            targetingEmptySlot &&
            selectedAbility &&
            (isHostSlot
              ? selectedAbility.kind === "active" ||
                selectedAbility.kind === "essence"
              : isAugSlot
                ? selectedAbility.kind === "augment" ||
                  selectedAbility.kind === "active" ||
                  selectedAbility.kind === "essence"
                : selectedAbility.kind === "augment"
                  ? targetingAugmentMatch
                  : true);
          const tutorialSlotHighlight = tutorialAbilitySlotHighlightClass(
            boardSlotKey,
            abilitySlot,
            equipment,
          );
          const abilityArtUrl = ability
            ? getAbilityCardImageUrl({
                ...ability,
                type: ability.type || "ability",
              })
            : null;
          const abilityFoilFilter = ability
            ? getFoilArtFilterClass(ability.foilEffect)
            : "";
          const buildAbilityFaceCard = (source) => ({
            ...(catalogMap[source.cardId] || {}),
            ...source,
            id: source.cardId || source.id,
            type: source.type || catalogMap[source.cardId]?.type || [
              "ability",
            ],
            cost: source.effectiveCost || source.cost,
          });
          const abilityLevelUpCompare =
            ability && ownBoard && !inspectOnly
              ? getAbilityLevelUpCompare(ability)
              : null;
          const faceCard = ability
            ? buildAbilityFaceCard(abilityLevelUpCompare?.display || ability)
            : null;
          const compareFaceCard = abilityLevelUpCompare
            ? buildAbilityFaceCard(abilityLevelUpCompare.compare)
            : null;
          const minionActive =
            Boolean(ability?.minion) && (ability.minion.hp || 0) > 0;
          const auraActive = Boolean(ability?.aura);
          const abilityUsed = slottedAbilityLooksUsed(
            slots,
            slotIndex,
            ability,
            equipment,
            seat,
          );
          const guideAbilityUsable = ability
            ? matchGuideAbilityUsable(ability, ownBoard && !inspectOnly)
            : false;
          const highlightClass = tutorialSlotHighlight
            ? tutorialSlotHighlight
            : traitAbilityLevelTarget || traitEquipmentEmpowerTarget
              ? asCircle || !ability
                ? styles.abilitySlotTargetMatch
                : styles.cardTargetMatch
              : emptyTargetHighlight && targetingAugmentMatch
              ? styles.abilitySlotTargetMatch
              : emptyTargetHighlight
                ? styles.abilitySlotTarget
                : "";
          const handleSlotClick = (event) => {
            event.stopPropagation();
            if (ownBoard && movingAbility && isMyTurn && !ability) {
              if (!canUseAbilitySlotAction) {
                showToast(t("match.board.alreadyPlayedAbility"));
                setMovingAbility(null);
                return;
              }
              if (
                movingAbility.boardSlot === boardSlotKey &&
                getColumnAbilitySlots(actingSeat, movingAbility.boardSlot).some(
                  (slot) =>
                    slot?.id === abilitySlot.id &&
                    slot?.ability?.instanceId ===
                      movingAbility.abilityInstanceId,
                )
              ) {
                setMovingAbility(null);
                return;
              }
              send({
                type: "ai:moveAbility",
                fromBoardSlot: movingAbility.boardSlot,
                abilityInstanceId: movingAbility.abilityInstanceId,
                toBoardSlot: boardSlotKey,
                targetSlotId: abilitySlot.id,
              });
              setMovingAbility(null);
              return;
            }
            if (
              ownBoard &&
              selectedHand?.deck === "ability" &&
              isMyTurn &&
              !ability
            ) {
              if (!canUseAbilitySlotAction) {
                showToast(t("match.board.alreadyPlayedAbility"));
                setSelectedHand(null);
                return;
              }
              send({
                type: "ai:playAbility",
                instanceId: selectedHand.instanceId,
                boardSlot: boardSlotKey,
                slotId: abilitySlot.id,
              });
              setSelectedHand(null);
              return;
            }
            if (ability) {
              closeKeywordTip();
              setSelectedEquipmentId(null);
              const canOpenBoardMenu =
                ownBoard &&
                !inspectOnly &&
                !isReplayView &&
                !isSpectateView &&
                (isMyTurn || isWorkshopMatch);
              if (canOpenBoardMenu) {
                setSlottedAbilityExam(null);
                setHandMenu(null);
                setSelectedHand(null);
                setBoardMenu((current) =>
                  current?.kind === "ability" &&
                  current.instanceId === ability.instanceId
                    ? null
                    : {
                        kind: "ability",
                        instanceId: ability.instanceId,
                        boardSlot: boardSlotKey,
                      },
                );
                return;
              }
              if (cardFocusHost) {
                setSlottedAbilityExam(null);
                setHandMenu(null);
                setSelectedHand(null);
                setBoardMenu((current) =>
                  current?.kind === "ability" &&
                  current.instanceId === ability.instanceId
                    ? null
                    : {
                        kind: "ability",
                        instanceId: ability.instanceId,
                        boardSlot: boardSlotKey,
                        examineOnly: true,
                      },
                );
                return;
              }
              setBoardMenu(null);
              setSlottedAbilityExam({
                boardSlot: boardSlotKey,
                abilityInstanceId: ability.instanceId,
                cardId: ability.cardId,
              });
            }
          };
          const abilityLevel = ability ? getAbilityDisplayLevel(ability) : 0;
          const abilityMetaTags = ability
            ? [
                ability.inherent ? t("match.board.tagInherent") : null,
                formatAbilityCardTags(ability),
                ability.combo > 0 ? t("match.board.tagCombo") : null,
                t("match.board.tagLevel", { level: abilityLevel }),
                minionActive
                  ? t("match.board.tagActiveMinion", { name: ability.minion.name, hp: ability.minion.hp })
                  : null,
                auraActive
                  ? t("match.board.tagActiveAura", { name: ability.aura.name || ability.name })
                  : null,
                ability.kind === "augment" &&
                ability.augmentStatus === "mismatch"
                  ? "X"
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")
            : "";
          const slotTitle = traitAbilityLevelTarget
            ? t("match.board.slotTitleCanLevelUp")
            : traitEquipmentEmpowerTarget
              ? t("match.board.slotTitleEmpowerArmor")
            : emptyTargetHighlight && targetingAugmentMatch
            ? t("match.board.slotTitleLinkedAugment")
            : emptyTargetHighlight
              ? t("match.board.slotTitleSlotHere")
              : isMismatch
                ? ability.augmentStatusReason || t("match.board.mismatch")
                : abilityInert
                  ? t("match.board.slotTitleInert", { name: ability.name })
                : minionActive
                  ? `${ability.minion.name} ${ability.minion.hp}/${ability.minion.maxHp}${
                      ability.minion.expended ? t("match.board.expendedSuffix") : ""
                    }`
                  : auraActive
                    ? t("match.board.auraActiveTitle", { name: ability.aura.name || ability.name })
                    : abilityUsed && ability?.kind === "augment"
                      ? t("match.board.linkedActiveUsed", { name: ability.name })
                      : isActionBar && ability
                        ? [ability.name, abilityMetaTags]
                            .filter(Boolean)
                            .join(" · ")
                        : costText
                          ? t("match.board.costTitle", { cost: costText })
                          : ability?.name || "";

          if (isActionBar) {
            if (asCircle) {
              const augmentMenuOpen =
                boardMenu?.kind === "ability" &&
                ability &&
                boardMenu.instanceId === ability.instanceId;
              return (
                <div
                  className={`${styles.abilitySlotAugmentWrap}${
                    augmentMenuOpen
                      ? ` ${styles.boardCardSlotActionsOpen}`
                      : ""
                  }`}
                >
                  <button
                    type="button"
                    className={`${styles.abilitySlotCircle} ${
                      styles.abilitySlotBoardAugment
                    } ${TINT_CLASS[primaryAttribute(ability)] || ""} ${
                      ability ? styles.abilitySlotFilled : ""
                    } ${abilityArtUrl ? styles.abilitySlotWithArt : ""} ${
                      abilityUsed ? styles.abilitySlotUsed : ""
                    } ${abilityInert ? styles.abilitySlotInert : ""} ${
                      minionActive || auraActive
                        ? styles.abilitySlotMinionActive
                        : ""
                    } ${
                      isMismatch ? styles.abilitySlotMismatch : ""
                    } ${highlightClass}${
                      ability?.instanceId &&
                      cardPlayPendingIds.has(ability.instanceId)
                        ? ` ${styles.cardPlayPending}`
                        : ""
                    }`}
                    data-gamepad-item=""
                    data-combat-anchor={
                      ability?.instanceId
                        ? `ability:${ability.instanceId}`
                        : undefined
                    }
                    title={slotTitle}
                    aria-label={
                      ability
                        ? ability.name
                        : emptyTargetHighlight
                          ? slotTitle
                          : t("match.board.emptyAbilitySlot")
                    }
                    onClick={handleSlotClick}
                  >
                    {abilityArtUrl ? (
                      <span
                        aria-hidden="true"
                        className={[styles.slotAbilityArt, abilityFoilFilter]
                          .filter(Boolean)
                          .join(" ")}
                        style={{ backgroundImage: `url("${abilityArtUrl}")` }}
                      />
                    ) : null}
                    {ability ? (
                      <FoilOverlay
                        foilEffect={ability.foilEffect}
                        cardId={ability.cardId}
                      />
                    ) : null}
                    {isMismatch ? (
                      <span className={styles.mismatchMark} aria-hidden="true">
                        X
                      </span>
                    ) : null}
                  </button>
                  {augmentMenuOpen
                    ? renderInCardFocus(
                        renderBoardAbilityActionsMenu(ability, {
                          boardSlot: boardSlotKey,
                          slotId: abilitySlot.id,
                          equipment,
                          seat,
                        }),
                        {
                          key: `ability:${ability.instanceId}`,
                          card: faceCard,
                          foilEffect: ability.foilEffect || null,
                        },
                      )
                    : null}
                </div>
              );
            }
            const fletchCounterAmount = getChargeCount(ability, "fletch");
            const fletchCounterLabel = t("match.board.fletchCounter", { count: fletchCounterAmount });
            const renderAbilityCardBadges = () => (
              <>
                {ability &&
                ability.kind === "active" &&
                !abilityInert &&
                !abilityUsed &&
                !minionActive &&
                !auraActive ? (
                  <button
                    type="button"
                    className={`${styles.boardReadyStar}${
                      keywordTip?.id === "active"
                        ? ` ${styles.keywordIconButtonActive}`
                        : ""
                    }${
                      guideAbilityUsable
                        ? ` ${styles.matchGuideDiamondGlow}`
                        : ""
                    }`}
                    aria-label={t("match.board.activeBadge")}
                    title={t("match.board.activeBadge")}
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleKeywordTip(getKeywordById("active"));
                    }}
                  >
                    <ExpendActivateIcon />
                  </button>
                ) : null}
                {fletchCounterAmount > 0 ? (
                  <button
                    type="button"
                    className={`${styles.abilityFletchMark}${
                      keywordTip?.id === getChargeKindKeywordId("fletch")
                        ? ` ${styles.keywordIconButtonActive}`
                        : ""
                    }`}
                    aria-label={fletchCounterLabel}
                    title={fletchCounterLabel}
                    onClick={(event) => {
                      event.stopPropagation();
                      const fletchKeyword = getKeywordById(
                        getChargeKindKeywordId("fletch"),
                      );
                      if (!fletchKeyword) return;
                      toggleKeywordTip({
                        ...fletchKeyword,
                        description: `${fletchKeyword.description} Current: ${fletchCounterLabel} (+${fletchCounterAmount} damage).`,
                      });
                    }}
                  >
                    <span className={styles.abilityFletchMarkCount}>
                      {fletchCounterAmount}
                    </span>
                    <MaskIcon
                      src={STATUS_EFFECT_ICON_URL.fletch}
                      className={styles.equipStatusMarkIconGlyph}
                    />
                  </button>
                ) : null}
              </>
            );
            return (
              <div
                className={`${styles.abilitySlotCardWrap}${
                  abilityUsed ? ` ${styles.abilitySlotCardWrapUsed}` : ""
                }${abilityInert ? ` ${styles.abilitySlotInert}` : ""}${
                  boardMenu?.kind === "ability" &&
                  ability &&
                  boardMenu.instanceId === ability.instanceId
                    ? ` ${styles.boardCardSlotActionsOpen}`
                    : ""
                }${
                  ability?.instanceId &&
                  cardPlayPendingIds.has(ability.instanceId)
                    ? ` ${styles.cardPlayPending}`
                    : ""
                }`}
                data-combat-anchor={
                  ability?.instanceId
                    ? `ability:${ability.instanceId}`
                    : undefined
                }
              >
                {ability && faceCard ? (
                  <ScaledCardFace
                    card={faceCard}
                    compareCard={compareFaceCard}
                    foilEffect={ability.foilEffect || null}
                    showPowerBadge={false}
                    onClick={handleSlotClick}
                    highlightClass={
                      [
                        highlightClass,
                        isMismatch ? styles.abilitySlotMismatch : "",
                        minionActive || auraActive
                          ? styles.abilitySlotMinionActive
                          : "",
                        boardMenu?.kind === "ability" &&
                        boardMenu.instanceId === ability.instanceId
                          ? styles.cardSelected
                          : "",
                        guideAbilityUsable ? styles.matchGuideGlow : "",
                      ]
                        .filter(Boolean)
                        .join(" ")
                    }
                  />
                ) : (
                  <div
                    className={`${styles.abilitySlotRect}${
                      emptyTargetHighlight && highlightClass
                        ? ` ${highlightClass}`
                        : ""
                    }`}
                  >
                    <button
                      type="button"
                      className={styles.abilitySlotRectHit}
                      data-gamepad-item=""
                      title={slotTitle}
                      aria-label={
                        emptyTargetHighlight
                          ? slotTitle
                          : t("match.board.emptyAbilitySlot")
                      }
                      onClick={handleSlotClick}
                    >
                      <span className={styles.boardSlotEmptyIconGlow}>
                        <MaskIcon
                          src={BOARD_EMPTY_SLOT_ICON_URL.activeAbility}
                          className={styles.boardSlotEmptyIcon}
                        />
                      </span>
                    </button>
                  </div>
                )}
                {renderAbilityCardBadges()}
                {boardMenu?.kind === "ability" &&
                ability &&
                boardMenu.instanceId === ability.instanceId
                  ? renderInCardFocus(
                      renderBoardAbilityActionsMenu(ability, {
                        boardSlot: boardSlotKey,
                        slotId: abilitySlot.id,
                        equipment,
                        seat,
                      }),
                      {
                        key: `ability:${ability.instanceId}`,
                        card: faceCard,
                        foilEffect: ability.foilEffect || null,
                        overlayClassName: styles.abilitySlotCardWrap,
                        overlay: renderAbilityCardBadges(),
                      },
                    )
                  : null}
              </div>
            );
          }

          return (
            <button
              type="button"
              className={`${styles.abilitySlotCircle} ${
                TINT_CLASS[primaryAttribute(ability)] || ""
              } ${ability ? styles.abilitySlotFilled : ""} ${
                abilityArtUrl ? styles.abilitySlotWithArt : ""
              } ${abilityUsed ? styles.abilitySlotUsed : ""} ${
                abilityInert ? styles.abilitySlotInert : ""
              } ${
                minionActive || auraActive
                  ? styles.abilitySlotMinionActive
                  : ""
              } ${
                isMismatch ? styles.abilitySlotMismatch : ""
              } ${highlightClass}${
                ability?.instanceId &&
                cardPlayPendingIds.has(ability.instanceId)
                  ? ` ${styles.cardPlayPending}`
                  : ""
              }${guideAbilityUsable ? ` ${styles.matchGuideGlow}` : ""}`}
              data-gamepad-item=""
              data-combat-anchor={
                ability?.instanceId
                  ? `ability:${ability.instanceId}`
                  : undefined
              }
              title={slotTitle}
              aria-label={
                ability && !showCardNames ? ability.name : undefined
              }
              onClick={handleSlotClick}
            >
              {ability ? (
                <>
                  {abilityArtUrl ? (
                    <span
                      aria-hidden="true"
                      className={[
                        styles.slotAbilityArt,
                        abilityFoilFilter,
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      style={{
                        backgroundImage: `url("${abilityArtUrl}")`,
                      }}
                    />
                  ) : null}
                  <FoilOverlay
                    foilEffect={ability.foilEffect}
                    cardId={ability.cardId}
                  />
                  <span className={styles.slotAbilityContent}>
                    {showCardNames ? (
                      <span className={styles.slotAbilityName}>
                        <span className={styles.slotAbilityNameText}>
                          {ability.name}
                        </span>
                        {!minionActive && !auraActive ? (
                          <AbilityLevelIcons
                            level={abilityLevel}
                            className={styles.slotAbilityLevelIcons}
                          />
                        ) : null}
                      </span>
                    ) : !minionActive && !auraActive ? (
                      <AbilityLevelIcons
                        level={abilityLevel}
                        className={styles.slotAbilityLevelIcons}
                      />
                    ) : null}
                    <span className={styles.slotAbilityMeta}>
                      <span className={styles.slotAbilityCost}>
                        {minionActive
                          ? `${ability.minion.name} ${ability.minion.hp}`
                          : auraActive
                            ? `${ability.aura.name || ability.name}`
                            : costText || null}
                      </span>
                    </span>
                    {isMismatch ? (
                      <span className={styles.mismatchMark} aria-hidden="true">
                        X
                      </span>
                    ) : null}
                  </span>
                </>
              ) : null}
            </button>
          );
        }}
      />
    );
  };

  const formatMatchHudPlayerLabel = (playerName) => {
    if (!playerName) return playerName;
    if (playerName === displayUsername) return t("match.board.you");
    if (
      (opponentUsername && playerName === opponentUsername) ||
      playerName === "Opponent"
    ) {
      return t("match.board.opp");
    }
    return playerName;
  };

  const formatCombatLogText = (text) => {
    if (!text) return text;
    const replacements = [];
    if (displayUsername) replacements.push([displayUsername, "YOU"]);
    if (opponentUsername) replacements.push([opponentUsername, "OPP"]);
    if (!replacements.length) return text;
    replacements.sort((a, b) => b[0].length - a[0].length);
    let result = text;
    for (const [from, to] of replacements) {
      result = result.split(from).join(to);
    }
    return result;
  };

  const renderSeatBoardArea = (seat, ownBoard, ownerName = null, options = {}) => {
    if (!seat) return null;
    const { mirrored = false, showBoardVitals = false } = options;
    return (
      <div className={styles.seatBoardWrap} data-gamepad-zone="board">
        <div className={styles.seatBoardLayout}>
          <div className={styles.seatBoardMain}>
            {ownerName && !showBoardVitals ? (
              <div className={styles.seatBoardHeader}>
                <div className={styles.seatBoardIdentity}>
                  <div className={styles.seatBoardOwner}>
                    {formatMatchHudPlayerLabel(ownerName)}
                  </div>
                </div>
              </div>
            ) : null}
            {renderBodyBoard(seat, ownBoard, false, {
              mirrored,
              showBoardVitals,
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderMatchTimer = () => {
    if (matchTimerEndsAt == null) return null;
    const title =
      matchTimerKind === "mulligan"
        ? t("match.board.timerMulligan")
        : matchTimerKind === "turn"
          ? t("match.board.timerTurn")
          : t("match.board.timerMatch");
    return (
      <MatchTimerOverlay
        endsAt={matchTimerEndsAt}
        kind={matchTimerKind}
        title={title}
        caption={
          matchTimerKind === "mulligan" ? t("match.board.mulligan") : null
        }
      />
    );
  };

  const formatMatchTurnLabel = (playerName) => {
    const label = formatMatchHudPlayerLabel(playerName);
    return label === t("match.board.you") ? t("match.board.yourTurn") : t("match.board.playerTurn", { name: label });
  };

  const renderMatchPhaseLine = (className) => {
    if (!displayMatch) return null;
    return (
      <div className={className}>
        {displayMatch.phase === "mulligan" && displayMatch.firstPlayer
          ? t("match.board.firstPlayerWillBe", { name: formatMatchHudPlayerLabel(displayMatch.firstPlayer) })
          : displayMatch.activePlayer
            ? `${formatMatchTurnLabel(displayMatch.activePlayer)} · `
            : ""}
        {t("match.board.round", { round: displayMatch.round || 1 })}
      </div>
    );
  };

  const renderFacingBoards = (selfInteractive, oppInteractive) => {
    if (!selfSeat || !opponentSeat) return null;
    return (
      <div className={styles.facingBoards} data-gamepad-zone="board">
        {renderSeatBoardArea(
          opponentSeat,
          oppInteractive,
          opponentUsername || "Opponent",
          { mirrored: true, showBoardVitals: true },
        )}
        <div className={styles.boardPhaseAnchor}>
          {renderMatchPhaseLine(
            `${styles.statusPhaseLine} ${styles.boardPhaseLine}`,
          )}
        </div>
        {renderSeatBoardArea(
          selfSeat,
          selfInteractive,
          displayUsername,
          { mirrored: false, showBoardVitals: true },
        )}
        {renderMatchTimer()}
      </div>
    );
  };

  const renderSeatPeekStrip = (
    peekSeat,
    peekName,
    { emphasize = false } = {},
  ) => {
    if (!peekSeat) return null;
    const { gear, abilities } = listSeatPeekPieces(peekSeat, catalogMap);
    const renderPeekChip = (piece) => {
      const artUrl =
        piece.kind === "aspect"
          ? getAspectCardImageUrl(piece.card)
          : piece.kind === "ability"
            ? getAbilityCardImageUrl({
                ...(piece.ability || piece.card || {}),
                type: (piece.ability || piece.card)?.type || "ability",
              })
            : getEquipmentCardImageUrl(piece.equipment || piece.card);
      const isAbilityTarget =
        piece.kind === "equipment" &&
        Boolean(pendingAbilityTarget) &&
        isMyTurn &&
        pendingTargetEquipmentIds.has(piece.equipment.instanceId);
      const selected =
        piece.kind === "equipment" &&
        selectedEquipmentId === piece.equipment.instanceId;
      const abilityExamOpen =
        piece.kind === "ability" &&
        slottedAbilityExam?.abilityInstanceId === piece.ability.instanceId;
      const examOpen =
        piece.kind !== "ability" &&
        piece.cardId &&
        examCardId === piece.cardId;
      return (
        <button
          key={piece.key}
          type="button"
          className={[
            styles.seatPeekChip,
            piece.kind === "ability" ? styles.seatPeekChipAbility : null,
            piece.expended ? styles.seatPeekChipExpended : null,
            isAbilityTarget ? styles.seatPeekChipTarget : null,
            selected || examOpen || abilityExamOpen
              ? styles.seatPeekChipSelected
              : null,
            TINT_CLASS[
              primaryAttribute(
                piece.ability || piece.card || piece.equipment,
              )
            ] || null,
          ]
            .filter(Boolean)
            .join(" ")}
          data-gamepad-item=""
          aria-label={`${piece.slotLabel} ${piece.name}`}
          title={`${piece.slotLabel}: ${piece.name}`}
          onClick={() => {
            setEquipExpendConfirm(null);
            setEquipCounterExam(null);
            if (piece.kind === "ability") {
              setSelectedEquipmentId(null);
              setSlottedAbilityExam({
                boardSlot: piece.boardSlot || piece.slotKey,
                abilityInstanceId: piece.ability.instanceId,
                cardId: piece.cardId,
              });
              return;
            }
            setSlottedAbilityExam(null);
            if (piece.kind === "aspect") {
              setSelectedEquipmentId(null);
              openCardExam(piece.cardId, piece.foil);
              return;
            }
            if (
              pendingAbilityTarget &&
              !pendingTargetEquipmentIds.has(piece.equipment.instanceId)
            ) {
              return;
            }
            setSelectedEquipmentId(piece.equipment.instanceId);
          }}
        >
          {artUrl ? (
            <span
              aria-hidden="true"
              className={[
                styles.seatPeekChipArt,
                getFoilArtFilterClass(piece.foil),
              ]
                .filter(Boolean)
                .join(" ")}
              style={{ backgroundImage: `url("${artUrl}")` }}
            />
          ) : null}
          <span className={styles.seatPeekChipShade} aria-hidden="true" />
          <span className={styles.seatPeekChipSlot}>{piece.slotLabel}</span>
          <span className={styles.seatPeekChipName}>{piece.name}</span>
        </button>
      );
    };
    return (
      <div
        className={[
          styles.seatPeekStrip,
          emphasize ? styles.seatPeekStripEmphasized : null,
        ]
          .filter(Boolean)
          .join(" ")}
        data-gamepad-zone="peek"
      >
        <div className={styles.seatPeekStripHeader}>
          <span className={styles.seatPeekStripOwner}>
            {formatMatchHudPlayerLabel(peekName) || t("match.board.opp")}
          </span>
        </div>
        {gear.length || abilities.length ? (
          <div className={styles.seatPeekStripRows}>
            <div className={styles.seatPeekStripTrack} aria-label={t("match.board.peekEquipment")}>
              {gear.length ? (
                gear.map(renderPeekChip)
              ) : (
                <div className={styles.seatPeekStripEmpty}>{t("match.board.noEquipment")}</div>
              )}
            </div>
            <div className={styles.seatPeekStripTrack} aria-label={t("match.board.peekAbilities")}>
              {abilities.length ? (
                abilities.map(renderPeekChip)
              ) : (
                <div className={styles.seatPeekStripEmpty}>{t("match.board.noAbilities")}</div>
              )}
            </div>
          </div>
        ) : (
          <div className={styles.seatPeekStripEmpty}>{t("match.board.noOccupiedSlots")}</div>
        )}
      </div>
    );
  };

  const renderSeatVitalsBlock = (seat, name, options = {}) => {
    if (!seat) return null;
    const {
      side = "player",
      mirrored = false,
      floaterEntries = [],
      nameDisabled = false,
      omitHealth = false,
    } = options;
    const statusKeywordProps = {
      activeKeywordId: keywordTip?.id || null,
      onKeywordClick: toggleKeywordTip,
      onMinionClick: (tip, minion) => {
        if (
          side === "player" &&
          minion?.abilityInstanceId &&
          isMyTurn &&
          !isSpectateView &&
          !isReplayView
        ) {
          setKeywordTip({
            id: tip?.id || `minion:${minion.instanceId}`,
            label: tip?.label || minion.name || "Minion",
            description:
              tip?.description ||
              `${minion.name || "Minion"} is an active Minion with ${
                minion.hp || 0
              }/${minion.maxHp || 0} health.`,
            cardId: tip?.cardId || minion.cardId || null,
            unsummon: {
              equipmentInstanceId: minion.equipmentInstanceId || null,
              abilityInstanceId: minion.abilityInstanceId,
            },
          });
          return;
        }
        if (tip?.cardId) {
          openCardExam(tip.cardId);
          return;
        }
        toggleKeywordTip(tip);
      },
    };
    const badges = renderSeatStatusBadges(seat, {
      keywordProps: statusKeywordProps,
    });
    const isResourceAnimTarget = Boolean(actingSeat && seat === actingSeat);
    const resourceIcons = (
      <ActionBarResourceIcons
        resources={
          isResourceAnimTarget
            ? subtractResourceBag(
                seat.resources,
                pendingResourceReveal.resources,
              )
            : seat.resources
        }
        persistentResources={
          isResourceAnimTarget
            ? subtractResourceBag(
                seat.persistentResources,
                pendingResourceReveal.persistent,
              )
            : seat.persistentResources
        }
        delayedResources={seat.delayedResources}
        blockRef={isResourceAnimTarget ? actionBarResourcesRef : null}
        popKeys={isResourceAnimTarget ? resourceIconPops : null}
        onPopEnd={
          isResourceAnimTarget
            ? (key) => {
                setResourceIconPops((current) => {
                  if (!current[key]) return current;
                  const next = { ...current };
                  delete next[key];
                  return next;
                });
              }
            : null
        }
        convertPulses={isResourceAnimTarget ? resourceConvertPulses : null}
        onConvertEnd={
          isResourceAnimTarget
            ? (attribute, pulseKey) => {
                setResourceConvertPulses((current) => {
                  if (current[attribute]?.key !== pulseKey) return current;
                  const next = { ...current };
                  delete next[attribute];
                  return next;
                });
              }
            : null
        }
      />
    );
    const badgeNode = badges ? (
      <span className={styles.statusVitalsBadges}>{badges}</span>
    ) : null;
    const visual =
      name && visualVitalsByUser[name] ? visualVitalsByUser[name] : null;
    const hp = Math.max(0, visual?.hp ?? seat.hp ?? 0);
    const maxHp = Math.max(0, visual?.maxHp ?? seat.maxHp ?? 0);
    const hpPct =
      maxHp > 0 ? Math.max(0, Math.min(100, (hp / maxHp) * 100)) : 0;
    const primaryNode = (
      <span
        key={`${side}-primary-${vitalsHitFlash[side] || 0}`}
        className={`${styles.statusVitalsPrimary}${
          vitalsHitFlash[side] > 0 ? ` ${styles.statusVitalsPrimaryHit}` : ""
        }`}
      >
        <span className={styles.statusVitalsName}>
          <button
            type="button"
            className={[
              styles.statusVitalsNameButton,
              getNameColorTextClassName(
                (displayMatch || match)?.nameColors?.[name]?.id ||
                  (name === displayUsername ? player?.nameColor : null),
              ),
            ]
              .filter(Boolean)
              .join(" ")}
            style={
              getNameColorTextProps(
                (displayMatch || match)?.nameColors?.[name]?.id ||
                  (name === displayUsername ? player?.nameColor : null),
              ).style
            }
            onClick={(event) => {
              event.stopPropagation();
              openPortraitPreview(name, mirrored);
            }}
            disabled={nameDisabled || !name}
          >
            {name || (side === "opponent" ? t("match.board.opp") : t("match.board.you"))}
          </button>
        </span>
        {omitHealth ? null : (
          <>
            {" "}
            <span
              className={styles.statusVitalsHp}
            >
              <span className={styles.statusVitalsHeart} aria-hidden="true">
                ♥
              </span>
              {hp}/{maxHp}
            </span>
          </>
        )}
      </span>
    );
    const floaterLayer = (
      <span className={styles.statusVitalsFloaterLayer} aria-hidden="true">
        {floaterEntries
          .filter((entry) => !isXpVitalsFloaterText(entry.text))
          .map((entry) => (
          <span
            key={entry.key}
            className={`${styles.statusVitalsFloater} ${
              side === "opponent"
                ? styles.statusVitalsFloaterOpponent
                : styles.statusVitalsFloaterPlayer
            }`}
          >
            {renderVitalsFloaterText(entry.text)}
          </span>
        ))}
      </span>
    );
    const hitBurst =
      vitalsHitFlash[side] > 0 ? (
        <span
          key={`${side}-hit-burst-${vitalsHitFlash[side]}`}
          className={styles.statusVitalsHitFlashBurst}
          aria-hidden="true"
        />
      ) : null;
    const level = Math.max(1, Math.min(5, seat.level ?? 1));
    const xp = Math.max(0, seat.xp ?? 0);
    const xpToNext = Math.max(1, seat.xpToNext ?? xpToNextForLevel(level));
    const xpFloaterEntries = floaterEntries.filter((entry) =>
      isXpVitalsFloaterText(entry.text),
    );
    const healthOrb = omitHealth ? (
      <div className={styles.boardHealthOrbWrap}>
        {floaterLayer}
        <div
          className={styles.boardHealthOrb}
          aria-label={t("match.board.healthOrbLabel", { hp, maxHp })}
        >
          <span
            className={styles.boardHealthOrbFill}
            style={{ height: `${hpPct}%` }}
            aria-hidden="true"
          />
          <span className={styles.boardHealthOrbValue}>
            {hp}/{maxHp}
          </span>
        </div>
        <BoardXpGauge
          side={side}
          level={level}
          xp={xp}
          xpToNext={xpToNext}
          floaterEntries={xpFloaterEntries}
          className={
            side === "player" && tutorialHint?.highlightXpGauge
              ? styles.tutorialXpHighlight
              : ""
          }
        />
        {side === "player" && seat?.xpQuests && !isReplayView ? (
          <button
            type="button"
            className={`${styles.boardXpQuestsButton}${
              tutorialHint?.highlightQuests
                ? ` ${styles.tutorialXpHighlight}`
                : ""
            }`}
            onClick={(event) => {
              event.stopPropagation();
              setXpQuestsOpen(true);
            }}
          >
            {t("match.board.quests")}
          </button>
        ) : null}
      </div>
    ) : null;
    return {
      vitals: (
      <span
        className={`${
          side === "opponent"
            ? styles.statusVitalsOpponent
            : styles.statusVitalsPlayer
        }${
          !omitHealth && vitalsHitFlash[side] > 0
            ? ` ${styles.statusVitalsHitFlash}`
            : ""
        }`}
      >
        {omitHealth ? null : hitBurst}
        {omitHealth ? null : floaterLayer}
        <span className={styles.statusVitalsRow}>
          {side === "opponent" ? (
            <>
              {badgeNode}
              {resourceIcons}
              {primaryNode}
            </>
          ) : (
            <>
              {primaryNode}
              {resourceIcons}
              {badgeNode}
            </>
          )}
        </span>
      </span>
      ),
      healthOrb,
      badges: null,
    };
  };

  const renderEquipExpendConfirmPopup = () => {
    if (!equipExpendConfirm) return null;
    const { message } = equipExpendConfirm;
    const fallbackStyle = {
      position: "fixed",
      left: equipExpendConfirm.rect.left,
      top: equipExpendConfirm.rect.top + equipExpendConfirm.rect.height / 2,
      transform: "translateY(-50%)",
      visibility: "hidden",
      zIndex: 12000,
    };
    return (
      <div
        ref={equipExpendConfirmPopupRef}
        className={styles.equipExpendConfirmPopup}
        style={equipExpendConfirmPopupStyle || fallbackStyle}
        onClick={(event) => event.stopPropagation()}
      >
        {message}
      </div>
    );
  };

  const renderEquipCounterExamPopup = () => {
    if (!equipCounterExam) return null;
    const { message } = equipCounterExam;
    const fallbackStyle = {
      position: "fixed",
      left: equipCounterExam.rect.left,
      top: equipCounterExam.rect.top + equipCounterExam.rect.height / 2,
      transform: "translateY(-50%)",
      visibility: "hidden",
      zIndex: 12000,
    };
    return (
      <div
        ref={equipCounterExamPopupRef}
        className={styles.equipExpendConfirmPopup}
        style={equipCounterExamPopupStyle || fallbackStyle}
        onClick={(event) => event.stopPropagation()}
      >
        {message}
      </div>
    );
  };

  const handleEquipCounterExamClick = (event, counterKey, message) => {
    event.stopPropagation();
    if (equipCounterExam?.key === counterKey) {
      setEquipCounterExam(null);
      setEquipCounterExamPopupStyle(null);
      return;
    }
    setEquipExpendConfirm(null);
    setEquipExpendConfirmPopupStyle(null);
    const rect = event.currentTarget.getBoundingClientRect();
    setEquipCounterExamPopupStyle(null);
    setEquipCounterExam({
      key: counterKey,
      message,
      rect: {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      },
    });
  };

  const handleEquipExpendConfirmClick = (event, key, actionText, run) => {
    event.stopPropagation();
    if (equipExpendConfirm?.key === key) {
      setEquipExpendConfirm(null);
      setEquipExpendConfirmPopupStyle(null);
      run();
      return;
    }
    setEquipCounterExam(null);
    setEquipCounterExamPopupStyle(null);
    const rect = event.currentTarget.getBoundingClientRect();
    setEquipExpendConfirmPopupStyle(null);
    setEquipExpendConfirm({
      key,
      message: t("match.board.tapAgainToExpend", { action: actionText }),
      rect: {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      },
    });
  };

  const renderEquipResourceGrantControl = (equipment, shockStacks) => {
    const grantChoices = listEquipmentResourceGrantChoices(equipment);
    if (!grantChoices.length) return null;
    const directResources = resourceGrantEntries(equipment.resources);

    if (directResources.length) {
      return (
        <div
          className={styles.equipResourceGrants}
          aria-hidden="true"
        >
          <ResourceGrantChoiceIconStrip
            choices={grantChoices}
            shockStacks={shockStacks}
            iconClassName={styles.equipResourceGrantIcon}
          />
          <DelayedResourceGrantIcons
            equipment={equipment}
            className={styles.equipResourceGrantIcon}
          />
        </div>
      );
    }

    return (
      <div className={styles.equipResourceGrants} aria-hidden="true">
        {grantChoices.map((entries, choiceIndex) => {
          const persistAction = listEquipmentEffectActions(
            equipment,
            actingSeat,
          ).find(
            (action) =>
              action.persistGrants?.length &&
              JSON.stringify(action.persistGrants) === JSON.stringify(entries),
          );
          return (
            <span
              key={`resource-choice-${choiceIndex}`}
              className={styles.equipResourceGrantDisplay}
            >
              <ResourceGrantIconListWithShock
                entries={entries}
                shockStacks={shockStacks}
                persist={Boolean(persistAction)}
                className={styles.equipResourceGrantIcon}
              />
            </span>
          );
        })}
      </div>
    );
  };

  const renderRepeatLastRoundButton = () => null;

  const renderBodyBoard = (
    seat,
    ownBoard,
    inspectOnly = false,
    options = {},
  ) => {
    if (!seat) return null;
    const { mirrored = false, showBoardVitals = false } = options;
    const boardOwnerName =
      seat?.username ||
      (mirrored
        ? opponentUsername || null
        : displayUsername || username || null);
    const boardArtUrl = getGameBoardImageUrl(
      normalizeBoardId(
        (displayMatch || match)?.boards?.[boardOwnerName] ||
          (boardOwnerName === username ? player?.board : null) ||
          DEFAULT_BOARD_ID,
      ),
    );
    const shockStacks = ownBoard ? Math.max(0, seat.shockStacks || 0) : 0;
    const targetingEquipment =
      Boolean(pendingAbilityTarget) &&
      isMyTurn &&
      !inspectOnly &&
      (isWorkshopMatch || !ownBoard);
    const targetingAspect =
      targetingEquipment && pendingValidAspectTarget;
    const selectedHandEquipmentHighlight = (() => {
      if (!ownBoard || inspectOnly || isReplayView || isSpectateView) {
        return null;
      }
      if (!isMyTurn && !isWorkshopMatch) return null;
      if (!handMenu || handMenu.deck !== "equipment") return null;
      const handCard = (seat.hand || []).find(
        (entry) => entry.instanceId === handMenu.instanceId,
      );
      if (!handCard || handCard.hidden) return null;
      const catalogCard = catalogMap[handCard.cardId];
      if (!catalogCard) return null;
      if (
        !cardHasType(catalogCard, "equipment") &&
        !catalogCard.slot
      ) {
        return null;
      }
      const card = handCard.token
        ? { ...catalogCard, token: true }
        : catalogCard;
      const boardSlot = resolveCardBoardSlot(card);
      if (!boardSlot) return null;
      if (
        !isWorkshopMatch &&
        seat.playedEquipment &&
        !isNinjutsuFreeThrowableTokenEquip(seat, catalogMap, handCard, card)
      ) {
        return null;
      }
      return {
        handCard,
        card,
        boardSlot,
        canEquip: canPlayEquipmentHandCard(handCard, card),
      };
    })();
    const resolvePendingAspectTarget = () => {
      send({
        type: "ai:resolveAbilityTarget",
        target: { aspect: true },
      });
      setSelectedEquipmentId(null);
      setSlottedAbilityExam(null);
      setEquipExpendConfirm(null);
      setEquipCounterExam(null);
    };
    const resolvePendingEquipmentTarget = (equipmentInstanceId) => {
      send({
        type: "ai:resolveAbilityTarget",
        target: { equipmentInstanceId },
      });
      setSelectedEquipmentId(null);
      setSlottedAbilityExam(null);
      setEquipExpendConfirm(null);
      setEquipCounterExam(null);
    };
    const boardVitalsSide = mirrored ? "opponent" : "player";
    const boardVitalsName = mirrored
      ? opponentUsername || "Opponent"
      : displayUsername;
    const boardVitalsParts =
      showBoardVitals
        ? renderSeatVitalsBlock(seat, boardVitalsName, {
            side: boardVitalsSide,
            mirrored,
            nameDisabled: mirrored && !opponentUsername,
            omitHealth: true,
            floaterEntries: vitalsFloaterDisplay.filter(
              (entry) => entry.side === boardVitalsSide,
            ),
          })
        : null;
    const aspectId =
      typeof seat.aspectId === "string" && seat.aspectId
        ? seat.aspectId
        : null;
    const aspectCard = aspectId ? catalogMap[aspectId] || null : null;
    const aspectFoil = seat.aspectFoil || null;
    const canSwitchWorkshopAspect =
      isWorkshopMatch && ownBoard && !inspectOnly && !isReplayView && !isSpectateView;
    const openBoardAspect = () => {
      setEquipExpendConfirm(null);
      setEquipCounterExam(null);
      setSlottedAbilityExam(null);
      setSelectedEquipmentId(null);
      if (canSwitchWorkshopAspect) {
        openWorkshopAspectPicker();
        return;
      }
      if (aspectCard) {
        openCardExam(aspectCard.id, aspectFoil);
      }
    };
    const renderBoardAspectSlot = () => (
      <div className={`${styles.slotColumn} ${styles.slotAspect}`}>
        <div
          className={`${styles.slot}${
            aspectCard ? ` ${styles.slotFilled}` : ""
          }`}
        >
          {aspectCard ? (
            <div className={styles.equipBlockWrap}>
              <div
                className={`${styles.equipBlock} ${styles.equipBlockTraditional}${
                  targetingAspect ? ` ${styles.equipBlockTarget}` : ""
                }${ownBoard ? ` ${styles.equipBlockOwn}` : ""}`}
                data-combat-anchor={
                  boardOwnerName ? `aspect:${boardOwnerName}` : undefined
                }
                onClick={(event) => {
                  event.stopPropagation();
                  if (targetingAspect) {
                    resolvePendingAspectTarget();
                    return;
                  }
                  openBoardAspect();
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    event.stopPropagation();
                    if (targetingAspect) {
                      resolvePendingAspectTarget();
                      return;
                    }
                    openBoardAspect();
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={
                  targetingAspect
                    ? t("match.board.targetAspect")
                    : aspectCard.name || t("match.board.aspect")
                }
                data-gamepad-item=""
              >
                <div className={styles.equipTraditionalCard}>
                  <KeywordTipContext.Provider value={null}>
                    <CardFace
                      card={aspectCard}
                      foilEffect={aspectFoil}
                      showPowerBadge={false}
                    />
                  </KeywordTipContext.Provider>
                </div>
              </div>
            </div>
          ) : (
            <div
              className={`${styles.slotEmpty}${
                targetingAspect ? ` ${styles.equipBlockTarget}` : ""
              }`}
              role={
                canSwitchWorkshopAspect || targetingAspect
                  ? "button"
                  : undefined
              }
              tabIndex={
                canSwitchWorkshopAspect || targetingAspect ? 0 : undefined
              }
              aria-label={t("match.board.aspectSlotLabel")}
              data-gamepad-item={
                canSwitchWorkshopAspect || targetingAspect ? "" : undefined
              }
              onClick={
                canSwitchWorkshopAspect || targetingAspect
                  ? (event) => {
                      event.stopPropagation();
                      if (targetingAspect) {
                        resolvePendingAspectTarget();
                        return;
                      }
                      openBoardAspect();
                    }
                  : undefined
              }
              onKeyDown={
                canSwitchWorkshopAspect || targetingAspect
                  ? (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        event.stopPropagation();
                        if (targetingAspect) {
                          resolvePendingAspectTarget();
                          return;
                        }
                        openBoardAspect();
                      }
                    }
                  : undefined
              }
            >
              {t("match.board.aspectSlot")}
            </div>
          )}
        </div>
        {boardVitalsParts?.healthOrb ? (
          <div className={styles.slotHealth}>{boardVitalsParts.healthOrb}</div>
        ) : null}
      </div>
    );
    return (
      <div
        className={`${styles.bodyBoard}${
          mirrored ? ` ${styles.bodyBoardMirrored}` : ""
        }${showBoardVitals ? ` ${styles.bodyBoardWithVitals}` : ""}`}
        data-board-owner={boardOwnerName || undefined}
        style={
          boardArtUrl
            ? {
                "--ai-player-board-image": `url("${boardArtUrl}")`,
              }
            : undefined
        }
        onClick={() => {
          setEquipExpendConfirm(null);
          setEquipCounterExam(null);
        }}
      >
        {boardVitalsParts?.vitals ? (
          <div className={styles.boardAspectVitals}>
            {boardVitalsParts.vitals}
          </div>
        ) : null}
        {mirrored ? null : renderBoardAspectSlot()}
        <KeywordTipContext.Provider value={null}>
        {BODY_LAYOUT.map((slot) => {
          const equipment = boardEquipmentForSlot(seat, slot.key);
          const slotLabel = { weapon: t("match.board.slotWeapon"), armor: t("match.board.slotArmor"), trinket: t("match.board.slotTrinket") }[slot.key] || slot.label;
          const isEquipHandTarget =
            selectedHandEquipmentHighlight?.boardSlot === slot.key;
          const equipHandTargetReady =
            Boolean(isEquipHandTarget) &&
            selectedHandEquipmentHighlight.canEquip &&
            !equipment;
          const equipHandTargetClass = !isEquipHandTarget
            ? ""
            : selectedHandEquipmentHighlight.canEquip && !equipment
              ? styles.equipSlotTargetReady
              : styles.equipSlotTarget;
          const occupyingMinion = equipment
            ? getEquipmentOccupyingMinion(equipment, seat)
            : null;
          const occupyingAura =
            equipment && !occupyingMinion
              ? getEquipmentOccupyingAura(equipment, seat)
              : null;
          const boardOccupant = occupyingMinion || occupyingAura;
          const activeMinions = equipment
            ? getEquipmentActiveMinions(equipment, seat)
            : [];
          const occupyingAbility = occupyingMinion
            ? findAbilityForMinion(equipment, occupyingMinion, seat)
            : occupyingAura
              ? findAbilityForAura(equipment, occupyingAura, seat)
              : null;
          const occupyingCatalog =
            boardOccupant?.cardId && catalogMap[boardOccupant.cardId]
              ? catalogMap[boardOccupant.cardId]
              : occupyingAbility;
          const displayCard = boardOccupant
            ? occupyingCatalog || occupyingAbility || boardOccupant
            : equipment;
          const equipmentIsToken = Boolean(!boardOccupant && equipment?.token);
          const displayFoil = boardOccupant
            ? boardOccupant.foilEffect ||
              occupyingAbility?.foilEffect ||
              null
            : equipmentIsToken
              ? null
              : equipment?.foilEffect || null;
          const displayCardId = boardOccupant
            ? boardOccupant.cardId || occupyingAbility?.cardId || null
            : equipment?.cardId || null;
          const equipmentArtUrl = boardOccupant
            ? getAbilityCardImageUrl({
                ...(occupyingCatalog || {}),
                cardId: displayCardId,
                id: displayCardId,
                type:
                  occupyingCatalog?.type ||
                  (occupyingAura
                    ? ["ability", "aura"]
                    : ["ability", "minion"]),
              }) || getCardImageUrl(occupyingCatalog)
            : equipment
              ? getEquipmentCardImageUrl(equipment)
              : null;
          const equipmentFrameCard =
            boardOccupant && occupyingCatalog
              ? occupyingCatalog
              : getEquipmentFrameCard(equipment, catalogMap);
          const equipmentBorderless = isBorderlessFoil(displayFoil);
          const equipmentTraditional = isTraditionalFoil(displayFoil);
          const equipmentFrameUrl =
            displayCard && !equipmentBorderless && !equipmentTraditional
              ? getCardFrameUrl(equipmentFrameCard)
              : null;
          const equipmentHybridOutline =
            displayCard &&
            !equipmentFrameUrl &&
            !equipmentBorderless &&
            !getSimpleCardFrameKey(equipmentFrameCard, displayFoil)
              ? getHybridOutlineProps(
                  equipmentFrameCard || displayCard,
                  equipmentTraditional
                    ? TRADITIONAL_ATTRIBUTE_COLOR
                    : ATTRIBUTE_COLOR_VAR,
                )
              : { className: "", style: undefined };
          const isAbilityTarget =
            targetingEquipment &&
            equipment &&
            pendingTargetEquipmentIds.has(equipment.instanceId);
          const guideEquipmentUsable = equipment
            ? matchGuideEquipmentUsable(equipment, ownBoard && !inspectOnly)
            : false;
          const displayName = occupyingMinion
            ? occupyingMinion.name || t("match.board.minion")
            : occupyingAura
              ? occupyingAura.name || t("match.board.aura")
              : equipment?.name;
          const abilityStrip = !boardOccupant ? (
              <div
                className={
                  mirrored ? styles.abilitySlotsOver : styles.abilitySlotsUnder
                }
              >
                {renderAbilitySlots(
                  slot.key,
                  ownBoard && !inspectOnly,
                  inspectOnly,
                  seat,
                  "actionBar",
                )}
              </div>
            ) : null;
          const renderEquipmentBadges = () => (
            <>
              {!boardOccupant && equipmentHasStatusEffects(equipment) ? (
                <EquipmentStatusMarks equipment={equipment} />
              ) : null}
              {!boardOccupant && !equipmentCannotExpend(equipment) ? (
                <button
                  type="button"
                  className={`${styles.boardReadyStar}${
                    keywordTip?.id === "expend"
                      ? ` ${styles.keywordIconButtonActive}`
                      : ""
                  }${
                    guideEquipmentUsable
                      ? ` ${styles.matchGuideDiamondGlow}`
                      : ""
                  }`}
                  aria-label={t("match.board.expend")}
                  title={t("match.board.expend")}
                  onClick={(event) => {
                    event.stopPropagation();
                    toggleKeywordTip(getKeywordById("expend"));
                  }}
                >
                  <ExpendActivateIcon />
                </button>
              ) : null}
              {occupyingMinion ? (
                <span
                  className={styles.equipMinionHpBadge}
                  aria-label={t("match.board.minionHealthLabel", { name: occupyingMinion.name || t("match.board.minion"), hp: occupyingMinion.hp || 0, maxHp: occupyingMinion.maxHp || 0 })}
                >
                  {occupyingMinion.hp || 0}/{occupyingMinion.maxHp || 0}
                  {activeMinions.length > 1
                    ? ` · +${activeMinions.length - 1}`
                    : ""}
                </span>
              ) : occupyingAura ? (
                <span
                  className={styles.equipMinionHpBadge}
                  aria-label={t("match.board.auraActiveLabel", { name: occupyingAura.name || t("match.board.aura") })}
                >
                  {t("match.board.aura")}
                </span>
              ) : (
                (() => {
                  const localCounters = getEquipmentLocalCounters(equipment);
                  if (!localCounters.length) return null;
                  return (
                    <div className={styles.equipLocalCounterRow}>
                      {localCounters.map((counter) => {
                        const counterKey = `${equipment.instanceId}:${counter.id}`;
                        const counterValue =
                          counter.max != null
                            ? `${counter.amount}/${counter.max}`
                            : String(counter.amount);
                        const counterMessage = `${counter.label}: ${counterValue}`;
                        const counterKeyword = getKeywordById(counter.id);
                        const counterExamOpen = counterKeyword
                          ? keywordTip?.id === counterKeyword.id
                          : equipCounterExam?.key === counterKey;
                        return (
                          <button
                            key={counter.id}
                            type="button"
                            className={`${styles.equipLocalCounterBadge}${
                              counter.iconUrl || counter.chargeIcon
                                ? ` ${styles.equipLocalCounterBadgeWithIcon}`
                                : ""
                            }${
                              counterExamOpen
                                ? ` ${styles.keywordIconButtonActive}`
                                : ""
                            }`}
                            aria-label={counterMessage}
                            aria-pressed={counterExamOpen}
                            onClick={(event) => {
                              event.stopPropagation();
                              setEquipCounterExam(null);
                              setEquipCounterExamPopupStyle(null);
                              if (counterKeyword) {
                                toggleKeywordTip({
                                  ...counterKeyword,
                                  description: `${counterKeyword.description} Current ${counter.label}: ${counterValue}.`,
                                });
                                return;
                              }
                              handleEquipCounterExamClick(
                                event,
                                counterKey,
                                counterMessage,
                              );
                            }}
                          >
                            {counter.chargeIcon ? (
                              <ChargeIcon
                                className={styles.equipLocalCounterChargeIcon}
                              />
                            ) : null}
                            {counter.iconUrl ? (
                              <MaskIcon
                                src={counter.iconUrl}
                                className={styles.equipLocalCounterIcon}
                              />
                            ) : null}
                            <span>{counterValue}</span>
                          </button>
                        );
                      })}
                    </div>
                  );
                })()
              )}
            </>
          );
          return (
            <div
              key={slot.key}
              className={`${styles.slotColumn} ${slot.className}`}
            >
              {mirrored ? abilityStrip : null}
              <div
                className={`${styles.slot}${
                  equipment ? ` ${styles.slotFilled}` : ""
                }${equipHandTargetClass ? ` ${equipHandTargetClass}` : ""}`}
              >
              {equipment ? (
                <div
                  className={`${styles.equipBlockWrap}${
                    boardMenu?.kind === "equipment" &&
                    boardMenu.instanceId === equipment.instanceId
                      ? ` ${styles.boardCardSlotActionsOpen}`
                      : ""
                  }`}
                >
                  <div
                    className={`${styles.equipBlock} ${
                      equipmentFrameUrl
                        ? styles.equipBlockWithFrame
                        : equipmentBorderless
                          ? styles.equipBlockBorderless
                          : equipmentHybridOutline.className ||
                            TINT_CLASS[primaryAttribute(displayCard)] ||
                            ""
                    } ${equipmentArtUrl ? styles.equipBlockWithArt : ""} ${
                      equipmentTraditional ? styles.equipBlockTraditional : ""
                    } ${equipmentIsToken ? styles.equipBlockToken : ""} ${
                      (occupyingMinion
                        ? occupyingMinion.expended
                        : equipment.expended)
                        ? styles.equipBlockExpended
                        : ""
                    } ${
                      selectedEquipmentId === equipment.instanceId ||
                      (boardMenu?.kind === "equipment" &&
                        boardMenu.instanceId === equipment.instanceId)
                        ? styles.equipBlockSelected
                        : ""
                    } ${isAbilityTarget ? styles.equipBlockTarget : ""}${
                      ownBoard ? styles.equipBlockOwn : ""
                    }${
                      boardOccupant ? ` ${styles.equipBlockMinionActive}` : ""
                    }${
                      cardPlayPendingIds.has(equipment.instanceId)
                        ? ` ${styles.cardPlayPending}`
                        : ""
                    }${
                      ownBoard &&
                      !inspectOnly &&
                      isMyTurn &&
                      pendingTraitEquipmentIds.has(equipment.instanceId)
                        ? ` ${styles.cardTargetMatch}`
                        : ""
                    }${tutorialEquipmentHighlightClass(equipment)}${
                      guideEquipmentUsable ? ` ${styles.matchGuideGlow}` : ""
                    }`}
                    style={equipmentHybridOutline.style}
                    data-combat-anchor={
                      equipment.instanceId
                        ? `equipment:${equipment.instanceId}`
                        : undefined
                    }
                    onClick={() => {
                      if (pendingAbilityTarget && !isAbilityTarget) return;
                      if (isAbilityTarget) {
                        resolvePendingEquipmentTarget(equipment.instanceId);
                        return;
                      }
                      setEquipExpendConfirm(null);
                      setEquipCounterExam(null);
                      setSlottedAbilityExam(null);
                      const canOpenBoardMenu =
                        ownBoard &&
                        !inspectOnly &&
                        !isReplayView &&
                        !isSpectateView &&
                        (isMyTurn || isWorkshopMatch);
                      if (canOpenBoardMenu || cardFocusHost) {
                        setSelectedEquipmentId(null);
                        setHandMenu(null);
                        setSelectedHand(null);
                        setBoardMenu((current) =>
                          current?.kind === "equipment" &&
                          current.instanceId === equipment.instanceId
                            ? null
                            : {
                                kind: "equipment",
                                instanceId: equipment.instanceId,
                                examineOnly: !canOpenBoardMenu,
                              },
                        );
                        return;
                      }
                      setBoardMenu(null);
                      setSelectedEquipmentId(equipment.instanceId);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        if (pendingAbilityTarget && !isAbilityTarget) return;
                        if (isAbilityTarget) {
                          resolvePendingEquipmentTarget(equipment.instanceId);
                          return;
                        }
                        setEquipExpendConfirm(null);
                        setEquipCounterExam(null);
                        setSlottedAbilityExam(null);
                        const canOpenBoardMenu =
                          ownBoard &&
                          !inspectOnly &&
                          !isReplayView &&
                          !isSpectateView &&
                          (isMyTurn || isWorkshopMatch);
                        if (canOpenBoardMenu || cardFocusHost) {
                          setSelectedEquipmentId(null);
                          setHandMenu(null);
                          setSelectedHand(null);
                          setBoardMenu((current) =>
                            current?.kind === "equipment" &&
                            current.instanceId === equipment.instanceId
                              ? null
                              : {
                                  kind: "equipment",
                                  instanceId: equipment.instanceId,
                                  examineOnly: !canOpenBoardMenu,
                                },
                          );
                          return;
                        }
                        setBoardMenu(null);
                        setSelectedEquipmentId(equipment.instanceId);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    data-gamepad-item=""
                  >
                  {equipmentTraditional && equipmentFrameCard ? (
                    <ScaledCardFace
                      className={styles.equipScaledCard}
                      card={
                        equipmentIsToken
                          ? { ...equipmentFrameCard, token: true }
                          : equipmentFrameCard
                      }
                      foilEffect={displayFoil}
                      showPowerBadge={false}
                    />
                  ) : equipmentArtUrl ? (
                    <div
                      aria-hidden="true"
                      className={[
                        styles.equipArt,
                        getFoilArtFilterClass(displayFoil),
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      <AiLazyImage
                        className={styles.equipArtMedia}
                        src={equipmentArtUrl}
                        alt=""
                        decoding="async"
                        draggable={false}
                      />
                    </div>
                  ) : null}
                  {equipmentFrameUrl ? (
                    <CardFrameOverlay frameUrl={equipmentFrameUrl} />
                  ) : null}
                  {!equipmentTraditional ? (
                    <FoilOverlay
                      foilEffect={displayFoil}
                      cardId={displayCardId}
                    />
                  ) : null}
                  {renderEquipmentBadges()}
                  {(() => {
                    if (boardOccupant) {
                      if (equipmentTraditional) return null;
                      return (
                        <div className={styles.equipNameRow}>
                          <FitEquipName className={styles.equipName}>
                            {displayName}
                          </FitEquipName>
                        </div>
                      );
                    }
                    const grantChoices =
                      listEquipmentResourceGrantChoices(equipment);
                    const resourcesControl =
                      grantChoices.length &&
                      (!equipmentTraditional || shockStacks > 0)
                        ? renderEquipResourceGrantControl(
                            equipment,
                            shockStacks,
                          )
                        : null;
                    if (equipmentTraditional && !resourcesControl) return null;
                    return (
                      <div
                        className={`${styles.equipNameRow} ${
                          equipmentTraditional
                            ? styles.equipNameRowTraditional
                            : ""
                        }`}
                      >
                        {!equipmentTraditional ? (
                          <FitEquipName
                            className={styles.equipName}
                            trailing={
                              <EquipmentTierIcons
                                tier={getEquipmentTier(equipmentFrameCard)}
                                className={styles.equipNameTierIcons}
                              />
                            }
                          >
                            {equipment.name}
                          </FitEquipName>
                        ) : null}
                        {resourcesControl}
                      </div>
                    );
                  })()}
                  {!boardOccupant ? (
                    (() => {
                      if (equipment.expendDisabled) {
                        return (
                          <div className={styles.equipExpendCost}>{t("match.board.hexed")}</div>
                        );
                      }
                      const expendCostText = formatCost(equipment.expendCost);
                      if (!expendCostText) return null;
                      return (
                        <div className={styles.equipExpendCost}>
                          {t("match.board.expendCost", { cost: expendCostText })}
                        </div>
                      );
                    })()
                  ) : null}
                  </div>
                  {boardMenu?.kind === "equipment" &&
                  boardMenu.instanceId === equipment.instanceId
                    ? renderInCardFocus(
                        renderBoardEquipmentActionsMenu(equipment, seat),
                        {
                          key: `equipment:${equipment.instanceId}`,
                          card: equipmentFrameCard
                            ? equipmentIsToken
                              ? { ...equipmentFrameCard, token: true }
                              : equipmentFrameCard
                            : displayCard,
                          foilEffect: displayFoil,
                          overlayClassName: equipmentTraditional
                            ? styles.equipBlockTraditional
                            : "",
                          overlay: (
                            <>
                              {renderEquipmentBadges()}
                              {equipmentTraditional &&
                              !boardOccupant &&
                              shockStacks > 0 ? (
                                <div
                                  className={`${styles.equipNameRow} ${styles.equipNameRowTraditional}`}
                                >
                                  {renderEquipResourceGrantControl(
                                    equipment,
                                    shockStacks,
                                  )}
                                </div>
                              ) : null}
                            </>
                          ),
                        },
                      )
                    : null}
                </div>
              ) : (
                <div
                  className={`${styles.slotEmpty} ${styles.equipSlotEmpty}${
                    equipHandTargetClass ? ` ${equipHandTargetClass}` : ""
                  }`}
                  role="button"
                  tabIndex={0}
                  title={
                    isEquipHandTarget && selectedHandEquipmentHighlight
                      ? t("match.board.equipHere")
                      : t("match.board.slotTitle", { slot: slotLabel })
                  }
                  aria-label={t("match.board.slotAriaLabel", { slot: slotLabel })}
                  data-gamepad-item=""
                  onClick={
                    isEquipHandTarget && selectedHandEquipmentHighlight
                      ? (event) => {
                          event.stopPropagation();
                          if (!equipHandTargetReady) return;
                          playHandEquipmentCard(
                            selectedHandEquipmentHighlight.handCard,
                            selectedHandEquipmentHighlight.card,
                            slot.key,
                          );
                        }
                      : undefined
                  }
                  onKeyDown={
                    isEquipHandTarget && selectedHandEquipmentHighlight
                      ? (event) => {
                          if (event.key !== "Enter" && event.key !== " ") {
                            return;
                          }
                          event.preventDefault();
                          event.stopPropagation();
                          if (!equipHandTargetReady) return;
                          playHandEquipmentCard(
                            selectedHandEquipmentHighlight.handCard,
                            selectedHandEquipmentHighlight.card,
                            slot.key,
                          );
                        }
                      : undefined
                  }
                >
                  {BOARD_EMPTY_SLOT_ICON_URL[slot.key] ? (
                    <span className={styles.boardSlotEmptyIconGlow}>
                      <MaskIcon
                        src={BOARD_EMPTY_SLOT_ICON_URL[slot.key]}
                        className={styles.boardSlotEmptyIcon}
                      />
                    </span>
                  ) : (
                    t("match.board.slotTitle", { slot: slotLabel })
                  )}
                </div>
              )}
              </div>
              {mirrored ? null : abilityStrip}
            </div>
          );
        })}
        </KeywordTipContext.Provider>
        {mirrored ? renderBoardAspectSlot() : null}
        {inspectOnly ? null : renderRepeatLastRoundButton(seat, ownBoard)}
      </div>
    );
  };

  const getEquipmentEffectLabel = (equipment, seat = null, labelOptions = {}) => {
    const activatedEffect = equipment?.activatedEffect;
    if (!activatedEffect) return null;
    const adventureOptions = {
      adventureMonsterEffects: Boolean(displayMatch?.adventure),
      equipment,
      seat,
      omitChargeCost: Boolean(getEffectConsumeChargeCost(activatedEffect)),
      omitDurability: true,
      ...labelOptions,
    };
    if (Array.isArray(activatedEffect.choose)) {
      const parts = activatedEffect.choose
        .map((entry) =>
          getEquipmentEffectLabel(
            { ...equipment, activatedEffect: entry },
            seat,
            labelOptions,
          ),
        )
        .filter(Boolean);
      return parts.length ? parts.join(" OR ") : null;
    }
    if (Array.isArray(activatedEffect)) {
      const parts = activatedEffect
        .map((entry) =>
          getEquipmentEffectLabel(
            { ...equipment, activatedEffect: entry },
            seat,
            labelOptions,
          ),
        )
        .filter(Boolean);
      return parts.length ? parts.join(" · ") : null;
    }
    const adventureLabel = formatAdventureMonsterEffectText(
      activatedEffect,
      adventureOptions,
    );
    if (adventureLabel) return adventureLabel;
    if (activatedEffect.id === "damageOpponent") {
      if (activatedEffect.chargeDamage) {
        return formatActivatedEffectText(activatedEffect, {
          omitCost: true,
          ...adventureOptions,
        });
      }
      const ignoreArmorSuffix = activatedEffect.ignoreArmor
        ? " · Ignore Armor"
        : (activatedEffect.effectiveIgnorePhysicalArmor ??
              activatedEffect.ignorePhysicalArmor ??
              0) > 0
          ? ` · Ignore ${
              activatedEffect.effectiveIgnorePhysicalArmor ??
              activatedEffect.ignorePhysicalArmor
            } Physical Armor`
          : "";
      const encoreSuffix = activatedEffect.encore ? " · Encore" : "";
      if (
        Array.isArray(activatedEffect.damageParts) &&
        activatedEffect.damageParts.length
      ) {
        const partText = activatedEffect.damageParts
          .map(
            (part) =>
              `${part.amount || 0} ${part.damageType || "physical"}`,
          )
          .join(" + ");
        const total =
          activatedEffect.effectiveAmount ??
          activatedEffect.damageParts.reduce(
            (sum, part) => sum + (part.amount || 0),
            0,
          );
        return `${partText} (${total})${ignoreArmorSuffix}${encoreSuffix}`;
      }
      return `${activatedEffect.effectiveAmount ?? activatedEffect.amount ?? 0} ${
        activatedEffect.damageType || "physical"
      } damage${ignoreArmorSuffix}${encoreSuffix}`;
    }
    if (activatedEffect.id === "damageSelf") {
      const ignoreArmorSuffix = activatedEffect.ignoreArmor
        ? " · Ignore Armor"
        : "";
      return `Take ${activatedEffect.amount || 0} ${
        activatedEffect.damageType || "physical"
      } damage${ignoreArmorSuffix}`;
    }
    if (activatedEffect.id === "bleed") {
      const damage =
        activatedEffect.effectiveDamage ?? activatedEffect.damage ?? 0;
      const stacks =
        activatedEffect.effectiveAmount ?? activatedEffect.amount ?? 0;
      return damage > 0
        ? `${damage} ${activatedEffect.damageType || "physical"} · Bleed ${stacks}`
        : `Bleed ${stacks}`;
    }
    if (activatedEffect.id === "poison") {
      const damage =
        activatedEffect.effectiveDamage ?? activatedEffect.damage ?? 0;
      const stacks =
        activatedEffect.effectiveAmount ?? activatedEffect.amount ?? 0;
      return damage > 0
        ? `${damage} ${activatedEffect.damageType || "physical"} · Poison ${stacks}`
        : `Poison ${stacks}`;
    }
    if (activatedEffect.id === "healSelf") {
      const healAmount =
        activatedEffect.effectiveAmount ?? activatedEffect.amount ?? 0;
      const cleanseAmount = activatedEffect.removePoison || 0;
      if (cleanseAmount <= 0) return `Heal ${healAmount}`;
      return healAmount > 0
        ? `Heal ${healAmount} · Cleanse Poison ${cleanseAmount}`
        : `Cleanse Poison ${cleanseAmount}`;
    }
    if (activatedEffect.id === "maxHealthIncrease") {
      return `Max Health ${
        activatedEffect.effectiveAmount ?? activatedEffect.amount ?? 0
      }`;
    }
    if (activatedEffect.id === "temporaryPhysicalArmor") {
      return `Physical Armor ${
        activatedEffect.effectiveAmount ?? activatedEffect.amount ?? 0
      }`;
    }
    if (activatedEffect.id === "temporaryMagicArmor") {
      return `Magic Armor ${
        activatedEffect.effectiveAmount ?? activatedEffect.amount ?? 0
      }`;
    }
    if (activatedEffect.id === "regeneration") {
      return `Regeneration ${
        activatedEffect.effectiveAmount ?? activatedEffect.amount ?? 0
      }`;
    }
    if (activatedEffect.id === "persist") {
      return null;
    }
    if (activatedEffect.id === "convertResource") {
      return formatActivatedEffectText(activatedEffect, adventureOptions);
    }
    return formatActivatedEffectText(activatedEffect, adventureOptions);
  };

  const resolveChosenIdentityActionEntries = (effect, equipment) => {
    const identity = equipment?.chosenIdentity;
    if (!identity || !effectDependsOnIdentity(effect)) return null;
    const entries = resolveIdentityEffectEntries(effect, identity).filter(
      (entry) =>
        entry?.id && entry.id !== "persist" && entry.id !== "grantResources",
    );
    return entries.length ? entries : null;
  };

  const getIdentityActionLabelAndDurability = (
    equipment,
    seat,
    identityEntries,
  ) => ({
    label: getEquipmentEffectLabel(
      {
        ...equipment,
        activatedEffect:
          identityEntries.length === 1 ? identityEntries[0] : identityEntries,
      },
      seat,
      { omitPayment: true },
    ),
    durabilityCost: identityEntries.reduce(
      (sum, entry) => sum + getEffectDurabilityCost(entry, { equipment }),
      0,
    ),
  });

  const listEquipmentEffectActions = (equipment, seat = null) => {
    const activated = equipment?.activatedEffect;
    if (!activated) return [];
    if (Array.isArray(activated.choose)) {
      return activated.choose
        .map((effect, index) => {
          const persistGrants = persistGrantEntries(effect);
          const resourceGrants = resourceGrantEntriesFromEffect(effect);
          const identityEntries = resourceGrants.length
            ? null
            : resolveChosenIdentityActionEntries(effect, equipment);
          const identityParts = identityEntries
            ? getIdentityActionLabelAndDurability(
                equipment,
                seat,
                identityEntries,
              )
            : null;
          const label = resourceGrants.length
            ? null
            : identityParts
              ? identityParts.label
              : getEquipmentEffectLabel(
                  { ...equipment, activatedEffect: effect },
                  seat,
                );
          if (!persistGrants.length && !resourceGrants.length && !label) {
            return null;
          }
          const identityCost = identityEntries
            ? identityEntries.reduce(
                (sum, entry) => sum + getIdentityCostAmount(entry),
                0,
              )
            : 0;
          return {
            index,
            persistGrants,
            resourceGrants,
            label,
            outputDelta: resolveActivatedEffectOutputDelta(effect),
            cost: mergeCostMaps(
              equipment.expendCost,
              identityEntries
                ? identityEntries.reduce(
                    (sum, entry) => mergeCostMaps(sum, entry.cost),
                    {},
                  )
                : effect.cost,
              identityCost > 0
                ? { [equipment.chosenIdentity]: identityCost }
                : null,
            ),
            chargeCost: getEffectConsumeChargeCost(effect),
            durabilityCost: identityParts
              ? identityParts.durabilityCost
              : getEffectPrefixDurabilityCost(effect, {
                  equipment,
                }),
          };
        })
        .filter(Boolean);
    }
    const persistGrants = persistGrantEntries(activated);
    const resourceGrants = resourceGrantEntriesFromEffect(activated);
    const { selfDamage, effect: outputEffect } = splitSelfDamageCost(activated);
    const identityEntries = resourceGrants.length
      ? null
      : resolveChosenIdentityActionEntries(outputEffect, equipment);
    const identityParts = identityEntries
      ? getIdentityActionLabelAndDurability(
          equipment,
          seat,
          identityEntries,
        )
      : null;
    const label = resourceGrants.length
      ? null
      : identityParts
        ? identityParts.label
        : getEquipmentEffectLabel(
            { ...equipment, activatedEffect: outputEffect },
            seat,
          );
    if (!persistGrants.length && !resourceGrants.length && !label) return [];
    return [
      {
        index: null,
        persistGrants,
        resourceGrants,
        selfDamage,
        label,
        outputDelta: resolveActivatedEffectOutputDelta(activated),
        cost: equipment.effectExpendCost,
        chargeCost: getEffectConsumeChargeCost(activated),
        durabilityCost: identityParts
          ? identityParts.durabilityCost
          : getEffectPrefixDurabilityCost(activated, { equipment }),
      },
    ];
  };

  const buildEquipmentQuickActions = (equipment, seat = null) => {
    if (!equipment) return [];
    const actions = [];
    listEquipmentEffectActions(equipment, seat).forEach((action) => {
      if (action.resourceGrants?.length || action.persistGrants?.length) {
        return;
      }
      const effect = getEquipmentEffectForActionIndex(
        equipment,
        action.index,
      );
      const display = formatEquipmentEffectQuickDisplay(
        effect,
        equipment,
        seat,
      );
      if (!display) return;
      actions.push({
        key: `effect-${action.index ?? "all"}`,
        mode: "effect",
        effectIndex: action.index,
        cost: action.cost,
        display,
      });
    });
    if (equipment.canRefresh) {
      actions.push({
        key: "refresh",
        mode: "refresh",
        effectIndex: null,
        cost: equipment.refreshCost,
        display: { top: "↻", bottom: t("match.board.quickRefresh") },
      });
    }
    if (equipment.canAddCharge) {
      actions.push({
        key: "charge",
        mode: "charge",
        effectIndex: null,
        cost: equipment.addChargeCost,
        display: { kind: "charge", top: "+" },
      });
    }
    if (equipment.canSharpen && !equipment.sharpened) {
      const sharpenAmount = Math.max(1, equipment.sharpenAmount || 1);
      actions.push({
        key: "sharpen",
        mode: "sharpen",
        effectIndex: null,
        cost: equipmentActionCost(equipment, "sharpen"),
        display: {
          kind: "sharpen",
          amount: String(sharpenAmount),
        },
      });
    }
    if (equipment.canHone && !equipment.honed) {
      const honeAmount = Math.max(1, equipment.honeAmount || 1);
      actions.push({
        key: "hone",
        mode: "hone",
        effectIndex: null,
        cost: equipmentActionCost(equipment, "hone"),
        display: {
          kind: "hone",
          amount: String(honeAmount),
        },
      });
    }
    if (equipment.canFletch && equipment.fletchEligible) {
      const fletchAmount = Math.max(1, equipment.fletchAmount || 1);
      actions.push({
        key: "fletch",
        mode: "fletch",
        effectIndex: null,
        cost: equipmentActionCost(equipment, "fletch"),
        display: {
          kind: "fletch",
          amount: String(fletchAmount),
        },
      });
    }
    if (equipment.canEnableEncore && !equipment.encoreEnabled) {
      actions.push({
        key: "enableEncore",
        mode: "enableEncore",
        effectIndex: null,
        cost: equipmentActionCost(equipment, "enableEncore"),
        display: {
          top: "∞",
          bottom: t("match.board.quickEncore"),
        },
      });
    }
    if (equipment.canResearch && !equipment.researched) {
      actions.push({
        key: "research",
        mode: "research",
        effectIndex: null,
        cost: equipmentActionCost(equipment, "research"),
        display: {
          kind: "research",
          amount: String(Math.max(1, equipment.researchAmount || 1)),
        },
      });
    }
    if (equipment.canSlottedDamage && !equipment.slottedDamageActive) {
      const amount = Math.max(1, equipment.slottedDamageAmount || 1);
      actions.push({
        key: "slottedDamage",
        mode: "slottedDamage",
        effectIndex: null,
        cost: equipment.slottedDamageCost || {},
        display: {
          top: `+${amount}`,
          bottom: t("match.board.quickSlotDamage"),
        },
      });
    }
    if (equipment.canRepair) {
      actions.push({
        key: "repair",
        mode: "repair",
        effectIndex: null,
        cost: equipment.repairCost || {},
        display: {
          top: "↺",
          bottom: t("match.board.quickRepair"),
        },
      });
    }
    appendPhysicalArmorPayQuickActions(actions, equipment, seat);
    return actions;
  };

  const renderEquipmentQuickActionFace = (display) => {
    if (display.kind === "damage") {
      const elementalSrc =
        ELEMENTAL_DAMAGE_ICON_URL[display.damageType] || null;
      return (
        <img
          src={elementalSrc || DAMAGE_ICON_URL}
          alt=""
          className={styles.equipQuickActionFullIcon}
          draggable={false}
        />
      );
    }
    if (display.kind === "heal") {
      return (
        <img
          src={HEAL_ICON_URL}
          alt=""
          className={styles.equipQuickActionFullIcon}
          draggable={false}
        />
      );
    }
    if (display.kind === "charge") {
      return (
        <>
          {display.top ? (
            <span className={styles.equipQuickActionTop}>{display.top}</span>
          ) : null}
          <ChargeIcon className={styles.equipQuickActionChargeIcon} />
        </>
      );
    }
    if (STATUS_EFFECT_ICON_IDS.includes(display.kind)) {
      return (
        <>
          <img
            src={STATUS_EFFECT_ICON_URL[display.kind]}
            alt=""
            className={styles.equipQuickActionStatusIcon}
            draggable={false}
          />
          {display.amount ? (
            <span className={styles.equipQuickActionTop}>{display.amount}</span>
          ) : null}
        </>
      );
    }
    return (
      <>
        {display.top ? (
          <span className={styles.equipQuickActionTop}>{display.top}</span>
        ) : null}
        {display.bottom ? (
          <span className={styles.equipQuickActionBottom}>{display.bottom}</span>
        ) : null}
      </>
    );
  };

  const renderEquipmentQuickActions = (
    equipment,
    shockStacks = 0,
    actions = null,
  ) => {
    const list = actions || buildEquipmentQuickActions(equipment, actingSeat);
    if (!list.length) return null;
    const seatResources = mergeSeatResources(selfSeat);
    return (
      <div
        className={styles.equipQuickActions}
        onClick={(event) => event.stopPropagation()}
      >
        {list.map((action) => {
          let hardBlocked =
            equipmentBuffActionDisabled(equipment) || targetingBlocksActions;
          let unaffordable = false;
          let ready = false;
          let chargeBlockMessage = null;
          if (action.mode === "resources") {
            hardBlocked =
              equipmentCannotExpendForResources(equipment) ||
              targetingBlocksActions;
            unaffordable = action.payWithPhysicalArmor
              ? !canAffordPhysicalArmorCost(actingSeat, action.cost)
              : !canAffordAbilityCost(
                  seatResources,
                  action.cost,
                  costAffordContext,
                );
            ready = !hardBlocked && !unaffordable;
          } else if (action.mode === "effect") {
            hardBlocked =
              equipmentCannotExpendNonResource(equipment) ||
              targetingBlocksActions;
            if (!hardBlocked) {
              chargeBlockMessage = getEquipmentEffectChargeBlockMessage(
                equipment,
                getEquipmentEffectForActionIndex(equipment, action.effectIndex),
              );
              hardBlocked = Boolean(chargeBlockMessage);
            }
            unaffordable = action.payWithPhysicalArmor
              ? !canAffordPhysicalArmorCost(actingSeat, action.cost)
              : !canAffordAbilityCost(
                  seatResources,
                  action.cost,
                  costAffordContext,
                );
            ready = !hardBlocked && !unaffordable;
          } else if (action.mode === "refresh") {
            hardBlocked =
              hardBlocked ||
              !equipment.expended ||
              targetingBlocksActions;
            unaffordable = !canAffordAbilityCost(
              seatResources,
              action.cost,
              costAffordContext,
            );
            ready = !hardBlocked && !unaffordable;
          } else if (action.mode === "charge") {
            hardBlocked = hardBlocked || targetingBlocksActions;
            unaffordable = !canAffordAbilityCost(
              seatResources,
              action.cost,
              costAffordContext,
            );
            ready = !hardBlocked && !unaffordable;
          } else if (
            action.mode === "sharpen" ||
            action.mode === "hone" ||
            action.mode === "fletch" ||
            action.mode === "enableEncore" ||
            action.mode === "research" ||
            action.mode === "slottedDamage" ||
            action.mode === "repair"
          ) {
            hardBlocked =
              (action.mode !== "repair" && hardBlocked) ||
              targetingBlocksActions;
            unaffordable = !canAffordEquipmentActionCost(
              seatResources,
              action.cost,
              costAffordContext,
            );
            ready = !hardBlocked && !unaffordable;
          }
          const successKey =
            action.mode === "resources"
              ? `expend:${equipment.instanceId}:resources`
              : action.mode === "effect"
                ? `expend:${equipment.instanceId}:effect:${action.effectIndex ?? "all"}`
                : action.mode === "refresh"
                  ? `refresh:${equipment.instanceId}`
                  : action.mode === "sharpen"
                    ? `sharpen:${equipment.instanceId}`
                    : action.mode === "hone"
                      ? `hone:${equipment.instanceId}`
                      : action.mode === "fletch"
                        ? `fletch:${equipment.instanceId}`
                        : action.mode === "enableEncore"
                          ? `enableEncore:${equipment.instanceId}`
                          : action.mode === "research"
                            ? `research:${equipment.instanceId}`
                            : action.mode === "slottedDamage"
                              ? `slottedDamage:${equipment.instanceId}`
                              : action.mode === "repair"
                                ? `repair:${equipment.instanceId}`
                                : `charge:${equipment.instanceId}`;
          const ariaLabel = action.display.grantEntries?.length
            ? t("match.board.gainResources")
            : action.display.kind === "damage"
              ? t("match.board.dealDamage")
              : action.display.kind === "heal"
                ? t("match.board.heal")
                : action.display.kind === "physicalArmor"
                  ? `${t("match.board.physicalArmor")}${
                      action.display.amount
                        ? ` ${action.display.amount}`
                        : ""
                    }`
                  : action.display.kind === "magicArmor"
                    ? `${t("match.board.magicArmor")}${
                        action.display.amount
                          ? ` ${action.display.amount}`
                          : ""
                      }`
                    : STATUS_EFFECT_ICON_IDS.includes(action.display.kind)
                      ? `${action.display.kind}${
                          action.display.amount
                            ? ` ${action.display.amount}`
                            : ""
                        }`
                      : `${action.display.top || ""} ${action.display.bottom || ""}`.trim();
          const iconOnly =
            action.display.kind === "damage" ||
            action.display.kind === "heal";
          const effectForConfirm =
            action.mode === "effect"
              ? getEquipmentEffectForActionIndex(
                  equipment,
                  action.effectIndex,
                )
              : null;
          const confirmKey =
            action.mode === "effect"
              ? buildEquipExpendConfirmKey(
                  equipment.instanceId,
                  "effect",
                  action.effectIndex ?? "all",
                )
              : null;
          const confirmText = effectForConfirm
            ? buildEquipEffectExpendConfirmText(
                effectForConfirm,
                equipment,
                actingSeat,
              )
            : null;
          const hardBlockMessage = chargeBlockMessage
            ? chargeBlockMessage
            : action.mode === "resources" || action.mode === "effect"
              ? equipmentExpendBlockMessage(equipment, targetingBlocksActions)
              : targetingBlocksActions
                ? t("match.board.resolveTargetingFirst")
                : equipmentBuffActionDisabled(equipment)
                  ? equipment.broken
                    ? t("match.board.equipmentBrokenCannotExpend")
                    : t("match.board.equipmentCannotExpend")
                  : action.mode === "refresh" && !equipment.expended
                    ? t("match.board.refreshRequiresExpended")
                    : t("match.board.cannotUseAction");
          return (
            <button
              key={action.key}
              type="button"
              className={`${styles.equipQuickAction} ${
                iconOnly ? styles.equipQuickActionIconOnly : ""
              } ${
                ready ? styles.equipQuickActionReady : ""
              }${
                confirmKey && equipExpendConfirm?.key === confirmKey
                  ? ` ${styles.equipQuickActionPending}`
                  : ""
              }${actionSuccessClass(successKey)}${
                hardBlocked || unaffordable
                  ? ` ${styles.buttonLooksDisabled}`
                  : ""
              }`}
              aria-disabled={hardBlocked || unaffordable ? true : undefined}
              aria-label={ariaLabel || t("match.board.equipmentAction")}
              title={ariaLabel || undefined}
              onClick={(event) => {
                event.stopPropagation();
                if (toastIfBlockedAction(hardBlocked, hardBlockMessage)) {
                  return;
                }
                if (
                  action.payWithPhysicalArmor
                    ? toastIfBlockedAction(
                        !canAffordPhysicalArmorCost(actingSeat, action.cost),
                        t("match.board.notEnoughPhysicalArmor"),
                      )
                    : toastIfUnaffordableCost(seatResources, action.cost)
                ) {
                  return;
                }
                if (action.mode === "effect") {
                  handleEquipExpendConfirmClick(
                    event,
                    confirmKey,
                    confirmText,
                    () => {
                      send({
                        type: "ai:expendEquipment",
                        instanceId: equipment.instanceId,
                        mode: "effect",
                        effectIndex: action.effectIndex,
                        ...(action.payWithPhysicalArmor
                          ? { payWithPhysicalArmor: true }
                          : {}),
                      });
                    },
                  );
                  return;
                }
                if (action.mode === "refresh") {
                  send({
                    type: "ai:refreshEquipment",
                    instanceId: equipment.instanceId,
                  });
                  return;
                }
                if (action.mode === "resources") {
                  send({
                    type: "ai:expendEquipment",
                    instanceId: equipment.instanceId,
                    mode: "resources",
                  });
                  return;
                }
                if (action.mode === "sharpen") {
                  send({
                    type: "ai:sharpenEquipment",
                    instanceId: equipment.instanceId,
                  });
                  return;
                }
                if (action.mode === "hone") {
                  send({
                    type: "ai:honeEquipment",
                    instanceId: equipment.instanceId,
                  });
                  return;
                }
                if (action.mode === "fletch") {
                  send({
                    type: "ai:fletchEquipment",
                    instanceId: equipment.instanceId,
                  });
                  return;
                }
                if (action.mode === "enableEncore") {
                  send({
                    type: "ai:enableEncoreEquipment",
                    instanceId: equipment.instanceId,
                  });
                  return;
                }
                if (action.mode === "research") {
                  send({
                    type: "ai:researchEquipment",
                    instanceId: equipment.instanceId,
                  });
                  return;
                }
                if (action.mode === "slottedDamage") {
                  send({
                    type: "ai:activateSlottedDamageEquipment",
                    instanceId: equipment.instanceId,
                  });
                  return;
                }
                if (action.mode === "repair") {
                  send({
                    type: "ai:repairEquipment",
                    instanceId: equipment.instanceId,
                  });
                  return;
                }
                send({
                  type: "ai:addEquipmentCharge",
                  instanceId: equipment.instanceId,
                });
              }}
            >
              {renderEquipmentQuickActionFace(action.display)}
            </button>
          );
        })}
      </div>
    );
  };

  const renderExamineOnlyActionsMenu = (onExamine) => (
    <div
      className={styles.boardCardActions}
      onClick={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        className={`${styles.button} ${styles.boardCardActionButton}`}
        data-gamepad-item=""
        onClick={() => {
          setBoardMenu(null);
          onExamine();
        }}
      >
        {t("match.board.examine")}
      </button>
    </div>
  );

  const renderTraitEquipmentEmpowerAction = (instanceId, closeMenu) =>
    canEmpowerTraitEquipment(instanceId) ? (
      <button
        type="button"
        className={`${styles.button} ${styles.boardCardActionButton} ${styles.actionReady}`}
        data-gamepad-item=""
        onClick={() => {
          closeMenu();
          chooseTraitEquipment(instanceId);
        }}
      >
        {pendingTraitEquipmentAbilityIds.has(instanceId)
          ? t("match.board.empowerAbility")
          : t("match.board.empowerEquipment")}
        {pendingTraitEquipmentAbilityIds.has(instanceId)
          ? renderAbilityLevelUpHint(
              (pendingTraitEquipmentChoice?.options || []).find(
                (option) => option.abilityInstanceId === instanceId,
              ),
            )
          : null}
      </button>
    ) : null;

  const renderBoardEquipmentActionsMenu = (equipment, seat) => {
    if (!equipment) return null;
    if (boardMenu?.examineOnly) {
      return renderExamineOnlyActionsMenu(() => {
        setSlottedAbilityExam(null);
        setSelectedEquipmentId(equipment.instanceId);
      });
    }
    const occupyingMinion = getEquipmentOccupyingMinion(equipment, seat);
    const occupyingAura = !occupyingMinion
      ? getEquipmentOccupyingAura(equipment, seat)
      : null;
    const occupyingAbility = occupyingMinion
      ? findAbilityForMinion(equipment, occupyingMinion, seat)
      : occupyingAura
        ? findAbilityForAura(equipment, occupyingAura, seat)
        : null;
    const seatResources = mergeSeatResources(selfSeat);
    const shockStacks = Math.max(0, actingSeat?.shockStacks || 0);
    const grantChoices = listEquipmentResourceGrantChoices(equipment);
    const directResources = resourceGrantEntries(equipment.resources);
    const allEffectActions = listEquipmentEffectActions(equipment, seat);
    const resourceChoiceActions = allEffectActions.filter(
      (action) => action.resourceGrants?.length || action.persistGrants?.length,
    );
    const effectActions = expandEquipmentResourceEffectActionsForArmorPay(
      allEffectActions.filter(
        (action) =>
          !(action.resourceGrants?.length || action.persistGrants?.length),
      ),
      equipment,
      seat,
    );
    const hardExpendBlocked =
      equipmentCannotExpendForResources(equipment) || targetingBlocksActions;
    const hardEffectBlocked =
      equipmentCannotExpendNonResource(equipment) || targetingBlocksActions;
    const closeMenu = () => setBoardMenu(null);
    const equipmentLevelUpCompare = getEquipmentLevelUpCompare(equipment);

    if (occupyingAura) {
      return (
        <KeywordTipContext.Provider value={null}>
          <div
            className={styles.boardCardActions}
            onClick={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className={`${styles.button} ${styles.boardCardActionButton}`}
              data-gamepad-item=""
              onClick={() => {
                closeMenu();
                setSlottedAbilityExam(null);
                setSelectedEquipmentId(equipment.instanceId);
              }}
            >
              {t("match.board.examine")}
            </button>
            {renderTraitEquipmentEmpowerAction(equipment.instanceId, closeMenu)}
            {occupyingAbility ? (
              <button
                type="button"
                className={`${styles.button} ${styles.boardCardActionButton}${actionSuccessClass(
                  `deactivateAura:${occupyingAbility.instanceId}`,
                )}`}
                data-gamepad-item=""
                disabled={targetingBlocksActions}
                onClick={() => {
                  if (
                    toastIfBlockedAction(
                      targetingBlocksActions,
                      t("match.board.resolveTargetingFirst"),
                    )
                  ) {
                    return;
                  }
                  requestDeactivateAura({
                    equipmentInstanceId: equipment.instanceId,
                    abilityInstanceId: occupyingAbility.instanceId,
                  });
                  closeMenu();
                }}
              >
                {t("match.board.deactivateAura")}
              </button>
            ) : null}
          </div>
        </KeywordTipContext.Provider>
      );
    }

    return (
      <KeywordTipContext.Provider value={null}>
      <div
        className={styles.boardCardActions}
        onClick={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className={`${styles.button} ${styles.boardCardActionButton}`}
          data-gamepad-item=""
          onClick={() => {
            closeMenu();
            setSlottedAbilityExam(null);
            setSelectedEquipmentId(equipment.instanceId);
          }}
        >
          {t("match.board.examine")}
        </button>
        {renderTraitEquipmentEmpowerAction(equipment.instanceId, closeMenu)}
        {grantChoices.length > 0 && directResources.length > 0 ? (
          <button
            type="button"
            className={`${styles.button} ${styles.boardCardActionButton}${tutorialHighlightClass(
              equipment,
              "resources",
            )}${
              !hardExpendBlocked &&
              canAffordAbilityCost(
                seatResources,
                equipment.expendCost,
                costAffordContext,
              )
                ? ` ${styles.actionReady}`
                : ""
            }${
              hardExpendBlocked ||
              !canAffordAbilityCost(
                seatResources,
                equipment.expendCost,
                costAffordContext,
              )
                ? ` ${styles.buttonLooksDisabled}`
                : ""
            }${actionSuccessClass(`expend:${equipment.instanceId}:resources`)}`}
            data-gamepad-item=""
            onClick={() => {
              if (
                toastIfBlockedAction(
                  hardExpendBlocked,
                  equipmentExpendBlockMessage(
                    equipment,
                    targetingBlocksActions,
                  ),
                )
              ) {
                return;
              }
              if (
                toastIfUnaffordableCost(seatResources, equipment.expendCost)
              ) {
                return;
              }
              send({
                type: "ai:expendEquipment",
                instanceId: equipment.instanceId,
                mode: "resources",
              });
              closeMenu();
            }}
          >
            <ExpendArrowPrefix cost={equipment.expendCost} />
            <ResourceGrantIconListWithShock
              entries={directResources}
              shockStacks={shockStacks}
              className={styles.handCardActionResourceIcons}
            />
            <DelayedResourceGrantIcons
              equipment={equipment}
              className={styles.handCardActionResourceIcons}
            />
          </button>
        ) : null}
        {directResources.length === 0
          ? resourceChoiceActions.map((action) => {
              const unaffordable = !canAffordAbilityCost(
                seatResources,
                action.cost,
                costAffordContext,
              );
              const entries =
                action.resourceGrants?.length > 0
                  ? action.resourceGrants
                  : action.persistGrants;
              return (
                <button
                  key={`board-resource-choice-${action.index ?? "all"}`}
                  type="button"
                  className={`${styles.button} ${styles.boardCardActionButton}${tutorialHighlightClass(
                    equipment,
                    "effect",
                    action.index,
                  )}${
                    !hardExpendBlocked && !unaffordable
                      ? ` ${styles.actionReady}`
                      : ""
                  }${
                    hardExpendBlocked || unaffordable
                      ? ` ${styles.buttonLooksDisabled}`
                      : ""
                  }${actionSuccessClass(
                    `expend:${equipment.instanceId}:effect:${action.index ?? "all"}`,
                  )}`}
                  data-gamepad-item=""
                  onClick={() => {
                    if (
                toastIfBlockedAction(
                  hardExpendBlocked,
                  equipmentExpendBlockMessage(
                    equipment,
                    targetingBlocksActions,
                  ),
                )
              ) {
                      return;
                    }
                    if (toastIfUnaffordableCost(seatResources, action.cost)) {
                      return;
                    }
                    send({
                      type: "ai:expendEquipment",
                      instanceId: equipment.instanceId,
                      mode: "effect",
                      effectIndex: action.index,
                    });
                    closeMenu();
                  }}
                >
                  <ExpendArrowPrefix
                    cost={action.cost}
                    chargeCost={action.chargeCost}
                    durabilityCost={action.durabilityCost}
                  />
                  <ResourceGrantIconListWithShock
                    entries={entries}
                    shockStacks={shockStacks}
                    persist={Boolean(action.persistGrants?.length)}
                    className={styles.handCardActionResourceIcons}
                  />
                </button>
              );
            })
          : null}
        {effectActions.map((action) => {
          const actionCost = action.payWithPhysicalArmor
            ? action.armorPayCost
            : action.cost;
          const unaffordable = action.payWithPhysicalArmor
            ? !canAffordPhysicalArmorCost(seat, actionCost)
            : !canAffordAbilityCost(
                seatResources,
                action.cost,
                costAffordContext,
              );
          const resolveEffectActionLabel = (sourceEquipment, sourceAction) =>
            formatAbilityShorthandText(sourceAction?.label) ||
            formatAbilityShorthandText(
              formatActivatedEffectText(
                getEquipmentEffectForActionIndex(sourceEquipment, action.index),
                {
                  omitCost: true,
                  omitChargeCost: Boolean(sourceAction?.chargeCost),
                  omitDurability: true,
                  equipment: sourceEquipment,
                  seat,
                },
              ),
            ) ||
            null;
          const findLevelUpEquipmentAction = (sourceEquipment) =>
            listEquipmentEffectActions(sourceEquipment, seat).find(
              (entry) => (entry.index ?? null) === (action.index ?? null),
            ) || action;
          const displayEquipment = equipmentLevelUpCompare?.display || equipment;
          const effect = getEquipmentEffectForActionIndex(
            displayEquipment,
            action.index,
          );
          const label =
            resolveEffectActionLabel(
              displayEquipment,
              equipmentLevelUpCompare
                ? findLevelUpEquipmentAction(displayEquipment)
                : action,
            ) || t("match.board.effectFallback");
          const compareEquipment = equipmentLevelUpCompare?.compare || null;
          const compareEffect = compareEquipment
            ? getEquipmentEffectForActionIndex(compareEquipment, action.index)
            : null;
          const compareLabel = compareEquipment
            ? resolveEffectActionLabel(
                compareEquipment,
                findLevelUpEquipmentAction(compareEquipment),
              )
            : null;
          const chargeBlockMessage = hardEffectBlocked
            ? null
            : getEquipmentEffectChargeBlockMessage(
                equipment,
                getEquipmentEffectForActionIndex(equipment, action.index),
              );
          const effectBlocked = hardEffectBlocked || Boolean(chargeBlockMessage);
          return (
            <button
              key={`board-effect-${action.armorPayKey || (action.index ?? "all")}`}
              type="button"
              className={`${styles.button} ${styles.boardCardActionButton}${tutorialHighlightClass(
                equipment,
                "effect",
                action.index,
              )}${
                !effectBlocked && !unaffordable
                  ? ` ${styles.actionReady}`
                  : ""
              }${
                effectBlocked || unaffordable
                  ? ` ${styles.buttonLooksDisabled}`
                  : ""
              }${actionSuccessClass(
                `expend:${equipment.instanceId}:effect:${action.index ?? "all"}`,
              )}`}
              data-gamepad-item=""
              onClick={() => {
                if (
                toastIfBlockedAction(
                  effectBlocked,
                  chargeBlockMessage ||
                    equipmentExpendBlockMessage(
                      equipment,
                      targetingBlocksActions,
                    ),
                )
              ) {
                  return;
                }
                if (
                  action.payWithPhysicalArmor
                    ? toastIfBlockedAction(
                        !canAffordPhysicalArmorCost(seat, actionCost),
                        t("match.board.notEnoughPhysicalArmor"),
                      )
                    : toastIfUnaffordableCost(seatResources, action.cost)
                ) {
                  return;
                }
                send({
                  type: "ai:expendEquipment",
                  instanceId: equipment.instanceId,
                  mode: "effect",
                  effectIndex: action.index,
                  ...(action.payWithPhysicalArmor
                    ? { payWithPhysicalArmor: true }
                    : {}),
                });
                closeMenu();
              }}
            >
              <ExpendArrowPrefix
                cost={actionCost}
                chargeCost={action.payWithPhysicalArmor ? 0 : action.chargeCost}
                durabilityCost={action.durabilityCost}
                selfDamage={action.selfDamage}
              />
              <EffectActionOutputFace
                effect={effect}
                equipment={displayEquipment}
                seat={seat}
                fallbackText={label}
                outputDelta={
                  action.outputDelta || effect?.outputDelta || 0
                }
                shorthand={false}
                compareEffect={compareEffect}
                compareEquipment={compareEquipment}
                compareText={compareLabel}
              />
            </button>
          );
        })}
        {renderInherentAbilityActivateButtons(equipment, seat, {
          buttonClassName: `${styles.button} ${styles.boardCardActionButton}`,
          onActivated: closeMenu,
        })}
        {equipment.canSharpen && !equipment.sharpened ? (
          <button
            type="button"
            className={`${styles.button} ${styles.boardCardActionButton}${
              !equipmentBuffActionDisabled(equipment) &&
              !targetingBlocksActions &&
              canAffordEquipmentActionCost(
                seatResources,
                equipmentActionCost(equipment, "sharpen"),
                costAffordContext,
              )
                ? ` ${styles.actionReady}`
                : ""
            }${
              equipmentBuffActionDisabled(equipment) ||
              targetingBlocksActions ||
              !canAffordEquipmentActionCost(
                seatResources,
                equipmentActionCost(equipment, "sharpen"),
                costAffordContext,
              )
                ? ` ${styles.buttonLooksDisabled}`
                : ""
            }`}
            data-gamepad-item=""
            aria-disabled={
              equipmentBuffActionDisabled(equipment) ||
              targetingBlocksActions ||
              !canAffordEquipmentActionCost(
                seatResources,
                equipmentActionCost(equipment, "sharpen"),
                costAffordContext,
              )
                ? true
                : undefined
            }
            onClick={() => {
              if (
                toastIfBlockedAction(
                  equipmentBuffActionDisabled(equipment) || targetingBlocksActions,
                  targetingBlocksActions
                    ? t("match.board.resolveTargetingFirst")
                    : t("match.board.equipmentCannotExpend"),
                )
              ) {
                return;
              }
              if (
                toastIfUnaffordableCost(
                  seatResources,
                  equipmentActionCost(equipment, "sharpen"),
                )
              ) {
                return;
              }
              send({
                type: "ai:sharpenEquipment",
                instanceId: equipment.instanceId,
              });
              closeMenu();
            }}
          >
            <PaymentArrowPrefix
              cost={equipmentActionCost(equipment, "sharpen")}
            />
            {renderTextWithStatusEffectIcons(
              formatSharpenLabel(equipment.sharpenAmount),
              { shorthand: false },
            )}
          </button>
        ) : null}
        {equipment.canHone && !equipment.honed ? (
          <button
            type="button"
            className={`${styles.button} ${styles.boardCardActionButton}${
              !equipmentBuffActionDisabled(equipment) &&
              !targetingBlocksActions &&
              canAffordEquipmentActionCost(
                seatResources,
                equipmentActionCost(equipment, "hone"),
                costAffordContext,
              )
                ? ` ${styles.actionReady}`
                : ""
            }${
              equipmentBuffActionDisabled(equipment) ||
              targetingBlocksActions ||
              !canAffordEquipmentActionCost(
                seatResources,
                equipmentActionCost(equipment, "hone"),
                costAffordContext,
              )
                ? ` ${styles.buttonLooksDisabled}`
                : ""
            }`}
            data-gamepad-item=""
            aria-disabled={
              equipmentBuffActionDisabled(equipment) ||
              targetingBlocksActions ||
              !canAffordEquipmentActionCost(
                seatResources,
                equipmentActionCost(equipment, "hone"),
                costAffordContext,
              )
                ? true
                : undefined
            }
            onClick={() => {
              if (
                toastIfBlockedAction(
                  equipmentBuffActionDisabled(equipment) || targetingBlocksActions,
                  targetingBlocksActions
                    ? t("match.board.resolveTargetingFirst")
                    : t("match.board.equipmentCannotExpend"),
                )
              ) {
                return;
              }
              if (
                toastIfUnaffordableCost(
                  seatResources,
                  equipmentActionCost(equipment, "hone"),
                )
              ) {
                return;
              }
              send({
                type: "ai:honeEquipment",
                instanceId: equipment.instanceId,
              });
              closeMenu();
            }}
          >
            <PaymentArrowPrefix
              cost={equipmentActionCost(equipment, "hone")}
            />
            {renderTextWithStatusEffectIcons(
              formatHoneLabel(equipment.honeAmount),
              { shorthand: false },
            )}
          </button>
        ) : null}
        {equipment.canFletch && equipment.fletchEligible ? (
          <button
            type="button"
            className={`${styles.button} ${styles.boardCardActionButton}${
              !equipmentBuffActionDisabled(equipment) &&
              !targetingBlocksActions &&
              canAffordEquipmentActionCost(
                seatResources,
                equipmentActionCost(equipment, "fletch"),
                costAffordContext,
              )
                ? ` ${styles.actionReady}`
                : ""
            }${
              equipmentBuffActionDisabled(equipment) ||
              targetingBlocksActions ||
              !canAffordEquipmentActionCost(
                seatResources,
                equipmentActionCost(equipment, "fletch"),
                costAffordContext,
              )
                ? ` ${styles.buttonLooksDisabled}`
                : ""
            }`}
            data-gamepad-item=""
            aria-disabled={
              equipmentBuffActionDisabled(equipment) ||
              targetingBlocksActions ||
              !canAffordEquipmentActionCost(
                seatResources,
                equipmentActionCost(equipment, "fletch"),
                costAffordContext,
              )
                ? true
                : undefined
            }
            onClick={() => {
              if (
                toastIfBlockedAction(
                  equipmentBuffActionDisabled(equipment) || targetingBlocksActions,
                  targetingBlocksActions
                    ? t("match.board.resolveTargetingFirst")
                    : t("match.board.equipmentCannotExpend"),
                )
              ) {
                return;
              }
              if (
                toastIfUnaffordableCost(
                  seatResources,
                  equipmentActionCost(equipment, "fletch"),
                )
              ) {
                return;
              }
              send({
                type: "ai:fletchEquipment",
                instanceId: equipment.instanceId,
              });
              closeMenu();
            }}
          >
            <PaymentArrowPrefix
              cost={equipmentActionCost(equipment, "fletch")}
            />
            {renderTextWithStatusEffectIcons(
              formatFletchLabel(equipment.fletchAmount),
              { shorthand: false },
            )}
          </button>
        ) : null}
        {equipment.canEnableEncore && !equipment.encoreEnabled ? (
          <button
            type="button"
            className={`${styles.button} ${styles.boardCardActionButton}${
              !equipmentBuffActionDisabled(equipment) &&
              !targetingBlocksActions &&
              canAffordEquipmentActionCost(
                seatResources,
                equipmentActionCost(equipment, "enableEncore"),
                costAffordContext,
              )
                ? ` ${styles.actionReady}`
                : ""
            }${
              equipmentBuffActionDisabled(equipment) ||
              targetingBlocksActions ||
              !canAffordEquipmentActionCost(
                seatResources,
                equipmentActionCost(equipment, "enableEncore"),
                costAffordContext,
              )
                ? ` ${styles.buttonLooksDisabled}`
                : ""
            }`}
            data-gamepad-item=""
            aria-disabled={
              equipmentBuffActionDisabled(equipment) ||
              targetingBlocksActions ||
              !canAffordEquipmentActionCost(
                seatResources,
                equipmentActionCost(equipment, "enableEncore"),
                costAffordContext,
              )
                ? true
                : undefined
            }
            onClick={() => {
              if (
                toastIfBlockedAction(
                  equipmentBuffActionDisabled(equipment) || targetingBlocksActions,
                  targetingBlocksActions
                    ? t("match.board.resolveTargetingFirst")
                    : t("match.board.equipmentCannotExpend"),
                )
              ) {
                return;
              }
              if (
                toastIfUnaffordableCost(
                  seatResources,
                  equipmentActionCost(equipment, "enableEncore"),
                )
              ) {
                return;
              }
              send({
                type: "ai:enableEncoreEquipment",
                instanceId: equipment.instanceId,
              });
              closeMenu();
            }}
          >
            <PaymentArrowPrefix
              cost={equipmentActionCost(equipment, "enableEncore")}
            />
            {t("match.board.encore")}
          </button>
        ) : null}
        {equipment.canResearch && !equipment.researched ? (
          <button
            type="button"
            className={`${styles.button} ${styles.boardCardActionButton}${
              !equipmentBuffActionDisabled(equipment) &&
              !targetingBlocksActions &&
              canAffordEquipmentActionCost(
                seatResources,
                equipmentActionCost(equipment, "research"),
                costAffordContext,
              )
                ? ` ${styles.actionReady}`
                : ""
            }${
              equipmentBuffActionDisabled(equipment) ||
              targetingBlocksActions ||
              !canAffordEquipmentActionCost(
                seatResources,
                equipmentActionCost(equipment, "research"),
                costAffordContext,
              )
                ? ` ${styles.buttonLooksDisabled}`
                : ""
            }`}
            data-gamepad-item=""
            aria-disabled={
              equipmentBuffActionDisabled(equipment) ||
              targetingBlocksActions ||
              !canAffordEquipmentActionCost(
                seatResources,
                equipmentActionCost(equipment, "research"),
                costAffordContext,
              )
                ? true
                : undefined
            }
            onClick={() => {
              if (
                toastIfBlockedAction(
                  equipmentBuffActionDisabled(equipment) || targetingBlocksActions,
                  targetingBlocksActions
                    ? t("match.board.resolveTargetingFirst")
                    : t("match.board.equipmentCannotExpend"),
                )
              ) {
                return;
              }
              if (
                toastIfUnaffordableCost(
                  seatResources,
                  equipmentActionCost(equipment, "research"),
                )
              ) {
                return;
              }
              send({
                type: "ai:researchEquipment",
                instanceId: equipment.instanceId,
              });
              closeMenu();
            }}
          >
            <PaymentArrowPrefix
              cost={equipmentActionCost(equipment, "research")}
            />
            <StatusEffectIconStack effectId="research" amount={1} />
          </button>
        ) : null}
        {equipment.canSlottedDamage && !equipment.slottedDamageActive ? (
          <button
            type="button"
            className={`${styles.button} ${styles.boardCardActionButton}${
              !equipmentBuffActionDisabled(equipment) &&
              !targetingBlocksActions &&
              canAffordEquipmentActionCost(
                seatResources,
                equipment.slottedDamageCost || {},
                costAffordContext,
              )
                ? ` ${styles.actionReady}`
                : ""
            }${
              equipmentBuffActionDisabled(equipment) ||
              targetingBlocksActions ||
              !canAffordEquipmentActionCost(
                seatResources,
                equipment.slottedDamageCost || {},
                costAffordContext,
              )
                ? ` ${styles.buttonLooksDisabled}`
                : ""
            }`}
            data-gamepad-item=""
            aria-disabled={
              equipmentBuffActionDisabled(equipment) ||
              targetingBlocksActions ||
              !canAffordEquipmentActionCost(
                seatResources,
                equipment.slottedDamageCost || {},
                costAffordContext,
              )
                ? true
                : undefined
            }
            onClick={() => {
              if (
                toastIfBlockedAction(
                  equipmentBuffActionDisabled(equipment) || targetingBlocksActions,
                  targetingBlocksActions
                    ? t("match.board.resolveTargetingFirst")
                    : t("match.board.equipmentCannotExpend"),
                )
              ) {
                return;
              }
              if (
                toastIfUnaffordableCost(
                  seatResources,
                  equipment.slottedDamageCost || {},
                )
              ) {
                return;
              }
              send({
                type: "ai:activateSlottedDamageEquipment",
                instanceId: equipment.instanceId,
              });
              closeMenu();
            }}
          >
            <PaymentArrowPrefix cost={equipment.slottedDamageCost || {}} />
            {t("match.board.slottedDamage", { amount: Math.max(1, equipment.slottedDamageAmount || 1) })}
          </button>
        ) : null}
        {equipment.canRepair ? (
          <button
            type="button"
            className={`${styles.button} ${styles.boardCardActionButton}${
              !targetingBlocksActions &&
              canAffordEquipmentActionCost(
                seatResources,
                equipment.repairCost || {},
                costAffordContext,
              )
                ? ` ${styles.actionReady}`
                : ""
            }${
              targetingBlocksActions ||
              !canAffordEquipmentActionCost(
                seatResources,
                equipment.repairCost || {},
                costAffordContext,
              )
                ? ` ${styles.buttonLooksDisabled}`
                : ""
            }`}
            data-gamepad-item=""
            aria-disabled={
              targetingBlocksActions ||
              !canAffordEquipmentActionCost(
                seatResources,
                equipment.repairCost || {},
                costAffordContext,
              )
                ? true
                : undefined
            }
            onClick={() => {
              if (
                toastIfBlockedAction(
                  targetingBlocksActions,
                  t("match.board.resolveTargetingFirst"),
                )
              ) {
                return;
              }
              if (
                toastIfUnaffordableCost(
                  seatResources,
                  equipment.repairCost || {},
                )
              ) {
                return;
              }
              send({
                type: "ai:repairEquipment",
                instanceId: equipment.instanceId,
              });
              closeMenu();
            }}
          >
            <PaymentArrowPrefix cost={equipment.repairCost || {}} />
            {t("match.board.repair")}
          </button>
        ) : null}
        {equipment.canRefresh ? (
          <button
            type="button"
            className={`${styles.button} ${styles.boardCardActionButton}${
              equipment.expended &&
              !equipmentBuffActionDisabled(equipment) &&
              canAffordAbilityCost(
                seatResources,
                equipment.refreshCost,
                costAffordContext,
              )
                ? ` ${styles.actionReady}`
                : ""
            }${
              !equipment.expended ||
              equipmentBuffActionDisabled(equipment) ||
              !canAffordAbilityCost(
                seatResources,
                equipment.refreshCost,
                costAffordContext,
              )
                ? ` ${styles.buttonLooksDisabled}`
                : ""
            }`}
            data-gamepad-item=""
            aria-disabled={
              !equipment.expended ||
              equipmentBuffActionDisabled(equipment) ||
              !canAffordAbilityCost(
                seatResources,
                equipment.refreshCost,
                costAffordContext,
              )
                ? true
                : undefined
            }
            onClick={() => {
              if (
                toastIfBlockedAction(
                  !equipment.expended || equipmentBuffActionDisabled(equipment),
                  !equipment.expended
                    ? t("match.board.equipmentNotExpended")
                    : t("match.board.equipmentCannotExpend"),
                )
              ) {
                return;
              }
              if (
                toastIfUnaffordableCost(seatResources, equipment.refreshCost)
              ) {
                return;
              }
              send({
                type: "ai:refreshEquipment",
                instanceId: equipment.instanceId,
              });
              closeMenu();
            }}
          >
            <PaymentArrowPrefix cost={equipment.refreshCost} />
            {t("match.board.refresh")}
          </button>
        ) : null}
        {equipment.canAddCharge ? (
          <button
            type="button"
            className={`${styles.button} ${styles.boardCardActionButton}${
              !equipmentBuffActionDisabled(equipment) &&
              !targetingBlocksActions &&
              canAffordAbilityCost(
                seatResources,
                equipment.addChargeCost,
                costAffordContext,
              )
                ? ` ${styles.actionReady}`
                : ""
            }${
              equipmentBuffActionDisabled(equipment) ||
              targetingBlocksActions ||
              !canAffordAbilityCost(
                seatResources,
                equipment.addChargeCost,
                costAffordContext,
              )
                ? ` ${styles.buttonLooksDisabled}`
                : ""
            }`}
            data-gamepad-item=""
            aria-disabled={
              equipmentBuffActionDisabled(equipment) ||
              targetingBlocksActions ||
              !canAffordAbilityCost(
                seatResources,
                equipment.addChargeCost,
                costAffordContext,
              )
                ? true
                : undefined
            }
            onClick={() => {
              if (
                toastIfBlockedAction(
                  equipmentBuffActionDisabled(equipment) || targetingBlocksActions,
                  targetingBlocksActions
                    ? t("match.board.resolveTargetingFirst")
                    : t("match.board.equipmentCannotExpend"),
                )
              ) {
                return;
              }
              if (
                toastIfUnaffordableCost(seatResources, equipment.addChargeCost)
              ) {
                return;
              }
              send({
                type: "ai:addEquipmentCharge",
                instanceId: equipment.instanceId,
              });
              closeMenu();
            }}
          >
            <PaymentArrowPrefix cost={equipment.addChargeCost} />
            {equipment.addChargeLabel || t("match.board.charge")}
          </button>
        ) : null}
        <button
          type="button"
          className={`${styles.button} ${styles.boardCardActionButton}`}
          data-gamepad-item=""
          onClick={() => {
            send({
              type: "ai:discardEquipment",
              instanceId: equipment.instanceId,
            });
            closeMenu();
          }}
        >
          {t("match.board.discard")}
        </button>
      </div>
      </KeywordTipContext.Provider>
    );
  };

  const renderBoardAbilityActionsMenu = (
    ability,
    {
      boardSlot,
      slotId = null,
      equipment,
      seat,
    },
  ) => {
    if (!ability) return null;
    if (boardMenu?.examineOnly) {
      return renderExamineOnlyActionsMenu(() => {
        setSelectedEquipmentId(null);
        setSlottedAbilityExam({
          boardSlot,
          abilityInstanceId: ability.instanceId,
          cardId: ability.cardId,
        });
      });
    }
    const canActivateFromSlot =
      Boolean(ability.inherent) ||
      slotId === "host" ||
      slotId == null;
    const bloodMagicHealthCost = Math.max(0, ability.effectiveHealthCost || 0);
    const abilityHealthCost =
      bloodMagicHealthCost +
      getAbilityHealthCostAmount(ability.effect, abilityDisplayScale(ability));
    const activateLabel = canActivateFromSlot
      ? getAbilityActivateLabel(ability)
      : null;
    const minionActive =
      Boolean(ability.minion) && (ability.minion.hp || 0) > 0;
    const auraActive = Boolean(ability.aura);
    const minionExpendActions = minionActive
      ? listMinionExpendActions(ability)
      : [];
    const equipmentOccupied = equipment
      ? equipmentHasBoardOccupant(equipment, seat)
      : false;
    const isSummonAbility = ability.effect?.id === "summonMinion";
    const isAuraAbility = ability.effect?.id === "activateAura";
    const abilityReady =
      canActivateFromSlot &&
      ability.kind === "active" &&
      Boolean(activateLabel) &&
      !ability.activated &&
      !minionActive &&
      !auraActive &&
      (!equipmentOccupied || isSummonAbility || isAuraAbility) &&
      canAffordAbilityActivation(seat, ability, catalogMap);
    const closeMenu = () => setBoardMenu(null);
    const abilityLevelUpCompare = getAbilityLevelUpCompare(ability);
    const levelUpDisplayActions = abilityLevelUpCompare
      ? listAbilityActivateActions(abilityLevelUpCompare.display)
      : null;
    const levelUpCompareActions = abilityLevelUpCompare
      ? listAbilityActivateActions(abilityLevelUpCompare.compare)
      : null;
    const findLevelUpActionLabel = (list, index) =>
      list?.find((entry) => (entry.index ?? null) === (index ?? null))
        ?.label ?? null;

    return (
      <KeywordTipContext.Provider value={null}>
      <div
        className={styles.boardCardActions}
        onClick={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className={`${styles.button} ${styles.boardCardActionButton}`}
          data-gamepad-item=""
          onClick={() => {
            closeMenu();
            setSelectedEquipmentId(null);
            setSlottedAbilityExam({
              boardSlot,
              abilityInstanceId: ability.instanceId,
              cardId: ability.cardId,
            });
          }}
        >
          {t("match.board.examine")}
        </button>
        {canLevelUpTraitAbility(ability) ? (
          <button
            type="button"
            className={`${styles.button} ${styles.boardCardActionButton} ${styles.actionReady}`}
            data-gamepad-item=""
            onClick={() => {
              closeMenu();
              chooseTraitAbilityLevel(ability);
            }}
          >
            {t("match.board.levelUpAbility")}
            {renderAbilityLevelUpHint(ability)}
          </button>
        ) : null}
        {renderTraitEquipmentEmpowerAction(ability.instanceId, closeMenu)}
        {ability.kind === "active" && minionActive
          ? minionExpendActions.map((action) => (
              <button
                key={`board-minion-${action.index ?? "main"}`}
                type="button"
                className={`${styles.button} ${styles.boardCardActionButton}`}
                data-gamepad-item=""
                disabled={
                  Boolean(ability.minion.expended) || targetingBlocksActions
                }
                onClick={() => {
                  requestExpendMinion(equipment, ability, action.index);
                  closeMenu();
                }}
              >
                {renderTextWithStatusEffectIcons(action.label)}
              </button>
            ))
          : null}
        {ability.kind === "active" && minionActive ? (
          <button
            type="button"
            className={`${styles.button} ${styles.boardCardActionButton}`}
            data-gamepad-item=""
            disabled={targetingBlocksActions}
            onClick={() => {
              requestUnsummonMinion({
                equipmentInstanceId: equipment.instanceId,
                abilityInstanceId: ability.instanceId,
              });
              closeMenu();
            }}
          >
            {t("match.board.unsummon")}
          </button>
        ) : null}
        {ability.kind === "active" && auraActive ? (
          <button
            type="button"
            className={`${styles.button} ${styles.boardCardActionButton}`}
            data-gamepad-item=""
            disabled={targetingBlocksActions}
            onClick={() => {
              requestDeactivateAura({
                equipmentInstanceId: equipment.instanceId,
                abilityInstanceId: ability.instanceId,
              });
              closeMenu();
            }}
          >
            {t("match.board.deactivate")}
          </button>
        ) : null}
        {ability.kind === "active" && activateLabel
          ? expandAbilityActivateActionsForArmorPay(
              ability,
              seat,
              listAbilityActivateActions(ability),
            ).map((action) => {
              const choiceAbility = withAbilityChoiceCost(ability, action.index);
              const armorCost = physicalArmorPayCostFromResourceCost(
                choiceAbility.effectiveCost || choiceAbility.cost || {},
              );
              const actionCost = action.payWithPhysicalArmor
                ? armorCost
                : choiceAbility.effectiveCost || choiceAbility.cost;
              const actionHealthCost =
                abilityHealthCost -
                bloodMagicHealthCost +
                Math.max(0, choiceAbility.effectiveHealthCost || 0);
              const actionReady = action.payWithPhysicalArmor
                ? canActivateFromSlot &&
                  ability.kind === "active" &&
                  Boolean(activateLabel) &&
                  !ability.activated &&
                  !minionActive &&
                  !auraActive &&
                  (!equipmentOccupied || isSummonAbility || isAuraAbility) &&
                  armorCost &&
                  canAffordPhysicalArmorCost(seat, armorCost)
                : abilityReady &&
                  canAffordAbilityActivation(seat, choiceAbility, catalogMap);
              return (
              <button
                key={`board-activate-${action.index ?? "main"}${action.armorPayKey || ""}`}
                type="button"
                className={`${styles.button} ${styles.boardCardActionButton}${
                  actionReady && !targetingBlocksActions
                    ? ` ${styles.actionReady}`
                    : ""
                }${tutorialAbilityHighlightClass(boardSlot, ability)}${
                  !actionReady || targetingBlocksActions
                    ? ` ${styles.buttonLooksDisabled}`
                    : ""
                }`}
                data-gamepad-item=""
                onClick={() => {
                  if (
                    toastIfBlockedAction(
                      targetingBlocksActions,
                      t("match.board.resolveTargetingFirst"),
                    )
                  ) {
                    return;
                  }
                  if (
                    toastIfBlockedAction(
                      Boolean(ability.activated),
                      t("match.board.alreadyActivated"),
                    )
                  ) {
                    return;
                  }
                  if (
                    toastIfBlockedAction(minionActive, t("match.board.minionAlreadyActive"))
                  ) {
                    return;
                  }
                  if (
                    toastIfBlockedAction(auraActive, t("match.board.auraAlreadyActive"))
                  ) {
                    return;
                  }
                  if (
                    toastIfBlockedAction(
                      equipmentOccupied && !isSummonAbility && !isAuraAbility,
                      t("match.board.hostEquipmentOccupied"),
                    )
                  ) {
                    return;
                  }
                  requestActivateAbility(
                    boardSlot,
                    ability,
                    equipment,
                    action.index,
                    { payWithPhysicalArmor: action.payWithPhysicalArmor },
                  );
                  closeMenu();
                }}
              >
                <AbilityActivatePrefix
                  cost={actionCost}
                  healthCost={
                    action.payWithPhysicalArmor ? 0 : actionHealthCost
                  }
                  chargeCost={
                    action.payWithPhysicalArmor
                      ? 0
                      : getEffectConsumeChargeCost(ability.effect)
                  }
                />
                {renderTextWithStatusEffectIcons(
                  findLevelUpActionLabel(levelUpDisplayActions, action.index) ??
                    action.label,
                  {
                    outputDelta:
                      (abilityLevelUpCompare?.display || ability).outputDelta ||
                      0,
                    compareText: findLevelUpActionLabel(
                      levelUpCompareActions,
                      action.index,
                    ),
                  },
                )}
              </button>
              );
            })
          : null}
        {!ability.inherent ? (
          <button
            type="button"
            className={`${styles.button} ${styles.boardCardActionButton}${
              canUseAbilitySlotAction &&
              boardHasEmptyAbilitySlot &&
              !targetingBlocksActions
                ? ` ${styles.actionReady}`
                : ""
            }${
              !canUseAbilitySlotAction ||
              !boardHasEmptyAbilitySlot ||
              targetingBlocksActions
                ? ` ${styles.buttonLooksDisabled}`
                : ""
            }`}
            data-gamepad-item=""
            onClick={() => {
              if (
                toastIfBlockedAction(
                  targetingBlocksActions,
                  t("match.board.resolveTargetingFirst"),
                )
              ) {
                return;
              }
              if (
                toastIfBlockedAction(
                  minionActive,
                  t("match.board.unsummonMinionFirst"),
                )
              ) {
                return;
              }
              if (
                toastIfBlockedAction(auraActive, t("match.board.deactivateAuraFirst"))
              ) {
                return;
              }
              startMovingAbility(boardSlot, ability);
            }}
          >
            {t("match.board.moveAbility")}
          </button>
        ) : null}
        {!ability.inherent && !auraActive ? (
          <button
            type="button"
            className={`${styles.button} ${styles.boardCardActionButton}`}
            data-gamepad-item=""
            onClick={() => {
              if (
                toastIfBlockedAction(auraActive, t("match.board.deactivateAuraFirst"))
              ) {
                return;
              }
              send({
                type: "ai:discardAbility",
                boardSlot,
                abilityInstanceId: ability.instanceId,
              });
              closeMenu();
            }}
          >
            {t("match.board.discard")}
          </button>
        ) : null}
      </div>
      </KeywordTipContext.Provider>
    );
  };

  const getAbilityActivateLabel = (ability) => {
    if (!ability || ability.kind !== "active") return null;
    const ignoreArmorSuffix = ability.ignoreArmor ? " · Ignore Armor" : "";
    const ignorePhysical =
      ability.effectiveIgnorePhysicalArmor ??
      ability.effect?.effectiveIgnorePhysicalArmor ??
      ability.effect?.ignorePhysicalArmor ??
      0;
    const ignorePhysicalArmorSuffix =
      !ability.ignoreArmor && ignorePhysical > 0
        ? ` · Ignore ${ignorePhysical} Physical Armor`
        : "";
    const echoCard = getAbilityEchoPreviewCard(ability);
    const echoLabel = echoCard ? getAbilityActivateLabel(echoCard) : null;
    const echoSuffix = ability.echo
      ? echoLabel && echoLabel !== "Activate"
        ? ` + ${echoLabel}`
        : " · Echo"
      : "";
    const effectSuffix = `${ignoreArmorSuffix}${ignorePhysicalArmorSuffix}${echoSuffix}`;
    const level = Math.max(1, ability?.effectiveLevel || ability?.level || 1);
    if (Array.isArray(ability.effect?.choose)) {
      return (
        formatAbilityShorthandText(
          formatActivatedEffectText(getAbilityChoiceDisplayEffect(ability)),
        ) || "Activate"
      );
    }
    if (ability.effect?.id === "summonMinion") {
      if (ability.minion && (ability.minion.hp || 0) > 0) {
        return null;
      }
      const name =
        ability.minionName ||
        ability.effect.name ||
        String(ability.name || "Minion").replace(/^Summon\s+/i, "");
      const hp = ability.effectiveMinionHp ?? ability.effect.hp ?? 0;
      return `Summon ${name} (${hp} HP)${effectSuffix}`;
    }
    if (ability.effect?.id === "activateAura") {
      if (ability.aura) {
        return null;
      }
      return `Activate ${ability.name || "Aura"}${effectSuffix}`;
    }
    const scaledEffect = scaleEffectAmounts(ability.effect, level, {
      additive: abilityUsesAdditiveLevelScale(ability),
    });
    const outputText = formatAbilityShorthandText(
      formatAbilityOutputEffectText(scaledEffect),
    );
    if (listEffectEntries(ability.effect).length > 1 && outputText) {
      return `${outputText}${effectSuffix}`;
    }
    if (ability.effect?.id === "damageOpponent") {
      const parts = [
        `${ability.effectiveAmount ?? ability.effect.amount ?? 0} ${
          ability.effect.damageType || "physical"
        }${formatElementalDamageAddText(ability.effectiveElementalDamage)} damage`,
      ];
      const burn = ability.effectiveBurn ?? ability.effect.burn ?? 0;
      const chill = ability.effectiveChill ?? ability.effect.chill ?? 0;
      const shock = ability.effectiveShock ?? ability.effect.shock ?? 0;
      if (burn > 0) parts.push(`Burn ${burn}`);
      if (chill > 0) parts.push(`Chill ${chill}`);
      if (shock > 0) parts.push(`Shock ${shock}`);
      return `${parts.join(" · ")}${effectSuffix}`;
    }
    if (ability.effect?.id === "convertPhysicalArmorToDamage") {
      return `${ability.effectiveAmount ?? ability.effect.amount ?? 0} physical damage${effectSuffix}`;
    }
    if (ability.effect?.id === "convertMagicArmorToDamage") {
      return `${ability.effectiveAmount ?? ability.effect.amount ?? 0} magical damage${effectSuffix}`;
    }
    if (ability.effect?.id === "hexExpendCost") {
      const hexText = formatAbilityOutputEffectText({
        ...ability.effect,
        effectiveDisableExpend:
          ability.effectiveDisableExpend ??
          Boolean(ability.effect.modify?.disableExpend),
        effectiveSlottedAbilityCostAdd: ability.effectiveSlottedAbilityCostAdd,
      });
      return `${hexText || "Hex opponent equipment"}${effectSuffix}`;
    }
    if (ability.effect?.id === "bleed") {
      const damage =
        ability.effectiveDamage ?? ability.effect.damage ?? 0;
      const stacks =
        ability.effectiveAmount ?? ability.effect.amount ?? 0;
      return `${damage} ${
        ability.effect.damageType || "physical"
      } · Bleed ${stacks}${effectSuffix}`;
    }
    if (ability.effect?.id === "poison") {
      const damage = ability.effectiveDamage ?? ability.effect.damage ?? 0;
      const stacks =
        ability.effectiveAmount ?? ability.effect.amount ?? 0;
      if (damage > 0) {
        return `${damage} ${
          ability.effect.damageType || "physical"
        } · Poison ${stacks}${effectSuffix}`;
      }
      return `Poison ${stacks}${effectSuffix}`;
    }
    if (ability.effect?.id === "convertStacks") {
      const damageType = ability.effect.damageType || "physical";
      return `${ability.effectiveAmount ?? 0} ${damageType} damage${effectSuffix}`;
    }
    if (ability.effect?.id === "wither") {
      return `Wither ${
        ability.effectiveAmount ?? ability.effect.amount ?? 0
      }${effectSuffix}`;
    }
    if (ability.effect?.id === "healSelf") {
      return `Heal ${ability.effectiveAmount ?? ability.effect.amount ?? 0}${effectSuffix}`;
    }
    if (ability.effect?.id === "maxHealthIncrease") {
      return `Max Health ${
        ability.effectiveAmount ?? ability.effect.amount ?? 0
      }${effectSuffix}`;
    }
    if (ability.effect?.id === "temporaryPhysicalArmor") {
      return `Physical Armor ${
        ability.effectiveAmount ?? ability.effect.amount ?? 0
      }${effectSuffix}`;
    }
    if (ability.effect?.id === "temporaryMagicArmor") {
      return `Magic Armor ${
        ability.effectiveAmount ?? ability.effect.amount ?? 0
      }${effectSuffix}`;
    }
    if (ability.effect?.id === "temporaryThorns") {
      const percent =
        ability.effectivePercent ??
        ability.effect.percent ??
        ability.effect.amount ??
        10;
      const matchType = ability.effect.match?.type || "melee";
      return `Thorns ${percent}% (${matchType}) until end of turn${effectSuffix}`;
    }
    if (ability.effect?.id === "regeneration") {
      return `Regeneration ${
        ability.effectiveAmount ?? ability.effect.amount ?? 0
      }${effectSuffix}`;
    }
    return ability.echo ? `Activate${effectSuffix}` : "Activate";
  };

  const listBoardEquipment = (seat) => {
    if (!seat?.board) return [];
    const seen = new Set();
    const list = [];
    BODY_LAYOUT.forEach(({ key }) => {
      const equipment = boardEquipmentForSlot(seat, key);
      if (!equipment?.instanceId || seen.has(equipment.instanceId)) return;
      seen.add(equipment.instanceId);
      list.push(equipment);
    });
    return list;
  };

  const getRemainingMatchActionCounts = (seat) => {
    if (!seat) {
      return { equip: 0, ability: 0, expendGear: 0, activateAbilities: 0 };
    }
    const equip = isWorkshopMatch || !seat.playedEquipment ? 1 : 0;
    const ability = isWorkshopMatch
      ? 1
      : (seat.playedAbility ? 0 : 1) +
        Math.max(0, Number(seat.nimbleFingersCharges) || 0);
    let expendGear = 0;
    let activateAbilities = 0;
    listAbilityBoardSlotKeys(seat).forEach((boardSlot) => {
      const equipment = boardEquipmentForSlot(seat, boardSlot);
      const occupied = equipment
        ? equipmentHasBoardOccupant(equipment, seat)
        : false;
      const considerAbility = (ability) => {
        if (!ability || ability.kind !== "active") return;
        const minionActive =
          Boolean(ability.minion) && (ability.minion.hp || 0) > 0;
        const auraActive = Boolean(ability.aura);
        if (ability.activated || minionActive || auraActive) return;
        if (occupied) {
          const isSummonAbility = ability.effect?.id === "summonMinion";
          const isAuraAbility = ability.effect?.id === "activateAura";
          if (!isSummonAbility && !isAuraAbility) return;
        }
        activateAbilities += 1;
      };
      getColumnAbilitySlots(seat, boardSlot).forEach((slot) => {
        considerAbility(slot?.ability);
      });
      considerAbility(equipment?.inherentAbility);
    });
    listBoardEquipment(seat).forEach((equipment) => {
      const occupied = equipmentHasBoardOccupant(equipment, seat);
      if (
        !equipment.expended &&
        !equipment.expendDisabled &&
        !occupied &&
        (equipmentHasResourceExpend(equipment) ||
          (!equipment.broken &&
            listEquipmentEffectActions(equipment, seat).length > 0))
      ) {
        expendGear += 1;
      }
    });
    return { equip, ability, expendGear, activateAbilities };
  };

  const renderRemainingActionsStrip = () => {
    if (isReplayView || isSpectateView) return null;
    if (!isMyTurn && !isWorkshopMatch) return null;
    if (
      !displayMatch ||
      (displayMatch.phase !== "main" && !isWorkshopMatch)
    ) {
      return null;
    }
    const counts = getRemainingMatchActionCounts(actingSeat);
    return (
      <div className={styles.actionBarTop}>
        <div className={styles.remainingActionsStrip}>
          <span className={styles.remainingActionsPrefix}>
            {t("match.board.remainingActs")}
          </span>
          <RemainingActionChip
            hintId="remaining-equip"
            label={t("match.board.remainingEquip")}
            count={counts.equip}
            hintText={t("match.board.hintEquip")}
            activeHintId={activeGameHint?.id || null}
            onToggle={toggleGameHint}
          />
          <RemainingActionChip
            hintId="remaining-ability"
            label={t("match.board.remainingAbility")}
            count={counts.ability}
            hintText={t("match.board.hintAbility")}
            activeHintId={activeGameHint?.id || null}
            onToggle={toggleGameHint}
          />
          <RemainingActionChip
            hintId="remaining-expend-gear"
            label={t("match.board.remainingExpendGear")}
            count={counts.expendGear}
            hintText={t("match.board.hintExpendGear")}
            activeHintId={activeGameHint?.id || null}
            onToggle={toggleGameHint}
          />
          <RemainingActionChip
            hintId="remaining-activate-abilities"
            label={t("match.board.remainingActivateAbilities")}
            count={counts.activateAbilities}
            hintText={t("match.board.hintActivateAbilities")}
            activeHintId={activeGameHint?.id || null}
            onToggle={toggleGameHint}
          />
        </div>
      </div>
    );
  };

  const requestActivateAbility = (
    boardSlot,
    ability,
    equipment = null,
    effectIndex = null,
    { payWithPhysicalArmor = false } = {},
  ) => {
    setSelectedHand(null);
    setMovingAbility(null);
    setSlottedAbilityExam(null);
    const costAbility = withAbilityChoiceCost(ability, effectIndex);
    if (payWithPhysicalArmor) {
      const armorCost = physicalArmorPayCostFromResourceCost(
        costAbility?.effectiveCost || costAbility?.cost || {},
      );
      if (
        !armorCost ||
        !canAffordPhysicalArmorCost(selfSeat, armorCost)
      ) {
        setSelectedEquipmentId(null);
        setEquipExpendConfirm(null);
        setEquipExpendConfirmPopupStyle(null);
        setEquipCounterExam(null);
        setEquipCounterExamPopupStyle(null);
        showToast(t("match.board.notEnoughPhysicalArmor"));
        return;
      }
    } else if (!canAffordAbilityActivation(selfSeat, costAbility, catalogMap)) {
      setSelectedEquipmentId(null);
      setEquipExpendConfirm(null);
      setEquipExpendConfirmPopupStyle(null);
      setEquipCounterExam(null);
      setEquipCounterExamPopupStyle(null);
      showToast(
        describeAbilityActivationShortfall(selfSeat, costAbility, catalogMap) ||
          t("match.board.notEnoughResources"),
      );
      return;
    }
    const selectedEffect =
      effectIndex != null && Array.isArray(ability.effect?.choose)
        ? ability.effect.choose[effectIndex]
        : ability.effect;
    const activate = (payment = undefined) => {
      send({
        type: "ai:activateAbility",
        boardSlot,
        abilityInstanceId: ability.instanceId,
        ...(payment ? { payment } : {}),
        ...(effectIndex == null ? {} : { effectIndex }),
        ...(payWithPhysicalArmor ? { payWithPhysicalArmor: true } : {}),
      });
    };
    const cost = costAbility.effectiveCost || costAbility.cost || {};
    const info = getNeutralFlexPaymentInfo(mergeSeatResources(selfSeat), cost);
    const wouldLose = actionWouldCauseLoss(
      selfSeat,
      selectedEffect,
      abilityDisplayScale(ability),
    );
    const confirmIfNeeded = (run) => {
      if (!wouldLose) {
        run();
        return;
      }
      setLoseConfirm({
        action: "activate",
        name: ability.name,
        run,
      });
    };
    if (info.needsChoice) {
      setPaymentPrompt({
        boardSlot,
        equipmentInstanceId: equipment?.instanceId || null,
        abilityInstanceId: ability.instanceId,
        effectIndex,
        flex: info.flex,
        available: info.available,
        fixed: info.fixed,
        name: ability.name,
        wouldLose,
      });
      setPaymentDraft(
        buildRecommendedFlexPayment(info.available, info.flex, info.fixed),
      );
      return;
    }
    confirmIfNeeded(() => activate());
  };

  const requestExpendMinion = (equipment, ability, effectIndex = null) => {
    if (!equipment || !ability) return;
    setSelectedHand(null);
    setMovingAbility(null);
    setSlottedAbilityExam(null);
    setSelectedEquipmentId(null);
    send({
      type: "ai:expendMinion",
      equipmentInstanceId: equipment.instanceId,
      abilityInstanceId: ability.instanceId,
      ...(effectIndex == null ? {} : { effectIndex }),
    });
  };

  const requestUnsummonMinion = ({
    equipmentInstanceId = null,
    abilityInstanceId = null,
  } = {}) => {
    if (!abilityInstanceId) return;
    setSelectedHand(null);
    setMovingAbility(null);
    setSlottedAbilityExam(null);
    setSelectedEquipmentId(null);
    closeKeywordTip();
    send({
      type: "ai:unsummonMinion",
      equipmentInstanceId,
      abilityInstanceId,
    });
  };

  const requestDeactivateAura = ({
    equipmentInstanceId = null,
    abilityInstanceId = null,
  } = {}) => {
    if (!abilityInstanceId) return;
    setSelectedHand(null);
    setMovingAbility(null);
    setSlottedAbilityExam(null);
    setSelectedEquipmentId(null);
    closeKeywordTip();
    send({
      type: "ai:deactivateAura",
      equipmentInstanceId,
      abilityInstanceId,
    });
  };

  const listAbilityActivateActions = (ability) => {
    if (!ability || ability.kind !== "active") return [];
    if (Array.isArray(ability.effect?.choose)) {
      return ability.effect.choose.map((_, index) => ({
        index,
        label:
          withAbilityEchoSuffix(
            formatAbilityShorthandText(
              formatActivatedEffectText(
                getAbilityChoiceDisplayEntry(ability, index),
              ),
            ),
            formatAbilityEchoChoiceText(ability, index),
          ) || t("match.board.effectNumber", { number: index + 1 }),
      }));
    }
    const label = getAbilityActivateLabel(ability);
    if (!label) return [];
    return [{ index: null, label }];
  };

  const listMinionExpendActions = (ability) => {
    const action = ability?.effect?.action;
    if (!action) return [];
    if (Array.isArray(action.choose)) {
      return action.choose.map((entry, index) => ({
        index,
        label:
          formatAbilityShorthandText(formatActivatedEffectText(entry)) ||
          t("match.board.actionNumber", { number: index + 1 }),
      }));
    }
    const label =
      formatAbilityShorthandText(formatActivatedEffectText(action)) || t("match.board.expend");
    return [{ index: null, label }];
  };

  const startMovingAbility = (boardSlot, ability) => {
    if (!boardSlot || !ability) return;
    if (!canUseAbilitySlotAction) {
      showToast(t("match.board.alreadyPlayedAbility"));
      return;
    }
    if (!boardHasEmptyAbilitySlot) {
      showToast(t("match.board.noEmptyAbilitySlots"));
      return;
    }
    setSelectedHand(null);
    setMovingAbility({
      boardSlot,
      abilityInstanceId: ability.instanceId,
    });
    setSelectedEquipmentId(null);
    setSlottedAbilityExam(null);
    setHandMenu(null);
    setBoardMenu(null);
    showToast(t("match.board.selectEmptyAbilitySlot"));
  };

  const renderActionBarInherentAbility = (
    equipment,
    ability,
    ownBoard,
    seat,
  ) => {
    if (!ability) return null;
    const minionActive =
      Boolean(ability.minion) && (ability.minion.hp || 0) > 0;
    const auraActive = Boolean(ability.aura);
    const abilityUsed = slottedAbilityLooksUsed(
      [],
      -1,
      ability,
      equipment,
      seat,
    );
    const abilityArtUrl = getAbilityCardImageUrl(ability);
    const traitAbilityLevelTarget =
      ownBoard &&
      isMyTurn &&
      pendingTraitAbilityLevelIds.has(ability.instanceId);
    const traitEquipmentEmpowerTarget =
      ownBoard &&
      isMyTurn &&
      pendingTraitEquipmentAbilityIds.has(ability.instanceId);
    return (
      <div className={styles.abilitySlotActionBarGroup}>
        <div className={styles.abilitySlotActionBarCell}>
          <div className={styles.abilitySlotCardWrap}>
            <div
              className={`${styles.abilitySlotRect} ${
                TINT_CLASS[primaryAttribute(ability)] || ""
              } ${styles.abilitySlotFilled} ${
                abilityArtUrl ? styles.abilitySlotWithArt : ""
              } ${abilityUsed ? styles.abilitySlotUsed : ""} ${
                minionActive || auraActive ? styles.abilitySlotMinionActive : ""
              }${
                traitAbilityLevelTarget || traitEquipmentEmpowerTarget
                  ? ` ${styles.abilitySlotTargetMatch}`
                  : ""
              }`}
            >
              {abilityArtUrl ? (
                <span
                  aria-hidden="true"
                  className={[
                    styles.slotAbilityArt,
                    getFoilArtFilterClass(ability.foilEffect),
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  style={{ backgroundImage: `url("${abilityArtUrl}")` }}
                />
              ) : null}
              <FoilOverlay
                foilEffect={ability.foilEffect}
                cardId={ability.cardId}
              />
              <button
                type="button"
                className={styles.abilitySlotRectHit}
                data-gamepad-item=""
                title={
                  traitAbilityLevelTarget
                    ? t("match.board.slotTitleCanLevelUp")
                    : traitEquipmentEmpowerTarget
                      ? t("match.board.slotTitleEmpowerArmor")
                      : ability.name
                }
                aria-label={ability.name}
                onClick={(event) => {
                  event.stopPropagation();
                  closeKeywordTip();
                  setSelectedEquipmentId(null);
                  setSlottedAbilityExam({
                    boardSlot:
                      Object.entries(seat?.board || {}).find(
                        ([, item]) =>
                          item && item.instanceId === equipment.instanceId,
                      )?.[0] || null,
                    abilityInstanceId: ability.instanceId,
                    cardId: ability.cardId,
                  });
                }}
              ></button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderInherentAbilityActivateButtons = (
    equipment,
    seat,
    { buttonClassName = styles.button, onActivated = null } = {},
  ) => {
    const ability = equipment?.inherentAbility;
    if (!ability || ability.kind !== "active") return null;
    const boardSlot = getEquipmentBoardSlotKey(seat, equipment);
    if (!boardSlot) return null;
    const activateLabel = getAbilityActivateLabel(ability);
    if (!activateLabel) return null;
    const minionActive =
      Boolean(ability.minion) && (ability.minion.hp || 0) > 0;
    const auraActive = Boolean(ability.aura);
    const isSummonAbility = ability.effect?.id === "summonMinion";
    const isAuraAbility = ability.effect?.id === "activateAura";
    const equipmentOccupied = equipmentHasBoardOccupant(equipment, seat);
    const occupiedBlocks =
      equipmentOccupied && !isSummonAbility && !isAuraAbility;
    const bloodMagicHealthCost = Math.max(0, ability.effectiveHealthCost || 0);
    const abilityHealthCost =
      bloodMagicHealthCost +
      getAbilityHealthCostAmount(ability.effect, abilityDisplayScale(ability));
    return expandAbilityActivateActionsForArmorPay(
      ability,
      seat,
      listAbilityActivateActions(ability),
    ).map((action) => {
      const choiceAbility = withAbilityChoiceCost(ability, action.index);
      const armorCost = physicalArmorPayCostFromResourceCost(
        choiceAbility.effectiveCost || choiceAbility.cost || {},
      );
      const actionCost = action.payWithPhysicalArmor
        ? armorCost
        : choiceAbility.effectiveCost || choiceAbility.cost;
      const actionHealthCost =
        abilityHealthCost -
        bloodMagicHealthCost +
        Math.max(0, choiceAbility.effectiveHealthCost || 0);
      const affordable = action.payWithPhysicalArmor
        ? Boolean(armorCost) && canAffordPhysicalArmorCost(seat, armorCost)
        : canAffordAbilityActivation(seat, choiceAbility, catalogMap);
      const actionReady =
        !ability.activated &&
        !minionActive &&
        !auraActive &&
        !occupiedBlocks &&
        affordable;
      return (
        <button
          key={`inherent-activate-${ability.instanceId}-${action.index ?? "main"}${action.armorPayKey || ""}`}
          type="button"
          className={`${buttonClassName}${
            actionReady && !targetingBlocksActions
              ? ` ${styles.actionReady}`
              : ""
          }${
            !actionReady || targetingBlocksActions
              ? ` ${styles.buttonLooksDisabled}`
              : ""
          }`}
          data-gamepad-item=""
          title={`${t("match.board.tagInherent")} · ${ability.name}`}
          onClick={() => {
            if (
              toastIfBlockedAction(
                targetingBlocksActions,
                t("match.board.resolveTargetingFirst"),
              )
            ) {
              return;
            }
            if (
              toastIfBlockedAction(
                Boolean(ability.activated),
                t("match.board.alreadyActivated"),
              )
            ) {
              return;
            }
            if (
              toastIfBlockedAction(minionActive, t("match.board.minionAlreadyActive"))
            ) {
              return;
            }
            if (
              toastIfBlockedAction(auraActive, t("match.board.auraAlreadyActive"))
            ) {
              return;
            }
            if (
              toastIfBlockedAction(
                occupiedBlocks,
                t("match.board.hostEquipmentOccupied"),
              )
            ) {
              return;
            }
            requestActivateAbility(boardSlot, ability, equipment, action.index, {
              payWithPhysicalArmor: action.payWithPhysicalArmor,
            });
            if (typeof onActivated === "function") onActivated();
          }}
        >
          <AbilityActivatePrefix
            cost={actionCost}
            healthCost={action.payWithPhysicalArmor ? 0 : actionHealthCost}
            chargeCost={
              action.payWithPhysicalArmor
                ? 0
                : getEffectConsumeChargeCost(ability.effect)
            }
          />
          {`${ability.name}: `}
          {renderTextWithStatusEffectIcons(action.label)}
        </button>
      );
    });
  };

  const renderBoardActionOwner = (ownerName = null) => {
    if (!ownerName) return null;
    return (
      <div className={styles.seatBoardIdentity}>
        <div className={styles.seatBoardOwner}>
          {formatMatchHudPlayerLabel(ownerName)}
        </div>
      </div>
    );
  };

  const renderBoardActionBars = (seat, ownBoard, ownerName = null) => {
    if (isReplayView || !seat) return null;
    const canAct = Boolean(ownBoard && isMyTurn);
    const seatResources = mergeSeatResources(ownBoard ? seat : selfSeat);
    const aspectId =
      typeof seat.aspectId === "string" && seat.aspectId
        ? seat.aspectId
        : null;
    const aspectCard = aspectId ? catalogMap[aspectId] || null : null;
    const aspectFoil = seat.aspectFoil || null;
    const aspectArtUrl = aspectCard ? getAspectCardImageUrl(aspectCard) : null;
    const aspectHybrid = aspectCard
      ? getHybridOutlineProps(aspectCard)
      : { className: "", style: undefined };
    const canSwitchWorkshopAspect =
      isWorkshopMatch && ownBoard && !isReplayView && !isSpectateView;
    const openAspectFromBars = () => {
      setEquipExpendConfirm(null);
      setEquipCounterExam(null);
      setSlottedAbilityExam(null);
      setSelectedEquipmentId(null);
      if (canSwitchWorkshopAspect) {
        openWorkshopAspectPicker();
        return;
      }
      if (aspectCard) {
        openCardExam(aspectCard.id, aspectFoil);
      }
    };

    return (
      <div className={styles.boardActionBars} data-gamepad-zone="board">
        {renderBoardActionOwner(ownerName)}
        <div className={styles.boardActionBarsSection}>
          <div className={styles.muted}>{t("match.panel.aspect")}</div>
          {aspectCard ? (
            <div
              className={`${styles.boardActionStrip} ${
                aspectHybrid.className ||
                TINT_CLASS[primaryAttribute(aspectCard)] ||
                ""
              } ${aspectArtUrl ? styles.boardActionStripWithArt : ""}`}
              style={aspectHybrid.style}
            >
              {aspectArtUrl ? (
                <div
                  aria-hidden="true"
                  className={styles.boardActionStripArt}
                >
                  <div
                    className={[
                      styles.boardActionStripArtFill,
                      getFoilArtFilterClass(aspectFoil),
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    style={{ backgroundImage: `url("${aspectArtUrl}")` }}
                  />
                  <div className={styles.boardActionStripArtShade} />
                  <FoilOverlay foilEffect={aspectFoil} cardId={aspectId} />
                </div>
              ) : null}
              <div className={styles.boardActionStripInfo}>
                <button
                  type="button"
                  className={styles.boardActionStripName}
                  onClick={openAspectFromBars}
                >
                  {aspectCard.name}
                </button>
                {aspectCard.text ? (
                  <div className={styles.boardActionStripPassive}>
                    {aspectCard.text}
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <button
              type="button"
              className={`${styles.boardActionStrip} ${styles.boardActionStripEmpty} ${styles.boardActionStripOneLine}`}
              disabled={!canSwitchWorkshopAspect}
              onClick={
                canSwitchWorkshopAspect ? openAspectFromBars : undefined
              }
            >
              <div className={styles.boardActionStripInfo}>
                <span className={styles.boardActionStripName}>
                  {t("match.panel.aspectEmpty")}
                </span>
              </div>
            </button>
          )}
        </div>
        <div className={styles.boardActionBarsSection}>
          <div className={styles.muted}>{t("match.panel.equipment")}</div>
          {BODY_LAYOUT.map((slot) => {
            const equipment = boardEquipmentForSlot(seat, slot.key);
            if (!equipment) {
              return (
                <div
                  key={`empty-${slot.key}`}
                  className={`${styles.boardActionStrip} ${styles.boardActionStripEmpty} ${styles.boardActionStripOneLine}`}
                >
                  <div className={styles.boardActionStripInfo}>
                    <span className={styles.boardActionStripName}>
                      {t("match.panel.slotEmpty", { slot: boardSlotDisplayLabel(slot.key, slot.label) })}
                    </span>
                  </div>
                </div>
              );
            }
            const occupyingMinion = getEquipmentOccupyingMinion(
              equipment,
              seat,
            );
            const occupyingAura = !occupyingMinion
              ? getEquipmentOccupyingAura(equipment, seat)
              : null;
            const boardOccupant = occupyingMinion || occupyingAura;
            const occupyingAbility = occupyingMinion
              ? findAbilityForMinion(equipment, occupyingMinion, seat)
              : occupyingAura
                ? findAbilityForAura(equipment, occupyingAura, seat)
                : null;
            const occupyingCard =
              (boardOccupant?.cardId &&
                catalogMap[boardOccupant.cardId]) ||
              occupyingAbility ||
              null;
            const resourceGrants = boardOccupant
              ? []
              : resourceGrantEntries(equipment.resources);
            const effectActions = boardOccupant
              ? []
              : expandEquipmentResourceEffectActionsForArmorPay(
                  listEquipmentEffectActions(equipment, seat),
                  equipment,
                  seat,
                );
            const shockStacks = Math.max(0, seat?.shockStacks || 0);
            const columnSlots = getColumnAbilitySlots(seat, slot.key);
            const stripArtUrl = boardOccupant
              ? getCardImageUrl(occupyingCard)
              : getEquipmentCardImageUrl(equipment);
            const stripFoil = boardOccupant
              ? boardOccupant.foilEffect ||
                occupyingAbility?.foilEffect ||
                null
              : equipment.foilEffect;
            const stripCardId = boardOccupant
              ? boardOccupant.cardId || occupyingAbility?.cardId || null
              : equipment.cardId;
            const equipmentStripHybrid = getHybridOutlineProps(
              boardOccupant ? occupyingCard || equipment : equipment,
            );
            const isAbilityTarget =
              Boolean(pendingAbilityTarget) &&
              isMyTurn &&
              (isWorkshopMatch || !ownBoard) &&
              pendingTargetEquipmentIds.has(equipment.instanceId);
            const minionExpendActions =
              occupyingMinion && occupyingAbility
                ? listMinionExpendActions(occupyingAbility)
                : [];
            const hasAbilitySlotArea =
              !boardOccupant &&
              (columnSlots.length > 0 || Boolean(equipment.inherentAbility));
            return (
              <div
                key={`${slot.key}-${equipment.instanceId}`}
                className={`${styles.boardActionStrip} ${
                  hasAbilitySlotArea ? styles.boardActionStripWithSlots : ""
                } ${
                  equipmentStripHybrid.className ||
                  TINT_CLASS[
                    primaryAttribute(
                      boardOccupant
                        ? occupyingCard || occupyingAbility || equipment
                        : equipment,
                    )
                  ] ||
                  ""
                } ${
                  (occupyingMinion
                    ? occupyingMinion.expended
                    : equipment.expended)
                    ? styles.boardActionStripUsed
                    : ""
                } ${
                  boardOccupant ? styles.boardActionStripMinionActive : ""
                } ${stripArtUrl ? styles.boardActionStripWithArt : ""} ${
                  isAbilityTarget ? styles.equipBlockTarget : ""
                }`}
                style={equipmentStripHybrid.style}
              >
                {stripArtUrl ? (
                  <div
                    aria-hidden="true"
                    className={styles.boardActionStripArt}
                  >
                    <div
                      className={[
                        styles.boardActionStripArtFill,
                        getFoilArtFilterClass(stripFoil),
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      style={{ backgroundImage: `url("${stripArtUrl}")` }}
                    />
                    <div className={styles.boardActionStripArtShade} />
                    <FoilOverlay
                      foilEffect={stripFoil}
                      cardId={stripCardId}
                    />
                  </div>
                ) : null}
                <div className={styles.boardActionStripMain}>
                <div className={styles.boardActionStripInfo}>
                  <button
                    type="button"
                    className={styles.boardActionStripName}
                    onClick={() =>
                      setSelectedEquipmentId(equipment.instanceId)
                    }
                  >
                    {occupyingMinion
                      ? `${occupyingMinion.name || t("match.panel.minion")} ${
                          occupyingMinion.hp || 0
                        }/${occupyingMinion.maxHp || 0}`
                      : occupyingAura
                        ? occupyingAura.name || t("match.panel.aura")
                        : equipment.name}
                  </button>
                  {occupyingMinion ? (
                    <div className={styles.boardActionStripTags}>
                      {t("match.panel.minion")}
                      {t("match.panel.onEquipment", { name: equipment.name })}
                    </div>
                  ) : occupyingAura ? (
                    <div className={styles.boardActionStripTags}>
                      {t("match.panel.aura")}
                      {t("match.panel.onEquipment", { name: equipment.name })}
                    </div>
                  ) : null}
                  {!boardOccupant
                    ? (equipment.chargeCounters || []).map((counter) => (
                        <div
                          key={`charge-${counter.id}`}
                          className={styles.boardActionStripCharges}
                          title={counter.label || t("match.panel.charges")}
                        >
                          <ChargeIcon
                            className={styles.boardActionStripChargeIcon}
                            title={counter.label}
                          />
                          {counter.amount || 0}
                        </div>
                      ))
                    : null}
                  {!boardOccupant && equipment.maxDurability != null ? (
                    <div
                      className={styles.boardActionStripCharges}
                      title={t("match.panel.durability")}
                    >
                      <MaskIcon
                        src={DURABILITY_ICON_URL}
                        className={styles.boardActionStripDurabilityIcon}
                      />
                      {equipment.durability ?? 0}/{equipment.maxDurability}
                      {equipment.broken ? t("match.panel.broken") : ""}
                    </div>
                  ) : null}
                  {!boardOccupant && formatEquipmentCardTags(equipment) ? (
                    <div className={styles.boardActionStripTags}>
                      {formatEquipmentCardTags(equipment)}
                    </div>
                  ) : null}
                  {!boardOccupant && equipment.text ? (
                    <div className={styles.boardActionStripPassive}>
                      {equipment.text}
                    </div>
                  ) : null}
                </div>
                <div className={styles.boardActionStripActions}>
                  {canAct && occupyingMinion && occupyingAbility
                    ? minionExpendActions.map((action) => (
                        <button
                          key={`strip-minion-expend-${action.index ?? "main"}`}
                          type="button"
                          className={`${styles.button} ${
                            !occupyingMinion.expended &&
                            !targetingBlocksActions
                              ? styles.actionReady
                              : ""
                          }`}
                          disabled={
                            Boolean(occupyingMinion.expended) ||
                            targetingBlocksActions
                          }
                          onClick={() =>
                            requestExpendMinion(
                              equipment,
                              occupyingAbility,
                              action.index,
                            )
                          }
                        >
                          {renderTextWithStatusEffectIcons(action.label)}
                        </button>
                      ))
                    : null}
                  {canAct && occupyingMinion && occupyingAbility ? (
                    <button
                      type="button"
                      className={styles.button}
                      disabled={targetingBlocksActions}
                      onClick={() =>
                        requestUnsummonMinion({
                          equipmentInstanceId: equipment.instanceId,
                          abilityInstanceId: occupyingAbility.instanceId,
                        })
                      }
                    >
                      {t("match.panel.unsummon")}
                    </button>
                  ) : null}
                  {canAct && occupyingAura && occupyingAbility ? (
                    <button
                      type="button"
                      className={`${styles.button}${actionSuccessClass(
                        `deactivateAura:${occupyingAbility.instanceId}`,
                      )}`}
                      disabled={targetingBlocksActions}
                      onClick={() =>
                        requestDeactivateAura({
                          equipmentInstanceId: equipment.instanceId,
                          abilityInstanceId: occupyingAbility.instanceId,
                        })
                      }
                    >
                      {t("match.panel.deactivate")}
                    </button>
                  ) : null}
                  {canAct && !boardOccupant && resourceGrants.length > 0 && (
                    <button
                      type="button"
                      className={`${styles.button}${tutorialHighlightClass(
                        equipment,
                        "resources",
                      )}${
                        equipmentCannotExpendForResources(equipment)
                          ? ` ${styles.buttonLooksDisabled}`
                          : ""
                      }`}
                      onClick={() => {
                        if (
                          toastIfBlockedAction(
                            equipmentCannotExpendForResources(equipment),
                            equipmentExpendBlockMessage(equipment),
                          )
                        ) {
                          return;
                        }
                        if (
                          toastIfUnaffordableCost(
                            seatResources,
                            equipment.expendCost,
                          )
                        ) {
                          return;
                        }
                        send({
                          type: "ai:expendEquipment",
                          instanceId: equipment.instanceId,
                          mode: "resources",
                        });
                      }}
                    >
                      <ExpendArrowPrefix cost={equipment.expendCost} />
                      {t("match.panel.gain")}
                      <ResourceGrantIconListWithShock
                        entries={resourceGrants}
                        shockStacks={shockStacks}
                      />
                      <DelayedResourceGrantIcons equipment={equipment} />
                    </button>
                  )}
                  {canAct &&
                    !boardOccupant &&
                    effectActions.map((action) => {
                      const actionCost = action.payWithPhysicalArmor
                        ? action.armorPayCost
                        : action.cost;
                      const unaffordable = action.payWithPhysicalArmor
                        ? !canAffordPhysicalArmorCost(seat, actionCost)
                        : !canAffordAbilityCost(
                            seatResources,
                            action.cost,
                            costAffordContext,
                          );
                      const baseBlocked =
                        equipmentEffectActionBlocked(equipment, action);
                      const chargeBlockMessage = baseBlocked
                        ? null
                        : getEquipmentEffectChargeBlockMessage(
                            equipment,
                            getEquipmentEffectForActionIndex(
                              equipment,
                              action.index,
                            ),
                          );
                      const hardBlocked =
                        baseBlocked || Boolean(chargeBlockMessage);
                      return (
                      <button
                        key={`strip-effect-${action.armorPayKey || (action.index ?? "all")}`}
                        type="button"
                        className={`${styles.button} ${
                          !hardBlocked &&
                          !targetingBlocksActions &&
                          !unaffordable
                            ? styles.actionReady
                            : ""
                        }${tutorialHighlightClass(
                          equipment,
                          "effect",
                          action.index,
                        )}${
                          hardBlocked || unaffordable
                            ? ` ${styles.buttonLooksDisabled}`
                            : ""
                        }`}
                        aria-disabled={
                          hardBlocked || unaffordable ? true : undefined
                        }
                        onClick={() => {
                          if (
                            toastIfBlockedAction(
                              hardBlocked,
                              chargeBlockMessage ||
                                equipmentExpendBlockMessage(
                                  equipment,
                                  targetingBlocksActions,
                                ),
                            )
                          ) {
                            return;
                          }
                          if (
                            toastIfBlockedAction(
                              targetingBlocksActions,
                              t("match.panel.toast.resolveTargetingFirst"),
                            )
                          ) {
                            return;
                          }
                          if (
                            action.payWithPhysicalArmor
                              ? toastIfBlockedAction(
                                  !canAffordPhysicalArmorCost(seat, actionCost),
                                  t("match.panel.toast.notEnoughPhysicalArmor"),
                                )
                              : toastIfUnaffordableCost(
                                  seatResources,
                                  action.cost,
                                )
                          ) {
                            return;
                          }
                          send({
                            type: "ai:expendEquipment",
                            instanceId: equipment.instanceId,
                            mode: "effect",
                            effectIndex: action.index,
                            ...(action.payWithPhysicalArmor
                              ? { payWithPhysicalArmor: true }
                              : {}),
                          });
                        }}
                      >
                        <ExpendArrowPrefix
                          cost={actionCost}
                          chargeCost={
                            action.payWithPhysicalArmor ? 0 : action.chargeCost
                          }
                          durabilityCost={action.durabilityCost}
                          selfDamage={action.selfDamage}
                        />
                        {renderEquipmentEffectActionContent(
                          action,
                          shockStacks,
                          {
                            effect: getEquipmentEffectForActionIndex(
                              equipment,
                              action.index,
                            ),
                            equipment,
                            seat: equipmentSeat,
                          },
                        )}
                      </button>
                      );
                    })}
                  {canAct && !boardOccupant
                    ? renderInherentAbilityActivateButtons(equipment, seat)
                    : null}
                  {canAct && !boardOccupant && equipment.canRefresh && (
                    <button
                      type="button"
                      className={`${styles.button}${
                        !equipment.expended ||
                        equipmentBuffActionDisabled(equipment) ||
                        !canAffordAbilityCost(
                          seatResources,
                          equipment.refreshCost,
                          costAffordContext,
                        )
                          ? ` ${styles.buttonLooksDisabled}`
                          : ""
                      }`}
                      aria-disabled={
                        !equipment.expended ||
                        equipmentBuffActionDisabled(equipment) ||
                        !canAffordAbilityCost(
                          seatResources,
                          equipment.refreshCost,
                          costAffordContext,
                        )
                          ? true
                          : undefined
                      }
                      onClick={() => {
                        if (
                          toastIfBlockedAction(
                            !equipment.expended,
                            t("match.panel.toast.refreshRequiresExpended"),
                          )
                        ) {
                          return;
                        }
                        if (
                          toastIfBlockedAction(
                            equipmentBuffActionDisabled(equipment),
                            t("match.panel.toast.equipmentCannotExpend"),
                          )
                        ) {
                          return;
                        }
                        if (
                          toastIfUnaffordableCost(
                            seatResources,
                            equipment.refreshCost,
                          )
                        ) {
                          return;
                        }
                        send({
                          type: "ai:refreshEquipment",
                          instanceId: equipment.instanceId,
                        });
                      }}
                    >
                      <PaymentArrowPrefix cost={equipment.refreshCost} />
                      {t("match.panel.refresh")}
                    </button>
                  )}
                  {canAct && !boardOccupant && equipment.canAddCharge && (
                    <button
                      type="button"
                      className={`${styles.button} ${
                        !equipmentBuffActionDisabled(equipment) &&
                        !targetingBlocksActions &&
                        canAffordAbilityCost(
                          seatResources,
                          equipment.addChargeCost,
                          costAffordContext,
                        )
                          ? styles.actionReady
                          : ""
                      }${actionSuccessClass(
                        `charge:${equipment.instanceId}`,
                      )}${
                        equipmentBuffActionDisabled(equipment) ||
                        targetingBlocksActions ||
                        !canAffordAbilityCost(
                          seatResources,
                          equipment.addChargeCost,
                          costAffordContext,
                        )
                          ? ` ${styles.buttonLooksDisabled}`
                          : ""
                      }`}
                      aria-disabled={
                        equipmentBuffActionDisabled(equipment) ||
                        targetingBlocksActions ||
                        !canAffordAbilityCost(
                          seatResources,
                          equipment.addChargeCost,
                          costAffordContext,
                        )
                          ? true
                          : undefined
                      }
                      onClick={() => {
                        if (
                          toastIfBlockedAction(
                            equipmentBuffActionDisabled(equipment),
                            t("match.panel.toast.equipmentCannotExpend"),
                          )
                        ) {
                          return;
                        }
                        if (
                          toastIfBlockedAction(
                            targetingBlocksActions,
                            t("match.panel.toast.resolveTargetingFirst"),
                          )
                        ) {
                          return;
                        }
                        if (
                          toastIfUnaffordableCost(
                            seatResources,
                            equipment.addChargeCost,
                          )
                        ) {
                          return;
                        }
                        send({
                          type: "ai:addEquipmentCharge",
                          instanceId: equipment.instanceId,
                        });
                      }}
                    >
                      <PaymentArrowPrefix cost={equipment.addChargeCost} />
                      {equipment.addChargeLabel || t("match.panel.charge")}
                    </button>
                  )}
                  {canAct &&
                    !boardOccupant &&
                    equipment.canSharpen &&
                    !equipment.sharpened && (
                      <button
                        type="button"
                        className={`${styles.button} ${
                          !equipmentBuffActionDisabled(equipment) &&
                          !targetingBlocksActions &&
                          canAffordAbilityCost(
                            seatResources,
                            equipment.sharpenCost,
                            costAffordContext,
                          )
                            ? styles.actionReady
                            : ""
                        }${actionSuccessClass(
                          `sharpen:${equipment.instanceId}`,
                        )}${
                          equipmentBuffActionDisabled(equipment) ||
                          targetingBlocksActions ||
                          !canAffordAbilityCost(
                            seatResources,
                            equipment.sharpenCost,
                            costAffordContext,
                          )
                            ? ` ${styles.buttonLooksDisabled}`
                            : ""
                        }`}
                        aria-disabled={
                          equipmentBuffActionDisabled(equipment) ||
                          targetingBlocksActions ||
                          !canAffordAbilityCost(
                            seatResources,
                            equipment.sharpenCost,
                            costAffordContext,
                          )
                            ? true
                            : undefined
                        }
                        onClick={() => {
                          if (
                            toastIfBlockedAction(
                              equipmentBuffActionDisabled(equipment),
                              t("match.panel.toast.equipmentCannotExpend"),
                            )
                          ) {
                            return;
                          }
                          if (
                            toastIfBlockedAction(
                              targetingBlocksActions,
                              t("match.panel.toast.resolveTargetingFirst"),
                            )
                          ) {
                            return;
                          }
                          if (
                            toastIfUnaffordableCost(
                              seatResources,
                              equipment.sharpenCost,
                            )
                          ) {
                            return;
                          }
                          send({
                            type: "ai:sharpenEquipment",
                            instanceId: equipment.instanceId,
                          });
                        }}
                      >
                        <PaymentArrowPrefix cost={equipment.sharpenCost} />
                        {renderTextWithStatusEffectIcons(
              formatSharpenLabel(equipment.sharpenAmount),
              { shorthand: false },
            )}
                      </button>
                    )}
                  {canAct &&
                    !boardOccupant &&
                    equipment.canFletch &&
                    equipment.fletchEligible && (
                      <button
                        type="button"
                        className={`${styles.button} ${
                          !equipmentBuffActionDisabled(equipment) &&
                          !targetingBlocksActions &&
                          canAffordAbilityCost(
                            seatResources,
                            equipment.fletchCost,
                            costAffordContext,
                          )
                            ? styles.actionReady
                            : ""
                        }${actionSuccessClass(
                          `fletch:${equipment.instanceId}`,
                        )}${
                          equipmentBuffActionDisabled(equipment) ||
                          targetingBlocksActions ||
                          !canAffordAbilityCost(
                            seatResources,
                            equipment.fletchCost,
                            costAffordContext,
                          )
                            ? ` ${styles.buttonLooksDisabled}`
                            : ""
                        }`}
                        aria-disabled={
                          equipmentBuffActionDisabled(equipment) ||
                          targetingBlocksActions ||
                          !canAffordAbilityCost(
                            seatResources,
                            equipment.fletchCost,
                            costAffordContext,
                          )
                            ? true
                            : undefined
                        }
                        onClick={() => {
                          if (
                            toastIfBlockedAction(
                              equipmentBuffActionDisabled(equipment),
                              t("match.panel.toast.equipmentCannotExpend"),
                            )
                          ) {
                            return;
                          }
                          if (
                            toastIfBlockedAction(
                              targetingBlocksActions,
                              t("match.panel.toast.resolveTargetingFirst"),
                            )
                          ) {
                            return;
                          }
                          if (
                            toastIfUnaffordableCost(
                              seatResources,
                              equipment.fletchCost,
                            )
                          ) {
                            return;
                          }
                          send({
                            type: "ai:fletchEquipment",
                            instanceId: equipment.instanceId,
                          });
                        }}
                      >
                        <PaymentArrowPrefix cost={equipment.fletchCost} />
                        {renderTextWithStatusEffectIcons(
              formatFletchLabel(equipment.fletchAmount),
              { shorthand: false },
            )}
                      </button>
                    )}
                  {canAct &&
                    !boardOccupant &&
                    equipment.canEnableEncore &&
                    !equipment.encoreEnabled && (
                      <button
                        type="button"
                        className={`${styles.button} ${
                          !equipmentBuffActionDisabled(equipment) &&
                          !targetingBlocksActions &&
                          canAffordAbilityCost(
                            seatResources,
                            equipment.enableEncoreCost,
                            costAffordContext,
                          )
                            ? styles.actionReady
                            : ""
                        }${actionSuccessClass(
                          `enableEncore:${equipment.instanceId}`,
                        )}${
                          equipmentBuffActionDisabled(equipment) ||
                          targetingBlocksActions ||
                          !canAffordAbilityCost(
                            seatResources,
                            equipment.enableEncoreCost,
                            costAffordContext,
                          )
                            ? ` ${styles.buttonLooksDisabled}`
                            : ""
                        }`}
                        aria-disabled={
                          equipmentBuffActionDisabled(equipment) ||
                          targetingBlocksActions ||
                          !canAffordAbilityCost(
                            seatResources,
                            equipment.enableEncoreCost,
                            costAffordContext,
                          )
                            ? true
                            : undefined
                        }
                        onClick={() => {
                          if (
                            toastIfBlockedAction(
                              equipmentBuffActionDisabled(equipment),
                              t("match.panel.toast.equipmentCannotExpend"),
                            )
                          ) {
                            return;
                          }
                          if (
                            toastIfBlockedAction(
                              targetingBlocksActions,
                              t("match.panel.toast.resolveTargetingFirst"),
                            )
                          ) {
                            return;
                          }
                          if (
                            toastIfUnaffordableCost(
                              seatResources,
                              equipment.enableEncoreCost,
                            )
                          ) {
                            return;
                          }
                          send({
                            type: "ai:enableEncoreEquipment",
                            instanceId: equipment.instanceId,
                          });
                        }}
                      >
                        <PaymentArrowPrefix
                          cost={equipment.enableEncoreCost}
                        />
                        {t("match.panel.encore")}
                      </button>
                    )}
                  {canAct &&
                    !boardOccupant &&
                    equipment.canHone &&
                    !equipment.honed && (
                      <button
                        type="button"
                        className={`${styles.button} ${
                          !equipmentBuffActionDisabled(equipment) &&
                          !targetingBlocksActions &&
                          canAffordAbilityCost(
                            seatResources,
                            equipment.honeCost,
                            costAffordContext,
                          )
                            ? styles.actionReady
                            : ""
                        }${actionSuccessClass(`hone:${equipment.instanceId}`)}${
                          equipmentBuffActionDisabled(equipment) ||
                          targetingBlocksActions ||
                          !canAffordAbilityCost(
                            seatResources,
                            equipment.honeCost,
                            costAffordContext,
                          )
                            ? ` ${styles.buttonLooksDisabled}`
                            : ""
                        }`}
                        onClick={() => {
                          if (
                            toastIfBlockedAction(
                              equipmentBuffActionDisabled(equipment) || targetingBlocksActions,
                              targetingBlocksActions
                                ? t("match.panel.toast.resolveTargetingFirst")
                                : t("match.panel.toast.equipmentCannotExpend"),
                            )
                          ) {
                            return;
                          }
                          if (
                            toastIfUnaffordableCost(
                              seatResources,
                              equipment.honeCost,
                            )
                          ) {
                            return;
                          }
                          send({
                            type: "ai:honeEquipment",
                            instanceId: equipment.instanceId,
                          });
                        }}
                      >
                        <PaymentArrowPrefix cost={equipment.honeCost} />
                        {renderTextWithStatusEffectIcons(
              formatHoneLabel(equipment.honeAmount),
              { shorthand: false },
            )}
                      </button>
                    )}
                  {canAct &&
                    !boardOccupant &&
                    equipment.canResearch &&
                    !equipment.researched && (
                      <button
                        type="button"
                        className={`${styles.button} ${
                          !equipmentBuffActionDisabled(equipment) &&
                          !targetingBlocksActions &&
                          canAffordAbilityCost(
                            seatResources,
                            equipment.researchCost,
                            costAffordContext,
                          )
                            ? styles.actionReady
                            : ""
                        }${actionSuccessClass(
                          `research:${equipment.instanceId}`,
                        )}${
                          equipmentBuffActionDisabled(equipment) ||
                          targetingBlocksActions ||
                          !canAffordAbilityCost(
                            seatResources,
                            equipment.researchCost,
                            costAffordContext,
                          )
                            ? ` ${styles.buttonLooksDisabled}`
                            : ""
                        }`}
                        onClick={() => {
                          if (
                            toastIfBlockedAction(
                              equipmentBuffActionDisabled(equipment) || targetingBlocksActions,
                              targetingBlocksActions
                                ? t("match.panel.toast.resolveTargetingFirst")
                                : t("match.panel.toast.equipmentCannotExpend"),
                            )
                          ) {
                            return;
                          }
                          if (
                            toastIfUnaffordableCost(
                              seatResources,
                              equipment.researchCost,
                            )
                          ) {
                            return;
                          }
                          send({
                            type: "ai:researchEquipment",
                            instanceId: equipment.instanceId,
                          });
                        }}
                      >
                        <PaymentArrowPrefix cost={equipment.researchCost} />
                        <StatusEffectIconStack effectId="research" amount={1} />
                      </button>
                    )}
                  {canAct &&
                    !boardOccupant &&
                    equipment.canSlottedDamage &&
                    !equipment.slottedDamageActive && (
                      <button
                        type="button"
                        className={`${styles.button} ${
                          !equipmentBuffActionDisabled(equipment) &&
                          !targetingBlocksActions &&
                          canAffordAbilityCost(
                            seatResources,
                            equipment.slottedDamageCost || {},
                            costAffordContext,
                          )
                            ? styles.actionReady
                            : ""
                        }${actionSuccessClass(
                          `slottedDamage:${equipment.instanceId}`,
                        )}${
                          equipmentBuffActionDisabled(equipment) ||
                          targetingBlocksActions ||
                          !canAffordAbilityCost(
                            seatResources,
                            equipment.slottedDamageCost || {},
                            costAffordContext,
                          )
                            ? ` ${styles.buttonLooksDisabled}`
                            : ""
                        }`}
                        onClick={() => {
                          if (
                            toastIfBlockedAction(
                              equipmentBuffActionDisabled(equipment) || targetingBlocksActions,
                              targetingBlocksActions
                                ? t("match.panel.toast.resolveTargetingFirst")
                                : t("match.panel.toast.equipmentCannotExpend"),
                            )
                          ) {
                            return;
                          }
                          if (
                            toastIfUnaffordableCost(
                              seatResources,
                              equipment.slottedDamageCost || {},
                            )
                          ) {
                            return;
                          }
                          send({
                            type: "ai:activateSlottedDamageEquipment",
                            instanceId: equipment.instanceId,
                          });
                        }}
                      >
                        <PaymentArrowPrefix
                          cost={equipment.slottedDamageCost || {}}
                        />
                        {t("match.panel.slottedDamageButton", { amount: Math.max(1, equipment.slottedDamageAmount || 1) })}
                      </button>
                    )}
                  {canAct && !boardOccupant && equipment.canRepair && (
                    <button
                      type="button"
                      className={`${styles.button} ${
                        !targetingBlocksActions &&
                        canAffordAbilityCost(
                          seatResources,
                          equipment.repairCost || {},
                          costAffordContext,
                        )
                          ? styles.actionReady
                          : ""
                      }${actionSuccessClass(
                        `repair:${equipment.instanceId}`,
                      )}${
                        targetingBlocksActions ||
                        !canAffordAbilityCost(
                          seatResources,
                          equipment.repairCost || {},
                          costAffordContext,
                        )
                          ? ` ${styles.buttonLooksDisabled}`
                          : ""
                      }`}
                      onClick={() => {
                        if (
                          toastIfBlockedAction(
                            targetingBlocksActions,
                            t("match.panel.toast.resolveTargetingFirst"),
                          )
                        ) {
                          return;
                        }
                        if (
                          toastIfUnaffordableCost(
                            seatResources,
                            equipment.repairCost || {},
                          )
                        ) {
                          return;
                        }
                        send({
                          type: "ai:repairEquipment",
                          instanceId: equipment.instanceId,
                        });
                      }}
                    >
                      <PaymentArrowPrefix cost={equipment.repairCost || {}} />
                      {t("match.panel.repair")}
                    </button>
                  )}
                  {canAct && !boardOccupant && (
                    <button
                      type="button"
                      className={styles.button}
                      onClick={() => {
                        send({
                          type: "ai:discardEquipment",
                          instanceId: equipment.instanceId,
                        });
                      }}
                    >
                      {t("match.panel.discard")}
                    </button>
                  )}
                </div>
                </div>
                {hasAbilitySlotArea ? (
                  <div className={styles.boardActionStripSlots}>
                    {columnSlots.length > 0
                      ? renderAbilitySlots(
                          slot.key,
                          ownBoard,
                          !ownBoard,
                          seat,
                          "actionBar",
                        )
                      : null}
                    {equipment.inherentAbility
                      ? renderActionBarInherentAbility(
                          equipment,
                          equipment.inherentAbility,
                          ownBoard,
                          seat,
                        )
                      : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        {renderRepeatLastRoundButton(seat, ownBoard)}
      </div>
    );
  };

  const closeKeywordTip = () => setKeywordTip(null);

  const openKeywordTip = (keyword) => {
    if (!keyword) {
      setKeywordTip(null);
      return;
    }
    setKeywordTip({
      id: keyword.id,
      label: keyword.label,
      description: keyword.description,
    });
  };

  const toggleKeywordTip = (keyword) => {
    if (!keyword) {
      setKeywordTip(null);
      return;
    }
    setKeywordTip((current) =>
      current?.id === keyword.id
        ? null
        : {
            id: keyword.id,
            label: keyword.label,
            description: keyword.description,
          },
    );
  };

  const renderKeywordTip = () => {
    if (!keywordTip) return null;
    const canUnsummon =
      Boolean(keywordTip.unsummon?.abilityInstanceId) &&
      isMyTurn &&
      !isSpectateView &&
      !isReplayView &&
      !targetingBlocksActions;
    return (
      <div
        className={styles.keywordTooltipOverlay}
        onClick={(event) => {
          event.stopPropagation();
          closeKeywordTip();
        }}
      >
        <div
          className={styles.keywordTooltip}
          role="dialog"
          aria-label={keywordTip.label}
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <div className={styles.keywordTooltipTitle}>{keywordTip.label}</div>
          <div className={styles.keywordTooltipBody}>
            {keywordTip.description}
          </div>
          {keywordTip.unsummon ? (
            <div className={styles.row}>
              <button
                type="button"
                className={`${styles.button}${actionSuccessClass(
                  `unsummonMinion:${keywordTip.unsummon.abilityInstanceId}`,
                )}`}
                disabled={!canUnsummon}
                onClick={() =>
                  requestUnsummonMinion({
                    equipmentInstanceId:
                      keywordTip.unsummon.equipmentInstanceId,
                    abilityInstanceId: keywordTip.unsummon.abilityInstanceId,
                  })
                }
              >
                {t("match.panel.unsummon")}
              </button>
              {keywordTip.cardId ? (
                <button
                  type="button"
                  className={styles.button}
                  onClick={() => {
                    closeKeywordTip();
                    openCardExam(keywordTip.cardId);
                  }}
                >
                  {t("match.panel.examine")}
                </button>
              ) : null}
              <button
                type="button"
                className={styles.button}
                onClick={closeKeywordTip}
              >
                {t("common.close")}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const renderKeywordDetailLine = (line, key = line) => (
    <div key={key} className={styles.detailLine}>
      {renderTextWithStatusEffectIcons(
        line && typeof line === "object" ? line.text : line,
        {
          outputDelta:
            line && typeof line === "object" ? line.outputDelta || 0 : 0,
          shorthand: false,
          cardMatcher: cardNameMatcher,
          onCardClick: openCardExam,
          keywordProps: {
            activeKeywordId: keywordTip?.id || null,
            onKeywordClick: toggleKeywordTip,
          },
        },
      )}
    </div>
  );

  const renderHandExamEntryValue = (entry, key = entry.key || entry.label) =>
    entry.keywordId ? (
          <KeywordBadge
            keywordId={entry.keywordId}
            label={entry.value}
            className={styles.keywordLink}
            activeKeywordId={keywordTip?.id || null}
            onKeywordClick={toggleKeywordTip}
          />
        ) : entry.rawTags ? (
          <HandExamTypeTags
            tags={entry.rawTags}
            activeKeywordId={keywordTip?.id || null}
            onKeywordClick={toggleKeywordTip}
          />
        ) : entry.abilityMode ? (
          <div className={styles.detailLineValueRow}>
            <span className={styles.detailLineExpendCost}>
              {renderExpendKeywordStar()}
              <HealthCostIcons
                amount={entry.abilityMode.healthCost}
                keywordProps={{
                  activeKeywordId: keywordTip?.id || null,
                  onKeywordClick: toggleKeywordTip,
                }}
              />
              {entry.abilityMode.cost?.length > 0 ? (
                <AttributeIconList
                  entries={entry.abilityMode.cost}
                  keywordProps={{
                    activeKeywordId: keywordTip?.id || null,
                    onKeywordClick: toggleKeywordTip,
                  }}
                />
              ) : null}
              <span className={styles.cardEffectArrow} aria-hidden="true" />
            </span>
            <span className={styles.detailLineExpendEffect}>
              {renderTextWithStatusEffectIcons(entry.abilityMode.text, {
                shorthand: false,
                outputDelta: entry.abilityMode.outputDelta,
                keywordProps: {
                  activeKeywordId: keywordTip?.id || null,
                  onKeywordClick: toggleKeywordTip,
                },
                cardMatcher: cardNameMatcher,
                onCardClick: openCardExam,
              })}
            </span>
          </div>
        ) : entry.costEntries ? (
          <AttributeIconList
            entries={entry.costEntries}
            keywordProps={{
              activeKeywordId: keywordTip?.id || null,
              onKeywordClick: toggleKeywordTip,
            }}
          />
        ) : entry.resourceChoices ? (
          <span className={styles.detailLineResourceChoices}>
            {entry.resourceChoices.map((choice, choiceIndex) => (
              <React.Fragment key={`resource-choice-${choiceIndex}`}>
                {choiceIndex > 0 ? (
                  <span className={styles.detailLineResourceChoiceSep}>/</span>
                ) : null}
                <AttributeIconList
                  entries={choice}
                  keywordProps={{
                    activeKeywordId: keywordTip?.id || null,
                    onKeywordClick: toggleKeywordTip,
                  }}
                />
              </React.Fragment>
            ))}
          </span>
        ) : (
          String(entry.value || "")
            .split("\n")
            .map((line, lineIndex) => (
              <div
                key={`${key}-line-${lineIndex}`}
                className={styles.detailLineValueRow}
              >
                {renderTextWithStatusEffectIcons(line, {
                  shorthand: false,
                  keywordProps: {
                    activeKeywordId: keywordTip?.id || null,
                    onKeywordClick: toggleKeywordTip,
                  },
                  cardMatcher: cardNameMatcher,
                  onCardClick: openCardExam,
                })}
              </div>
            ))
        );

  const renderAspectTraitExamSection = (card, { showTitle = true } = {}) => {
    if (!card || !cardHasType(card, "aspect")) return null;
    const traitLevels = listAspectTraitExamLevels(card);
    const effectText = formatAspectExamEffectText(card);
    if (!traitLevels.length && !effectText) return null;
    const allocatedTraits =
      selfSeat?.aspectId === card.id
        ? selectedTraits
        : opponentSeat?.aspectId === card.id
          ? Array.isArray(opponentSeat?.traits)
            ? opponentSeat.traits
            : []
          : [];
    const treeNodes = buildTraitTreeNodes(card, allocatedTraits, null);
    const treeEdges = buildTraitTreeEdges(treeNodes, card.id || null);
    const focusedNode =
      treeNodes.find((node) => node.id === aspectExamTraitFocusId) || null;
    const hasAllocatedTraits = allocatedTraits.some((trait) => trait?.id);
    return (
      <div className={styles.aspectExamTraits}>
        {showTitle ? (
          <div className={styles.aspectExamTraitsTitle}>{t("match.panel.traits")}</div>
        ) : null}
        <div
          className={`${styles.traitTree} ${styles.aspectExamTraitTree}${
            card.id === "as_ninjutsu" ? ` ${styles.traitTreeHub}` : ""
          }${
            hasAllocatedTraits ? ` ${styles.aspectExamTraitTreeAllocated}` : ""
          }`}
          aria-label={t("match.panel.aspectTraitTreeAria", { name: card.name || t("match.panel.aspect") })}
        >
          <svg
            className={styles.traitTreeLines}
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            {treeEdges.map((edge) => (
              <line
                key={edge.id}
                className={
                  edge.active
                    ? styles.traitTreeLineActive
                    : styles.traitTreeLine
                }
                x1={edge.from.x}
                y1={edge.from.y}
                x2={edge.to.x}
                y2={edge.to.y}
              />
            ))}
          </svg>
          {treeNodes.map((node) => {
            const isFocused = aspectExamTraitFocusId === node.id;
            const nodeClass = [
              styles.traitTreeNode,
              node.kind === "aspect" ? styles.traitTreeNodeAspect : "",
              node.selected ? styles.traitTreeNodeSelected : "",
              isFocused ? styles.traitTreeNodeFocused : "",
            ]
              .filter(Boolean)
              .join(" ");
            return (
              <button
                key={node.id}
                type="button"
                className={nodeClass}
                style={{ left: `${node.x}%`, top: `${node.y}%` }}
                aria-label={node.shortLabel || node.label}
                aria-pressed={isFocused}
                onClick={() =>
                  setAspectExamTraitFocusId((current) =>
                    current === node.id ? null : node.id,
                  )
                }
              >
                <span className={styles.traitTreeNodeCircle}>
                  {traitTreeNodeMark(node)}
                </span>
                <span className={styles.traitTreeNodeCaption}>
                  {renderTraitTreeCaptionParts(
                    node.captionParts,
                    node.shortLabel,
                  )}
                </span>
              </button>
            );
          })}
        </div>
        {focusedNode
          ? createPortal(
              <div
                className={styles.aspectExamTraitHelperOverlay}
                onClick={() => setAspectExamTraitFocusId(null)}
                role="presentation"
              >
                <div
                  className={styles.aspectExamTraitHelperPanel}
                  role="dialog"
                  aria-modal="true"
                  aria-label={
                    focusedNode.kind === "aspect"
                      ? focusedNode.label
                      : t("match.panel.levelTrait", { level: focusedNode.level })
                  }
                  onClick={(event) => event.stopPropagation()}
                >
                  <div className={styles.aspectExamTraitHelperTitle}>
                    {focusedNode.kind === "aspect"
                      ? focusedNode.label
                      : t("match.panel.levelTraitLabel", { level: focusedNode.level, label: focusedNode.label })}
                  </div>
                  <div className={styles.aspectExamTraitHelperDetail}>
                    <KeywordRichText
                      text={focusedNode.detail}
                      activeKeywordId={keywordTip?.id || null}
                      onKeywordClick={toggleKeywordTip}
                    />
                  </div>
                  <div className={styles.aspectExamTraitHelperStatus}>
                    {focusedNode.kind === "aspect"
                      ? t("match.panel.aspectBaseEffect")
                      : focusedNode.selected
                        ? t("match.panel.allocated")
                        : t("match.panel.traitOption")}
                  </div>
                  <button
                    type="button"
                    className={`${styles.button} ${styles.buttonPrimary}`}
                    onClick={() => setAspectExamTraitFocusId(null)}
                  >
                    {t("common.close")}
                  </button>
                  <div className={styles.aspectExamTraitHelperHint}>
                    {t("match.panel.clickAnywhereToClose")}
                  </div>
                </div>
              </div>,
              document.body,
            )
          : (
            <div className={styles.aspectExamTraitHint}>
              {t("match.panel.tapNodeHint")}
            </div>
          )}
      </div>
    );
  };

  const buildExamArtControls = (
    name,
    artUrl,
    frameCard = null,
    foilEffect = null,
    options = {},
  ) => {
    const borderless = isBorderlessFoil(foilEffect);
    const traditional = isTraditionalFoil(foilEffect);
    const frameUrl =
      borderless || traditional ? null : getCardFrameUrl(frameCard);
    const hybridOutline =
      !frameUrl && !borderless && !getSimpleCardFrameKey(frameCard, foilEffect)
        ? getHybridOutlineProps(
            frameCard,
            traditional ? TRADITIONAL_ATTRIBUTE_COLOR : ATTRIBUTE_COLOR_VAR,
          )
        : { className: "", style: undefined };
    const foilArtFilter = getFoilArtFilterClass(foilEffect);
    const examCardId = frameCard?.id || null;
    const fullArtUrl =
      frameCard && artUrl && getCardImageUrl(frameCard) === artUrl
        ? getCardFullImageUrl(frameCard)
        : artUrl;
    const unlockedHolos = Array.isArray(options.unlockedHolos)
      ? options.unlockedHolos
      : [];
    const onFoilChange =
      typeof options.onFoilChange === "function" ? options.onFoilChange : null;
    const buildArtPreview = (previewFoil) => {
      const previewBorderless = isBorderlessFoil(previewFoil);
      const previewTraditional = isTraditionalFoil(previewFoil);
      const previewFrameUrl = previewBorderless || previewTraditional
        ? null
        : getCardFrameUrl(frameCard);
      const previewOutline =
        !previewFrameUrl &&
        !previewBorderless &&
        !getSimpleCardFrameKey(frameCard, previewFoil)
          ? getHybridOutlineProps(
              frameCard,
              previewTraditional
                ? TRADITIONAL_ATTRIBUTE_COLOR
                : ATTRIBUTE_COLOR_VAR,
            )
          : { className: "", style: undefined };
      return {
        artUrl,
        fullArtUrl,
        frameUrl: previewFrameUrl || null,
        foilEffect: previewFoil || null,
        hybridOutline: previewOutline.className
          ? {
              className: previewOutline.className,
              style: previewOutline.style,
            }
          : null,
        cardId: examCardId,
        card: frameCard,
      };
    };
    const openArtPreview = () => {
      if (!artUrl) return;
      setExamArtPreview(buildArtPreview(foilEffect));
    };
    const art =
        artUrl && traditional && frameCard ? (
          <div className={styles.cardExamFoilPreview}>
            <CardFace
              key={foilEffect || "base"}
              card={frameCard}
              foilEffect={foilEffect}
              foilQuality={examFoilQuality}
              foilAnimateIdle={examFoilQuality === "maximum"}
              fullArt
              fitText={false}
              hideSlotIcon
              onClick={(event) => {
                event.stopPropagation();
                openArtPreview();
              }}
            />
          </div>
        ) : artUrl ? (
          <button
            type="button"
            className={`${styles.cardExamArt} ${
              frameUrl ? styles.cardExamArtWithFrame : ""
            } ${borderless ? styles.cardExamArtBorderless : ""} ${
              hybridOutline.className
            }`}
            style={hybridOutline.style}
            onClick={(event) => {
              event.stopPropagation();
              openArtPreview();
            }}
            aria-label={t("match.panel.viewArtAria", { name })}
          >
            <div
              className={[styles.cardExamArtImage, foilArtFilter]
                .filter(Boolean)
                .join(" ")}
            >
              <AiLazyImage
                alt=""
                className={styles.cardExamArtMedia}
                src={fullArtUrl}
                placeholderSrc={artUrl}
                eager
                draggable={false}
              />
            </div>
            {frameUrl ? <CardFrameOverlay frameUrl={frameUrl} /> : null}
            <FoilOverlay
              key={foilEffect || "base"}
              foilEffect={foilEffect}
              cardId={examCardId}
              quality={examFoilQuality}
              animateIdle={examFoilQuality === "maximum"}
            />
          </button>
        ) : null;
    const foilSelect =
          unlockedHolos.length > 0 && onFoilChange ? (
            <label className={styles.cardExamFoilSelect}>
              <span className={styles.muted}>{t("match.panel.variant")}</span>
              <select
                className={styles.select}
                value={foilEffect || ""}
                onChange={(event) => {
                  const nextFoil = event.target.value || null;
                  onFoilChange(nextFoil);
                  setExamArtPreview((current) =>
                    current ? buildArtPreview(nextFoil) : current,
                  );
                }}
                onClick={(event) => event.stopPropagation()}
              >
                <option value="">{t("match.panel.variantBase")}</option>
                {unlockedHolos.map((foil) => (
                  <option key={foil} value={foil}>
                    {foilEffectLabel(foil)}
                  </option>
                ))}
              </select>
            </label>
          ) : null;
    return { art, foilSelect };
  };

  const renderExamTitleRow = (
    name,
    artUrl,
    frameCard = null,
    foilEffect = null,
    options = {},
  ) => {
    const { art, foilSelect } = buildExamArtControls(
      name,
      artUrl,
      frameCard,
      foilEffect,
      options,
    );
    return (
      <div className={styles.cardExamTitleRow}>
        {art}
        <div className={styles.cardExamTitleMain}>
          <h3 className={styles.actionPopupTitle}>{name}</h3>
          {foilSelect}
        </div>
      </div>
    );
  };

  const renderExpendKeywordStar = () => (
    <KeywordBadge
      keywordId="expend"
      label={<ExpendActivateIcon className={styles.cardExpendStarIcon} />}
      className={`${styles.cardExpendStarButton}`}
      activeKeywordId={keywordTip?.id || null}
      onKeywordClick={toggleKeywordTip}
    />
  );

  const renderEquipmentDescriptionLine = (line, index) => {
    if (
      line &&
      typeof line === "object" &&
      (line.kind === "expendIcons" ||
        line.kind === "expendEffect" ||
        line.kind === "paidEffect")
    ) {
      const showExpendStar = line.kind !== "paidEffect";
      return (
        <div
          key={`${line.kind}-${index}`}
          className={`${styles.detailLine} ${styles.detailLineExpend}`}
        >
          <span className={styles.detailLineExpendCost}>
            {line.identity ? (
              <IdentityModePrefix attribute={line.identity} />
            ) : null}
            {showExpendStar ? renderExpendKeywordStar() : null}
            {line.cost?.length > 0 ? (
              <AttributeIconList entries={line.cost} />
            ) : null}
            <ChargeCostIcons amount={line.chargeCost} />
            <DurabilityCostIcons
              amount={line.durabilityCost}
              keywordProps={{
                activeKeywordId: keywordTip?.id || null,
                onKeywordClick: toggleKeywordTip,
              }}
            />
            <SelfDamageCostIcons
              entries={line.selfDamage}
              keywordProps={{
                activeKeywordId: keywordTip?.id || null,
                onKeywordClick: toggleKeywordTip,
              }}
            />
            <span className={styles.cardEffectArrow} aria-hidden="true" />
          </span>
          {line.kind === "expendIcons" ? (
            line.choices?.length ? (
              <ResourceGrantChoiceIconStrip
                choices={line.choices}
                persist={line.persist}
              />
            ) : (
              <AttributeIconList entries={line.entries} persist={line.persist} />
            )
          ) : (
            <span className={styles.detailLineExpendEffect}>
              {renderTextWithStatusEffectIcons(line.text, {
                outputDelta: line.outputDelta || 0,
                shorthand: false,
                keywordProps: {
                  activeKeywordId: keywordTip?.id || null,
                  onKeywordClick: toggleKeywordTip,
                },
              })}
            </span>
          )}
        </div>
      );
    }
    return renderKeywordDetailLine(line, typeof line === "string" ? line : index);
  };

  const renderCardExamV2Section = ({
    sectionKey,
    title,
    keywordId = null,
    hint = null,
    children,
  }) => (
    <section key={sectionKey} className={styles.examV2Section}>
      <header className={styles.examV2SectionHead}>
        {keywordId && getKeywordById(keywordId) ? (
          <KeywordBadge
            keywordId={keywordId}
            label={title}
            className={`${styles.examV2SectionTitle} ${styles.examV2SectionTitleLink}`}
            activeKeywordId={keywordTip?.id || null}
            onKeywordClick={toggleKeywordTip}
          />
        ) : (
          <h4 className={styles.examV2SectionTitle}>{title}</h4>
        )}
        {hint ? <p className={styles.examV2SectionHint}>{hint}</p> : null}
      </header>
      <div className={styles.examV2SectionBody}>{children}</div>
    </section>
  );

  const renderCardExamV2SetChip = (card) => {
    const setId = normalizeCardSet(card?.set);
    if (!setId) return null;
    const active = keywordTip?.id === getCardSetKeywordId(setId);
    return (
      <button
        type="button"
        className={`${styles.examV2SetChip}${
          active ? ` ${styles.keywordLinkActive}` : ""
        }`}
        aria-label={t("match.panel.expansionSetAria", { set: formatCardSetLabel(setId) })}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          toggleKeywordTip(getCardSetKeyword(setId));
        }}
      >
        <img
          className={styles.examV2SetIcon}
          src={getCardSetIconUrl(setId)}
          alt=""
          draggable={false}
        />
        <span className={styles.examV2SetText}>
          <span className={styles.examV2SetCaption}>{t("match.panel.set")}</span>
          <span className={styles.examV2SetLabel}>
            {formatCardSetLabel(setId)}
          </span>
        </span>
      </button>
    );
  };

  const renderCardExamV2Body = (card, options = {}) => {
    const {
      foilEffect = null,
      foilOptions = {},
      extraStats = [],
      detailEntries = null,
      descriptionLines = null,
      extraSections = [],
    } = options;
    const keywordProps = {
      activeKeywordId: keywordTip?.id || null,
      onKeywordClick: toggleKeywordTip,
    };
    const isEquipment = cardHasType(card, "equipment");
    const isAspect = cardHasType(card, "aspect");
    const isAbility = !isEquipment && !isAspect;
    const groups = groupCardExamEntries(
      detailEntries || getHandExamLabeledDetailEntries(card),
    );
    const { art, foilSelect } = buildExamArtControls(
      card.name,
      getCardImageUrl(card),
      card,
      foilEffect,
      foilOptions,
    );
    const attributes = cardAttributeList(card);
    const kindKey = isAbility ? String(card.kind || "").toLowerCase() : "";
    const tagParts = String(groups.tags?.rawTags || "")
      .split(" · ")
      .map((part) => part.trim())
      .filter(Boolean)
      .filter((part) => {
        const lower = part.toLowerCase();
        return lower !== kindKey && lower !== "aspect";
      });
    const triggerDisplays = getEquipmentTriggerDisplays(card);
    const equipmentLines = isEquipment
      ? splitEquipmentExamDescriptionLines(
          (descriptionLines || getEquipmentDescriptionLines(card)).filter(
            (line) =>
              typeof line !== "string" || !isEquipmentTriggerDetailLine(line),
          ),
        )
      : { expend: [], actions: [], passives: [], rules: [] };
    const abilityFaceText = isAbility ? formatAbilityFaceText(card) : null;
    const abilityFaceLines =
      abilityFaceText &&
      !groups.effects.length &&
      card.effect?.id !== "summonMinion"
        ? abilityFaceText.split("\n").filter((line) => line.trim())
        : [];
    const augmentMatchTags =
      kindKey === "augment" ? formatAugmentMatchTags(card.augment) : [];
    const augmentMatchText = augmentMatchTags.join(" · ");
    const augmentLinkDamageGroup =
      augmentMatchTags.includes("damage") &&
      augmentMatchTags.some((tag) => String(tag).includes(" / "));
    const augmentLinkTags = augmentLinkDamageGroup
      ? augmentMatchTags.filter((tag) => tag !== "damage")
      : augmentMatchTags;
    const effectEntries = augmentMatchText
      ? groups.effects.filter((entry) => entry.value !== augmentMatchText)
      : groups.effects;
    const renderTagKeyword = (tag, key) => (
      <KeywordBadge
        key={key}
        keywordId={
          getCardTypeKeywordId(tag) || (getKeywordById(tag) ? tag : null)
        }
        label={formatAttributeLabel(tag)}
        className={styles.keywordLink}
        {...keywordProps}
      />
    );
    const aspectEffectText = isAspect ? formatAspectExamEffectText(card) : null;
    const slotLabel = isEquipment ? formatSlotLabel(card.slot) : "";
    const boardSlotLabel = isEquipment
      ? formatSlotLabel(resolveCardBoardSlot(card))
      : "";
    const renderRule = (key, content) => (
      <div key={key} className={styles.examV2RuleItem}>
        {content}
      </div>
    );
    const renderRuleEntries = (entries, prefix) =>
      entries.map((entry, index) => {
        const key = entry.key || `${prefix}-${index}`;
        return renderRule(
          key,
          <div className={styles.detailLine}>
            {renderHandExamEntryValue(entry, key)}
          </div>,
        );
      });
    const renderRuleLines = (lines, prefix) =>
      lines.map((line, index) =>
        renderRule(`${prefix}-${index}`, renderKeywordDetailLine(line)),
      );

    const statTiles = [
      {
        key: "power",
        label: (
          <>
            <MaskIcon src={POWER_ICON_URL} className={styles.examV2StatIcon} />
            <KeywordBadge
              keywordId="power"
              label={t("match.panel.power")}
              className={styles.keywordLink}
              {...keywordProps}
            />
          </>
        ),
        value: String(getCardPower(card)),
      },
      isEquipment
        ? {
            key: "tier",
            label: t("match.panel.tier"),
            value: (
              <span className={styles.abilityExamLevelLine}>
                <EquipmentTierIcons
                  tier={getEquipmentTier(card)}
                  className={styles.abilityExamLevelIcons}
                />
                <span>{getEquipmentTier(card)}</span>
              </span>
            ),
          }
        : null,
      attributes.length
        ? {
            key: "attribute",
            label: attributes.length > 1 ? t("match.panel.attributes") : t("match.panel.attribute"),
            value: attributes.map((attribute) => (
              <span key={attribute} className={styles.examV2Attribute}>
                <AttributeIconList
                  entries={[[attribute, 1]]}
                  keywordProps={keywordProps}
                />
                <KeywordBadge
                  keywordId={attribute}
                  label={formatAttributeLabel(attribute)}
                  className={styles.keywordLink}
                  {...keywordProps}
                />
              </span>
            )),
          }
        : null,
      kindKey
        ? {
            key: "kind",
            label: t("match.panel.kind"),
            value: (
              <KeywordBadge
                keywordId={kindKey}
                label={formatAttributeLabel(kindKey)}
                className={styles.keywordLink}
                {...keywordProps}
              />
            ),
          }
        : null,
      augmentMatchTags.length
        ? {
            key: "links",
            label: t("match.panel.linksTo"),
            value: augmentLinkTags.flatMap((tag, tagIndex) => {
              const parts = String(tag).split(" / ");
              const withDamage = augmentLinkDamageGroup && parts.length > 1;
              return parts.map((part, partIndex) => (
                <React.Fragment key={`${tag}-${tagIndex}-${part}-${partIndex}`}>
                  {partIndex > 0 ? (
                    <span className={styles.examV2StatJoin}>{t("match.panel.or")}</span>
                  ) : null}
                  <span className={styles.examV2Attribute}>
                    {renderTagKeyword(part, `${part}-${partIndex}`)}
                    {withDamage
                      ? renderTagKeyword("damage", `${part}-${partIndex}-damage`)
                      : null}
                  </span>
                </React.Fragment>
              ));
            }),
            sub: t("match.panel.matchingActiveAbilities"),
          }
        : null,
      slotLabel
        ? {
            key: "slot",
            label: t("match.panel.slot"),
            value: slotLabel,
            sub:
              boardSlotLabel && boardSlotLabel !== slotLabel
                ? t("match.panel.boardSlotSub", { slot: boardSlotLabel })
                : null,
          }
        : null,
      ...groups.stats
        .filter((stat) => stat.label !== "Slot")
        .map((stat) => ({
          key: `stat-${stat.label}`,
          label: stat.label,
          value: stat.entries.map((entry, index) => (
            <React.Fragment key={`${stat.label}-${index}`}>
              {index > 0 ? (
                <span className={styles.examV2StatJoin}>+</span>
              ) : null}
              {renderHandExamEntryValue(entry, `${stat.label}-${index}`)}
            </React.Fragment>
          )),
        })),
      ...extraStats.map((stat, index) => ({ key: `extra-${index}`, ...stat })),
    ].filter(Boolean);

    const sections = [];
    if (aspectEffectText) {
      sections.push(
        renderCardExamV2Section({
          sectionKey: "aspect-effect",
          title: t("match.panel.matchEffect"),
          keywordId: "aspect",
          children: (
            <div className={`${styles.detailLine} ${styles.examV2Prose}`}>
              {String(aspectEffectText)
                .split("\n")
                .map((line, lineIndex) => (
                  <div
                    key={`aspect-effect-${lineIndex}`}
                    className={styles.detailLineValueRow}
                  >
                    {renderTextWithStatusEffectIcons(line, {
                      shorthand: false,
                      keywordProps,
                    })}
                  </div>
                ))}
            </div>
          ),
        }),
      );
    }
    if (groups.passives.length && !aspectEffectText) {
      sections.push(
        renderCardExamV2Section({
          sectionKey: "passive",
          title: t("match.panel.passive"),
          keywordId: "passive",
          children: (
            <div className={styles.examV2RuleList}>
              {renderRuleEntries(groups.passives, "passive")}
            </div>
          ),
        }),
      );
    }
    if (effectEntries.length || abilityFaceLines.length) {
      sections.push(
        renderCardExamV2Section({
          sectionKey: "effect",
          title: kindKey ? t("match.panel.kindEffect", { kind: formatAttributeLabel(kindKey) }) : t("match.panel.effect"),
          keywordId: kindKey || null,
          children: (
            <div className={styles.examV2RuleList}>
              {renderRuleEntries(effectEntries, "effect")}
              {renderRuleLines(abilityFaceLines, "face")}
            </div>
          ),
        }),
      );
    }
    if (triggerDisplays.length) {
      sections.push(
        renderCardExamV2Section({
          sectionKey: "triggers",
          title: t("match.panel.triggers"),
          keywordId: "triggers",
          children: (
            <div className={styles.examV2RuleList}>
              {triggerDisplays.map((entry) =>
                renderRule(
                  entry.key,
                  <div className={styles.examV2Trigger}>
                    <KeywordBadge
                      keywordId={entry.keywordId}
                      label={entry.examLabel}
                      className={styles.examV2TriggerLabel}
                      {...keywordProps}
                    />
                    <div className={styles.examV2TriggerText}>
                      {renderTextWithStatusEffectIcons(entry.text, {
                        shorthand: false,
                        keywordProps,
                      })}
                    </div>
                  </div>,
                ),
              )}
            </div>
          ),
        }),
      );
    }
    const renderEquipmentRuleLines = (lines, prefix) =>
      lines.map((line, index) =>
        renderRule(
          `${prefix}-${line.kind}-${index}`,
          renderEquipmentDescriptionLine(line, index),
        ),
      );
    if (equipmentLines.expend.length) {
      sections.push(
        renderCardExamV2Section({
          sectionKey: "expend",
          title: t("match.panel.expend"),
          keywordId: "expend",
          children: (
            <div className={styles.examV2RuleList}>
              {renderEquipmentRuleLines(equipmentLines.expend, "expend")}
            </div>
          ),
        }),
      );
    }
    if (equipmentLines.actions.length) {
      sections.push(
        renderCardExamV2Section({
          sectionKey: "equipment-actions",
          title: t("match.panel.equipmentActions"),
          keywordId: "equipmentActions",
          children: (
            <div className={styles.examV2RuleList}>
              {renderEquipmentRuleLines(equipmentLines.actions, "action")}
            </div>
          ),
        }),
      );
    }
    if (equipmentLines.passives.length) {
      sections.push(
        renderCardExamV2Section({
          sectionKey: "equipment-passive",
          title: t("match.panel.passive"),
          keywordId: "passive",
          children: (
            <div className={styles.examV2RuleList}>
              {renderRuleLines(equipmentLines.passives, "passive")}
            </div>
          ),
        }),
      );
    }
    if (equipmentLines.rules.length) {
      sections.push(
        renderCardExamV2Section({
          sectionKey: "card-text",
          title: t("match.panel.cardText"),
          children: (
            <div className={styles.examV2RuleList}>
              {renderRuleLines(equipmentLines.rules, "rule")}
            </div>
          ),
        }),
      );
    }
    if (groups.invokes.length) {
      sections.push(
        renderCardExamV2Section({
          sectionKey: "invoke",
          title: (
            <InvokeIcon
              label={t("match.panel.invoke")}
              className={styles.examV2SectionTitleIcon}
            />
          ),
          keywordId: "invoke",
          children: (
            <div className={styles.examV2RuleList}>
              {renderRuleEntries(groups.invokes, "invoke")}
            </div>
          ),
        }),
      );
    }
    const levelTable = cardHasType(card, "ability")
      ? renderAbilityLevelTable(card, { showLabel: false })
      : null;
    if (levelTable) {
      sections.push(
        renderCardExamV2Section({
          sectionKey: "levels",
          title: t("match.panel.levelProgression"),
          keywordId: "levelProgression",
          children: levelTable,
        }),
      );
    }
    const traitTree = renderAspectTraitExamSection(card, { showTitle: false });
    if (traitTree) {
      sections.push(
        renderCardExamV2Section({
          sectionKey: "traits",
          title: t("match.panel.traitTree"),
          keywordId: "traitTree",
          children: traitTree,
        }),
      );
    }
    sections.push(...extraSections);

    return (
      <div className={styles.examV2Layout}>
        {art || foilSelect ? (
          <aside className={styles.examV2Aside}>
            {art ? <div className={styles.examV2ArtFrame}>{art}</div> : null}
            {art ? (
              <span className={styles.examV2ArtHint}>{t("match.panel.tapArtToEnlarge")}</span>
            ) : null}
            {foilSelect}
          </aside>
        ) : null}
        <div className={styles.examV2Main}>
          <header className={styles.examV2Header}>
            <div className={styles.examV2Chips}>
              {groups.cardType ? (
                <KeywordBadge
                  keywordId={groups.cardType.keywordId}
                  label={groups.cardType.value}
                  className={`${styles.examV2Chip} ${styles.examV2ChipPrimary}`}
                  {...keywordProps}
                />
              ) : null}
              {tagParts.map((part, index) => (
                <KeywordBadge
                  key={`${part}-${index}`}
                  keywordId={getCardTypeKeywordId(part)}
                  label={formatAttributeLabel(part)}
                  className={styles.examV2Chip}
                  {...keywordProps}
                />
              ))}
            </div>
            <h3 className={styles.examV2Name}>{card.name}</h3>
            <div className={styles.examV2Stats}>
              {statTiles.map((tile) => (
                <div key={tile.key} className={styles.examV2Stat}>
                  <span className={styles.examV2StatLabel}>{tile.label}</span>
                  <span className={styles.examV2StatValue}>{tile.value}</span>
                  {tile.sub ? (
                    <span className={styles.examV2StatSub}>{tile.sub}</span>
                  ) : null}
                </div>
              ))}
            </div>
          </header>
          {sections}
        </div>
      </div>
    );
  };

  const renderEquipmentPopup = () => {
    if (!selectedEquipmentId) return null;
    const found = findBoardEquipment(selectedEquipmentId);
    if (!found) return null;
    const { equipment, own } = found;
    const equipmentSeat = own ? actingSeat : opponentSeat;
    const activeMinions = getEquipmentActiveMinions(equipment, equipmentSeat);
    const occupyingMinion = activeMinions[0] || null;
    const occupyingAura = !occupyingMinion
      ? getEquipmentOccupyingAura(equipment, equipmentSeat)
      : null;
    const boardOccupant = occupyingMinion || occupyingAura;
    const equipmentCard = getEquipmentFrameCard(equipment, catalogMap);
    const boardExamOptions = {
      adventureMonsterEffects: Boolean(displayMatch?.adventure),
      equipment,
    };
    const descriptionLines = getEquipmentDescriptionLines(
      equipment,
      boardExamOptions,
    );
    const boardExamDetailEntries = getHandExamLabeledDetailEntries(
      equipmentCard,
      boardExamOptions,
    );
    const runtimeStatEntries = [];
    (equipment.chargeCounters || []).forEach((counter) => {
      runtimeStatEntries.push({
        label: counter.label || getChargeKindLabel(counter.id),
        value: String(counter.amount || 0),
        chargeIcon: true,
      });
    });
    if (equipment.maxDurability != null) {
      const durabilityEntry = {
        label: "Durability",
        value: `${equipment.durability ?? 0}/${equipment.maxDurability}${
          equipment.broken ? t("match.panel.broken") : ""
        }`,
        keywordId: "durability",
        key: "durability",
      };
      const durabilityIndex = boardExamDetailEntries.findIndex(
        (entry) => entry.key === "durability" || entry.label === "Durability",
      );
      if (durabilityIndex >= 0) {
        boardExamDetailEntries[durabilityIndex] = durabilityEntry;
      } else {
        runtimeStatEntries.push(durabilityEntry);
      }
    }
    if (runtimeStatEntries.length) {
      const expansionSetIndex = boardExamDetailEntries.findIndex(
        (entry) => entry.label === "Expansion Set",
      );
      if (expansionSetIndex >= 0) {
        boardExamDetailEntries.splice(
          expansionSetIndex,
          0,
          ...runtimeStatEntries,
        );
      } else {
        boardExamDetailEntries.push(...runtimeStatEntries);
      }
    }
    const appliedMarks = listEquipmentAppliedMarks(equipment).filter(
      (mark) => mark.lines.length > 0,
    );
    const canApplyPendingTarget =
      Boolean(pendingAbilityTarget) &&
      isMyTurn &&
      pendingTargetEquipmentIds.has(equipment.instanceId);
    const applyAbilityName =
      pendingAbilityTarget?.abilityName || t("match.panel.ability");
    const primaryOccupantAbility = occupyingMinion
      ? findAbilityForMinion(equipment, occupyingMinion, equipmentSeat)
      : occupyingAura
        ? findAbilityForAura(equipment, occupyingAura, equipmentSeat)
        : null;
    const primaryOccupantCard =
      (boardOccupant?.cardId && catalogMap[boardOccupant.cardId]) ||
      primaryOccupantAbility ||
      null;
    const primaryOccupantFoil =
      boardOccupant?.foilEffect ||
      primaryOccupantAbility?.foilEffect ||
      null;
    const examCard = boardOccupant
      ? primaryOccupantCard || equipmentCard
      : equipmentCard;
    const examPanelProps = getCardExamV2PanelProps(examCard);
    const occupantExtraStats = occupyingMinion
      ? [
          {
            label: t("match.panel.health"),
            value: `${occupyingMinion.hp || 0}/${occupyingMinion.maxHp || 0}`,
            sub: occupyingMinion.expended ? t("match.panel.expendedThisTurn") : null,
          },
          ...(activeMinions.length > 1
            ? [{ label: t("match.panel.minions"), value: String(activeMinions.length) }]
            : []),
        ]
      : [];
    const examExtraSections = [];
    if (boardOccupant && descriptionLines.length) {
      examExtraSections.push(
        renderCardExamV2Section({
          sectionKey: "host-equipment",
          title: t("match.panel.hostEquipment", { name: equipment.name }),
          children: (
            <div className={styles.examV2RuleList}>
              {descriptionLines.map((line, index) => (
                <div key={`host-${index}`} className={styles.examV2RuleItem}>
                  {renderEquipmentDescriptionLine(line, index)}
                </div>
              ))}
            </div>
          ),
        }),
      );
    }
    if (appliedMarks.length) {
      examExtraSections.push(
        renderCardExamV2Section({
          sectionKey: "applied-effects",
          title: t("match.panel.appliedEffects"),
          children: (
            <div className={styles.examV2RuleList}>
              {appliedMarks.flatMap((mark) =>
                mark.lines.map((line, lineIndex) => (
                  <div
                    key={`${mark.key}-${lineIndex}`}
                    className={styles.examV2RuleItem}
                  >
                    <div className={styles.appliedEffectLine}>
                      <span
                        className={`${styles.equipStatusMarkIcon} ${styles.appliedEffectMarkIcon}`}
                        aria-hidden="true"
                      >
                        <MaskIcon
                          src={mark.iconUrl}
                          className={styles.equipStatusMarkIconGlyph}
                        />
                      </span>
                      {renderTextWithStatusEffectIcons(line, {
                        shorthand: false,
                      })}
                    </div>
                  </div>
                )),
              )}
            </div>
          ),
        }),
      );
    }
    const closeEquipmentExam = () => {
      closeKeywordTip();
      setSlottedAbilityExam(null);
      setSelectedEquipmentId(null);
    };

    return (
      <div
        className={`${styles.actionPopup} ${styles.matchExamOverlay}`}
        onClick={closeEquipmentExam}
      >
        <div
          className={`${styles.actionPopupPanel} ${styles.cardExamPanel} ${styles.examV2Panel} ${examPanelProps.className}`}
          style={examPanelProps.style}
          onClick={(event) => {
            event.stopPropagation();
            closeKeywordTip();
          }}
        >
          <ScrollHintSurface
            className={`${styles.cardExamBody} ${styles.examV2Body}`}
          >
            {renderCardExamV2Body(
              examCard,
              boardOccupant
                ? {
                    foilEffect: primaryOccupantFoil,
                    extraStats: occupantExtraStats,
                    extraSections: examExtraSections,
                  }
                : {
                    foilEffect: equipment.foilEffect || null,
                    detailEntries: boardExamDetailEntries,
                    descriptionLines,
                    extraSections: examExtraSections,
                  },
            )}
          </ScrollHintSurface>
          <div className={`${styles.slotDetailActions} ${styles.examV2Footer}`}>
            {renderCardExamV2SetChip(examCard)}
            <span className={styles.examV2FooterSpacer} />
            {canApplyPendingTarget ? (
              <button
                type="button"
                className={`${styles.button} ${styles.buttonPrimary}`}
                onClick={() => {
                  closeKeywordTip();
                  send({
                    type: "ai:resolveAbilityTarget",
                    target: {
                      equipmentInstanceId: equipment.instanceId,
                    },
                  });
                  setSelectedEquipmentId(null);
                }}
              >
                {t("match.panel.applyAbility", { name: applyAbilityName })}
              </button>
            ) : null}
            {canEmpowerTraitEquipment(equipment.instanceId) ? (
              <button
                type="button"
                className={`${styles.button} ${styles.buttonPrimary} ${styles.actionReady}`}
                onClick={() => {
                  closeEquipmentExam();
                  chooseTraitEquipment(equipment.instanceId);
                }}
              >
                {t("match.board.empowerEquipment")}
              </button>
            ) : null}
            <button
              type="button"
              className={styles.button}
              onClick={closeEquipmentExam}
            >
              {t("common.close")}
            </button>
          </div>
        </div>
      </div>
    );
  };


  const renderSlottedAbilityExam = () => {
    if (!slottedAbilityExam) return null;
    const found = findBoardAbility(
      slottedAbilityExam.abilityInstanceId,
      slottedAbilityExam.boardSlot || null,
    );
    if (!found) return null;
    const {
      ability,
      seat: equipmentSeat,
      slots,
      slotIndex: abilitySlotIndex,
      equipment,
      rack,
    } = found;
    if (!ability) return null;
    const minionActive =
      Boolean(ability.minion) && (ability.minion.hp || 0) > 0;
    const auraActive = Boolean(ability.aura);
    const abilityUsed = slottedAbilityLooksUsed(
      slots,
      abilitySlotIndex,
      ability,
      equipment || rack,
      equipmentSeat,
    );
    const catalogCard =
      catalogMap[ability.cardId || slottedAbilityExam.cardId] || null;
    const examCard = catalogCard || ability;
    const examPanelProps = getCardExamV2PanelProps(examCard);
    const displayLevel = getAbilityDisplayLevel(ability);
    const baseLevel = ability.level || 1;
    const statusParts = [
      abilityUsed ? t("match.panel.used") : null,
      minionActive
        ? t("match.panel.activeMinionStatus", { name: ability.minion.name, hp: ability.minion.hp })
        : null,
      auraActive ? t("match.panel.activeStatus", { name: ability.aura.name || ability.name }) : null,
      ability.kind === "augment" && ability.augmentStatus === "mismatch"
        ? t("match.panel.mismatch")
        : null,
    ].filter(Boolean);
    const abilityExtraStats = [
      {
        label: t("match.panel.level"),
        value: (
          <span className={styles.abilityExamLevelLine}>
            <AbilityLevelIcons
              level={displayLevel}
              className={styles.abilityExamLevelIcons}
            />
            <span>{displayLevel}</span>
          </span>
        ),
        sub: displayLevel !== baseLevel ? t("match.panel.baseLevel", { level: baseLevel }) : null,
      },
      ...(statusParts.length
        ? [{ label: t("match.panel.status"), value: statusParts.join(" · ") }]
        : []),
    ];
    const closeAbilityExam = () => {
      closeKeywordTip();
      setSlottedAbilityExam(null);
    };
    return (
      <div
        className={`${styles.actionPopup} ${styles.matchExamOverlay}`}
        onClick={closeAbilityExam}
      >
        <div
          className={`${styles.actionPopupPanel} ${styles.cardExamPanel} ${styles.examV2Panel} ${examPanelProps.className}`}
          style={examPanelProps.style}
          onClick={(event) => {
            event.stopPropagation();
            closeKeywordTip();
          }}
        >
          <ScrollHintSurface
            className={`${styles.cardExamBody} ${styles.examV2Body}`}
          >
            {renderCardExamV2Body(examCard, {
              foilEffect: ability.foilEffect || null,
              extraStats: abilityExtraStats,
            })}
          </ScrollHintSurface>
          <div className={`${styles.slotDetailActions} ${styles.examV2Footer}`}>
            {renderCardExamV2SetChip(examCard)}
            <span className={styles.examV2FooterSpacer} />
            {canLevelUpTraitAbility(ability) ? (
              <button
                type="button"
                className={`${styles.button} ${styles.buttonPrimary} ${styles.actionReady}`}
                onClick={() => {
                  closeAbilityExam();
                  chooseTraitAbilityLevel(ability);
                }}
              >
                {t("match.panel.levelUpAbility")}
                {renderAbilityLevelUpHint(ability)}
              </button>
            ) : null}
            {canEmpowerTraitEquipment(ability.instanceId) ? (
              <button
                type="button"
                className={`${styles.button} ${styles.buttonPrimary} ${styles.actionReady}`}
                onClick={() => {
                  closeAbilityExam();
                  chooseTraitEquipment(ability.instanceId);
                }}
              >
                {t("match.board.empowerAbility")}
                {renderAbilityLevelUpHint(ability)}
              </button>
            ) : null}
            <button
              type="button"
              className={styles.button}
              onClick={closeAbilityExam}
            >
              {t("common.close")}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCombatLog = () => {
    if (!combatLogOpen) return null;
    const sourceLog =
      displayMatch?.combatLog || matchSummary?.combatLog || [];
    const entries = [...sourceLog].reverse();
    const activeLogId = isReplayView ? replayDisplay?.entry?.id : null;
    const canExportGameLog =
      !isReplayView && Boolean(displayMatch || matchSummary);
    return (
      <div
        className={styles.actionPopup}
        onClick={() => setCombatLogOpen(false)}
      >
        <div
          className={styles.combatLogPanel}
          onClick={(event) => event.stopPropagation()}
        >
          <div className={styles.combatLogHeader}>
            <h3 className={styles.actionPopupTitle}>{t("hud.gameLog")}</h3>
            <div className={styles.combatLogHeaderActions}>
              {canExportGameLog ? (
                <button
                  type="button"
                  className={styles.button}
                  disabled={gameLogExportBusy}
                  title={t("match.panel.exportGameLogTitle")}
                  onClick={() => {
                    setGameLogExportBusy(true);
                    send({ type: "ai:exportGameLog" });
                    setTimeout(() => setGameLogExportBusy(false), 8000);
                  }}
                >
                  {gameLogExportBusy ? t("match.panel.exporting") : t("match.panel.export")}
                </button>
              ) : null}
              <button
                type="button"
                className={styles.button}
                onClick={() => setCombatLogOpen(false)}
              >
                {t("common.close")}
              </button>
            </div>
          </div>
          <ScrollHintSurface
            ref={combatLogListRef}
            className={styles.combatLogList}
            onScroll={() => {
              const list = combatLogListRef.current;
              if (!list) return;
              combatLogStickTopRef.current = list.scrollTop <= 8;
            }}
          >
            {entries.length === 0 ? (
              <div className={styles.combatLogEmpty}>{t("match.panel.noActionsYet")}</div>
            ) : (
              entries.map((entry) => (
                <div
                  key={entry.id}
                  className={`${
                    entry.kind === "round"
                      ? styles.combatLogRound
                      : entry.kind === "turn"
                        ? styles.combatLogTurn
                        : styles.combatLogAction
                  } ${
                    activeLogId && entry.id === activeLogId
                      ? styles.adminLogActive
                      : ""
                  }`}
                >
                  <CombatLogRichText
                    text={formatCombatLogText(entry.text)}
                    matcher={cardNameMatcher}
                    onCardClick={(cardId) => openCardExam(cardId)}
                  />
                </div>
              ))
            )}
          </ScrollHintSurface>
        </div>
      </div>
    );
  };

  const copyPatchNotesPart = async (entry) => {
    const parts = splitPatchNotesForDiscord(formatPatchNotesMarkdown(entry));
    const index = Math.min(patchNotesCopyPart[entry.version] || 0, parts.length - 1);
    try {
      await navigator.clipboard.writeText(parts[index]);
    } catch {
      showToast("Couldn't copy to the clipboard. Use Export .md instead.");
      return;
    }
    const nextIndex = index + 1 < parts.length ? index + 1 : 0;
    setPatchNotesCopyPart((current) => ({
      ...current,
      [entry.version]: nextIndex,
    }));
    showToast(
      parts.length > 1
        ? `Copied v${entry.version} part ${index + 1} of ${parts.length}${
            nextIndex ? "" : " (all parts copied)"
          }`
        : `Copied v${entry.version} patch notes`,
    );
  };

  const exportPatchNotes = (entry) => {
    downloadTextFile(
      `aeterna-invicta-patch-notes-v${entry.version}.md`,
      `${formatPatchNotesMarkdown(entry)}\n`,
    );
  };

  const renderPatchNotesAdminActions = (entry) => {
    if (!isAdmin) return null;
    const parts = splitPatchNotesForDiscord(formatPatchNotesMarkdown(entry));
    const nextPart = patchNotesCopyPart[entry.version] || 0;
    return (
      <div className={styles.patchNotesAdminActions}>
        <button
          type="button"
          className={styles.button}
          onClick={() => copyPatchNotesPart(entry)}
          title="Copy as Discord Markdown. Notes over 2,000 characters copy in parts."
        >
          {parts.length > 1
            ? `Copy for Discord (part ${nextPart + 1}/${parts.length})`
            : "Copy for Discord"}
        </button>
        <button
          type="button"
          className={styles.button}
          onClick={() => exportPatchNotes(entry)}
        >
          Export .md
        </button>
      </div>
    );
  };

  const renderPatchNotes = () => {
    if (!patchNotesOpen) return null;
    return (
      <div
        className={`${styles.actionPopup} ${styles.patchNotesOverlay}`}
        onClick={() => setPatchNotesOpen(false)}
      >
        <div
          className={styles.patchNotesPanel}
          onClick={(event) => event.stopPropagation()}
        >
          <div className={styles.patchNotesHeader}>
            <h3 className={styles.actionPopupTitle}>{t("patchNotes.title")}</h3>
            <button
              type="button"
              className={styles.button}
              onClick={() => setPatchNotesOpen(false)}
            >
              {t("common.close")}
            </button>
          </div>
          <ScrollHintSurface className={styles.patchNotesBody}>
            {PATCH_NOTES.map((entry) => (
              <section
                key={entry.version}
                className={styles.patchNotesVersion}
              >
                <h4 className={styles.patchNotesVersionTitle}>
                  v{entry.version}
                </h4>
                {renderPatchNotesAdminActions(entry)}
                {entry.sections.map((section) => (
                  <div
                    key={`${entry.version}-${section.title}`}
                    className={styles.patchNotesSection}
                  >
                    <h5 className={styles.patchNotesSectionTitle}>
                      {section.title}
                    </h5>
                    <ul className={styles.patchNotesList}>
                      {section.items.map((item) => (
                        <li key={item} className={styles.patchNotesItem}>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </section>
            ))}
          </ScrollHintSurface>
        </div>
      </div>
    );
  };

  const renderXpQuestsPopup = () => {
    if (!xpQuestsOpen) return null;
    const quests = selfSeat?.xpQuests || null;
    const turnDone = quests?.turn || {};
    const matchDone = quests?.match || {};
    return (
      <div
        className={`${styles.actionPopup} ${styles.xpQuestsOverlay}`}
        style={
          xpQuestsBottomInset > 0
            ? { paddingBottom: `${xpQuestsBottomInset + 8}px` }
            : undefined
        }
        onClick={() => setXpQuestsOpen(false)}
      >
        <div
          className={styles.xpQuestsPanel}
          onClick={(event) => event.stopPropagation()}
        >
          <div className={styles.xpQuestsHeader}>
            <h3 className={styles.actionPopupTitle}>{t("match.panel.quests")}</h3>
            <button
              type="button"
              className={styles.button}
              onClick={() => setXpQuestsOpen(false)}
            >
              {t("common.close")}
            </button>
          </div>
          <div className={styles.xpQuestsBody}>
            <section className={styles.xpQuestsSection}>
              <h4 className={styles.xpQuestsSectionTitle}>{t("match.panel.eachTurn")}</h4>
              <p className={styles.xpQuestsSectionHint}>
                {t("match.panel.eachTurnHint")}
              </p>
              <ul className={styles.xpQuestsList}>
                {XP_TURN_QUESTS.map((quest) => {
                  const done = Boolean(turnDone[quest.id]);
                  return (
                    <li key={quest.id} className={styles.xpQuestsItem}>
                      <span
                        className={`${styles.xpQuestsCheck}${
                          done ? ` ${styles.xpQuestsCheckDone}` : ""
                        }`}
                        aria-hidden="true"
                      >
                        {done ? "✓" : ""}
                      </span>
                      <span className={styles.xpQuestsLabel}>
                        {t(`match.quest.${quest.id}`)}
                      </span>
                      <span className={styles.xpQuestsXp}>+{quest.xp} XP</span>
                    </li>
                  );
                })}
              </ul>
            </section>
            <section className={styles.xpQuestsSection}>
              <h4 className={styles.xpQuestsSectionTitle}>
                {t("match.panel.duringGame")}
              </h4>
              <p className={styles.xpQuestsSectionHint}>
                {t("match.panel.duringGameHint")}
              </p>
              <ul className={styles.xpQuestsList}>
                {XP_MATCH_QUESTS.map((quest) => {
                  const done = Boolean(matchDone[quest.id]);
                  const stepProgress =
                    quest.stepsKey && quests?.match?.[quest.stepsKey]
                      ? quests.match[quest.stepsKey]
                      : null;
                  return (
                    <li
                      key={quest.id}
                      className={`${styles.xpQuestsItem}${
                        quest.steps?.length ? ` ${styles.xpQuestsItemWithSteps}` : ""
                      }`}
                    >
                      <span
                        className={`${styles.xpQuestsCheck}${
                          done ? ` ${styles.xpQuestsCheckDone}` : ""
                        }`}
                        aria-hidden="true"
                      >
                        {done ? "✓" : ""}
                      </span>
                      <span className={styles.xpQuestsLabel}>
                        {t(`match.quest.${quest.id}`)}
                      </span>
                      <span className={styles.xpQuestsXp}>+{quest.xp} XP</span>
                      {quest.steps?.length ? (
                        <ul className={styles.xpQuestsSubList}>
                          {quest.steps.map((step) => {
                            const stepDone = Boolean(stepProgress?.[step.id]);
                            return (
                              <li
                                key={step.id}
                                className={styles.xpQuestsSubItem}
                              >
                                <span
                                  className={`${styles.xpQuestsCheck} ${styles.xpQuestsSubCheck}${
                                    stepDone
                                      ? ` ${styles.xpQuestsCheckDone}`
                                      : ""
                                  }`}
                                  aria-hidden="true"
                                >
                                  {stepDone ? "✓" : ""}
                                </span>
                                <span className={styles.xpQuestsSubLabel}>
                                  {t(`match.questStep.${step.id}`)}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </section>
          </div>
        </div>
      </div>
    );
  };

  const renderAttributeChoicePopup = () => {
    if (!isMyTurn || !pendingAttributeChoice || isReplayView) return null;
    const attributes = pendingAbilityTarget.validAttributes || [];
    const sourceName = pendingAbilityTarget.abilityName || t("match.panel.effectLower");
    const pendingEffect = pendingAbilityTarget.effect;
    const choicePrompt =
      pendingEffect?.id === "grantSlottedAttributeResource"
        ? t("match.panel.chooseSlottedResource")
        : t("match.panel.convertNeutral");
    return (
      <div
        className={styles.actionPopup}
        onClick={(event) => event.stopPropagation()}
      >
        <ScrollHintSurface className={styles.actionPopupPanel}>
          <h3 className={styles.actionPopupTitle}>{sourceName}</h3>
          <div className={styles.detailBlock}>
            <div className={styles.detailLine}>{choicePrompt}</div>
          </div>
          <div className={styles.row}>
            {attributes.map((attribute) => (
              <button
                key={attribute}
                type="button"
                className={styles.button}
                onClick={() => {
                  send({
                    type: "ai:resolveAbilityTarget",
                    target: { attribute },
                  });
                }}
              >
                {t("match.panel.gain")}
                <AttributeIcons attribute={attribute} count={1} />
              </button>
            ))}
          </div>
          <div className={styles.row}>
            <button
              type="button"
              className={styles.button}
              onClick={() => send({ type: "ai:cancelAbilityTarget" })}
            >
              {t("common.cancel")}
            </button>
          </div>
        </ScrollHintSurface>
      </div>
    );
  };

  const renderAspectResourceChoicePopup = () => {
    if (!isMyTurn || !pendingAspectResourceChoice || isReplayView) return null;
    if (pendingTraitChoice || pendingTraitAbilityLevel || pendingTraitEquipmentChoice || pendingIdentityChoice)
      return null;
    const attributes = pendingAspectResourceChoice.attributes || [];
    return (
      <div
        className={styles.actionPopup}
        onClick={(event) => event.stopPropagation()}
      >
        <ScrollHintSurface className={styles.actionPopupPanel}>
          <h3 className={styles.actionPopupTitle}>Path of Focus</h3>
          <div className={styles.detailBlock}>
            <div className={styles.detailLine}>
              {t("match.panel.chooseFocusResourcePrompt")}
            </div>
          </div>
          <div className={styles.row}>
            {attributes.map((attribute) => (
              <button
                key={attribute}
                type="button"
                className={styles.button}
                onClick={() => {
                  send({
                    type: "ai:resolveAspectChoice",
                    attribute,
                  });
                }}
              >
                {t("match.panel.gain")}
                <AttributeIcons attribute={attribute} count={1} />
              </button>
            ))}
          </div>
        </ScrollHintSurface>
      </div>
    );
  };

  const renderIdentityChoicePopup = () => {
    if (!isMyTurn || !pendingIdentityChoice || isReplayView) return null;
    if (pendingTraitChoice || pendingTraitAbilityLevel || pendingTraitEquipmentChoice) return null;
    if (identityChoiceBoardPeek) return null;
    const attributes = pendingIdentityChoice.attributes || [];
    const equipment =
      findBoardEquipment(pendingIdentityChoice.equipmentInstanceId)
        ?.equipment || null;
    const title = equipment?.name || t("match.panel.chooseIdentity");
    return (
      <KeywordTipContext.Provider value={null}>
        <div
          className={styles.actionPopup}
          onClick={(event) => event.stopPropagation()}
        >
          <ScrollHintSurface className={styles.actionPopupPanel}>
            <h3 className={styles.actionPopupTitle}>{title}</h3>
            <div className={styles.detailBlock}>
              <div className={styles.detailLine}>
                {t("match.panel.chooseIdentityPrompt")}
              </div>
            </div>
            <div className={styles.row}>
              {attributes.map((attribute) => (
                <button
                  key={attribute}
                  type="button"
                  className={styles.button}
                  onClick={() => {
                    send({
                      type: "ai:resolveIdentityChoice",
                      attribute,
                    });
                  }}
                >
                  <AttributeIcons attribute={attribute} count={1} />
                </button>
              ))}
            </div>
            <div className={styles.traitChoiceActions}>
              <button
                type="button"
                className={styles.button}
                onClick={() => setIdentityChoiceBoardPeek(true)}
              >
                {t("match.panel.viewBoard")}
              </button>
            </div>
          </ScrollHintSurface>
        </div>
      </KeywordTipContext.Provider>
    );
  };

  const renderLevelUpOverlay = () => {
    if (!levelUpOverlay || isReplayView) return null;
    const level = levelUpOverlay.level || 0;
    return (
      <div
        className={styles.levelUpOverlay}
        onClick={(event) => event.stopPropagation()}
      >
        <div
          key={levelUpOverlay.key}
          className={styles.levelUpBanner}
          aria-live="assertive"
        >
          <div className={styles.levelUpTitle}>{t("match.panel.levelUp")}</div>
          {level > 0 ? (
            <div className={styles.levelUpSubtitle}>{t("match.panel.levelN", { level })}</div>
          ) : null}
        </div>
      </div>
    );
  };

  const renderTraitChoicePopup = () => {
    if (!isMyTurn || !pendingTraitChoice || isReplayView) return null;
    if (pendingTraitAbilityLevel) return null;
    if (pendingTraitEquipmentChoice) return null;
    if (!traitChoiceModalReady || traitChoiceBoardPeek || levelUpOverlay) {
      return null;
    }
    const level = pendingTraitChoice.level || 0;
    const aspectCard = catalogMap[selfSeat?.aspectId] || null;
    const treeNodes = buildTraitTreeNodes(
      aspectCard,
      selectedTraits,
      pendingTraitChoice,
    );
    const treeEdges = buildTraitTreeEdges(treeNodes, aspectCard?.id || null);
    const focusedNode =
      treeNodes.find((node) => node.id === traitTreeFocusId) || null;
    const canConfirmFocused = Boolean(focusedNode?.available);
    let focusedStatus = null;
    if (focusedNode) {
      if (canConfirmFocused) focusedStatus = null;
      else if (focusedNode.selected) focusedStatus = t("match.panel.selected");
      else if (focusedNode.kind === "aspect") focusedStatus = t("match.panel.aspectBaseEffect");
      else if (focusedNode.level < level) focusedStatus = t("match.panel.notChosen");
      else focusedStatus = t("match.panel.futureChoice");
    }
    return (
      <div
        className={styles.actionPopup}
        onClick={(event) => event.stopPropagation()}
      >
        <ScrollHintSurface
          className={`${styles.actionPopupPanel} ${styles.traitChoicePanel}`}
        >
          <h3 className={styles.actionPopupTitle}>
            {t("match.panel.levelTrait", { level })}
          </h3>
          <div className={styles.detailBlock}>
            <div className={styles.traitChoiceOptionDetail}>
              {t("match.panel.traitChoiceHint")}
            </div>
          </div>
          <div
            className={`${styles.traitTree}${
              aspectCard?.id === "as_ninjutsu" ? ` ${styles.traitTreeHub}` : ""
            }`}
            aria-label={t("match.panel.traitTreeAria")}
          >
            <svg
              className={styles.traitTreeLines}
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              {treeEdges.map((edge) => (
                <line
                  key={edge.id}
                  className={
                    edge.active
                      ? styles.traitTreeLineActive
                      : styles.traitTreeLine
                  }
                  x1={edge.from.x}
                  y1={edge.from.y}
                  x2={edge.to.x}
                  y2={edge.to.y}
                />
              ))}
            </svg>
            {treeNodes.map((node) => {
              const isFocused = traitTreeFocusId === node.id;
              const nodeClass = [
                styles.traitTreeNode,
                node.kind === "aspect" ? styles.traitTreeNodeAspect : "",
                node.selected ? styles.traitTreeNodeSelected : "",
                node.available ? styles.traitTreeNodeAvailable : "",
                isFocused ? styles.traitTreeNodeFocused : "",
              ]
                .filter(Boolean)
                .join(" ");
              return (
                <button
                  key={node.id}
                  type="button"
                  className={nodeClass}
                  style={{ left: `${node.x}%`, top: `${node.y}%` }}
                  aria-label={node.shortLabel || node.label}
                  aria-pressed={isFocused}
                  onClick={() =>
                    setTraitTreeFocusId((current) =>
                      current === node.id ? null : node.id,
                    )
                  }
                >
                  <span className={styles.traitTreeNodeCircle}>
                    {traitTreeNodeMark(node)}
                  </span>
                  <span className={styles.traitTreeNodeCaption}>
                    {renderTraitTreeCaptionParts(
                      node.captionParts,
                      node.shortLabel,
                    )}
                  </span>
                </button>
              );
            })}
          </div>
          <div className={styles.traitChoiceActions}>
            <button
              type="button"
              className={styles.button}
              onClick={() => setTraitChoiceBoardPeek(true)}
            >
              {t("match.panel.viewBoard")}
            </button>
          </div>
        </ScrollHintSurface>
        {focusedNode ? (
          <div
            className={styles.traitTreeHelperOverlay}
            onClick={() => setTraitTreeFocusId(null)}
            role="presentation"
          >
            <div
              className={styles.traitTreeHelperPanel}
              role="dialog"
              aria-modal="true"
              aria-label={
                focusedNode.kind === "aspect"
                  ? focusedNode.label
                  : t("match.panel.levelTrait", { level: focusedNode.level })
              }
              onClick={(event) => event.stopPropagation()}
            >
              <div className={styles.traitChoiceOptionLabel}>
                {focusedNode.kind === "aspect"
                  ? focusedNode.label
                  : t("match.panel.levelTraitLabel", { level: focusedNode.level, label: focusedNode.label })}
              </div>
              <div className={styles.traitChoiceOptionDetail}>
                <KeywordRichText
                  text={focusedNode.detail}
                  activeKeywordId={keywordTip?.id || null}
                  onKeywordClick={toggleKeywordTip}
                />
              </div>
              {canConfirmFocused ? (
                <button
                  type="button"
                  className={`${styles.button} ${styles.buttonPrimary} ${styles.traitTreeConfirm}`}
                  onClick={() => {
                    send({
                      type: "ai:resolveTraitChoice",
                      traitId: focusedNode.id,
                    });
                    setTraitTreeFocusId(null);
                  }}
                >
                  {t("match.panel.confirmTrait")}
                </button>
              ) : focusedStatus ? (
                <div className={styles.traitTreeDetailStatus}>{focusedStatus}</div>
              ) : null}
              <div className={styles.traitTreeHelperHint}>
                {t("match.panel.clickOutsideToClose")}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const renderTraitAbilityLevelPopup = () => {
    if (!isMyTurn || !pendingTraitAbilityLevel || isReplayView) return null;
    if (levelUpOverlay) return null;
    const aspectCard = catalogMap[selfSeat?.aspectId] || null;
    const aspectAttribute = getAspectTraitAttribute(
      selfSeat?.aspectId,
      aspectCard?.attributes || [],
    );
    const attributeLabel = ASPECT_TRAIT_ATTR_LABEL[aspectAttribute]
      ? t(`common.attribute.${aspectAttribute}`)
      : aspectTraitAttributeLabel(aspectAttribute);
    const collapsed = traitLevelPromptCollapsed;
    return (
      <div
        className={`${styles.traitAbilityLevelBanner} ${styles.traitAbilityLevelBannerCollapsible}`}
        role="status"
        aria-live="polite"
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.traitAbilityLevelBannerHeader}>
          <div className={styles.traitAbilityLevelBannerTitle}>
            {t("match.panel.levelUpAbility")}
          </div>
          <button
            type="button"
            className={styles.traitAbilityLevelBannerToggle}
            aria-expanded={!collapsed}
            aria-label={collapsed ? t("match.panel.showLevelUpPrompt") : t("match.panel.hideLevelUpPrompt")}
            title={collapsed ? t("match.panel.showLevelUpPrompt") : t("match.panel.hideLevelUpPrompt")}
            onClick={() => setTraitLevelPromptCollapsed((value) => !value)}
          >
            {collapsed ? "+" : "−"}
          </button>
        </div>
        {!collapsed ? (
          <div className={styles.traitAbilityLevelBannerDetail}>
            {t("match.panel.levelUpAbilityPrompt", { attribute: attributeLabel })}
          </div>
        ) : null}
      </div>
    );
  };

  const renderTraitEquipmentChoicePopup = () => {
    if (!isMyTurn || !pendingTraitEquipmentChoice || isReplayView) return null;
    if (levelUpOverlay) return null;
    const isPhysicalArmorEmpower =
      pendingTraitEquipmentChoice.kind === "physicalArmorEmpower";
    return (
      <div
        className={styles.traitAbilityLevelBanner}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.traitAbilityLevelBannerTitle}>
          {isPhysicalArmorEmpower
            ? t("match.panel.empowerPhysicalArmorCard")
            : t("match.panel.empowerEquipment")}
        </div>
        <div className={styles.traitAbilityLevelBannerDetail}>
          {isPhysicalArmorEmpower
            ? t("match.panel.empowerPhysicalArmorHint")
            : t("match.panel.empowerEquipmentHint")}
        </div>
      </div>
    );
  };

  const renderChargeResourceChoicePopup = () => {
    if (!isMyTurn || !pendingChargeResourceChoice || isReplayView) return null;
    const attributes = pendingAbilityTarget.validAttributes || [];
    const maxResources = Math.max(0, pendingAbilityTarget.maxResources || 0);
    const chargesPerResource = Math.max(
      1,
      pendingAbilityTarget.chargesPerResource || 3,
    );
    const chargeLabel = pendingAbilityTarget.chargeLabel || t("match.panel.charges");
    const currentCharges = Math.max(
      0,
      pendingAbilityTarget.currentCharges || 0,
    );
    const sourceName = pendingAbilityTarget.abilityName || t("match.panel.effectLower");
    const allocated = attributes.reduce(
      (sum, attribute) => sum + (chargeResourceDraft[attribute] || 0),
      0,
    );
    const remaining = Math.max(0, maxResources - allocated);
    const canConfirm = allocated >= 1 && allocated <= maxResources;
    return (
      <div
        className={styles.actionPopup}
        onClick={(event) => event.stopPropagation()}
      >
        <ScrollHintSurface className={styles.actionPopupPanel}>
          <h3 className={styles.actionPopupTitle}>{sourceName}</h3>
          <div className={styles.detailBlock}>
            <div className={styles.detailLine}>
              {t("match.panel.chargeResourceSpend", { label: chargeLabel, current: currentCharges, per: chargesPerResource })}
            </div>
            <div className={styles.detailLine}>
              {t("match.panel.allocateUpTo", { max: maxResources, remaining })}
            </div>
          </div>
          {attributes.map((attribute) => {
            const value = chargeResourceDraft[attribute] || 0;
            const fieldMax = value + remaining;
            return (
              <div key={attribute} className={styles.paymentRow}>
                <AttributeIcons attribute={attribute} count={1} />
                <span className={styles.muted}>{t("match.panel.maxValue", { max: fieldMax })}</span>
                <div className={styles.paymentStepper}>
                  <button
                    type="button"
                    className={styles.button}
                    disabled={value <= 0}
                    onClick={() =>
                      setChargeResourceDraft((current) => ({
                        ...current,
                        [attribute]: Math.max(0, (current[attribute] || 0) - 1),
                      }))
                    }
                  >
                    -
                  </button>
                  <span>{value}</span>
                  <button
                    type="button"
                    className={styles.button}
                    disabled={value >= fieldMax}
                    onClick={() =>
                      setChargeResourceDraft((current) => ({
                        ...current,
                        [attribute]: Math.min(
                          fieldMax,
                          (current[attribute] || 0) + 1,
                        ),
                      }))
                    }
                  >
                    +
                  </button>
                </div>
              </div>
            );
          })}
          <div className={styles.row}>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary}`}
              disabled={!canConfirm}
              onClick={() => {
                const resources = {};
                attributes.forEach((attribute) => {
                  const amount = chargeResourceDraft[attribute] || 0;
                  if (amount > 0) resources[attribute] = amount;
                });
                send({
                  type: "ai:resolveAbilityTarget",
                  target: { resources },
                });
              }}
            >
              {t("match.panel.confirm")}
            </button>
            <button
              type="button"
              className={styles.button}
              onClick={() => send({ type: "ai:cancelAbilityTarget" })}
            >
              {t("common.cancel")}
            </button>
          </div>
        </ScrollHintSurface>
      </div>
    );
  };

  const renderPaymentPrompt = () => {
    if (!paymentPrompt) return null;
    const fixed = paymentPrompt.fixed || {};
    const weights = paymentPrompt.weights || null;
    const fillOrder =
      paymentPrompt.fillOrder || RECOMMENDED_FLEX_PAYMENT_ORDER;
    const pool = poolFromAvailable(paymentPrompt.available, fixed);
    const mins = paymentMinimums(fixed);
    const needed = fullPaymentNeeded(fixed, paymentPrompt.flex, weights);
    const paid = paymentTotal(paymentDraft, weights);
    const remaining = needed - paid;
    const canConfirm = remaining === 0;
    const remainingDisplay = weights
      ? remaining / 2
      : remaining;

    return (
      <div
        className={`${styles.actionPopup} ${styles.paymentPromptOverlay}`}
        onClick={() => setPaymentPrompt(null)}
      >
        <ScrollHintSurface
          className={styles.actionPopupPanel}
          onClick={(event) => event.stopPropagation()}
        >
          <h3 className={styles.actionPopupTitle}>
            {t("match.panel.payFor", { name: paymentPrompt.name })}
          </h3>
          <div className={styles.detailBlock}>
            <div className={styles.detailLine}>
              {t("match.panel.spendAnyFlex", { amount: paymentPrompt.flex })}
            </div>
            <div className={styles.detailLine}>
              {t("match.panel.remaining", { amount: Math.max(0, remainingDisplay) })}
            </div>
            {FLEX_PAYMENT_ATTRIBUTES.map((attribute) => {
              const maxPool = pool[attribute] || 0;
              const minValue = mins[attribute] || 0;
              const weight = attributePaymentWeight(attribute, weights);
              if ((maxPool <= 0 && minValue <= 0) || weight <= 0) return null;
              const others = FLEX_PAYMENT_ATTRIBUTES.filter(
                (entry) => entry !== attribute,
              );
              const otherMinWeighted = others.reduce(
                (sum, entry) =>
                  sum +
                  (mins[entry] || 0) *
                    attributePaymentWeight(entry, weights),
                0,
              );
              const maxByNeed = Math.floor(
                Math.max(0, needed - otherMinWeighted) / weight,
              );
              const fieldMax = Math.min(
                maxPool,
                Math.max(minValue, maxByNeed),
              );
              const value = paymentDraft[attribute] || 0;
              const setValue = (next) => {
                setPaymentDraft(
                  redistributeFullPayment(
                    attribute,
                    next,
                    paymentPrompt.available,
                    paymentPrompt.flex,
                    fixed,
                    weights,
                    fillOrder,
                  ),
                );
              };
              return (
                <div key={attribute} className={styles.paymentRow}>
                  <AttributeIcons attribute={attribute} count={1} />
                  <span className={styles.muted}>{t("match.panel.maxValue", { max: fieldMax })}</span>
                  <div className={styles.paymentStepper}>
                    <button
                      type="button"
                      className={styles.paymentStepButton}
                      disabled={value <= minValue}
                      aria-label={t("match.panel.decreaseAria", { attribute })}
                      onClick={() => setValue(value - 1)}
                    >
                      -
                    </button>
                    <input
                      type="number"
                      min={minValue}
                      max={fieldMax}
                      value={value}
                      onChange={(event) => {
                        setValue(Number(event.target.value) || 0);
                      }}
                    />
                    <button
                      type="button"
                      className={styles.paymentStepButton}
                      disabled={value >= fieldMax}
                      aria-label={t("match.panel.increaseAria", { attribute })}
                      onClick={() => setValue(value + 1)}
                    >
                      +
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <div className={styles.row}>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary}`}
              disabled={!canConfirm}
              onClick={() => {
                const run = () => {
                  const payment = flexPaymentFromFull(paymentDraft, fixed);
                  if (paymentPrompt.kind === "ability" || !paymentPrompt.kind) {
                    send({
                      type: "ai:activateAbility",
                      boardSlot: paymentPrompt.boardSlot || null,
                      equipmentInstanceId: paymentPrompt.equipmentInstanceId,
                      abilityInstanceId: paymentPrompt.abilityInstanceId,
                      payment,
                      ...(paymentPrompt.effectIndex == null
                        ? {}
                        : { effectIndex: paymentPrompt.effectIndex }),
                    });
                  } else {
                    const typeByKind = {
                      sharpen: "ai:sharpenEquipment",
                      hone: "ai:honeEquipment",
                      fletch: "ai:fletchEquipment",
                      enableEncore: "ai:enableEncoreEquipment",
                      research: "ai:researchEquipment",
                      slottedDamage: "ai:activateSlottedDamageEquipment",
                      repair: "ai:repairEquipment",
                    };
                    send({
                      type: typeByKind[paymentPrompt.kind] || "ai:activateAbility",
                      instanceId: paymentPrompt.instanceId,
                      payment,
                    });
                  }
                  setPaymentPrompt(null);
                };
                if (paymentPrompt.wouldLose) {
                  setLoseConfirm({
                    action: "activate",
                    name: paymentPrompt.name,
                    run,
                  });
                  return;
                }
                run();
              }}
            >
              {t("match.panel.confirm")}
            </button>
            <button
              type="button"
              className={styles.button}
              onClick={() => setPaymentPrompt(null)}
            >
              {t("common.cancel")}
            </button>
          </div>
        </ScrollHintSurface>
      </div>
    );
  };

  // Equip / Slot hand off to the board (pick a slot, then use the card's
  // board buttons), so Drawer mode lowers the hand out of the way. Not saved
  // as the device preference; the arrow raises it again.
  const lowerHandDrawerForBoard = () => {
    if (handDisplay === "drawer") setHandDrawerOpenState(false);
  };

  const playHandEquipmentCard = (handCard, card, targetSlot = null) => {
    if (!handCard || !card) return;
    const run = () => {
      send({
        type: "ai:playEquipment",
        instanceId: handCard.instanceId,
        ...(targetSlot ? { targetSlot } : {}),
      });
      setHandMenu(null);
      setBoardMenu(null);
      setSelectedHand(null);
      lowerHandDrawerForBoard();
    };
    if (actionWouldCauseLoss(actingSeat, card.onEquip)) {
      setLoseConfirm({
        action: "equip",
        name: card.name,
        run,
      });
      return;
    }
    run();
  };

  const canPlayEquipmentHandCard = (handCard, card) => {
    if (!actingSeat) return false;
    if (!isMyTurn && !isWorkshopMatch) return false;
    if (targetingBlocksActions) return false;
    if (isWorkshopMatch) return true;
    if (
      getAvailableEquipmentEquipSlots(actingSeat.board, card, catalogMap)
        .length === 0
    ) {
      return false;
    }
    if (!actingSeat.playedEquipment) return true;
    return isNinjutsuFreeThrowableTokenEquip(
      actingSeat,
      catalogMap,
      handCard,
      card,
    );
  };

  const canSlotAbilityHandCard = (card) => {
    if (!actingSeat) return false;
    if (!isMyTurn && !isWorkshopMatch) return false;
    if (targetingBlocksActions) return false;
    const kind = card?.kind;
    if (kind === "active" || kind === "essence") {
      if (!boardHasEmptyHostAbilitySlot && !boardHasEmptyAugmentAbilitySlot) {
        return false;
      }
    } else if (kind === "augment") {
      if (!boardHasEmptyAugmentAbilitySlot) return false;
    } else if (!boardHasEmptyAbilitySlot) {
      return false;
    }
    if (isWorkshopMatch) return true;
    return (
      !actingSeat.playedAbility ||
      (actingSeat.nimbleFingersCharges || 0) > 0
    );
  };

  const computeMatchGuideState = () => {
    const guide = {
      active: matchGuideActive,
      phase: null,
      playableHandIds: new Set(),
      usableEquipmentIds: new Set(),
      usableAbilityIds: new Set(),
      inherentReadyEquipmentIds: new Set(),
      canPlayEquipment: false,
      canSlotAbility: false,
      canInvoke: false,
      unaffordableAbilities: 0,
      nothingLeft: false,
    };
    if (!matchGuideActive || !selfSeat) return guide;
    if (displayMatch?.phase === "mulligan") {
      guide.phase = selfSeat.ready ? "mulliganWaiting" : "mulligan";
      return guide;
    }
    if (!isMyTurn) {
      guide.phase = displayMatch?.phase === "main" ? "opponentTurn" : null;
      return guide;
    }
    if (targetingBlocksActions) {
      guide.phase = "pending";
      return guide;
    }
    guide.phase = "myTurn";
    (selfSeat.hand || []).forEach((handCard) => {
      if (!handCard || handCard.hidden) return;
      const catalogCard = catalogMap[handCard.cardId];
      if (!catalogCard) return;
      const card = handCard.token ? { ...catalogCard, token: true } : catalogCard;
      if (card.invoke && !selfSeat.invokedHand) guide.canInvoke = true;
      const isEquipment =
        cardHasType(catalogCard, "equipment") || Boolean(catalogCard.slot);
      if (isEquipment) {
        if (!canPlayEquipmentHandCard(handCard, card)) return;
        guide.canPlayEquipment = true;
      } else {
        if (!canSlotAbilityHandCard(card)) return;
        guide.canSlotAbility = true;
      }
      guide.playableHandIds.add(handCard.instanceId);
    });
    listBoardEquipment(selfSeat).forEach((equipment) => {
      const minion = getEquipmentOccupyingMinion(equipment, selfSeat);
      if (minion) {
        const minionAbility = findAbilityForMinion(equipment, minion, selfSeat);
        if (!minion.expended && minionAbility?.effect?.action) {
          guide.usableEquipmentIds.add(equipment.instanceId);
        }
        return;
      }
      if (equipmentHasBoardOccupant(equipment, selfSeat)) return;
      if (equipmentCannotExpend(equipment)) return;
      if (
        equipment.expendCost &&
        !canAffordAbilityCost(mergeSeatResources(selfSeat), equipment.expendCost, {
          seat: selfSeat,
          catalogMap,
        })
      ) {
        return;
      }
      if (
        (equipmentHasResourceExpend(equipment) &&
          !equipment.resourceExpendBlocked) ||
        (!equipment.broken &&
          listEquipmentEffectActions(equipment, selfSeat).some(
            (action) =>
              !getEquipmentEffectChargeBlockMessage(
                equipment,
                getEquipmentEffectForActionIndex(equipment, action.index),
              ),
          ))
      ) {
        guide.usableEquipmentIds.add(equipment.instanceId);
      }
    });
    listAbilityBoardSlotKeys(selfSeat).forEach((boardSlot) => {
      const equipment = boardEquipmentForSlot(selfSeat, boardSlot);
      const occupied = equipment
        ? equipmentHasBoardOccupant(equipment, selfSeat)
        : false;
      const considerAbility = (ability, hostEquipment = null) => {
        if (!ability || ability.kind !== "active") return;
        if (ability.minion && (ability.minion.hp || 0) > 0) return;
        if (ability.aura || abilityLooksUsed(ability)) return;
        if (
          occupied &&
          ability.effect?.id !== "summonMinion" &&
          ability.effect?.id !== "activateAura"
        ) {
          return;
        }
        const affordable = Array.isArray(ability.effect?.choose)
          ? ability.effect.choose.some((_, index) =>
              canAffordAbilityActivation(
                selfSeat,
                withAbilityChoiceCost(ability, index),
                catalogMap,
              ),
            )
          : canAffordAbilityActivation(selfSeat, ability, catalogMap);
        if (!affordable) {
          guide.unaffordableAbilities += 1;
          return;
        }
        guide.usableAbilityIds.add(ability.instanceId);
        if (hostEquipment?.instanceId) {
          guide.inherentReadyEquipmentIds.add(hostEquipment.instanceId);
        }
      };
      getColumnAbilitySlots(selfSeat, boardSlot).forEach((slot) => {
        considerAbility(slot?.ability);
      });
      considerAbility(equipment?.inherentAbility, equipment);
    });
    guide.nothingLeft =
      !guide.canPlayEquipment &&
      !guide.canSlotAbility &&
      guide.usableEquipmentIds.size === 0 &&
      guide.usableAbilityIds.size === 0;
    return guide;
  };
  const matchGuideState = computeMatchGuideState();
  const matchGuideEndTurnGlow =
    matchGuideState.phase === "myTurn" &&
    !readyEndTurnLocked &&
    (matchGuideState.nothingLeft || matchGuideIdle);
  const matchGuideHandGlowClass = (handCard) =>
    matchGuideState.playableHandIds.has(handCard?.instanceId) &&
    handMenu?.instanceId !== handCard.instanceId &&
    selectedHand?.instanceId !== handCard.instanceId
      ? ` ${styles.matchGuideGlow}`
      : "";
  const matchGuideActionClass = (ready) =>
    ready && matchGuideState.phase === "myTurn"
      ? ` ${styles.tutorialHighlight} ${styles.actionReady}`
      : "";
  const matchGuideEquipmentUsable = (equipment, ownBoard) =>
    Boolean(ownBoard) &&
    (matchGuideState.usableEquipmentIds.has(equipment?.instanceId) ||
      matchGuideState.inherentReadyEquipmentIds.has(equipment?.instanceId));
  const matchGuideAbilityUsable = (ability, ownBoard) =>
    Boolean(ownBoard) &&
    matchGuideState.usableAbilityIds.has(ability?.instanceId);

  const listMatchGuideItems = () => {
    const guide = matchGuideState;
    if (guide.phase === "mulligan") {
      return [{ key: "mulligan", text: t("match.guide.mulligan") }];
    }
    if (guide.phase === "mulliganWaiting") {
      return [{ key: "waiting", text: t("match.guide.mulliganWaiting") }];
    }
    if (guide.phase === "opponentTurn") {
      return [{ key: "opponent", text: t("match.guide.opponentTurn") }];
    }
    if (guide.phase === "pending") {
      return [{ key: "pending", text: t("match.guide.pendingChoice") }];
    }
    const items = [];
    if (guide.canPlayEquipment) {
      items.push({ key: "equip", text: t("match.guide.playEquipment") });
    }
    if (guide.canSlotAbility) {
      items.push({ key: "ability", text: t("match.guide.slotAbility") });
    }
    if (guide.usableEquipmentIds.size > 0) {
      items.push({
        key: "expend",
        text: t("match.guide.expendEquipment", {
          count: guide.usableEquipmentIds.size,
        }),
      });
    }
    if (guide.usableAbilityIds.size > 0) {
      items.push({
        key: "activate",
        text: t("match.guide.activateAbilities", {
          count: guide.usableAbilityIds.size,
        }),
      });
    }
    if (guide.unaffordableAbilities > 0 && guide.usableEquipmentIds.size > 0) {
      items.push({ key: "resources", text: t("match.guide.needResources") });
    }
    if (guide.nothingLeft) {
      items.push({ key: "done", text: t("match.guide.nothingLeft"), tone: "done" });
    } else if (matchGuideIdle) {
      items.push({ key: "idle", text: t("match.guide.idle"), tone: "done" });
    }
    if (guide.canInvoke) {
      items.push({ key: "invoke", text: t("match.guide.invoke"), tone: "optional" });
    }
    return items;
  };

  const renderMatchGuidePanel = () => {
    if (!matchGuideState.active || !matchGuideState.phase) return null;
    if (matchGuideMinimized) {
      return (
        <button
          type="button"
          className={`${styles.matchGuidePill}${
            matchGuideEndTurnGlow ? ` ${styles.matchGuidePillAlert}` : ""
          }`}
          aria-label={t("match.guide.show")}
          title={t("match.guide.show")}
          onClick={toggleMatchGuideMinimized}
        >
          <span className={styles.matchGuidePillMark} aria-hidden="true">
            ?
          </span>
          {t("match.guide.label")}
        </button>
      );
    }
    const titleKey = {
      mulligan: "match.guide.title.openingHand",
      mulliganWaiting: "match.guide.title.openingHand",
      opponentTurn: "match.guide.title.opponentTurn",
      pending: "match.guide.title.makeChoice",
      myTurn: "match.guide.title.yourTurn",
    }[matchGuideState.phase];
    return (
      <div className={styles.matchGuidePanel} role="status" aria-live="polite">
        <div className={styles.matchGuidePanelHeader}>
          <span className={styles.matchGuidePanelTitle}>{t(titleKey)}</span>
          <button
            type="button"
            className={styles.matchGuidePanelToggle}
            aria-label={t("match.guide.hide")}
            title={t("match.guide.hide")}
            onClick={toggleMatchGuideMinimized}
          >
            −
          </button>
        </div>
        <ul className={styles.matchGuidePanelList}>
          {listMatchGuideItems().map((item) => (
            <li
              key={item.key}
              className={`${styles.matchGuidePanelItem}${
                item.tone === "done" ? ` ${styles.matchGuidePanelItemDone}` : ""
              }${
                item.tone === "optional"
                  ? ` ${styles.matchGuidePanelItemOptional}`
                  : ""
              }`}
            >
              {item.text}
            </li>
          ))}
        </ul>
        <button
          type="button"
          className={styles.matchGuidePanelOff}
          onClick={() => {
            setMatchGuidesSetting("off");
            showToast(t("match.guide.turnedOff"));
          }}
        >
          {t("match.guide.turnOff")}
        </button>
      </div>
    );
  };

  const renderMatchHandCard = (handCard, emptyKey) => {
    if (!handCard) {
      return (
        <div
          key={emptyKey}
          className={styles.handSlotEmpty}
          aria-hidden="true"
        />
      );
    }
    if (handCard.hidden) {
      return (
        <div
          key={handCard.instanceId}
          className={`${styles.cardBack} ${styles.cardCompact}`}
          aria-label={t("match.panel.hiddenCard")}
        >
          ?
        </div>
      );
    }
    const catalogCard = catalogMap[handCard.cardId];
    if (!catalogCard) {
      return (
        <div
          key={handCard.instanceId}
          className={styles.handSlotEmpty}
          aria-hidden="true"
        />
      );
    }
    const card = handCard.token
      ? { ...catalogCard, token: true }
      : catalogCard;
    const isEquipment =
      cardHasType(catalogCard, "equipment") || Boolean(catalogCard.slot);
    const deck = isEquipment ? "equipment" : "ability";
    const selected =
      handMenu?.instanceId === handCard.instanceId ||
      selectedHand?.instanceId === handCard.instanceId;
    const actionsOpen = handMenu?.instanceId === handCard.instanceId;
    const canApplyHandDiscardDraw =
      Boolean(isMyTurn) &&
      pendingTargetsHandAbilities &&
      !isEquipment &&
      pendingTargetHandAbilityIds.has(handCard.instanceId);
    const canEquip = isEquipment && canPlayEquipmentHandCard(handCard, card);
    const canSlot = !isEquipment && canSlotAbilityHandCard(card);
    const handActionTurnOk =
      !isReplayView &&
      !isSpectateView &&
      (isMyTurn || isWorkshopMatch);
    const showEquip =
      !canApplyHandDiscardDraw &&
      isEquipment &&
      handActionTurnOk;
    const equipBlockMessage = showEquip
      ? handEquipBlockMessage(actingSeat, handCard, card, catalogMap, {
          isMyTurn,
          isWorkshopMatch,
          targetingBlocksActions,
        })
      : null;
    const showSlot =
      !canApplyHandDiscardDraw &&
      !isEquipment &&
      handActionTurnOk;
    const slotBlockMessage = showSlot
      ? handSlotBlockMessage(actingSeat, card, {
          isMyTurn,
          isWorkshopMatch,
          targetingBlocksActions,
          boardHasEmptyHostAbilitySlot,
          boardHasEmptyAugmentAbilitySlot,
          boardHasEmptyAbilitySlot,
        })
      : null;
    const showInvoke =
      !canApplyHandDiscardDraw &&
      handActionTurnOk &&
      Boolean(card?.invoke);
    const invokeBlockMessage = showInvoke
      ? targetingBlocksActions
        ? t("match.panel.toast.resolvePendingChoiceFirst")
        : handInvokeBlockMessage(actingSeat, { isWorkshopMatch })
      : null;
    const invokeActions = showInvoke ? listInvokeEffectActions(card) : [];
    const showArcanistInvoke =
      !canApplyHandDiscardDraw &&
      handActionTurnOk &&
      seatHasArcanistMagicalInvoke(actingSeat) &&
      cardHasArcanistMagicalInvokeEffectClient(card);
    const arcanistInvokeBlockMessage = showArcanistInvoke
      ? targetingBlocksActions
        ? t("match.panel.toast.resolvePendingChoiceFirst")
        : handInvokeBlockMessage(actingSeat, { isWorkshopMatch })
      : null;
    const showBrawlerInvoke =
      !canApplyHandDiscardDraw &&
      handActionTurnOk &&
      seatHasBrawlerMeleeInvoke(actingSeat) &&
      cardHasBrawlerMeleeInvokeEffectClient(card) &&
      !cardNativeInvokeHasPhysicalDamageAmount(card, 1) &&
      !cardNativeInvokeHasPhysicalDamageAmount(card, 2);
    const brawlerInvokeBlockMessage = showBrawlerInvoke
      ? targetingBlocksActions
        ? t("match.panel.toast.resolvePendingChoiceFirst")
        : handInvokeBlockMessage(actingSeat, { isWorkshopMatch })
      : null;
    const showIroncladInvoke =
      !canApplyHandDiscardDraw &&
      handActionTurnOk &&
      seatHasIroncladPhysicalInvoke(actingSeat) &&
      cardHasIroncladPhysicalInvokeEffectClient(card);
    const ironcladInvokeBlockMessage = showIroncladInvoke
      ? targetingBlocksActions
        ? t("match.panel.toast.resolvePendingChoiceFirst")
        : handInvokeBlockMessage(actingSeat, { isWorkshopMatch })
      : null;
    const defaultEquipTarget = null;
    const invokeBadgeActions =
      actionsOpen ? [] : listInvokeEffectActions(card);
    const invokeBadge = invokeBadgeActions.length ? (
      <div
        className={`${styles.handInvokeBadge}${
          actingSeat?.invokedHand && !isWorkshopMatch
            ? ` ${styles.handInvokeBadgeSpent}`
            : ""
        }`}
        aria-hidden="true"
      >
        <InvokeIcon className={styles.handInvokeBadgeIcon} />
        <span className={styles.cardEffectArrow} aria-hidden="true" />
        {invokeBadgeActions.map((action, actionIndex) => (
          <React.Fragment key={`invoke-badge-${actionIndex}`}>
            {actionIndex > 0 ? (
              <span className={styles.handInvokeBadgeDivider}>/</span>
            ) : null}
            {renderInvokeEffectSummary(
              action.effect,
              card,
              actingSeat,
              styles.handCardActionResourceIcons,
            )}
          </React.Fragment>
        ))}
      </div>
    ) : null;

    const raised = raisedHandInstanceId === handCard.instanceId;
    const raiseHandCard = () => {
      setRaisedHandInstanceId((current) =>
        current === handCard.instanceId ? current : handCard.instanceId,
      );
    };

    return (
      <div
        key={handCard.instanceId}
        className={`${styles.handCardSlot}${
          actionsOpen ? ` ${styles.handCardSlotActionsOpen}` : ""
        }${selected ? ` ${styles.handCardSlotActive}` : ""}${
          raised ? ` ${styles.handCardSlotRaised}` : ""
        }`}
        onPointerEnter={raiseHandCard}
        onFocus={raiseHandCard}
      >
        {actionsOpen ? renderInCardFocus(
          <div className={styles.handCardActions}>
            {canApplyHandDiscardDraw ? (
              <button
                type="button"
                className={`${styles.button} ${styles.buttonPrimary} ${styles.handCardActionButton}`}
                onClick={(event) => {
                  event.stopPropagation();
                  send({
                    type: "ai:resolveAbilityTarget",
                    target: {
                      abilityInstanceId: handCard.instanceId,
                    },
                  });
                  setHandMenu(null);
                  setSelectedHand(null);
                }}
              >
                {t("match.panel.discardAndDraw")}
              </button>
            ) : null}
            {!canApplyHandDiscardDraw && showEquip ? (
              <button
                type="button"
                className={`${styles.button} ${styles.buttonPrimary} ${styles.handCardActionButton}${
                  canEquip ? "" : ` ${styles.buttonLooksDisabled}`
                }${canEquip ? tutorialEquipHighlightClass(handCard) : ""}${matchGuideActionClass(canEquip)}`}
                aria-disabled={canEquip ? undefined : true}
                onClick={(event) => {
                  event.stopPropagation();
                  if (toastIfBlockedAction(Boolean(equipBlockMessage), equipBlockMessage)) {
                    return;
                  }
                  playHandEquipmentCard(handCard, card, defaultEquipTarget);
                }}
              >
                {t("match.panel.equip")}
              </button>
            ) : null}
            {!canApplyHandDiscardDraw && showSlot ? (
              <button
                type="button"
                className={`${styles.button} ${styles.buttonPrimary} ${styles.handCardActionButton}${
                  canSlot ? "" : ` ${styles.buttonLooksDisabled}`
                }${canSlot ? tutorialSlotButtonHighlightClass(handCard) : ""}${matchGuideActionClass(canSlot)}`}
                aria-disabled={canSlot ? undefined : true}
                onClick={(event) => {
                  event.stopPropagation();
                  if (toastIfBlockedAction(Boolean(slotBlockMessage), slotBlockMessage)) {
                    return;
                  }
                  setMovingAbility(null);
                  setSelectedHand({
                    deck: "ability",
                    instanceId: handCard.instanceId,
                  });
                  setHandMenu(null);
                  lowerHandDrawerForBoard();
                }}
              >
                {t("match.panel.slotAction")}
              </button>
            ) : null}
            <button
              type="button"
              className={`${styles.button} ${styles.handCardActionButton}`}
              onClick={(event) => {
                event.stopPropagation();
                openCardExam(handCard.cardId, handCard.foilEffect || null);
                setHandMenu(null);
              }}
            >
              {t("match.panel.examine")}
            </button>
            {invokeActions.map((action, actionIndex) => {
              return (
                <button
                  key={`invoke-${action.effectIndex ?? "all"}-${actionIndex}`}
                  type="button"
                  className={`${styles.button} ${styles.handCardActionButton}${
                    invokeBlockMessage ? ` ${styles.buttonLooksDisabled}` : ""
                  }`}
                  aria-disabled={invokeBlockMessage ? true : undefined}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (
                      toastIfBlockedAction(
                        Boolean(invokeBlockMessage),
                        invokeBlockMessage,
                      )
                    ) {
                      return;
                    }
                    send({
                      type: "ai:invokeHand",
                      instanceId: handCard.instanceId,
                      ...(action.effectIndex == null
                        ? {}
                        : { effectIndex: action.effectIndex }),
                    });
                    setHandMenu(null);
                    setSelectedHand(null);
                  }}
                >
                  <InvokeIcon label={t("match.panel.invoke")} />
                  <span className={styles.cardEffectArrow} aria-hidden="true" />
                  {renderInvokeEffectSummary(
                    action.effect,
                    card,
                    actingSeat,
                    styles.handCardActionResourceIcons,
                  )}
                </button>
              );
            })}
            {showArcanistInvoke ? (
              <button
                type="button"
                className={`${styles.button} ${styles.handCardActionButton}${
                  arcanistInvokeBlockMessage
                    ? ` ${styles.buttonLooksDisabled}`
                    : ""
                }`}
                aria-disabled={arcanistInvokeBlockMessage ? true : undefined}
                onClick={(event) => {
                  event.stopPropagation();
                  if (
                    toastIfBlockedAction(
                      Boolean(arcanistInvokeBlockMessage),
                      arcanistInvokeBlockMessage,
                    )
                  ) {
                    return;
                  }
                  send({
                    type: "ai:invokeHand",
                    instanceId: handCard.instanceId,
                    arcanistMagical: true,
                  });
                  setHandMenu(null);
                  setSelectedHand(null);
                }}
              >
                <InvokeIcon label={t("match.panel.invoke")} />
                <span className={styles.cardEffectArrow} aria-hidden="true" />
                <AttributeIconList
                  entries={[["wisdom", 2]]}
                  className={styles.handCardActionResourceIcons}
                />
              </button>
            ) : null}
            {showBrawlerInvoke ? (
              <button
                type="button"
                className={`${styles.button} ${styles.handCardActionButton}${
                  brawlerInvokeBlockMessage
                    ? ` ${styles.buttonLooksDisabled}`
                    : ""
                }`}
                aria-disabled={brawlerInvokeBlockMessage ? true : undefined}
                onClick={(event) => {
                  event.stopPropagation();
                  if (
                    toastIfBlockedAction(
                      Boolean(brawlerInvokeBlockMessage),
                      brawlerInvokeBlockMessage,
                    )
                  ) {
                    return;
                  }
                  send({
                    type: "ai:invokeHand",
                    instanceId: handCard.instanceId,
                    brawlerMelee: true,
                  });
                  setHandMenu(null);
                  setSelectedHand(null);
                }}
              >
                <InvokeIcon label={t("match.panel.invoke")} />
                <span className={styles.cardEffectArrow} aria-hidden="true" />
                <span>{renderTextWithStatusEffectIcons("2 physical damage")}</span>
              </button>
            ) : null}
            {showIroncladInvoke ? (
              <button
                type="button"
                className={`${styles.button} ${styles.handCardActionButton}${
                  ironcladInvokeBlockMessage
                    ? ` ${styles.buttonLooksDisabled}`
                    : ""
                }`}
                aria-disabled={ironcladInvokeBlockMessage ? true : undefined}
                onClick={(event) => {
                  event.stopPropagation();
                  if (
                    toastIfBlockedAction(
                      Boolean(ironcladInvokeBlockMessage),
                      ironcladInvokeBlockMessage,
                    )
                  ) {
                    return;
                  }
                  send({
                    type: "ai:invokeHand",
                    instanceId: handCard.instanceId,
                    ironcladPhysical: true,
                  });
                  setHandMenu(null);
                  setSelectedHand(null);
                }}
              >
                <InvokeIcon label={t("match.panel.invoke")} />
                <span className={styles.cardEffectArrow} aria-hidden="true" />
                <span>{t("match.panel.plusOnePhysArmor")}</span>
                <AttributeIconList
                  entries={[["physicalArmor", 1]]}
                  className={styles.handCardActionResourceIcons}
                />
              </button>
            ) : null}
          </div>,
          {
            key: `hand:${handCard.instanceId}`,
            card,
            foilEffect: handCard.foilEffect || null,
          },
        ) : null}
        <ScaledCardFace
          className={styles.handCardFrame}
          frameOverlay={invokeBadge}
          card={card}
          hideName={!showCardNames}
          foilEffect={handCard.foilEffect || null}
          highlightClass={`${
            isEquipment
              ? tutorialHandHighlightClass(handCard)
              : tutorialAbilityHandHighlightClass(handCard)
          }${matchGuideHandGlowClass(handCard)}`}
          selected={selected}
          targeted={
            !isEquipment &&
            pendingTargetsHandAbilities &&
            pendingTargetHandAbilityIds.has(handCard.instanceId)
          }
          onClick={() => {
            if (isReplayView) {
              if (handCard.cardId) {
                openCardExam(handCard.cardId, handCard.foilEffect || null);
              }
              return;
            }
            if (
              pendingAbilityTarget &&
              (!pendingTargetsHandAbilities ||
                !pendingTargetHandAbilityIds.has(handCard.instanceId))
            ) {
              return;
            }
            if (targetingBlocksActions && !canApplyHandDiscardDraw) {
              return;
            }
            setRaisedHandInstanceId((current) =>
              current === handCard.instanceId ? current : handCard.instanceId,
            );
            setSelectedHand(null);
            setBoardMenu(null);
            setHandMenu((current) =>
              current?.instanceId === handCard.instanceId &&
              current?.deck === deck
                ? null
                : { deck, instanceId: handCard.instanceId },
            );
          }}
        />
      </div>
    );
  };

  const renderHandMenu = () => null;

  const openAdventureMode = () => {
    const run = player?.adventureRun;
    if (
      adventureCombatSummaryActive(run) ||
      (match?.adventure && match?.phase === "ended")
    ) {
      requestView("adventureSummary");
      return;
    }
    if (run?.phase === "combat" && match && match.phase !== "ended") {
      requestView("match");
      return;
    }
    if (run) {
      requestView("adventure");
      return;
    }
    send({ type: "ai:adventureStart" });
    requestView("adventure");
  };

  const resetAdventureRun = () => {
    send({ type: "ai:adventureReset" });
    setAdventureResetConfirm(false);
    setAdventureDiscardIds([]);
    setAdventureInventorySelection(null);
    requestView("menu");
  };

  const renderAdventureResetConfirm = () => {
    if (!adventureResetConfirm) return null;
    return (
      <div
        className={styles.actionPopup}
        onClick={() => setAdventureResetConfirm(false)}
      >
        <ScrollHintSurface
          className={styles.actionPopupPanel}
          onClick={(event) => event.stopPropagation()}
        >
          <h3 className={styles.actionPopupTitle}>
            {t("settings.adventure.confirmTitle")}
          </h3>
          <div className={styles.muted}>
            {t("settings.adventure.confirmBody")}
          </div>
          <div className={styles.row}>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonDanger}`}
              onClick={resetAdventureRun}
            >
              {t("settings.adventure.reset")}
            </button>
            <button
              type="button"
              className={styles.button}
              onClick={() => setAdventureResetConfirm(false)}
            >
              {t("common.cancel")}
            </button>
          </div>
        </ScrollHintSurface>
      </div>
    );
  };

  const renderAdventureEnemyActionBars = () => {
    const enemies = displayMatch?.adventureEnemies || [];
    const targetId = displayMatch?.adventureTargetEnemyId;
    const needsTargetGlow = Boolean(
      isMyTurn &&
        (displayMatch?.adventureDamageSweep || pendingAdventureEnemyTarget),
    );
    return (
      <div className={styles.boardActionBars} data-gamepad-zone="board">
        <div className={styles.muted}>{t("match.view.enemies")}</div>
        <div className={styles.boardActionBarsSection}>
          {enemies.map((enemy) => {
            const defeated = Boolean(enemy.defeated);
            const selected = enemy.id === targetId;
            const choosingEnemyTarget =
              isMyTurn &&
              !defeated &&
              (pendingAdventureEnemyTarget ||
                displayMatch?.adventureDamageSweep);
            const imageUrl = getMonsterImageUrl(enemy);
            const statusParts = [];
            if (enemy.bleedStacks > 0) statusParts.push(t("match.view.statusBleed", { amount: enemy.bleedStacks }));
            if (enemy.poisonStacks > 0) statusParts.push(t("match.view.statusPoison", { amount: enemy.poisonStacks }));
            if (enemy.burnStacks > 0) statusParts.push(t("match.view.statusBurn", { amount: enemy.burnStacks }));
            if (enemy.chillStacks > 0) statusParts.push(t("match.view.statusChill", { amount: enemy.chillStacks }));
            if (enemy.shockStacks > 0) statusParts.push(t("match.view.statusShock", { amount: enemy.shockStacks }));
            if (enemy.witherStacks > 0) statusParts.push(t("match.view.statusWither", { amount: enemy.witherStacks }));
            if (enemy.hexLevel > 0) statusParts.push(t("match.view.statusHex", { amount: enemy.hexLevel }));
            return (
              <button
                key={enemy.id}
                type="button"
                className={`${styles.boardActionStrip} ${styles.adventureEnemyStrip} ${
                  imageUrl ? styles.boardActionStripWithArt : ""
                } ${defeated ? styles.adventureEnemyStripDefeated : ""} ${
                  selected ? styles.adventureEnemyStripSelected : ""
                } ${
                  !defeated && needsTargetGlow
                    ? styles.adventureEnemyStripTarget
                    : ""
                }`}
                disabled={!isMyTurn}
                title={
                  defeated
                    ? t("match.view.enemyDefeated")
                    : choosingEnemyTarget
                      ? selected
                        ? t("match.view.enemySelectedTarget")
                        : t("match.view.enemySelect")
                      : t("match.view.enemyExamine")
                }
                onClick={() => {
                  if (!isMyTurn) return;
                  if (choosingEnemyTarget) {
                    send({
                      type: "ai:adventureSelectEnemy",
                      enemyId: enemy.id,
                    });
                    return;
                  }
                  setAdventureMonsterExamId(enemy.id);
                }}
              >
                {imageUrl ? (
                  <div
                    aria-hidden="true"
                    className={styles.boardActionStripArt}
                  >
                    <div
                      className={styles.boardActionStripArtFill}
                      style={{ backgroundImage: `url("${imageUrl}")` }}
                    />
                    <div className={styles.boardActionStripArtShade} />
                  </div>
                ) : null}
                <div className={styles.boardActionStripInfo}>
                  <span className={styles.boardActionStripName}>
                    {enemy.displayName || enemy.name}
                  </span>
                  <div className={styles.adventureEnemyHp}>
                    {t("adventure.hp", { hp: enemy.hp, maxHp: enemy.maxHp })}
                  </div>
                  {statusParts.length > 0 ? (
                    <div className={styles.boardActionStripTags}>
                      {statusParts.join(" · ")}
                    </div>
                  ) : null}
                  {(enemy.attacks || []).length > 0 ? (
                    <div className={styles.boardActionStripPassive}>
                      {(enemy.attacks || [])
                        .map((attack) => formatAdventureMonsterAttackStrip(attack))
                        .join(" · ")}
                    </div>
                  ) : null}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderAdventureEnemyBoardGrid = () => {
    const enemies = displayMatch?.adventureEnemies || [];
    const targetId = displayMatch?.adventureTargetEnemyId;
    const needsTargetGlow = Boolean(
      isMyTurn &&
        (displayMatch?.adventureDamageSweep || pendingAdventureEnemyTarget),
    );
    const cellCount = Math.max(3, Math.min(6, Math.max(enemies.length, 3)));
    const cells = Array.from(
      { length: cellCount },
      (_, index) => enemies[index] || null,
    );
    return (
      <div className={styles.adventureEnemyBoardWrap} data-gamepad-zone="board">
        <div className={styles.adventureEnemyBoard}>
          {cells.map((enemy, index) => {
            if (!enemy) {
              return (
                <div
                  key={`adventure-enemy-empty-${index}`}
                  className={`${styles.slot} ${styles.adventureEnemyBoardSlot}`}
                >
                  <div
                    className={styles.slotEmpty}
                    role="button"
                    tabIndex={0}
                    aria-label={t("match.view.emptyEnemySlotAria", { slot: index + 1 })}
                    data-gamepad-item=""
                  >
                    {t("match.view.emptySlot")}
                  </div>
                </div>
              );
            }
            const defeated = Boolean(enemy.defeated);
            const selected = enemy.id === targetId;
            const choosingEnemyTarget =
              isMyTurn &&
              !defeated &&
              (pendingAdventureEnemyTarget ||
                displayMatch?.adventureDamageSweep);
            const imageUrl = getMonsterImageUrl(enemy);
            const statusParts = [];
            if (enemy.bleedStacks > 0) statusParts.push(t("match.view.statusBleed", { amount: enemy.bleedStacks }));
            if (enemy.poisonStacks > 0) statusParts.push(t("match.view.statusPoison", { amount: enemy.poisonStacks }));
            if (enemy.burnStacks > 0) statusParts.push(t("match.view.statusBurn", { amount: enemy.burnStacks }));
            if (enemy.chillStacks > 0) statusParts.push(t("match.view.statusChill", { amount: enemy.chillStacks }));
            if (enemy.shockStacks > 0) statusParts.push(t("match.view.statusShock", { amount: enemy.shockStacks }));
            if (enemy.witherStacks > 0) statusParts.push(t("match.view.statusWither", { amount: enemy.witherStacks }));
            if (enemy.hexLevel > 0) statusParts.push(t("match.view.statusHex", { amount: enemy.hexLevel }));
            const intentionSummary = formatAdventureMonsterIntention(enemy);
            const intentReady =
              !defeated && Math.max(0, Math.floor(Number(enemy.turnsUntilAct) || 0)) <= 1;
            return (
              <div
                key={enemy.id}
                className={`${styles.slot} ${styles.adventureEnemyBoardSlot} ${
                  selected ? styles.adventureEnemyBoardSlotSelected : ""
                } ${
                  !defeated && needsTargetGlow
                    ? styles.adventureEnemyBoardSlotTarget
                    : ""
                }`}
              >
                <div
                  className={`${styles.equipBlock} ${styles.adventureEnemyEquipTile} ${
                    imageUrl ? styles.equipBlockWithArt : ""
                  } ${defeated ? styles.adventureEnemyEquipTileDefeated : ""} ${
                    selected ? styles.equipBlockSelected : ""
                  } ${
                    !defeated && needsTargetGlow
                      ? styles.equipBlockTarget
                      : ""
                  }`}
                  role="button"
                  tabIndex={isMyTurn ? 0 : -1}
                  aria-disabled={!isMyTurn}
                  title={
                    defeated
                      ? t("match.view.enemyDefeated")
                      : choosingEnemyTarget
                        ? selected
                          ? t("match.view.enemySelectedTarget")
                          : t("match.view.enemySelect")
                        : t("match.view.enemyExamine")
                  }
                  onClick={() => {
                    if (!isMyTurn) return;
                    if (choosingEnemyTarget) {
                      send({
                        type: "ai:adventureSelectEnemy",
                        enemyId: enemy.id,
                      });
                      return;
                    }
                    setAdventureMonsterExamId(enemy.id);
                  }}
                  onKeyDown={(event) => {
                    if (!isMyTurn) return;
                    if (event.key !== "Enter" && event.key !== " ") return;
                    event.preventDefault();
                    if (choosingEnemyTarget) {
                      send({
                        type: "ai:adventureSelectEnemy",
                        enemyId: enemy.id,
                      });
                      return;
                    }
                    setAdventureMonsterExamId(enemy.id);
                  }}
                >
                  {imageUrl ? (
                    <AiLazyImage
                      className={styles.equipArt}
                      src={imageUrl}
                      alt=""
                      decoding="async"
                      draggable={false}
                    />
                  ) : null}
                  <div className={styles.equipNameRow}>
                    <div className={styles.equipName}>
                      {enemy.displayName || enemy.name}
                    </div>
                  </div>
                  <div className={styles.adventureEnemyHp}>
                    {t("adventure.hp", { hp: enemy.hp, maxHp: enemy.maxHp })}
                  </div>
                  {intentionSummary ? (
                    <div
                      className={`${styles.adventureEnemyIntention} ${
                        intentReady ? styles.adventureEnemyIntentionReady : ""
                      }`}
                    >
                      {intentionSummary}
                    </div>
                  ) : null}
                  {statusParts.length > 0 ? (
                    <div className={styles.adventureEnemyStatusSummary}>
                      {statusParts.join(" · ")}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderAdventureEnemies = () => renderAdventureEnemyBoardGrid();

  const renderAdventureMonsterExam = () => {
    if (!adventureMonsterExamId || !displayMatch?.adventure) return null;
    const enemy = (displayMatch.adventureEnemies || []).find(
      (entry) => entry.id === adventureMonsterExamId,
    );
    if (!enemy) return null;
    const imageUrl = getMonsterImageUrl(enemy);
    const statusParts = [];
    if (enemy.bleedStacks > 0) statusParts.push(t("match.view.statusBleed", { amount: enemy.bleedStacks }));
    if (enemy.poisonStacks > 0) statusParts.push(t("match.view.statusPoison", { amount: enemy.poisonStacks }));
    if (enemy.burnStacks > 0) statusParts.push(t("match.view.statusBurn", { amount: enemy.burnStacks }));
    if (enemy.chillStacks > 0) statusParts.push(t("match.view.statusChill", { amount: enemy.chillStacks }));
    if (enemy.shockStacks > 0) statusParts.push(t("match.view.statusShock", { amount: enemy.shockStacks }));
    if (enemy.witherStacks > 0) statusParts.push(t("match.view.statusWither", { amount: enemy.witherStacks }));
    if (enemy.hexLevel > 0) statusParts.push(t("match.view.statusHex", { amount: enemy.hexLevel }));
    const closeMonsterExam = () => setAdventureMonsterExamId(null);
    return (
      <div
        className={`${styles.actionPopup} ${styles.cardExamOverlay}`}
        onClick={closeMonsterExam}
      >
        <div
          className={`${styles.actionPopupPanel} ${styles.cardExamPanel}`}
          onClick={(event) => event.stopPropagation()}
        >
          <ScrollHintSurface className={styles.cardExamBody}>
            {renderExamTitleRow(
              enemy.displayName || enemy.name,
              imageUrl,
              null,
            )}
            <div className={styles.detailBlock}>
              <div className={styles.detailLine}>
                {t("adventure.hp", { hp: enemy.hp, maxHp: enemy.maxHp })}
              </div>
              {enemy.defeated ? (
                <div className={styles.detailLine}>{t("match.view.enemyDefeated")}</div>
              ) : null}
              {!enemy.defeated && formatAdventureMonsterIntention(enemy) ? (
                <div className={styles.detailLine}>
                  {t("adventure.intention", {
                    intention: formatAdventureMonsterIntention(enemy),
                  })}
                </div>
              ) : null}
              {!enemy.defeated && enemy.attackInterval ? (
                <div className={styles.detailLine}>
                  {t("adventure.actsEvery", { count: enemy.attackInterval })}
                </div>
              ) : null}
              {statusParts.length > 0 ? (
                <div className={styles.detailLine}>{statusParts.join(" · ")}</div>
              ) : null}
              {(enemy.attacks || []).map((attack) => (
                <div
                  key={`${attack.id || attack.name}-${attack.damage}-${attack.heal}`}
                  className={styles.detailLine}
                >
                  {formatAdventureMonsterAttackLine(attack)}
                  {attack.id && attack.id === enemy.intendedAttackId
                    ? t("adventure.nextSuffix")
                    : ""}
                </div>
              ))}
              {enemy.text ? (
                <div className={styles.detailLine}>{enemy.text}</div>
              ) : null}
            </div>
          </ScrollHintSurface>
          <div className={styles.slotDetailActions}>
            <button type="button" className={styles.button} onClick={closeMonsterExam}>
              {t("common.close")}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderAdventureTravelConfirm = (run) => {
    if (!adventureTravelConfirm) return null;
    const deckReady = isAdventureDeckValid(adventureEquipment, adventureAbility, adventureAspect);
    const node =
      (run?.map?.nodes || []).find(
        (entry) => entry.id === adventureTravelConfirm.id,
      ) || adventureTravelConfirm;
    const inParty = Boolean(adventureParty?.members?.length > 1);
    const nodeLabel = adventureNodeLabel(node) || t("adventure.node");
    const title = node.revealed
      ? inParty
        ? t("adventure.chooseNodeTitle", { name: nodeLabel })
        : t("adventure.travelNodeTitle", { name: nodeLabel })
      : inParty
        ? t("adventure.chooseUnknownTitle")
        : t("adventure.travelUnknownTitle");
    return (
      <div
        className={styles.actionPopup}
        onClick={() => setAdventureTravelConfirm(null)}
      >
        <ScrollHintSurface
          className={styles.actionPopupPanel}
          onClick={(event) => event.stopPropagation()}
        >
          <h3 className={styles.actionPopupTitle}>{title}</h3>
          <div className={styles.detailLine}>
            {node.revealed
              ? formatAdventureNodePreview(node.preview)
              : t("adventure.pathHidden")}
          </div>
          {inParty ? (
            <div className={styles.muted}>
              {t("adventure.partyMustConfirm")}
            </div>
          ) : null}
          {deckReady ? null : (
            <div className={styles.muted}>
              {t("adventure.fillDeckBeforeTravel")}
            </div>
          )}
          <div className={styles.row}>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary}`}
              disabled={!deckReady}
              onClick={() => {
                send({
                  type: "ai:adventureSelectNode",
                  nodeId: node.id,
                });
                setAdventureTravelConfirm(null);
              }}
            >
              {inParty ? t("adventure.choose") : t("adventure.travel")}
            </button>
            <button
              type="button"
              className={styles.button}
              onClick={() => setAdventureTravelConfirm(null)}
            >
              {t("common.cancel")}
            </button>
          </div>
        </ScrollHintSurface>
      </div>
    );
  };

  const renderAdventureMap = (run) => {
    const deckReady = isAdventureDeckValid(adventureEquipment, adventureAbility, adventureAspect);
    const inParty = Boolean(adventureParty?.members?.length > 1);
    const mapRun =
      inParty && adventureParty?.sharedMap
        ? { ...run, map: adventureParty.sharedMap, phase: "hub" }
        : run;
    return (
      <div>
        {deckReady ? null : (
          <div className={styles.muted}>
            {t("adventure.fillEverySlot", { max: MAX_COPIES })}
          </div>
        )}
        {inParty && adventureParty?.pendingNodeId ? (
          <div className={styles.adventurePartyReadyRow}>
            <div className={styles.muted}>
              {t("adventure.waitingPartyConfirm", {
                ready: (adventureParty.confirmedReady || []).length,
                total: adventureParty.members.length,
              })}
            </div>
            {(adventureParty.confirmedReady || []).includes(username) ? null : (
              <button
                type="button"
                className={`${styles.button} ${styles.buttonPrimary}`}
                onClick={() => send({ type: "ai:adventurePartyConfirmReady" })}
              >
                {t("adventure.confirmReady")}
              </button>
            )}
          </div>
        ) : null}
        <AdventureMapPanel
          run={mapRun}
          deckReady={deckReady}
          party={adventureParty}
          username={username}
          onVoteNode={(nodeId) =>
            send({ type: "ai:adventurePartyVoteNode", nodeId })
          }
          onRequestTravel={(node) => setAdventureTravelConfirm(node)}
        />
      </div>
    );
  };

  const openAdventureInventoryCard = (zone, index, cardId) => {
    if (!cardId) return;
    setAdventureInventorySelection({ zone, index });
    openCardExam(cardId);
  };

  const adventureActiveEffects = player?.adventureRun?.activeEffects || [];

  const renderAdventureEffectsButton = ({ alwaysShow = false } = {}) => {
    if (!alwaysShow && !adventureActiveEffects.length) return null;
    return (
      <button
        type="button"
        className={styles.button}
        onClick={() => setAdventureEffectsOpen(true)}
      >
        {t("adventure.effects")}
      </button>
    );
  };

  const renderAdventureEffectsModal = () => {
    if (!adventureEffectsOpen) return null;
    return (
      <div
        className={styles.actionPopup}
        onClick={() => setAdventureEffectsOpen(false)}
      >
        <ScrollHintSurface
          className={styles.actionPopupPanel}
          onClick={(event) => event.stopPropagation()}
        >
          <h3 className={styles.actionPopupTitle}>{t("adventure.activeEffects")}</h3>
          <div className={styles.detailBlock}>
            {adventureActiveEffects.length ? (
              adventureActiveEffects.map((entry) => (
                <div key={entry.id} className={styles.detailLine}>
                  {entry.name}
                  {entry.description ? ` — ${entry.description}` : ""}
                </div>
              ))
            ) : (
              <div className={styles.detailLine}>{t("adventure.noActiveEffects")}</div>
            )}
          </div>
          <div className={styles.slotDetailActions}>
            <button
              type="button"
              className={styles.button}
              onClick={() => setAdventureEffectsOpen(false)}
            >
              {t("common.close")}
            </button>
          </div>
        </ScrollHintSurface>
      </div>
    );
  };

  const renderAdventureInventory = (run) => {
    const equipmentIds = run?.inventory?.equipmentIds || [];
    const abilityIds = run?.inventory?.abilityIds || [];
    return (
      <div>
        <div className={styles.muted}>
          {t("adventure.inventoryCount", {
            n: run?.inventoryCount ?? 0,
            max: run?.inventoryMax ?? 50,
          })}
        </div>
        <div className={styles.adventureInventoryGrid}>
          {equipmentIds.map((cardId, index) => {
            const card = catalogMap[cardId];
            if (!card) return null;
            return (
              <CardFace
                key={`inv-eq-${index}-${cardId}`}
                card={card}
                selected={
                  adventureInventorySelection?.zone === "equipment" &&
                  adventureInventorySelection?.index === index
                }
                onClick={() => openAdventureInventoryCard("equipment", index, cardId)}
              />
            );
          })}
          {abilityIds.map((cardId, index) => {
            const card = catalogMap[cardId];
            if (!card) return null;
            return (
              <CardFace
                key={`inv-ab-${index}-${cardId}`}
                card={card}
                selected={
                  adventureInventorySelection?.zone === "ability" &&
                  adventureInventorySelection?.index === index
                }
                onClick={() => openAdventureInventoryCard("ability", index, cardId)}
              />
            );
          })}
        </div>
      </div>
    );
  };

  const renderAdventureDeckEditor = (run) => {
    const equipmentFilled = filledDeckIds(adventureEquipment).length;
    const abilityFilled = filledDeckIds(adventureAbility).length;
    const deckReady = isAdventureDeckValid(
      adventureEquipment,
      adventureAbility,
      adventureAspect,
    );
    return (
      <div className={styles.deckEditor}>
        <div className={styles.muted}>
          {t("adventure.deckCounts", {
            name: run?.deck?.name || t("adventure.defaultDeckName"),
            aspect: adventureAspect ? 1 : 0,
            equipment: equipmentFilled,
            ability: abilityFilled,
            min: MIN_DECK,
          })}
        </div>
        {deckReady ? null : (
          <div className={styles.muted}>
            {t("adventure.deckEditHint", { max: MAX_COPIES })}
          </div>
        )}
        <div>
          <h3 className={styles.panelTitle}>{t("deck.aspect")}</h3>
          {renderDeckSlotGrid("aspect", padAspectSlot(adventureAspect))}
        </div>
        <div>
          <h3 className={styles.panelTitle}>{t("deck.equipment")}</h3>
          {renderDeckSlotGrid("equipment", adventureEquipment)}
        </div>
        <div>
          <h3 className={styles.panelTitle}>{t("deck.ability")}</h3>
          {renderDeckSlotGrid("ability", adventureAbility)}
        </div>
        {renderSlotCardDetail()}
        {renderSlotPicker()}
      </div>
    );
  };

  const renderAdventureRewardChoices = (
    cards,
    takenIds,
    onTake,
    onLeave,
    keyPrefix,
  ) => (
    <div className={styles.adventureInventoryGrid}>
      {cards.map((card) => {
        const taking = takenIds.includes(card.id);
        return (
          <div
            key={`${keyPrefix}-${card.id}`}
            className={styles.adventureTreasureItem}
          >
            <CardFace
              card={card}
              selected={taking}
              faded={!taking}
              onClick={() => openCardExam(card.id)}
            />
            <div className={styles.adventureTreasureActions}>
              {taking ? (
                <button
                  type="button"
                  className={`${styles.button} ${styles.adventureTreasureToggle}`}
                  onClick={() => onLeave(card.id)}
                >
                  {t("adventure.leave")}
                </button>
              ) : (
                <button
                  type="button"
                  className={`${styles.button} ${styles.adventureTreasureToggle} ${styles.buttonPrimary}`}
                  onClick={() => onTake(card.id)}
                >
                  {t("adventure.take")}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderAdventureSummaryEnemyExam = (enemies) => {
    if (!adventureSummaryEnemyExamId) return null;
    const enemy = (enemies || []).find(
      (entry) => entry.id === adventureSummaryEnemyExamId,
    );
    if (!enemy) return null;
    const imageUrl = getMonsterImageUrl(enemy);
    const closeEnemyExam = () => setAdventureSummaryEnemyExamId(null);
    return (
      <div
        className={`${styles.actionPopup} ${styles.cardExamOverlay}`}
        onClick={closeEnemyExam}
      >
        <div
          className={`${styles.actionPopupPanel} ${styles.cardExamPanel}`}
          onClick={(event) => event.stopPropagation()}
        >
          <ScrollHintSurface className={styles.cardExamBody}>
            {renderExamTitleRow(
              enemy.displayName || enemy.name,
              imageUrl,
              null,
            )}
            <div className={styles.detailBlock}>
              <div className={styles.detailLine}>
                {t("adventure.enemyType", {
                  name: enemy.name || enemy.displayName || t("adventure.unknown"),
                })}
              </div>
              <div className={styles.detailLine}>
                {t("adventure.hp", { hp: enemy.hp, maxHp: enemy.maxHp })}
              </div>
              {enemy.defeated ? (
                <div className={styles.detailLine}>{t("match.view.enemyDefeated")}</div>
              ) : null}
              {(enemy.attacks || []).map((attack) => (
                <div
                  key={`${attack.id || attack.name}-${attack.damage}-${attack.heal}`}
                  className={styles.detailLine}
                >
                  {formatAdventureMonsterAttackLine(attack)}
                </div>
              ))}
              {enemy.text ? (
                <div className={styles.detailLine}>{enemy.text}</div>
              ) : null}
            </div>
          </ScrollHintSurface>
          <div className={styles.slotDetailActions}>
            <button
              type="button"
              className={`${styles.button} ${styles.handExamClose}`}
              onClick={closeEnemyExam}
            >
              {t("common.close")}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderAdventurePartyPanel = () => {
    if (!adventurePartyOpen) return null;
    const members = adventureParty?.members || [];
    const eligible = adventureParty?.eligiblePlayers || [];
    const incoming = adventureParty?.incomingInvites || [];
    return (
      <div
        className={styles.actionPopup}
        onClick={() => setAdventurePartyOpen(false)}
      >
        <ScrollHintSurface
          className={styles.actionPopupPanel}
          onClick={(event) => event.stopPropagation()}
        >
          <h3 className={styles.actionPopupTitle}>{t("adventure.party")}</h3>
          <div className={styles.muted}>
            {t("adventure.partyHint")}
          </div>
          <div className={styles.detailBlock}>
            <div className={styles.detailLine}>
              {t("adventure.partyLeader", {
                name: adventureParty?.leader || username,
              })}
            </div>
            {members.map((name) => (
              <div key={name} className={styles.detailLine}>
                {name}
                {name === adventureParty?.leader ? t("adventure.leaderSuffix") : ""}
              </div>
            ))}
          </div>
          {incoming.length ? (
            <div className={styles.detailBlock}>
              {incoming.map((invite) => (
                <div key={invite.id} className={styles.row}>
                  <span>{t("adventure.invitedYou", { name: invite.from })}</span>
                  <button
                    type="button"
                    className={`${styles.button} ${styles.buttonPrimary}`}
                    onClick={() => {
                      send({
                        type: "ai:adventurePartyRespond",
                        inviteId: invite.id,
                        accepted: true,
                      });
                      setIncomingPartyInvite(null);
                    }}
                  >
                    {t("match.popup.accept")}
                  </button>
                  <button
                    type="button"
                    className={styles.button}
                    onClick={() => {
                      send({
                        type: "ai:adventurePartyRespond",
                        inviteId: invite.id,
                        accepted: false,
                        from: invite.from,
                      });
                      setIncomingPartyInvite(null);
                    }}
                  >
                    {t("match.popup.decline")}
                  </button>
                </div>
              ))}
            </div>
          ) : null}
          <div className={styles.detailBlock}>
            {eligible.length ? (
              eligible.map((name) => (
                <div key={name} className={styles.row}>
                  <span>{name}</span>
                  <button
                    type="button"
                    className={styles.button}
                    disabled={
                      members.length >= 4 ||
                      (adventureParty?.leader &&
                        adventureParty.leader !== username &&
                        members.length > 1)
                    }
                    onClick={() =>
                      send({ type: "ai:adventurePartyInvite", username: name })
                    }
                  >
                    {t("adventure.invite")}
                  </button>
                </div>
              ))
            ) : (
              <div className={styles.muted}>{t("adventure.noEligible")}</div>
            )}
          </div>
          <div className={styles.row}>
            {members.length > 1 ? (
              <button
                type="button"
                className={`${styles.button} ${styles.buttonDanger}`}
                onClick={() => {
                  send({ type: "ai:adventurePartyLeave" });
                  setAdventurePartyOpen(false);
                }}
              >
                {t("adventure.leaveParty")}
              </button>
            ) : null}
            <button
              type="button"
              className={styles.button}
              onClick={() => setAdventurePartyOpen(false)}
            >
              {t("common.close")}
            </button>
          </div>
        </ScrollHintSurface>
      </div>
    );
  };

  const renderAdventurePartyLeaveConfirm = (label, cardIds = []) => {
    const inParty = Boolean(adventureParty?.members?.length > 1);
    if (!inParty) return null;
    const confirmed = adventureParty?.confirmedLeave || [];
    const already = confirmed.includes(username);
    return (
      <div className={styles.adventurePartyReadyRow}>
        <div className={styles.muted}>
          {label} ({confirmed.length}/{adventureParty.members.length})
        </div>
        {already ? (
          <div className={styles.muted}>{t("adventure.waitingParty")}</div>
        ) : (
          <button
            type="button"
            className={`${styles.button} ${styles.buttonPrimary}`}
            onClick={() =>
              send({
                type: "ai:adventurePartyConfirmLeave",
                cardIds,
              })
            }
          >
            {t("match.popup.confirm")}
          </button>
        )}
      </div>
    );
  };

  const renderAdventureCombatSummary = () => {
    const run = player?.adventureRun;
    const summary = run?.combatSummary;
    const endedAdventureMatch = Boolean(match?.adventure && match?.phase === "ended");
    if (!run && !endedAdventureMatch) return null;
    if (!summary && !endedAdventureMatch) return null;
    const enemies = summary?.enemies?.length
      ? summary.enemies
      : match?.adventureEnemies || [];
    const defeatedEnemies = enemies.filter((enemy) => enemy.defeated);
    const enemyTypeCounts = defeatedEnemies.reduce((counts, enemy) => {
      const key = enemy.name || enemy.displayName || "Unknown";
      counts[key] = (counts[key] || 0) + 1;
      return counts;
    }, {});
    const rewardIds = summary?.cardRewardIds || [];
    const rewardCards = rewardIds
      .map((id) => catalogMap[id])
      .filter(Boolean);
    const canContinue = Boolean(summary);
    const finishCombatSummary = () => {
      if (!canContinue) return;
      if (adventureCombatRewardTakeIds.length > 0) {
        send({
          type: "ai:adventurePickCombatRewards",
          cardIds: adventureCombatRewardTakeIds,
        });
      }
      send({ type: "ai:adventureContinueCombatSummary" });
      setAdventureCombatRewardTakeIds([]);
      setAdventureDiscardIds([]);
      setAdventureSummaryEnemyExamId(null);
    };
    return (
      <div className={styles.summaryView}>
        <div className={styles.panel}>
          <h2 className={styles.panelTitle}>{t("adventure.combatSummary")}</h2>
          <div className={styles.adventurePanelHeader}>
            <div className={styles.muted}>
              {t("adventure.resultGold", {
                result:
                  summary && !summary.won
                    ? t("match.view.defeat")
                    : t("match.view.victory"),
                gold: summary?.goldEarned || 0,
              })}
            </div>
            {renderAdventureEffectsButton()}
          </div>
          <div className={styles.muted}>
            {t("adventure.enemiesDefeated", {
              defeated: defeatedEnemies.length,
              count: enemies.length,
            })}
          </div>
          {Object.keys(enemyTypeCounts).length > 0 ? (
            <div className={styles.muted}>
              {Object.entries(enemyTypeCounts)
                .map(([name, count]) =>
                  count > 1 ? `${name} x${count}` : name,
                )
                .join(", ")}
            </div>
          ) : null}
          {enemies.length > 0 ? (
            <>
              <h3 className={styles.panelTitle}>{t("match.view.enemies")}</h3>
              <div className={styles.adventureEnemyGrid}>
                {enemies.map((enemy) => {
                  const imageUrl = getMonsterImageUrl(enemy);
                  const enemyName = enemy.displayName || enemy.name || t("adventure.enemy");
                  const showRemainingHp =
                    summary && !summary.won && !enemy.defeated;
                  return (
                    <div
                      key={`summary-enemy-${enemy.id}`}
                      className={styles.adventureEnemySummaryItem}
                    >
                      <button
                        type="button"
                        className={`${styles.adventureEnemyPortraitCard} ${
                          imageUrl
                            ? styles.adventureEnemyPortraitCardWithArt
                            : ""
                        }`}
                        onClick={() => setAdventureSummaryEnemyExamId(enemy.id)}
                      >
                        {imageUrl ? (
                          <span
                            aria-hidden="true"
                            className={styles.adventureEnemyPortraitArt}
                            style={{
                              backgroundImage: `url("${imageUrl}")`,
                            }}
                          />
                        ) : (
                          <span
                            aria-hidden="true"
                            className={styles.adventureEnemyPortraitFallback}
                          >
                            {enemyName.slice(0, 1).toUpperCase()}
                          </span>
                        )}
                        <div className={styles.adventureEnemyPortraitHeader}>
                          <div className={styles.adventureEnemyPortraitName}>
                            {enemyName}
                          </div>
                        </div>
                        {showRemainingHp ? (
                          <div className={styles.adventureEnemyPortraitFooter}>
                            {t("adventure.hp", { hp: enemy.hp, maxHp: enemy.maxHp })}
                          </div>
                        ) : (
                          <span aria-hidden="true" />
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          ) : null}
          {rewardCards.length > 0 ? (
            <>
              <h3 className={styles.panelTitle}>{t("adventure.rewards")}</h3>
              <div className={styles.muted}>
                {t("adventure.rewardHint")}
              </div>
              {renderAdventureRewardChoices(
                rewardCards,
                adventureCombatRewardTakeIds,
                (cardId) =>
                  setAdventureCombatRewardTakeIds((current) =>
                    current.includes(cardId) ? current : [...current, cardId],
                  ),
                (cardId) =>
                  setAdventureCombatRewardTakeIds((current) =>
                    current.filter((id) => id !== cardId),
                  ),
                "combat-reward",
              )}
            </>
          ) : null}
          <div className={styles.row}>
            {Boolean(adventureParty?.members?.length > 1) ? (
              renderAdventurePartyLeaveConfirm(
                t("adventure.continueWhenReady"),
                adventureCombatRewardTakeIds,
              )
            ) : (
              <button
                type="button"
                className={`${styles.button} ${styles.buttonPrimary}`}
                disabled={!canContinue}
                onClick={finishCombatSummary}
              >
                {t("match.view.continue")}
              </button>
            )}
          </div>
          {!canContinue ? (
            <div className={styles.muted}>{t("adventure.resolvingCombat")}</div>
          ) : null}
        </div>
        {renderAdventureSummaryEnemyExam(enemies)}
      </div>
    );
  };

  const renderAdventureHub = () => {
    const run = player?.adventureRun;
    if (!run) return null;
    if (adventureCombatSummaryActive(run)) return null;
    const inParty = Boolean(adventureParty?.members?.length > 1);
    if (run.phase === "shop") {
      const items = run.encounter?.items || [];
      return (
        <div className={styles.panel}>
          <h2 className={styles.panelTitle}>{t("adventure.shop")}</h2>
          <div className={styles.adventurePanelHeader}>
            <div className={styles.muted}>
              {t("adventure.shopHint", { gold: run.gold })}
            </div>
            {renderAdventureEffectsButton()}
          </div>
          <div className={styles.adventureInventoryGrid}>
            {items.map((item, index) => {
              const card = catalogMap[item.cardId];
              if (!card) return null;
              const showBase =
                item.basePrice != null && item.basePrice > item.price;
              return (
                <div key={`shop-${item.cardId}-${index}`}>
                  <CardFace
                    card={card}
                    selected={examCardId === card.id}
                    onClick={() => openCardExam(card.id)}
                  />
                  <button
                    type="button"
                    className={styles.button}
                    onClick={() =>
                      send({ type: "ai:adventureBuyShopItem", index })
                    }
                  >
                    {t("adventure.buyGold", { price: item.price })}
                    {showBase ? ` (${item.basePrice})` : ""}
                  </button>
                </div>
              );
            })}
          </div>
          {inParty ? (
            renderAdventurePartyLeaveConfirm(t("adventure.leaveShopWhenReady"))
          ) : (
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary}`}
              onClick={() => send({ type: "ai:adventureLeaveShop" })}
            >
              {t("adventure.leaveShop")}
            </button>
          )}
        </div>
      );
    }
    if (run.phase === "reward") {
      const cardIds = run.encounter?.cardIds || [];
      const cards = cardIds
        .map((id) => catalogMap[id])
        .filter(Boolean);
      const markTreasureTake = (cardId) => {
        setAdventureTreasureTakeIds((current) =>
          current.includes(cardId) ? current : [...current, cardId],
        );
      };
      const markTreasureLeave = (cardId) => {
        setAdventureTreasureTakeIds((current) =>
          current.filter((id) => id !== cardId),
        );
      };
      return (
        <div className={styles.panel}>
          <h2 className={styles.panelTitle}>{t("adventure.treasure")}</h2>
          <div className={styles.adventurePanelHeader}>
            <div className={styles.muted}>
              {t("adventure.treasureHint", { gold: run.encounter?.gold || 0 })}
            </div>
            {renderAdventureEffectsButton()}
          </div>
          {renderAdventureRewardChoices(
            cards,
            adventureTreasureTakeIds,
            markTreasureTake,
            markTreasureLeave,
            "treasure",
          )}
          {inParty ? (
            renderAdventurePartyLeaveConfirm(
              t("adventure.continueWhenReady"),
              adventureTreasureTakeIds,
            )
          ) : (
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary}`}
              onClick={() => {
                send({
                  type: "ai:adventureTakeReward",
                  cardIds: adventureTreasureTakeIds,
                });
                setAdventureTreasureTakeIds([]);
              }}
            >
              {t("match.view.continue")}
            </button>
          )}
        </div>
      );
    }
    if (run.phase === "story") {
      const effectNames = run.encounter?.effectNames || [];
      return (
        <div className={styles.panel}>
          <h2 className={styles.panelTitle}>{t("adventure.story")}</h2>
          <div className={styles.adventurePanelHeader}>
            <div className={styles.detailLine}>{run.encounter?.text || ""}</div>
            {renderAdventureEffectsButton()}
          </div>
          {effectNames.length > 0 ? (
            <div className={styles.muted}>
              {t("adventure.youGain", { effects: effectNames.join(", ") })}
            </div>
          ) : null}
          {inParty ? (
            renderAdventurePartyLeaveConfirm(t("adventure.continueWhenReady"))
          ) : (
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary}`}
              onClick={() => send({ type: "ai:adventureContinueStory" })}
            >
              {t("match.view.continue")}
            </button>
          )}
        </div>
      );
    }
    if (run.phase === "combat") {
      return (
        <div className={styles.panel}>
          <h2 className={styles.panelTitle}>{t("match.view.endedCombat")}</h2>
          <div className={styles.muted}>{t("adventure.enteringCombat")}</div>
        </div>
      );
    }
    return (
      <div className={styles.panel}>
        <h2 className={styles.panelTitle}>{t("adventure.title")}</h2>
        <div className={styles.adventurePanelHeader}>
          <div className={styles.adventureGold}>{t("adventure.gold", { gold: run.gold })}</div>
          <button
            type="button"
            className={styles.button}
            onClick={() => {
              setAdventurePartyOpen(true);
              send({ type: "ai:adventurePartyRefresh" });
            }}
          >
            {t("adventure.party")}
            {inParty ? ` (${adventureParty.members.length})` : ""}
          </button>
          {renderAdventureEffectsButton({ alwaysShow: true })}
        </div>
        <div className={styles.adventureHubTabs}>
          <button
            type="button"
            className={`${styles.button} ${
              adventureTab === "deck" ? styles.buttonPrimary : ""
            }`}
            onClick={() => {
              setAdventureTab("deck");
              setAdventureInventorySelection(null);
            }}
          >
            {t("adventure.tabDeck")}
          </button>
          <button
            type="button"
            className={`${styles.button} ${
              adventureTab === "inventory" ? styles.buttonPrimary : ""
            }`}
            onClick={() => setAdventureTab("inventory")}
          >
            {t("adventure.tabInventory")}
          </button>
          <button
            type="button"
            className={`${styles.button} ${
              adventureTab === "map" ? styles.buttonPrimary : ""
            }`}
            onClick={() => {
              setAdventureTab("map");
              setAdventureInventorySelection(null);
            }}
          >
            {t("adventure.tabMap")}
          </button>
        </div>
        {adventureTab === "deck" ? renderAdventureDeckEditor(run) : null}
        {adventureTab === "inventory" ? renderAdventureInventory(run) : null}
        {adventureTab === "map" ? renderAdventureMap(run) : null}
        {renderAdventureTravelConfirm(run)}
        {renderAdventurePartyPanel()}
      </div>
    );
  };

  const openDraftMode = () => {
    const run = player?.draftRun;
    if (run?.phase === "picking") {
      requestView("draft");
      return;
    }
    if (run?.phase === "deckbuilding" || run?.phase === "ready") {
      requestView("draftDeck");
      return;
    }
    if (run?.phase === "complete") {
      requestView("draftSummary");
      return;
    }
    const freeAvailable = Boolean(player?.draftFreeAvailable);
    const entryCost = Math.max(
      0,
      Math.floor(Number(player?.draftEntryCost) || 10),
    );
    const currency = Math.max(0, Math.floor(Number(player?.currency) || 0));
    if (!freeAvailable && currency < entryCost) {
      showToast(t("draft.costToast", { cost: entryCost }));
      return;
    }
    send({ type: "ai:draftStart" });
    requestView("draft");
  };

  const forfeitDraftRun = () => {
    send({ type: "ai:draftForfeit" });
    setDraftForfeitConfirm(false);
    setDraftedCardsOpen(false);
    requestView("menu");
  };

  const renderDraftedCardsModal = () => {
    if (!draftedCardsOpen) return null;
    const run = player?.draftRun;
    const aspectCards = (run?.pool?.aspectIds || [])
      .map((id) => catalogMap[id])
      .filter(Boolean);
    const equipmentCards = (run?.pool?.equipmentIds || [])
      .map((id) => catalogMap[id])
      .filter(Boolean);
    const abilityCards = (run?.pool?.abilityIds || [])
      .map((id) => catalogMap[id])
      .filter(Boolean);
    return (
      <div
        className={styles.actionPopup}
        onClick={() => setDraftedCardsOpen(false)}
      >
        <ScrollHintSurface
          className={`${styles.actionPopupPanel} ${styles.socialProfilePanel}`}
          onClick={(event) => event.stopPropagation()}
        >
          <h3 className={styles.actionPopupTitle}>{t("draft.draftedCards")}</h3>
          <div className={styles.socialDeckSection}>
            <div className={styles.socialDeckLabel}>
              {t("draft.aspectCount", { n: aspectCards.length })}
            </div>
            <div className={styles.socialDeckGrid}>
              {aspectCards.map((card, index) => (
                <CardFace
                  key={`draft-as-${card.id}-${index}`}
                  card={card}
                  selected={examCardId === card.id}
                  onClick={() => openCardExam(card.id)}
                  activeKeywordId={keywordTip?.id || null}
                  onKeywordClick={(keyword) => {
                    openKeywordTip(keyword);
                    openCardExam(card.id);
                  }}
                />
              ))}
              {!aspectCards.length && (
                <div className={styles.detailLine}>{t("draft.noneYet")}</div>
              )}
            </div>
          </div>
          <div className={styles.socialDeckSection}>
            <div className={styles.socialDeckLabel}>
              {t("draft.equipmentCount", { n: equipmentCards.length })}
            </div>
            <div className={styles.socialDeckGrid}>
              {equipmentCards.map((card, index) => (
                <CardFace
                  key={`draft-eq-${card.id}-${index}`}
                  card={card}
                  selected={examCardId === card.id}
                  onClick={() => openCardExam(card.id)}
                  activeKeywordId={keywordTip?.id || null}
                  onKeywordClick={(keyword) => {
                    openKeywordTip(keyword);
                    openCardExam(card.id);
                  }}
                />
              ))}
              {!equipmentCards.length && (
                <div className={styles.detailLine}>{t("draft.noneYet")}</div>
              )}
            </div>
          </div>
          <div className={styles.socialDeckSection}>
            <div className={styles.socialDeckLabel}>
              {t("draft.abilityCount", { n: abilityCards.length })}
            </div>
            <div className={styles.socialDeckGrid}>
              {abilityCards.map((card, index) => (
                <CardFace
                  key={`draft-ab-${card.id}-${index}`}
                  card={card}
                  selected={examCardId === card.id}
                  onClick={() => openCardExam(card.id)}
                  activeKeywordId={keywordTip?.id || null}
                  onKeywordClick={(keyword) => {
                    openKeywordTip(keyword);
                    openCardExam(card.id);
                  }}
                />
              ))}
              {!abilityCards.length && (
                <div className={styles.detailLine}>{t("draft.noneYet")}</div>
              )}
            </div>
          </div>
          <div className={styles.row}>
            <button
              type="button"
              className={styles.button}
              onClick={() => setDraftedCardsOpen(false)}
            >
              {t("common.close")}
            </button>
          </div>
        </ScrollHintSurface>
      </div>
    );
  };

  const renderDraftPick = () => {
    const run = player?.draftRun;
    const choices = (run?.choices || [])
      .map((id) => catalogMap[id])
      .filter(Boolean);
    const kindLabel =
      run?.choiceKind === "aspect"
        ? t("deck.aspect")
        : run?.choiceKind === "ability"
          ? t("deck.ability")
          : t("deck.equipment");
    return (
      <div className={`${styles.panel} ${styles.draftPickPanel}`}>
        <div className={styles.draftPickHeader}>
          <h2 className={styles.panelTitle}>{t("draft.title")}</h2>
          <div className={styles.muted}>
            {t("draft.roundChoice", {
              round: run?.round || 1,
              total: run?.totalRounds || 25,
              kind: kindLabel,
            })}
          </div>
          <div className={`${styles.row} ${styles.draftPickActions}`}>
            <button
              type="button"
              className={styles.button}
              onClick={() => setDraftedCardsOpen(true)}
            >
              {t("draft.draftedCards")}
            </button>
            <button
              type="button"
              className={styles.button}
              onClick={() => setDraftForfeitConfirm(true)}
            >
              {t("draft.forfeit")}
            </button>
          </div>
        </div>
        <div className={styles.draftPickGrid}>
          {choices.map((card, index) => (
            <div
              key={`${run?.round || 0}-${index}-${card.id}`}
              className={styles.draftPickOption}
            >
              <CardFace
                card={card}
                selected={examCardId === card.id}
                onClick={() => openCardExam(card.id)}
                activeKeywordId={keywordTip?.id || null}
                onKeywordClick={(keyword) => {
                  openKeywordTip(keyword);
                  openCardExam(card.id);
                }}
              />
              <button
                type="button"
                className={`${styles.button} ${styles.buttonPrimary}`}
                onClick={() => send({ type: "ai:draftPick", cardId: card.id })}
              >
                {t("draft.pick")}
              </button>
            </div>
          ))}
        </div>
        {renderDraftedCardsModal()}
        {renderDraftForfeitConfirm()}
      </div>
    );
  };

  const renderDraftForfeitConfirm = () => {
    if (!draftForfeitConfirm) return null;
    return (
      <div
        className={styles.actionPopup}
        onClick={() => setDraftForfeitConfirm(false)}
      >
        <ScrollHintSurface
          className={styles.actionPopupPanel}
          onClick={(event) => event.stopPropagation()}
        >
          <h3 className={styles.actionPopupTitle}>{t("draft.forfeitTitle")}</h3>
          <div className={styles.muted}>
            {t("draft.forfeitBody")}
          </div>
          <div className={styles.row}>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonDanger}`}
              onClick={forfeitDraftRun}
            >
              {t("draft.forfeit")}
            </button>
            <button
              type="button"
              className={styles.button}
              onClick={() => setDraftForfeitConfirm(false)}
            >
              {t("common.cancel")}
            </button>
          </div>
        </ScrollHintSurface>
      </div>
    );
  };

  const renderDraftCardList = (title, ids) => {
    const cards = (ids || []).map((id) => catalogMap[id]).filter(Boolean);
    return (
      <div className={styles.socialDeckSection}>
        <div className={styles.socialDeckLabel}>{title}</div>
        <div className={styles.socialDeckGrid}>
          {cards.map((card, index) => (
            <CardFace
              key={`${title}-${card.id}-${index}`}
              card={card}
              selected={examCardId === card.id}
              onClick={() => openCardExam(card.id)}
              activeKeywordId={keywordTip?.id || null}
              onKeywordClick={(keyword) => {
                openKeywordTip(keyword);
                openCardExam(card.id);
              }}
            />
          ))}
        </div>
      </div>
    );
  };

  const renderDraftDeckEditor = () => {
    const run = player?.draftRun;
    const equipmentFilled = filledDeckIds(draftEquipment).length;
    const abilityFilled = filledDeckIds(draftAbility).length;
    const deckIsValid = isDraftDeckValid(
      draftEquipment,
      draftAbility,
      draftAspect,
    );
    const canQueue = deckIsValid && run?.phase !== "picking";
    return (
      <div className={styles.panel}>
        <h2 className={styles.panelTitle}>{t("draft.deckTitle")}</h2>
        <div className={styles.muted}>
          {t("draft.deckStatus", {
            lives: run?.lives ?? 0,
            maxLives: run?.maxLives ?? 3,
            played: run?.gamesPlayed ?? 0,
            maxGames: run?.maxGames ?? 10,
            wins: run?.wins ?? 0,
            losses: run?.losses ?? 0,
            draws: run?.draws ?? 0,
          })}
        </div>
        <div className={styles.muted}>
          {t("draft.deckCounts", {
            aspect: draftAspect ? 1 : 0,
            equipment: equipmentFilled,
            ability: abilityFilled,
            min: MIN_DECK,
            poolAspect: (run?.pool?.aspectIds || []).length,
            poolEquipment: (run?.pool?.equipmentIds || []).length,
            poolAbility: (run?.pool?.abilityIds || []).length,
          })}
        </div>
        <div className={styles.row}>
          <button
            type="button"
            className={`${styles.button} ${styles.buttonPrimary}`}
            disabled={!canQueue || awaitingSummary}
            onClick={() => {
              send({
                type: "ai:draftSaveDeck",
                equipmentIds: filledDeckIds(draftEquipment),
                abilityIds: filledDeckIds(draftAbility),
                aspectId: draftAspect,
              });
              send({ type: "ai:draftQueueCpu" });
            }}
          >
            {t("draft.queueCpu")}
          </button>
          <button
            type="button"
            className={styles.button}
            disabled={!canQueue || awaitingSummary}
            onClick={() => {
              send({
                type: "ai:draftSaveDeck",
                equipmentIds: filledDeckIds(draftEquipment),
                abilityIds: filledDeckIds(draftAbility),
                aspectId: draftAspect,
              });
              send({ type: "ai:draftQueuePlayer" });
            }}
          >
            {t("draft.queueHuman")}
          </button>
          {draftQueued && (
            <button
              type="button"
              className={styles.button}
              onClick={() => send({ type: "ai:cancelQueue" })}
            >
              {t("match.lobby.cancelQueue")}
            </button>
          )}
          <button
            type="button"
            className={styles.button}
            onClick={() => setDraftedCardsOpen(true)}
          >
            {t("draft.draftedCards")}
          </button>
          <button
            type="button"
            className={styles.button}
            onClick={() => setDraftForfeitConfirm(true)}
          >
            {t("draft.forfeit")}
          </button>
        </div>
        {draftQueued && <div className={styles.muted}>{t("hud.queued")}</div>}
        <div className={styles.deckEditor}>
          <div>
            <h3 className={styles.panelTitle}>{t("deck.aspect")}</h3>
            {renderDeckSlotGrid("aspect", padAspectSlot(draftAspect))}
          </div>
          <div>
            <h3 className={styles.panelTitle}>{t("deck.equipment")}</h3>
            {renderDeckSlotGrid("equipment", draftEquipment)}
          </div>
          <div>
            <h3 className={styles.panelTitle}>{t("deck.ability")}</h3>
            {renderDeckSlotGrid("ability", draftAbility)}
          </div>
        </div>
        {renderSlotCardDetail()}
        {renderSlotPicker()}
        {renderDraftedCardsModal()}
        {renderDraftForfeitConfirm()}
        {deckIsValid ? null : (
          <div className={styles.muted}>
            {t("draft.buildHint")}
          </div>
        )}
      </div>
    );
  };

  const renderDraftSummary = () => {
    const summary = player?.draftRun?.summary;
    if (!summary) {
      return (
        <div className={styles.panel}>
          {renderCurrencyBalance()}
          <h2 className={styles.panelTitle}>{t("draft.complete")}</h2>
          <div className={styles.row}>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary}`}
              onClick={() => {
                send({ type: "ai:acknowledgeDraftSummary" });
                requestView("menu");
              }}
            >
              {t("draft.collectRewards")}
            </button>
          </div>
        </div>
      );
    }
    const currencyReward = Math.max(
      0,
      Math.floor(Number(summary.currencyReward) || 0),
    );
    const foilUnlocks = summary.holoUnlocks?.length
      ? summary.holoUnlocks
      : summary.holoUnlock
        ? [summary.holoUnlock]
        : [];
    return (
      <div className={styles.panel}>
        {renderCurrencyBalance()}
        <h2 className={styles.panelTitle}>{t("draft.complete")}</h2>
        <div className={styles.detailBlock}>
          <div className={styles.detailLine}>
            {t("draft.livesRemaining", { lives: summary.livesRemaining })}
          </div>
          <div className={styles.detailLine}>
            {t("draft.record", {
              wins: summary.wins,
              losses: summary.losses,
              draws: summary.draws,
            })}
          </div>
          <div className={styles.detailLine}>
            {t("draft.games", { games: summary.gamesPlayed })}
          </div>
          <div className={styles.detailLine}>
            {t("draft.reward")}{" "}
            {currencyReward > 0
              ? renderCurrencyAmount(currencyReward)
              : t("match.view.none")}
          </div>
          {foilUnlocks.length ? (
            <div className={styles.detailLine}>
              {t("draft.wizardFoils", { n: foilUnlocks.length })}
              {foilUnlocks.map((entry, index) => (
                <div
                  key={`draft-foil-${entry.cardId}-${entry.foilEffect}-${index}`}
                  className={styles.muted}
                >
                  {entry.name}
                  {entry.label ? ` (${entry.label})` : ""}
                </div>
              ))}
            </div>
          ) : null}
        </div>
        {renderDraftCardList(
          t("draft.deckAspect"),
          summary.deck?.aspectId
            ? [summary.deck.aspectId]
            : summary.deck?.aspectIds,
        )}
        {renderDraftCardList(t("draft.deckEquipment"), summary.deck?.equipmentIds)}
        {renderDraftCardList(t("draft.deckAbility"), summary.deck?.abilityIds)}
        {renderDraftCardList(t("draft.poolAspect"), summary.pool?.aspectIds)}
        {renderDraftCardList(t("draft.poolEquipment"), summary.pool?.equipmentIds)}
        {renderDraftCardList(t("draft.poolAbility"), summary.pool?.abilityIds)}
        <div className={styles.row}>
          <button
            type="button"
            className={`${styles.button} ${styles.buttonPrimary}`}
            onClick={() => {
              send({ type: "ai:acknowledgeDraftSummary" });
              requestView("menu");
            }}
          >
            {t("draft.collectRewards")}
          </button>
        </div>
      </div>
    );
  };

  const renderTutorials = () => {
    const scenarios = tutorials.length ? tutorials : [];
    const groups = [];
    scenarios.forEach((scenario) => {
      const category = scenario.category || null;
      let group = groups.find((entry) => entry.category === category);
      if (!group) {
        group = { category, scenarios: [] };
        groups.push(group);
      }
      group.scenarios.push(scenario);
    });
    return (
      <div className={styles.panel}>
        <div className={styles.row}>
          <button
            type="button"
            className={styles.button}
            onClick={() => requestView("menu")}
          >
            {t("common.returnToMenu")}
          </button>
        </div>
        <h2 className={styles.panelTitle}>{t("match.lobby.tutorials")}</h2>
        <div className={styles.tutorialCategories}>
          {groups.map((group) => (
            <div
              key={group.category || "uncategorized"}
              className={styles.tutorialCategory}
            >
              {group.category ? (
                <div className={styles.tutorialsBlockHeader}>
                  {group.category}
                </div>
              ) : null}
              <div className={styles.tutorialList}>
                {group.scenarios.map((scenario) => (
                  <button
                    key={scenario.id}
                    type="button"
                    className={styles.tutorialScenarioButton}
                    disabled={awaitingSummary || inLiveMatch}
                    onClick={() => startTutorialScenario(scenario.id)}
                  >
                    {scenario.completed ? (
                      <span
                        className={styles.tutorialScenarioCheck}
                        aria-hidden="true"
                      >
                        ✓
                      </span>
                    ) : (
                      <span
                        className={styles.tutorialScenarioCheckSpacer}
                        aria-hidden="true"
                      />
                    )}
                    <span className={styles.tutorialScenarioCopy}>
                      <span className={styles.tutorialScenarioName}>
                        {scenario.name}
                      </span>
                      {scenario.description ? (
                        <span className={styles.tutorialScenarioDescription}>
                          {scenario.description}
                        </span>
                      ) : null}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderCurrencyAmount = (amount, { className = "" } = {}) => (
    <span className={`${styles.currencyAmount}${className ? ` ${className}` : ""}`}>
      <img
        src={CURRENCY_ICON_URL}
        alt=""
        className={styles.currencyIcon}
        draggable={false}
      />
      <span>{Math.max(0, Math.floor(Number(amount) || 0))}</span>
    </span>
  );

  const renderCurrencyBalance = () => (
    <div
      className={styles.currencyBalance}
      aria-label={t("common.currencyAmount", {
        amount: Math.max(0, Math.floor(Number(player?.currency) || 0)),
      })}
    >
      {renderCurrencyAmount(player?.currency)}
    </div>
  );

  const formatMarketplaceRefresh = (refreshesAt) => {
    const end = Number(refreshesAt);
    if (!Number.isFinite(end) || end <= 0) return "";
    const remainingMs = Math.max(0, end - Date.now());
    const totalMinutes = Math.floor(remainingMs / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 0) return t("common.hoursMinutesShort", { hours, minutes });
    return t("common.minutesShort", { minutes });
  };

  const renderMenu = () => {
    const queueDecks = player?.deckPairs || [];
    const queueDeck =
      queueDecks.find((pair) => pair.id === player?.activeDeckPairId) ||
      queueDecks[0] ||
      null;
    const useRandomQueueDeck = Boolean(player?.useRandomQueueDeck);
    const queueDeckId = useRandomQueueDeck
      ? RANDOM_QUEUE_DECK_OPTION
      : queueDeck?.id || "";
    const deckIsValid = (pair) =>
      Boolean(
        pair &&
          isClassicDeckValid(
            pair.equipmentIds || [],
            pair.abilityIds || [],
            pair.aspectId,
            catalogMap,
            player?.collection,
          ),
      );
    const hasValidQueueDeck = useRandomQueueDeck
      ? queueDecks.some((pair) => deckIsValid(pair))
      : deckIsValid(queueDeck);
    const run = player?.draftRun;
    const adventureRun = player?.adventureRun;
    const adventureInProgress = Boolean(adventureRun);
    const draftInProgress = Boolean(
      run &&
        (run.phase === "picking" ||
          run.phase === "deckbuilding" ||
          run.phase === "ready" ||
          run.phase === "complete"),
    );
    return (
      <div className={styles.panel}>
        {renderCurrencyBalance()}
        {inLiveMatch ? (
          <div className={styles.row}>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary}`}
              onClick={() => requestView("match")}
            >
              {t("match.lobby.backToMatch")}
            </button>
          </div>
        ) : (
          <>
            <div className={styles.classicBlock}>
              <div className={styles.menuSectionHeader}>{t("match.lobby.classic")}</div>
              <div className={styles.menuModeGrid}>
                <div className={styles.menuModeCardWrap}>
                  <button
                    type="button"
                    className={`${styles.menuModeCard} ${
                      queueOpponent === "cpu" ? styles.menuModeCardSelected : ""
                    }`}
                    aria-pressed={queueOpponent === "cpu"}
                    aria-label={t("match.lobby.queueVsCpu")}
                    onClick={() => setQueueOpponent("cpu")}
                    disabled={awaitingSummary || queued || draftQueued}
                  >
                    <img
                      className={styles.menuModeCardImage}
                      src={MENU_MODE_CARD_URL.classicCpu}
                      alt=""
                      draggable={false}
                    />
                    <img
                      className={styles.menuModeCardFrame}
                      src={MENU_MODE_CARD_FRAME_URL}
                      alt=""
                      aria-hidden="true"
                      draggable={false}
                    />
                    <span className={styles.menuModeCardLabel}>{t("match.lobby.vsCpu")}</span>
                  </button>
                </div>
                <div className={styles.menuModeCardWrap}>
                  {queueOpponent === "human" ? (
                    <div className={styles.queueCountPopup} role="status">
                      {t("match.lobby.playersInQueue", { count: playersInQueue })}
                    </div>
                  ) : null}
                  <button
                    type="button"
                    className={`${styles.menuModeCard} ${
                      queueOpponent === "human"
                        ? styles.menuModeCardSelected
                        : ""
                    }`}
                    aria-pressed={queueOpponent === "human"}
                    aria-label={t("match.lobby.queueVsHuman")}
                    onClick={() => setQueueOpponent("human")}
                    disabled={awaitingSummary || queued || draftQueued}
                  >
                    <img
                      className={styles.menuModeCardImage}
                      src={MENU_MODE_CARD_URL.classicHuman}
                      alt=""
                      draggable={false}
                    />
                    <img
                      className={styles.menuModeCardFrame}
                      src={MENU_MODE_CARD_FRAME_URL}
                      alt=""
                      aria-hidden="true"
                      draggable={false}
                    />
                    <span className={styles.menuModeCardLabel}>{t("match.lobby.vsHuman")}</span>
                  </button>
                </div>
              </div>
              <div className={styles.row}>
                {queued || draftQueued ? (
                  <button
                    type="button"
                    className={`${styles.button} ${styles.enterQueueButton}`}
                    onClick={() => send({ type: "ai:cancelQueue" })}
                  >
                    {t("match.lobby.cancelQueue")}
                  </button>
                ) : (
                  <button
                    type="button"
                    className={`${styles.button} ${styles.buttonPrimary} ${styles.enterQueueButton}`}
                    onClick={() =>
                      send({
                        type:
                          queueOpponent === "human"
                            ? "ai:queuePlayer"
                            : "ai:queueCpu",
                      })
                    }
                    disabled={
                      awaitingSummary ||
                      !hasValidQueueDeck ||
                      !queueOpponent
                    }
                  >
                    <NavButtonLabel>
                      {queueOpponent === "human"
                        ? t("match.lobby.enterQueueVsHuman")
                        : t("match.lobby.playAgainstCpu")}
                    </NavButtonLabel>
                  </button>
                )}
              </div>
              {!hasValidQueueDeck ? (
                <div className={styles.muted}>
                  {t("match.lobby.setValidDeck")}
                </div>
              ) : null}
              <div className={styles.queueDeckRow}>
                <label className={styles.queueDeckLabel} htmlFor="ai-queue-deck">
                  {t("match.lobby.currentDeck")}
                </label>
                <select
                  id="ai-queue-deck"
                  className={styles.select}
                  value={queueDeckId}
                  disabled={!queueDecks.length}
                  onChange={(event) => {
                    const deckId = event.target.value;
                    if (!deckId) return;
                    if (deckId === RANDOM_QUEUE_DECK_OPTION) {
                      if (player?.useRandomQueueDeck) return;
                      send({
                        type: "ai:setActiveDeckPair",
                        deckId: RANDOM_QUEUE_DECK_OPTION,
                      });
                      return;
                    }
                    if (
                      deckId === player?.activeDeckPairId &&
                      !player?.useRandomQueueDeck
                    ) {
                      return;
                    }
                    send({
                      type: "ai:setActiveDeckPair",
                      deckId,
                    });
                  }}
                >
                  {queueDecks.length ? (
                    <option value={RANDOM_QUEUE_DECK_OPTION}>
                      {t("match.lobby.randomDeck")}
                    </option>
                  ) : null}
                  {queueDecks.map((pair) => (
                    <option key={pair.id} value={pair.id}>
                      {pair.name}
                    </option>
                  ))}
                </select>
              </div>
              {useRandomQueueDeck ? (
                <div className={styles.muted}>
                  {t("match.lobby.randomDeckHint")}
                </div>
              ) : queueDeck ? (
                <div className={styles.queueDeckPreviewDrawer}>
                  <button
                    type="button"
                    className={styles.queueDeckPreviewToggle}
                    aria-expanded={queueDeckPreviewOpen}
                    onClick={() =>
                      setQueueDeckPreviewOpen((open) => !open)
                    }
                  >
                    <span>{t("match.lobby.deckPreview")}</span>
                    <span
                      className={`${styles.queueDeckPreviewChevron}${
                        queueDeckPreviewOpen
                          ? ` ${styles.queueDeckPreviewChevronOpen}`
                          : ""
                      }`}
                      aria-hidden="true"
                    />
                  </button>
                  {queueDeckPreviewOpen ? (
                    <div className={styles.queueDeckPreview}>
                      {(() => {
                        const aspectCard = queueDeck.aspectId
                          ? catalogMap[queueDeck.aspectId]
                          : null;
                        const equipmentIds = queueDeck.equipmentIds || [];
                        const abilityIds = queueDeck.abilityIds || [];
                        const equipmentFoils = padFoilSlots(
                          queueDeck.equipmentFoils || [],
                        );
                        const abilityFoils = padFoilSlots(
                          queueDeck.abilityFoils || [],
                        );
                        const renderPreviewCard = (
                          cardId,
                          key,
                          foilEffect = null,
                        ) => {
                          const card = cardId ? catalogMap[cardId] : null;
                          if (!card) return null;
                          return (
                            <CardFace
                              key={key}
                              card={card}
                              compact
                              artOnly
                              foilEffect={foilEffect}
                              selected={
                                examCardId === card.id &&
                                (examFoilEffect || null) ===
                                  (foilEffect || null)
                              }
                              onClick={() =>
                                openCardExam(card.id, foilEffect || null)
                              }
                            />
                          );
                        };
                        return (
                          <>
                            <div className={styles.queueDeckPreviewSection}>
                              <div className={styles.queueDeckPreviewLabel}>
                                {t("deck.aspect")}
                              </div>
                              <div className={styles.queueDeckPreviewGrid}>
                                {renderPreviewCard(
                                  queueDeck.aspectId,
                                  `queue-as-${queueDeck.id}-${queueDeck.aspectId}`,
                                  queueDeck.aspectFoil || null,
                                )}
                                {!aspectCard ? (
                                  <div className={styles.muted}>{t("match.lobby.noAspect")}</div>
                                ) : null}
                              </div>
                            </div>
                            <div className={styles.queueDeckPreviewSection}>
                              <div className={styles.queueDeckPreviewLabel}>
                                {t("deck.equipment")}
                              </div>
                              <div className={styles.queueDeckPreviewGrid}>
                                {equipmentIds.map((cardId, index) =>
                                  renderPreviewCard(
                                    cardId,
                                    `queue-eq-${queueDeck.id}-${index}-${cardId}`,
                                    equipmentFoils[index] || null,
                                  ),
                                )}
                              </div>
                            </div>
                            <div className={styles.queueDeckPreviewSection}>
                              <div className={styles.queueDeckPreviewLabel}>
                                {t("deck.ability")}
                              </div>
                              <div className={styles.queueDeckPreviewGrid}>
                                {abilityIds.map((cardId, index) =>
                                  renderPreviewCard(
                                    cardId,
                                    `queue-ab-${queueDeck.id}-${index}-${cardId}`,
                                    abilityFoils[index] || null,
                                  ),
                                )}
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
            <div className={styles.draftQueueBlock}>
              <div className={styles.menuSectionHeader}>{t("match.lobby.modes")}</div>
              <div className={styles.menuModeGridModes}>
                <button
                  type="button"
                  className={styles.menuModeCard}
                  aria-label={
                    draftInProgress
                      ? run?.phase === "complete"
                        ? t("match.lobby.collectDraftRewards")
                        : t("match.lobby.continueDraftMode")
                      : player?.draftFreeAvailable
                        ? t("match.lobby.playDraftModeFree")
                        : t("match.lobby.playDraftModeCost", {
                            cost: Math.max(
                              0,
                              Math.floor(Number(player?.draftEntryCost) || 10),
                            ),
                          })
                  }
                  onClick={openDraftMode}
                  disabled={
                    awaitingSummary ||
                    (!draftInProgress &&
                      !player?.draftFreeAvailable &&
                      Math.max(0, Math.floor(Number(player?.currency) || 0)) <
                        Math.max(
                          0,
                          Math.floor(Number(player?.draftEntryCost) || 10),
                        ))
                  }
                >
                  <img
                    className={styles.menuModeCardImage}
                    src={MENU_MODE_CARD_URL.draft}
                    alt=""
                    draggable={false}
                  />
                  <img
                    className={styles.menuModeCardFrame}
                    src={MENU_MODE_CARD_FRAME_URL}
                    alt=""
                    aria-hidden="true"
                    draggable={false}
                  />
                  <span className={styles.menuModeCardLabel}>
                    {draftInProgress
                      ? run?.phase === "complete"
                        ? t("match.lobby.collectDraft")
                        : t("match.lobby.continueDraft")
                      : player?.draftFreeAvailable
                        ? t("match.lobby.draftFree")
                        : (
                          <>
                            {t("match.lobby.draft")} (
                            {renderCurrencyAmount(
                              Math.max(
                                0,
                                Math.floor(Number(player?.draftEntryCost) || 10),
                              ),
                              { className: styles.menuModeCardCurrency },
                            )}
                            )
                          </>
                        )}
                  </span>
                </button>
                <button
                  type="button"
                  className={styles.menuModeCard}
                  aria-label={
                    adventureInProgress
                      ? t("match.lobby.continueAdventureMode")
                      : t("match.lobby.playAdventureMode")
                  }
                  onClick={openAdventureMode}
                  disabled={awaitingSummary}
                >
                  <img
                    className={styles.menuModeCardImage}
                    src={MENU_MODE_CARD_URL.adventure}
                    alt=""
                    draggable={false}
                  />
                  <img
                    className={styles.menuModeCardFrame}
                    src={MENU_MODE_CARD_FRAME_URL}
                    alt=""
                    aria-hidden="true"
                    draggable={false}
                  />
                  <span className={styles.menuModeCardLabel}>
                    {adventureInProgress ? t("match.lobby.continueAdventure") : t("match.lobby.adventure")}
                  </span>
                </button>
                <button
                  type="button"
                  className={styles.menuModeCard}
                  aria-label={t("match.lobby.tutorialScenarios")}
                  onClick={openTutorialsNav}
                  disabled={awaitingSummary || inLiveMatch}
                >
                  <img
                    className={styles.menuModeCardImage}
                    src={MENU_MODE_CARD_URL.tutorial}
                    alt=""
                    draggable={false}
                  />
                  <img
                    className={styles.menuModeCardFrame}
                    src={MENU_MODE_CARD_FRAME_URL}
                    alt=""
                    aria-hidden="true"
                    draggable={false}
                  />
                  <span className={styles.menuModeCardLabel}>{t("match.lobby.tutorials")}</span>
                </button>
              </div>
              {draftInProgress && run?.phase !== "picking" && run?.phase !== "complete" ? (
                <div className={styles.muted}>
                  {t("match.lobby.draftStatus", {
                    lives: run.lives,
                    played: run.gamesPlayed,
                    max: run.maxGames,
                    wins: run.wins,
                    losses: run.losses,
                    draws: run.draws,
                  })}
                </div>
              ) : null}
              {run?.phase === "picking" ? (
                <div className={styles.muted}>
                  {t("match.lobby.draftRound", { round: run.round, total: run.totalRounds })}
                </div>
              ) : null}
              {run?.phase === "complete" ? (
                <div className={styles.muted}>
                  {t("match.lobby.collectDraftFirst")}
                </div>
              ) : null}
              {adventureInProgress && adventureRun?.eventLabel ? (
                <div className={styles.muted}>{adventureRun.eventLabel}</div>
              ) : null}
            </div>
          </>
        )}
        {(queued || draftQueued) && !inLiveMatch && (
          <div className={styles.muted}>{t("hud.queued")}</div>
        )}
      </div>
    );
  };

  const renderAdmin = () => {
    const adminPlayerTableRows = sortAdminPlayers(
      adminHidePlayerBots
        ? adminPlayerDecks.filter((entry) => !entry.bot)
        : adminPlayerDecks,
      adminPlayerSort,
      adminTrackingNow,
    );
    const openAdminSubView = (nextView) => {
      setAdminSubView(nextView);
      if (nextView === "logs") {
        send({ type: "ai:adminListCompletedGames" });
        send({ type: "ai:adminListPlayerDecks" });
      }
      if (nextView === "players") {
        send({ type: "ai:adminListPlayerDecks" });
      }
      if (nextView === "playerBots") {
        send({ type: "ai:adminListPlayerBots" });
      }
      if (nextView === "reports") {
        send({ type: "ai:adminListPlayerReports" });
      }
      if (nextView === "serverMessages") {
        setAdminMaintenanceConfirm(false);
        send({ type: "ai:adminGetServerMessages" });
      }
    };
    const openReportedPlayerInDatabase = (targetUsername) => {
      setAdminPlayerTarget(targetUsername);
      openAdminSubView("players");
    };
    const openPlayerReportCount = adminPlayerReports.filter(
      (report) => report.status !== "resolved",
    ).length;
    const filteredPlayerReports = adminPlayerReports.filter((report) =>
      adminReportFilter === "all" ? true : report.status !== "resolved",
    );
    const adminPlayerTargetMuted = Boolean(
      adminPlayerDecks.find((entry) => entry.username === adminPlayerTarget)
        ?.chatMuted,
    );

    const deckOptions = [];
    adminPlayerDecks.forEach((entry) => {
      (entry.decks || []).forEach((deck) => {
        deckOptions.push({
          key: `${entry.username}::${deck.id}`,
          username: entry.username,
          deckId: deck.id,
          label: `${entry.username} — ${deck.name}`,
        });
      });
    });

    const parseDeckKey = (key) => {
      if (!key || typeof key !== "string") return null;
      const splitAt = key.indexOf("::");
      if (splitAt < 0) return null;
      return {
        username: key.slice(0, splitAt),
        deckId: key.slice(splitAt + 2),
      };
    };

    const selectedGameId = adminPreviewGame?.id || adminGame?.id || null;
    const adminGrantCards =
      adminPlayerDecks.find((entry) => entry.username === adminPlayerTarget)
        ?.grantCards || [];
    const adminUnlockCardValue = adminGrantCards.some(
      (card) => card.id === adminUnlockCardId,
    )
      ? adminUnlockCardId
      : "";
    const adminUnlockFoilValue = ALL_FOIL_EFFECT_POOL.includes(
      adminUnlockFoilEffect,
    )
      ? adminUnlockFoilEffect
      : "";
    const adminTargetEntry =
      adminPlayerDecks.find((entry) => entry.username === adminPlayerTarget) ||
      null;
    const adminTargetCurrency = Math.max(
      0,
      Math.floor(Number(adminTargetEntry?.currency) || 0),
    );
    const adminCurrencyGrantValue = Math.floor(Number(adminCurrencyAmount));
    const canGrantCurrency =
      Boolean(adminPlayerTarget) &&
      Number.isFinite(adminCurrencyGrantValue) &&
      adminCurrencyGrantValue > 0;

    if (!adminSubView) {
      return (
        <div className={styles.panel}>
          <h2 className={styles.panelTitle}>Admin</h2>
          <div className={styles.muted}>
            Choose a tools view. Each section loads only when you open it.
          </div>
          <div className={styles.adminNavGrid}>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary} ${styles.adminNavButton}`}
              onClick={() => openAdminSubView("catalog")}
            >
              Card Catalog
            </button>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary} ${styles.adminNavButton}`}
              onClick={() => openAdminSubView("logs")}
            >
              View Game Logs
            </button>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary} ${styles.adminNavButton}`}
              onClick={() => openAdminSubView("players")}
            >
              Player Database
            </button>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary} ${styles.adminNavButton}`}
              onClick={() => openAdminSubView("playerBots")}
            >
              Player Bots
            </button>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary} ${styles.adminNavButton}`}
              onClick={() => openAdminSubView("reports")}
            >
              User Reports
              {openPlayerReportCount ? ` (${openPlayerReportCount})` : ""}
            </button>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary} ${styles.adminNavButton}`}
              onClick={() => openAdminSubView("serverMessages")}
            >
              Server Messages
              {serverMaintenance ? " (Countdown)" : ""}
            </button>
          </div>
        </div>
      );
    }

    const subViewTitle =
      adminSubView === "catalog"
        ? "Card Catalog"
        : adminSubView === "logs"
          ? "View Game Logs"
          : adminSubView === "reports"
            ? "User Reports"
            : adminSubView === "serverMessages"
              ? "Server Messages"
              : adminSubView === "playerBots"
                ? "Player Bots"
                : "Player Database";

    return (
      <div className={styles.panel}>
        <div className={styles.row}>
          <button
            type="button"
            className={styles.button}
            onClick={() => setAdminSubView(null)}
          >
            Back
          </button>
        </div>
        <h2 className={styles.panelTitle}>{subViewTitle}</h2>

        {adminSubView === "catalog" ? (
          <AdminCardEditor send={send} catalog={adminCardCatalog} />
        ) : null}

        {adminSubView === "playerBots" ? (
          <AdminPlayerBots
            send={send}
            bots={adminPlayerBots.bots}
            enabled={adminPlayerBots.enabled}
            limits={adminPlayerBots.limits}
            analysis={adminPlayerBotAnalysis}
          />
        ) : null}

        {adminSubView === "serverMessages" ? (
          <>
            <div className={styles.detailBlock}>
              <div className={styles.detailLine}>Maintenance Countdown</div>
              {serverMaintenance ? (
                <div className={styles.serverMessagesMaintenance}>
                  {serverMaintenance.started ? (
                    "Countdown finished. Players have been told maintenance is starting now; shut down the server when ready."
                  ) : (
                    <>
                      Maintenance begins in{" "}
                      <ServerMaintenanceCountdown
                        startsAt={serverMaintenance.startsAt}
                        className={styles.serverMessagesCountdown}
                      />{" "}
                      ({formatServerMessageTime(serverMaintenance.startsAt)})
                      {serverMaintenance.startedBy
                        ? `, started by ${serverMaintenance.startedBy}`
                        : ""}
                      .
                    </>
                  )}
                </div>
              ) : (
                <div className={styles.muted}>
                  No maintenance countdown is active.
                </div>
              )}
              <div className={styles.muted}>
                Starting a countdown announces it to every connected player
                immediately, then automatically posts reminders at{" "}
                {adminCountdownOptions.length
                  ? adminCountdownOptions
                      .map((minutes) =>
                        minutes >= 60 && minutes % 60 === 0
                          ? `${minutes / 60}h`
                          : `${minutes}m`,
                      )
                      .join(", ")
                  : "each step"}{" "}
                remaining, and a final &quot;starting now&quot; notice. The
                server is not shut down automatically.
              </div>
              <label className={styles.detailLine}>
                Countdown length
                <select
                  className={styles.adminSelect}
                  value={adminMaintenanceMinutes}
                  onChange={(event) => {
                    setAdminMaintenanceMinutes(Number(event.target.value));
                    setAdminMaintenanceConfirm(false);
                  }}
                >
                  {adminCountdownOptions.map((minutes) => (
                    <option key={minutes} value={minutes}>
                      {minutes >= 60 && minutes % 60 === 0
                        ? `${minutes / 60} hour${minutes === 60 ? "" : "s"}`
                        : `${minutes} minute${minutes === 1 ? "" : "s"}`}
                    </option>
                  ))}
                </select>
              </label>
              <label className={styles.detailLine}>
                Optional note (appended to each reminder)
                <input
                  className={styles.input}
                  type="text"
                  value={adminMaintenanceNote}
                  maxLength={adminMaintenanceNoteMax}
                  placeholder="e.g. Applying patch 0.68."
                  onChange={(event) =>
                    setAdminMaintenanceNote(event.target.value)
                  }
                />
              </label>
              {!adminMaintenanceConfirm ? (
                <div className={styles.row}>
                  <button
                    type="button"
                    className={`${styles.button} ${styles.buttonPrimary}`}
                    disabled={!adminCountdownOptions.length}
                    onClick={() => setAdminMaintenanceConfirm(true)}
                  >
                    {serverMaintenance
                      ? "Restart Countdown"
                      : "Start Maintenance Countdown"}
                  </button>
                  {serverMaintenance ? (
                    <button
                      type="button"
                      className={`${styles.button} ${styles.buttonDanger}`}
                      onClick={() =>
                        send({ type: "ai:adminCancelMaintenance" })
                      }
                    >
                      Cancel Countdown
                    </button>
                  ) : null}
                </div>
              ) : (
                <div className={styles.detailBlock}>
                  <div className={styles.detailLine}>
                    Announce maintenance in{" "}
                    {adminMaintenanceMinutes >= 60 &&
                    adminMaintenanceMinutes % 60 === 0
                      ? `${adminMaintenanceMinutes / 60} hour${
                          adminMaintenanceMinutes === 60 ? "" : "s"
                        }`
                      : `${adminMaintenanceMinutes} minute${
                          adminMaintenanceMinutes === 1 ? "" : "s"
                        }`}{" "}
                    to every connected player?
                  </div>
                  <div className={styles.row}>
                    <button
                      type="button"
                      className={`${styles.button} ${styles.buttonPrimary}`}
                      onClick={() => {
                        send({
                          type: "ai:adminStartMaintenance",
                          minutes: adminMaintenanceMinutes,
                          note: adminMaintenanceNote,
                        });
                        setAdminMaintenanceConfirm(false);
                      }}
                    >
                      Confirm Countdown
                    </button>
                    <button
                      type="button"
                      className={styles.button}
                      onClick={() => setAdminMaintenanceConfirm(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className={styles.detailBlock}>
              <div className={styles.detailLine}>Send Message</div>
              <div className={styles.muted}>
                Pick a template to fill the message, edit it if needed, then
                send it to every connected player.
              </div>
              <div className={styles.row}>
                {adminServerTemplates.map((template) => (
                  <button
                    key={template.id}
                    type="button"
                    className={styles.button}
                    onClick={() => setAdminServerMessageText(template.text)}
                  >
                    {template.label}
                  </button>
                ))}
              </div>
              <textarea
                className={`${styles.input} ${styles.contactFormTextarea}`}
                value={adminServerMessageText}
                maxLength={adminServerMessageMax}
                rows={4}
                placeholder="Message to all players"
                onChange={(event) =>
                  setAdminServerMessageText(event.target.value)
                }
              />
              <div className={styles.muted}>
                {adminServerMessageText.length}/{adminServerMessageMax}
              </div>
              <div className={styles.row}>
                <button
                  type="button"
                  className={`${styles.button} ${styles.buttonPrimary}`}
                  disabled={!adminServerMessageText.trim()}
                  onClick={() => {
                    const text = adminServerMessageText.trim();
                    if (!text) return;
                    send({ type: "ai:adminSendServerMessage", text });
                    setAdminServerMessageText("");
                  }}
                >
                  Send to All Players
                </button>
              </div>
            </div>

            <div className={styles.detailBlock}>
              <div className={styles.detailLine}>Recent Messages</div>
              {serverMessages.length ? (
                <ul className={styles.serverMessagesList}>
                  {[...serverMessages].reverse().map((entry) => (
                    <li
                      key={entry.id}
                      className={`${styles.serverMessagesItem} ${
                        entry.kind === "maintenance"
                          ? styles.serverMessagesItemMaintenance
                          : ""
                      }`}
                    >
                      <div className={styles.serverMessagesItemHead}>
                        <span>{serverMessageTitle(entry.kind)}</span>
                        <span className={styles.muted}>
                          {formatServerMessageTime(entry.at)}
                        </span>
                      </div>
                      <div className={styles.serverMessagesItemText}>
                        {entry.text}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className={styles.muted}>
                  No messages sent in the last 24 hours.
                </div>
              )}
            </div>
          </>
        ) : null}

        {adminSubView === "reports" ? (
          <div className={styles.detailBlock}>
            <div className={styles.row}>
              <button
                type="button"
                className={`${styles.button} ${
                  adminReportFilter === "open" ? styles.buttonPrimary : ""
                }`}
                onClick={() => setAdminReportFilter("open")}
              >
                Open ({openPlayerReportCount})
              </button>
              <button
                type="button"
                className={`${styles.button} ${
                  adminReportFilter === "all" ? styles.buttonPrimary : ""
                }`}
                onClick={() => setAdminReportFilter("all")}
              >
                All ({adminPlayerReports.length})
              </button>
              <button
                type="button"
                className={styles.button}
                onClick={() => send({ type: "ai:adminListPlayerReports" })}
              >
                Refresh
              </button>
            </div>
            {!filteredPlayerReports.length ? (
              <div className={styles.muted}>
                {adminReportFilter === "open"
                  ? "No open reports."
                  : "No reports yet."}
              </div>
            ) : (
              <div className={styles.adminReportList}>
                {filteredPlayerReports.map((report) => {
                  const resolved = report.status === "resolved";
                  return (
                    <div
                      key={report.id}
                      className={`${styles.adminReportCard} ${
                        resolved ? styles.adminReportCardResolved : ""
                      }`}
                    >
                      <div className={styles.adminReportHeader}>
                        <span>
                          #{report.number} · {report.categoryLabel}
                        </span>
                        <span className={styles.muted}>
                          {report.createdAt
                            ? new Date(report.createdAt).toLocaleString()
                            : ""}
                        </span>
                      </div>
                      <div className={styles.detailLine}>
                        {report.reporter} reported {report.reported} · from{" "}
                        {report.context === "match"
                          ? "a match"
                          : report.context === "chat"
                            ? "chat"
                            : "a profile"}
                        {report.matchId ? ` (${report.matchId})` : ""}
                      </div>
                      <div className={styles.detailLine}>
                        {report.details || (
                          <span className={styles.muted}>No details given.</span>
                        )}
                      </div>
                      {Array.isArray(report.recentMessages) &&
                      report.recentMessages.length ? (
                        <>
                          <div className={styles.muted}>
                            Recent chat from {report.reported}:
                          </div>
                          <ul className={styles.adminReportMessages}>
                            {report.recentMessages.map((message, index) => (
                              <li key={message.id || index}>{message.text}</li>
                            ))}
                          </ul>
                        </>
                      ) : null}
                      {resolved ? (
                        <div className={styles.muted}>
                          Resolved
                          {report.resolvedBy ? ` by ${report.resolvedBy}` : ""}
                          {report.resolvedAt
                            ? ` · ${new Date(report.resolvedAt).toLocaleString()}`
                            : ""}
                        </div>
                      ) : null}
                      <div className={styles.row}>
                        <button
                          type="button"
                          className={`${styles.button} ${
                            resolved ? "" : styles.buttonPrimary
                          }`}
                          onClick={() =>
                            send({
                              type: "ai:adminSetPlayerReportStatus",
                              reportId: report.id,
                              status: resolved ? "open" : "resolved",
                            })
                          }
                        >
                          {resolved ? "Reopen" : "Mark Resolved"}
                        </button>
                        <button
                          type="button"
                          className={styles.button}
                          onClick={() =>
                            openReportedPlayerInDatabase(report.reported)
                          }
                        >
                          Open {report.reported} in Player Database
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : null}

        {adminSubView === "logs" ? (
          <>
            <div className={styles.detailBlock}>
              <div className={styles.detailLine}>Completed Games</div>
              <div className={styles.row}>
                <button
                  type="button"
                  className={styles.button}
                  onClick={() => send({ type: "ai:adminListCompletedGames" })}
                >
                  Refresh
                </button>
              </div>
              {!completedGameSummaries.length ? (
                <div className={styles.muted}>No completed games saved yet.</div>
              ) : (
                <div className={styles.adminGameList}>
                  {completedGameSummaries.map((game) => (
                    <button
                      key={game.id}
                      type="button"
                      className={`${styles.button} ${styles.adminGameListItem} ${
                        selectedGameId === game.id ? styles.buttonPrimary : ""
                      }`}
                      onClick={() =>
                        send({
                          type: "ai:adminGetCompletedGame",
                          gameId: game.id,
                        })
                      }
                    >
                      <span>
                        {game.completedAt
                          ? new Date(game.completedAt).toLocaleString()
                          : game.id}
                      </span>
                      <span className={styles.muted}>
                        {(game.players || []).join(" vs ")} · R{game.rounds} ·{" "}
                        {formatCompletedGameEnded(game.endedBy, game.winner)}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.detailBlock}>
              <div className={styles.detailLine}>Simulate CPU vs CPU</div>
              <div className={styles.muted}>
                Pick any saved player deck for each CPU, then run a full match.
              </div>
              <div className={styles.row}>
                <button
                  type="button"
                  className={styles.button}
                  onClick={() => send({ type: "ai:adminListPlayerDecks" })}
                >
                  Refresh Decks
                </button>
              </div>
              <label className={styles.detailLine}>
                CPU 1 deck
                <select
                  className={styles.adminSelect}
                  value={simDeckA}
                  onChange={(event) => setSimDeckA(event.target.value)}
                >
                  <option value="">Select deck</option>
                  {deckOptions.map((option) => (
                    <option key={`a-${option.key}`} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className={styles.detailLine}>
                CPU 1 difficulty
                <select
                  className={styles.adminSelect}
                  value={simCpuADifficulty}
                  onChange={(event) => setSimCpuADifficulty(event.target.value)}
                >
                  <option value="easy">Easy</option>
                  <option value="normal">Normal</option>
                  <option value="hard">Hard</option>
                </select>
              </label>
              <label className={styles.detailLine}>
                CPU 1 personality
                <select
                  className={styles.adminSelect}
                  value={simCpuAPersonality}
                  onChange={(event) =>
                    setSimCpuAPersonality(event.target.value)
                  }
                >
                  <option value="balanced">Balanced</option>
                  <option value="aggressive">Aggressive</option>
                  <option value="defensive">Defensive</option>
                  <option value="value">Value</option>
                </select>
              </label>
              <label className={styles.detailLine}>
                CPU 2 deck
                <select
                  className={styles.adminSelect}
                  value={simDeckB}
                  onChange={(event) => setSimDeckB(event.target.value)}
                >
                  <option value="">Select deck</option>
                  {deckOptions.map((option) => (
                    <option key={`b-${option.key}`} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className={styles.detailLine}>
                CPU 2 difficulty
                <select
                  className={styles.adminSelect}
                  value={simCpuBDifficulty}
                  onChange={(event) => setSimCpuBDifficulty(event.target.value)}
                >
                  <option value="easy">Easy</option>
                  <option value="normal">Normal</option>
                  <option value="hard">Hard</option>
                </select>
              </label>
              <label className={styles.detailLine}>
                CPU 2 personality
                <select
                  className={styles.adminSelect}
                  value={simCpuBPersonality}
                  onChange={(event) =>
                    setSimCpuBPersonality(event.target.value)
                  }
                >
                  <option value="balanced">Balanced</option>
                  <option value="aggressive">Aggressive</option>
                  <option value="defensive">Defensive</option>
                  <option value="value">Value</option>
                </select>
              </label>
              <div className={styles.row}>
                <button
                  type="button"
                  className={`${styles.button} ${styles.buttonPrimary}`}
                  disabled={!simDeckA || !simDeckB || simRunning}
                  onClick={() => {
                    const deckA = parseDeckKey(simDeckA);
                    const deckB = parseDeckKey(simDeckB);
                    if (!deckA || !deckB) return;
                    setSimRunning(true);
                    send({
                      type: "ai:adminSimulateCpuMatch",
                      deckA,
                      deckB,
                      cpuA: {
                        difficulty: simCpuADifficulty,
                        personality: simCpuAPersonality,
                      },
                      cpuB: {
                        difficulty: simCpuBDifficulty,
                        personality: simCpuBPersonality,
                      },
                    });
                  }}
                >
                  {simRunning ? "Simulating…" : "Run Simulation"}
                </button>
              </div>
            </div>
          </>
        ) : null}

        {adminSubView === "players" ? (
          <>
            <div className={styles.detailBlock}>
              <div className={styles.detailLine}>Player Tracking</div>
              <div className={styles.muted}>
                Online status, session length, login location, and account
                origin. Refreshes every 15 seconds while this view is open.
              </div>
              <div className={styles.row}>
                <button
                  type="button"
                  className={styles.button}
                  onClick={() => send({ type: "ai:adminListPlayerDecks" })}
                >
                  Refresh Players
                </button>
                <button
                  type="button"
                  className={`${styles.button} ${
                    adminHidePlayerBots ? styles.buttonPrimary : ""
                  }`}
                  aria-pressed={adminHidePlayerBots}
                  onClick={() => setAdminHidePlayerBots((current) => !current)}
                >
                  {adminHidePlayerBots ? "Show Player Bots" : "Hide Player Bots"}
                </button>
                {adminPlayerSort ? (
                  <button
                    type="button"
                    className={styles.button}
                    onClick={() => setAdminPlayerSort(null)}
                  >
                    Clear Sort
                  </button>
                ) : null}
              </div>
              {!adminPlayerTableRows.length ? (
                <div className={styles.muted}>
                  {adminPlayerDecks.length
                    ? "No players match (bots are hidden)."
                    : "No player saves yet."}
                </div>
              ) : (
                <div className={styles.adminPlayerTableWrap}>
                  <table className={styles.adminPlayerTable}>
                    <thead>
                      <tr>
                        {ADMIN_PLAYER_COLUMNS.map((column) => {
                          const active = adminPlayerSort?.key === column.id;
                          return (
                            <th
                              key={column.id}
                              aria-sort={
                                active
                                  ? adminPlayerSort.dir === "desc"
                                    ? "descending"
                                    : "ascending"
                                  : "none"
                              }
                            >
                              <button
                                type="button"
                                className={`${styles.adminSortButton} ${
                                  active ? styles.adminSortButtonActive : ""
                                }`}
                                onClick={() =>
                                  setAdminPlayerSort((current) => {
                                    if (current?.key === column.id) {
                                      return {
                                        key: column.id,
                                        dir:
                                          current.dir === "desc"
                                            ? "asc"
                                            : "desc",
                                      };
                                    }
                                    return {
                                      key: column.id,
                                      dir: column.numeric ? "desc" : "asc",
                                    };
                                  })
                                }
                              >
                                {column.label}
                                <span aria-hidden="true">
                                  {active
                                    ? adminPlayerSort.dir === "desc"
                                      ? " ▼"
                                      : " ▲"
                                    : ""}
                                </span>
                              </button>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {adminPlayerTableRows.map((entry) => {
                        const sessionMs =
                          entry.online && entry.sessionStartedAt
                            ? Math.max(
                                0,
                                adminTrackingNow - entry.sessionStartedAt,
                              )
                            : 0;
                        return (
                          <tr
                            key={entry.username}
                            className={
                              adminPlayerTarget === entry.username
                                ? styles.adminPlayerRowSelected
                                : undefined
                            }
                            onClick={() => {
                              setAdminPlayerTarget(entry.username);
                              setAdminWipeConfirm(false);
                              setAdminResetDecksConfirm(false);
                            }}
                          >
                            <td>
                              <div className={styles.adminPlayerName}>
                                {entry.username}
                              </div>
                              <div className={styles.muted}>
                                {entry.bot
                                  ? `Bot · ${entry.bot.label}`
                                  : entry.accountKind === "guest"
                                    ? "Guest"
                                    : "Account"}
                                {entry.online ? " · Online" : ""}
                              </div>
                            </td>
                            <td>{entry.status || "—"}</td>
                            <td>{entry.chatMuted ? "Muted" : "Open"}</td>
                            <td>{entry.currentView || "—"}</td>
                            <td>
                              {entry.online
                                ? formatAdminDuration(sessionMs)
                                : "—"}
                            </td>
                            <td>
                              <div>
                                {formatAdminTimestamp(entry.lastLoginAt)}
                              </div>
                              <div className={styles.muted}>
                                via {formatAdminVia(entry.lastVia)}
                              </div>
                            </td>
                            <td>
                              {formatAdminLocation(entry.lastIp, entry.lastGeo)}
                            </td>
                            <td>
                              <div>
                                {formatAdminTimestamp(entry.createdAt)}
                              </div>
                              <div className={styles.muted}>
                                via {formatAdminVia(entry.createdVia)}
                              </div>
                            </td>
                            <td>
                              {formatAdminLocation(
                                entry.createdIp,
                                entry.createdGeo,
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className={styles.detailBlock}>
              <div className={styles.detailLine}>Player Progress</div>
              <div className={styles.muted}>
                Grant unlocks, reset decks, or wipe a single player&apos;s save.
                Click a row above or use the dropdown to select a player.
              </div>
              <label className={styles.detailLine}>
                Player
                <select
                  className={styles.adminSelect}
                  value={adminPlayerTarget}
                  onChange={(event) => {
                    setAdminPlayerTarget(event.target.value);
                    setAdminUnlockCardId("");
                    setAdminUnlockFoilEffect("");
                    setAdminWipeConfirm(false);
                    setAdminResetDecksConfirm(false);
                  }}
                >
                  <option value="">Select player</option>
                  {[
                    ...new Set(
                      adminPlayerDecks
                        .filter((entry) => !adminHidePlayerBots || !entry.bot)
                        .map((entry) => entry.username)
                        .filter(Boolean),
                    ),
                  ]
                    .sort((a, b) => a.localeCompare(b))
                    .map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                </select>
              </label>
              <label className={styles.detailLine}>
                Grant card / foil
                <select
                  className={styles.adminSelect}
                  value={adminUnlockCardValue}
                  disabled={!adminPlayerTarget || !adminGrantCards.length}
                  onChange={(event) =>
                    setAdminUnlockCardId(event.target.value)
                  }
                >
                  <option value="">
                    {!adminPlayerTarget
                      ? "Select a player first"
                      : "Select a card"}
                  </option>
                  {adminGrantCards.map((card) => (
                    <option key={card.id} value={card.id}>
                      {`${card.unlocked ? "⭐ " : ""}${
                        card.kind
                          ? `${card.kind[0].toUpperCase()}${card.kind.slice(1)} · ${card.name}`
                          : card.name
                      }`}
                    </option>
                  ))}
                </select>
              </label>
              <label className={styles.detailLine}>
                Foil variant
                <select
                  className={styles.adminSelect}
                  value={adminUnlockFoilValue}
                  disabled={!adminPlayerTarget || !adminUnlockCardValue}
                  onChange={(event) =>
                    setAdminUnlockFoilEffect(event.target.value)
                  }
                >
                  <option value="">None (card only)</option>
                  {sortFoilEffects(ALL_FOIL_EFFECT_POOL).map((foilId) => (
                    <option key={foilId} value={foilId}>
                      {foilEffectLabel(foilId)}
                    </option>
                  ))}
                </select>
              </label>
              <div className={styles.row}>
                <button
                  type="button"
                  className={`${styles.button} ${styles.buttonPrimary}`}
                  disabled={!adminPlayerTarget || !adminUnlockCardValue}
                  onClick={() => {
                    if (!adminPlayerTarget || !adminUnlockCardValue) return;
                    send({
                      type: "ai:adminGrantCard",
                      targetUsername: adminPlayerTarget,
                      cardId: adminUnlockCardValue,
                      ...(adminUnlockFoilValue
                        ? { foilEffect: adminUnlockFoilValue }
                        : {}),
                    });
                    setAdminUnlockCardId("");
                    setAdminUnlockFoilEffect("");
                  }}
                >
                  Grant Selected
                </button>
              </div>
              <div className={styles.row}>
                <button
                  type="button"
                  className={`${styles.button} ${
                    adminPlayerTargetMuted
                      ? styles.buttonPrimary
                      : styles.buttonDanger
                  }`}
                  disabled={!adminPlayerTarget}
                  onClick={() => {
                    if (!adminPlayerTarget) return;
                    send({
                      type: "ai:adminSetChatMuted",
                      targetUsername: adminPlayerTarget,
                      muted: !adminPlayerTargetMuted,
                    });
                  }}
                >
                  {adminPlayerTargetMuted ? "Unmute Player Chat" : "Mute Player Chat"}
                </button>
              </div>
              <div className={styles.row}>
                <button
                  type="button"
                  className={`${styles.button} ${styles.buttonPrimary}`}
                  disabled={!adminPlayerTarget}
                  onClick={() => {
                    if (!adminPlayerTarget) return;
                    send({
                      type: "ai:adminGrantRandomCard",
                      targetUsername: adminPlayerTarget,
                    });
                  }}
                >
                  Unlock Random Card
                </button>
                <button
                  type="button"
                  className={`${styles.button} ${styles.buttonPrimary}`}
                  disabled={!adminPlayerTarget}
                  onClick={() => {
                    if (!adminPlayerTarget) return;
                    send({
                      type: "ai:adminGrantAllCards",
                      targetUsername: adminPlayerTarget,
                    });
                  }}
                >
                  Unlock All Cards
                </button>
                <button
                  type="button"
                  className={`${styles.button} ${styles.buttonPrimary}`}
                  disabled={!adminPlayerTarget}
                  onClick={() => {
                    if (!adminPlayerTarget) return;
                    send({
                      type: "ai:adminGrantOwnedHolos",
                      targetUsername: adminPlayerTarget,
                    });
                  }}
                >
                  Unlock Owned Foils
                </button>
                <button
                  type="button"
                  className={styles.button}
                  disabled={!adminPlayerTarget}
                  onClick={() => {
                    if (!adminPlayerTarget) return;
                    send({
                      type: "ai:adminGrantAllHolos",
                      targetUsername: adminPlayerTarget,
                    });
                  }}
                >
                  Unlock All Cards + Foils
                </button>
                <button
                  type="button"
                  className={`${styles.button} ${styles.buttonPrimary}`}
                  disabled={!adminPlayerTarget}
                  onClick={() => {
                    if (!adminPlayerTarget) return;
                    send({
                      type: "ai:adminGrantAllBoards",
                      targetUsername: adminPlayerTarget,
                    });
                  }}
                >
                  Unlock All Boards
                </button>
                <button
                  type="button"
                  className={`${styles.button} ${styles.buttonPrimary}`}
                  disabled={!adminPlayerTarget}
                  onClick={() => {
                    if (!adminPlayerTarget) return;
                    send({
                      type: "ai:adminGrantAllNameColors",
                      targetUsername: adminPlayerTarget,
                    });
                  }}
                >
                  Unlock All Name Colors
                </button>
              </div>
              <label className={styles.detailLine}>
                Grant currency
                {adminPlayerTarget
                  ? ` (current ${adminTargetCurrency})`
                  : ""}
                <input
                  className={styles.input}
                  type="number"
                  min="1"
                  step="1"
                  value={adminCurrencyAmount}
                  disabled={!adminPlayerTarget}
                  onChange={(event) =>
                    setAdminCurrencyAmount(event.target.value)
                  }
                />
              </label>
              <div className={styles.row}>
                <button
                  type="button"
                  className={`${styles.button} ${styles.buttonPrimary}`}
                  disabled={!canGrantCurrency}
                  onClick={() => {
                    if (!canGrantCurrency) return;
                    send({
                      type: "ai:adminGrantCurrency",
                      targetUsername: adminPlayerTarget,
                      amount: adminCurrencyGrantValue,
                    });
                  }}
                >
                  Grant Currency
                </button>
                <button
                  type="button"
                  className={`${styles.button} ${styles.buttonPrimary}`}
                  disabled={!adminPlayerTarget}
                  onClick={() => {
                    if (!adminPlayerTarget) return;
                    send({
                      type: "ai:adminFillDailies",
                      targetUsername: adminPlayerTarget,
                    });
                  }}
                >
                  Fill Empty Dailies
                </button>
              </div>
              <div className={styles.muted}>
                Mute Player Chat immediately blocks chat and permanently deletes
                that player&apos;s existing messages from the chat log. Unmute
                restores eligibility only; deleted messages are not restored.
                Grant Selected unlocks the chosen card if needed and can also
                grant a specific foil variant (including stamp foils). Unlock
                Random Card grants one locked card (no foil). Unlock All Cards
                leaves foils alone. Unlock Owned Foils only fills standard
                variants for cards already unlocked. Unlock All Cards + Foils
                grants the full catalog and every standard foil. Stamp foils are
                achievement-only and are not included in bulk unlocks. Unlock All
                Boards grants every player board cosmetic. Unlock All Name Colors
                grants every name color cosmetic. Grant Currency adds the entered
                amount to the selected player&apos;s wallet. Fill Empty Dailies
                immediately fills every blank dailies slot for the selected
                player, skipping the refill timer.
              </div>
              {!adminResetDecksConfirm ? (
                <div className={styles.row}>
                  <button
                    type="button"
                    className={`${styles.button} ${styles.buttonDanger}`}
                    disabled={!adminPlayerTarget}
                    onClick={() => setAdminResetDecksConfirm(true)}
                  >
                    Reset Player Decks
                  </button>
                </div>
              ) : (
                <div className={styles.detailBlock}>
                  <div className={styles.detailLine}>
                    Replace {adminPlayerTarget}&apos;s decks with the full
                    Library starter list?
                  </div>
                  <div className={styles.row}>
                    <button
                      type="button"
                      className={`${styles.button} ${styles.buttonDanger}`}
                      onClick={() => {
                        send({
                          type: "ai:adminResetPlayerDecks",
                          targetUsername: adminPlayerTarget,
                        });
                        setAdminResetDecksConfirm(false);
                      }}
                    >
                      Confirm Deck Reset
                    </button>
                    <button
                      type="button"
                      className={styles.button}
                      onClick={() => setAdminResetDecksConfirm(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              {!adminWipeConfirm ? (
                <div className={styles.row}>
                  <button
                    type="button"
                    className={`${styles.button} ${styles.buttonDanger}`}
                    disabled={!adminPlayerTarget}
                    onClick={() => setAdminWipeConfirm(true)}
                  >
                    Wipe Player
                  </button>
                </div>
              ) : (
                <div className={styles.detailBlock}>
                  <div className={styles.detailLine}>
                    Permanently wipe {adminPlayerTarget}&apos;s collection,
                    foils, decks, records, and runs, then recreate starter
                    progress?
                  </div>
                  <div className={styles.row}>
                    <button
                      type="button"
                      className={`${styles.button} ${styles.buttonDanger}`}
                      onClick={() => {
                        send({
                          type: "ai:adminWipePlayer",
                          targetUsername: adminPlayerTarget,
                        });
                        setAdminWipeConfirm(false);
                      }}
                    >
                      Confirm Wipe
                    </button>
                    <button
                      type="button"
                      className={styles.button}
                      onClick={() => setAdminWipeConfirm(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className={styles.detailBlock}>
              <div className={styles.detailLine}>
                Reset all player saves to rebuild starter decks and collections
                from current card data.
              </div>
            </div>
            {!resetSavesConfirm ? (
              <div className={styles.row}>
                <button
                  type="button"
                  className={`${styles.button} ${styles.buttonDanger}`}
                  onClick={() => setResetSavesConfirm(true)}
                  disabled={Boolean(match) || awaitingSummary}
                >
                  Reset All Saves
                </button>
              </div>
            ) : (
              <div className={styles.detailBlock}>
                <div className={styles.detailLine}>
                  This permanently deletes every Aeterna Invicta save and
                  recreates connected players with fresh starter decks.
                </div>
                <div className={styles.row}>
                  <button
                    type="button"
                    className={`${styles.button} ${styles.buttonDanger}`}
                    onClick={() => {
                      send({ type: "ai:adminResetSaves" });
                      setResetSavesConfirm(false);
                    }}
                  >
                    Confirm Reset
                  </button>
                  <button
                    type="button"
                    className={styles.button}
                    onClick={() => setResetSavesConfirm(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </>
        ) : null}
      </div>
    );
  };

  const renderSummary = () => {
    const summary = matchSummary;
    if (!summary) {
      return (
        <div className={styles.summaryView}>
          <div className={styles.summaryHero}>
            <h2 className={styles.summaryResult}>{t("match.view.matchOver")}</h2>
            <div className={styles.muted}>{t("match.view.waitingForSummary")}</div>
          </div>
        </div>
      );
    }
    const draw = summary.result === "draw";
    const victory = summary.result === "victory";
    const endedLabel =
      summary.endedBy === "draw"
        ? t("match.view.resultDraw")
        : summary.endedBy === "forfeit"
          ? t("hud.forfeit")
          : summary.endedBy === "timeout"
            ? t("match.view.endedTimeout")
            : t("match.view.endedCombat");
    const resultLabel = summary.isTutorial
      ? victory
        ? t("match.view.tutorialComplete")
        : draw
          ? t("match.view.resultDraw")
          : t("match.view.tryAgain")
      : draw
        ? t("match.view.resultDraw")
        : victory
          ? t("match.view.victory")
          : t("match.view.defeat");
    const resultClass = draw
      ? styles.summaryResultDraw
      : victory
        ? styles.summaryResultVictory
        : styles.summaryResultDefeat;
    const liveOpponentName =
      summary.them?.username ||
      summary.opponent ||
      displayMatch?.players?.find((name) => name !== displayUsername) ||
      null;
    const liveOpponentSeat = liveOpponentName
      ? displayMatch?.seats?.[liveOpponentName]
      : null;
    const hydrateSummarySeat = (snapshot, liveSeat) => {
      if (!snapshot && !liveSeat) return null;
      return {
        ...(liveSeat || {}),
        ...(snapshot || {}),
        board:
          snapshot?.board && typeof snapshot.board === "object"
            ? snapshot.board
            : liveSeat?.board || {},
        hand: Array.isArray(snapshot?.hand)
          ? snapshot.hand
          : liveSeat?.hand ||
            [
              ...(Array.isArray(snapshot?.equipmentHand)
                ? snapshot.equipmentHand
                : liveSeat?.equipmentHand || []),
              ...(Array.isArray(snapshot?.abilityHand)
                ? snapshot.abilityHand
                : liveSeat?.abilityHand || []),
            ],
        deck: Array.isArray(snapshot?.deck)
          ? snapshot.deck
          : liveSeat?.deck ||
            [
              ...(Array.isArray(snapshot?.equipmentDeck)
                ? snapshot.equipmentDeck
                : liveSeat?.equipmentDeck || []),
              ...(Array.isArray(snapshot?.abilityDeck)
                ? snapshot.abilityDeck
                : liveSeat?.abilityDeck || []),
            ],
        discard: Array.isArray(snapshot?.discard)
          ? snapshot.discard
          : liveSeat?.discard || [],
        equipmentIds: Array.isArray(snapshot?.equipmentIds)
          ? snapshot.equipmentIds
          : liveSeat?.equipmentIds || [],
        abilityIds: Array.isArray(snapshot?.abilityIds)
          ? snapshot.abilityIds
          : liveSeat?.abilityIds || [],
        aspectId:
          typeof snapshot?.aspectId === "string" && snapshot.aspectId
            ? snapshot.aspectId
            : liveSeat?.aspectId || null,
        deckName: snapshot?.deckName || liveSeat?.deckName || "Deck",
      };
    };
    const youSeat = hydrateSummarySeat(summary.you, selfSeat);
    const themSeat = hydrateSummarySeat(summary.them, liveOpponentSeat);

    const openSummaryDeck = (seat) => {
      if (!seat) return;
      setSummaryDeckView({
        username: seat.username || "Player",
        name: seat.deckName || "Deck",
        equipmentIds: Array.isArray(seat.equipmentIds) ? seat.equipmentIds : [],
        abilityIds: Array.isArray(seat.abilityIds) ? seat.abilityIds : [],
        aspectId: seat.aspectId || null,
      });
    };

    const renderSummaryDeckModal = () => {
      if (!summaryDeckView) return null;
      const aspectCard = summaryDeckView.aspectId
        ? catalogMap[summaryDeckView.aspectId] || null
        : null;
      const equipmentCards = (summaryDeckView.equipmentIds || [])
        .map((id) => catalogMap[id])
        .filter(Boolean);
      const abilityCards = (summaryDeckView.abilityIds || [])
        .map((id) => catalogMap[id])
        .filter(Boolean);
      const { equipmentPower, abilityPower, aspectPower, totalPower } =
        getDeckPairPowerTotals(
          summaryDeckView.equipmentIds || [],
          summaryDeckView.abilityIds || [],
          summaryDeckView.aspectId,
          catalogMap,
        );
      return (
        <div
          className={styles.actionPopup}
          onClick={() => setSummaryDeckView(null)}
        >
          <ScrollHintSurface
            className={`${styles.actionPopupPanel} ${styles.socialProfilePanel}`}
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className={styles.actionPopupTitle}>
              {summaryDeckView.username}
            </h3>
            {summaryDeckView.name && summaryDeckView.name !== "Deck" ? (
              <div className={styles.muted}>{summaryDeckView.name}</div>
            ) : null}
            <div className={styles.muted}>
              {t("match.view.deckPower", { total: totalPower, max: MAX_DECK_POWER })}
            </div>
            {aspectCard ? (
              <div className={styles.socialDeckSection}>
                <div className={styles.socialDeckLabel}>
                  {t("match.view.deckAspectPower", { power: aspectPower })}
                </div>
                <div className={styles.socialDeckGrid}>
                  <CardFace
                    key={`as-${aspectCard.id}`}
                    card={aspectCard}
                    selected={examCardId === aspectCard.id}
                    onClick={() => openCardExam(aspectCard.id)}
                    activeKeywordId={keywordTip?.id || null}
                    onKeywordClick={(keyword) => {
                      openKeywordTip(keyword);
                      openCardExam(aspectCard.id);
                    }}
                  />
                </div>
              </div>
            ) : null}
            <div className={styles.socialDeckSection}>
              <div className={styles.socialDeckLabel}>
                {t("match.view.deckEquipmentPower", { cards: equipmentCards.length, power: equipmentPower })}
              </div>
              <div className={styles.socialDeckGrid}>
                {equipmentCards.map((card, index) => (
                  <CardFace
                    key={`eq-${card.id}-${index}`}
                    card={card}
                    selected={examCardId === card.id}
                    onClick={() => openCardExam(card.id)}
                    activeKeywordId={keywordTip?.id || null}
                    onKeywordClick={(keyword) => {
                      openKeywordTip(keyword);
                      openCardExam(card.id);
                    }}
                  />
                ))}
              </div>
            </div>
            <div className={styles.socialDeckSection}>
              <div className={styles.socialDeckLabel}>
                {t("match.view.deckAbilitiesPower", { cards: abilityCards.length, power: abilityPower })}
              </div>
              <div className={styles.socialDeckGrid}>
                {abilityCards.map((card, index) => (
                  <CardFace
                    key={`ab-${card.id}-${index}`}
                    card={card}
                    selected={examCardId === card.id}
                    onClick={() => openCardExam(card.id)}
                    activeKeywordId={keywordTip?.id || null}
                    onKeywordClick={(keyword) => {
                      openKeywordTip(keyword);
                      openCardExam(card.id);
                    }}
                  />
                ))}
              </div>
            </div>
            <div className={styles.row}>
              <button
                type="button"
                className={styles.button}
                onClick={() => setSummaryDeckView(null)}
              >
                {t("common.close")}
              </button>
            </div>
          </ScrollHintSurface>
        </div>
      );
    };

    const renderSummaryStatus = (seat) => {
      if (!seat) return null;
      const dodgeStacks = Math.max(0, seat.dodgeStacks || 0);
      const bleedStacks = Math.max(0, seat.bleedStacks || 0);
      const poisonStacks = Math.max(0, seat.poisonStacks || 0);
      const regenerationStacks = Math.max(0, seat.regenerationStacks || 0);
      const burnStacks = Math.max(0, seat.burnStacks || 0);
      const chillStacks = Math.max(0, seat.chillStacks || 0);
      const shockStacks = Math.max(0, seat.shockStacks || 0);
      const witherStacks = Math.max(0, seat.witherStacks || 0);
      const restockStacks = Math.max(0, seat.restockStacks || 0);
      const hasArmor =
        (seat.physicalArmor || 0) > 0 ||
        (seat.magicArmor || 0) > 0 ||
        (seat.fireResistance || 0) > 0 ||
        (seat.coldResistance || 0) > 0 ||
        (seat.lightningResistance || 0) > 0;
      if (
        !hasArmor &&
        dodgeStacks <= 0 &&
        bleedStacks <= 0 &&
        poisonStacks <= 0 &&
        regenerationStacks <= 0 &&
        burnStacks <= 0 &&
        chillStacks <= 0 &&
        shockStacks <= 0 &&
        witherStacks <= 0 &&
        restockStacks <= 0
      ) {
        return null;
      }
      return (
        <div className={styles.summarySeatStatus}>
          {renderSeatArmorBadges(seat)}
          {dodgeStacks > 0 ? (
            <StatusEffectIconStack effectId="dodge" amount={dodgeStacks} />
          ) : null}
          {regenerationStacks > 0 ? (
            <StatusEffectIconStack
              effectId="regeneration"
              amount={regenerationStacks}
            />
          ) : null}
          {bleedStacks > 0 ? (
            <StatusEffectIconStack effectId="bleed" amount={bleedStacks} />
          ) : null}
          {poisonStacks > 0 ? (
            <StatusEffectIconStack effectId="poison" amount={poisonStacks} />
          ) : null}
          {burnStacks > 0 ? (
            <StatusEffectIconStack effectId="burn" amount={burnStacks} />
          ) : null}
          {chillStacks > 0 ? (
            <StatusEffectIconStack effectId="chill" amount={chillStacks} />
          ) : null}
          {shockStacks > 0 ? (
            <StatusEffectIconStack effectId="shock" amount={shockStacks} />
          ) : null}
          {witherStacks > 0 ? (
            <span className={styles.witherStatusBadge} title={t("match.view.wither")}>
              {t("match.view.statusWither", { amount: witherStacks })}
            </span>
          ) : null}
          {restockStacks > 0 ? (
            <StatusEffectIconStack effectId="restock" amount={restockStacks} />
          ) : null}
        </div>
      );
    };

    const renderSummaryCardRow = (cards) => {
      const list = (cards || []).filter((card) => card?.cardId);
      if (!list.length) {
        return <div className={styles.summaryEmpty}>{t("match.view.noneRemaining")}</div>;
      }
      return (
        <div className={styles.summaryCardRow}>
          {list.map((card) => (
            <CardFace
              key={card.instanceId}
              card={catalogMap[card.cardId]}
              foilEffect={card.foilEffect || null}
              selected={examCardId === card.cardId}
              onClick={() => {
                if (card.cardId) openCardExam(card.cardId, card.foilEffect || null);
              }}
            />
          ))}
        </div>
      );
    };

    const renderSummarySeat = (seat, { title, isCpu, isYou }) => {
      if (!seat) return null;
      const winner = summary.winner === seat.username;
      const displayName = seat.username || title;
      return (
        <section
          className={`${styles.summarySeat} ${
            winner ? styles.summarySeatWinner : ""
          }`}
        >
          <div className={styles.summarySeatHeader}>
            <div>
              {isYou ? (
                <div className={styles.summarySeatYou}>{t("match.view.you")}</div>
              ) : null}
              <button
                type="button"
                className={styles.summarySeatNameButton}
                onClick={() => openSummaryDeck(seat)}
              >
                {displayName}
                {isCpu ? " (CPU)" : ""}
              </button>
              <div className={styles.summarySeatHp}>
                {t("adventure.hp", { hp: seat.hp ?? 0, maxHp: seat.maxHp ?? 0 })}
              </div>
            </div>
            {winner ? (
              <div className={styles.summaryWinnerMark}>{t("match.view.winner")}</div>
            ) : null}
          </div>
          {renderSummaryStatus(seat)}
          <div className={styles.summarySectionTitle}>{t("match.view.board")}</div>
          <div className={styles.summaryBoard}>
            {renderBodyBoard(seat, false, true)}
          </div>
          <div className={styles.summarySectionTitle}>
            {t("match.view.handCount", { n: seat.handCount ?? (seat.hand || []).length })}
          </div>
          {renderSummaryCardRow(seat.hand)}
          <div className={styles.summarySectionTitle}>
            {t("match.view.deckCount", { n: seat.deckCount ?? (seat.deck || []).length })}
          </div>
          {renderSummaryCardRow(seat.deck)}
          <div className={styles.summarySectionTitle}>
            {t("match.view.discardCount", { n: seat.discardCount ?? (seat.discard || []).length })}
          </div>
          {renderSummaryCardRow(seat.discard)}
        </section>
      );
    };

    const continueButton = (
      <button
        type="button"
        className={`${styles.button} ${styles.buttonPrimary}`}
        onClick={() => {
          send({ type: "ai:acknowledgeSummary" });
          setView(summary.isTutorial ? "tutorials" : "menu");
        }}
      >
        {t("match.view.continue")}
      </button>
    );
    const logButton = (
      <button
        type="button"
        className={styles.button}
        onClick={() => {
          combatLogStickTopRef.current = true;
          setCombatLogOpen(true);
        }}
      >
        {t("hud.gameLog")}
      </button>
    );

    const adOffer = summary.adRewardOffer;
    const adOfferAvailable =
      canShowAds() &&
      Boolean(adOffer) &&
      !adOffer.claimed &&
      summary.endedBy !== "forfeit" &&
      summary.endedBy !== "timeout";
    const adCardSlots = adOfferAvailable
      ? Math.max(0, Math.floor(Number(adOffer.cardSlots) || 0))
      : 0;
    const adFoilSlots = adOfferAvailable
      ? Math.max(0, Math.floor(Number(adOffer.foilSlots) || 0))
      : 0;
    const openAdRewardPrompt = () => {
      if (adRewardBusy || !adOfferAvailable) return;
      setAdRewardPromptOpen(true);
    };
    const confirmAdRewardWatch = async () => {
      if (adRewardBusy || !adOfferAvailable) return;
      setAdRewardBusy(true);
      try {
        const watched = await showRewardedAd({ dev: isAdmin });
        if (!watched) {
          showToast(t("match.view.adNotCompleted"));
          return;
        }
        send({ type: "ai:claimAdMatchRewards" });
        setAdRewardPromptOpen(false);
      } catch (error) {
        const reason = isAdmin
          ? String((error && (error.message || error.code)) || error || "")
          : "";
        showToast(
          reason
            ? `${t("match.view.adStartFailed")} (${reason})`
            : t("match.view.adStartFailed"),
        );
      } finally {
        setAdRewardBusy(false);
      }
    };
    const renderAdUnlockSlots = (count, kind) => {
      if (count <= 0) return null;
      return Array.from({ length: count }, (_, index) => (
        <button
          key={`ad-unlock-${kind}-${index}`}
          type="button"
          className={styles.summaryAdUnlockSlot}
          aria-label={t("match.view.adExtraUnlock")}
          title={t("match.view.adExtraUnlock")}
          disabled={adRewardBusy}
          onClick={openAdRewardPrompt}
        >
          ?
        </button>
      ));
    };

    return (
      <div className={styles.summaryView}>
        <div className={styles.summaryHero}>
          <div className={styles.summaryHeroTop}>
            <h2 className={`${styles.summaryResult} ${resultClass}`}>
              {resultLabel}
            </h2>
            <div className={styles.summaryHeroActions}>
              {logButton}
              {continueButton}
            </div>
          </div>
          {summary.cardUnlocks?.length || adCardSlots > 0 ? (
            <div className={styles.summaryHoloUnlock}>
              {t("match.view.unlockedCards", { count: summary.cardUnlocks?.length || 0 })}
              {adCardSlots > 0
                ? ` · ${t("match.view.bonusAvailable", { bonus: adCardSlots })}`
                : ""}
              <div className={styles.summaryCardUnlockGrid}>
                {(summary.cardUnlocks || []).map((entry) => {
                  const card = catalogMap[entry.cardId];
                  if (!card) return null;
                  return (
                    <CardFace
                      key={`unlock-${entry.cardId}`}
                      card={card}
                      compact
                      artOnly
                      selected={examCardId === entry.cardId}
                      onClick={() => openCardExam(entry.cardId)}
                    />
                  );
                })}
                {renderAdUnlockSlots(adCardSlots, "card")}
              </div>
            </div>
          ) : null}
          {summary.currencyReward > 0 ? (
            <div className={styles.summaryCurrencyReward}>
              {t("match.view.earned")} {renderCurrencyAmount(summary.currencyReward)}
            </div>
          ) : null}
          {summary.holoUnlocks?.length ||
          summary.holoUnlock ||
          adFoilSlots > 0 ? (
            <div className={styles.summaryHoloUnlock}>
              {(() => {
                const foilUnlocks = summary.holoUnlocks?.length
                  ? summary.holoUnlocks
                  : summary.holoUnlock
                    ? [summary.holoUnlock]
                    : [];
                const boardUnlocks = foilUnlocks.filter(
                  (entry) => entry?.kind === "board",
                );
                const nameColorUnlocks = foilUnlocks.filter(
                  (entry) => entry?.kind === "nameColor",
                );
                const cardFoilUnlocks = foilUnlocks.filter(
                  (entry) =>
                    entry?.kind !== "board" && entry?.kind !== "nameColor",
                );
                const total = foilUnlocks.length;
                return (
                  <>
                    {t("match.view.unlockedFoils", { count: total })}
                    {adFoilSlots > 0
                      ? ` · ${t("match.view.bonusAvailable", { bonus: adFoilSlots })}`
                      : ""}
                    {boardUnlocks.length ? (
                      <div className={styles.summaryBoardUnlockGrid}>
                        {boardUnlocks.map((entry, index) => (
                          <div
                            key={`board-unlock-${entry.boardId}-${index}`}
                            className={styles.summaryBoardUnlock}
                          >
                            <img
                              className={styles.summaryBoardUnlockImage}
                              src={getGameBoardImageUrl(entry.boardId)}
                              alt=""
                              draggable={false}
                            />
                            <span className={styles.summaryBoardUnlockLabel}>
                              {localizedOptionLabel("customize.boardName", {
                                id: entry.boardId,
                                label: entry.label || entry.name || entry.boardId,
                              })}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : null}
                    {nameColorUnlocks.length ? (
                      <div className={styles.summaryNameColorUnlockGrid}>
                        {nameColorUnlocks.map((entry, index) => (
                          <div
                            key={`name-color-unlock-${entry.nameColorId}-${index}`}
                            className={styles.summaryNameColorUnlock}
                          >
                            <span
                              className={getNameColorSwatchClassName(
                                entry.nameColorId,
                              )}
                              style={
                                getNameColorEffect(entry.nameColorId)
                                  ? undefined
                                  : {
                                      background:
                                        entry.hex ||
                                        getNameColorHex(entry.nameColorId),
                                    }
                              }
                              aria-hidden="true"
                            />
                            <span className={styles.summaryBoardUnlockLabel}>
                              {localizedOptionLabel("customize.colorName", {
                                id: entry.nameColorId,
                                label: entry.label || entry.name || entry.nameColorId,
                              })}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : null}
                    {cardFoilUnlocks.length || adFoilSlots > 0 ? (
                      <div className={styles.summaryCardUnlockGrid}>
                        {cardFoilUnlocks.map((entry, index) => {
                          const card = catalogMap[entry.cardId];
                          if (!card) return null;
                          return (
                            <CardFace
                              key={`foil-unlock-${entry.cardId}-${entry.foilEffect}-${index}`}
                              card={card}
                              foilEffect={entry.foilEffect || null}
                              compact
                              artOnly
                              selected={
                                examCardId === entry.cardId &&
                                (examFoilEffect || null) ===
                                  (entry.foilEffect || null)
                              }
                              onClick={() =>
                                openCardExam(
                                  entry.cardId,
                                  entry.foilEffect || null,
                                )
                              }
                            />
                          );
                        })}
                        {renderAdUnlockSlots(adFoilSlots, "foil")}
                      </div>
                    ) : null}
                  </>
                );
              })()}
            </div>
          ) : null}
          {adRewardPromptOpen ? (
            <div
              className={styles.actionPopup}
              onClick={() => {
                if (adRewardBusy) return;
                setAdRewardPromptOpen(false);
              }}
            >
              <ScrollHintSurface
                className={`${styles.actionPopupPanel} ${styles.adRewardPromptPanel}`}
                onClick={(event) => event.stopPropagation()}
              >
                <h3 className={styles.actionPopupTitle}>
                  {t("match.view.adPromptTitle")}
                </h3>
                <div className={styles.detailBlock}>
                  <div className={styles.detailLine}>
                    {t("match.view.adPromptBody")}
                  </div>
                </div>
                <div className={styles.row}>
                  <button
                    type="button"
                    className={`${styles.button} ${styles.buttonPrimary}`}
                    disabled={adRewardBusy}
                    onClick={confirmAdRewardWatch}
                  >
                    {adRewardBusy ? t("match.view.loading") : t("match.view.adWatch")}
                  </button>
                  <button
                    type="button"
                    className={styles.button}
                    disabled={adRewardBusy}
                    onClick={() => setAdRewardPromptOpen(false)}
                  >
                    {t("match.view.notNow")}
                  </button>
                </div>
              </ScrollHintSurface>
            </div>
          ) : null}
          <div className={styles.summaryMeta}>
            <div className={styles.summaryMetaItem}>
              {t("match.view.winner")}{" "}
              {summary.winner ? (
                <button
                  type="button"
                  className={styles.summaryMetaNameButton}
                  onClick={() =>
                    openSummaryDeck(
                      summary.winner === youSeat?.username
                        ? youSeat
                        : themSeat,
                    )
                  }
                >
                  {summary.winner}
                </button>
              ) : (
                <span className={styles.summaryMetaValue}>
                  {draw ? t("match.view.none") : "—"}
                </span>
              )}
            </div>
            <div className={styles.summaryMetaItem}>
              {t("match.view.endedBy")}{" "}
              <span className={styles.summaryMetaValue}>{endedLabel}</span>
            </div>
            <div className={styles.summaryMetaItem}>
              {t("match.view.rounds")}{" "}
              <span className={styles.summaryMetaValue}>
                {summary.rounds ?? 1}
              </span>
            </div>
            <div className={styles.summaryMetaItem}>
              {t("match.view.opponent")}{" "}
              {themSeat ? (
                <button
                  type="button"
                  className={styles.summaryMetaNameButton}
                  onClick={() => openSummaryDeck(themSeat)}
                >
                  {summary.opponent || themSeat.username || "—"}
                  {summary.isCpu ? " (CPU)" : ""}
                </button>
              ) : (
                <span className={styles.summaryMetaValue}>
                  {summary.opponent || "—"}
                  {summary.isCpu ? " (CPU)" : ""}
                </span>
              )}
            </div>
          </div>
          <div className={styles.summaryStats}>
            <div className={styles.summaryStat}>
              <div className={styles.summaryStatLabel}>{t("match.view.damageDealt")}</div>
              <div className={styles.summaryStatValue}>
                {summary.damageDealt ?? 0}
              </div>
            </div>
            <div className={styles.summaryStat}>
              <div className={styles.summaryStatLabel}>{t("match.view.damageTaken")}</div>
              <div className={styles.summaryStatValue}>
                {summary.damageTaken ?? 0}
              </div>
            </div>
            <div className={styles.summaryStat}>
              <div className={styles.summaryStatLabel}>{t("match.view.healed")}</div>
              <div className={styles.summaryStatValue}>
                {summary.healed ?? 0}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.summarySeats}>
          {renderSummarySeat(youSeat, {
            title: t("match.view.you"),
            isCpu: false,
            isYou: true,
          })}
          {renderSummarySeat(themSeat, {
            title: themSeat?.username || summary.opponent || t("match.view.opponent"),
            isCpu: Boolean(summary.isCpu),
            isYou: false,
          })}
        </div>
        <div className={styles.summaryActions}>
          {logButton}
          {continueButton}
        </div>
        {renderSummaryDeckModal()}
        {renderEquipmentPopup()}
        {renderSlottedAbilityExam()}
        {renderEquipExpendConfirmPopup()}
        {renderEquipCounterExamPopup()}
        {renderCombatLog()}
      </div>
    );
  };

  const renderCardExam = () => {
    if (!examCardId) return null;
    const card = catalogMap[examCardId];
    if (!card) return null;
    const unlockedExamHolos =
      view === "collection"
        ? sortFoilEffects(
            (player?.unlockedHolos?.[card.id] || [])
              .map((foil) => resolveFoilEffectId(foil))
              .filter(Boolean)
              .filter((foil, index, list) => list.indexOf(foil) === index),
          )
        : [];
    const examPreviewFoil = resolveFoilEffectId(examFoilEffect);
    const showCollectionHoloSelect = unlockedExamHolos.length > 0;
    const adventureRun = player?.adventureRun;
    const adventureInventoryZone = adventureInventorySelection?.zone;
    const adventureInventoryIndex = adventureInventorySelection?.index;
    const adventureInventoryList =
      adventureInventoryZone === "equipment"
        ? adventureRun?.inventory?.equipmentIds || []
        : adventureInventoryZone === "ability"
          ? adventureRun?.inventory?.abilityIds || []
          : [];
    const adventureInventoryCardId =
      adventureInventoryIndex != null
        ? adventureInventoryList[adventureInventoryIndex]
        : null;
    const adventureDeckList =
      adventureInventoryZone === "equipment"
        ? adventureRun?.deck?.equipmentIds || []
        : adventureInventoryZone === "ability"
          ? adventureRun?.deck?.abilityIds || []
          : [];
    const showAdventureInventoryDiscard =
      view === "adventure" &&
      adventureTab === "inventory" &&
      adventureRun?.phase === "hub" &&
      adventureInventorySelection &&
      adventureInventoryCardId === examCardId &&
      (cardHasType(card, "equipment") || cardHasType(card, "ability"));
    const canAdventureInventoryDiscard =
      showAdventureInventoryDiscard &&
      adventureInventoryList.filter((id) => id === examCardId).length >
        adventureDeckList.filter((id) => id === examCardId).length;
    const closeCardExamModal = () => {
      closeKeywordTip();
      closeCardExam();
      setAdventureInventorySelection(null);
    };
    const isStackedExam = Boolean(
      slotPicker ||
        handMenu ||
        boardMenu ||
        selectedEquipmentId ||
        slottedAbilityExam ||
        adventureMonsterExamId ||
        portraitPreview,
    );
    const examPanelProps = getCardExamV2PanelProps(card);
    const examPanelClass = isStackedExam
      ? styles.stackedCardExamPanel
      : styles.cardExamPanel;
    return (
      <div
        className={`${styles.actionPopup} ${styles.cardExamOverlay}${
          isStackedExam ? ` ${styles.cardExamOverlayRaised}` : ""
        }`}
        onClick={closeCardExamModal}
      >
        <div
          className={`${styles.actionPopupPanel} ${examPanelClass} ${styles.examV2Panel} ${examPanelProps.className}`}
          style={examPanelProps.style}
          onClick={(event) => {
            event.stopPropagation();
            closeKeywordTip();
          }}
        >
          <ScrollHintSurface
            className={`${styles.cardExamBody} ${styles.examV2Body}`}
          >
            {renderCardExamV2Body(card, {
              foilEffect: examPreviewFoil,
              foilOptions: showCollectionHoloSelect
                ? {
                    unlockedHolos: unlockedExamHolos,
                    onFoilChange: setExamFoilEffect,
                  }
                : {},
            })}
          </ScrollHintSurface>
          <div className={`${styles.slotDetailActions} ${styles.examV2Footer}`}>
            {renderCardExamV2SetChip(card)}
            <span className={styles.examV2FooterSpacer} />
            {showAdventureInventoryDiscard ? (
              <button
                type="button"
                className={`${styles.button} ${styles.buttonDanger}`}
                disabled={!canAdventureInventoryDiscard}
                onClick={() => {
                  if (!adventureInventorySelection) return;
                  send({
                    type: "ai:adventureDiscardInventory",
                    equipmentIndices:
                      adventureInventorySelection.zone === "equipment"
                        ? [adventureInventorySelection.index]
                        : [],
                    abilityIndices:
                      adventureInventorySelection.zone === "ability"
                        ? [adventureInventorySelection.index]
                        : [],
                  });
                  closeCardExam();
                }}
              >
                {t("match.view.throwAway")}
              </button>
            ) : null}
            <button
              type="button"
              className={`${styles.button}${
                isStackedExam ? ` ${styles.handExamClose}` : ""
              }`}
              onClick={closeCardExam}
            >
              {t("common.close")}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const sendChatMessage = () => {
    if (!canUseSocialChat) return;
    const text = chatInput.trim();
    if (!text) return;
    chatStickBottomRef.current = true;
    send({ type: "ai:socialChatSend", text });
    setChatInput("");
  };

  const acceptChatOptIn = () => {
    if (!chatEligible) return;
    send({ type: "ai:updateSettings", chatOptIn: true });
    setPlayer((current) =>
      current
        ? {
            ...current,
            settings: {
              ...(current.settings || {}),
              chatOptIn: true,
            },
          }
        : current,
    );
    setChatOptInOpen(false);
    send({ type: "ai:socialInit" });
  };

  const examineSocialPlayer = (targetUsername) => {
    if (!targetUsername) return;
    send({ type: "ai:socialRequestProfile", username: targetUsername });
  };

  const openSocialPlayer = (entry) => {
    if (!entry?.username) return;
    const listed =
      socialPlayers.find((player) => player.username === entry.username) ||
      null;
    setSocialDeckView(null);
    setSocialMenuPlayer(listed ? { ...listed, source: entry.source } : entry);
    setSocialProfile(null);
    examineSocialPlayer(entry.username);
  };

  const openSocialPlayerByUsername = (targetUsername, source) => {
    if (!targetUsername) return;
    openSocialPlayer({
      username: targetUsername,
      online: false,
      status: "Offline",
      source,
    });
  };

  const closeSocialPlayer = () => {
    setSocialMenuPlayer(null);
    setSocialProfile(null);
    setSocialDeckView(null);
  };

  const openReportPlayer = (targetUsername, context = "profile") => {
    if (!targetUsername || targetUsername === username) return;
    setReportTarget({ username: targetUsername, context });
    setReportCategory("");
    setReportDetails("");
    setReportError(null);
    setReportBusy(false);
  };

  const closeReportPlayer = () => {
    if (reportBusy) return;
    setReportTarget(null);
    setReportError(null);
  };

  const submitPlayerReport = () => {
    if (!reportTarget || reportBusy) return;
    if (!reportCategory) {
      setReportError(t("match.view.reportChooseCategory"));
      return;
    }
    setReportError(null);
    setReportBusy(true);
    send({
      type: "ai:socialReportPlayer",
      username: reportTarget.username,
      category: reportCategory,
      details: reportDetails.trim().slice(0, PLAYER_REPORT_DETAILS_MAX),
      context: reportTarget.context,
    });
  };

  const setPlayerBlocked = (targetUsername, blocked) => {
    if (!targetUsername || targetUsername === username) return;
    send({ type: "ai:socialBlockPlayer", username: targetUsername, blocked });
    setPlayer((current) => {
      if (!current) return current;
      const list = Array.isArray(current.blockedUsernames)
        ? current.blockedUsernames.filter((entry) => entry !== targetUsername)
        : [];
      return {
        ...current,
        blockedUsernames: blocked ? [...list, targetUsername] : list,
      };
    });
  };

  const turnChatOff = () => {
    send({ type: "ai:updateSettings", chatOptIn: false });
    setPlayer((current) =>
      current
        ? {
            ...current,
            settings: {
              ...(current.settings || {}),
              chatOptIn: false,
            },
          }
        : current,
    );
    setSocialMessages([]);
    showToast(t("match.view.chatTurnedOff"));
  };

  const renderPlayerSafetyActions = (targetUsername, context) => {
    if (!targetUsername || targetUsername === username) return null;
    const isBlocked = blockedUsernameSet.has(targetUsername);
    return (
      <>
        <button
          type="button"
          className={`${styles.button} ${styles.buttonDanger}`}
          onClick={() => openReportPlayer(targetUsername, context)}
        >
          {t("social.report")}
        </button>
        <button
          type="button"
          className={styles.button}
          onClick={() => setPlayerBlocked(targetUsername, !isBlocked)}
        >
          {isBlocked ? t("settings.blocked.unblock") : t("social.block")}
        </button>
      </>
    );
  };

  const challengeSocialPlayer = (targetUsername) => {
    if (!targetUsername || targetUsername === username) return;
    send({ type: "ai:socialChallenge", username: targetUsername });
  };

  const spectateSocialPlayer = (targetUsername) => {
    if (!targetUsername || targetUsername === username) return;
    send({ type: "ai:spectateStart", username: targetUsername });
  };

  const respondToChallenge = (challengeId, accepted, forfeitCpu = false) => {
    send({
      type: "ai:socialChallengeRespond",
      challengeId,
      accepted,
      forfeitCpu,
    });
    if (!accepted) {
      setIncomingChallenge(null);
      setChallengeForfeitPrompt(null);
    }
  };

  const acceptIncomingChallenge = () => {
    if (!incomingChallenge?.id) return;
    if (
      match &&
      match.phase !== "ended" &&
      match.isCpu &&
      !match.spectating
    ) {
      setChallengeForfeitPrompt({
        challengeId: incomingChallenge.id,
        from: incomingChallenge.from,
      });
      setIncomingChallenge(null);
      return;
    }
    respondToChallenge(incomingChallenge.id, true, false);
    setIncomingChallenge(null);
  };

  const setShowQuickUseSetting = (next) => {
    const enabled = next === true;
    setPlayer((current) =>
      current
        ? {
            ...current,
            settings: {
              ...(current.settings || {}),
              showQuickUse: enabled,
            },
          }
        : current,
    );
    send({ type: "ai:updateSettings", showQuickUse: enabled });
  };

  const setShowHintsSetting = (next) => {
    const enabled = next === true;
    setPlayer((current) =>
      current
        ? {
            ...current,
            settings: {
              ...(current.settings || {}),
              showHints: enabled,
            },
          }
        : current,
    );
    send({ type: "ai:updateSettings", showHints: enabled });
    if (!enabled) {
      setActiveGameHint(null);
    }
  };

  const setMatchGuidesSetting = (next) => {
    const value = normalizeMatchGuides(next);
    setPlayer((current) =>
      current
        ? {
            ...current,
            settings: {
              ...(current.settings || {}),
              matchGuides: value,
            },
          }
        : current,
    );
    send({ type: "ai:updateSettings", matchGuides: value });
  };

  const setShowCardNamesSetting = (next) => {
    const enabled = next === true;
    setPlayer((current) =>
      current
        ? {
            ...current,
            settings: {
              ...(current.settings || {}),
              showCardNames: enabled,
            },
          }
        : current,
    );
    send({ type: "ai:updateSettings", showCardNames: enabled });
  };

  const setLeftHandedSetting = (next) => {
    const enabled = next === true;
    setPlayer((current) =>
      current
        ? {
            ...current,
            settings: {
              ...(current.settings || {}),
              leftHanded: enabled,
            },
          }
        : current,
    );
    send({ type: "ai:updateSettings", leftHanded: enabled });
  };

  const setMusicSetting = (next) => {
    const enabled = next === true;
    setAudioMusicEnabled(enabled);
    setPlayer((current) =>
      current
        ? {
            ...current,
            settings: {
              ...(current.settings || {}),
              music: enabled,
            },
          }
        : current,
    );
    send({ type: "ai:updateSettings", music: enabled });
  };

  const setSoundSetting = (next) => {
    const enabled = next === true;
    setAudioSoundEnabled(enabled);
    setPlayer((current) =>
      current
        ? {
            ...current,
            settings: {
              ...(current.settings || {}),
              sound: enabled,
            },
          }
        : current,
    );
    send({ type: "ai:updateSettings", sound: enabled });
  };

  const setShowWorkshopSetting = (next) => {
    const enabled = next === true;
    setPlayer((current) =>
      current
        ? {
            ...current,
            settings: {
              ...(current.settings || {}),
              showWorkshop: enabled,
            },
          }
        : current,
    );
    send({ type: "ai:updateSettings", showWorkshop: enabled });
  };

  const toggleGameHint = (hintId, anchorEl) => {
    setActiveGameHint((current) =>
      current?.id === hintId ? null : hintId ? { id: hintId, anchorEl } : null,
    );
  };

  const setThemeSetting = (next) => {
    const themeValue = next === "light" ? "light" : "dark";
    setPlayer((current) =>
      current
        ? {
            ...current,
            settings: {
              ...(current.settings || {}),
              theme: themeValue,
            },
          }
        : current,
    );
    send({ type: "ai:updateSettings", theme: themeValue });
  };

  const setForceFullscreenSetting = (next) => {
    const enabled = next === true;
    setPlayer((current) =>
      current
        ? {
            ...current,
            settings: {
              ...(current.settings || {}),
              forceFullscreen: enabled,
            },
          }
        : current,
    );
    send({ type: "ai:updateSettings", forceFullscreen: enabled });
    if (enabled) {
      enterImmersiveMode(shellRef.current);
    } else {
      exitAppFullscreen();
    }
  };

  const setFoilEffectsSetting = (next) => {
    const foilEffects = normalizeFoilEffectsQuality(next);
    setPlayer((current) =>
      current
        ? {
            ...current,
            settings: {
              ...(current.settings || {}),
              foilEffects,
            },
          }
        : current,
    );
    send({ type: "ai:updateSettings", foilEffects });
  };

  const setGraphicsQualitySetting = (next) => {
    const value = normalizeGraphicsQuality(next);
    if (value === "auto") {
      clearLearnedGraphicsTier();
      setAutoGraphicsTier(resolveAutoGraphicsTier());
    }
    saveGraphicsQuality(value);
    setGraphicsQuality(value);
  };

  const setToastsSetting = (next) => {
    const toasts = normalizeToastsMode(next);
    toastsModeRef.current = toasts;
    if (toasts === "off") {
      toastQueueRef.current = [];
      clearToastTimers();
      toastActiveRef.current = false;
      toastExitingRef.current = false;
      setToast(null);
      completionToastQueueRef.current = [];
      if (completionToastTimerRef.current) {
        window.clearTimeout(completionToastTimerRef.current);
        completionToastTimerRef.current = null;
      }
      setCompletionToast(null);
    }
    setPlayer((current) =>
      current
        ? {
            ...current,
            settings: {
              ...(current.settings || {}),
              toasts,
            },
          }
        : current,
    );
    send({ type: "ai:updateSettings", toasts });
  };

  const setToastSpeedSetting = (next) => {
    const speed = normalizeToastSpeed(next);
    toastSpeedRef.current = speed;
    setPlayer((current) =>
      current
        ? {
            ...current,
            settings: {
              ...(current.settings || {}),
              toastSpeed: speed,
            },
          }
        : current,
    );
    send({ type: "ai:updateSettings", toastSpeed: speed });
  };

  const setHandDisplaySetting = (next) => {
    const mode = next === "dynamic" ? "dynamic" : "drawer";
    setPlayer((current) =>
      current
        ? {
            ...current,
            settings: {
              ...(current.settings || {}),
              handDisplay: mode,
            },
          }
        : current,
    );
    send({ type: "ai:updateSettings", handDisplay: mode });
  };

  const setLanguageSetting = (next) => {
    const nextLanguage = normalizeLanguage(next);
    setStoredLanguage(nextLanguage);
    setPlayer((current) =>
      current
        ? {
            ...current,
            settings: {
              ...(current.settings || {}),
              language: nextLanguage,
            },
          }
        : current,
    );
    send({ type: "ai:updateSettings", language: nextLanguage });
  };

  const renderCustomize = () => {
    const unlockedBoards = normalizeUnlockedBoards(player?.unlockedBoards);
    const selectedBoard = resolvePlayerBoardId(player);
    const unlockedNameColors = normalizeUnlockedNameColors(
      player?.unlockedNameColors,
    );
    const selectedNameColor = resolvePlayerNameColorId(player);
    const selectedNameColorText = getNameColorTextProps(selectedNameColor);
    // Purchases need a second tap on the same item within a few seconds.
    const confirmPurchase = (key, purchase) => {
      if (customizePurchaseArmed === key) {
        setCustomizePurchaseArmed(null);
        purchase();
        return;
      }
      setCustomizePurchaseArmed(key);
    };
    const renderUnlockCost = (key, cost) => (
      <span
        className={`${styles.customizeUnlockCost} ${
          customizePurchaseArmed === key ? styles.customizeUnlockConfirm : ""
        }`}
      >
        {customizePurchaseArmed === key
          ? t("customize.confirmPurchase")
          : renderCurrencyAmount(cost)}
      </span>
    );
    return (
      <div className={`${styles.panel} ${styles.customizePanel}`}>
        {renderCurrencyBalance()}
        <h2 className={styles.panelTitle}>{t("nav.customize")}</h2>
        <div className={styles.customizePreviewWrap}>
          <img
            className={styles.customizeBoardPreview}
            src={getGameBoardImageUrl(selectedBoard)}
            alt=""
            draggable={false}
          />
        </div>
        <div className={styles.customizeSection}>
          <h3 className={styles.customizeSectionTitle}>{t("customize.board")}</h3>
          <div className={styles.boardPicker}>
            {BOARD_LIST.map((board) => {
              const unlocked = unlockedBoards.includes(board.id);
              const canBuy = !unlocked && !board.free;
              const boardLabel = localizedOptionLabel("customize.boardName", board);
              return (
                <button
                  key={board.id}
                  type="button"
                  className={`${styles.boardOption} ${
                    selectedBoard === board.id ? styles.boardOptionActive : ""
                  } ${unlocked || canBuy ? "" : styles.boardOptionLocked} ${
                    customizePurchaseArmed === `board:${board.id}`
                      ? styles.customizeOptionArmed
                      : ""
                  }`}
                  disabled={!unlocked && !canBuy}
                  onClick={() => {
                    if (unlocked) {
                      send({ type: "ai:updateBoard", board: board.id });
                      return;
                    }
                    if (!canBuy) return;
                    confirmPurchase(`board:${board.id}`, () =>
                      send({ type: "ai:purchaseBoard", board: board.id }),
                    );
                  }}
                  aria-pressed={selectedBoard === board.id}
                  title={
                    unlocked
                      ? boardLabel
                      : canBuy
                        ? t("customize.unlockFor", {
                            name: boardLabel,
                            cost: BOARD_UNLOCK_CURRENCY_COST,
                          })
                        : `${boardLabel}${t("customize.lockedSuffix")}`
                  }
                >
                  <img
                    className={styles.boardOptionImage}
                    src={getGameBoardImageUrl(board.id)}
                    alt=""
                    draggable={false}
                  />
                  <span className={styles.portraitOptionLabel}>
                    {boardLabel}
                    {unlocked ? "" : t("customize.lockedSuffix")}
                  </span>
                  {!unlocked && !board.free
                    ? renderUnlockCost(
                        `board:${board.id}`,
                        BOARD_UNLOCK_CURRENCY_COST,
                      )
                    : null}
                </button>
              );
            })}
          </div>
        </div>
        <div className={styles.customizeSection}>
          <h3 className={styles.customizeSectionTitle}>{t("customize.nameColor")}</h3>
          <div
            className={[
              styles.nameColorPreview,
              selectedNameColorText.className,
            ]
              .filter(Boolean)
              .join(" ")}
            style={selectedNameColorText.style}
          >
            {player?.username || t("common.player")}
          </div>
          <div className={styles.nameColorPicker}>
            {NAME_COLOR_LIST.map((color) => {
              const unlocked = unlockedNameColors.includes(color.id);
              const canBuy = !unlocked && !color.free;
              const colorLabel = localizedOptionLabel("customize.colorName", color);
              const unlockCost = color.effect
                ? HOLO_NAME_COLOR_UNLOCK_CURRENCY_COST
                : NAME_COLOR_UNLOCK_CURRENCY_COST;
              return (
                <button
                  key={color.id}
                  type="button"
                  className={`${styles.nameColorOption} ${
                    selectedNameColor === color.id
                      ? styles.nameColorOptionActive
                      : ""
                  } ${unlocked || canBuy ? "" : styles.nameColorOptionLocked} ${
                    customizePurchaseArmed === `nameColor:${color.id}`
                      ? styles.customizeOptionArmed
                      : ""
                  }`}
                  disabled={!unlocked && !canBuy}
                  onClick={() => {
                    if (unlocked) {
                      send({
                        type: "ai:updateNameColor",
                        nameColor: color.id,
                      });
                      return;
                    }
                    if (!canBuy) return;
                    confirmPurchase(`nameColor:${color.id}`, () =>
                      send({
                        type: "ai:purchaseNameColor",
                        nameColor: color.id,
                      }),
                    );
                  }}
                  aria-pressed={selectedNameColor === color.id}
                  title={
                    unlocked
                      ? colorLabel
                      : canBuy
                        ? t("customize.unlockFor", { name: colorLabel, cost: unlockCost })
                        : `${colorLabel}${t("customize.lockedSuffix")}`
                  }
                >
                  <span
                    className={getNameColorSwatchClassName(color.id)}
                    style={
                      color.effect ? undefined : { background: color.hex }
                    }
                    aria-hidden="true"
                  />
                  <span className={styles.portraitOptionLabel}>
                    {colorLabel}
                    {unlocked ? "" : t("customize.lockedSuffix")}
                  </span>
                  {!unlocked && !color.free
                    ? renderUnlockCost(`nameColor:${color.id}`, unlockCost)
                    : null}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderMarketplace = () => {
    const market = player?.marketplace || null;
    const cards = Array.isArray(market?.cards) ? market.cards : [];
    const foils = Array.isArray(market?.foils) ? market.foils : [];
    const refreshLabel = formatMarketplaceRefresh(market?.refreshesAt);
    const confirmCard = marketplaceBuyConfirm
      ? catalogMap[marketplaceBuyConfirm.cardId]
      : null;
    const confirmCost = marketplaceBuyConfirm?.cost;
    const confirmFoilEffect = marketplaceBuyConfirm?.foilEffect || null;
    const canAffordConfirm =
      confirmCost != null &&
      Math.max(0, Math.floor(Number(player?.currency) || 0)) >= confirmCost;
    return (
      <div className={styles.panel}>
        {renderCurrencyBalance()}
        <h2 className={`${styles.panelTitle} ${styles.marketplaceTitle}`}>
          {t("nav.marketplace")}
        </h2>
        {refreshLabel ? (
          <div className={styles.marketplaceRefresh}>
            {t("marketplace.refreshesIn", { time: refreshLabel })}
          </div>
        ) : null}
        <div className={styles.marketplaceSection}>
          <h3 className={styles.marketplaceSectionTitle}>{t("marketplace.cards")}</h3>
          {cards.length ? (
            <div className={`${styles.cardGrid} ${styles.marketplaceGrid}`}>
              {cards.map((entry) => {
                const purchased = Boolean(entry.purchased);
                const card = catalogMap[entry.cardId];
                if (!purchased && !card) return null;
                const cost =
                  entry.cost != null
                    ? entry.cost
                    : MARKETPLACE_CARD_CURRENCY_COST;
                return (
                  <div
                    key={`market-card-${entry.cardId}`}
                    className={`${styles.marketplaceOffer}${
                      purchased ? ` ${styles.marketplaceOfferSold}` : ""
                    }`}
                  >
                    {purchased || !card ? (
                      <div
                        className={styles.marketplaceSoldFace}
                        aria-label={t("marketplace.sold")}
                      >
                        <span>{t("marketplace.sold")}</span>
                      </div>
                    ) : (
                      <CardFace
                        card={card}
                        fitText={false}
                        selected={
                          examCardId === card.id && !examFoilEffect
                        }
                        onClick={() => openCardExam(card.id)}
                      />
                    )}
                    <button
                      type="button"
                      className={styles.button}
                      disabled={purchased}
                      onClick={() => {
                        if (purchased || !card) return;
                        setMarketplaceBuyConfirm({
                          kind: "card",
                          cardId: entry.cardId,
                          cost,
                        });
                      }}
                    >
                      {purchased ? t("marketplace.sold") : <>{t("marketplace.buy")} {renderCurrencyAmount(cost)}</>}
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={styles.muted}>{t("marketplace.noCards")}</div>
          )}
        </div>
        <div
          className={`${styles.marketplaceSection} ${styles.marketplaceSectionFoil}`}
        >
          <h3 className={styles.marketplaceSectionTitle}>{t("marketplace.foils")}</h3>
          {foils.length ? (
            <div className={`${styles.cardGrid} ${styles.marketplaceGrid}`}>
              {foils.map((entry, foilIndex) => {
                const purchased = Boolean(entry.purchased);
                const card = catalogMap[entry.cardId];
                if (!purchased && !card) return null;
                const cost =
                  entry.cost != null
                    ? entry.cost
                    : MARKETPLACE_FOIL_CURRENCY_COST;
                const foilEffect = entry.foilEffect || null;
                return (
                  <div
                    key={`market-foil-${entry.cardId}-${entry.foilEffect}`}
                    className={`${styles.marketplaceOffer}${
                      purchased ? ` ${styles.marketplaceOfferSold}` : ""
                    }`}
                  >
                    {purchased || !card ? (
                      <div
                        className={styles.marketplaceSoldFace}
                        aria-label={t("marketplace.sold")}
                      >
                        <span>{t("marketplace.sold")}</span>
                      </div>
                    ) : (
                      <DeferredFoilCardFace
                        revealIndex={foilIndex}
                        card={card}
                        fitText={false}
                        foilEffect={foilEffect}
                        foilQuality="maximum"
                        foilAnimateIdle
                        selected={
                          examCardId === card.id &&
                          (examFoilEffect || null) === (foilEffect || null)
                        }
                        onClick={() => openCardExam(card.id, foilEffect)}
                      />
                    )}
                    {!purchased ? (
                      <div className={styles.marketplaceOfferLabel}>
                        {entry.label || entry.name || entry.cardId}
                      </div>
                    ) : null}
                    <button
                      type="button"
                      className={styles.button}
                      disabled={purchased}
                      onClick={() => {
                        if (purchased || !card) return;
                        setMarketplaceBuyConfirm({
                          kind: "foil",
                          cardId: entry.cardId,
                          foilEffect: entry.foilEffect,
                          cost,
                          label: entry.label || entry.name || entry.cardId,
                        });
                      }}
                    >
                      {purchased ? t("marketplace.sold") : <>{t("marketplace.buy")} {renderCurrencyAmount(cost)}</>}
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={styles.muted}>{t("marketplace.noFoils")}</div>
          )}
        </div>
        {marketplaceBuyConfirm && confirmCard ? (
          <div
            className={styles.actionPopup}
            onClick={() => setMarketplaceBuyConfirm(null)}
          >
            <ScrollHintSurface
              className={`${styles.actionPopupPanel} ${styles.marketplaceBuyConfirmPanel}`}
              onClick={(event) => event.stopPropagation()}
            >
              <div className={styles.marketplaceBuyConfirmCard}>
                <CardFace
                  card={confirmCard}
                  fitText={false}
                  foilEffect={confirmFoilEffect}
                  foilQuality={confirmFoilEffect ? "maximum" : undefined}
                  foilAnimateIdle={Boolean(confirmFoilEffect)}
                />
              </div>
              {marketplaceBuyConfirm.kind === "foil" &&
              marketplaceBuyConfirm.label ? (
                <div className={styles.marketplaceOfferLabel}>
                  {marketplaceBuyConfirm.label}
                </div>
              ) : null}
              {!canAffordConfirm ? (
                <div className={styles.muted}>{t("marketplace.notEnough")}</div>
              ) : null}
              <div className={`${styles.row} ${styles.marketplaceBuyConfirmActions}`}>
                <button
                  type="button"
                  className={`${styles.button} ${styles.buttonPrimary}`}
                  disabled={!canAffordConfirm}
                  onClick={() => {
                    if (!canAffordConfirm) return;
                    if (marketplaceBuyConfirm.kind === "foil") {
                      send({
                        type: "ai:marketplaceBuyFoil",
                        cardId: marketplaceBuyConfirm.cardId,
                        foilEffect: marketplaceBuyConfirm.foilEffect,
                      });
                    } else {
                      send({
                        type: "ai:marketplaceBuyCard",
                        cardId: marketplaceBuyConfirm.cardId,
                      });
                    }
                    setMarketplaceBuyConfirm(null);
                  }}
                >
                  {t("marketplace.confirmBuy")} {renderCurrencyAmount(confirmCost)}
                </button>
                <button
                  type="button"
                  className={styles.button}
                  onClick={() => setMarketplaceBuyConfirm(null)}
                >
                  {t("common.cancel")}
                </button>
              </div>
            </ScrollHintSurface>
          </div>
        ) : null}
      </div>
    );
  };

  const formatDailyTimer = (deadline) => {
    const remaining = Math.max(
      0,
      Math.ceil((Number(deadline) - dailiesNow) / 1000),
    );
    const hours = Math.floor(remaining / 3600);
    const minutes = Math.floor((remaining % 3600) / 60);
    const seconds = String(remaining % 60).padStart(2, "0");
    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, "0")}:${seconds}`;
    }
    return `${minutes}:${seconds}`;
  };

  const localizeDailyLabel = (daily) => {
    const fallback = daily?.label || "";
    if (!daily?.kind || language === DEFAULT_LANGUAGE) return fallback;
    const target = Math.max(1, Math.floor(Number(daily.target) || 0));
    const variant = String(daily.variant || "");
    const term = (key) => {
      const value = t(key);
      return value === key ? null : value;
    };
    const humanize = (value) =>
      String(value || "")
        .replace(/_/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .split(" ")
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
    const cardName = () => catalogMap[variant]?.name || humanize(variant);
    switch (daily.kind) {
      case "dealDamage": {
        const type = term(`adventure.damageType.${variant}`);
        return type ? t("dailies.dealDamage", { target, type }) : fallback;
      }
      case "applyAilment": {
        const ailment = term(`dailies.ailment.${variant}`);
        return ailment ? t("dailies.applyAilment", { target, ailment }) : fallback;
      }
      case "gainResource": {
        const attribute = term(`common.attribute.${variant}`);
        return attribute
          ? t("dailies.gainResource", { target, attribute })
          : fallback;
      }
      case "playCards": {
        const [group, value] = variant.split(":");
        if (group === "category") {
          return t(
            value === "equipment"
              ? "dailies.playEquipment"
              : "dailies.playAbility",
            { target },
          );
        }
        if (group === "attribute") {
          const attribute = term(`common.attribute.${value}`);
          return attribute
            ? t("dailies.playAttribute", { target, attribute })
            : fallback;
        }
        if (group === "kind") {
          const kind = term(`deck.kind.${value}`);
          return kind ? t("dailies.playKind", { target, kind }) : fallback;
        }
        return t("dailies.playTag", { target, tag: humanize(value) });
      }
      case "winWithAspect":
        return t("dailies.winWithAspect", { count: target, name: cardName() });
      case "expendDamage":
        return t("dailies.expendDamage", { target });
      case "expendEffect":
        return t("dailies.expendEffect", { target });
      case "activateAbility":
        return t("dailies.activateAbility", { target });
      case "invokeCards":
        return t("dailies.invokeCards", { target });
      case "activateCost1":
      case "activateCost2":
      case "activateCost3":
        return t("dailies.activateCard", { target, name: cardName() });
      default:
        return fallback;
    }
  };

  const renderDailyIcon = (daily) => {
    const icon = resolveDailyIcon(daily);
    if (!icon?.src) return null;
    if (icon.mask) {
      return <MaskIcon src={icon.src} className={styles.dailyIconMask} />;
    }
    return (
      <img
        src={icon.src}
        alt=""
        className={styles.dailyIcon}
        draggable={false}
      />
    );
  };

  const renderAchievementProgress = (value, total) => {
    const safeTotal = Math.max(0, Math.floor(Number(total) || 0));
    const safeValue = Math.min(
      safeTotal,
      Math.max(0, Math.floor(Number(value) || 0)),
    );
    const percent = safeTotal
      ? Math.round((safeValue / safeTotal) * 100)
      : 0;
    return (
      <span
        className={`${styles.dailyProgressTrack} ${styles.achievementProgressTrack}`}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={safeTotal}
        aria-valuenow={safeValue}
      >
        <span
          className={styles.dailyProgressFill}
          style={{ width: `${percent}%` }}
        />
        <span className={styles.dailyProgressText}>
          {safeValue} / {safeTotal}
        </span>
      </span>
    );
  };

  const renderAchievementsModal = (categories, openCategory, readyCount) => {
    if (!achievementsModal) return null;
    const close = () => setAchievementsModal(null);
    return (
      <div className={styles.actionPopup} onClick={close}>
        <ScrollHintSurface
          className={`${styles.actionPopupPanel} ${styles.achievementsModalPanel}`}
          onClick={(event) => event.stopPropagation()}
        >
          <div className={styles.achievementsModalHeader}>
            {openCategory ? (
              <button
                type="button"
                className={styles.button}
                onClick={() => setAchievementsModal({ categoryId: null })}
              >
                {t("common.back")}
              </button>
            ) : null}
            <h3 className={styles.achievementsModalTitle}>
              {openCategory ? openCategory.name : t("achievements.title")}
            </h3>
            <button
              type="button"
              className={styles.dailyRemoveButton}
              onClick={close}
              aria-label={t("achievements.close")}
              title={t("common.close")}
            >
              ✕
            </button>
          </div>
          {!openCategory ? (
            categories.length ? (
              <div className={styles.achievementCategoryList}>
                {categories.map((category) => {
                  const ready = readyCount(category);
                  return (
                    <button
                      key={category.id}
                      type="button"
                      className={`${styles.achievementCategoryButton}${
                        category.collected
                          ? ` ${styles.achievementCategoryButtonDone}`
                          : ""
                      }`}
                      onClick={() =>
                        setAchievementsModal({ categoryId: category.id })
                      }
                    >
                      <span className={styles.achievementCategoryHeader}>
                        <span className={styles.achievementCategoryName}>
                          {category.name}
                        </span>
                        {ready > 0 ? (
                          <span className={styles.achievementReadyBadge}>
                            {t("achievements.ready", { n: ready })}
                          </span>
                        ) : null}
                      </span>
                      {renderAchievementProgress(
                        category.collectedCount,
                        category.total,
                      )}
                      <span className={styles.dailyReward}>
                        {category.collected
                          ? t("achievements.completionRewardCollected")
                          : t("achievements.completionReward")}{" "}
                        {renderCurrencyAmount(category.reward)}
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className={styles.muted}>{t("achievements.none")}</div>
            )
          ) : (
            <>
              <div className={styles.achievementCategorySummary}>
                <div className={styles.achievementsHint}>
                  {openCategory.description}
                </div>
                {renderAchievementProgress(
                  openCategory.collectedCount,
                  openCategory.total,
                )}
                <div className={styles.dailyFooter}>
                  <span className={styles.dailyReward}>
                    {t("achievements.completionReward")}{" "}
                    {renderCurrencyAmount(openCategory.reward)}
                  </span>
                  {openCategory.collected ? (
                    <span className={styles.achievementStatus}>{t("achievements.collected")}</span>
                  ) : (
                    <button
                      type="button"
                      className={`${styles.button} ${styles.buttonPrimary}`}
                      disabled={!openCategory.complete}
                      title={
                        openCategory.complete
                          ? t("achievements.collectCompletionTitle")
                          : t("achievements.collectAllFirst")
                      }
                      onClick={() => {
                        playSfx("uiSwitch");
                        send({
                          type: "ai:achievementCategoryCollect",
                          categoryId: openCategory.id,
                        });
                      }}
                    >
                      {t("achievements.collect")} {renderCurrencyAmount(openCategory.reward)}
                    </button>
                  )}
                </div>
              </div>
              <div className={styles.achievementList}>
                {(openCategory.achievements || []).map((achievement) => (
                  <div
                    key={achievement.id}
                    className={`${styles.achievementRow}${
                      achievement.completed
                        ? ` ${styles.achievementRowComplete}`
                        : ""
                    }`}
                  >
                    <span className={styles.achievementRowText}>
                      <span className={styles.achievementRowName}>
                        {achievement.name}
                      </span>
                      <span className={styles.achievementRowDescription}>
                        {achievement.description}
                      </span>
                    </span>
                    {achievement.collected ? (
                      <span className={styles.achievementStatus}>
                        {t("achievements.collected")}
                      </span>
                    ) : achievement.completed ? (
                      <button
                        type="button"
                        className={`${styles.button} ${styles.buttonPrimary}`}
                        onClick={() => {
                          playSfx("uiSwitch");
                          send({
                            type: "ai:achievementCollect",
                            achievementId: achievement.id,
                          });
                        }}
                      >
                        {t("achievements.collect")} {renderCurrencyAmount(achievement.reward)}
                      </button>
                    ) : (
                      <span className={styles.achievementStatus}>
                        {renderCurrencyAmount(achievement.reward)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </ScrollHintSurface>
      </div>
    );
  };

  const renderAchievements = () => {
    const dailies = player?.dailies || null;
    const slots = Array.isArray(dailies?.slots) ? dailies.slots : [];
    const rerollCost = Math.max(
      0,
      Math.floor(Number(dailies?.rerollCost ?? 1) || 0),
    );
    const currency = Math.max(0, Math.floor(Number(player?.currency) || 0));
    const achievementCategories = Array.isArray(
      player?.achievements?.categories,
    )
      ? player.achievements.categories
      : [];
    const categoryReadyCount = (category) =>
      (category.achievements || []).filter(
        (entry) => entry.completed && !entry.collected,
      ).length + (category.complete && !category.collected ? 1 : 0);
    const achievementTotal = achievementCategories.reduce(
      (sum, category) => sum + (Number(category.total) || 0),
      0,
    );
    const achievementCollectedTotal = achievementCategories.reduce(
      (sum, category) => sum + (Number(category.collectedCount) || 0),
      0,
    );
    const achievementReadyTotal = achievementCategories.reduce(
      (sum, category) => sum + categoryReadyCount(category),
      0,
    );
    const openCategory = achievementsModal?.categoryId
      ? achievementCategories.find(
          (category) => category.id === achievementsModal.categoryId,
        ) || null
      : null;
    return (
      <div className={styles.panel}>
        {renderCurrencyBalance()}
        <h2 className={`${styles.panelTitle} ${styles.achievementsTitle}`}>
          {t("nav.achievements")}
        </h2>
        <div className={styles.achievementsSection}>
          <h3 className={styles.achievementsSectionTitle}>{t("achievements.dailies")}</h3>
          <p className={styles.achievementsHint}>
            {t("achievements.dailiesHint")}
          </p>
          {slots.length ? (
            <div className={styles.dailyList}>
              {slots.map((slot, index) => {
                if (!slot?.kind) {
                  const refillAt = Number(slot?.refillAt) || 0;
                  return (
                    <div
                      key={`daily-empty-${index}`}
                      className={`${styles.dailyCard} ${styles.dailyCardEmpty}`}
                    >
                      <span className={styles.dailyEmptyLabel}>{t("achievements.emptySlot")}</span>
                      <span className={styles.dailyTimer}>
                        {refillAt > dailiesNow
                          ? t("achievements.untilNewDaily", {
                              time: formatDailyTimer(refillAt),
                            })
                          : t("achievements.newDailyArriving")}
                      </span>
                    </div>
                  );
                }
                const target = Math.max(1, Math.floor(Number(slot.target) || 1));
                const progress = Math.min(
                  target,
                  Math.max(0, Math.floor(Number(slot.progress) || 0)),
                );
                const completed = Boolean(slot.completed) || progress >= target;
                const percent = Math.round((progress / target) * 100);
                const expiresAt = Number(slot.expiresAt) || 0;
                const showExpiry = !completed && progress <= 0 && expiresAt > 0;
                const armedAction =
                  dailyActionArmed?.id === slot.id
                    ? dailyActionArmed.action
                    : null;
                const dailyLabel = localizeDailyLabel(slot);
                return (
                  <div
                    key={slot.id}
                    className={`${styles.dailyCard}${
                      completed ? ` ${styles.dailyCardComplete}` : ""
                    }`}
                  >
                    <div className={styles.dailyHeader}>
                      <span className={styles.dailyIconWrap}>
                        {renderDailyIcon(slot)}
                      </span>
                      <span className={styles.dailyLabel}>{dailyLabel}</span>
                    </div>
                    <div
                      className={styles.dailyProgressTrack}
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuemax={target}
                      aria-valuenow={progress}
                    >
                      <span
                        className={styles.dailyProgressFill}
                        style={{ width: `${percent}%` }}
                      />
                      <span className={styles.dailyProgressText}>
                        {progress} / {target}
                      </span>
                    </div>
                    <div className={styles.dailyFooter}>
                      <span className={styles.dailyReward}>
                        {t("draft.reward")} {renderCurrencyAmount(slot.reward)}
                      </span>
                      {showExpiry ? (
                        <span className={styles.dailyTimer}>
                          {t("achievements.untilExpired", {
                            time: formatDailyTimer(expiresAt),
                          })}
                        </span>
                      ) : null}
                      {completed ? (
                        <button
                          type="button"
                          className={`${styles.button} ${styles.buttonPrimary}`}
                          onClick={() => {
                            playSfx("uiSwitch");
                            send({ type: "ai:dailyCollect", dailyId: slot.id });
                          }}
                        >
                          {t("achievements.collect")} {renderCurrencyAmount(slot.reward)}
                        </button>
                      ) : null}
                    </div>
                    {!completed ? (
                      <div className={styles.dailyActions}>
                        <button
                          type="button"
                          className={`${styles.dailyRemoveButton}${
                            armedAction === "remove"
                              ? ` ${styles.dailyActionArmed}`
                              : ""
                          }`}
                          onClick={() => {
                            if (armedAction === "remove") {
                              setDailyActionArmed(null);
                              send({ type: "ai:dailyRemove", dailyId: slot.id });
                              return;
                            }
                            setDailyActionArmed({ id: slot.id, action: "remove" });
                          }}
                          aria-label={
                            armedAction === "remove"
                              ? t("achievements.removeAgainAria", { label: dailyLabel })
                              : t("achievements.removeAria", { label: dailyLabel })
                          }
                          title={
                            armedAction === "remove"
                              ? t("achievements.removeAgain")
                              : t("achievements.remove")
                          }
                        >
                          {armedAction === "remove" ? t("match.popup.confirm") : "✕"}
                        </button>
                        {!slot.rerolled ? (
                          <button
                            type="button"
                            className={`${styles.dailyRerollButton}${
                              armedAction === "reroll"
                                ? ` ${styles.dailyActionArmed}`
                                : ""
                            }`}
                            disabled={currency < rerollCost}
                            aria-label={
                              armedAction === "reroll"
                                ? t("achievements.rerollAgainAria", {
                                    cost: rerollCost,
                                    label: dailyLabel,
                                  })
                                : t("achievements.rerollAria", {
                                    cost: rerollCost,
                                    label: dailyLabel,
                                  })
                            }
                            title={
                              currency < rerollCost
                                ? t("marketplace.notEnough")
                                : armedAction === "reroll"
                                  ? t("achievements.rerollAgain")
                                  : t("achievements.reroll")
                            }
                            onClick={() => {
                              if (armedAction === "reroll") {
                                setDailyActionArmed(null);
                                send({ type: "ai:dailyReroll", dailyId: slot.id });
                                return;
                              }
                              setDailyActionArmed({ id: slot.id, action: "reroll" });
                            }}
                          >
                            {armedAction === "reroll" ? (
                              t("match.popup.confirm")
                            ) : (
                              <MaskIcon
                                src={DAILY_REROLL_ICON_URL}
                                className={styles.dailyRerollIcon}
                              />
                            )}
                            {renderCurrencyAmount(rerollCost)}
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={styles.muted}>{t("achievements.loadingDailies")}</div>
          )}
        </div>
        <div className={styles.achievementsSection}>
          <h3 className={styles.achievementsSectionTitle}>{t("achievements.title")}</h3>
          <button
            type="button"
            className={styles.achievementCategoryButton}
            onClick={() => {
              playSfx("uiSwitch");
              setAchievementsModal({ categoryId: null });
            }}
          >
            <span className={styles.achievementCategoryHeader}>
              <span className={styles.achievementCategoryName}>
                {t("achievements.view")}
              </span>
              {achievementReadyTotal > 0 ? (
                <span className={styles.achievementReadyBadge}>
                  {t("achievements.ready", { n: achievementReadyTotal })}
                </span>
              ) : null}
            </span>
            {renderAchievementProgress(
              achievementCollectedTotal,
              achievementTotal,
            )}
          </button>
        </div>
        {renderAchievementsModal(
          achievementCategories,
          openCategory,
          categoryReadyCount,
        )}
      </div>
    );
  };

  const renderHumanQueueOverlay = () => {
    if (!queued || draftQueued || inLiveMatch || matchIntro) return null;
    const minutes = Math.floor(queueElapsedSec / 60);
    const seconds = queueElapsedSec % 60;
    const timerLabel = `${minutes}:${String(seconds).padStart(2, "0")}`;
    return (
      <div className={styles.humanQueueOverlay}>
        <div className={styles.humanQueueCard}>
          <div className={styles.humanQueueTitle}>
            {t("match.view.queueTitle")}
          </div>
          <div className={styles.humanQueueTimer}>{timerLabel}</div>
          <div className={styles.humanQueueMeta}>
            {t("match.view.playersInQueue", { players: playersInQueue })}
          </div>
          <button
            type="button"
            className={styles.button}
            onClick={() => send({ type: "ai:cancelQueue" })}
          >
            {t("match.view.cancelQueue")}
          </button>
        </div>
      </div>
    );
  };

  const renderMatchIntro = () => {
    if (!matchIntro) return null;
    const selfAspect =
      matchIntro.selfAspectId && catalogMap[matchIntro.selfAspectId]
        ? catalogMap[matchIntro.selfAspectId]
        : null;
    const opponentAspect =
      matchIntro.opponentAspectId && catalogMap[matchIntro.opponentAspectId]
        ? catalogMap[matchIntro.opponentAspectId]
        : null;
    const selfNameColor = getNameColorTextProps(
      matchIntro.selfNameColorId || player?.nameColor,
    );
    const opponentNameColor = getNameColorTextProps(
      matchIntro.opponentNameColorId,
      DEFAULT_NAME_COLOR_HEX,
    );
    return (
      <div className={styles.matchIntroOverlay}>
        <div className={styles.matchIntroCard}>
          <div className={styles.matchIntroSide}>
            {selfAspect ? (
              <div className={styles.matchIntroAspect}>
                <CardFace
                  card={selfAspect}
                  foilEffect={matchIntro.selfAspectFoil || null}
                  compact
                />
              </div>
            ) : null}
            <div
              className={[styles.matchIntroName, selfNameColor.className]
                .filter(Boolean)
                .join(" ")}
              style={selfNameColor.style}
            >
              {matchIntro.selfUsername}
            </div>
          </div>
          <div className={styles.matchIntroVs}>VS</div>
          <div className={styles.matchIntroSide}>
            {opponentAspect ? (
              <div className={styles.matchIntroAspect}>
                <CardFace
                  card={opponentAspect}
                  foilEffect={matchIntro.opponentAspectFoil || null}
                  compact
                />
              </div>
            ) : null}
            <div
              className={[styles.matchIntroName, opponentNameColor.className]
                .filter(Boolean)
                .join(" ")}
              style={opponentNameColor.style}
            >
              {matchIntro.opponentUsername}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const openPortraitPreview = (targetUsername, mirrored = false) => {
    if (!targetUsername || !displayMatch) return;
    setPortraitPreview({
      username: targetUsername,
      mirrored,
    });
  };

  const renderPortraitPreview = () => {
    if (!portraitPreview) return null;
    const seat = displayMatch?.seats?.[portraitPreview.username] || null;
    const aspectId =
      typeof seat?.aspectId === "string" && seat.aspectId
        ? seat.aspectId
        : null;
    const aspectCard = aspectId ? catalogMap[aspectId] || null : null;
    const statusKeywordProps = {
      activeKeywordId: keywordTip?.id || null,
      onKeywordClick: toggleKeywordTip,
      onMinionClick: (tip) => {
        if (tip?.cardId) {
          openCardExam(tip.cardId);
          return;
        }
        toggleKeywordTip(tip);
      },
    };
    const badges = renderSeatStatusBadges(seat, {
      keywordProps: statusKeywordProps,
    });
    const deckCounts = formatSeatDeckCounts(seat);
    const aspectText =
      typeof aspectCard?.text === "string" && aspectCard.text.trim()
        ? aspectCard.text.trim()
        : null;
    const canReportPreviewPlayer =
      portraitPreview.username !== username &&
      !displayMatch?.workshop &&
      !displayMatch?.tutorial &&
      socialPlayers.some((entry) => entry.username === portraitPreview.username);
    return (
      <div
        className={styles.portraitPreviewOverlay}
        onClick={() => setPortraitPreview(null)}
        role="button"
        tabIndex={0}
        aria-label={t("match.portrait.closeAria")}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " " || event.key === "Escape") {
            event.preventDefault();
            setPortraitPreview(null);
          }
        }}
      >
        <div
          className={styles.portraitPreviewCard}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          role="presentation"
        >
          {aspectCard ? (
            <div className={styles.portraitPreviewAspectCard}>
              <CardFace
                card={aspectCard}
                foilEffect={seat?.aspectFoil || null}
                artOnly
                compact
                onClick={() =>
                  openCardExam(aspectCard.id, seat?.aspectFoil || null)
                }
              />
            </div>
          ) : null}
          <div className={styles.portraitPreviewName}>
            {portraitPreview.username}
          </div>
          {seat ? (
            <div className={styles.portraitPreviewSummary}>
              <div className={styles.portraitPreviewHp}>
                ♥ {seat.hp ?? 0}/{seat.maxHp ?? 0}
              </div>
              {aspectCard ? (
                <button
                  type="button"
                  className={styles.portraitPreviewAspect}
                  onClick={() =>
                    openCardExam(aspectCard.id, seat.aspectFoil || null)
                  }
                >
                  {aspectCard.name}
                </button>
              ) : null}
              <ActionBarResourceIcons
                resources={seat.resources}
                persistentResources={seat.persistentResources}
                delayedResources={seat.delayedResources}
              />
              {badges ? (
                <div className={styles.portraitPreviewBadges}>{badges}</div>
              ) : null}
              {deckCounts ? (
                <div className={styles.portraitPreviewDecks}>
                  {t("match.portrait.deck", { n: deckCounts.deck })}
                  <br />
                  {t("match.portrait.hand", { n: deckCounts.hand })}
                  <br />
                  <button
                    type="button"
                    className={styles.portraitPreviewDiscardButton}
                    onClick={() => {
                      playSfx("uiSwitch");
                      setDiscardViewer(portraitPreview.username);
                    }}
                  >
                    {t("match.portrait.discard", { n: deckCounts.discard })}
                  </button>
                </div>
              ) : null}
              {aspectText ? (
                <div className={styles.portraitPreviewAspectText}>
                  {aspectText}
                </div>
              ) : null}
              {(() => {
                const traitsByLevel = [2, 3, 4, 5].map((level) => ({
                  level,
                  traits: (Array.isArray(seat.traits) ? seat.traits : []).filter(
                    (trait) => Number(trait?.level) === level,
                  ),
                }));
                const hasAnyTrait = traitsByLevel.some(
                  (entry) => entry.traits.length > 0,
                );
                if (!hasAnyTrait) return null;
                return (
                  <div className={styles.portraitPreviewTraits}>
                    {traitsByLevel.map(({ level, traits }) =>
                      traits.length ? (
                        <div
                          key={`trait-level-${level}`}
                          className={styles.portraitPreviewTraitSection}
                        >
                          <div className={styles.portraitPreviewTraitLevel}>
                            {t("match.panel.levelN", { level })}
                          </div>
                          {traits.map((trait, index) => (
                            <div
                              key={`${trait.id || trait.label}-${level}-${index}`}
                              className={styles.portraitPreviewTraitEntry}
                            >
                              <div className={styles.portraitPreviewTraitLabel}>
                                {trait.label || trait.id}
                              </div>
                              {trait.detail ? (
                                <div className={styles.portraitPreviewTraitDetail}>
                                  {trait.detail}
                                </div>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      ) : null,
                    )}
                  </div>
                );
              })()}
            </div>
          ) : null}
        </div>
        {canReportPreviewPlayer ? (
          <div
            className={styles.portraitPreviewSafetyActions}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            role="presentation"
          >
            {renderPlayerSafetyActions(portraitPreview.username, "match")}
          </div>
        ) : null}
        <button
          type="button"
          className={`${styles.button} ${styles.portraitPreviewClose}`}
          onClick={(event) => {
            event.stopPropagation();
            setPortraitPreview(null);
          }}
        >
          {t("common.close")}
        </button>
      </div>
    );
  };

  const renderDiscardViewer = () => {
    if (!discardViewer || !displayMatch) return null;
    const seat = displayMatch.seats?.[discardViewer] || null;
    const entries = (Array.isArray(seat?.discard) ? seat.discard : [])
      .map((entry, index) => ({
        entry,
        index,
        card: entry?.cardId ? catalogMap[entry.cardId] || null : null,
      }))
      .filter((item) => item.card)
      .reverse();
    const closeDiscardViewer = () => setDiscardViewer(null);
    return (
      <div
        className={`${styles.actionPopup} ${styles.discardViewerOverlay}`}
        onClick={closeDiscardViewer}
      >
        <div
          className={`${styles.actionPopupPanel} ${styles.discardViewerPanel}`}
          onClick={(event) => event.stopPropagation()}
          role="dialog"
          aria-label={t("match.view.discardPileAria", { name: discardViewer })}
        >
          <h3 className={styles.actionPopupTitle}>
            {`${discardViewer} · ${t("match.view.discardCount", { n: entries.length })}`}
          </h3>
          <div className={styles.muted}>
            {t("match.view.discardViewerHint")}
          </div>
          {entries.length ? (
            <ScrollHintSurface className={styles.discardViewerGrid}>
              {entries.map(({ entry, index, card }) => (
                <CardFace
                  key={`${entry.instanceId || card.id}-${index}`}
                  card={card}
                  foilEffect={entry.foilEffect || null}
                  compact
                  fitText={false}
                  onClick={() => openCardExam(card.id, entry.foilEffect || null)}
                />
              ))}
            </ScrollHintSurface>
          ) : (
            <div className={styles.discardViewerEmpty}>{t("match.view.discardEmpty")}</div>
          )}
          <div className={styles.row}>
            <button
              type="button"
              className={styles.button}
              onClick={closeDiscardViewer}
            >
              {t("common.close")}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const requestLogout = () => {
    loggingOutRef.current = true;
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "ai:logout" }));
      return;
    }
    clearSession();
    dispatch({ type: "UNSET_USER" });
  };

  const getApiBase = () =>
    typeof process !== "undefined" && process.env.REACT_APP_AETERNA_API_URL
      ? String(process.env.REACT_APP_AETERNA_API_URL).replace(/\/$/, "")
      : "";

  const resetAccountPanel = () => {
    setAccountPanel(null);
    setAccountError("");
    setAccountPassword("");
    setAccountNewPassword("");
    setAccountConfirmPassword("");
    setAccountDeleteConfirm("");
    setAccountBusy(false);
  };

  const openAccountPanel = (panel) => {
    setLogoutConfirm(false);
    setAccountPanel(panel);
    setAccountError("");
    setAccountPassword("");
    setAccountNewPassword("");
    setAccountConfirmPassword("");
    setAccountDeleteConfirm("");
    setAccountBusy(false);
  };

  const submitChangePassword = async () => {
    if (accountNewPassword.length < 4) {
      setAccountError(t("match.view.account.passwordTooShort"));
      return;
    }
    if (accountNewPassword !== accountConfirmPassword) {
      setAccountError(t("match.view.account.passwordMismatch"));
      return;
    }
    setAccountBusy(true);
    setAccountError("");
    try {
      const response = await axios.post(
        `${getApiBase()}/api/user/change-password`,
        {
          username,
          currentPassword: accountPassword,
          newPassword: accountNewPassword,
        },
      );
      const nextToken =
        typeof response.data?.token === "string" ? response.data.token.trim() : "";
      if (nextToken) {
        const nextUser = {
          id: 0,
          username,
          token: nextToken,
          kind: "account",
        };
        saveAccountSession(nextUser);
        dispatch({ type: "SET_USER", payload: nextUser });
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(
            JSON.stringify({
              type: "ai:identify",
              username,
              token: nextToken,
              kind: "account",
              via: getNativePlatform(),
            }),
          );
        }
      }
      showToast(t("match.view.account.passwordUpdated"));
      resetAccountPanel();
    } catch (err) {
      const message =
        err.response?.data?.error ||
        err.response?.data ||
        t("match.view.account.changePasswordFailed");
      setAccountError(typeof message === "string" ? message : t("match.view.account.requestFailed"));
      setAccountBusy(false);
    }
  };

  const submitDeleteAccount = async () => {
    if (accountDeleteConfirm !== username) {
      setAccountError(t("match.view.account.typeUsernameToConfirm"));
      return;
    }
    if (!accountPassword) {
      setAccountError(t("match.view.account.enterCurrentPassword"));
      return;
    }
    setAccountBusy(true);
    setAccountError("");
    try {
      await axios.post(`${getApiBase()}/api/user/delete-account`, {
        username,
        password: accountPassword,
        confirm: accountDeleteConfirm,
      });
      loggingOutRef.current = true;
      clearSession();
      dispatch({ type: "UNSET_USER" });
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        try {
          wsRef.current.close();
        } catch (error) {
          // Ignore close errors.
        }
      }
    } catch (err) {
      const message =
        err.response?.data?.error ||
        err.response?.data ||
        t("match.view.account.deleteFailed");
      setAccountError(typeof message === "string" ? message : t("match.view.account.requestFailed"));
      setAccountBusy(false);
    }
  };

  const submitMigrateAccount = async () => {
    const name = migrateUsername.trim();
    if (!name) {
      setMigrateError(t("match.view.account.enterUsername"));
      return;
    }
    if (/\s/.test(name)) {
      setMigrateError(t("match.view.account.usernameNoSpaces"));
      return;
    }
    if (migratePassword.length < 4) {
      setMigrateError(t("match.view.account.passwordTooShort"));
      return;
    }
    setMigrateBusy(true);
    setMigrateError("");
    try {
      await axios.post(`${getApiBase()}/api/user/register`, {
        username: name,
        password: migratePassword,
        via: getNativePlatform(),
      });
      send({
        type: "ai:migrateGuest",
        targetUsername: name,
        password: migratePassword,
        via: getNativePlatform(),
      });
    } catch (err) {
      const message =
        err.response?.data?.error ||
        err.response?.data ||
        t("match.view.account.createFailed");
      setMigrateError(typeof message === "string" ? message : t("match.view.account.requestFailed"));
      setMigrateBusy(false);
    }
  };

  const renderVolumeSetting = ({ id, level, onChange, onCommit }) => {
    const percent = Math.round(level * 100);
    const label = t(`settings.${id}Volume.label`);
    const step = (delta) => {
      onChange(percent + delta);
      onCommit?.();
    };
    return (
      <div className={styles.settingsBlock} data-gamepad-setting>
        <div className={styles.settingsRow}>
          <button
            type="button"
            className={styles.settingsLabelButton}
            data-gamepad-setting-label
          >
            {label}
          </button>
          <div
            className={`${styles.settingsToggle} ${styles.settingsVolume}`}
            data-gamepad-setting-options
          >
            <button
              type="button"
              className={`${styles.button} ${styles.settingsVolumeStep}`}
              aria-label={t("settings.volume.decrease", { label })}
              disabled={percent <= 0}
              data-gamepad-skip
              onClick={() => step(-VOLUME_STEP_PERCENT)}
            >
              −
            </button>
            <input
              type="range"
              className={styles.settingsVolumeSlider}
              min={0}
              max={100}
              step={5}
              value={percent}
              aria-label={label}
              aria-valuetext={`${percent}%`}
              style={{ "--ai-volume-fill": `${percent}%` }}
              data-gamepad-skip
              onChange={(event) => onChange(Number(event.target.value))}
              onPointerUp={onCommit}
              onKeyUp={onCommit}
            />
            <button
              type="button"
              className={`${styles.button} ${styles.settingsVolumeStep}`}
              aria-label={t("settings.volume.increase", { label })}
              disabled={percent >= 100}
              data-gamepad-skip
              onClick={() => step(VOLUME_STEP_PERCENT)}
            >
              +
            </button>
            <span className={styles.settingsVolumeValue}>{percent}%</span>
          </div>
        </div>
        <p className={styles.settingsDescription}>
          {t("settings.volume.description")}
        </p>
      </div>
    );
  };

  const renderSettings = () => {
    const activeSection = SETTINGS_SECTIONS.find(
      (section) => section.id === settingsSectionId,
    );
    if (!activeSection) {
      return (
        <div className={styles.panel}>
          <h2 className={styles.panelTitle}>{t("settings.title")}</h2>
          <div className={styles.settingsMenu}>
            {SETTINGS_SECTIONS.map((section) => {
              const summary =
                section.id === "account"
                  ? `${username}${
                      isGuestAccount ? t("settings.account.guestSuffix") : ""
                    }`
                  : section.items
                      .filter(
                        (item) =>
                          !isGuestAccount ||
                          !SETTINGS_ACCOUNT_ONLY_ITEMS.has(item),
                      )
                      .map((item) => t(`settings.${item}.label`))
                      .join(" · ");
              return (
                <button
                  key={section.id}
                  type="button"
                  className={`${styles.settingsMenuItem} ${
                    section.danger ? styles.settingsMenuItemDanger : ""
                  }`}
                  onClick={() => {
                    playSfx("uiSwitch");
                    setSettingsSectionId(section.id);
                  }}
                >
                  <span className={styles.settingsMenuText}>
                    <span className={styles.settingsMenuTitle}>
                      {t(`settings.section.${section.id}`)}
                    </span>
                    <span className={styles.settingsMenuSummary}>
                      {summary}
                    </span>
                  </span>
                  <span className={styles.settingsMenuChevron} aria-hidden="true">
                    ›
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      );
    }
    return (
      <div
        className={styles.panel}
        data-gamepad-settings
      >
        <div className={styles.settingsSectionHeader}>
          <button
            type="button"
            className={`${styles.button} ${styles.settingsBackButton}`}
            aria-label={t("settings.backToMenu")}
            onClick={() => setSettingsSectionId(null)}
          >
            ‹ {t("settings.title")}
          </button>
          <h2
            className={`${styles.panelTitle} ${
              activeSection.danger ? styles.settingsDangerTitle : ""
            }`}
          >
            {t(`settings.section.${activeSection.id}`)}
          </h2>
        </div>
        {settingsSectionId === "account" ? (
          <section aria-label={t("settings.section.account")}>
            <div className={styles.settingsBlock} data-gamepad-setting>
              <div className={styles.settingsRow}>
                <button
                  type="button"
                  className={styles.settingsLabelButton}
                  data-gamepad-setting-label
                >
                  {t("settings.account.label")}
                </button>
                <span className={styles.settingsValue}>
                  {username}
                  {isGuestAccount ? t("settings.account.guestSuffix") : ""}
                </span>
              </div>
              <p className={styles.settingsDescription}>
                {isGuestAccount
                  ? t("settings.account.guestDescription")
                  : t("settings.account.savedDescription")}
              </p>
              {isGuestAccount ? (
                <>
                  {!migrateOpen ? (
                    <div className={styles.row}>
                      <button
                        type="button"
                        className={`${styles.button} ${styles.buttonPrimary}`}
                        data-gamepad-skip
                        onClick={() => {
                          setMigrateOpen(true);
                          setMigrateError("");
                        }}
                      >
                        {t("settings.account.createAndKeep")}
                      </button>
                    </div>
                  ) : (
                    <div className={styles.detailBlock}>
                      <div className={styles.detailLine}>
                        {t("settings.account.migratePrompt")}
                      </div>
                      <label className={styles.detailLine}>
                        {t("settings.account.username")}
                        <input
                          value={migrateUsername}
                          onChange={(event) => setMigrateUsername(event.target.value)}
                          autoComplete="username"
                          className={styles.adminSelect}
                        />
                      </label>
                      <label className={styles.detailLine}>
                        {t("settings.account.password")}
                        <input
                          type="password"
                          value={migratePassword}
                          onChange={(event) => setMigratePassword(event.target.value)}
                          autoComplete="new-password"
                          className={styles.adminSelect}
                        />
                      </label>
                      {migrateError ? (
                        <div className={styles.muted}>{migrateError}</div>
                      ) : null}
                      <div className={styles.row}>
                        <button
                          type="button"
                          className={`${styles.button} ${styles.buttonPrimary}`}
                          disabled={migrateBusy}
                          data-gamepad-skip
                          onClick={submitMigrateAccount}
                        >
                          {migrateBusy
                            ? t("settings.account.migrating")
                            : t("settings.account.createAccount")}
                        </button>
                        <button
                          type="button"
                          className={styles.button}
                          disabled={migrateBusy}
                          data-gamepad-skip
                          onClick={() => {
                            setMigrateOpen(false);
                            setMigrateError("");
                          }}
                        >
                          {t("common.cancel")}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {accountPanel === "password" ? (
                    <div className={styles.detailBlock}>
                      <div className={styles.detailLine}>
                        {t("settings.account.changePasswordPrompt")}
                      </div>
                      <label className={styles.detailLine}>
                        {t("settings.account.currentPassword")}
                        <input
                          type="password"
                          value={accountPassword}
                          onChange={(event) => setAccountPassword(event.target.value)}
                          autoComplete="current-password"
                          className={styles.adminSelect}
                        />
                      </label>
                      <label className={styles.detailLine}>
                        {t("settings.account.newPassword")}
                        <input
                          type="password"
                          value={accountNewPassword}
                          onChange={(event) =>
                            setAccountNewPassword(event.target.value)
                          }
                          autoComplete="new-password"
                          className={styles.adminSelect}
                        />
                      </label>
                      <label className={styles.detailLine}>
                        {t("settings.account.confirmNewPassword")}
                        <input
                          type="password"
                          value={accountConfirmPassword}
                          onChange={(event) =>
                            setAccountConfirmPassword(event.target.value)
                          }
                          autoComplete="new-password"
                          className={styles.adminSelect}
                        />
                      </label>
                      {accountError && accountPanel === "password" ? (
                        <div className={styles.muted}>{accountError}</div>
                      ) : null}
                      <div className={styles.row}>
                        <button
                          type="button"
                          className={`${styles.button} ${styles.buttonPrimary}`}
                          disabled={accountBusy}
                          data-gamepad-skip
                          onClick={submitChangePassword}
                        >
                          {accountBusy
                            ? t("settings.account.saving")
                            : t("settings.account.updatePassword")}
                        </button>
                        <button
                          type="button"
                          className={styles.button}
                          disabled={accountBusy}
                          data-gamepad-skip
                          onClick={resetAccountPanel}
                        >
                          {t("common.cancel")}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className={styles.row}>
                      <button
                        type="button"
                        className={styles.button}
                        data-gamepad-skip
                        onClick={() => openAccountPanel("password")}
                      >
                        {t("settings.account.changePassword")}
                      </button>
                    </div>
                  )}
                </>
              )}
              {!logoutConfirm ? (
                <div className={styles.row}>
                  <button
                    type="button"
                    className={`${styles.button} ${styles.buttonDanger}`}
                    data-gamepad-skip
                    onClick={() => {
                      if (accountPanel === "password") resetAccountPanel();
                      setLogoutConfirm(true);
                    }}
                  >
                    {t("settings.account.logOut")}
                  </button>
                </div>
              ) : (
                <div className={styles.detailBlock}>
                  <div className={styles.detailLine}>
                    {isGuestAccount
                      ? t("settings.account.logOutGuestConfirm")
                      : t("settings.account.logOutConfirm")}
                  </div>
                  <div className={styles.row}>
                    <button
                      type="button"
                      className={`${styles.button} ${styles.buttonDanger}`}
                      data-gamepad-skip
                      onClick={() => {
                        setLogoutConfirm(false);
                        requestLogout();
                      }}
                    >
                      {t("settings.account.confirmLogOut")}
                    </button>
                    <button
                      type="button"
                      className={styles.button}
                      data-gamepad-skip
                      onClick={() => setLogoutConfirm(false)}
                    >
                      {t("common.cancel")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </section>
        ) : null}
        {settingsSectionId === "general" ? (
          <section aria-label={t("settings.section.general")}>
            <div className={styles.settingsBlock} data-gamepad-setting>
              <div className={styles.settingsRow}>
                <button
                  type="button"
                  className={styles.settingsLabelButton}
                  data-gamepad-setting-label
                >
                  {t("settings.language.label")}
                </button>
                <div
                  className={styles.settingsToggle}
                  data-gamepad-setting-options
                >
                  <select
                    className={styles.select}
                    value={language}
                    aria-label={t("settings.language.label")}
                    data-gamepad-skip
                    onChange={(event) => setLanguageSetting(event.target.value)}
                  >
                    {LANGUAGE_OPTIONS.map((option) => (
                      <option key={option.id} value={option.id} lang={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <p className={styles.settingsDescription}>
                {t("settings.language.description")}
              </p>
            </div>
            <div className={styles.settingsBlock} data-gamepad-setting>
              <div className={styles.settingsRow}>
                <button
                  type="button"
                  className={styles.settingsLabelButton}
                  data-gamepad-setting-label
                >
                  {t("settings.music.label")}
                </button>
                <div
                  className={styles.settingsToggle}
                  data-gamepad-setting-options
                >
                  <button
                    type="button"
                    className={`${styles.button} ${
                      musicEnabled ? "" : styles.buttonPrimary
                    }`}
                    aria-pressed={!musicEnabled}
                    data-gamepad-skip
                    onClick={() => setMusicSetting(false)}
                  >
                    {t("common.off")}
                  </button>
                  <button
                    type="button"
                    className={`${styles.button} ${
                      musicEnabled ? styles.buttonPrimary : ""
                    }`}
                    aria-pressed={musicEnabled}
                    data-gamepad-skip
                    onClick={() => setMusicSetting(true)}
                  >
                    {t("common.on")}
                  </button>
                </div>
              </div>
              <p className={styles.settingsDescription}>
                {t("settings.music.description")}
              </p>
              <div className={styles.row}>
                <button
                  type="button"
                  className={styles.button}
                  disabled={!musicEnabled}
                  data-gamepad-skip
                  onClick={() => skipToNextTrack()}
                >
                  {t("settings.sound.skipTrack")}
                </button>
              </div>
            </div>
            {renderVolumeSetting({
              id: "music",
              level: musicEnabled ? musicVolumeLevel : 0,
              onChange: updateMusicVolume,
            })}
            <div className={styles.settingsBlock} data-gamepad-setting>
              <div className={styles.settingsRow}>
                <button
                  type="button"
                  className={styles.settingsLabelButton}
                  data-gamepad-setting-label
                >
                  {t("settings.sound.label")}
                </button>
                <div
                  className={styles.settingsToggle}
                  data-gamepad-setting-options
                >
                  <button
                    type="button"
                    className={`${styles.button} ${
                      soundEnabled ? "" : styles.buttonPrimary
                    }`}
                    aria-pressed={!soundEnabled}
                    data-gamepad-skip
                    onClick={() => setSoundSetting(false)}
                  >
                    {t("common.off")}
                  </button>
                  <button
                    type="button"
                    className={`${styles.button} ${
                      soundEnabled ? styles.buttonPrimary : ""
                    }`}
                    aria-pressed={soundEnabled}
                    data-gamepad-skip
                    onClick={() => setSoundSetting(true)}
                  >
                    {t("common.on")}
                  </button>
                </div>
              </div>
              <p className={styles.settingsDescription}>
                {t("settings.sound.description")}
              </p>
            </div>
            {renderVolumeSetting({
              id: "sound",
              level: soundEnabled ? soundVolumeLevel : 0,
              onChange: updateSoundVolume,
              onCommit: previewSoundVolume,
            })}
          </section>
        ) : null}
        {settingsSectionId === "gameplay" ? (
          <section aria-label={t("settings.section.gameplay")}>
            <div className={styles.settingsBlock} data-gamepad-setting>
              <div className={styles.settingsRow}>
                <button
                  type="button"
                  className={styles.settingsLabelButton}
                  data-gamepad-setting-label
                >
                  {t("settings.quickUse.label")}
                </button>
                <div
                  className={styles.settingsToggle}
                  data-gamepad-setting-options
                >
                  <button
                    type="button"
                    className={`${styles.button} ${
                      showQuickUse ? "" : styles.buttonPrimary
                    }`}
                    aria-pressed={!showQuickUse}
                    data-gamepad-skip
                    onClick={() => setShowQuickUseSetting(false)}
                  >
                    {t("common.off")}
                  </button>
                  <button
                    type="button"
                    className={`${styles.button} ${
                      showQuickUse ? styles.buttonPrimary : ""
                    }`}
                    aria-pressed={showQuickUse}
                    data-gamepad-skip
                    onClick={() => setShowQuickUseSetting(true)}
                  >
                    {t("common.on")}
                  </button>
                </div>
              </div>
              <p className={styles.settingsDescription}>
                {t("settings.quickUse.description")}
              </p>
            </div>
            <div className={styles.settingsBlock} data-gamepad-setting>
              <div className={styles.settingsRow}>
                <button
                  type="button"
                  className={styles.settingsLabelButton}
                  data-gamepad-setting-label
                >
                  {t("settings.hints.label")}
                </button>
                <div
                  className={styles.settingsToggle}
                  data-gamepad-setting-options
                >
                  <button
                    type="button"
                    className={`${styles.button} ${
                      showHints ? "" : styles.buttonPrimary
                    }`}
                    aria-pressed={!showHints}
                    data-gamepad-skip
                    onClick={() => setShowHintsSetting(false)}
                  >
                    {t("common.off")}
                  </button>
                  <button
                    type="button"
                    className={`${styles.button} ${
                      showHints ? styles.buttonPrimary : ""
                    }`}
                    aria-pressed={showHints}
                    data-gamepad-skip
                    onClick={() => setShowHintsSetting(true)}
                  >
                    {t("common.on")}
                  </button>
                </div>
              </div>
              <p className={styles.settingsDescription}>
                {t("settings.hints.description")}
              </p>
            </div>
            <div className={styles.settingsBlock} data-gamepad-setting>
              <div className={styles.settingsRow}>
                <button
                  type="button"
                  className={styles.settingsLabelButton}
                  data-gamepad-setting-label
                >
                  {t("settings.matchGuides.label")}
                </button>
                <div
                  className={styles.settingsToggle}
                  data-gamepad-setting-options
                >
                  {[
                    { id: "off", label: t("common.off") },
                    { id: "auto", label: t("settings.matchGuides.auto") },
                    { id: "on", label: t("common.on") },
                  ].map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      className={`${styles.button} ${
                        matchGuidesSetting === option.id ? styles.buttonPrimary : ""
                      }`}
                      aria-pressed={matchGuidesSetting === option.id}
                      data-gamepad-skip
                      onClick={() => setMatchGuidesSetting(option.id)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              <p className={styles.settingsDescription}>
                {t("settings.matchGuides.description", {
                  games: MATCH_GUIDES_AUTO_GAMES,
                })}
              </p>
            </div>
            <div className={styles.settingsBlock} data-gamepad-setting>
              <div className={styles.settingsRow}>
                <button
                  type="button"
                  className={styles.settingsLabelButton}
                  data-gamepad-setting-label
                >
                  {t("settings.cardNames.label")}
                </button>
                <div
                  className={styles.settingsToggle}
                  data-gamepad-setting-options
                >
                  <button
                    type="button"
                    className={`${styles.button} ${
                      showCardNames ? "" : styles.buttonPrimary
                    }`}
                    aria-pressed={!showCardNames}
                    data-gamepad-skip
                    onClick={() => setShowCardNamesSetting(false)}
                  >
                    {t("common.off")}
                  </button>
                  <button
                    type="button"
                    className={`${styles.button} ${
                      showCardNames ? styles.buttonPrimary : ""
                    }`}
                    aria-pressed={showCardNames}
                    data-gamepad-skip
                    onClick={() => setShowCardNamesSetting(true)}
                  >
                    {t("common.on")}
                  </button>
                </div>
              </div>
              <p className={styles.settingsDescription}>
                {t("settings.cardNames.description")}
              </p>
            </div>
            <div className={styles.settingsBlock} data-gamepad-setting>
              <div className={styles.settingsRow}>
                <button
                  type="button"
                  className={styles.settingsLabelButton}
                  data-gamepad-setting-label
                >
                  {t("settings.toasts.label")}
                </button>
                <div
                  className={styles.settingsToggle}
                  data-gamepad-setting-options
                >
                  <button
                    type="button"
                    className={`${styles.button} ${
                      toastsMode === "off" ? styles.buttonPrimary : ""
                    }`}
                    aria-pressed={toastsMode === "off"}
                    data-gamepad-skip
                    onClick={() => setToastsSetting("off")}
                  >
                    {t("common.off")}
                  </button>
                  <button
                    type="button"
                    className={`${styles.button} ${
                      toastsMode === "card" ? styles.buttonPrimary : ""
                    }`}
                    aria-pressed={toastsMode === "card"}
                    data-gamepad-skip
                    onClick={() => setToastsSetting("card")}
                  >
                    {t("settings.toasts.cardOnly")}
                  </button>
                  <button
                    type="button"
                    className={`${styles.button} ${
                      toastsMode === "text" ? styles.buttonPrimary : ""
                    }`}
                    aria-pressed={toastsMode === "text"}
                    data-gamepad-skip
                    onClick={() => setToastsSetting("text")}
                  >
                    {t("settings.toasts.textOnly")}
                  </button>
                  <button
                    type="button"
                    className={`${styles.button} ${
                      toastsMode === "all" ? styles.buttonPrimary : ""
                    }`}
                    aria-pressed={toastsMode === "all"}
                    data-gamepad-skip
                    onClick={() => setToastsSetting("all")}
                  >
                    {t("settings.toasts.all")}
                  </button>
                </div>
              </div>
              <p className={styles.settingsDescription}>
                {t("settings.toasts.description")}
              </p>
            </div>
            <div className={styles.settingsBlock} data-gamepad-setting>
              <div className={styles.settingsRow}>
                <button
                  type="button"
                  className={styles.settingsLabelButton}
                  data-gamepad-setting-label
                >
                  {t("settings.toastSpeed.label")}
                </button>
                <div
                  className={styles.settingsToggle}
                  data-gamepad-setting-options
                >
                  <button
                    type="button"
                    className={`${styles.button} ${
                      toastSpeed === "fast" ? styles.buttonPrimary : ""
                    }`}
                    aria-pressed={toastSpeed === "fast"}
                    data-gamepad-skip
                    onClick={() => setToastSpeedSetting("fast")}
                  >
                    {t("settings.toastSpeed.fast")}
                  </button>
                  <button
                    type="button"
                    className={`${styles.button} ${
                      toastSpeed === "standard" ? styles.buttonPrimary : ""
                    }`}
                    aria-pressed={toastSpeed === "standard"}
                    data-gamepad-skip
                    onClick={() => setToastSpeedSetting("standard")}
                  >
                    {t("settings.toastSpeed.standard")}
                  </button>
                </div>
              </div>
              <p className={styles.settingsDescription}>
                {t("settings.toastSpeed.description")}
              </p>
            </div>
          </section>
        ) : null}
        {settingsSectionId === "display" ? (
          <section aria-label={t("settings.section.display")}>
            <div className={styles.settingsBlock} data-gamepad-setting>
              <div className={styles.settingsRow}>
                <button
                  type="button"
                  className={styles.settingsLabelButton}
                  data-gamepad-setting-label
                >
                  {t("settings.graphics.label")}
                </button>
                <div
                  className={styles.settingsToggle}
                  data-gamepad-setting-options
                >
                  {GRAPHICS_QUALITY_OPTIONS.map((option) => (
                    <button
                      key={option}
                      type="button"
                      className={`${styles.button} ${
                        graphicsQuality === option ? styles.buttonPrimary : ""
                      }`}
                      aria-pressed={graphicsQuality === option}
                      data-gamepad-skip
                      onClick={() => setGraphicsQualitySetting(option)}
                    >
                      {t(`settings.graphics.${option}`)}
                    </button>
                  ))}
                </div>
              </div>
              <p className={styles.settingsDescription}>
                {t("settings.graphics.description")}
                {graphicsQuality === "auto"
                  ? ` ${t("settings.graphics.autoCurrent", {
                      tier: t(`settings.graphics.${autoGraphicsTier}`),
                    })}`
                  : ""}
              </p>
            </div>
            <div className={styles.settingsBlock} data-gamepad-setting>
              <div className={styles.settingsRow}>
                <button
                  type="button"
                  className={styles.settingsLabelButton}
                  data-gamepad-setting-label
                >
                  {t("settings.foil.label")}
                </button>
                <div
                  className={styles.settingsToggle}
                  data-gamepad-setting-options
                >
                  <button
                    type="button"
                    className={`${styles.button} ${
                      foilEffectsQuality === "off" ? styles.buttonPrimary : ""
                    }`}
                    aria-pressed={foilEffectsQuality === "off"}
                    data-gamepad-skip
                    onClick={() => setFoilEffectsSetting("off")}
                  >
                    {t("common.off")}
                  </button>
                  <button
                    type="button"
                    className={`${styles.button} ${
                      foilEffectsQuality === "minimum" ? styles.buttonPrimary : ""
                    }`}
                    aria-pressed={foilEffectsQuality === "minimum"}
                    data-gamepad-skip
                    onClick={() => setFoilEffectsSetting("minimum")}
                  >
                    {t("settings.foil.minimum")}
                  </button>
                  <button
                    type="button"
                    className={`${styles.button} ${
                      foilEffectsQuality === "maximum" ? styles.buttonPrimary : ""
                    }`}
                    aria-pressed={foilEffectsQuality === "maximum"}
                    data-gamepad-skip
                    onClick={() => setFoilEffectsSetting("maximum")}
                  >
                    {t("settings.foil.maximum")}
                  </button>
                </div>
              </div>
              <p className={styles.settingsDescription}>
                {t("settings.foil.description")}
              </p>
            </div>
            <div className={styles.settingsBlock} data-gamepad-setting>
              <div className={styles.settingsRow}>
                <button
                  type="button"
                  className={styles.settingsLabelButton}
                  data-gamepad-setting-label
                >
                  {t("settings.fullscreen.label")}
                </button>
                <div
                  className={styles.settingsToggle}
                  data-gamepad-setting-options
                >
                  <button
                    type="button"
                    className={`${styles.button} ${
                      forceFullscreen ? "" : styles.buttonPrimary
                    }`}
                    aria-pressed={!forceFullscreen}
                    data-gamepad-skip
                    onClick={() => setForceFullscreenSetting(false)}
                  >
                    {t("common.off")}
                  </button>
                  <button
                    type="button"
                    className={`${styles.button} ${
                      forceFullscreen ? styles.buttonPrimary : ""
                    }`}
                    aria-pressed={forceFullscreen}
                    data-gamepad-skip
                    onClick={() => setForceFullscreenSetting(true)}
                  >
                    {t("common.on")}
                  </button>
                </div>
              </div>
              <p className={styles.settingsDescription}>
                {t("settings.fullscreen.description")}
              </p>
            </div>
            <div className={styles.settingsBlock} data-gamepad-setting>
              <div className={styles.settingsRow}>
                <button
                  type="button"
                  className={styles.settingsLabelButton}
                  data-gamepad-setting-label
                >
                  {t("settings.theme.label")}
                </button>
                <div
                  className={styles.settingsToggle}
                  data-gamepad-setting-options
                >
                  <button
                    type="button"
                    className={`${styles.button} ${
                      theme === "dark" ? styles.buttonPrimary : ""
                    }`}
                    aria-pressed={theme === "dark"}
                    data-gamepad-skip
                    onClick={() => setThemeSetting("dark")}
                  >
                    {t("settings.theme.dark")}
                  </button>
                  <button
                    type="button"
                    className={`${styles.button} ${
                      theme === "light" ? styles.buttonPrimary : ""
                    }`}
                    aria-pressed={theme === "light"}
                    data-gamepad-skip
                    onClick={() => setThemeSetting("light")}
                  >
                    {t("settings.theme.light")}
                  </button>
                </div>
              </div>
              <p className={styles.settingsDescription}>
                {t("settings.theme.description")}
              </p>
            </div>
            <div className={styles.settingsBlock} data-gamepad-setting>
              <div className={styles.settingsRow}>
                <button
                  type="button"
                  className={styles.settingsLabelButton}
                  data-gamepad-setting-label
                >
                  {t("settings.workshop.label")}
                </button>
                <div
                  className={styles.settingsToggle}
                  data-gamepad-setting-options
                >
                  <button
                    type="button"
                    className={`${styles.button} ${
                      showWorkshop ? "" : styles.buttonPrimary
                    }`}
                    aria-pressed={!showWorkshop}
                    data-gamepad-skip
                    onClick={() => setShowWorkshopSetting(false)}
                  >
                    {t("common.hide")}
                  </button>
                  <button
                    type="button"
                    className={`${styles.button} ${
                      showWorkshop ? styles.buttonPrimary : ""
                    }`}
                    aria-pressed={showWorkshop}
                    data-gamepad-skip
                    onClick={() => setShowWorkshopSetting(true)}
                  >
                    {t("common.show")}
                  </button>
                </div>
              </div>
              <p className={styles.settingsDescription}>
                {t("settings.workshop.description")}
              </p>
            </div>
          </section>
        ) : null}
        {settingsSectionId === "controls" ? (
          <section aria-label={t("settings.section.controls")}>
            <div className={styles.settingsBlock} data-gamepad-setting>
              <div className={styles.settingsRow}>
                <button
                  type="button"
                  className={styles.settingsLabelButton}
                  data-gamepad-setting-label
                >
                  {t("settings.handDisplay.label")}
                </button>
                <div
                  className={styles.settingsToggle}
                  data-gamepad-setting-options
                >
                  <button
                    type="button"
                    className={`${styles.button} ${
                      handDisplay === "drawer" ? styles.buttonPrimary : ""
                    }`}
                    aria-pressed={handDisplay === "drawer"}
                    data-gamepad-skip
                    onClick={() => setHandDisplaySetting("drawer")}
                  >
                    {t("settings.handDisplay.drawer")}
                  </button>
                  <button
                    type="button"
                    className={`${styles.button} ${
                      handDisplay === "dynamic" ? styles.buttonPrimary : ""
                    }`}
                    aria-pressed={handDisplay === "dynamic"}
                    data-gamepad-skip
                    onClick={() => setHandDisplaySetting("dynamic")}
                  >
                    {t("settings.handDisplay.dynamic")}
                  </button>
                </div>
              </div>
              <p className={styles.settingsDescription}>
                {t("settings.handDisplay.description")}
              </p>
            </div>
            <div className={styles.settingsBlock} data-gamepad-setting>
              <div className={styles.settingsRow}>
                <button
                  type="button"
                  className={styles.settingsLabelButton}
                  data-gamepad-setting-label
                >
                  {t("settings.leftHanded.label")}
                </button>
                <div
                  className={styles.settingsToggle}
                  data-gamepad-setting-options
                >
                  <button
                    type="button"
                    className={`${styles.button} ${
                      leftHanded ? "" : styles.buttonPrimary
                    }`}
                    aria-pressed={!leftHanded}
                    data-gamepad-skip
                    onClick={() => setLeftHandedSetting(false)}
                  >
                    {t("common.off")}
                  </button>
                  <button
                    type="button"
                    className={`${styles.button} ${
                      leftHanded ? styles.buttonPrimary : ""
                    }`}
                    aria-pressed={leftHanded}
                    data-gamepad-skip
                    onClick={() => setLeftHandedSetting(true)}
                  >
                    {t("common.on")}
                  </button>
                </div>
              </div>
              <p className={styles.settingsDescription}>
                {t("settings.leftHanded.description")}
              </p>
            </div>
            <div className={styles.settingsBlock} data-gamepad-setting>
              <div className={styles.settingsRow}>
                <button
                  type="button"
                  className={styles.settingsLabelButton}
                  data-gamepad-setting-label
                >
                  {t("settings.controller.label")}
                </button>
                <span className={styles.settingsValue}>
                  {gamepadConnected
                    ? gamepadPadId || t("settings.controller.connected")
                    : t("settings.controller.none")}
                </span>
              </div>
              <p className={styles.settingsDescription}>
                {t("settings.controller.description")}
              </p>
            </div>
          </section>
        ) : null}
        {settingsSectionId === "social" ? (
          <section aria-label={t("settings.section.social")}>
            {!isGuestAccount ? (
              <div className={styles.settingsBlock} data-gamepad-setting>
                <div className={styles.settingsRow}>
                  <button
                    type="button"
                    className={styles.settingsLabelButton}
                    data-gamepad-setting-label
                  >
                    {t("settings.chat.label")}
                  </button>
                  <div
                    className={styles.settingsToggle}
                    data-gamepad-setting-options
                  >
                    {chatOptIn ? (
                      <button
                        type="button"
                        className={`${styles.button} ${styles.buttonDanger}`}
                        data-gamepad-skip
                        onClick={turnChatOff}
                      >
                        {t("settings.chat.turnOff")}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className={`${styles.button} ${styles.buttonPrimary}`}
                        disabled={!chatEligible || chatMuted}
                        data-gamepad-skip
                        onClick={() => setChatOptInOpen(true)}
                      >
                        {t("settings.chat.reviewTerms")}
                      </button>
                    )}
                  </div>
                </div>
                <p className={styles.settingsDescription}>
                  {chatMuted
                    ? t("settings.chat.muted")
                    : chatOptIn
                      ? t("settings.chat.on")
                      : chatEligible
                        ? t("settings.chat.off")
                        : t("settings.chat.locked", {
                            games: CHAT_MIN_COMPLETED_GAMES,
                          })}
                </p>
              </div>
            ) : null}
            <div className={styles.settingsBlock} data-gamepad-setting>
              <div className={styles.settingsRow}>
                <button
                  type="button"
                  className={styles.settingsLabelButton}
                  data-gamepad-setting-label
                >
                  {t("settings.blocked.label")}
                </button>
                <span className={styles.settingsValue}>{blockedUsernames.length}</span>
              </div>
              {blockedUsernames.length ? (
                <div
                  className={styles.blockedPlayerList}
                  data-gamepad-setting-options
                >
                  {blockedUsernames.map((blockedName) => (
                    <div key={blockedName} className={styles.blockedPlayerRow}>
                      <span>{blockedName}</span>
                      <button
                        type="button"
                        className={styles.button}
                        data-gamepad-skip
                        onClick={() => setPlayerBlocked(blockedName, false)}
                      >
                        {t("settings.blocked.unblock")}
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
              <p className={styles.settingsDescription}>
                {t("settings.blocked.description")}
              </p>
            </div>
          </section>
        ) : null}
        {settingsSectionId === "danger" ? (
          <section aria-label={t("settings.section.danger")}>
            <div className={styles.settingsBlock} data-gamepad-setting>
              <div className={styles.settingsRow}>
                <button
                  type="button"
                  className={styles.settingsLabelButton}
                  data-gamepad-setting-label
                >
                  {t("settings.adventure.label")}
                </button>
                <div
                  className={styles.settingsToggle}
                  data-gamepad-setting-options
                >
                  <button
                    type="button"
                    className={`${styles.button} ${styles.buttonDanger}`}
                    disabled={!player?.adventureRun}
                    data-gamepad-skip
                    onClick={() => setAdventureResetConfirm(true)}
                  >
                    {t("settings.adventure.reset")}
                  </button>
                </div>
              </div>
              {player?.adventureRun ? (
                <p className={styles.settingsDescription}>
                  {t("settings.adventure.description")}
                </p>
              ) : (
                <p className={styles.settingsDescription}>
                  {t("settings.adventure.none")}
                </p>
              )}
            </div>
            {renderAdventureResetConfirm()}
            {!isGuestAccount ? (
              <div className={styles.settingsBlock} data-gamepad-setting>
                <div className={styles.settingsRow}>
                  <button
                    type="button"
                    className={styles.settingsLabelButton}
                    data-gamepad-setting-label
                  >
                    {t("settings.deleteAccount.label")}
                  </button>
                </div>
                <p className={styles.settingsDescription}>
                  {t("settings.deleteAccount.description")}
                </p>
                {accountPanel === "delete" ? (
                  <div className={styles.detailBlock}>
                    <label className={styles.detailLine}>
                      {t("settings.deleteAccount.typeUsername", { username })}
                      <input
                        value={accountDeleteConfirm}
                        onChange={(event) =>
                          setAccountDeleteConfirm(event.target.value)
                        }
                        autoComplete="off"
                        className={styles.adminSelect}
                      />
                    </label>
                    <label className={styles.detailLine}>
                      {t("settings.account.currentPassword")}
                      <input
                        type="password"
                        value={accountPassword}
                        onChange={(event) => setAccountPassword(event.target.value)}
                        autoComplete="current-password"
                        className={styles.adminSelect}
                      />
                    </label>
                    {accountError && accountPanel === "delete" ? (
                      <div className={styles.muted}>{accountError}</div>
                    ) : null}
                    <div className={styles.row}>
                      <button
                        type="button"
                        className={`${styles.button} ${styles.buttonDanger}`}
                        disabled={accountBusy}
                        data-gamepad-skip
                        onClick={submitDeleteAccount}
                      >
                        {accountBusy
                          ? t("settings.deleteAccount.deleting")
                          : t("settings.deleteAccount.forever")}
                      </button>
                      <button
                        type="button"
                        className={styles.button}
                        disabled={accountBusy}
                        data-gamepad-skip
                        onClick={resetAccountPanel}
                      >
                        {t("common.cancel")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className={styles.row}>
                    <button
                      type="button"
                      className={`${styles.button} ${styles.buttonDanger}`}
                      data-gamepad-skip
                      onClick={() => openAccountPanel("delete")}
                    >
                      {t("settings.deleteAccount.label")}
                    </button>
                    <button
                      type="button"
                      className={styles.button}
                      data-gamepad-skip
                      onClick={openPrivacyPolicy}
                    >
                      {t("support.privacyPolicy")}
                    </button>
                  </div>
                )}
              </div>
            ) : null}
          </section>
        ) : null}
      </div>
    );
  };

  const renderSocial = () => {
    const visibleSocialMessages = socialMessages.filter(
      (message) => !blockedUsernameSet.has(message.username),
    );
    const gamesRemaining = Math.max(
      0,
      CHAT_MIN_COMPLETED_GAMES - chatCompletedGames,
    );
    let chatLockMessage = null;
    if (chatMuted) {
      chatLockMessage = t("social.chatMuted");
    } else if (isGuestAccount) {
      chatLockMessage = t("social.chatGuest");
    } else if (!chatEligible) {
      chatLockMessage = t(
        gamesRemaining > 0 ? "social.chatLockedRemaining" : "social.chatLocked",
        {
          games: CHAT_MIN_COMPLETED_GAMES,
          completed: chatCompletedGames,
          remaining: gamesRemaining,
        },
      );
    } else if (!chatOptIn) {
      chatLockMessage = t("social.chatAgreeTerms");
    }
    const onlinePlayerCount = socialPlayers.filter((entry) => entry.online)
      .length;
    return (
      <div className={styles.socialLayout}>
        <div
          className={`${styles.panel} ${styles.socialPlayersPanel}${
            socialPlayersCollapsed ? ` ${styles.socialPlayersPanelCollapsed}` : ""
          }`}
        >
          <h2 className={styles.panelTitle}>
            <button
              type="button"
              className={styles.socialPlayersToggle}
              onClick={toggleSocialPlayersCollapsed}
              aria-expanded={!socialPlayersCollapsed}
              aria-controls="social-player-list"
              title={
                socialPlayersCollapsed
                  ? t("social.showPlayers")
                  : t("social.hidePlayers")
              }
            >
              <span>{t("social.players")}</span>
              <span className={styles.socialPlayersCount}>
                {t("social.playersOnline", {
                  online: onlinePlayerCount,
                  total: socialPlayers.length,
                })}
              </span>
              <svg
                className={styles.socialPlayersChevron}
                viewBox="0 0 16 16"
                aria-hidden="true"
              >
                <path
                  d="M4 6l4 4 4-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </h2>
          <div
            id="social-player-list"
            className={styles.socialPlayerList}
            hidden={socialPlayersCollapsed}
          >
            {socialPlayers.map((entry) => (
              <div key={entry.username} className={styles.socialPlayerRow}>
                <div className={styles.socialPlayerMeta}>
                  <button
                    type="button"
                    className={styles.socialPlayerName}
                    onClick={() => openSocialPlayer(entry)}
                  >
                    {entry.username}
                  </button>
                  <span className={styles.socialPlayerStatus}>
                    {localizeSocialStatus(entry.status)}
                  </span>
                </div>
              </div>
            ))}
            {!socialPlayers.length && (
              <div className={styles.detailLine}>{t("social.noPlayers")}</div>
            )}
          </div>
        </div>
        <div className={`${styles.panel} ${styles.socialChatPanel}`}>
          <h2 className={styles.panelTitle}>{t("settings.chat.label")}</h2>
          {canUseSocialChat ? (
            <>
              <div
                className={styles.chatMessages}
                ref={chatListRef}
                onScroll={() => {
                  const el = chatListRef.current;
                  if (!el) return;
                  const distance =
                    el.scrollHeight - el.scrollTop - el.clientHeight;
                  chatStickBottomRef.current = distance <= 48;
                }}
              >
                {visibleSocialMessages.map((message) => {
                  const chatNameColor = message.nameColorId
                    ? getNameColorTextProps(message.nameColorId)
                    : {
                        className: null,
                        style: {
                          color:
                            message.nameColorHex || DEFAULT_NAME_COLOR_HEX,
                        },
                      };
                  return (
                    <div key={message.id} className={styles.chatMessage}>
                      <button
                        type="button"
                        className={[
                          styles.chatSender,
                          chatNameColor.className,
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        style={chatNameColor.style}
                        onClick={() =>
                          openSocialPlayerByUsername(message.username, "chat")
                        }
                      >
                        {message.username}
                      </button>
                      <span className={styles.chatSeparator}>: </span>
                      <span className={styles.chatText}>{message.text}</span>
                    </div>
                  );
                })}
                {!visibleSocialMessages.length && (
                  <div className={styles.detailLine}>{t("social.noMessages")}</div>
                )}
              </div>
              <form
                className={styles.chatInputRow}
                onSubmit={(event) => {
                  event.preventDefault();
                  sendChatMessage();
                }}
              >
                <input
                  type="text"
                  className={styles.chatInput}
                  value={chatInput}
                  maxLength={280}
                  placeholder={t("social.messagePlaceholder")}
                  enterKeyHint="send"
                  onChange={(event) => setChatInput(event.target.value)}
                />
              </form>
            </>
          ) : (
            <div className={styles.chatLocked}>
              <p className={styles.communityCopy}>{chatLockMessage}</p>
              {chatEligible && !chatOptIn && !chatMuted ? (
                <button
                  type="button"
                  className={`${styles.button} ${styles.buttonPrimary}`}
                  onClick={() => setChatOptInOpen(true)}
                >
                  {t("settings.chat.reviewTerms")}
                </button>
              ) : null}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderReportPlayer = () => {
    if (!reportTarget) return null;
    const targetName = reportTarget.username;
    const isBlocked = blockedUsernameSet.has(targetName);
    return (
      <div
        className={`${styles.actionPopup} ${styles.reportPlayerOverlay}`}
        onClick={closeReportPlayer}
      >
        <ScrollHintSurface
          className={`${styles.actionPopupPanel} ${styles.reportPlayerPanel}`}
          onClick={(event) => event.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label={t("social.reportTitle", { name: targetName })}
        >
          <h3 className={styles.actionPopupTitle}>
            {t("social.reportTitle", { name: targetName })}
          </h3>
          <div className={styles.detailBlock}>
            <div className={styles.detailLine}>
              {t("social.reportPrompt")}
            </div>
            <div
              className={styles.reportCategoryList}
              role="radiogroup"
              aria-label={t("social.reportCategory")}
            >
              {PLAYER_REPORT_CATEGORIES.map((category) => (
                <button
                  key={category.id}
                  type="button"
                  role="radio"
                  aria-checked={reportCategory === category.id}
                  className={`${styles.button} ${
                    reportCategory === category.id ? styles.buttonPrimary : ""
                  }`}
                  disabled={reportBusy}
                  onClick={() => {
                    setReportCategory(category.id);
                    setReportError(null);
                  }}
                >
                  {localizedOptionLabel("social.reportCategoryName", category)}
                </button>
              ))}
            </div>
          </div>
          <div className={styles.detailBlock}>
            <label className={styles.detailLine} htmlFor="ai-report-details">
              {t("social.reportDetails")}
            </label>
            <textarea
              id="ai-report-details"
              className={styles.reportDetailsInput}
              value={reportDetails}
              maxLength={PLAYER_REPORT_DETAILS_MAX}
              placeholder={t("social.reportDetailsPlaceholder")}
              disabled={reportBusy}
              onChange={(event) => setReportDetails(event.target.value)}
            />
            <div className={styles.reportDetailsCount}>
              {reportDetails.length}/{PLAYER_REPORT_DETAILS_MAX}
            </div>
            {reportTarget.context === "chat" ? (
              <div className={styles.muted}>
                {t("social.reportChatIncluded")}
              </div>
            ) : null}
            {reportError ? (
              <div className={styles.muted} role="alert">
                {reportError}
              </div>
            ) : null}
          </div>
          {!isBlocked ? (
            <div className={styles.muted}>
              {t("social.reportBlockHint", { name: targetName })}
            </div>
          ) : null}
          <div className={styles.row}>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonDanger}`}
              disabled={reportBusy || !reportCategory}
              onClick={submitPlayerReport}
            >
              {reportBusy ? t("social.sending") : t("social.submitReport")}
            </button>
            {!isBlocked ? (
              <button
                type="button"
                className={styles.button}
                disabled={reportBusy}
                onClick={() => setPlayerBlocked(targetName, true)}
              >
                {t("social.block")}
              </button>
            ) : null}
            <button
              type="button"
              className={styles.button}
              disabled={reportBusy}
              onClick={closeReportPlayer}
            >
              {t("common.cancel")}
            </button>
          </div>
        </ScrollHintSurface>
      </div>
    );
  };

  const renderServerMessages = () => {
    if (!serverMessagesOpen) return null;
    const close = () => setServerMessagesOpen(false);
    const newestFirst = [...serverMessages].reverse();
    return (
      <div className={styles.actionPopup} onClick={close}>
        <ScrollHintSurface
          className={`${styles.actionPopupPanel} ${styles.serverMessagesPanel}`}
          onClick={(event) => event.stopPropagation()}
        >
          <h3 className={styles.actionPopupTitle}>
            {t("serverMessages.title")}
          </h3>
          {serverMaintenance ? (
            <div className={styles.serverMessagesMaintenance}>
              {serverMaintenance.started ? (
                t("serverMessages.inProgress")
              ) : (
                <>
                  {t("serverMessages.beginsIn")}{" "}
                  <ServerMaintenanceCountdown
                    startsAt={serverMaintenance.startsAt}
                    className={styles.serverMessagesCountdown}
                  />
                </>
              )}
            </div>
          ) : null}
          {newestFirst.length ? (
            <ul className={styles.serverMessagesList}>
              {newestFirst.map((entry) => (
                <li
                  key={entry.id}
                  className={`${styles.serverMessagesItem} ${
                    entry.kind === "maintenance"
                      ? styles.serverMessagesItemMaintenance
                      : ""
                  }`}
                >
                  <div className={styles.serverMessagesItemHead}>
                    <span>{serverMessageTitle(entry.kind, t)}</span>
                    <span className={styles.muted}>
                      {formatServerMessageTime(entry.at, language)}
                    </span>
                  </div>
                  <div className={styles.serverMessagesItemText}>
                    {localizeServerMessageText(entry, t)}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className={styles.muted}>{t("serverMessages.empty")}</div>
          )}
          <div className={styles.row}>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary}`}
              onClick={close}
            >
              {t("common.close")}
            </button>
          </div>
        </ScrollHintSurface>
      </div>
    );
  };

  const renderChatOptIn = () => {
    if (!chatOptInOpen) return null;
    return (
      <div
        className={styles.actionPopup}
        onClick={() => setChatOptInOpen(false)}
      >
        <ScrollHintSurface
          className={`${styles.actionPopupPanel} ${styles.chatOptInPanel}`}
          onClick={(event) => event.stopPropagation()}
        >
          <h3 className={styles.actionPopupTitle}>{t("social.optIn.title")}</h3>
          <div className={styles.detailBlock}>
            <div className={styles.detailLine}>
              {t("social.optIn.optional")}
            </div>
            <div className={styles.detailLine}>
              {t("social.optIn.notMonitored")}
            </div>
            <div className={styles.detailLine}>
              {t("social.optIn.conduct")}
            </div>
            <div className={styles.detailLine}>
              {t("social.optIn.sensitive")}
            </div>
            <div className={styles.detailLine}>
              {t("social.optIn.detailsBefore")}
              <a
                className={styles.chatOptInLink}
                href={PRIVACY_POLICY_PAGE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("support.privacyPolicy")}
              </a>
              {t("social.optIn.detailsAfter")}
            </div>
          </div>
          <div className={styles.row}>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary}`}
              onClick={acceptChatOptIn}
            >
              {t("social.optIn.agree")}
            </button>
            <button
              type="button"
              className={styles.button}
              onClick={() => setChatOptInOpen(false)}
            >
              {t("match.view.notNow")}
            </button>
          </div>
        </ScrollHintSurface>
      </div>
    );
  };

  const formatRecordTriplet = (record) => {
    const wins = Math.max(0, Math.floor(Number(record?.wins) || 0));
    const losses = Math.max(0, Math.floor(Number(record?.losses) || 0));
    const ties = Math.max(0, Math.floor(Number(record?.draws) || 0));
    return `${wins}/${losses}/${ties}`;
  };

  const openContactForm = () => {
    setContactFormOpen(true);
    setContactConfirmation(null);
    setContactError(null);
    setContactSending(false);
  };

  const closeContactForm = () => {
    if (contactSending) return;
    setContactFormOpen(false);
    setContactError(null);
  };

  const submitContactForm = async () => {
    if (contactSending) return;
    const email = String(contactEmail || "").trim();
    const message = String(contactMessage || "").trim();
    if (!email) {
      setContactError(t("support.contact.enterEmail"));
      return;
    }
    if (!message) {
      setContactError(t("support.contact.enterMessage"));
      return;
    }
    setContactError(null);
    setContactSending(true);
    try {
      const response = await fetch("/api/support", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          kind: contactKind === "bug" ? "bug" : "support",
          email,
          message,
          username: player?.username || username || null,
        }),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.ok) {
        setContactError(
          data?.message || t("support.contact.sendFailed"),
        );
        return;
      }
      if (typeof data.mailtoUrl === "string" && data.mailtoUrl) {
        window.location.href = data.mailtoUrl;
      }
      setContactConfirmation(
        language === DEFAULT_LANGUAGE && data.confirmationMessage
          ? data.confirmationMessage
          : t("support.contact.confirmation", { email: SUPPORT_CONTACT_EMAIL }),
      );
      setContactFormOpen(false);
      setContactMessage("");
      setContactError(null);
    } catch (error) {
      setContactError(t("support.contact.sendFailed"));
    } finally {
      setContactSending(false);
    }
  };

  const renderCommunity = () => (
    <div className={styles.communityLayout}>
      <div className={`${styles.panel} ${styles.communityPanel}`}>
        <h2 className={styles.panelTitle}>{t("nav.community")}</h2>
        <p className={styles.communityCopy}>
          {t("community.discordCopy")}
        </p>
        <a
          className={`${styles.button} ${styles.buttonPrimary} ${styles.communityDiscordLink}`}
          href={DISCORD_INVITE_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("community.joinDiscord")}
        </a>
      </div>
    </div>
  );

  const supportEntryTitle = (entry) =>
    localizedOptionLabel("support.entryTitle", {
      id: entry.id,
      label: entry.title,
    });

  const renderSupportInfoPanel = (entry) => (
    <div key={entry.id} className={`${styles.panel} ${styles.communityPanel}`}>
      <h2 className={styles.panelTitle}>{supportEntryTitle(entry)}</h2>
      <p className={styles.communityCopy}>
        {localizedOptionLabel("support.entrySummary", {
          id: entry.id,
          label: entry.summary,
        })}
      </p>
      <button
        type="button"
        className={`${styles.button} ${styles.buttonPrimary}`}
        onClick={() => setSupportInfoId(entry.id)}
      >
        {supportEntryTitle(entry)}
      </button>
    </div>
  );

  const openPrivacyPolicy = () => {
    setPrivacyPolicyError(null);
    setPrivacyPolicyOpen(true);
  };

  const scrollPrivacyPolicyToAnchor = (event) => {
    const anchor = event.target.closest?.('a[href^="#"]');
    if (!anchor) return;
    event.preventDefault();
    const targetId = anchor.getAttribute("href").slice(1);
    const target = targetId
      ? event.currentTarget.querySelector(`[id="${CSS.escape(targetId)}"]`)
      : null;
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const renderSupport = () => (
    <div className={styles.communityLayout}>
      <div className={`${styles.panel} ${styles.communityPanel}`}>
        <h2 className={styles.panelTitle}>{t("support.contactTitle")}</h2>
        <p className={styles.communityCopy}>{t("support.contactCopy")}</p>
        <button
          type="button"
          className={`${styles.button} ${styles.buttonPrimary}`}
          onClick={openContactForm}
        >
          {t("support.contactTitle")}
        </button>
      </div>
      {SUPPORT_INFO_ENTRIES.filter((entry) => entry.id !== "accountData").map(
        renderSupportInfoPanel,
      )}
      <div className={`${styles.panel} ${styles.communityPanel}`}>
        <h2 className={styles.panelTitle}>{t("support.privacyTitle")}</h2>
        <p className={styles.communityCopy}>{t("support.privacyCopy")}</p>
        <ul className={styles.supportPrivacyHighlights}>
          <li>{t("support.privacyHighlightCollect")}</li>
          <li>{t("support.privacyHighlightShare")}</li>
          <li>{t("support.privacyHighlightChat")}</li>
          <li>{t("support.privacyHighlightDelete")}</li>
        </ul>
        <div className={styles.supportPrivacyActions}>
          <button
            type="button"
            className={`${styles.button} ${styles.buttonPrimary}`}
            onClick={openPrivacyPolicy}
          >
            {t("support.privacyRead")}
          </button>
          <a
            className={`${styles.button} ${styles.communityDiscordLink}`}
            href={PRIVACY_POLICY_PUBLIC_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("support.privacyOpenWeb")}
          </a>
          <a
            className={`${styles.button} ${styles.communityDiscordLink}`}
            href={DELETE_ACCOUNT_PAGE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("support.privacyDeletePage")}
          </a>
        </div>
        <p className={styles.communityCopy}>
          {t("support.privacyPublicNote", { email: SUPPORT_CONTACT_EMAIL })}
        </p>
      </div>
      {SUPPORT_INFO_ENTRIES.filter((entry) => entry.id === "accountData").map(
        renderSupportInfoPanel,
      )}
      <div className={`${styles.panel} ${styles.communityPanel}`}>
        <h2 className={styles.panelTitle}>{t("support.monetization")}</h2>
        <p className={styles.communityCopy}>{t("support.monetizationCopy")}</p>
        <button
          type="button"
          className={`${styles.button} ${styles.buttonPrimary}`}
          onClick={() => setMonetizationInfoOpen(true)}
        >
          {t("support.monetization")}
        </button>
      </div>
      <div className={`${styles.panel} ${styles.communityPanel}`}>
        <h2 className={styles.panelTitle}>{t("support.designPhilosophy")}</h2>
        <p className={styles.communityCopy}>
          {t("support.designPhilosophyCopy")}
        </p>
        <button
          type="button"
          className={`${styles.button} ${styles.buttonPrimary}`}
          onClick={() => setDesignPhilosophyOpen(true)}
        >
          {t("support.designPhilosophyButton")}
        </button>
      </div>
      <div className={`${styles.panel} ${styles.communityPanel}`}>
        <h2 className={styles.panelTitle}>{t("support.credits")}</h2>
        <p className={styles.communityCopy}>{t("support.creditsCopy")}</p>
        <button
          type="button"
          className={`${styles.button} ${styles.buttonPrimary}`}
          onClick={() => setCreditsOpen(true)}
        >
          {t("support.credits")}
        </button>
      </div>
    </div>
  );

  const renderPrivacyPolicy = () => {
    if (!privacyPolicyOpen) return null;
    return (
      <div
        className={`${styles.actionPopup} ${styles.privacyPolicyOverlay}`}
        onClick={() => setPrivacyPolicyOpen(false)}
      >
        <div
          className={styles.privacyPolicyPanel}
          onClick={(event) => event.stopPropagation()}
        >
          <div className={styles.privacyPolicyHeader}>
            <h3 className={styles.actionPopupTitle}>
              {t("support.privacyPolicy")}
            </h3>
            <button
              type="button"
              className={styles.button}
              onClick={() => setPrivacyPolicyOpen(false)}
            >
              {t("common.close")}
            </button>
          </div>
          <ScrollHintSurface className={styles.privacyPolicyBody}>
            {privacyPolicyLoading ? (
              <p className={styles.privacyPolicyStatus}>
                {t("common.loading")}
              </p>
            ) : null}
            {privacyPolicyError ? (
              <p className={styles.privacyPolicyStatus}>{privacyPolicyError}</p>
            ) : null}
            {privacyPolicyHtml ? (
              <div
                className={styles.privacyPolicyContent}
                onClick={scrollPrivacyPolicyToAnchor}
                dangerouslySetInnerHTML={{ __html: privacyPolicyHtml }}
              />
            ) : null}
          </ScrollHintSurface>
        </div>
      </div>
    );
  };

  const renderMonetizationInfo = () => {
    if (!monetizationInfoOpen) return null;
    return (
      <div
        className={`${styles.actionPopup} ${styles.privacyPolicyOverlay}`}
        onClick={() => setMonetizationInfoOpen(false)}
      >
        <div
          className={styles.privacyPolicyPanel}
          onClick={(event) => event.stopPropagation()}
        >
          <div className={styles.privacyPolicyHeader}>
            <h3 className={styles.actionPopupTitle}>
              {t("support.monetization")}
            </h3>
            <button
              type="button"
              className={styles.button}
              onClick={() => setMonetizationInfoOpen(false)}
            >
              {t("common.close")}
            </button>
          </div>
          <ScrollHintSurface className={styles.privacyPolicyBody}>
            <div className={styles.designPhilosophyLetter}>
              <p>
                <strong>Aeterna Invicta</strong> is developed with the
                assistance and support of Large Language Models (LLMs). These
                tools may be used during development for brainstorming,
                organization, drafting, technical assistance, and other
                supporting tasks. However,{" "}
                <strong>
                  all final design decisions are made by the game&apos;s
                  designer and developer
                </strong>
                .
              </p>
              <p>
                Card designs, balance changes, gameplay systems, and new
                features are ultimately reviewed, implemented, and tested by
                the designer, with player feedback and real-world play
                contributing significantly to the continued development of the
                game. LLMs are development tools, not autonomous designers or
                decision-makers for Aeterna Invicta.
              </p>
              <h4 className={styles.monetizationHeading}>
                Why Aeterna Invicta Has Monetization
              </h4>
              <p>
                Any monetization within Aeterna Invicta exists primarily to
                help cover the ongoing costs of operating the game, including
                website hosting, servers, infrastructure, development services,
                and related upkeep.
              </p>
              <p>
                Aeterna Invicta is not currently operated with the expectation
                of generating significant profit. The primary designer and
                developer generally aims to cover the costs associated with
                maintaining and developing the game and may, at times,
                personally absorb costs beyond the revenue the game generates.
              </p>
              <p>
                Player support—whether through viewing advertisements,
                purchasing optional in-game content, or simply playing and
                participating in the community—is sincerely appreciated.
              </p>
              <h4 className={styles.monetizationHeading}>
                What Is Offered Today
              </h4>
              <ul className={styles.creditsList}>
                <li>
                  <strong>Optional advertisements.</strong> After some classic
                  matches in the Android or iOS app, you may choose to watch an
                  advertisement for extra foil unlocks. Web browser players
                  receive the standard rewards without an advertisement option.
                  Advertisements are never shown unless
                  you choose to watch one, and advertising partners will be
                  configured to show ads appropriate for players 13 and up.
                </li>
                <li>
                  <strong>Earned Currency.</strong> Currency is earned by
                  playing and can be spent in the Marketplace, Customize, and
                  Draft. It has no real-world value and cannot be exchanged for
                  money.
                </li>
                <li>
                  <strong>No real-money purchases.</strong> Aeterna Invicta
                  does not currently sell Currency, cards, or other items for
                  real money. If optional purchases are added later, prices
                  will be shown before you buy, payment will be handled by the
                  app store or payment provider, and this page will be updated.
                </li>
              </ul>
              <h4 className={styles.monetizationHeading}>No Pay-to-Win</h4>
              <p>
                <strong>
                  Aeterna Invicta is not, and will not become, a pay-to-win
                  game.
                </strong>
              </p>
              <p>
                Any content available through microtransactions may also
                include items that are unlocked, earned, awarded, or otherwise
                obtained through gameplay. Regardless of how an item is
                acquired,{" "}
                <strong>
                  purchasing a microtransaction will never provide a
                  competitive gameplay advantage, increase a player&apos;s
                  power level, or make a player&apos;s cards, deck, or account
                  stronger than those of a player who spends no money.
                </strong>
              </p>
              <p>
                Monetized content is intended to remain optional and
                non-competitive. Spending money should never be a requirement
                for competing fairly or enjoying the core gameplay experience.
              </p>
              <h4 className={styles.monetizationHeading}>
                Our Commitment to Players
              </h4>
              <p>
                Players are the reason Aeterna Invicta can continue to exist and
                improve. Whether you financially support the game, watch an
                advertisement, provide feedback, report an issue, participate
                in testing, or simply play, your contribution matters.
              </p>
              <p>
                The goal is to maintain a fair collectible card game where
                gameplay decisions, deckbuilding, strategy, and player skill
                determine competitive success—not how much money someone has
                spent.
              </p>
            </div>
          </ScrollHintSurface>
        </div>
      </div>
    );
  };

  const renderDesignPhilosophy = () => {
    if (!designPhilosophyOpen) return null;
    return (
      <div
        className={`${styles.actionPopup} ${styles.privacyPolicyOverlay}`}
        onClick={() => setDesignPhilosophyOpen(false)}
      >
        <div
          className={styles.privacyPolicyPanel}
          onClick={(event) => event.stopPropagation()}
        >
          <div className={styles.privacyPolicyHeader}>
            <h3 className={styles.actionPopupTitle}>
              {t("support.designPhilosophy")}
            </h3>
            <button
              type="button"
              className={styles.button}
              onClick={() => setDesignPhilosophyOpen(false)}
            >
              {t("common.close")}
            </button>
          </div>
          <ScrollHintSurface className={styles.privacyPolicyBody}>
            <div className={styles.designPhilosophyLetter}>
              <p>
                A short letter from the designer, randomnewb.
              </p>
              <p>
                I grew up in the 90’s, playing all three of the “Big 3” trading
                card games to varying degrees. I also got to experience the
                golden era of Blizzard and played a significant amount of Diablo
                II and tens of thousands of hours of Starcraft. I come to game
                development with decades of experience, playing board games and
                card games, as well as tinkering with my own physical
                prototypes and designs.
              </p>
              <p>
                I hope that my love for game mechanics and design inspired by
                classic trading card games, modern board games, and action RPGs
                like Path of Exile are readily noticeable in Aeterna Invicta. I
                hope that I have captured the essence and feeling of gearing up
                and powering up a character to duke it out against someone else
                with the same objective.
              </p>
              <p>
                I am excited to further develop Aeterna Invicta with more
                content in future expansions to, of course, add new cards, with
                new effects, new aspects to encourage deck building and ways to
                play, balance changes to shake up the meta, and even new core
                systems to the game itself. We may see an entirely different
                game in 2.0 or 3.0 than we currently have in 1.0!
              </p>
              <p>
                So, thank you for coming along for the ride and thank you for
                playing.
              </p>
              <p className={styles.designPhilosophySignoff}>- randomnewb</p>
            </div>
          </ScrollHintSurface>
        </div>
      </div>
    );
  };

  const renderLore = () => (
    <div className={styles.communityLayout}>
      {LORE_ENTRIES.map((entry) => (
        <div
          key={entry.id}
          className={`${styles.panel} ${styles.communityPanel}`}
        >
          <h2 className={styles.panelTitle}>{entry.title}</h2>
          <p className={styles.communityCopy}>{entry.summary}</p>
          <button
            type="button"
            className={`${styles.button} ${styles.buttonPrimary}`}
            onClick={() => setLoreEntryId(entry.id)}
          >
            {t("lore.readMore")}
          </button>
        </div>
      ))}
    </div>
  );

  const renderLoreInline = (text) =>
    String(text || "")
      .split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g)
      .filter(Boolean)
      .map((part, index) => {
        if (part.length > 4 && part.startsWith("**") && part.endsWith("**")) {
          return <strong key={index}>{part.slice(2, -2)}</strong>;
        }
        if (part.length > 2 && part.startsWith("*") && part.endsWith("*")) {
          return <em key={index}>{part.slice(1, -1)}</em>;
        }
        return part;
      });

  const renderLoreList = (items, ordered, key) => {
    const ListTag = ordered ? "ol" : "ul";
    return (
      <ListTag key={key} className={styles.loreList}>
        {(items || []).map((item, index) => {
          const listItem = typeof item === "string" ? { text: item } : item;
          return (
            <li key={index} value={listItem.value}>
              {renderLoreInline(listItem.text)}
              {listItem.items
                ? renderLoreList(listItem.items, listItem.ordered, "nested")
                : null}
            </li>
          );
        })}
      </ListTag>
    );
  };

  const renderLoreBlocks = (blocks) =>
    (blocks || []).map((block, index) => {
      if (typeof block === "string") {
        return <p key={index}>{renderLoreInline(block)}</p>;
      }
      if (block.type === "heading") {
        return (
          <h4 key={index} className={styles.loreHeading}>
            {block.text}
          </h4>
        );
      }
      return renderLoreList(block.items, block.ordered, index);
    });

  const rulebookSectionDomId = (sectionId) => `ai-rulebook-${sectionId}`;

  const scrollToRulebookTarget = (domId) => {
    const target = document.getElementById(domId);
    if (!target) return;
    const reduceMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    target.scrollIntoView({
      behavior: reduceMotion ? "auto" : "smooth",
      block: "start",
    });
    target.focus({ preventScroll: true });
  };

  const renderRulebookAnchor = (domId, children, className) => (
    <a
      href={`#${domId}`}
      className={className}
      onClick={(event) => {
        event.preventDefault();
        scrollToRulebookTarget(domId);
      }}
    >
      {children}
    </a>
  );

  const renderRulebookSection = (section) => {
    const domId = rulebookSectionDomId(section.id);
    const major = section.number.endsWith(".0");
    return (
      <section
        key={section.id}
        id={domId}
        tabIndex={-1}
        className={`${styles.rulebookSection} ${
          major ? styles.rulebookSectionMajor : ""
        }`}
        aria-labelledby={`${domId}-title`}
      >
        <div className={styles.rulebookSectionHeader}>
          <h3 id={`${domId}-title`} className={styles.rulebookSectionTitle}>
            <span className={styles.rulebookSectionNumber}>
              {section.number}
            </span>
            {section.title}
          </h3>
          {section.id !== RULEBOOK_SECTIONS[0].id
            ? renderRulebookAnchor(
                "ai-rulebook-contents",
                t("rulebook.contents"),
                styles.rulebookBackLink,
              )
            : null}
        </div>
        <div className={styles.designPhilosophyLetter}>
          {renderLoreBlocks(section.blocks)}
        </div>
      </section>
    );
  };

  const renderRulebook = () => {
    const [introSection, ...bodySections] = RULEBOOK_SECTIONS;
    return (
      <div className={`${styles.panel} ${styles.rulebookPanel}`}>
        <h2 className={styles.panelTitle}>{t("rulebook.title")}</h2>
        {renderRulebookSection(introSection)}
        <nav
          id="ai-rulebook-contents"
          tabIndex={-1}
          className={styles.rulebookToc}
          aria-labelledby="ai-rulebook-contents-title"
        >
          <h3
            id="ai-rulebook-contents-title"
            className={styles.rulebookTocTitle}
          >
            {t("rulebook.tableOfContents")}
          </h3>
          <ol className={styles.rulebookTocList}>
            {RULEBOOK_SECTIONS.map((section) => (
              <li
                key={section.id}
                className={
                  section.number.endsWith(".0")
                    ? styles.rulebookTocMajor
                    : styles.rulebookTocMinor
                }
              >
                {renderRulebookAnchor(
                  rulebookSectionDomId(section.id),
                  <>
                    <span className={styles.rulebookTocNumber}>
                      {section.number}
                    </span>
                    {section.title}
                  </>,
                  styles.rulebookTocLink,
                )}
              </li>
            ))}
          </ol>
        </nav>
        {bodySections.map(renderRulebookSection)}
      </div>
    );
  };

  const renderLoreEntry = () => {
    if (!loreEntryId) return null;
    const entry = LORE_ENTRIES.find((item) => item.id === loreEntryId);
    if (!entry) return null;
    return (
      <div
        className={`${styles.actionPopup} ${styles.privacyPolicyOverlay}`}
        onClick={() => setLoreEntryId(null)}
      >
        <div
          className={styles.privacyPolicyPanel}
          onClick={(event) => event.stopPropagation()}
        >
          <div className={styles.privacyPolicyHeader}>
            <h3 className={styles.actionPopupTitle}>{entry.title}</h3>
            <button
              type="button"
              className={styles.button}
              onClick={() => setLoreEntryId(null)}
            >
              {t("common.close")}
            </button>
          </div>
          <ScrollHintSurface className={styles.privacyPolicyBody}>
            <div className={styles.designPhilosophyLetter}>
              {renderLoreBlocks(entry.blocks)}
            </div>
          </ScrollHintSurface>
        </div>
      </div>
    );
  };

  const renderSupportInfoEntry = () => {
    if (!supportInfoId) return null;
    const entry = SUPPORT_INFO_ENTRIES.find(
      (item) => item.id === supportInfoId,
    );
    if (!entry) return null;
    return (
      <div
        className={`${styles.actionPopup} ${styles.privacyPolicyOverlay}`}
        onClick={() => setSupportInfoId(null)}
      >
        <div
          className={styles.privacyPolicyPanel}
          onClick={(event) => event.stopPropagation()}
        >
          <div className={styles.privacyPolicyHeader}>
            <h3 className={styles.actionPopupTitle}>
              {supportEntryTitle(entry)}
            </h3>
            <button
              type="button"
              className={styles.button}
              onClick={() => setSupportInfoId(null)}
            >
              {t("common.close")}
            </button>
          </div>
          <ScrollHintSurface className={styles.privacyPolicyBody}>
            <div className={styles.designPhilosophyLetter}>
              {renderLoreBlocks(entry.blocks)}
            </div>
          </ScrollHintSurface>
        </div>
      </div>
    );
  };

  const renderCredits = () => {
    if (!creditsOpen) return null;
    return (
      <div
        className={`${styles.actionPopup} ${styles.privacyPolicyOverlay}`}
        onClick={() => setCreditsOpen(false)}
      >
        <div
          className={styles.privacyPolicyPanel}
          onClick={(event) => event.stopPropagation()}
        >
          <div className={styles.privacyPolicyHeader}>
            <h3 className={styles.actionPopupTitle}>{t("support.credits")}</h3>
            <button
              type="button"
              className={styles.button}
              onClick={() => setCreditsOpen(false)}
            >
              {t("common.close")}
            </button>
          </div>
          <ScrollHintSurface className={styles.privacyPolicyBody}>
            <div className={styles.designPhilosophyLetter}>
              <p>
                A big thank you to my friends, family, and playtesters, for which
                I would not be able to have such a fine game without.
              </p>
              <ul className={styles.creditsList}>
                <li>
                  <strong>Rhematic</strong> — For playtesting every step of the
                  way, general balance support, and UI/UX testing
                </li>
                <li>
                  <strong>Grabbitbros</strong> — For playtesting,
                  encouragement, and thought partnership (and he runs the
                  coolest Japanese-style Claw Arcade in Minnesota:{" "}
                  <a
                    href="https://www.grabbitarcade.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Grabbit Arcade
                  </a>
                  )
                </li>
                <li>
                  <strong>“Ninja” Quach</strong> — For playtesting, and people
                  literally call him Ninja, that&apos;s pretty awesome
                </li>
                <li>
                  <strong>@TheLilaDoez</strong> — For spending a ton of time
                  suggesting UI/UX changes and playtesting not only this game,
                  but many of my other games. Please reach out on Discord
                  because @TheLilaDoez is very talented at UI/UX!!!
                </li>
              </ul>
              <p>
                And of course, the publisher, ATN Dev. Please check out their
                other work here:{" "}
                <a
                  href="https://play.google.com/store/apps/developer?id=ATN+Software"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://play.google.com/store/apps/developer?id=ATN+Software
                </a>
              </p>
              <h4 className={styles.monetizationHeading}>
                Open Source Acknowledgements
              </h4>
              <p>
                The holographic &quot;trading card&quot; hover display, including
                the card tilt, glare, and foil effects, was adapted from the
                work of <strong>@simeydotme</strong> (Simon Goellner). Thank you
                for sharing such fantastic work:
              </p>
              <ul className={styles.creditsList}>
                <li>
                  <strong>Pokémon Cards Holographic effect in CSS</strong> —{" "}
                  <a
                    href="https://github.com/simeydotme/pokemon-cards-css"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://github.com/simeydotme/pokemon-cards-css
                  </a>
                </li>
                <li>
                  <strong>Hover Tilt</strong> —{" "}
                  <a
                    href="https://github.com/simeydotme/hover-tilt"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://github.com/simeydotme/hover-tilt
                  </a>
                </li>
              </ul>
              <p>
                Various pattern backgrounds are from <strong>Hero Patterns</strong>{" "}
                by Steve Schoger, licensed under CC BY 4.0:
              </p>
              <ul className={styles.creditsList}>
                <li>
                  <strong>Hero Patterns</strong> —{" "}
                  <a
                    href="https://heropatterns.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://heropatterns.com/
                  </a>
                </li>
              </ul>
            </div>
          </ScrollHintSurface>
        </div>
      </div>
    );
  };

  const renderRanking = () => {
    const canGoPrev = rankingPage > 0;
    const canGoNext = rankingPage + 1 < rankingTotalPages;
    return (
      <div className={styles.rankingLayout}>
        <div className={`${styles.panel} ${styles.rankingPanel}`}>
          <h2 className={styles.panelTitle}>{t("nav.ranking")}</h2>
          <div className={styles.rankingSortRow}>
            <button
              type="button"
              className={`${styles.button} ${
                rankingSortBy === "cpu" ? styles.buttonPrimary : ""
              }`}
              onClick={() => {
                setRankingSortBy("cpu");
                setRankingPage(0);
              }}
            >
              {t("ranking.cpuWinRatio")}
            </button>
            <button
              type="button"
              className={`${styles.button} ${
                rankingSortBy === "human" ? styles.buttonPrimary : ""
              }`}
              onClick={() => {
                setRankingSortBy("human");
                setRankingPage(0);
              }}
            >
              {t("ranking.humanWinRatio")}
            </button>
          </div>
          <div
            className={`${styles.rankingHeaderRow} ${styles.rankingColumnsSingle}`}
          >
            <span className={styles.rankingColRank}>#</span>
            <span className={styles.rankingColName}>{t("common.player")}</span>
            <span className={styles.rankingColRecord}>
              {rankingSortBy === "human"
                ? t("ranking.humanRecord")
                : t("ranking.cpuRecord")}
            </span>
          </div>
          <div className={styles.rankingList}>
            {rankingEntries.map((entry) => (
              <div
                key={entry.username}
                className={`${styles.rankingRow} ${styles.rankingColumnsSingle}`}
              >
                <span className={styles.rankingColRank}>#{entry.rank}</span>
                <button
                  type="button"
                  className={`${styles.socialPlayerName} ${styles.rankingColName}`}
                  onClick={() => openSocialPlayer(entry)}
                >
                  {entry.username}
                </button>
                <span className={styles.rankingColRecord}>
                  {formatRecordTriplet(
                    rankingSortBy === "human"
                      ? entry.humanRecord
                      : entry.cpuRecord,
                  )}
                </span>
              </div>
            ))}
            {!rankingEntries.length && (
              <div className={styles.detailLine}>{t("social.noPlayers")}</div>
            )}
          </div>
          <div className={styles.rankingPager}>
            <span className={styles.rankingPagerMeta}>
              {rankingTotal
                ? t("deck.pager.pageOf", {
                    page: rankingPage + 1,
                    total: rankingTotalPages,
                  })
                : t("deck.pager.pageOf", { page: 1, total: 1 })}
            </span>
            <div className={styles.rankingPagerButtons}>
              <button
                type="button"
                className={styles.button}
                disabled={!canGoPrev}
                onClick={() => setRankingPage((current) => Math.max(0, current - 1))}
              >
                {t("ranking.prev")}
              </button>
              <button
                type="button"
                className={styles.button}
                disabled={!canGoNext}
                onClick={() =>
                  setRankingPage((current) =>
                    Math.min(rankingTotalPages - 1, current + 1),
                  )
                }
              >
                {t("ranking.next")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSocialPlayerMenu = () => {
    if (!socialMenuPlayer) return null;
    const entry = socialMenuPlayer;
    const isSelf = entry.username === username;
    const canChallenge =
      !isSelf &&
      !blockedUsernameSet.has(entry.username) &&
      entry.online &&
      entry.status !== "In Game" &&
      entry.status !== "In CPU Game" &&
      entry.status !== "In Workshop";
    const canSpectate =
      !isSelf &&
      entry.online &&
      (entry.status === "In Game" || entry.status === "In CPU Game") &&
      !inLiveMatch &&
      !awaitingSummary;
    const record = socialProfile?.record || {
      wins: 0,
      losses: 0,
      draws: 0,
      gamesPlayed: 0,
    };
    const draftRecord = socialProfile?.draftRecord || {
      wins: 0,
      losses: 0,
      draws: 0,
      gamesPlayed: 0,
    };
    const bestDraftRun = socialProfile?.bestDraftRun || null;
    const decks = Array.isArray(socialProfile?.decks)
      ? socialProfile.decks
      : [];
    const activeDeckPairId =
      socialProfile?.activeDeckPairId || socialProfile?.deck?.id || null;
    return (
      <div className={styles.actionPopup} onClick={closeSocialPlayer}>
        <ScrollHintSurface
          className={`${styles.actionPopupPanel} ${styles.socialProfilePanel}`}
          onClick={(event) => event.stopPropagation()}
        >
          <h3 className={styles.actionPopupTitle}>{entry.username}</h3>
          <div className={styles.detailBlock}>
            <div className={styles.detailLine}>
              {t("draft.games", { games: record.gamesPlayed })}
            </div>
            <div className={styles.detailLine}>
              {t("draft.record", {
                wins: record.wins,
                losses: record.losses,
                draws: record.draws,
              })}
            </div>
          </div>
          <div className={styles.detailBlock}>
            <div className={styles.detailLine}>{t("social.draftMode")}</div>
            <div className={styles.detailLine}>
              {t("draft.games", { games: draftRecord.gamesPlayed })}
            </div>
            <div className={styles.detailLine}>
              {t("draft.record", {
                wins: draftRecord.wins,
                losses: draftRecord.losses,
                draws: draftRecord.draws,
              })}
            </div>
            {bestDraftRun ? (
              <>
                <div className={styles.detailLine}>
                  {t("social.bestDraftRun", {
                    wins: bestDraftRun.wins,
                    losses: bestDraftRun.losses,
                    draws: bestDraftRun.draws,
                    lives: bestDraftRun.livesRemaining,
                    games: bestDraftRun.gamesPlayed,
                  })}
                </div>
                <div className={styles.row}>
                  <button
                    type="button"
                    className={styles.button}
                    onClick={() =>
                      setSocialDeckView({
                        name: t("draft.deckTitle"),
                        equipmentIds: bestDraftRun.deck?.equipmentIds || [],
                        abilityIds: bestDraftRun.deck?.abilityIds || [],
                        aspectId:
                          bestDraftRun.deck?.aspectId ||
                          bestDraftRun.deck?.aspectIds?.[0] ||
                          null,
                      })
                    }
                  >
                    {t("draft.deckTitle")}
                  </button>
                  <button
                    type="button"
                    className={styles.button}
                    onClick={() =>
                      setSocialDeckView({
                        name: t("social.draftPool"),
                        equipmentIds: bestDraftRun.pool?.equipmentIds || [],
                        abilityIds: bestDraftRun.pool?.abilityIds || [],
                        aspectId:
                          bestDraftRun.pool?.aspectId ||
                          bestDraftRun.pool?.aspectIds?.[0] ||
                          null,
                      })
                    }
                  >
                    {t("social.draftPool")}
                  </button>
                </div>
              </>
            ) : (
              <div className={styles.detailLine}>{t("social.noDraftRun")}</div>
            )}
          </div>
          <div className={styles.socialPlayerActions}>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary}`}
              disabled={!canChallenge || awaitingSummary || inLiveMatch}
              onClick={() => {
                challengeSocialPlayer(entry.username);
                closeSocialPlayer();
              }}
            >
              {t("match.popup.challengeTitle")}
            </button>
            {canSpectate ? (
              <button
                type="button"
                className={`${styles.button} ${styles.buttonPrimary}`}
                onClick={() => {
                  spectateSocialPlayer(entry.username);
                  closeSocialPlayer();
                }}
              >
                {t("social.spectateGame")}
              </button>
            ) : null}
            {renderPlayerSafetyActions(
              entry.username,
              entry.source === "chat" ? "chat" : "profile",
            )}
            <button
              type="button"
              className={styles.button}
              onClick={closeSocialPlayer}
            >
              {t("common.close")}
            </button>
          </div>
          {blockedUsernameSet.has(entry.username) ? (
            <div className={styles.muted}>{t("social.blockedNotice")}</div>
          ) : null}
          <div className={styles.socialDeckSection}>
            <div className={styles.socialDeckLabel}>{t("nav.decks")}</div>
            <div className={styles.socialDeckNameList}>
              {decks.map((deck) => {
                const isActive = deck.id === activeDeckPairId;
                return (
                  <button
                    key={deck.id}
                    type="button"
                    className={styles.socialDeckNameButton}
                    onClick={() => setSocialDeckView(deck)}
                  >
                    {isActive ? `${ACTIVE_DECK_MARKER} ` : ""}
                    {deck.name || t("deck.defaultName")}
                  </button>
                );
              })}
              {!decks.length && (
                <div className={styles.detailLine}>{t("social.noDecks")}</div>
              )}
            </div>
          </div>
        </ScrollHintSurface>
      </div>
    );
  };

  const renderSocialDeckView = () => {
    if (!socialDeckView) return null;
    const deck = socialDeckView;
    const aspectCard = deck.aspectId ? catalogMap[deck.aspectId] || null : null;
    const equipmentCards = (deck.equipmentIds || [])
      .map((id) => catalogMap[id])
      .filter(Boolean);
    const abilityCards = (deck.abilityIds || [])
      .map((id) => catalogMap[id])
      .filter(Boolean);
    const { equipmentPower, abilityPower, aspectPower, totalPower } =
      getDeckPairPowerTotals(
        deck.equipmentIds || [],
        deck.abilityIds || [],
        deck.aspectId,
        catalogMap,
      );
    return (
      <div
        className={styles.actionPopup}
        onClick={() => setSocialDeckView(null)}
      >
        <ScrollHintSurface
          className={`${styles.actionPopupPanel} ${styles.socialProfilePanel}`}
          onClick={(event) => event.stopPropagation()}
        >
          <h3 className={styles.actionPopupTitle}>
            {deck.name || t("deck.defaultName")}
          </h3>
          <div className={styles.muted}>
            {t("match.view.deckPower", {
              total: totalPower,
              max: MAX_DECK_POWER,
            })}
          </div>
          {aspectCard ? (
            <div className={styles.socialDeckSection}>
              <div className={styles.socialDeckLabel}>
                {t("match.view.deckAspectPower", { power: aspectPower })}
              </div>
              <div className={styles.socialDeckGrid}>
                <CardFace
                  key={`as-${aspectCard.id}`}
                  card={aspectCard}
                  selected={examCardId === aspectCard.id}
                  onClick={() => openCardExam(aspectCard.id)}
                  activeKeywordId={keywordTip?.id || null}
                  onKeywordClick={(keyword) => {
                    openKeywordTip(keyword);
                    openCardExam(aspectCard.id);
                  }}
                />
              </div>
            </div>
          ) : null}
          <div className={styles.socialDeckSection}>
            <div className={styles.socialDeckLabel}>
              {t("social.deckEquipmentPower", { power: equipmentPower })}
            </div>
            <div className={styles.socialDeckGrid}>
              {equipmentCards.map((card, index) => (
                <CardFace
                  key={`eq-${card.id}-${index}`}
                  card={card}
                  selected={examCardId === card.id}
                  onClick={() => openCardExam(card.id)}
                  activeKeywordId={keywordTip?.id || null}
                  onKeywordClick={(keyword) => {
                    openKeywordTip(keyword);
                    openCardExam(card.id);
                  }}
                />
              ))}
            </div>
          </div>
          <div className={styles.socialDeckSection}>
            <div className={styles.socialDeckLabel}>
              {t("social.deckAbilitiesPower", { power: abilityPower })}
            </div>
            <div className={styles.socialDeckGrid}>
              {abilityCards.map((card, index) => (
                <CardFace
                  key={`ab-${card.id}-${index}`}
                  card={card}
                  selected={examCardId === card.id}
                  onClick={() => openCardExam(card.id)}
                  activeKeywordId={keywordTip?.id || null}
                  onKeywordClick={(keyword) => {
                    openKeywordTip(keyword);
                    openCardExam(card.id);
                  }}
                />
              ))}
            </div>
          </div>
          <div className={styles.row}>
            <button
              type="button"
              className={styles.button}
              onClick={() => setSocialDeckView(null)}
            >
              {t("common.close")}
            </button>
          </div>
        </ScrollHintSurface>
      </div>
    );
  };

  const renderCatalogFilters = () => (
    <div className={styles.catalogTools}>
      <div className={styles.filters}>
        <select
          className={styles.select}
          value={catalogCategory}
          onChange={(event) => changeCatalogCategory(event.target.value)}
        >
          <option value="all">{t("deck.filters.allCards")}</option>
          <option value="aspect">{t("deck.aspect")}</option>
          <option value="equipment">{t("deck.equipment")}</option>
          <option value="ability">{t("deck.ability")}</option>
        </select>
        <select
          className={styles.select}
          value={filterAttr}
          onChange={(event) => setFilterAttr(event.target.value)}
        >
          <option value="all">{t("deck.filters.allAttributes")}</option>
          <option value="strength">{t("deck.attr.strength")}</option>
          <option value="dexterity">{t("deck.attr.dexterity")}</option>
          <option value="wisdom">{t("deck.attr.wisdom")}</option>
          <option value="neutral">{t("deck.attr.neutral")}</option>
        </select>
        {catalogCategory !== "aspect" ? (
          <select
            className={styles.select}
            value={catalogCost}
            onChange={(event) => setCatalogCost(event.target.value)}
          >
            <option value="all">
              {catalogCategory === "equipment"
                ? t("deck.filters.allResources")
                : catalogCategory === "ability"
                  ? t("deck.filters.allCosts")
                  : t("deck.filters.allCostsResources")}
            </option>
            <option value="0">0</option>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3+">3+</option>
          </select>
        ) : null}
        {catalogShowsEquipmentFilters ? (
          <select
            className={styles.select}
            value={catalogTier}
            onChange={(event) => setCatalogTier(event.target.value)}
          >
            <option value="all">{t("deck.filters.allTiers")}</option>
            {EQUIPMENT_TIER_FILTER_OPTIONS.map((tier) => (
              <option key={tier} value={tier}>
                {t("match.panel.tierN", { tier })}
              </option>
            ))}
          </select>
        ) : null}
      </div>
      <div className={styles.row}>
        <button
          type="button"
          className={styles.button}
          onClick={() => setCatalogFiltersOpen((open) => !open)}
        >
          {catalogFiltersOpen ? t("deck.filters.fewer") : t("deck.filters.more")}
        </button>
        {playerHasAnyHolo ? (
          <button
            type="button"
            className={`${styles.button} ${
              catalogHolo === "holo" ? styles.buttonPrimary : ""
            }`}
            aria-pressed={catalogHolo === "holo"}
            onClick={() =>
              setCatalogHolo((value) => (value === "holo" ? "all" : "holo"))
            }
          >
            {t("deck.filters.holoUnlocked")}
          </button>
        ) : null}
        <button
          type="button"
          className={`${styles.button} ${
            catalogOwned !== "all" ? styles.buttonPrimary : ""
          }`}
          aria-pressed={catalogOwned !== "all"}
          onClick={() =>
            setCatalogOwned((value) =>
              value === "all" ? "unlocked" : value === "unlocked" ? "locked" : "all",
            )
          }
        >
          {catalogOwned === "unlocked"
            ? t("deck.filters.cardsUnlocked")
            : catalogOwned === "locked"
              ? t("deck.filters.cardsLocked")
              : t("deck.filters.cardsAll")}
        </button>
        <button
          type="button"
          className={styles.button}
          onClick={clearCatalogFilters}
        >
          {t("deck.filters.clear")}
        </button>
      </div>
      {catalogFiltersOpen ? (
        <>
          <input
            className={`${styles.input} ${styles.catalogSearch}`}
            type="search"
            value={catalogSearch}
            placeholder={t("deck.filters.searchPlaceholder")}
            onChange={(event) => setCatalogSearch(event.target.value)}
          />
          <div className={styles.filters}>
            {catalogCategory === "equipment" && catalogSlotOptions.length ? (
              <select
                className={styles.select}
                value={catalogSlot}
                onChange={(event) => setCatalogSlot(event.target.value)}
              >
                <option value="all">{t("deck.filters.allSlots")}</option>
                {catalogSlotOptions.map((slot) => (
                  <option key={slot} value={slot}>
                    {formatSlotLabel(slot)}
                  </option>
                ))}
              </select>
            ) : null}
            {catalogShowsEquipmentFilters && catalogItemTypeOptions.length ? (
              <select
                className={styles.select}
                value={catalogItemType}
                onChange={(event) => setCatalogItemType(event.target.value)}
              >
                <option value="all">{t("deck.filters.allItemTypes")}</option>
                {renderCardTypeFilterOptions(catalogItemTypeOptions)}
              </select>
            ) : null}
            {catalogCategory === "ability" ? (
              <select
                className={styles.select}
                value={catalogKind}
                onChange={(event) => setCatalogKind(event.target.value)}
              >
                <option value="all">{t("deck.filters.allKinds")}</option>
                <option value="active">{t("deck.kind.active")}</option>
                <option value="augment">{t("deck.kind.augment")}</option>
                <option value="essence">{t("deck.kind.essence")}</option>
              </select>
            ) : null}
            {catalogTypeOptions.length ? (
              <select
                className={styles.select}
                value={filterType}
                onChange={(event) => setFilterType(event.target.value)}
              >
                <option value="all">{t("deck.filters.allTypes")}</option>
                {renderCardTypeFilterOptions(catalogTypeOptions, {
                  includePlain: catalogCategory !== "aspect",
                })}
              </select>
            ) : null}
            {catalogEffectOptions.length ? (
              <select
                className={styles.select}
                value={catalogEffect}
                onChange={(event) => setCatalogEffect(event.target.value)}
              >
                <option value="all">{t("deck.filters.allEffects")}</option>
                {catalogEffectOptions.map((effectId) => (
                  <option key={effectId} value={effectId}>
                    {formatEffectFilterLabel(effectId)}
                  </option>
                ))}
              </select>
            ) : null}
            <select
              className={styles.select}
              value={effectiveCatalogSort}
              onChange={(event) => setCatalogSort(event.target.value)}
            >
              {catalogSortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {t("deck.filters.sortBy", { label: option.label })}
                </option>
              ))}
            </select>
          </div>
        </>
      ) : null}
      <div className={styles.catalogResultCount}>
        {t("deck.catalog.resultCount", { count: filteredCatalog.length })}
      </div>
    </div>
  );

  const renderDeckVariantPicker = () => {
    if (!deckVariantPicker) return null;
    const card = catalogMap[deckVariantPicker.cardId];
    if (!card) return null;
    const unlocked = sortFoilEffects(player?.unlockedHolos?.[card.id] || []);
    return (
      <div
        className={`${styles.actionPopup} ${styles.deckVariantPicker}`}
        onClick={() => setDeckVariantPicker(null)}
      >
        <div
          className={`${styles.actionPopupPanel} ${styles.deckVariantPickerPanel}`}
          onClick={(event) => event.stopPropagation()}
        >
          <h3 className={styles.actionPopupTitle}>{card.name}</h3>
          <label className={styles.cardExamFoilSelect}>
            <span className={styles.muted}>{t("deck.variant.label")}</span>
            <select
              className={styles.select}
              value={deckVariantPicker.foilEffect || ""}
              onChange={(event) =>
                setDeckVariantPicker((current) =>
                  current
                    ? {
                        ...current,
                        foilEffect: event.target.value || null,
                      }
                    : current,
                )
              }
            >
              <option value="">{t("deck.variant.base")}</option>
              {unlocked.map((foil) => (
                <option key={foil} value={foil}>
                  {foilEffectLabel(foil)}
                </option>
              ))}
            </select>
          </label>
          <div className={styles.row}>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary}`}
              onClick={() => {
                setDraftSlot(
                  deckVariantPicker.deckType,
                  deckVariantPicker.index,
                  deckVariantPicker.cardId,
                  deckVariantPicker.foilEffect || null,
                );
                setDeckVariantPicker(null);
                setSlotPicker(null);
              }}
            >
              {t("deck.variant.confirm")}
            </button>
            <button
              type="button"
              className={styles.button}
              onClick={() => setDeckVariantPicker(null)}
            >
              {t("common.cancel")}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCatalogPager = (position) => {
    const goToPage = (page) => {
      setCatalogPage(Math.max(0, Math.min(catalogPageCount - 1, page)));
      if (position === "bottom") {
        catalogPagerTopRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    };
    return (
      <div
        ref={position === "top" ? catalogPagerTopRef : undefined}
        className={styles.catalogPager}
      >
        <button
          type="button"
          className={`${styles.button} ${styles.catalogPagerArrow}`}
          aria-label={t("deck.pager.previous")}
          disabled={currentCatalogPage <= 0}
          onClick={() => goToPage(currentCatalogPage - 1)}
        >
          ‹
        </button>
        <span className={styles.catalogPagerMeta}>
          {t("deck.pager.pageOf", { page: currentCatalogPage + 1, total: catalogPageCount })}
        </span>
        <button
          type="button"
          className={`${styles.button} ${styles.catalogPagerArrow}`}
          aria-label={t("deck.pager.next")}
          disabled={currentCatalogPage >= catalogPageCount - 1}
          onClick={() => goToPage(currentCatalogPage + 1)}
        >
          ›
        </button>
        {position === "top" ? (
          <select
            className={`${styles.select} ${styles.catalogPagerSize}`}
            value={String(catalogPageSize)}
            onChange={(event) =>
              setCatalogPageSize(
                event.target.value === "all" ? "all" : Number(event.target.value),
              )
            }
          >
            <option value="9">{t("deck.pager.perPage", { n: 9 })}</option>
            <option value="18">{t("deck.pager.perPage", { n: 18 })}</option>
            <option value="27">{t("deck.pager.perPage", { n: 27 })}</option>
            <option value="36">{t("deck.pager.perPage", { n: 36 })}</option>
            <option value="all">{t("deck.pager.all")}</option>
          </select>
        ) : null}
      </div>
    );
  };

  const renderCollection = () => (
    <div className={styles.panel}>
      <h2 className={styles.panelTitle}>{t("library.collection")}</h2>
      {renderCatalogFilters()}
      {renderCatalogPager("top")}
      <DenseCardGrid
        className={styles.collectionGrid}
        cards={pagedCatalog}
        selectedCardId={examCardId}
        collection={player?.collection}
        lockUnowned
        onOpenCard={openCardExam}
      />
      {catalogPageCount > 1 ? renderCatalogPager("bottom") : null}
    </div>
  );

  const renderLibraryDeckModal = () => {
    const pair =
      starterDecks.find((entry) => entry.id === libraryDeckId) || null;
    if (!pair) return null;
    const equipmentCards = (pair.equipmentIds || [])
      .map((cardId) => catalogMap[cardId])
      .filter(Boolean);
    const abilityCards = (pair.abilityIds || [])
      .map((cardId) => catalogMap[cardId])
      .filter(Boolean);
    const aspectCard = pair.aspectId ? catalogMap[pair.aspectId] : null;
    const { equipmentPower, abilityPower, aspectPower, totalPower } =
      getDeckPairPowerTotals(
        pair.equipmentIds || [],
        pair.abilityIds || [],
        pair.aspectId,
        catalogMap,
      );
    const renderLibraryCard = (card, key) => {
      const owned = playerOwnsCard(player?.collection, card.id);
      return (
        <CardFace
          key={key}
          card={card}
          compact
          fitText={false}
          locked={!owned}
          showLockedIcon={!owned}
          highlightClass={owned ? "" : ` ${styles.libraryDeckCardLocked}`}
          selected={examCardId === card.id}
          onClick={() => openCardExam(card.id)}
          activeKeywordId={keywordTip?.id || null}
          onKeywordClick={(keyword) => {
            openKeywordTip(keyword);
            openCardExam(card.id);
          }}
        />
      );
    };
    return (
      <div
        className={styles.actionPopup}
        onClick={() => setLibraryDeckId(null)}
      >
        <ScrollHintSurface
          className={`${styles.actionPopupPanel} ${styles.libraryDeckPanel}`}
          onClick={(event) => event.stopPropagation()}
        >
          <h3 className={styles.actionPopupTitle}>{pair.name}</h3>
          <div className={styles.muted}>
            {t("match.view.deckPower", {
              total: totalPower,
              max: MAX_DECK_POWER,
            })}
          </div>
          {aspectCard ? (
            <div className={styles.libraryDeckSection}>
              <h4 className={styles.libraryDeckSectionTitle}>
                {t("match.view.deckAspectPower", { power: aspectPower })}
              </h4>
              <div className={styles.libraryDeckGrid}>
                {renderLibraryCard(
                  aspectCard,
                  `library-as-${pair.id}-${aspectCard.id}`,
                )}
              </div>
            </div>
          ) : null}
          <div className={styles.libraryDeckSection}>
            <h4 className={styles.libraryDeckSectionTitle}>
              {t("social.deckEquipmentPower", { power: equipmentPower })}
            </h4>
            <div className={styles.libraryDeckGrid}>
              {equipmentCards.map((card, index) =>
                renderLibraryCard(
                  card,
                  `library-eq-${pair.id}-${index}-${card.id}`,
                ),
              )}
            </div>
          </div>
          <div className={styles.libraryDeckSection}>
            <h4 className={styles.libraryDeckSectionTitle}>
              {t("library.deckAbilityPower", { power: abilityPower })}
            </h4>
            <div className={styles.libraryDeckGrid}>
              {abilityCards.map((card, index) =>
                renderLibraryCard(
                  card,
                  `library-ab-${pair.id}-${index}-${card.id}`,
                ),
              )}
            </div>
          </div>
          <div className={`${styles.row} ${styles.libraryDeckActions}`}>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary}`}
              onClick={() => openLibraryDeckExport(pair)}
            >
              {t("library.export")}
            </button>
            <button
              type="button"
              className={styles.button}
              onClick={() => setLibraryDeckId(null)}
            >
              {t("common.close")}
            </button>
          </div>
        </ScrollHintSurface>
      </div>
    );
  };

  const renderLibraryResetConfirm = () => {
    if (!libraryResetConfirm) return null;
    return (
      <div
        className={styles.actionPopup}
        onClick={() => setLibraryResetConfirm(false)}
      >
        <ScrollHintSurface
          className={styles.actionPopupPanel}
          onClick={(event) => event.stopPropagation()}
        >
          <h3 className={styles.actionPopupTitle}>
            {t("library.resetDecksTitle")}
          </h3>
          <div className={styles.muted}>{t("library.resetDecksBody")}</div>
          <div className={styles.row}>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonDanger}`}
              onClick={() => {
                send({ type: "ai:resetLibraryDecks" });
                setLibraryResetConfirm(false);
              }}
              disabled={Boolean(match) && match.phase !== "ended" && !match.workshop}
            >
              {t("library.resetDecks")}
            </button>
            <button
              type="button"
              className={styles.button}
              onClick={() => setLibraryResetConfirm(false)}
            >
              {t("common.cancel")}
            </button>
          </div>
        </ScrollHintSurface>
      </div>
    );
  };

  const openLibrarySection = (section) => {
    playSfx("uiSwitch");
    closeCardExam();
    setLibraryDeckId(null);
    setLibraryResetConfirm(false);
    setDeckShare(null);
    setLoreEntryId(null);
    setLibrarySection(section);
  };

  const renderLibrary = () => {
    if (!librarySection) {
      return (
        <div className={styles.panel}>
          <h2 className={styles.panelTitle}>{t("nav.library")}</h2>
          <div className={styles.muted}>{t("library.intro")}</div>
          <div className={styles.adminNavGrid}>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary} ${styles.adminNavButton}`}
              onClick={() => requestViewFromNav("collection")}
            >
              {t("library.collection")}
            </button>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary} ${styles.adminNavButton}`}
              onClick={() => openLibrarySection("decks")}
            >
              {t("nav.decks")}
            </button>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary} ${styles.adminNavButton}`}
              onClick={() => openLibrarySection("rulebook")}
            >
              {t("library.rulebook")}
            </button>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary} ${styles.adminNavButton}`}
              onClick={() => openLibrarySection("glossary")}
            >
              {t("library.glossary")}
            </button>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary} ${styles.adminNavButton}`}
              onClick={() => openLibrarySection("lore")}
            >
              {t("library.lore")}
            </button>
          </div>
        </div>
      );
    }
    return (
      <>
        <div className={styles.row}>
          <button
            type="button"
            className={styles.button}
            onClick={() => openLibrarySection(null)}
          >
            {t("common.back")}
          </button>
        </div>
        {librarySection === "decks" ? renderLibraryDecks() : null}
        {librarySection === "rulebook" ? renderRulebook() : null}
        {librarySection === "lore" ? renderLore() : null}
      </>
    );
  };

  const renderLibraryDecks = () => (
    <div className={styles.panel}>
      <h2 className={styles.panelTitle}>{t("nav.decks")}</h2>
      <p className={styles.muted}>{t("library.decksIntro")}</p>
      <div className={styles.libraryDeckPicker}>
        {starterDecks.map((pair) => {
          const canImport = deckUsesOnlyOwnedCards(
            pair.equipmentIds || [],
            pair.abilityIds || [],
            pair.aspectId,
            player?.collection,
          );
          return (
            <button
              key={pair.id}
              type="button"
              className={`${styles.libraryDeckOption} ${
                libraryDeckId === pair.id ? styles.libraryDeckOptionActive : ""
              } ${canImport ? "" : styles.libraryDeckOptionUnavailable}`}
              onClick={() => setLibraryDeckId(pair.id)}
              aria-pressed={libraryDeckId === pair.id}
              title={
                canImport
                  ? undefined
                  : t("library.missingCards")
              }
            >
              <span
                className={styles.libraryDeckOptionSquare}
                aria-hidden="true"
              >
                {(pair.name || "?").trim().charAt(0).toUpperCase() || "?"}
              </span>
              <span className={styles.libraryDeckOptionLabel}>{pair.name}</span>
            </button>
          );
        })}
      </div>
      {!starterDecks.length ? (
        <div className={styles.muted}>{t("library.noDecks")}</div>
      ) : null}
      <div className={styles.libraryResetRow}>
        <button
          type="button"
          className={`${styles.button} ${styles.buttonDanger}`}
          onClick={() => setLibraryResetConfirm(true)}
          disabled={
            !starterDecks.length ||
            (Boolean(match) && match.phase !== "ended" && !match.workshop)
          }
        >
          {t("library.resetDecks")}
        </button>
      </div>
      {renderLibraryDeckModal()}
      {renderLibraryResetConfirm()}
    </div>
  );

  const renderGlossary = () => {
    const selected = glossarySelectedEntry;
    const relatedCards = glossaryRelatedCards;
    if (selected) {
      return (
        <div className={`${styles.panel} ${styles.glossaryPanel}`}>
          <div className={styles.glossaryDetailSticky}>
            <button
              type="button"
              className={`${styles.button} ${styles.glossaryBackButton}`}
              onClick={() => setGlossarySelectedId(null)}
            >
              {t("glossary.backToSearch")}
            </button>
          </div>
          <div className={styles.glossaryDetail}>
            <h2 className={styles.glossaryDetailTitle}>{selected.label}</h2>
            <p className={styles.glossaryDetailBody}>{selected.description}</p>
            <h3 className={styles.libraryDeckSectionTitle}>
              {t("glossary.relatedCards", { n: relatedCards.length })}
            </h3>
            {relatedCards.length ? (
              <div className={`${styles.cardGrid} ${styles.glossaryCardGrid}`}>
                {relatedCards.map((card) => (
                  <CardFace
                    key={card.id}
                    card={card}
                    fitText={false}
                    selected={examCardId === card.id}
                    onClick={() => openCardExam(card.id)}
                  />
                ))}
              </div>
            ) : (
              <div className={styles.muted}>{t("glossary.noRelatedCards")}</div>
            )}
          </div>
        </div>
      );
    }
    return (
      <div className={`${styles.panel} ${styles.glossaryPanel}`}>
        <h2 className={styles.panelTitle}>{t("library.glossary")}</h2>
        <label className={styles.queueDeckLabel} htmlFor="ai-glossary-search">
          {t("glossary.search")}
        </label>
        <input
          id="ai-glossary-search"
          className={styles.input}
          type="search"
          value={glossarySearch}
          onChange={(event) => setGlossarySearch(event.target.value)}
          placeholder={t("glossary.searchPlaceholder")}
        />
        <div className={styles.glossaryList}>
          {glossaryEntries.map((entry) => (
            <button
              key={entry.id}
              type="button"
              className={styles.glossaryEntry}
              onClick={() => setGlossarySelectedId(entry.id)}
            >
              {entry.label}
            </button>
          ))}
          {!glossaryEntries.length ? (
            <div className={styles.muted}>{t("glossary.noMatches")}</div>
          ) : null}
        </div>
      </div>
    );
  };

  const renderDeckShareModal = () => {
    if (!deckShare) return null;
    return (
      <div className={styles.actionPopup} onClick={closeDeckShare}>
        <ScrollHintSurface
          className={`${styles.actionPopupPanel} ${styles.deckSharePanel}`}
          onClick={(event) => event.stopPropagation()}
        >
          <h3 className={styles.actionPopupTitle}>
            {deckShare.mode === "export" ? t("deck.share.exportTitle") : t("deck.share.importTitle")}
          </h3>
          <textarea
            ref={deckShareTextareaRef}
            className={`${styles.input} ${styles.deckShareTextarea}`}
            readOnly={deckShare.mode === "export"}
            value={deckShare.text}
            onChange={
              deckShare.mode === "import"
                ? (event) =>
                    setDeckShare((current) =>
                      current
                        ? {
                            ...current,
                            text: event.target.value,
                            error: null,
                            copied: false,
                          }
                        : current,
                    )
                : undefined
            }
            placeholder={
              deckShare.mode === "import" ? t("deck.share.pastePlaceholder") : undefined
            }
            spellCheck={false}
          />
          {deckShare.error ? (
            <div className={styles.muted}>{deckShare.error}</div>
          ) : null}
          <div className={styles.row}>
            {deckShare.mode === "export" ? (
              <button
                type="button"
                className={`${styles.button} ${styles.buttonPrimary}`}
                onClick={() => copyDeckShareText()}
              >
                {deckShare.copied ? t("deck.share.copied") : t("deck.share.copy")}
              </button>
            ) : (
              <button
                type="button"
                className={`${styles.button} ${styles.buttonPrimary}`}
                disabled={!deckShare.text.trim()}
                onClick={() => importSharedDeck()}
              >
                {t("deck.share.import")}
              </button>
            )}
            <button
              type="button"
              className={styles.button}
              onClick={closeDeckShare}
            >
              {t("common.close")}
            </button>
          </div>
        </ScrollHintSurface>
      </div>
    );
  };

  const renderDeckAnalyzeCountBars = (rows, maxCount) => {
    if (!rows?.length) {
      return <div className={styles.muted}>{t("deck.analyze.none")}</div>;
    }
    const ceiling = Math.max(1, maxCount || Math.max(...rows.map((row) => row.count)));
    return (
      <div className={styles.deckAnalyzeBars}>
        {rows.map((row) => (
          <div key={row.key} className={styles.deckAnalyzeBarRow}>
            <div className={styles.deckAnalyzeBarLabel}>{row.label}</div>
            <div className={styles.deckAnalyzeBarTrack}>
              <div
                className={styles.deckAnalyzeBarFill}
                style={{ width: `${(row.count / ceiling) * 100}%` }}
              />
            </div>
            <div className={styles.deckAnalyzeBarValue}>{row.count}</div>
          </div>
        ))}
      </div>
    );
  };

  const renderDeckAnalyzeMetricTable = (section) => {
    const rows = section?.outputRows || [];
    if (!rows.length) {
      return <div className={styles.muted}>{t("deck.analyze.noOutputs")}</div>;
    }
    const maxAverage = Math.max(...rows.map((row) => row.average), 0.0001);
    const sparkClassByKind = {
      damage: styles.deckAnalyzeSpark_damage,
      heal: styles.deckAnalyzeSpark_heal,
      armor: styles.deckAnalyzeSpark_armor,
      resource: styles.deckAnalyzeSpark_resource,
      ailment: styles.deckAnalyzeSpark_ailment,
      draw: styles.deckAnalyzeSpark_draw,
    };
    return (
      <div className={styles.deckAnalyzeTableWrap}>
        <table className={styles.deckAnalyzeTable}>
          <thead>
            <tr>
              <th>{t("deck.analyze.output")}</th>
              <th>{t("deck.analyze.avgPerCard")}</th>
              <th>{t("deck.analyze.total")}</th>
              <th aria-hidden="true" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <td>{row.label}</td>
                <td>{formatAverage(row.average)}</td>
                <td>{formatAverage(row.total)}</td>
                <td className={styles.deckAnalyzeSparkCell}>
                  <div className={styles.deckAnalyzeSparkTrack}>
                    <div
                      className={`${styles.deckAnalyzeSparkFill} ${
                        sparkClassByKind[row.kind] || ""
                      }`}
                      style={{
                        width: `${(row.average / maxAverage) * 100}%`,
                      }}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderDeckAnalyzeSection = (title, section) => {
    if (!section) return null;
    return (
      <section className={styles.deckAnalyzeSection}>
        <h4 className={styles.deckAnalyzeSectionTitle}>
          {title}
          <span className={styles.deckAnalyzeSectionMeta}>
            {t("deck.analyze.sectionMeta", { count: section.count, power: section.power })}
            {section.averages?.cost > 0
              ? ` · ${t("deck.analyze.avgCost", { cost: formatAverage(section.averages.cost) })}`
              : ""}
          </span>
        </h4>
        <div className={styles.deckAnalyzeGrid}>
          {section.kinds?.length ? (
            <div className={styles.deckAnalyzeBlock}>
              <div className={styles.deckAnalyzeBlockTitle}>{t("deck.analyze.kinds")}</div>
              {renderDeckAnalyzeCountBars(section.kinds)}
            </div>
          ) : null}
          {section.boardSlots?.length ? (
            <div className={styles.deckAnalyzeBlock}>
              <div className={styles.deckAnalyzeBlockTitle}>{t("deck.analyze.boardSlots")}</div>
              {renderDeckAnalyzeCountBars(section.boardSlots)}
            </div>
          ) : null}
          {section.equipSlots?.length ? (
            <div className={styles.deckAnalyzeBlock}>
              <div className={styles.deckAnalyzeBlockTitle}>{t("deck.analyze.equipmentSlots")}</div>
              {renderDeckAnalyzeCountBars(section.equipSlots)}
            </div>
          ) : null}
          <div className={styles.deckAnalyzeBlock}>
            <div className={styles.deckAnalyzeBlockTitle}>{t("deck.analyze.attributes")}</div>
            {renderDeckAnalyzeCountBars(section.attributes)}
          </div>
          <div className={styles.deckAnalyzeBlock}>
            <div className={styles.deckAnalyzeBlockTitle}>{t("deck.analyze.typesPresent")}</div>
            {renderDeckAnalyzeCountBars(section.types)}
          </div>
          <div
            className={`${styles.deckAnalyzeBlock} ${styles.deckAnalyzeBlockWide}`}
          >
            <div className={styles.deckAnalyzeBlockTitle}>
              {t("deck.analyze.averageOutputs")}
            </div>
            {renderDeckAnalyzeMetricTable(section)}
          </div>
        </div>
      </section>
    );
  };

  const renderDeckAnalyzeModal = () => {
    if (!deckAnalyzeOpen) return null;
    const analysis = analyzeDeck(
      filledDeckIds(draftEquipment),
      filledDeckIds(draftAbility),
      draftAspect,
      (cardId) => catalogMap?.[cardId] || null,
    );
    return (
      <div
        className={styles.actionPopup}
        onClick={() => setDeckAnalyzeOpen(false)}
      >
        <ScrollHintSurface
          className={`${styles.actionPopupPanel} ${styles.deckAnalyzePanel}`}
          onClick={(event) => event.stopPropagation()}
        >
          <h3 className={styles.actionPopupTitle}>
            {t("deck.analyze.title", { name: draftName || t("deck.defaultName") })}
          </h3>
          <div className={styles.deckAnalyzeSummary}>
            <div>
              {t("deck.analyze.overview", { count: analysis.overview.cardCount, power: analysis.overview.power, max: MAX_DECK_POWER })}
            </div>
            {analysis.overview.attributes.length ? (
              <div className={styles.muted}>
                {t("deck.analyze.attributesLabel")}{" "}
                {analysis.overview.attributes
                  .map((row) => `${row.label} ${row.count}`)
                  .join(" · ")}
              </div>
            ) : null}
            {analysis.overview.types.length ? (
              <div className={styles.muted}>
                {t("deck.analyze.typesLabel")}{" "}
                {analysis.overview.types
                  .map((row) => `${row.label} ${row.count}`)
                  .join(" · ")}
              </div>
            ) : null}
          </div>
          <section className={styles.deckAnalyzeSection}>
            <h4 className={styles.deckAnalyzeSectionTitle}>
              {t("deck.aspect")}
              <span className={styles.deckAnalyzeSectionMeta}>
                {analysis.aspect.present
                  ? t("deck.analyze.aspectMeta", { name: analysis.aspect.name, power: analysis.aspect.power })
                  : t("deck.analyze.noneSelected")}
              </span>
            </h4>
            {analysis.aspect.present ? (
              <div className={styles.deckAnalyzeGrid}>
                <div className={styles.deckAnalyzeBlock}>
                  <div className={styles.deckAnalyzeBlockTitle}>{t("deck.analyze.attributes")}</div>
                  {renderDeckAnalyzeCountBars(analysis.aspect.attributes)}
                </div>
                {analysis.aspect.types.length ? (
                  <div className={styles.deckAnalyzeBlock}>
                    <div className={styles.deckAnalyzeBlockTitle}>{t("deck.analyze.types")}</div>
                    {renderDeckAnalyzeCountBars(analysis.aspect.types)}
                  </div>
                ) : null}
              </div>
            ) : (
              <div className={styles.muted}>{t("deck.analyze.pickAspect")}</div>
            )}
          </section>
          {renderDeckAnalyzeSection(t("deck.equipment"), analysis.equipment)}
          {renderDeckAnalyzeSection(t("deck.ability"), analysis.ability)}
          <div className={styles.row}>
            <button
              type="button"
              className={styles.button}
              onClick={() => setDeckAnalyzeOpen(false)}
            >
              {t("common.close")}
            </button>
          </div>
        </ScrollHintSurface>
      </div>
    );
  };

  const renderDeckRecommendCard = (cardId, extraClass = "") => {
    const card = catalogMap?.[cardId] || null;
    if (!card) return <div className={styles.muted}>{t("deck.recommend.unknownCard")}</div>;
    return (
      <div
        className={`${styles.socialDeckGrid} ${styles.deckRecommendCardSlot}${
          extraClass ? ` ${extraClass}` : ""
        }`}
      >
        <CardFace
          card={card}
          selected={examCardId === card.id}
          onClick={() => openCardExam(card.id)}
          activeKeywordId={keywordTip?.id || null}
          onKeywordClick={(keyword) => {
            openKeywordTip(keyword);
            openCardExam(card.id);
          }}
        />
      </div>
    );
  };

  const renderDeckRecommendModal = () => {
    if (!deckRecommend || view !== "deck" || awaitingSummary) return null;
    const { status, proposal, exhausted, baseline } = deckRecommend;
    const stale =
      buildCurrentDeckRecommendKey(
        deckRecommendCatalogKey,
        deckRecommendOwnershipKey,
      ) !== deckRecommend.key;
    const staleBlocker = stale ? getCurrentDeckRecommendBlocker() : null;
    const ready = status === "ready" && Boolean(proposal);
    const aspectCard = catalogMap?.[baseline.aspectId] || null;
    return (
      <div className={styles.actionPopup} onClick={() => closeDeckRecommend()}>
        <ScrollHintSurface
          ref={deckRecommendPanelRef}
          className={`${styles.actionPopupPanel} ${styles.deckRecommendPanel}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="deck-recommend-title"
          aria-busy={status === "loading" ? true : undefined}
          tabIndex={-1}
          onClick={(event) => event.stopPropagation()}
        >
          <h3 id="deck-recommend-title" className={styles.actionPopupTitle}>
            {t("deck.recommend.title", { name: draftName || t("deck.defaultName") })}
          </h3>
          <div className={styles.deckRecommendSummary}>
            {aspectCard ? (
              <div className={styles.deckRecommendAspect}>
                {renderDeckRecommendCard(
                  aspectCard.id,
                  styles.deckRecommendAspectCard,
                )}
                <div>
                  <div className={styles.deckRecommendLabel}>
                    {t("deck.recommend.aspectUnchanged")}
                  </div>
                  <div className={styles.deckRecommendValue}>
                    {aspectCard.name}
                  </div>
                </div>
              </div>
            ) : null}
            {ready ? (
              <div className={styles.deckRecommendStats}>
                <div>
                  <div className={styles.deckRecommendLabel}>{t("deck.recommend.swaps")}</div>
                  <div className={styles.deckRecommendValue}>
                    {proposal.swapCount}
                  </div>
                </div>
                <div>
                  <div className={styles.deckRecommendLabel}>{t("deck.totalPower")}</div>
                  <div className={styles.deckRecommendValue}>
                    {proposal.powerBefore} → {proposal.powerAfter}
                    <span className={styles.muted}>/{MAX_DECK_POWER}</span>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
          {status === "loading" ? (
            <div className={styles.deckRecommendStatus} role="status">
              {t("deck.recommend.loading")}
            </div>
          ) : null}
          {status === "empty" ? (
            <div className={styles.deckRecommendStatus} role="status">
              <strong>{t("deck.recommend.emptyTitle")}</strong>{" "}
              {t("deck.recommend.emptyBody")}
            </div>
          ) : null}
          {stale && status !== "loading" ? (
            <div className={styles.deckRecommendNotice} role="alert">
              {t("deck.recommend.staleNotice")}{" "}
              {staleBlocker || t("deck.recommend.recommendAgainHint")}
            </div>
          ) : null}
          {exhausted && !stale ? (
            <div className={styles.deckRecommendNotice} role="status">
              {t("deck.recommend.exhausted")}
            </div>
          ) : null}
          {ready ? (
            <ol className={styles.deckRecommendSwaps}>
              {proposal.swaps.map((swap) => (
                <li
                  key={`${swap.deckType}-${swap.index}`}
                  className={styles.deckRecommendSwap}
                >
                  <div className={styles.deckRecommendSwapHead}>
                    {swap.deckType === "equipment"
                      ? t("deck.recommend.equipmentSlot", { n: swap.index + 1 })
                      : t("deck.recommend.abilitySlot", { n: swap.index + 1 })}
                  </div>
                  <div className={styles.deckRecommendSwapCards}>
                    <div className={styles.deckRecommendSide}>
                      <div className={styles.deckRecommendLabel}>{t("deck.recommend.remove")}</div>
                      {renderDeckRecommendCard(swap.removedId)}
                    </div>
                    <div className={styles.deckRecommendArrow} aria-hidden="true">
                      →
                    </div>
                    <div className={styles.deckRecommendSide}>
                      <div className={styles.deckRecommendLabel}>{t("deck.recommend.add")}</div>
                      {renderDeckRecommendCard(swap.addedId)}
                    </div>
                  </div>
                  <div className={styles.deckRecommendReasons}>
                    <div className={styles.deckRecommendLabel}>{t("deck.recommend.whyAdd")}</div>
                    <ul className={styles.deckRecommendReasonList}>
                      {swap.reasons.map((reason, index) => (
                        <li key={`${swap.addedId}-${index}`}>{reason}</li>
                      ))}
                    </ul>
                    {swap.removedReason ? (
                      <>
                        <div className={styles.deckRecommendLabel}>
                          {t("deck.recommend.whyRemove")}
                        </div>
                        <div className={styles.muted}>{swap.removedReason}</div>
                      </>
                    ) : null}
                  </div>
                </li>
              ))}
            </ol>
          ) : null}
          <div className={styles.muted}>
            {t("deck.recommend.disclaimer")}
          </div>
          <div className={styles.row}>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary}`}
              disabled={!ready || stale}
              onClick={() => applyDeckRecommend()}
            >
              {t("deck.recommend.apply")}
            </button>
            <button
              type="button"
              className={styles.button}
              disabled={status === "loading" || Boolean(staleBlocker)}
              onClick={() =>
                stale ? openDeckRecommend() : retryDeckRecommend()
              }
            >
              {stale ? t("deck.recommend.again") : t("deck.recommend.tryAgain")}
            </button>
            <button
              type="button"
              className={styles.button}
              onClick={() => closeDeckRecommend()}
            >
              {t("common.cancel")}
            </button>
          </div>
        </ScrollHintSurface>
      </div>
    );
  };

  const renderDeckSlotGrid = (deckType, draft) => {
    const foilDraft =
      view === "deck"
        ? deckType === "aspect"
          ? draftAspectFoil
          : deckType === "equipment"
            ? draftEquipmentFoils
            : draftAbilityFoils
        : null;
    const foilSlots =
      foilDraft && deckType !== "aspect" ? padFoilSlots(foilDraft) : null;
    const aspectSlotFoil =
      view === "deck" && deckType === "aspect" ? draftAspectFoil : null;
    const slots =
      deckType === "aspect" ? padAspectSlot((draft || [])[0]) : padDeckSlots(draft);
    return (
    <div className={styles.deckSlotGrid}>
      {slots.map((cardId, index) => {
        const card = cardId ? catalogMap[cardId] : null;
        if (card) {
          return (
            <CardFace
              key={`${deckType}-slot-${index}`}
              card={card}
              foilEffect={
                deckType === "aspect"
                  ? aspectSlotFoil
                  : foilSlots
                    ? foilSlots[index] || null
                    : null
              }
              onClick={() =>
                setSlotPicker({ deckType, index, mode: "detail" })
              }
              activeKeywordId={keywordTip?.id || null}
              onKeywordClick={(keyword) => {
                openKeywordTip(keyword);
                setSlotPicker({ deckType, index, mode: "detail" });
              }}
            />
          );
        }
        return (
          <button
            key={`${deckType}-slot-${index}`}
            type="button"
            className={styles.deckSlotEmpty}
            onClick={() => {
              setPickerSuggest(false);
              setSlotPicker({ deckType, index, mode: "pick" });
            }}
          >
            {t("deck.slot.empty")}
          </button>
        );
      })}
    </div>
    );
  };

  const renderSlotCardDetail = () => {
    if (!slotPicker || slotPicker.mode !== "detail") return null;
    const { deckType, index } = slotPicker;
    const draft =
      deckType === "aspect"
        ? padAspectSlot(
            view === "adventure" ? adventureAspect : draftAspect,
          )
        : padDeckSlots(
            view === "adventure"
              ? deckType === "equipment"
                ? adventureEquipment
                : adventureAbility
              : deckType === "equipment"
                ? draftEquipment
                : draftAbility,
          );
    const currentId = draft[index] || null;
    const card = currentId ? catalogMap[currentId] : null;
    if (!card) return null;
    const slotFoil =
      view === "deck"
        ? deckType === "aspect"
          ? draftAspectFoil
          : padFoilSlots(
              deckType === "equipment"
                ? draftEquipmentFoils
                : draftAbilityFoils,
            )[index] || null
        : null;
    const slotUnlockedHolos =
      view === "deck"
        ? sortFoilEffects(player?.unlockedHolos?.[card.id] || [])
        : [];
    const slotDetailPanelProps = getCardExamV2PanelProps(card);
    const deckSlotLabel = `${
      deckType === "aspect"
        ? t("deck.aspect")
        : deckType === "equipment"
          ? t("deck.equipment")
          : t("deck.ability")
    } ${index + 1}`;
    return (
      <div
        className={`${styles.actionPopup} ${styles.cardExamOverlay}`}
        onClick={() => {
          closeKeywordTip();
          setSlotPicker(null);
        }}
      >
        <div
          className={`${styles.actionPopupPanel} ${styles.cardExamPanel} ${styles.examV2Panel} ${slotDetailPanelProps.className}`}
          style={slotDetailPanelProps.style}
          onClick={(event) => {
            event.stopPropagation();
            closeKeywordTip();
          }}
        >
          <ScrollHintSurface
            className={`${styles.cardExamBody} ${styles.examV2Body}`}
          >
            {renderCardExamV2Body(card, {
              foilEffect: slotFoil,
              foilOptions:
                slotUnlockedHolos.length > 0
                  ? {
                      unlockedHolos: slotUnlockedHolos,
                      onFoilChange: (nextFoil) =>
                        setDraftSlot(deckType, index, card.id, nextFoil || null),
                    }
                  : {},
              extraStats: [{ label: t("deck.slot.deckSlot"), value: deckSlotLabel }],
            })}
          </ScrollHintSurface>
          <div className={`${styles.slotDetailActions} ${styles.examV2Footer}`}>
            {renderCardExamV2SetChip(card)}
            <span className={styles.examV2FooterSpacer} />
            <button
              type="button"
              className={styles.button}
              onClick={() => {
                closeKeywordTip();
                if (deckType === "aspect") {
                  if (view === "adventure") {
                    setAdventureAspect(DEFAULT_ASPECT_ID);
                  } else if (view === "draftDeck") {
                    setDraftAspect(
                      player?.draftRun?.pool?.aspectId ||
                        (player?.draftRun?.pool?.aspectIds || [])[0] ||
                        DEFAULT_ASPECT_ID,
                    );
                    setDraftAspectFoil(null);
                  } else {
                    setDraftAspect(null);
                    setDraftAspectFoil(null);
                  }
                } else {
                  clearDeckSlot(deckType, index);
                }
                setSlotPicker(null);
              }}
            >
              {t("deck.slot.clear")}
            </button>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary}`}
              onClick={() => {
                closeKeywordTip();
                setPickerSuggest(false);
                setSlotPicker({ deckType, index, mode: "pick" });
              }}
            >
              {t("deck.slot.replaceCard")}
            </button>
            <button
              type="button"
              className={styles.button}
              onClick={() => {
                closeKeywordTip();
                setSlotPicker(null);
              }}
            >
              {t("common.close")}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderSlotPicker = () => {
    if (!slotPicker || slotPicker.mode !== "pick") return null;
    const { deckType, index } = slotPicker;
    const equipmentSource =
      view === "adventure" ? adventureEquipment : draftEquipment;
    const abilitySource =
      view === "adventure" ? adventureAbility : draftAbility;
    const aspectSource =
      view === "adventure" ? adventureAspect : draftAspect;
    const deckSource =
      deckType === "aspect"
        ? padAspectSlot(aspectSource)
        : deckType === "equipment"
          ? equipmentSource
          : abilitySource;
    const draft =
      deckType === "aspect" ? padAspectSlot(aspectSource) : padDeckSlots(deckSource);
    const currentId = draft[index] || null;
    const counts = countIds(
      draft.map((id, slotIndex) => (slotIndex === index ? null : id)),
    );
    const poolIds =
      deckType === "aspect"
        ? view === "draftDeck"
          ? [
              ...(player?.draftRun?.pool?.aspectIds || []),
              player?.draftRun?.pool?.aspectId,
            ].filter(Boolean)
          : null
        : view === "adventure"
          ? deckType === "equipment"
            ? player?.adventureRun?.inventory?.equipmentIds || []
            : player?.adventureRun?.inventory?.abilityIds || []
          : view === "draftDeck"
            ? deckType === "equipment"
              ? player?.draftRun?.pool?.equipmentIds || []
              : player?.draftRun?.pool?.abilityIds || []
            : null;
    const poolCounts = poolIds ? countIds(poolIds) : null;
    const baseOptions = (catalog || []).filter((card) => {
      if (!cardHasType(card, deckType)) return false;
      if (cardHasType(card, "token")) return false;
      if (view === "deck" && !playerOwnsCard(player?.collection, card.id)) {
        return false;
      }
      if (deckType === "aspect") {
        if (poolCounts && !(poolCounts[card.id] > 0)) return false;
        return true;
      }
      if (poolCounts && !(poolCounts[card.id] > 0)) return false;
      const limit = poolCounts ? poolCounts[card.id] || 0 : MAX_COPIES;
      return (counts[card.id] || 0) < limit;
    });
    const searchQuery = pickerSearch.trim().toLowerCase();
    const slotOptions = [
      ...new Set(baseOptions.map((card) => card.slot).filter(Boolean)),
    ].sort(
      (a, b) => (SLOT_SORT_ORDER[a] ?? 99) - (SLOT_SORT_ORDER[b] ?? 99),
    );
    const effectOptions = listEffectFilterOptions(baseOptions);
    const typeOptions = buildCardTypeFilterOptions(baseOptions);
    const itemTypeOptions = buildEquipmentItemTypeFilterOptions(baseOptions);
    const profileEquipmentIds = filledDeckIds(
      padDeckSlots(equipmentSource).map((id, slotIndex) =>
        deckType === "equipment" && slotIndex === index ? null : id,
      ),
    );
    const profileAbilityIds = filledDeckIds(
      padDeckSlots(abilitySource).map((id, slotIndex) =>
        deckType === "ability" && slotIndex === index ? null : id,
      ),
    );
    const suggestAvailable =
      deckType !== "aspect" &&
      canSuggestCards(profileEquipmentIds, profileAbilityIds);
    const filteredOptions = baseOptions.filter((card) => {
        if (pickerAttr !== "all" && !cardMatchesAttributeFilter(card, pickerAttr)) {
          return false;
        }
        if (deckType === "equipment" && pickerSlot !== "all") {
          if (card.slot !== pickerSlot) return false;
        }
        if (deckType === "equipment") {
          if (pickerItemType !== "all" && !cardHasType(card, pickerItemType)) {
            return false;
          }
          if (!cardMatchesEquipmentTierFilter(card, pickerTier)) return false;
        }
        if (deckType === "ability" && pickerKind !== "all") {
          if (card.kind !== pickerKind) return false;
        }
        if (pickerType !== "all") {
          if (pickerType === "plain") {
            if (cardHasType(card, "attack") || cardHasType(card, "magic")) {
              return false;
            }
          } else if (!cardMatchesTypeFilter(card, pickerType)) {
            return false;
          }
        }
        if (pickerEffect !== "all") {
          if (!cardMatchesEffectFilter(card, pickerEffect)) return false;
        }
        if (pickerCost !== "all") {
          const total = cardCostTotal(card);
          if (pickerCost === "3+") {
            if (total < 3) return false;
          } else if (total !== Number(pickerCost)) {
            return false;
          }
        }
        if (pickerHolo === "holo" && playerHasAnyHolo) {
          if (catalogHoloCount(card.id, player?.unlockedHolos) <= 0) {
            return false;
          }
        }
        if (searchQuery && !cardSearchHaystack(card).includes(searchQuery)) {
          return false;
        }
        return true;
      });
    const suggestProfile =
      pickerSuggest && suggestAvailable
        ? buildDeckProfile(
            profileEquipmentIds,
            profileAbilityIds,
            (id) => catalogMap[id],
            padAspectSlot(aspectSource)[0] || null,
          )
        : null;
    const suggestedOptions = suggestProfile
      ? filterSuggestedCards(filteredOptions, suggestProfile, deckType)
      : filteredOptions;
    const effectivePickerSort =
      (pickerSort === "holo" && !playerHasAnyHolo) ||
      (deckType === "aspect" &&
        (pickerSort === "cost" ||
          pickerSort === "slot" ||
          pickerSort === "kind")) ||
      (deckType === "equipment" && pickerSort === "kind") ||
      (deckType !== "equipment" && pickerSort === "tier") ||
      (deckType === "ability" && pickerSort === "slot")
        ? "name"
        : pickerSort;
    const options = suggestProfile
      ? suggestedOptions
      : [...suggestedOptions].sort((a, b) =>
          compareCatalogCards(a, b, effectivePickerSort, player?.unlockedHolos),
        );

    return (
      <div
        className={`${styles.actionPopup} ${styles.slotPickerOverlay}`}
        onClick={() => setSlotPicker(null)}
      >
        <div
          className={`${styles.actionPopupPanel} ${styles.slotPickerPanel}`}
          onClick={(event) => event.stopPropagation()}
        >
          <h3 className={styles.actionPopupTitle}>
            {deckType === "aspect"
              ? currentId
                ? t("deck.picker.replaceAspect")
                : t("deck.picker.chooseAspect")
              : deckType === "equipment"
                ? currentId
                  ? t("deck.picker.replaceEquipmentSlot", { n: index + 1 })
                  : t("deck.picker.chooseEquipmentSlot", { n: index + 1 })
                : currentId
                  ? t("deck.picker.replaceAbilitySlot", { n: index + 1 })
                  : t("deck.picker.chooseAbilitySlot", { n: index + 1 })}
          </h3>
          <div className={styles.row}>
            <button
              type="button"
              className={styles.button}
              onClick={() => setPickerFiltersOpen((open) => !open)}
            >
              {pickerFiltersOpen ? t("deck.picker.hideFilters") : t("deck.picker.showFilters")}
            </button>
            <button
              type="button"
              className={styles.button}
              onClick={clearPickerFilters}
            >
              {t("deck.filters.clear")}
            </button>
            {deckType !== "aspect" ? (
              <button
                type="button"
                className={`${styles.button} ${
                  pickerSuggest ? styles.buttonPrimary : ""
                }`}
                disabled={!suggestAvailable}
                onClick={() => setPickerSuggest((active) => !active)}
              >
                {t("deck.picker.suggest")}
              </button>
            ) : null}
            <button
              type="button"
              className={styles.button}
              onClick={() => setSlotPicker(null)}
            >
              {t("common.close")}
            </button>
          </div>
          <div className={styles.slotPickerTools}>
            {pickerFiltersOpen ? (
              <>
                <input
                  className={`${styles.input} ${styles.slotPickerSearch}`}
                  type="search"
                  value={pickerSearch}
                  placeholder={t("deck.filters.searchPlaceholder")}
                  onChange={(event) => setPickerSearch(event.target.value)}
                />
                <div className={styles.filters}>
              <select
                className={styles.select}
                value={pickerAttr}
                onChange={(event) => setPickerAttr(event.target.value)}
              >
                <option value="all">{t("deck.filters.allAttributes")}</option>
                <option value="strength">{t("deck.attr.strength")}</option>
                <option value="dexterity">{t("deck.attr.dexterity")}</option>
                <option value="wisdom">{t("deck.attr.wisdom")}</option>
                <option value="neutral">{t("deck.attr.neutral")}</option>
              </select>
              {deckType === "equipment" ? (
                <select
                  className={styles.select}
                  value={pickerSlot}
                  onChange={(event) => setPickerSlot(event.target.value)}
                >
                  <option value="all">{t("deck.filters.allSlots")}</option>
                  {slotOptions.map((slot) => (
                    <option key={slot} value={slot}>
                      {formatSlotLabel(slot)}
                    </option>
                  ))}
                </select>
              ) : deckType === "ability" ? (
                <select
                  className={styles.select}
                  value={pickerKind}
                  onChange={(event) => setPickerKind(event.target.value)}
                >
                  <option value="all">{t("deck.filters.allKinds")}</option>
                  <option value="active">{t("deck.kind.active")}</option>
                  <option value="augment">{t("deck.kind.augment")}</option>
                  <option value="essence">{t("deck.kind.essence")}</option>
                </select>
              ) : null}
              {deckType === "equipment"
                ? renderEquipmentPickerFilters({
                    itemTypeOptions,
                    itemType: pickerItemType,
                    onItemTypeChange: setPickerItemType,
                    tier: pickerTier,
                    onTierChange: setPickerTier,
                  })
                : null}
              {deckType !== "aspect" ? (
                <select
                  className={styles.select}
                  value={pickerType}
                  onChange={(event) => setPickerType(event.target.value)}
                >
                  <option value="all">{t("deck.filters.allTypes")}</option>
                  {renderCardTypeFilterOptions(typeOptions, {
                    includePlain: true,
                  })}
                </select>
              ) : null}
              <select
                className={styles.select}
                value={pickerEffect}
                onChange={(event) => setPickerEffect(event.target.value)}
              >
                <option value="all">{t("deck.filters.allEffects")}</option>
                {effectOptions.map((effectId) => (
                  <option key={effectId} value={effectId}>
                    {formatEffectFilterLabel(effectId)}
                  </option>
                ))}
              </select>
              {deckType !== "aspect" ? (
                <select
                  className={styles.select}
                  value={pickerCost}
                  onChange={(event) => setPickerCost(event.target.value)}
                >
                  <option value="all">
                    {deckType === "equipment" ? t("deck.filters.allResources") : t("deck.filters.allCosts")}
                  </option>
                  <option value="0">0</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3+">3+</option>
                </select>
              ) : null}
              <select
                className={styles.select}
                value={effectivePickerSort}
                onChange={(event) => setPickerSort(event.target.value)}
              >
                <option value="name">{t("deck.sort.name")}</option>
                <option value="type">{t("deck.sort.type")}</option>
                <option value="attribute">{t("deck.sort.attribute")}</option>
                {deckType === "equipment" ? (
                  <option value="cost">{t("deck.sort.resources")}</option>
                ) : deckType === "ability" ? (
                  <option value="cost">{t("deck.sort.cost")}</option>
                ) : null}
                <option value="power">{t("deck.sort.power")}</option>
                {deckType === "equipment" ? (
                  <>
                    <option value="slot">{t("deck.sort.slot")}</option>
                    <option value="tier">{t("deck.sort.tier")}</option>
                  </>
                ) : deckType === "ability" ? (
                  <option value="kind">{t("deck.sort.kind")}</option>
                ) : null}
                {playerHasAnyHolo ? (
                  <option value="holo">{t("deck.sort.holo")}</option>
                ) : null}
              </select>
              {playerHasAnyHolo ? (
                <button
                  type="button"
                  className={`${styles.button} ${
                    pickerHolo === "holo" ? styles.buttonPrimary : ""
                  }`}
                  aria-pressed={pickerHolo === "holo"}
                  onClick={() =>
                    setPickerHolo((value) => (value === "holo" ? "all" : "holo"))
                  }
                >
                  {t("deck.filters.holoUnlocked")}
                </button>
              ) : null}
            </div>
              </>
            ) : null}
          </div>
          <ScrollHintSurface className={styles.slotPickerGrid}>
            {options.map((card) => (
              <CardFace
                key={card.id}
                card={card}
                selected={card.id === currentId}
                compact
                fitText={false}
                onClick={() => pickSlotCard(card)}
              />
            ))}
          </ScrollHintSurface>
        </div>
      </div>
    );
  };

  const renderDeckBuilder = () => {
    const equipmentIds = filledDeckIds(draftEquipment);
    const abilityIds = filledDeckIds(draftAbility);
    const { equipmentPower, abilityPower, aspectPower, totalPower } =
      getDeckPairPowerTotals(
        equipmentIds,
        abilityIds,
        draftAspect,
        catalogMap,
      );
    const powerOverLimit = totalPower > MAX_DECK_POWER;
    const deckIsValid = isClassicDeckValid(
      draftEquipment,
      draftAbility,
      draftAspect,
      catalogMap,
      player?.collection,
    );
    const knownDeck = (player?.deckPairs || []).some(
      (pair) => pair.id === editingDeckId,
    );
    const isEditingDefault =
      Boolean(editingDeckId) &&
      editingDeckId === player?.activeDeckPairId &&
      knownDeck;
    const atDeckLimit = (player?.deckPairs || []).length >= MAX_DECK_PAIRS;
    const canDeleteDeck =
      knownDeck && (player?.deckPairs || []).length > 1;
    const deletingDeckName =
      (player?.deckPairs || []).find((pair) => pair.id === editingDeckId)
        ?.name ||
      draftName ||
      t("deck.defaultName");
    const recommendBlocker = getCurrentDeckRecommendBlocker();
    return (
      <div className={styles.panel}>
        <h2 className={styles.panelTitle}>{t("deck.builder.title")}</h2>
        <div className={styles.row}>
          <select
            className={styles.select}
            value={editingDeckId || ""}
            onChange={(event) => {
              const nextId = event.target.value;
              if (nextId === NEW_DECK_OPTION) {
                startNewDeck();
                return;
              }
              setUnsavedNewDeck(false);
              setDeleteDeckConfirm(false);
              setEditingDeckId(nextId);
            }}
          >
            {(player?.deckPairs || []).map((pair) => (
              <option key={pair.id} value={pair.id}>
                {pair.id === player?.activeDeckPairId
                  ? `${ACTIVE_DECK_MARKER} ${pair.name}`
                  : pair.name}
              </option>
            ))}
            {unsavedNewDeck &&
              editingDeckId &&
              !(player?.deckPairs || []).some(
                (pair) => pair.id === editingDeckId,
              ) && (
                <option value={editingDeckId}>
                  {draftName || t("deck.newDeck")}
                </option>
              )}
            <option value={NEW_DECK_OPTION} disabled={atDeckLimit}>
              {t("deck.newDeck")}
            </option>
          </select>
          <input
            className={styles.input}
            value={draftName}
            onFocus={() => {
              nameInputFocusedRef.current = true;
              setNameInputFocused(true);
            }}
            onChange={(event) => setDraftName(event.target.value)}
            onBlur={() => {
              nameInputFocusedRef.current = false;
              setNameInputFocused(false);
              const name =
                (draftName || "Deck").trim().slice(0, 40) || "Deck";
              if (name !== draftName) setDraftName(name);
              if (!editingDeckId) return;
              if (
                !isClassicDeckValid(
                  draftEquipment,
                  draftAbility,
                  draftAspect,
                  catalogMap,
                  player?.collection,
                )
              ) {
                return;
              }
              const pair = (player?.deckPairs || []).find(
                (entry) => entry.id === editingDeckId,
              );
              if (pair && pair.name === name) return;
              saveDeck({ name });
            }}
          />
          <button
            type="button"
            className={styles.button}
            disabled={!editingDeckId || isEditingDefault || !knownDeck}
            onClick={() =>
              send({
                type: "ai:setActiveDeckPair",
                deckId: editingDeckId,
              })
            }
          >
            {t("deck.builder.setDefault")}
          </button>
          <button
            type="button"
            className={styles.button}
            disabled={!deckIsValid || atDeckLimit}
            onClick={() => cloneCurrentDeck()}
          >
            {t("deck.builder.clone")}
          </button>
          <button
            type="button"
            className={`${styles.button} ${styles.buttonPrimary}`}
            onClick={() => saveDeck()}
            disabled={!deckIsValid}
          >
            {t("deck.builder.save")}
          </button>
          <button
            type="button"
            className={`${styles.button} ${styles.buttonDanger}`}
            disabled={!canDeleteDeck}
            onClick={() => setDeleteDeckConfirm(true)}
          >
            {t("deck.builder.delete")}
          </button>
          <button
            type="button"
            className={styles.button}
            disabled={!deckIsValid}
            onClick={() => openDeckExport()}
          >
            {t("deck.builder.export")}
          </button>
          <button
            type="button"
            className={styles.button}
            disabled={atDeckLimit}
            onClick={() => openDeckImport()}
          >
            {t("deck.share.import")}
          </button>
          <button
            type="button"
            className={styles.button}
            disabled={!equipmentIds.length && !abilityIds.length && !draftAspect}
            onClick={() => {
              setDeleteDeckConfirm(false);
              setDeckAnalyzeOpen(true);
            }}
          >
            {t("deck.builder.analyze")}
          </button>
          <button
            type="button"
            ref={deckRecommendButtonRef}
            className={`${styles.button}${
              recommendBlocker ? ` ${styles.buttonLooksDisabled}` : ""
            }`}
            aria-disabled={recommendBlocker ? true : undefined}
            aria-describedby={
              recommendBlocker && deckRecommendHintOpen
                ? "deck-recommend-hint"
                : undefined
            }
            title={recommendBlocker || t("deck.recommend.buttonTitle")}
            onClick={() => openDeckRecommend()}
          >
            {t("deck.recommend.button")}
          </button>
        </div>
        {recommendBlocker && deckRecommendHintOpen ? (
          <div
            id="deck-recommend-hint"
            className={`${styles.muted} ${styles.deckRecommendHint}`}
            role="status"
          >
            {t("deck.recommend.needsValidDeck")} {recommendBlocker}
          </div>
        ) : null}
        <h3
          className={`${styles.panelTitle} ${styles.deckTotalPowerTitle}${
            powerOverLimit ? ` ${styles.deckPowerOver}` : ""
          }`}
        >
          <span className={styles.deckPowerHeading}>
            {t("deck.totalPower")}
            <MaskIcon
              src={POWER_ICON_URL}
              className={styles.deckPowerHeadingIcon}
            />
            {totalPower}/{MAX_DECK_POWER}
          </span>
        </h3>
        {isEditingDefault || (unsavedNewDeck && !knownDeck) ? (
          <div className={styles.muted}>
            {[
              isEditingDefault ? t("deck.builder.default") : null,
              unsavedNewDeck && !knownDeck ? t("deck.builder.unsaved") : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </div>
        ) : null}
        <div className={styles.deckEditor}>
          <div>
            <h3 className={styles.panelTitle}>
              <span className={styles.deckPowerHeading}>
                {t("deck.aspect")}
                <MaskIcon
                  src={POWER_ICON_URL}
                  className={styles.deckPowerHeadingIcon}
                />
                {aspectPower}
              </span>
            </h3>
            {renderDeckSlotGrid("aspect", padAspectSlot(draftAspect))}
          </div>
          <div>
            <h3 className={styles.panelTitle}>
              <span className={styles.deckPowerHeading}>
                {t("deck.equipment")}
                <MaskIcon
                  src={POWER_ICON_URL}
                  className={styles.deckPowerHeadingIcon}
                />
                {equipmentPower}
              </span>
            </h3>
            {renderDeckSlotGrid("equipment", draftEquipment)}
          </div>
          <div>
            <h3 className={styles.panelTitle}>
              <span className={styles.deckPowerHeading}>
                {t("deck.ability")}
                <MaskIcon
                  src={POWER_ICON_URL}
                  className={styles.deckPowerHeadingIcon}
                />
                {abilityPower}
              </span>
            </h3>
            {renderDeckSlotGrid("ability", draftAbility)}
          </div>
        </div>
        {renderSlotCardDetail()}
        {renderSlotPicker()}
        {deleteDeckConfirm ? (
          <div
            className={styles.actionPopup}
            onClick={() => setDeleteDeckConfirm(false)}
          >
            <ScrollHintSurface
              className={styles.actionPopupPanel}
              onClick={(event) => event.stopPropagation()}
            >
              <h3 className={styles.actionPopupTitle}>{t("deck.builder.deleteTitle")}</h3>
              <div className={styles.muted}>
                {t("deck.builder.deleteBody", { name: deletingDeckName })}
              </div>
              <div className={styles.row}>
                <button
                  type="button"
                  className={`${styles.button} ${styles.buttonDanger}`}
                  onClick={deleteEditingDeck}
                >
                  {t("deck.builder.delete")}
                </button>
                <button
                  type="button"
                  className={styles.button}
                  onClick={() => setDeleteDeckConfirm(false)}
                >
                  {t("common.cancel")}
                </button>
              </div>
            </ScrollHintSurface>
          </div>
        ) : null}
      </div>
    );
  };

  const renderWorkshopBar = () => {
    if (!isWorkshopMatch) return null;
    const activeName = displayMatch?.activePlayer || "";
    return (
      <div
        className={`${styles.workshopBar} ${
          workshopControlsHidden ? styles.workshopBarCollapsed : ""
        }`}
        data-gamepad-zone="workshop"
      >
        {workshopControlsHidden ? (
          <div className={styles.workshopBarRow}>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary}`}
              onClick={() => setWorkshopControlsHidden(false)}
            >
              {t("workshop.showControls")}
            </button>
          </div>
        ) : (
          <>
            <div className={styles.workshopBarRow}>
              <button
                type="button"
                className={styles.button}
                onClick={() => {
                  clearPickerFilters();
                  setWorkshopPickerKind("all");
                  startTransition(() => {
                    setWorkshopPicker(true);
                  });
                }}
              >
                {t("workshop.addCard")}
              </button>
              <button
                type="button"
                className={`${styles.button} ${
                  !workshopActingAsOpponent ? styles.buttonPrimary : ""
                }`}
                onClick={() => setOpponentBoardExpanded(false)}
              >
                {t("game.workshop.seatYou")}
              </button>
              <button
                type="button"
                className={`${styles.button} ${
                  workshopActingAsOpponent ? styles.buttonPrimary : ""
                }`}
                onClick={() => setOpponentBoardExpanded(true)}
              >
                {t("game.workshop.seatOpponent")}
              </button>
              <button
                type="button"
                className={`${styles.button} ${
                  activeName === displayUsername ? styles.buttonPrimary : ""
                }`}
                onClick={() => setWorkshopTurn(displayUsername)}
              >
                {t("workshop.yourTurn")}
              </button>
              <button
                type="button"
                className={`${styles.button} ${
                  activeName === opponentUsername ? styles.buttonPrimary : ""
                }`}
                onClick={() => setWorkshopTurn(opponentUsername)}
                disabled={!opponentUsername}
              >
                {t("workshop.opponentTurn")}
              </button>
              <button
                type="button"
                className={styles.button}
                onClick={() => send({ type: "ai:workshopReset" })}
              >
                {t("workshop.reset")}
              </button>
              <button
                type="button"
                className={styles.button}
                onClick={() => {
                  requestView("menu");
                }}
              >
                {t("hud.leave")}
              </button>
              <button
                type="button"
                className={`${styles.button} ${styles.workshopHideControlsButton}`}
                onClick={() => setWorkshopControlsHidden(true)}
              >
                {t("workshop.hideControls")}
              </button>
            </div>
            <div className={styles.workshopBarRow}>
              <span className={styles.workshopBarLabel}>{workshopSeatLabel}</span>
              <div className={styles.workshopStepper}>
                <button
                  type="button"
                  className={styles.workshopStepButton}
                  onClick={() => adjustWorkshopHp(-1)}
                >
                  −
                </button>
                <span>
                  {t("workshop.hp", {
                    hp: actingSeat?.hp || 0,
                    maxHp: actingSeat?.maxHp || 0,
                  })}
                </span>
                <button
                  type="button"
                  className={styles.workshopStepButton}
                  onClick={() => adjustWorkshopHp(1)}
                >
                  +
                </button>
              </div>
              {["strength", "dexterity", "wisdom", "neutral"].map((attribute) => (
                <div key={attribute} className={styles.workshopStepper}>
                  <button
                    type="button"
                    className={styles.workshopStepButton}
                    onClick={() => adjustWorkshopResource(attribute, -1)}
                  >
                    −
                  </button>
                  <span className={ATTR_CLASS[attribute] || ""}>
                    {attribute.slice(0, 3).toUpperCase()}{" "}
                    {actingSeat?.resources?.[attribute] || 0}
                  </span>
                  <button
                    type="button"
                    className={styles.workshopStepButton}
                    onClick={() => adjustWorkshopResource(attribute, 1)}
                  >
                    +
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  const renderWorkshopPicker = () => {
    if (!workshopPicker || !isWorkshopMatch) return null;
    const pickingAspect = workshopPickerKind === "aspect";
    const currentAspectId =
      typeof actingSeat?.aspectId === "string" && actingSeat.aspectId
        ? actingSeat.aspectId
        : null;
    const counts = countIds(collectSeatCardIds(actingSeat));
    const baseOptions = (catalog || []).filter((card) => {
      if (pickingAspect) {
        return cardHasType(card, "aspect");
      }
      if (cardHasType(card, "aspect")) return false;
      if (workshopPickerKind !== "all" && !cardHasType(card, workshopPickerKind)) {
        return false;
      }
      return true;
    });
    const searchQuery = pickerSearch.trim().toLowerCase();
    const slotOptions = [
      ...new Set(baseOptions.map((card) => card.slot).filter(Boolean)),
    ].sort(
      (a, b) => (SLOT_SORT_ORDER[a] ?? 99) - (SLOT_SORT_ORDER[b] ?? 99),
    );
    const effectOptions = listEffectFilterOptions(baseOptions);
    const typeOptions = buildCardTypeFilterOptions(baseOptions);
    const itemTypeOptions = buildEquipmentItemTypeFilterOptions(baseOptions);
    const showEquipmentFilters =
      !pickingAspect && workshopPickerKind !== "ability";
    const options = baseOptions
      .filter((card) => {
        if (pickerAttr !== "all" && !cardMatchesAttributeFilter(card, pickerAttr)) {
          return false;
        }
        if (!pickingAspect && workshopPickerKind === "equipment" && pickerSlot !== "all") {
          if (card.slot !== pickerSlot) return false;
        }
        if (!pickingAspect && workshopPickerKind === "ability" && pickerKind !== "all") {
          if (card.kind !== pickerKind) return false;
        }
        if (showEquipmentFilters) {
          if (pickerItemType !== "all" && !cardHasType(card, pickerItemType)) {
            return false;
          }
          if (!cardMatchesEquipmentTierFilter(card, pickerTier)) return false;
        }
        if (!pickingAspect && pickerType !== "all") {
          if (pickerType === "plain") {
            if (cardHasType(card, "attack") || cardHasType(card, "magic")) {
              return false;
            }
          } else if (!cardMatchesTypeFilter(card, pickerType)) {
            return false;
          }
        }
        if (!pickingAspect && pickerEffect !== "all") {
          if (!cardMatchesEffectFilter(card, pickerEffect)) return false;
        }
        if (!pickingAspect && pickerCost !== "all") {
          const total = cardCostTotal(card);
          if (pickerCost === "3+") {
            if (total < 3) return false;
          } else if (total !== Number(pickerCost)) {
            return false;
          }
        }
        if (searchQuery && !cardSearchHaystack(card).includes(searchQuery)) {
          return false;
        }
        return true;
      })
      .sort((a, b) => comparePickerCards(a, b, pickerSort));

    return (
      <div
        className={`${styles.actionPopup} ${styles.workshopPickerOverlay}`}
        onClick={() => setWorkshopPicker(false)}
      >
        <div
          className={`${styles.actionPopupPanel} ${styles.slotPickerPanel} ${styles.workshopPickerPanel}`}
          onClick={(event) => event.stopPropagation()}
        >
          <h3 className={styles.actionPopupTitle}>
            {pickingAspect
              ? t("workshop.switchAspectFor", { name: workshopSeatLabel })
              : t("workshop.addCardTo", { name: workshopSeatLabel })}
          </h3>
          <div className={styles.muted}>
            {pickingAspect
              ? t("workshop.chooseAspect")
              : t("workshop.maxCopiesPerSide", { max: MAX_COPIES })}
          </div>
          <div className={styles.row}>
            <button
              type="button"
              className={styles.button}
              onClick={() => setPickerFiltersOpen((open) => !open)}
            >
              {pickerFiltersOpen
                ? t("deck.picker.hideFilters")
                : t("deck.picker.showFilters")}
            </button>
            <button
              type="button"
              className={styles.button}
              onClick={() => {
                clearPickerFilters();
                if (!pickingAspect) setWorkshopPickerKind("all");
              }}
            >
              {t("deck.filters.clear")}
            </button>
            <button
              type="button"
              className={styles.button}
              onClick={() => setWorkshopPicker(false)}
            >
              {t("common.close")}
            </button>
          </div>
          <div className={styles.slotPickerTools}>
            {pickerFiltersOpen ? (
              <>
                <input
                  className={`${styles.input} ${styles.slotPickerSearch}`}
                  type="search"
                  value={pickerSearch}
                  placeholder={t("deck.filters.searchPlaceholder")}
                  onChange={(event) => setPickerSearch(event.target.value)}
                />
                <div className={styles.filters}>
                  {pickingAspect ? null : (
                    <select
                      className={styles.select}
                      value={workshopPickerKind}
                      onChange={(event) =>
                        setWorkshopPickerKind(event.target.value)
                      }
                    >
                      <option value="all">{t("deck.filters.allCards")}</option>
                      <option value="equipment">{t("deck.equipment")}</option>
                      <option value="ability">{t("deck.ability")}</option>
                    </select>
                  )}
                  <select
                    className={styles.select}
                    value={pickerAttr}
                    onChange={(event) => setPickerAttr(event.target.value)}
                  >
                    <option value="all">{t("deck.filters.allAttributes")}</option>
                    <option value="strength">{t("deck.attr.strength")}</option>
                    <option value="dexterity">{t("deck.attr.dexterity")}</option>
                    <option value="wisdom">{t("deck.attr.wisdom")}</option>
                    <option value="neutral">{t("deck.attr.neutral")}</option>
                  </select>
                  {pickingAspect ? null : workshopPickerKind === "equipment" ? (
                    <select
                      className={styles.select}
                      value={pickerSlot}
                      onChange={(event) => setPickerSlot(event.target.value)}
                    >
                      <option value="all">{t("deck.filters.allSlots")}</option>
                      {slotOptions.map((slot) => (
                        <option key={slot} value={slot}>
                          {formatSlotLabel(slot)}
                        </option>
                      ))}
                    </select>
                  ) : workshopPickerKind === "ability" ? (
                    <select
                      className={styles.select}
                      value={pickerKind}
                      onChange={(event) => setPickerKind(event.target.value)}
                    >
                      <option value="all">{t("deck.filters.allKinds")}</option>
                      <option value="active">{t("deck.kind.active")}</option>
                      <option value="augment">{t("deck.kind.augment")}</option>
                      <option value="essence">{t("deck.kind.essence")}</option>
                    </select>
                  ) : null}
                  {showEquipmentFilters
                    ? renderEquipmentPickerFilters({
                        itemTypeOptions,
                        itemType: pickerItemType,
                        onItemTypeChange: setPickerItemType,
                        tier: pickerTier,
                        onTierChange: setPickerTier,
                      })
                    : null}
                  {pickingAspect ? null : (
                    <>
                      <select
                        className={styles.select}
                        value={pickerType}
                        onChange={(event) => setPickerType(event.target.value)}
                      >
                        <option value="all">{t("deck.filters.allTypes")}</option>
                        {renderCardTypeFilterOptions(typeOptions, {
                          includePlain: true,
                        })}
                      </select>
                      <select
                        className={styles.select}
                        value={pickerEffect}
                        onChange={(event) => setPickerEffect(event.target.value)}
                      >
                        <option value="all">{t("deck.filters.allEffects")}</option>
                        {effectOptions.map((effectId) => (
                          <option key={effectId} value={effectId}>
                            {formatEffectFilterLabel(effectId)}
                          </option>
                        ))}
                      </select>
                      <select
                        className={styles.select}
                        value={pickerCost}
                        onChange={(event) => setPickerCost(event.target.value)}
                      >
                        <option value="all">{t("deck.filters.allCosts")}</option>
                        <option value="0">0</option>
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3+">3+</option>
                      </select>
                    </>
                  )}
                  <select
                    className={styles.select}
                    value={pickerSort}
                    onChange={(event) => setPickerSort(event.target.value)}
                  >
                    <option value="name">
                      {t("deck.filters.sortBy", { label: t("deck.sort.name") })}
                    </option>
                    <option value="type">
                      {t("deck.filters.sortBy", { label: t("deck.sort.type") })}
                    </option>
                    <option value="attribute">
                      {t("deck.filters.sortBy", {
                        label: t("deck.sort.attribute"),
                      })}
                    </option>
                    {pickingAspect ? null : (
                      <option value="cost">
                        {t("deck.filters.sortBy", { label: t("deck.sort.cost") })}
                      </option>
                    )}
                    {showEquipmentFilters ? (
                      <option value="tier">
                        {t("deck.filters.sortBy", { label: t("deck.sort.tier") })}
                      </option>
                    ) : null}
                  </select>
                </div>
              </>
            ) : null}
          </div>
          <ScrollHintSurface className={styles.workshopPickerGrid}>
            {options.map((card) => {
              const atMax = pickingAspect
                ? false
                : (counts[card.id] || 0) >= MAX_COPIES;
              const isCurrentAspect =
                pickingAspect && currentAspectId === card.id;
              return (
                <CardFace
                  key={card.id}
                  card={card}
                  count={pickingAspect ? (isCurrentAspect ? 1 : 0) : counts[card.id] || 0}
                  faded={atMax || isCurrentAspect}
                  compact
                  fitText={false}
                  onClick={() => addWorkshopCard(card)}
                />
              );
            })}
          </ScrollHintSurface>
        </div>
      </div>
    );
  };

  const renderMatch = () => {
    if (!displayMatch) return null;
    const opponents = (displayMatch.players || []).filter(
      (name) => name !== displayUsername,
    );
    const opponent = opponents[0];
    const opponentSeat = displayMatch.seats?.[opponent];
    const isAdventureMatchView = Boolean(displayMatch.adventure);
    const adventureAllies = (displayMatch.adventureParty || []).filter(
      (name) => name && name !== displayUsername,
    );
    const adventureBoardCycle = isAdventureMatchView
      ? adventureAllies.length
        ? ["self", ...adventureAllies]
        : ["self"]
      : ["self", "opponent"];
    const cycleIndex =
      ((adventureBoardViewIndex % adventureBoardCycle.length) +
        adventureBoardCycle.length) %
      adventureBoardCycle.length;
    const boardCycleTarget = adventureBoardCycle[cycleIndex] || "self";
    const viewingAllyName =
      boardCycleTarget !== "self" &&
      boardCycleTarget !== "enemies" &&
      boardCycleTarget !== "opponent"
        ? boardCycleTarget
        : null;
    const showAdventureEnemies =
      isAdventureMatchView && boardCycleTarget === "enemies";
    const showOpponentBoard =
      !isAdventureMatchView && opponentBoardExpanded;
    const viewedAllySeat = viewingAllyName
      ? displayMatch.seats?.[viewingAllyName]
      : null;
    const boardSeat = showAdventureEnemies
      ? null
      : viewingAllyName
        ? viewedAllySeat
        : showOpponentBoard
          ? opponentSeat
          : selfSeat;
    const boardUsername = showAdventureEnemies
      ? null
      : viewingAllyName ||
        (showOpponentBoard ? opponent || "Opponent" : displayUsername);
    const boardIsOwn =
      !showAdventureEnemies &&
      !viewingAllyName &&
      !showOpponentBoard;
    const canUseAdventureFacing =
      isAdventureMatchView && Boolean(selfSeat) && !viewingAllyName;
    const canUseDualBoard =
      (!isAdventureMatchView &&
        Boolean(selfSeat) &&
        Boolean(opponentSeat)) ||
      canUseAdventureFacing;
    const selfBoardInteractive = !(
      workshopActingAsOpponent || spectateActingAsOpponent
    );
    const oppBoardInteractive = Boolean(
      workshopActingAsOpponent || spectateActingAsOpponent,
    );
    return (
      <div
        className={`${styles.matchView} ${
          canUseDualBoard ? styles.matchViewDual : ""
        } ${
          canUseAdventureFacing ? styles.matchViewAdventure : ""
        } ${
          isWorkshopMatch ? styles.matchViewWorkshop : ""
        } ${
          isWorkshopMatch && workshopControlsHidden
            ? styles.matchViewWorkshopControlsHidden
            : ""
        } ${leftHanded ? styles.matchViewLeftHanded : ""} ${
          handDisplay === "dynamic"
            ? styles.matchViewHandDynamic
            : styles.matchViewHandDrawer
        } ${
          handDisplay === "drawer" && !handDrawerOpen
            ? styles.matchViewHandClosed
            : ""
        }`}
        ref={matchViewRef}
      >
        <CombatProjectileLayer
          projectileStore={combatProjectileStore}
          hitFlashStore={combatHitFlashStore}
        />
        {matchEmoteDisplay.length ? (
          <div className={styles.matchEmoteLayer} aria-hidden="true">
            {matchEmoteDisplay.map((entry) => (
              <span
                key={entry.key}
                className={styles.matchEmoteBubble}
                style={{
                  "--emote-sx": `${entry.sx}px`,
                  "--emote-sy": `${entry.sy}px`,
                  "--emote-x1": `${entry.x1}px`,
                  "--emote-y1": `${entry.y1}px`,
                  "--emote-x2": `${entry.x2}px`,
                  "--emote-y2": `${entry.y2}px`,
                  "--emote-x3": `${entry.x3}px`,
                  "--emote-y3": `${entry.y3}px`,
                  "--emote-ex": `${entry.ex}px`,
                  "--emote-ey": `${entry.ey}px`,
                  "--emote-rot": entry.rot,
                  animationDuration: `${MATCH_EMOTE_ANIM_MS}ms`,
                }}
                onAnimationEnd={() => removeMatchEmote(entry.key)}
              >
                {entry.emoji}
              </span>
            ))}
          </div>
        ) : null}
        {cardPlayAnims.length ? (
          <div className={styles.cardPlayAnimLayer} aria-hidden="true">
            {cardPlayAnims.map((entry) => (
              <React.Fragment key={entry.key}>
                <div
                  className={styles.cardPlayAnim}
                  style={{
                    width: entry.width,
                    height: entry.height,
                    "--card-play-sx": `${entry.sx}px`,
                    "--card-play-sy": `${entry.sy}px`,
                    "--card-play-ex": `${entry.ex}px`,
                    "--card-play-ey": `${entry.ey}px`,
                    "--card-play-ox": `${entry.width / 2}px`,
                    "--card-play-oy": `${entry.height / 2}px`,
                    "--card-play-spin": `${entry.spinStart}deg`,
                    "--card-play-spin-mid": `${entry.spinStart * 0.65}deg`,
                    "--card-play-ms": `${CARD_PLAY_ANIM_MS}ms`,
                  }}
                >
                  {entry.ghostNode ? (
                    <div
                      className={styles.cardPlayAnimGhost}
                      ref={(el) => attachCardPlayGhost(el, entry.ghostNode)}
                    />
                  ) : entry.card ? (
                    <CardFace
                      card={entry.card}
                      foilEffect={entry.foilEffect}
                      showPowerBadge={false}
                      foilAnimateIdle
                    />
                  ) : null}
                </div>
                {entry.dust.map((particle) => (
                  <span
                    key={`${entry.key}-${particle.key}`}
                    className={styles.cardPlayDust}
                    style={{
                      left: particle.left,
                      top: particle.top,
                      width: particle.width,
                      height: particle.height,
                      marginLeft: -particle.width / 2,
                      marginTop: -particle.height / 2,
                      backgroundColor: particle.color,
                      borderRadius: particle.radius,
                      "--dust-peak": particle.opacity,
                      "--dust-rot": `${particle.rotate}deg`,
                      "--dust-rot-end": `${particle.rotate + particle.rotateEnd}deg`,
                      "--dust-dx-early": `${particle.dxEarly}px`,
                      "--dust-dy-early": `${particle.dyEarly}px`,
                      "--dust-dx": `${particle.dx}px`,
                      "--dust-dy": `${particle.dy}px`,
                      "--dust-delay": `${particle.delay}ms`,
                      "--dust-ms": `${particle.duration}ms`,
                    }}
                  />
                ))}
              </React.Fragment>
            ))}
          </div>
        ) : null}
        <div className={styles.cardFocusLayer}>
          <div
            className={`${styles.cardFocusBackdrop}${
              handMenu || boardMenu ? ` ${styles.cardFocusBackdropActive}` : ""
            }`}
            aria-hidden="true"
          />
          <div ref={setCardFocusHost} className={styles.cardFocusHost} />
          {cardFocusGhosts.map((ghost) => (
            <CardFocusGhost
              key={ghost.key}
              ghost={ghost}
              onDone={removeCardFocusGhost}
            />
          ))}
        </div>
        {renderWorkshopBar()}
        {displayMatch.winner &&
          !awaitingSummary &&
          !isReplayView &&
          !isAdventureMatchView && (
          <div className={styles.panel}>
            <div className={styles.panelTitle}>{t("match.view.winnerBanner", { name: displayMatch.winner })}</div>
          </div>
        )}

        <div
          className={[
            styles.matchLayout,
            canUseDualBoard ? styles.matchLayoutDual : null,
            canUseDualBoard && showOpponentBoard
              ? styles.matchLayoutShowOpp
              : null,
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {canUseAdventureFacing ? (
            <div
              className={`${styles.seatPanel} ${styles.matchSeatFacing}`}
            >
              <div className={styles.facingBoards} data-gamepad-zone="board">
                {renderAdventureEnemies()}
                <div className={styles.boardPhaseAnchor}>
                  {renderMatchPhaseLine(
                    `${styles.statusPhaseLine} ${styles.boardPhaseLine}`,
                  )}
                </div>
                {renderSeatBoardArea(
                  selfSeat,
                  selfBoardInteractive,
                  displayUsername,
                  { mirrored: false, showBoardVitals: true },
                )}
              </div>
              {isMyTurn && !targetingBlocksActions && showQuickUse && (
                <div className={styles.expendAllRow}>
                  <button
                    type="button"
                    className={styles.button}
                    onClick={() =>
                      send({ type: "ai:expendAllForResources" })
                    }
                  >
                    {t("match.view.quickUseEquipment")}
                  </button>
                  <button
                    type="button"
                    className={styles.button}
                    onClick={() =>
                      send({
                        type: "ai:activateCurrentAbilitiesForDamage",
                      })
                    }
                  >
                    {t("match.view.quickUseAbilities")}
                  </button>
                </div>
              )}
            </div>
          ) : canUseDualBoard ? (
            <div
              className={`${styles.seatPanel} ${styles.matchSeatFacing}`}
            >
              {renderFacingBoards(selfBoardInteractive, oppBoardInteractive)}
              {isMyTurn && !targetingBlocksActions && showQuickUse && (
                <div className={styles.expendAllRow}>
                  <button
                    type="button"
                    className={styles.button}
                    onClick={() =>
                      send({ type: "ai:expendAllForResources" })
                    }
                  >
                    {t("match.view.quickUseEquipment")}
                  </button>
                  <button
                    type="button"
                    className={styles.button}
                    onClick={() =>
                      send({
                        type: "ai:activateCurrentAbilitiesForDamage",
                      })
                    }
                  >
                    {t("match.view.quickUseAbilities")}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div
              className={`${styles.seatPanel} ${
                showAdventureEnemies || showOpponentBoard || viewingAllyName
                  ? styles.seatPanelOpponentView
                  : ""
              }`}
            >
              {showAdventureEnemies
                ? renderAdventureEnemies()
                : renderSeatBoardArea(
                      boardSeat,
                      boardIsOwn ||
                        workshopActingAsOpponent ||
                        spectateActingAsOpponent,
                      boardUsername || displayUsername,
                      viewingAllyName
                        ? { showBoardVitals: true }
                        : undefined,
                    )}
              {isMyTurn && !targetingBlocksActions && showQuickUse && (
                <div className={styles.expendAllRow}>
                  <button
                    type="button"
                    className={styles.button}
                    onClick={() => send({ type: "ai:expendAllForResources" })}
                  >
                    {t("match.view.quickUseEquipment")}
                  </button>
                  <button
                    type="button"
                    className={styles.button}
                    onClick={() =>
                      send({ type: "ai:activateCurrentAbilitiesForDamage" })
                    }
                  >
                    {t("match.view.quickUseAbilities")}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        <div className={styles.matchBottomDock} ref={matchBottomDockRef}>
          <div className={styles.matchHands} data-gamepad-zone="hands">
            {handDisplay === "drawer" ? (
              <button
                type="button"
                className={styles.handDrawerToggle}
                onClick={() => setHandDrawerOpen(!handDrawerOpen)}
                aria-expanded={handDrawerOpen}
                aria-label={
                  handDrawerOpen ? t("match.view.hideHand") : t("match.view.showHand")
                }
                title={
                  handDrawerOpen ? t("match.view.hideHand") : t("match.view.showHand")
                }
              >
                <svg viewBox="0 0 16 16" aria-hidden="true">
                  <path
                    d="M4 6l4 4 4-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            ) : null}
            <div className={styles.handColumnCenter} aria-label={t("match.view.hand")}>
              <div className={styles.handColumnHandStack}>
                {/* Hand card icons do not open keywords; use the Examine modal. */}
                <KeywordTipContext.Provider value={null}>
                <div className={styles.handRow}>
                  {(actingSeat?.hand || []).length === 0 ? (
                    <>
                      {padMatchHand([]).map((card, index) =>
                        renderMatchHandCard(null, `hand-empty-${index}`),
                      )}
                      <div className={styles.handEmptyMessage}>
                        {t("match.view.noCardsInHand")}
                      </div>
                    </>
                  ) : (
                    (actingSeat?.hand || []).map((card, index) =>
                      renderMatchHandCard(card, `hand-empty-${index}`),
                    )
                  )}
                </div>
                </KeywordTipContext.Provider>
                {showHints ? (
                  <GameHintButton
                    hintId="hand"
                    text={t("match.view.handHint")}
                    className={styles.gameHintButtonCenter}
                    activeHintId={activeGameHint?.id || null}
                    onToggle={toggleGameHint}
                  />
                ) : null}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.matchFloatingView} data-gamepad-zone="actions">
          {isAdventureMatchView && !opponentBoardExpanded
            ? renderAdventureEffectsButton()
            : null}
          <button
            type="button"
            className={`${styles.button} ${styles.actionBarStackedButton} ${styles.actionBarEye} ${
              styles.actionBarBoardCycle
            } ${
              showAdventureEnemies || showOpponentBoard || viewingAllyName
                ? styles.actionBarEyeActive
                : ""
            }`}
            onClick={() => {
              if (isAdventureMatchView) {
                const nextIndex =
                  (adventureBoardViewIndex + 1) % adventureBoardCycle.length;
                const nextTarget = adventureBoardCycle[nextIndex] || "self";
                setAdventureBoardViewIndex(nextIndex);
                setOpponentBoardExpanded(nextTarget === "enemies");
                if (nextTarget === "self") {
                  showToast(t("match.view.viewingYourBoard"));
                } else if (nextTarget === "enemies") {
                  showToast(t("match.view.viewingEnemies"));
                } else {
                  showToast(t("match.view.viewingPlayerBoard", { name: nextTarget }));
                }
                return;
              }
              if (opponentBoardExpanded) {
                if (isOpponentTurn) {
                  suppressOpponentAutoViewRef.current = true;
                }
                setOpponentBoardExpanded(false);
                showToast(
                  isSpectateView
                    ? t("match.view.viewingPlayerBoard", { name: displayUsername })
                    : t("match.view.viewingYourBoard"),
                );
                return;
              }
              if (isOpponentTurn) {
                suppressOpponentAutoViewRef.current = false;
              }
              setOpponentBoardExpanded(true);
              showToast(t("match.view.viewingPlayerBoard", { name: opponent || t("match.view.opponent") }));
            }}
            aria-pressed={
              showAdventureEnemies || showOpponentBoard || Boolean(viewingAllyName)
            }
            aria-label={t("match.view.cycleBoardView")}
            title={t("match.view.cycleBoardView")}
          >
            <span className={styles.actionBarStackedLabel}>
              <span>{t("match.view.viewLabel")}</span>
              <span>
                {showAdventureEnemies
                  ? t("match.view.viewEnemy")
                  : viewingAllyName
                    ? t("match.view.viewAlly")
                    : showOpponentBoard
                      ? t("match.view.viewOpp")
                      : t("match.view.viewSelf")}
              </span>
            </span>
          </button>
        </div>
        {(!isReplayView && !isSpectateView && showMatchHud) ? (
        <div className={styles.matchFloatingEndTurn} data-gamepad-zone="actions">
          {renderMatchGuidePanel()}
          {!isReplayView && !isSpectateView && isMyTurn && pendingAbilityTarget && (
            <button
              type="button"
              className={styles.button}
              onClick={() => {
                if (pendingAbilityTarget.freeCancel) {
                  send({ type: "ai:cancelAbilityTarget" });
                  return;
                }
                setCancelAbilityConfirm(true);
              }}
            >
              {pendingAbilityTarget.sourceKind === "expend"
                ? t("match.view.cancelEffectUse")
                : t("match.view.cancelAbilityUse")}
            </button>
          )}
          {!isReplayView &&
            !isSpectateView &&
            isMyTurn &&
            pendingAdventureEnemyTarget && (
            <button
              type="button"
              className={styles.button}
              onClick={() => send({ type: "ai:cancelAdventureEnemyTarget" })}
            >
              {pendingAdventureEnemyTarget.sourceKind === "expend"
                ? t("match.view.cancelEffectUse")
                : t("match.view.cancelAbilityUse")}
            </button>
          )}
          {!isReplayView &&
          !isSpectateView &&
          isMyTurn &&
          pendingTraitAbilityLevel ? (
            <div className={styles.matchFloatingEndTurnRow}>
              <button
                type="button"
                className={styles.button}
                title={t("match.view.skipAbilityLevelUpTitle")}
                onClick={() => send({ type: "ai:skipTraitAbilityLevel" })}
              >
                {t("match.view.skip")}
              </button>
              <button
                type="button"
                className={`${styles.button} ${styles.buttonPrimary} ${styles.matchEndTurnButton} ${styles.buttonLooksDisabled}`}
                disabled
                title={t("match.view.chooseAbilityOrSkipTitle")}
              >
                {t("match.view.endTurnLine1")}
                <br />
                {t("match.view.endTurnLine2")}
              </button>
            </div>
          ) : !isReplayView &&
            !isSpectateView &&
            isMyTurn &&
            pendingTraitEquipmentChoice ? (
            <div className={styles.matchFloatingEndTurnRow}>
              <button
                type="button"
                className={styles.button}
                title={t("match.view.skipEquipmentTraitTitle")}
                onClick={() => send({ type: "ai:skipTraitEquipmentChoice" })}
              >
                {t("match.view.skip")}
              </button>
              <button
                type="button"
                className={`${styles.button} ${styles.buttonPrimary} ${styles.matchEndTurnButton} ${styles.buttonLooksDisabled}`}
                disabled
                title={
                  pendingTraitEquipmentChoice?.kind === "physicalArmorEmpower"
                    ? t("match.view.chooseAbilityOrEquipmentOrSkipTitle")
                    : t("match.view.chooseEquipmentOrSkipTitle")
                }
              >
                {t("match.view.endTurnLine1")}
                <br />
                {t("match.view.endTurnLine2")}
              </button>
            </div>
          ) : !isReplayView &&
            !isSpectateView &&
            isMyTurn &&
            pendingTraitChoice ? (
            <div className={styles.matchFloatingEndTurnRow}>
              <button
                type="button"
                className={styles.button}
                onClick={() => setTraitChoiceBoardPeek((peek) => !peek)}
              >
                {traitChoiceBoardPeek ? (
                  <>
                    {t("match.view.chooseTraitLine1")}
                    <br />
                    {t("match.view.chooseTraitLine2")}
                  </>
                ) : (
                  <>
                    {t("match.view.viewBoardLine1")}
                    <br />
                    {t("match.view.viewBoardLine2")}
                  </>
                )}
              </button>
              <button
                type="button"
                className={`${styles.button} ${styles.buttonPrimary} ${styles.matchEndTurnButton} ${styles.buttonLooksDisabled}`}
                disabled
                title={t("match.view.chooseTraitTitle")}
              >
                {t("match.view.endTurnLine1")}
                <br />
                {t("match.view.endTurnLine2")}
              </button>
            </div>
          ) : !isReplayView &&
            !isSpectateView &&
            isMyTurn &&
            pendingIdentityChoice ? (
            <div className={styles.matchFloatingEndTurnRow}>
              <button
                type="button"
                className={styles.button}
                onClick={() => setIdentityChoiceBoardPeek((peek) => !peek)}
              >
                {identityChoiceBoardPeek ? (
                  <>
                    {t("match.view.chooseIdentityLine1")}
                    <br />
                    {t("match.view.chooseIdentityLine2")}
                  </>
                ) : (
                  <>
                    {t("match.view.viewBoardLine1")}
                    <br />
                    {t("match.view.viewBoardLine2")}
                  </>
                )}
              </button>
              <button
                type="button"
                className={`${styles.button} ${styles.buttonPrimary} ${styles.matchEndTurnButton} ${styles.buttonLooksDisabled}`}
                disabled
                title={t("match.view.chooseIdentityTitle")}
              >
                {t("match.view.endTurnLine1")}
                <br />
                {t("match.view.endTurnLine2")}
              </button>
            </div>
          ) : null}
          <div className={styles.matchPrimaryActionRow}>
            {showMulliganAction ? (
              <button
                type="button"
                className={styles.button}
                disabled={selfSeat.mulliganUsed}
                onClick={() => send({ type: "ai:mulligan" })}
              >
                {selfSeat.mulliganUsed ? t("match.view.mulliganed") : t("match.view.mulligan")}
              </button>
            ) : null}
            {showMulliganAction && (
              <button
                type="button"
                className={`${styles.button} ${styles.buttonPrimary}`}
                onClick={() => {
                  lockEndTurnAfterReady();
                  send({ type: "ai:ready" });
                }}
              >
                {t("match.view.ready")}
              </button>
            )}
            {!isReplayView &&
              !isSpectateView &&
              isMyTurn &&
              !targetingBlocksActions && (
              <button
                type="button"
                className={`${styles.button} ${styles.buttonPrimary} ${styles.matchEndTurnButton} ${readyEndTurnLocked ? styles.buttonLooksDisabled : ""}${
                  matchGuideEndTurnGlow ? ` ${styles.matchGuideEndTurnGlow}` : ""
                }`}
                disabled={readyEndTurnLocked}
                onClick={() => {
                  if (readyEndTurnLocked) return;
                  send({ type: "ai:endTurn" });
                }}
              >
                {t("match.view.endTurnLine1")} {t("match.view.endTurnLine2")}
              </button>
            )}
          </div>
        </div>
        ) : null}
        {renderEquipmentPopup()}
        {renderSlottedAbilityExam()}
        {renderEquipExpendConfirmPopup()}
        {renderEquipCounterExamPopup()}
        {renderAdventureMonsterExam()}
        {renderHandMenu()}
        {renderAttributeChoicePopup()}
        {renderLevelUpOverlay()}
        {renderTraitChoicePopup()}
        {renderTraitAbilityLevelPopup()}
        {renderTraitEquipmentChoicePopup()}
        {renderAspectResourceChoicePopup()}
        {renderIdentityChoicePopup()}
        {renderChargeResourceChoicePopup()}
        {renderPaymentPrompt()}
        {renderCombatLog()}
        {renderWorkshopPicker()}
        {cancelAbilityConfirm && (
          <div
            className={styles.actionPopup}
            onClick={() => setCancelAbilityConfirm(false)}
          >
            <ScrollHintSurface
              className={styles.actionPopupPanel}
              onClick={(event) => event.stopPropagation()}
            >
              <h3 className={styles.actionPopupTitle}>
                {pendingAbilityTarget?.sourceKind === "expend"
                  ? t("match.view.cancelEffectUseConfirm")
                  : t("match.view.cancelAbilityUseConfirm")}
              </h3>
              <div className={styles.detailBlock}>
                <div className={styles.detailLine}>
                  {pendingAbilityTarget?.freeCancel
                    ? t("match.view.noResourcesSpent")
                    : t("match.view.noRefund", {
                        name: pendingAbilityTarget?.abilityName || t("match.view.thisEffect"),
                      })}
                </div>
              </div>
              <div className={styles.row}>
                <button
                  type="button"
                  className={`${styles.button} ${styles.buttonPrimary}`}
                  onClick={() => {
                    setCancelAbilityConfirm(false);
                    send({ type: "ai:cancelAbilityTarget" });
                  }}
                >
                  {pendingAbilityTarget?.sourceKind === "expend"
                    ? t("match.view.cancelEffectUse")
                    : t("match.view.cancelAbilityUse")}
                </button>
                <button
                  type="button"
                  className={styles.button}
                  onClick={() => setCancelAbilityConfirm(false)}
                >
                  {t("match.view.keepTargeting")}
                </button>
              </div>
            </ScrollHintSurface>
          </div>
        )}
      </div>
    );
  };

  const renderReplayControls = () => {
    if (!isReplayView || !replayDisplay) return null;
    const {
      index,
      entry,
      log,
      turnIndexes,
      currentTurnPos,
    } = replayDisplay;
    const lastIndex = Math.max(0, log.length - 1);
    return (
      <aside className={styles.replayControls}>
        <div className={styles.replayControlsTitle}>Replay</div>
        <div className={styles.muted}>
          Step {index + 1}/{Math.max(1, log.length)}
        </div>
        <div className={styles.replayControlsCurrent}>
          {entry?.text || "No log entry"}
        </div>
        <button
          type="button"
          className={styles.button}
          disabled={index <= 0}
          onClick={() => setAdminReplayIndex(0)}
        >
          Start
        </button>
        <button
          type="button"
          className={styles.button}
          disabled={currentTurnPos <= 0}
          onClick={() =>
            setAdminReplayIndex(turnIndexes[Math.max(0, currentTurnPos - 1)] || 0)
          }
        >
          Prev Turn
        </button>
        <button
          type="button"
          className={styles.button}
          disabled={index <= 0}
          onClick={() => setAdminReplayIndex(Math.max(0, index - 1))}
        >
          Prev Action
        </button>
        <button
          type="button"
          className={styles.button}
          disabled={index >= lastIndex}
          onClick={() =>
            setAdminReplayIndex(Math.min(lastIndex, index + 1))
          }
        >
          Next Action
        </button>
        <button
          type="button"
          className={styles.button}
          disabled={
            currentTurnPos < 0 || currentTurnPos >= turnIndexes.length - 1
          }
          onClick={() =>
            setAdminReplayIndex(
              turnIndexes[
                Math.min(turnIndexes.length - 1, currentTurnPos + 1)
              ] || index,
            )
          }
        >
          Next Turn
        </button>
        <button
          type="button"
          className={styles.button}
          disabled={index >= lastIndex}
          onClick={() => setAdminReplayIndex(lastIndex)}
        >
          End
        </button>
        <button
          type="button"
          className={styles.button}
          onClick={() => {
            combatLogStickTopRef.current = true;
            setCombatLogOpen(true);
          }}
        >
          Game Log
        </button>
        <button
          type="button"
          className={`${styles.button} ${styles.buttonDanger}`}
          onClick={leaveReplay}
        >
          Leave Replay
        </button>
      </aside>
    );
  };

  const renderAdminGamePreview = () => {
    if (!adminPreviewGame || isReplayView) return null;
    const game = adminPreviewGame;
    const players = game.players || [];
    const deckLines = players.map((name) => {
      const deck = game.decks?.[name];
      if (!deck) return `${name}: —`;
      return `${name}: ${deck.name || deck.id || "Deck"}`;
    });
    const hpLines = players.map((name) => {
      const finalHp = game.finalHp?.[name];
      if (!finalHp) return `${name}: —`;
      return `${name}: ${finalHp.hp}/${finalHp.maxHp} HP`;
    });
    return (
      <div
        className={styles.actionPopup}
        onClick={() => setAdminPreviewGame(null)}
      >
        <ScrollHintSurface
          className={styles.actionPopupPanel}
          onClick={(event) => event.stopPropagation()}
        >
          <h3 className={styles.actionPopupTitle}>Completed Game</h3>
          <div className={styles.detailBlock}>
            <div className={styles.detailLine}>
              {game.completedAt
                ? new Date(game.completedAt).toLocaleString()
                : game.id}
            </div>
            <div className={styles.detailLine}>
              {players.join(" vs ") || "—"}
            </div>
            <div className={styles.detailLine}>
              {formatCompletedGameEnded(game.endedBy, game.winner)}
            </div>
            <div className={styles.detailLine}>
              Rounds {game.rounds || 1}
            </div>
            <div className={styles.detailLine}>
              Actions{" "}
              {Array.isArray(game.combatLog) ? game.combatLog.length : 0}
            </div>
            {deckLines.map((line) => (
              <div key={line} className={styles.detailLine}>
                {line}
              </div>
            ))}
            {hpLines.map((line) => (
              <div key={`hp-${line}`} className={styles.detailLine}>
                {line}
              </div>
            ))}
          </div>
          <div className={styles.row}>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary}`}
              onClick={startAdminReplay}
            >
              Replay Game
            </button>
            <button
              type="button"
              className={styles.button}
              onClick={() => setAdminPreviewGame(null)}
            >
              Close
            </button>
          </div>
        </ScrollHintSurface>
      </div>
    );
  };

  if (awaitingSummary) {
    keepCollectionViewRef.current = false;
    keepGlossaryViewRef.current = false;
  } else {
    if (view === "collection") keepCollectionViewRef.current = true;
    if (view === "library" && librarySection === "glossary") {
      keepGlossaryViewRef.current = true;
    }
  }
  const showCollectionView =
    !awaitingSummary &&
    (view === "collection" ||
      (keepCollectionViewRef.current && !inActiveMatchView));
  const glossaryActive = view === "library" && librarySection === "glossary";
  const showGlossaryView =
    !awaitingSummary &&
    (glossaryActive || (keepGlossaryViewRef.current && !inActiveMatchView));

  return (
    <FoilEffectsQualityContext.Provider value={activeFoilEffectsQuality}>
      <FoilAnimateIdleContext.Provider
        value={
          FOIL_SPOTLIGHT_VIEWS.has(view) || inAdventurePostCombat
        }
      >
        <KeywordTipContext.Provider
          value={{
            activeKeywordId: keywordTip?.id || null,
            onKeywordClick: toggleKeywordTip,
          }}
        >
        <div
          ref={shellRef}
          tabIndex={-1}
          className={`${styles.canvas} ${
            inActiveMatchView ? styles.canvasInMatch : ""
          } ${isReplayView ? styles.canvasInReplay : ""} ${
            sideNavExpanded ? "" : styles.canvasNavCollapsed
          } ${theme === "light" ? styles.canvasLight : ""} ${
            showSplash ? styles.canvasSplash : ""
          } ${gamepadConnected ? styles.canvasGamepad : ""} ${
            gamepadConnected && gamepadRegion === "nav"
              ? styles.canvasGamepadNav
              : ""
          } ${
            gamepadConnected && gamepadRegion === "main"
              ? styles.canvasGamepadMain
              : ""
          }`}
          style={matchTopographyStyle}
          onPointerDown={ensureImmersive}
          onContextMenu={(event) => event.preventDefault()}
        >
      {gamepadConnected ? (
        <span
          className={styles.gamepadStatus}
          title={t("gamepad.statusTitle", {
            pad: gamepadPadId || t("gamepad.connected"),
          })}
          aria-label={
            gamepadRegion === "nav"
              ? t("gamepad.ariaNav")
              : t("gamepad.ariaView")
          }
        >
          {gamepadRegion === "nav" ? t("gamepad.padNav") : t("gamepad.padView")}
        </span>
      ) : null}
      {showSplash ? (
        <div className={styles.splash} data-gamepad-region="main">
          <div className={styles.splashBrand}>
            <h1 className={styles.splashTitle}>Aeterna Invicta</h1>
            <div className={styles.splashDescription}>
              <p>{t("splash.description")}</p>
              <p className={styles.splashTagline}>{t("splash.tagline")}</p>
            </div>
          </div>
          <div className={styles.splashActions}>
            {isNewPlayer ? (
              <button
                type="button"
                className={`${styles.button} ${styles.splashTutorialsButton}`}
                onClick={enterTutorialsFromSplash}
              >
                {t("splash.tutorials")}
              </button>
            ) : null}
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary} ${styles.splashEnterButton}`}
              onClick={enterGame}
            >
              {t("splash.enterGame")}
            </button>
          </div>
        </div>
      ) : (
        <>
      {!showMatchHud && (
        <button
          type="button"
          className={styles.gameVersion}
          onClick={() => {
            if (inActiveMatchView) {
              combatLogStickTopRef.current = true;
              setCombatLogOpen(true);
              return;
            }
            setPatchNotesOpen(true);
          }}
          aria-label={inActiveMatchView ? t("hud.gameLog") : t("hud.patchNotes")}
        >
          {inActiveMatchView ? t("hud.gameLog") : `(v${GAME_VERSION})`}
        </button>
      )}
      <nav
        data-gamepad-region="nav"
        className={`${styles.sideNav} ${
          inActiveMatchView ? styles.sideNavInMatch : ""
        } ${sideNavExpanded ? "" : styles.sideNavCollapsed}`}
      >
        <button
          type="button"
          className={styles.navToggle}
          onClick={() => setSideNavExpanded((open) => !open)}
          aria-expanded={sideNavExpanded}
          aria-label={sideNavExpanded ? t("nav.collapse") : t("nav.expand")}
        >
          {sideNavExpanded ? "◀" : "▶"}
        </button>
        <div className={styles.sideNavLinks}>
          <button
            type="button"
            className={`${styles.navButton} ${
              queueNavActive ? styles.navButtonActive : ""
            }`}
            onClick={openQueueNav}
            disabled={isReplayView}
            aria-label={queueNavLabel}
            title={queueNavLabel}
          >
            <NavButtonIcon src={NAV_ICON_URL.main} />
          </button>
          <button
            type="button"
            className={`${styles.navButton} ${
              view === "deck" ? styles.navButtonActive : ""
            }`}
            onClick={() => requestViewFromNav("deck")}
            disabled={awaitingSummary || isReplayView}
            aria-label={t("nav.decks")}
            title={t("nav.decks")}
          >
            <NavButtonIcon src={NAV_ICON_URL.decks} />
          </button>
          <button
            type="button"
            className={`${styles.navButton} ${
              view === "library" || view === "collection"
                ? styles.navButtonActive
                : ""
            }`}
            onClick={() => requestViewFromNav("library")}
            disabled={awaitingSummary || isReplayView || view === "matchIntro"}
            aria-label={t("nav.library")}
            title={t("nav.library")}
          >
            <NavButtonIcon src={NAV_ICON_URL.library} />
          </button>
          <button
            type="button"
            className={`${styles.navButton} ${
              view === "achievements" ? styles.navButtonActive : ""
            } ${hasCollectableRewards ? styles.navButtonGilded : ""}`}
            onClick={() => requestViewFromNav("achievements")}
            disabled={awaitingSummary || isReplayView || view === "matchIntro"}
            aria-label={t("nav.achievements")}
            title={t("nav.achievements")}
          >
            <NavButtonIcon src={NAV_ICON_URL.achievements} />
          </button>
          <button
            type="button"
            className={`${styles.navButton} ${
              view === "customize" ? styles.navButtonActive : ""
            }`}
            onClick={() => requestViewFromNav("customize")}
            disabled={awaitingSummary || isReplayView || view === "matchIntro"}
            aria-label={t("nav.customize")}
            title={t("nav.customize")}
          >
            <NavButtonIcon src={NAV_ICON_URL.customize} />
          </button>
          <button
            type="button"
            className={`${styles.navButton} ${
              view === "marketplace" ? styles.navButtonActive : ""
            }`}
            onClick={() => requestViewFromNav("marketplace")}
            disabled={awaitingSummary || isReplayView || view === "matchIntro"}
            aria-label={t("nav.marketplace")}
            title={t("nav.marketplace")}
          >
            <NavButtonIcon src={NAV_ICON_URL.marketplace} />
          </button>
          <button
            type="button"
            className={`${styles.navButton} ${
              view === "social" ? styles.navButtonActive : ""
            }`}
            onClick={() => requestViewFromNav("social")}
            disabled={awaitingSummary || isReplayView || view === "matchIntro"}
            aria-label={t("nav.social")}
            title={t("nav.social")}
          >
            <NavButtonIcon src={NAV_ICON_URL.social} />
          </button>
          <button
            type="button"
            className={`${styles.navButton} ${
              view === "ranking" ? styles.navButtonActive : ""
            }`}
            onClick={() => requestViewFromNav("ranking")}
            disabled={awaitingSummary || isReplayView || view === "matchIntro"}
            aria-label={t("nav.ranking")}
            title={t("nav.ranking")}
          >
            <NavButtonIcon src={NAV_ICON_URL.ranking} />
          </button>
          <button
            type="button"
            className={`${styles.navButton} ${
              view === "settings" ? styles.navButtonActive : ""
            }`}
            onClick={() => requestViewFromNav("settings")}
            disabled={awaitingSummary || isReplayView}
            aria-label={t("nav.settings")}
            title={t("nav.settings")}
          >
            <NavButtonIcon src={NAV_ICON_URL.settings} />
          </button>
          {showWorkshop || view === "workshop" || match?.workshop ? (
            <button
              type="button"
              className={`${styles.navButton} ${
                view === "workshop" ? styles.navButtonActive : ""
              }`}
              onClick={openWorkshopNav}
              disabled={awaitingSummary || isReplayView || inLiveMatch}
              aria-label={t("nav.workshop")}
              title={t("nav.workshop")}
            >
              <NavButtonIcon src={NAV_ICON_URL.workshop} />
            </button>
          ) : null}
          <button
            type="button"
            className={`${styles.navButton} ${
              view === "community" ? styles.navButtonActive : ""
            }`}
            onClick={() => requestViewFromNav("community")}
            disabled={awaitingSummary || isReplayView || view === "matchIntro"}
            aria-label={t("nav.community")}
            title={t("nav.community")}
          >
            <NavButtonIcon src={NAV_ICON_URL.community} />
          </button>
          <button
            type="button"
            className={`${styles.navButton} ${
              view === "support" ? styles.navButtonActive : ""
            }`}
            onClick={() => requestViewFromNav("support")}
            disabled={awaitingSummary || isReplayView || view === "matchIntro"}
            aria-label={t("nav.support")}
            title={t("nav.support")}
          >
            <NavButtonIcon src={NAV_ICON_URL.support} />
          </button>
          {isAdmin && (
            <button
              type="button"
              className={`${styles.navButton} ${styles.navButtonAdmin} ${
                view === "admin" || isReplayView ? styles.navButtonActive : ""
              }`}
              onClick={() => {
                playSfx("uiSwitch");
                if (isReplayView) leaveReplay();
                else requestView("admin");
              }}
              disabled={awaitingSummary}
            >
              <NavButtonLabel>Admin</NavButtonLabel>
            </button>
          )}
        </div>
      </nav>

      <div className={styles.main} data-gamepad-region="main">
        <div
          className={`${styles.statusRow} ${
            showMatchHud ? styles.statusRowMatch : ""
          }`}
          {...(showMatchHud ? { "data-gamepad-zone": "hud" } : {})}
        >
          {showMatchHud ? renderMatchPhaseLine(styles.statusPhaseLine) : null}
          <div
            className={
              showMatchHud ? styles.statusMatchControls : styles.statusRowMain
            }
          >
            <div className={styles.statusLeft}>
              {showMatchHud && (
                <button
                  type="button"
                  className={styles.statusNavGear}
                  onClick={() => setSideNavExpanded((open) => !open)}
                  aria-expanded={sideNavExpanded}
                  aria-label={
                    sideNavExpanded ? t("hud.hideNavbar") : t("hud.showNavbar")
                  }
                  title={
                    sideNavExpanded ? t("hud.hideNavbar") : t("hud.showNavbar")
                  }
                >
                  {sideNavExpanded ? t("hud.hideNavbar") : t("hud.showNavbar")}
                </button>
              )}
              {(queued || draftQueued) && <span>{t("hud.queued")}</span>}
            </div>
            <div className={styles.statusCenter}>
              {showMatchHud ? (
                <div className={styles.statusMatchCenterRow}>
                  <span className={styles.statusDot} aria-hidden="true">
                    ·
                  </span>
                  <button
                    type="button"
                    className={styles.statusMatchLog}
                    onClick={() => {
                      combatLogStickTopRef.current = true;
                      setCombatLogOpen(true);
                    }}
                    aria-label={t("hud.gameLog")}
                    title={t("hud.gameLog")}
                  >
                    {t("hud.gameLog")}
                  </button>
                  {showMatchEmote ? (
                    <>
                      <span className={styles.statusDot} aria-hidden="true">
                        ·
                      </span>
                      <button
                        type="button"
                        className={`${styles.statusMatchLog}${
                          emotePanelOpen ? ` ${styles.statusMatchLogActive}` : ""
                        }`}
                        onClick={() => setEmotePanelOpen((open) => !open)}
                        aria-expanded={emotePanelOpen}
                        aria-label={
                          emotePanelOpen
                            ? t("match.view.closeEmotes")
                            : t("match.view.openEmotes")
                        }
                        title={t("match.view.emote")}
                      >
                        {t("match.view.emote")}
                      </button>
                    </>
                  ) : null}
                  {showMatchForfeit ? (
                    <span className={styles.statusDot} aria-hidden="true">
                      ·
                    </span>
                  ) : null}
                  {emotePanelOpen ? (
                    <div className={styles.matchEmotePanel}>
                      <button
                        type="button"
                        className={`${styles.button} ${styles.matchEmoteHideButton} ${
                          hideMatchEmotes ? styles.matchEmoteHideButtonActive : ""
                        }`}
                        onClick={() => setHideMatchEmotes((hidden) => !hidden)}
                        aria-pressed={hideMatchEmotes}
                        title={
                          hideMatchEmotes
                            ? t("match.view.showMatchEmotes")
                            : t("match.view.hideAllMatchEmotes")
                        }
                      >
                        {hideMatchEmotes ? t("match.view.showEmotes") : t("match.view.hideEmotes")}
                      </button>
                      <div
                        className={styles.matchEmoteGrid}
                        role="listbox"
                        aria-label={t("match.view.emotes")}
                      >
                        {MATCH_EMOTE_OPTIONS.map((emoji) => (
                          <button
                            key={emoji}
                            type="button"
                            className={styles.matchEmoteOption}
                            onClick={() => {
                              const nowMs = Date.now();
                              if (
                                nowMs - lastMatchEmoteSentAtRef.current <
                                MATCH_EMOTE_COOLDOWN_MS
                              ) {
                                return;
                              }
                              lastMatchEmoteSentAtRef.current = nowMs;
                              send({ type: "ai:emote", emoji });
                            }}
                            aria-label={t("match.view.sendEmote", { emoji })}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
            <div className={styles.statusRight}>
              {serverMessages.length ? (
                <button
                  type="button"
                  className={`${styles.serverMessagesButton} ${
                    serverMessagesUnread ? styles.serverMessagesButtonUnread : ""
                  } ${
                    serverMaintenance ? styles.serverMessagesButtonMaintenance : ""
                  }`}
                  onClick={openServerMessages}
                  aria-label={
                    serverMessagesUnread
                      ? t("hud.serverMessagesNew")
                      : t("hud.serverMessages")
                  }
                  title={t("hud.serverMessages")}
                >
                  !
                </button>
              ) : null}
              {showMatchForfeit && (
                <button
                  type="button"
                  className={styles.statusForfeit}
                  onClick={() => setForfeitConfirm(true)}
                  aria-label={
                    isSpectateView ? t("hud.leaveSpectate") : t("hud.forfeit")
                  }
                  title={
                    isSpectateView ? t("hud.leaveSpectate") : t("hud.forfeit")
                  }
                >
                  {isSpectateView ? t("hud.leave") : t("hud.forfeit")} 🏳️
                </button>
              )}
            </div>
          </div>
          {showMatchHud &&
          !(
            selfSeat &&
            (displayMatch?.adventure || opponentSeat)
          ) ? (
            <div className={styles.statusVitalsLine}>
              {(() => {
                const selfParts = renderSeatVitalsBlock(selfSeat, displayUsername, {
                  side: "player",
                  mirrored: false,
                  floaterEntries: vitalsFloaterDisplay.filter(
                    (entry) => entry.side === "player",
                  ),
                });
                return selfParts ? (
                  <span className={styles.statusVitalsPlayerGroup}>
                    {selfParts.vitals}
                    {selfParts.badges}
                  </span>
                ) : null;
              })()}
              {(() => {
                const oppParts = renderSeatVitalsBlock(
                  opponentSeat,
                  opponentUsername || t("match.view.opponent"),
                  {
                    side: "opponent",
                    mirrored: true,
                    nameDisabled: !opponentUsername,
                    floaterEntries: vitalsFloaterDisplay.filter(
                      (entry) => entry.side === "opponent",
                    ),
                  },
                );
                return oppParts ? (
                  <span className={styles.statusVitalsOpponentGroup}>
                    {oppParts.vitals}
                    {oppParts.badges}
                  </span>
                ) : null;
              })()}
            </div>
          ) : null}
        </div>
        {renderAdminGamePreview()}
        {renderSocialPlayerMenu()}
        {renderSocialDeckView()}
        {renderAdventureEffectsModal()}
        {incomingChallenge && (
          <div className={styles.actionPopup}>
            <ScrollHintSurface
              className={styles.actionPopupPanel}
              onClick={(event) => event.stopPropagation()}
            >
              <h3 className={styles.actionPopupTitle}>{t("match.popup.challengeTitle")}</h3>
              <div className={styles.detailBlock}>
                <div className={styles.detailLine}>
                  {t("match.popup.challengeBody", { name: incomingChallenge.from })}
                </div>
              </div>
              <div className={styles.row}>
                <button
                  type="button"
                  className={`${styles.button} ${styles.buttonPrimary}`}
                  onClick={acceptIncomingChallenge}
                >
                  {t("match.popup.accept")}
                </button>
                <button
                  type="button"
                  className={styles.button}
                  onClick={() =>
                    respondToChallenge(incomingChallenge.id, false)
                  }
                >
                  {t("match.popup.decline")}
                </button>
              </div>
            </ScrollHintSurface>
          </div>
        )}
        {incomingPartyInvite && (
          <div className={styles.actionPopup}>
            <ScrollHintSurface
              className={styles.actionPopupPanel}
              onClick={(event) => event.stopPropagation()}
            >
              <h3 className={styles.actionPopupTitle}>{t("match.popup.partyInviteTitle")}</h3>
              <div className={styles.detailBlock}>
                <div className={styles.detailLine}>
                  {t("match.popup.partyInviteBody", { name: incomingPartyInvite.from })}
                </div>
              </div>
              <div className={styles.row}>
                <button
                  type="button"
                  className={`${styles.button} ${styles.buttonPrimary}`}
                  onClick={() => {
                    send({
                      type: "ai:adventurePartyRespond",
                      inviteId: incomingPartyInvite.id,
                      accepted: true,
                    });
                    setIncomingPartyInvite(null);
                    requestView("adventure");
                  }}
                >
                  {t("match.popup.accept")}
                </button>
                <button
                  type="button"
                  className={styles.button}
                  onClick={() => {
                    send({
                      type: "ai:adventurePartyRespond",
                      inviteId: incomingPartyInvite.id,
                      accepted: false,
                      from: incomingPartyInvite.from,
                    });
                    setIncomingPartyInvite(null);
                  }}
                >
                  {t("match.popup.decline")}
                </button>
              </div>
            </ScrollHintSurface>
          </div>
        )}
        {challengeForfeitPrompt && (
          <div className={styles.actionPopup}>
            <ScrollHintSurface
              className={styles.actionPopupPanel}
              onClick={(event) => event.stopPropagation()}
            >
              <h3 className={styles.actionPopupTitle}>{t("match.popup.leaveCpuTitle")}</h3>
              <div className={styles.detailBlock}>
                <div className={styles.detailLine}>
                  {t("match.popup.leaveCpuBody")}
                </div>
              </div>
              <div className={styles.row}>
                <button
                  type="button"
                  className={`${styles.button} ${styles.buttonPrimary}`}
                  onClick={() => {
                    respondToChallenge(
                      challengeForfeitPrompt.challengeId,
                      true,
                      true,
                    );
                    setChallengeForfeitPrompt(null);
                  }}
                >
                  {t("match.popup.accept")}
                </button>
                <button
                  type="button"
                  className={styles.button}
                  onClick={() => {
                    respondToChallenge(
                      challengeForfeitPrompt.challengeId,
                      false,
                    );
                    setChallengeForfeitPrompt(null);
                  }}
                >
                  {t("match.popup.decline")}
                </button>
              </div>
            </ScrollHintSurface>
          </div>
        )}
        {forfeitConfirm && (
          <div
            className={styles.actionPopup}
            onClick={() => setForfeitConfirm(false)}
          >
            <ScrollHintSurface
              className={styles.actionPopupPanel}
              onClick={(event) => event.stopPropagation()}
            >
              <h3 className={styles.actionPopupTitle}>
                {isSpectateView ? t("match.popup.leaveSpectateTitle") : t("match.popup.forfeitTitle")}
              </h3>
              <div className={styles.detailBlock}>
                <div className={styles.detailLine}>
                  {isSpectateView
                    ? t("match.popup.leaveSpectateBody")
                    : t("match.popup.forfeitBody")}
                </div>
              </div>
              <div className={styles.row}>
                <button
                  type="button"
                  className={`${styles.button} ${styles.buttonPrimary}`}
                  onClick={() => {
                    setForfeitConfirm(false);
                    send({ type: "ai:leaveMatch" });
                    setSelectedEquipmentId(null);
                    setHandMenu(null);
                    setBoardMenu(null);
                    setSelectedHand(null);
                    if (isSpectateView) setView("social");
                  }}
                >
                  {isSpectateView ? t("match.popup.leave") : t("match.popup.forfeit")}
                </button>
                <button
                  type="button"
                  className={styles.button}
                  onClick={() => setForfeitConfirm(false)}
                >
                  {t("common.cancel")}
                </button>
              </div>
            </ScrollHintSurface>
          </div>
        )}
        {contactFormOpen && (
          <div className={styles.actionPopup} onClick={closeContactForm}>
            <ScrollHintSurface
              className={`${styles.actionPopupPanel} ${styles.contactFormPanel}`}
              onClick={(event) => event.stopPropagation()}
            >
              <h3 className={styles.actionPopupTitle}>
                {t("support.contactTitle")}
              </h3>
              <label className={styles.contactFormField}>
                <span className={styles.contactFormLabel}>
                  {t("support.contact.type")}
                </span>
                <select
                  className={styles.select}
                  value={contactKind}
                  disabled={contactSending}
                  onChange={(event) => {
                    setContactKind(
                      event.target.value === "bug" ? "bug" : "support",
                    );
                    setContactError(null);
                  }}
                >
                  <option value="support">{t("nav.support")}</option>
                  <option value="bug">{t("support.contact.bug")}</option>
                </select>
              </label>
              <label className={styles.contactFormField}>
                <span className={styles.contactFormLabel}>
                  {t("support.contact.email")}
                </span>
                <input
                  className={styles.input}
                  type="email"
                  autoComplete="email"
                  value={contactEmail}
                  disabled={contactSending}
                  onChange={(event) => {
                    setContactEmail(event.target.value);
                    setContactError(null);
                  }}
                />
              </label>
              <label className={styles.contactFormField}>
                <span className={styles.contactFormLabel}>
                  {contactKind === "bug"
                    ? t("support.contact.bugPrompt")
                    : t("support.contact.helpPrompt")}
                </span>
                <textarea
                  className={`${styles.input} ${styles.contactFormTextarea}`}
                  value={contactMessage}
                  disabled={contactSending}
                  rows={6}
                  onChange={(event) => {
                    setContactMessage(event.target.value);
                    setContactError(null);
                  }}
                />
              </label>
              {contactError ? (
                <div className={styles.contactFormError}>{contactError}</div>
              ) : null}
              <div className={styles.row}>
                <button
                  type="button"
                  className={`${styles.button} ${styles.buttonPrimary}`}
                  disabled={contactSending}
                  onClick={submitContactForm}
                >
                  {contactSending ? t("social.sending") : t("support.contact.send")}
                </button>
                <button
                  type="button"
                  className={styles.button}
                  disabled={contactSending}
                  onClick={closeContactForm}
                >
                  {t("common.cancel")}
                </button>
              </div>
            </ScrollHintSurface>
          </div>
        )}
        {contactConfirmation && (
          <div
            className={styles.actionPopup}
            onClick={() => setContactConfirmation(null)}
          >
            <ScrollHintSurface
              className={`${styles.actionPopupPanel} ${styles.contactFormPanel}`}
              onClick={(event) => event.stopPropagation()}
            >
              <h3 className={styles.actionPopupTitle}>
                {t("support.contact.sentTitle")}
              </h3>
              <div className={styles.detailBlock}>
                <div className={styles.detailLine}>{contactConfirmation}</div>
              </div>
              <div className={styles.row}>
                <button
                  type="button"
                  className={`${styles.button} ${styles.buttonPrimary}`}
                  onClick={() => setContactConfirmation(null)}
                >
                  {t("common.ok")}
                </button>
              </div>
            </ScrollHintSurface>
          </div>
        )}
        {loseConfirm && (
          <div
            className={styles.actionPopup}
            onClick={() => setLoseConfirm(null)}
          >
            <ScrollHintSurface
              className={styles.actionPopupPanel}
              onClick={(event) => event.stopPropagation()}
            >
              <h3 className={styles.actionPopupTitle}>{t("match.popup.loseTitle")}</h3>
              <div className={styles.detailBlock}>
                <div className={styles.detailLine}>
                  {loseConfirm.action === "equip"
                    ? t("match.popup.loseBodyEquip", { name: loseConfirm.name })
                    : t("match.popup.loseBodyActivate", { name: loseConfirm.name })}
                </div>
              </div>
              <div className={styles.row}>
                <button
                  type="button"
                  className={`${styles.button} ${styles.buttonPrimary}`}
                  onClick={() => {
                    const run = loseConfirm.run;
                    setLoseConfirm(null);
                    if (typeof run === "function") run();
                  }}
                >
                  {t("match.popup.confirm")}
                </button>
                <button
                  type="button"
                  className={styles.button}
                  onClick={() => setLoseConfirm(null)}
                >
                  {t("common.cancel")}
                </button>
              </div>
            </ScrollHintSurface>
          </div>
        )}
        {turnGlowId > 0 ? (
          <div
            key={turnGlowId}
            className={styles.turnGlow}
            aria-hidden="true"
            onAnimationEnd={() => {
              setTurnGlowId((current) => (current === turnGlowId ? 0 : current));
            }}
          />
        ) : null}
        {renderPatchNotes()}
        {renderPrivacyPolicy()}
        {renderMonetizationInfo()}
        {renderDesignPhilosophy()}
        {renderCredits()}
        {renderSupportInfoEntry()}
        {renderLoreEntry()}
        {renderChatOptIn()}
        {renderServerMessages()}
        {renderReportPlayer()}
        {renderXpQuestsPopup()}
        {reconnecting ? (
          <div
            className={styles.disconnectOverlay}
            role="alertdialog"
            aria-live="assertive"
            aria-label={t("match.popup.disconnected")}
          >
            <div className={styles.disconnectPanel}>
              <p className={styles.disconnectMessage}>
                {t("match.popup.reconnecting")}
              </p>
            </div>
          </div>
        ) : null}
        {serverToast ? (
          <button
            type="button"
            key={serverToast.id}
            className={`${styles.serverToast} ${
              serverToast.kind === "maintenance"
                ? styles.serverToastMaintenance
                : ""
            } ${serverToast.exiting ? styles.serverToastExiting : ""} ${
              completionToast ? styles.serverToastStacked : ""
            }`}
            onClick={openServerMessages}
            role="status"
            aria-live="assertive"
            title={t("serverMessages.view")}
          >
            <span className={styles.serverToastIcon} aria-hidden="true">
              !
            </span>
            <span className={styles.serverToastBody}>
              <span className={styles.serverToastTitle}>
                {serverMessageTitle(serverToast.kind, t)}
              </span>
              <span className={styles.serverToastText}>
                {localizeServerMessageText(serverToast, t)}
              </span>
            </span>
          </button>
        ) : null}
        {completionToast ? (
          <div
            key={completionToast.id}
            className={`${styles.completionToast} ${
              completionToast.exiting ? styles.completionToastExiting : ""
            }`}
            role="status"
            aria-live="polite"
          >
            <span className={styles.completionToastTitle}>
              {completionToast.category === "achievement"
                ? t("completion.achievement")
                : t("completion.daily")}
            </span>
            <span className={styles.completionToastDivider}>-</span>
            <span className={styles.completionToastName}>
              {completionToast.name}
            </span>
          </div>
        ) : null}
        {toast && toastsMode !== "off" && (
          <div
            key={toast.id}
            ref={toastStackRef}
            className={`${styles.toastStack} ${
              toast.exiting ? styles.toastStackExiting : ""
            }`}
          >
            {(toastsMode === "all" || toastsMode === "text") && (
              <div className={styles.toast} role="status" aria-live="polite">
                <span className={styles.toastMessage}>{toast.message}</span>
              </div>
            )}
            {(toastsMode === "all" || toastsMode === "card") &&
            toast.traditional &&
            toast.card ? (
              <div
                ref={toastCardRef}
                aria-hidden="true"
                className={styles.toastCardFace}
              >
                <CardFace
                  card={toast.card}
                  foilEffect={toast.foilEffect}
                  foilAnimateIdle
                />
              </div>
            ) : (toastsMode === "all" || toastsMode === "card") &&
              toast.imageUrl &&
              !toast.artFailed ? (
              <div
                ref={toastCardRef}
                aria-hidden="true"
                className={`${styles.toastCard} ${
                  toast.frameUrl ? styles.toastCardWithFrame : ""
                } ${toast.borderless ? styles.toastCardBorderless : ""} ${
                  toast.hybridClass || ""
                }`.trim()}
                style={toast.hybridStyle}
              >
                <div
                  className={[
                    styles.toastCardArt,
                    getFoilArtFilterClass(toast.foilEffect),
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <img
                    alt=""
                    className={styles.toastCardArtMedia}
                    src={toast.imageUrl}
                    draggable={false}
                    onError={() => {
                      setToast((current) =>
                        current && current.id === toast.id
                          ? { ...current, artFailed: true }
                          : current,
                      );
                    }}
                  />
                </div>
                {toast.frameUrl ? (
                  <CardFrameOverlay frameUrl={toast.frameUrl} />
                ) : null}
                <FoilOverlay
                  foilEffect={toast.foilEffect}
                  cardId={toast.cardId}
                  animateIdle
                />
              </div>
            ) : null}
          </div>
        )}
        {resourceFlyers.map((flyer) => {
          const iconUrl = ATTR_ICON_URL[flyer.attribute];
          if (!iconUrl) return null;
          return (
            <div
              key={flyer.key}
              className={`${styles.resourceFlyer} ${
                ATTR_CLASS[flyer.attribute] || ""
              }`}
              style={{
                left: flyer.startX,
                top: flyer.startY,
                "--dx": `${flyer.dx}px`,
                "--dy": `${flyer.dy}px`,
                "--spin": flyer.spin,
                "--pop-x": `${flyer.popX}px`,
                "--pop-y": `${flyer.popY}px`,
                "--duration": `${flyer.duration}s`,
                animationDelay: `${flyer.delay}ms`,
              }}
              aria-hidden="true"
              onAnimationEnd={() => {
                setResourceFlyers((current) =>
                  current.filter((entry) => entry.key !== flyer.key),
                );
                setPendingResourceReveal((current) => {
                  const bag = flyer.persist ? "persistent" : "resources";
                  const next = {
                    resources: { ...current.resources },
                    persistent: { ...current.persistent },
                  };
                  next[bag][flyer.attribute] = Math.max(
                    0,
                    (next[bag][flyer.attribute] || 0) - 1,
                  );
                  pendingResourceRevealRef.current = next;
                  return next;
                });
                const popKey = `${flyer.attribute}-${
                  flyer.persist ? "p" : "n"
                }-${flyer.revealIndex}`;
                setResourceIconPops((current) => ({
                  ...current,
                  [popKey]: (current[popKey] || 0) + 1,
                }));
              }}
            >
              <MaskIcon src={iconUrl} className={styles.attrIcon} />
            </div>
          );
        })}
        {tutorialHint?.message && isTutorialMatch && view === "match" ? (
          <div
            className={`${styles.tutorialHelperToast} ${
              tutorialHintMinimized ? styles.tutorialHelperToastMinimized : ""
            }`}
            role="status"
            aria-live="polite"
          >
            {!tutorialHintMinimized && (
              <span className={styles.tutorialHelperToastText}>
                {tutorialHint.message}
              </span>
            )}
            <button
              type="button"
              className={styles.tutorialHelperToastToggle}
              aria-label={tutorialHintMinimized ? t("common.showHint") : t("match.popup.hideHint")}
              title={tutorialHintMinimized ? t("common.showHint") : t("match.popup.hideHint")}
              onClick={() => setTutorialHintMinimized((v) => !v)}
            >
              {tutorialHintMinimized ? "?" : "−"}
            </button>
          </div>
        ) : null}
        {view === "menu" && !awaitingSummary && renderMenu()}
        {view === "tutorials" && !awaitingSummary && renderTutorials()}
        {view === "draft" && !awaitingSummary && renderDraftPick()}
        {view === "draftDeck" && !awaitingSummary && renderDraftDeckEditor()}
        {view === "draftSummary" && !awaitingSummary && renderDraftSummary()}
        {inAdventurePostCombat &&
          showAdventureCombatSummaryUi &&
          renderAdventureCombatSummary()}
        {view === "adventure" &&
          !awaitingSummary &&
          !inAdventurePostCombat &&
          renderAdventureHub()}
        {view === "admin" && isAdmin && !awaitingSummary && renderAdmin()}
        {view === "social" && !awaitingSummary && renderSocial()}
        {view === "ranking" && !awaitingSummary && renderRanking()}
        {view === "community" && !awaitingSummary && renderCommunity()}
        {view === "support" && !awaitingSummary && renderSupport()}
        {view === "settings" && !awaitingSummary && renderSettings()}
        {view === "customize" && !awaitingSummary && renderCustomize()}
        {view === "marketplace" && !awaitingSummary && renderMarketplace()}
        {view === "achievements" && !awaitingSummary && renderAchievements()}
        {showCollectionView ? (
          <div
            hidden={view !== "collection"}
            aria-hidden={view !== "collection"}
          >
            <div className={styles.row}>
              <button
                type="button"
                className={styles.button}
                onClick={() => requestViewFromNav("library")}
              >
                {t("common.back")}
              </button>
            </div>
            {renderCollection()}
          </div>
        ) : null}
        {view === "library" && !awaitingSummary && renderLibrary()}
        {showGlossaryView ? (
          <div hidden={!glossaryActive} aria-hidden={!glossaryActive}>
            {renderGlossary()}
          </div>
        ) : null}
        {view === "deck" && !awaitingSummary && renderDeckBuilder()}
        {showMatchBoardUi && renderMatch()}
        {isReplayView && (
          <div className={styles.replayShell}>
            <div className={styles.replayMatchPane}>{renderMatch()}</div>
            {renderReplayControls()}
          </div>
        )}
        {showMatchSummaryUi && renderSummary()}
        {view === "matchIntro" && renderMatchIntro()}
        {renderHumanQueueOverlay()}
        {renderPortraitPreview()}
        {renderDiscardViewer()}
        {renderDeckVariantPicker()}
        {renderDeckShareModal()}
        {renderDeckAnalyzeModal()}
        {renderDeckRecommendModal()}
        {renderCardExam()}
        {renderKeywordTip()}
        {examArtPreview?.artUrl ? (
          <AiHoloPreview
            styles={styles}
            foilEffect={examArtPreview.foilEffect}
            onClose={() => setExamArtPreview(null)}
            cardClassName={`${
              isTraditionalFoil(examArtPreview.foilEffect)
                ? styles.cardExamTraditionalExpanded
                : examArtPreview.frameUrl
                ? styles.cardExamArtExpandedWithFrame
                : ""
            } ${examArtPreview.hybridOutline?.className || ""}`.trim()}
            cardStyle={examArtPreview.hybridOutline?.style}
          >
            <div
              className={`${styles.aiHoloFace} ${
                isTraditionalFoil(examArtPreview.foilEffect)
                  ? styles.cardExamTraditionalFace
                  : ""
              }`}
            >
              {isTraditionalFoil(examArtPreview.foilEffect) &&
              examArtPreview.card ? (
                <CardFace
                  key={examArtPreview.foilEffect || "base"}
                  card={examArtPreview.card}
                  foilEffect={examArtPreview.foilEffect}
                  foilQuality={examFoilQuality}
                  inheritFoilPose
                  fullArt
                />
              ) : (
                <>
                  <div
                    className={[
                      styles.cardExamArtExpandedArt,
                      getFoilArtFilterClass(examArtPreview.foilEffect),
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <AiLazyImage
                      alt=""
                      className={styles.cardExamArtExpandedImage}
                      src={examArtPreview.fullArtUrl || examArtPreview.artUrl}
                      placeholderSrc={examArtPreview.artUrl}
                      eager
                      draggable={false}
                    />
                  </div>
                  {examArtPreview.frameUrl ? (
                    <CardFrameOverlay frameUrl={examArtPreview.frameUrl} />
                  ) : null}
                  <FoilOverlay
                    key={examArtPreview.foilEffect || "base"}
                    foilEffect={examArtPreview.foilEffect}
                    cardId={examArtPreview.cardId}
                    inheritPose
                    quality={examFoilQuality}
                  />
                </>
              )}
            </div>
          </AiHoloPreview>
        ) : null}
      </div>
        </>
      )}
      {showSplash &&
      assetLoadProgress &&
      assetLoadProgress.total > 0 &&
      assetLoadProgress.loaded < assetLoadProgress.total ? (
        <div
          className={styles.assetPreloadDock}
          role="status"
          aria-live="polite"
          aria-label={t("common.loadingAssets", {
            loaded: assetLoadProgress.loaded,
            total: assetLoadProgress.total,
          })}
        >
          <div className={styles.assetPreloadBarTrack}>
            <div
              className={styles.assetPreloadBarFill}
              style={{
                width: `${Math.min(
                  100,
                  Math.round(
                    (100 * assetLoadProgress.loaded) /
                      assetLoadProgress.total,
                  ),
                )}%`,
              }}
            />
          </div>
          <div className={styles.assetPreloadLabel}>
            {t("common.loadingAssets", {
              loaded: assetLoadProgress.loaded,
              total: assetLoadProgress.total,
            })}
          </div>
        </div>
      ) : null}
        </div>
        </KeywordTipContext.Provider>
      </FoilAnimateIdleContext.Provider>
    </FoilEffectsQualityContext.Provider>
  );
}
